import wretch, { WretchError } from "wretch";
import QueryStringAddon from "wretch/addons/queryString";
import * as z from "zod";
import qs from "qs";
import {
	useQuery,
	UseQueryOptions,
	UseQueryResult as UseBaseQueryResult,
	useMutation,
	UseMutationOptions,
	useInfiniteQuery,
	UseInfiniteQueryOptions,
	UseInfiniteQueryResult as UseBaseInfiniteQueryResult,
	QueryClient,
	QueryFunction,
	QueryKey,
} from "@tanstack/react-query";

const isProduction: boolean = process.env.NODE_ENV === "production";
const invariantPrefix: string = "Invariant failed";

// Throw an error if the condition fails
// Strip out error messages for production
// > Not providing an inline default argument for message as the result is smaller
function invariant(
	condition: any,
	// Can provide a string, or a function that returns a string for cases where
	// the message takes a fair amount of effort to compute
	message?: string | (() => string),
): asserts condition {
	if (condition) {
		return;
	}
	// Condition not passed

	// In production we strip the message but still throw
	if (isProduction) {
		throw new Error(invariantPrefix);
	}

	// When not in production we allow the message to pass through
	// *This block will be removed in production builds*

	const provided: string | undefined =
		typeof message === "function" ? message() : message;

	// Options:
	// 1. message provided: `${invariantPrefix}: ${provided}`
	// 2. message not provided: invariantPrefix
	const value: string = provided
		? `${invariantPrefix}: ${provided}`
		: invariantPrefix;
	throw new Error(value);
}

function error(err: unknown): WretchError {
	return err as WretchError;
}

type ErrorRecord = Record<string, string>;

interface GenericWretchError<T = ErrorRecord> extends WretchError {
	json: T;
}

export function toApiError<T extends ErrorRecord>(
	error: unknown,
): GenericWretchError<T> {
	return error as GenericWretchError<T>;
}

export interface ApiResponse<T> extends Pick<Response, "headers" | "url"> {
	data: T;
	status: number;
}

type UseQueryResult<TData = unknown, TError = unknown> = UseBaseQueryResult<
	TData,
	TError
> & {
	key: QueryKey;
	invalidate: () => Promise<void>;
};

type UseInfiniteQueryResult<
	TData = unknown,
	TError = unknown,
> = UseBaseInfiniteQueryResult<TData, TError> & {
	key: QueryKey;
	invalidate: () => Promise<void>;
};

export const filters = z
	.array(
		z
			.object({
				type: z
					.enum([
						"exact",
						"search",
						"boolean",
						"exists",
						"inarray",
						"datetimerange",
						"numberrange",
					])
					.describe("Type of filter"),
				field: z
					.enum([
						"id",
						"name",
						"type",
						"country",
						"city",
						"entityType",
						"isNational",
					])
					.describe("Property to filter"),
				value: z
					.union([
						z.string(),
						z.number(),
						z.boolean(),
						z
							.object({
								start: z.string().datetime({ offset: true }),
								end: z.string().datetime({ offset: true }),
							})
							.partial(),
					])
					.describe(
						"Filter value, can be a string, integer, date or a date range",
					),
			})
			.partial(),
	)
	.optional();
export type filters = z.infer<typeof filters>;

export const order = z
	.object({
		field: z
			.enum(["id", "name", "type", "country", "region", "city", "createdAt"])
			.describe("Sorting field"),
		direction: z.enum(["ASC", "DESC"]).describe("Sorting direction"),
	})
	.partial()

	.optional();
export type order = z.infer<typeof order>;

export const ClubHouseType = z.enum([
	"professional_club",
	"academy",
	"private_club",
	"amateur",
	"school_team",
	"community_program",
	"other",
]);
export type ClubHouseType = z.infer<typeof ClubHouseType>;

export const DiscoverClubHousesRowResponse = z.object({
	id: z.number().int().default(0),
	name: z.string().default(""),
	logo: z.string().nullable(),
	type: ClubHouseType.nullable(),
	numberOfTeams: z.number().int().nullable(),
	realNumberOfTeams: z.number().int().nullable(),
	country: z.string().nullable(),
	region: z.string().nullable(),
	city: z.string().nullable(),
	createdAt: z.string().datetime({ offset: true }).nullable(),
	entityType: z.string(),
	isNational: z.boolean(),
});
export type DiscoverClubHousesRowResponse = z.infer<
	typeof DiscoverClubHousesRowResponse
>;

export const DiscoverClubHousesResponse = z.object({
	page: z.number().int().default(0),
	limit: z.number().int().default(0),
	pages: z.number().int().default(0),
	total: z.number().int().default(0),
	items: z.array(DiscoverClubHousesRowResponse).default([]),
});
export type DiscoverClubHousesResponse = z.infer<
	typeof DiscoverClubHousesResponse
>;

export const SomeItemResponse = z.object({
	id: z.union([z.number(), z.string()]),
	name: z.string(),
	metadata: z
		.record(z.union([z.boolean(), z.string(), z.number()]))
		.nullable()
		.default(null),
});
export type SomeItemResponse = z.infer<typeof SomeItemResponse>;

export const DiscoverClubHouseTeamResponse = z.object({
	id: z.number().int(),
	name: z.string(),
	invited: z.boolean().nullish(),
});
export type DiscoverClubHouseTeamResponse = z.infer<
	typeof DiscoverClubHouseTeamResponse
>;

export const UserPermissionEntityType = z.enum([
	"club",
	"club_pro",
	"club_house",
	"team",
	"player",
]);
export type UserPermissionEntityType = z.infer<typeof UserPermissionEntityType>;

export const UserPermissionRole = z.enum([
	"coach",
	"player",
	"parent",
	"works_for_club",
]);
export type UserPermissionRole = z.infer<typeof UserPermissionRole>;

export const UserPermissionItemResponse = z.object({
	id: z.number().int(),
	policyName: z.string(),
	entityType: UserPermissionEntityType,
	entityId: z.number().int(),
	role: UserPermissionRole.nullable(),
	player: SomeItemResponse.nullable(),
	playerData: SomeItemResponse.nullable(),
	team: SomeItemResponse.nullable(),
	clubHouse: SomeItemResponse.nullable(),
	user: SomeItemResponse.nullable(),
});
export type UserPermissionItemResponse = z.infer<
	typeof UserPermissionItemResponse
>;

export const InviteLinkType = z.enum([
	"club_house_team",
	"club_house_member",
	"team_member",
]);
export type InviteLinkType = z.infer<typeof InviteLinkType>;

export const InviteLinkRole = z.enum([
	"club_house_admin",
	"club_house_member",
	"team_admin",
	"team_member",
]);
export type InviteLinkRole = z.infer<typeof InviteLinkRole>;

export const InviteLinkResponse = z.object({
	id: z.number().int(),
	hashCode: z.string(),
	type: InviteLinkType,
	role: InviteLinkRole.nullable(),
	createdAt: z.string().datetime({ offset: true }),
	createdBy: SomeItemResponse.nullable(),
});
export type InviteLinkResponse = z.infer<typeof InviteLinkResponse>;

export const InviteLinkHistoryResponse = z.object({
	id: z.number().int(),
	type: InviteLinkType,
	role: InviteLinkRole.nullable(),
	acceptedBy: SomeItemResponse.nullable(),
	inviteLinkId: z.number().int().nullable(),
	createdAt: z.string().datetime({ offset: true }),
});
export type InviteLinkHistoryResponse = z.infer<
	typeof InviteLinkHistoryResponse
>;

export const TokenMovementType = z.enum([
	"add_purchase",
	"add_gift",
	"remove_refund",
	"remove_remove",
	"remove_subscription",
	"remove_shared_analysis",
	"transfer_from_club",
	"transfer_to_team",
]);
export type TokenMovementType = z.infer<typeof TokenMovementType>;

export const TokenMovementAuthorType = z.enum(["user", "admin", "system"]);
export type TokenMovementAuthorType = z.infer<typeof TokenMovementAuthorType>;

export const TokenMovementResponse = z.object({
	id: z.number().int(),
	clubId: z.number().int().nullable(),
	clubName: z.string().nullable(),
	teamId: z.number().int().nullable(),
	teamName: z.string().nullable(),
	relatedClubId: z.number().int().nullable(),
	relatedClubName: z.string().nullable(),
	relatedTeamId: z.number().int().nullable(),
	relatedTeamName: z.string().nullable(),
	userId: z.number().int().nullable(),
	userName: z.string().nullable(),
	amount: z.number().int(),
	movementType: TokenMovementType,
	authorType: TokenMovementAuthorType,
	paymentLink: z.string().nullable(),
	purchasedAt: z.string().datetime({ offset: true }).nullable(),
	createdAt: z.string().datetime({ offset: true }),
	note: z.string().nullable(),
});
export type TokenMovementResponse = z.infer<typeof TokenMovementResponse>;

export const DiscoverClubHouseGetResponse = z.object({
	id: z.number().int(),
	name: z.string(),
	entityType: z.string(),
	isDiscover: z.boolean(),
	country: SomeItemResponse.nullable(),
	city: z.string().nullable(),
	region: z.string().nullable(),
	type: ClubHouseType.nullable(),
	useMediaForMarketingPurpose: z.boolean().nullable(),
	hasPlayerAccessToMetrics: z.boolean().nullable(),
	hasPlayerAccessToRatings: z.boolean().nullable(),
	numberOfTeams: z.number().int().nullable(),
	tokenBalance: z.number().int(),
	tokenReadAccess: z.boolean(),
	tokenEditAccess: z.boolean(),
	subscriptionReadAccess: z.boolean(),
	subscriptionEditAccess: z.boolean(),
	teamGroupReadAccess: z.boolean(),
	teamGroupEditAccess: z.boolean(),
	isPlayerAppDisabled: z.boolean(),
	isNational: z.boolean(),
	teams: z.array(DiscoverClubHouseTeamResponse),
	userPermissions: z.array(UserPermissionItemResponse).default([]),
	inviteLinks: z.array(InviteLinkResponse).default([]),
	inviteLinksHistory: z.array(InviteLinkHistoryResponse).default([]),
	tokenMovements: z.array(TokenMovementResponse).default([]),
});
export type DiscoverClubHouseGetResponse = z.infer<
	typeof DiscoverClubHouseGetResponse
>;

export const DiscoverClubHousePatchRequest = z.object({
	id: z.number().int(),
	name: z.string().nullish(),
	country: z.string().nullish(),
	region: z.string().nullish(),
	city: z.string().nullish(),
	useMediaForMarketingPurpose: z.boolean().nullish(),
	hasPlayerAccessToMetrics: z.boolean().nullish(),
	hasPlayerAccessToRatings: z.boolean().nullish(),
	numberOfTeams: z.number().int().nullish(),
	isPlayerAppDisabled: z.boolean().nullish(),
	isNational: z.boolean().nullish(),
	requestedFields: z.array(z.string()).optional().default([]),
});
export type DiscoverClubHousePatchRequest = z.infer<
	typeof DiscoverClubHousePatchRequest
>;

export const ChoiceResponse = z.object({
	label: z.string(),
	value: z.union([z.string(), z.number(), z.number(), z.string()]),
});
export type ChoiceResponse = z.infer<typeof ChoiceResponse>;

export const filters__2 = z
	.array(
		z
			.object({
				type: z
					.enum([
						"exact",
						"search",
						"boolean",
						"exists",
						"inarray",
						"datetimerange",
						"numberrange",
					])
					.describe("Type of filter"),
				field: z
					.enum([
						"id",
						"date",
						"createdAt",
						"ageCategory",
						"homeClub",
						"homeTeam",
						"awayClub",
						"awayTeam",
						"anyClubId",
						"anyTeamId",
						"playerId",
						"isVip",
						"isHidden",
						"isHiddenForScout",
						"isDiscover",
						"providerTag",
						"hasVideo",
						"analysisRequestedAt",
						"gameAnalysisStatus",
						"priority",
						"gameStatus",
						"requestedProcessingLevel",
						"owner",
						"country",
						"clubManager",
						"contentManager",
						"assignee",
					])
					.describe("Property to filter"),
				value: z
					.union([
						z.string(),
						z.number(),
						z.boolean(),
						z
							.object({
								start: z.string().datetime({ offset: true }),
								end: z.string().datetime({ offset: true }),
							})
							.partial(),
					])
					.describe(
						"Filter value, can be a string, integer, date or a date range",
					),
			})
			.partial(),
	)
	.optional();
export type filters__2 = z.infer<typeof filters__2>;

export const order__2 = z
	.object({
		field: z
			.enum([
				"id",
				"date",
				"createdAt",
				"ageCategory",
				"homeClubCountry",
				"homeClubName",
				"homeTeamName",
				"awayClubName",
				"awayTeamName",
				"clubManagerName",
				"contentManagerName",
				"assigneeName",
				"analysisRequestedAt",
				"priority",
				"gameAnalysisStatus",
				"analysisTurnaround",
				"analysisSla",
				"gameStatus",
				"isVip",
				"isHidden",
				"isHiddenForScout",
				"isDiscover",
				"providerTag",
				"hasVideo",
				"gameHistoriesCount",
				"physicalMetricsRequested",
				"homeIsTeamsheetFilled",
				"slaContent",
				"slaExpansion",
				"numberOfContentErrors",
				"numberOfTaggingErrors",
				"owners",
			])
			.describe("Sorting field"),
		direction: z.enum(["ASC", "DESC"]).describe("Sorting direction"),
	})
	.partial()

	.optional();
export type order__2 = z.infer<typeof order__2>;

export const AgeCategoryEnum = z.enum([
	"U6",
	"U7",
	"U8",
	"U9",
	"U10",
	"U11",
	"U12",
	"U13",
	"U14",
	"U15",
	"U16",
	"U17",
	"U18",
	"U19",
	"U20",
	"U21",
	"U23",
	"SENIOR",
	"N2",
	"N3",
]);
export type AgeCategoryEnum = z.infer<typeof AgeCategoryEnum>;

export const DiscoverGameTeamResponse = z.object({
	id: z.number().int(),
	name: z.string(),
	country: SomeItemResponse.nullable(),
});
export type DiscoverGameTeamResponse = z.infer<typeof DiscoverGameTeamResponse>;

export const ClubType = z.enum(["partner", "nonpartner", "discover"]);
export type ClubType = z.infer<typeof ClubType>;

export const DiscoverGameClubResponse = z.object({
	id: z.number().int(),
	name: z.string(),
	clubType: ClubType,
	country: SomeItemResponse.nullable(),
});
export type DiscoverGameClubResponse = z.infer<typeof DiscoverGameClubResponse>;

export const PositionEnum = z.enum([
	"G",
	"DD",
	"DC",
	"DG",
	"MDC",
	"MC",
	"MOC",
	"MG",
	"MD",
	"MR",
	"ATT",
	"AG",
	"AD",
]);
export type PositionEnum = z.infer<typeof PositionEnum>;

export const GenderEnum = z.enum(["Male", "Female", "Mixed"]);
export type GenderEnum = z.infer<typeof GenderEnum>;

export const DiscoverGamePlayerResponse = z.object({
	id: z.number().int().nullable(),
	name: z.string(),
	gender: GenderEnum.nullable(),
	playerData: SomeItemResponse.nullable(),
});
export type DiscoverGamePlayerResponse = z.infer<
	typeof DiscoverGamePlayerResponse
>;

export const DiscoverGameGamePlayerResponse = z.object({
	id: z.number().int(),
	numberSign: z.number().int().nullable(),
	position: PositionEnum.nullable(),
	isSubstitution: z.boolean(),
	player: DiscoverGamePlayerResponse,
});
export type DiscoverGameGamePlayerResponse = z.infer<
	typeof DiscoverGameGamePlayerResponse
>;

export const DiscoverGameGameTeamResponse = z.object({
	id: z.number().int(),
	score: z.number().int().nullable(),
	colorShirt: z.string().nullable(),
	colorShort: z.string().nullable(),
	isTeamsheetFilled: z.boolean().nullable(),
	playersCount: z.number().int(),
	teamName: z.string().nullable(),
	clubName: z.string().nullable(),
	team: DiscoverGameTeamResponse.nullable(),
	club: DiscoverGameClubResponse.nullable(),
	gamePlayers: z.array(DiscoverGameGamePlayerResponse).optional().default([]),
});
export type DiscoverGameGameTeamResponse = z.infer<
	typeof DiscoverGameGameTeamResponse
>;

export const DiscoverGameDressKitResponse = z.object({
	id: z.number().int(),
	name: z.string(),
	shirtColorPrimary: z.string(),
	shirtColorSecondary: z.string().nullish(),
	shortsColor: z.string(),
});
export type DiscoverGameDressKitResponse = z.infer<
	typeof DiscoverGameDressKitResponse
>;

export const DiscoverGameVideoResponse = z.object({
	id: z.number().int(),
	mp4AvailableAt: z.string().datetime({ offset: true }).nullable(),
	streamAvailableAt: z.string().datetime({ offset: true }).nullable(),
});
export type DiscoverGameVideoResponse = z.infer<
	typeof DiscoverGameVideoResponse
>;

export const ProcessingLevelEnum = z.enum(["full", "limited"]);
export type ProcessingLevelEnum = z.infer<typeof ProcessingLevelEnum>;

export const GameAnalysisStatusEnum = z.enum([
	"Requested",
	"In-Progress",
	"Review",
	"Done",
	"Investigation",
	"Technical Review",
	"Rejected",
]);
export type GameAnalysisStatusEnum = z.infer<typeof GameAnalysisStatusEnum>;

export const DiscoverGameGameAnalysisResponse = z.object({
	id: z.number().int(),
	provider: z.string().nullable(),
	isInProgress: z.boolean(),
	priority: z.number().int(),
	status: GameAnalysisStatusEnum.nullable(),
	statusDetailed: z.string().nullable(),
	statusDetailedStage: z.string().nullable(),
	anomalies: z.number().int(),
	createdAt: z.string().datetime({ offset: true }).nullable(),
	requestedAt: z.string().datetime({ offset: true }).nullable(),
	firstAvailableAt: z.string().datetime({ offset: true }).nullable(),
	analyzedAt: z.string().datetime({ offset: true }).nullable(),
	finalAt: z.string().datetime({ offset: true }).nullable(),
	resyncAfterUtc: z.string().datetime({ offset: true }).nullable(),
	requestedBy: SomeItemResponse.nullable(),
	createdBy: SomeItemResponse.nullable(),
	skipReview: z.boolean(),
	resync: z.boolean(),
	syncLocally: z.boolean(),
	skipStages: z.string().nullable(),
	resyncOptions: z.string().nullable(),
	processingLevel: ProcessingLevelEnum,
	swapped: z.boolean().nullable(),
});
export type DiscoverGameGameAnalysisResponse = z.infer<
	typeof DiscoverGameGameAnalysisResponse
>;

export const DiscoverGameResponse = z.object({
	id: z.number().int(),
	ageCategory: AgeCategoryEnum.nullable(),
	description: z.string().nullable(),
	type: z.string().nullable(),
	date: z.string().datetime({ offset: true }).nullable(),
	home: DiscoverGameGameTeamResponse,
	away: DiscoverGameGameTeamResponse,
	gameDressKit: DiscoverGameDressKitResponse.nullish(),
	video: DiscoverGameVideoResponse.nullable(),
	isVip: z.boolean(),
	isHidden: z.boolean(),
	isHiddenForScout: z.boolean(),
	providerTag: z.string().nullable(),
	isAnalysisFinished: z.boolean().nullable(),
	isAnalysisApproved: z.boolean().nullable(),
	analysisNotApprovedReason: z.string().nullable(),
	isFrozen: z.boolean().nullable(),
	isDiscover: z.boolean(),
	analysisRequestedAt: z.string().datetime({ offset: true }).nullable(),
	homeTeamStartsOnLeft: z.boolean().nullable(),
	requestedProcessingLevel: ProcessingLevelEnum.nullable(),
	league: SomeItemResponse.nullable(),
	owners: z.array(SomeItemResponse),
	gameAnalysis: z.array(DiscoverGameGameAnalysisResponse).default([]),
	currentGameAnalysis: DiscoverGameGameAnalysisResponse.nullish().default(null),
	gender: z.string().nullable().default(null),
	veoLink: z.string().nullable().default(null),
});
export type DiscoverGameResponse = z.infer<typeof DiscoverGameResponse>;

export const DiscoverGamesResponse = z.object({
	page: z.number().int().default(0),
	limit: z.number().int().default(0),
	pages: z.number().int().default(0),
	total: z.number().int().default(0),
	items: z.array(DiscoverGameResponse).default([]),
});
export type DiscoverGamesResponse = z.infer<typeof DiscoverGamesResponse>;

export const filters__3 = z
	.array(
		z
			.object({
				type: z
					.enum([
						"exact",
						"search",
						"boolean",
						"exists",
						"inarray",
						"datetimerange",
						"numberrange",
					])
					.describe("Type of filter"),
				field: z
					.enum([
						"id",
						"date",
						"createdAt",
						"ageCategory",
						"homeClub",
						"homeTeam",
						"awayClub",
						"awayTeam",
						"anyClubId",
						"anyTeamId",
						"playerId",
						"isVip",
						"isHidden",
						"isHiddenForScout",
						"isDiscover",
						"providerTag",
						"hasVideo",
						"analysisRequestedAt",
						"gameAnalysisStatus",
						"priority",
						"gameStatus",
						"requestedProcessingLevel",
						"owner",
						"country",
						"clubManager",
						"contentManager",
						"assignee",
					])
					.describe("Field to filter on"),
				value: z
					.union([
						z.string(),
						z.number(),
						z.boolean(),
						z.array(z.union([z.string(), z.number()])),
						z
							.object({
								start: z.string().datetime({ offset: true }),
								end: z.string().datetime({ offset: true }),
							})
							.partial(),
					])
					.describe(
						"Filter value (string, integer, boolean, string[] for inarray multiselect, or date range object)",
					),
			})
			.partial(),
	)
	.describe("Filter criteria")
	.optional();
export type filters__3 = z.infer<typeof filters__3>;

export const order__3 = z
	.object({
		field: z
			.enum([
				"id",
				"date",
				"createdAt",
				"ageCategory",
				"homeClubCountry",
				"homeClubName",
				"homeTeamName",
				"awayClubName",
				"awayTeamName",
				"clubManagerName",
				"contentManagerName",
				"assigneeName",
				"analysisRequestedAt",
				"priority",
				"gameAnalysisStatus",
				"analysisTurnaround",
				"analysisSla",
				"gameStatus",
				"isVip",
				"isHidden",
				"isHiddenForScout",
				"isDiscover",
				"providerTag",
				"hasVideo",
				"gameHistoriesCount",
				"physicalMetricsRequested",
				"homeIsTeamsheetFilled",
				"slaContent",
				"slaExpansion",
				"numberOfContentErrors",
				"numberOfTaggingErrors",
				"owners",
			])
			.describe("Field to sort by"),
		direction: z.enum(["ASC", "DESC"]).describe("Sort direction"),
	})
	.partial()

	.describe("Sort configuration")
	.optional();
export type order__3 = z.infer<typeof order__3>;

export const DynamicDataGridResponse = z.object({
	page: z.number().int().describe("Current page number").default(0),
	limit: z.number().int().describe("Items per page").default(0),
	pages: z.number().int().describe("Total number of pages").default(0),
	total: z.number().int().describe("Total items matching filters").default(0),
	items: z
		.array(
			z.record(
				z
					.union([
						z.boolean(),
						z.string(),
						z.number(),
						z.array(z.record(z.union([z.string(), z.number(), z.boolean()]))),
					])
					.nullable(),
			),
		)
		.describe(
			"Result items. Column values are mostly scalars; some columns (e.g. `owners`) are arrays of objects.",
		)
		.default([]),
});
export type DynamicDataGridResponse = z.infer<typeof DynamicDataGridResponse>;

export const DiscoverGamesOwnerFilterOptionResponse = z.object({
	value: z.string(),
	label: z.string(),
});
export type DiscoverGamesOwnerFilterOptionResponse = z.infer<
	typeof DiscoverGamesOwnerFilterOptionResponse
>;

export const DiscoverProgramGameClubOptionResponse = z.object({
	value: z.number().int(),
	label: z.string(),
});
export type DiscoverProgramGameClubOptionResponse = z.infer<
	typeof DiscoverProgramGameClubOptionResponse
>;

export const DiscoverProgramGameNationalLeagueOptionResponse = z.object({
	value: z.number().int(),
	label: z.string(),
});
export type DiscoverProgramGameNationalLeagueOptionResponse = z.infer<
	typeof DiscoverProgramGameNationalLeagueOptionResponse
>;

export const DiscoverProgramGamePostRequest = z.object({
	homeClubId: z.number().int().gt(0),
	homeTeamId: z.number().int().gt(0).nullish(),
	awayClubId: z.number().int().gt(0),
	awayTeamId: z.number().int().gt(0).nullish(),
	gameDate: z.string().regex(/\d{4}-\d{2}-\d{2}/),
	kickOffHour: z.string().regex(/.*(^([01]\d|2[0-3])$).*/),
	kickOffMinute: z.string().regex(/[0-5]\d/),
	isNational: z.boolean(),
	type: z.string(),
	gameOwnerIds: z.array(z.number().int()),
	priority: z.number().int().gte(0).lte(10),
	teamsheetLinks: z.array(z.string()).nullish().default(null),
	videoLinks: z.array(z.string()).nullish().default(null),
	gender: z.enum(["Male", "Female", "Mixed"]).nullish().default(null),
	nationalLeagueId: z.number().int().gt(0).nullish().default(null),
});
export type DiscoverProgramGamePostRequest = z.infer<
	typeof DiscoverProgramGamePostRequest
>;

export const DiscoverGamesBulkFieldEligibility = z.object({
	eligible: z.array(z.number().int()),
	ineligible: z.array(z.number().int()),
});
export type DiscoverGamesBulkFieldEligibility = z.infer<
	typeof DiscoverGamesBulkFieldEligibility
>;

export const DiscoverGamesBulkEligibilityResponse = z.object({
	total: z.number().int(),
	clubManager: DiscoverGamesBulkFieldEligibility,
	contentManager: DiscoverGamesBulkFieldEligibility,
	assignee: DiscoverGamesBulkFieldEligibility,
	priority: DiscoverGamesBulkFieldEligibility,
	gameStatus: DiscoverGamesBulkFieldEligibility,
});
export type DiscoverGamesBulkEligibilityResponse = z.infer<
	typeof DiscoverGamesBulkEligibilityResponse
>;

export const DiscoverGamesBulkPatchRequest = z
	.object({
		ids: z.array(z.number().int()).min(1).max(50).default([]),
		applyClubManager: z.boolean().default(false),
		clubManagerId: z.number().int().nullable(),
		applyContentManager: z.boolean().default(false),
		contentManagerId: z.number().int().nullable(),
		applyAssignee: z.boolean().default(false),
		assigneeId: z.number().int().nullable(),
		applyPriority: z.boolean().default(false),
		priority: z.number().int().nullable(),
		applyGameStatus: z.boolean().default(false),
		gameStatus: z.string().nullable(),
	})
	.partial();
export type DiscoverGamesBulkPatchRequest = z.infer<
	typeof DiscoverGamesBulkPatchRequest
>;

export const DiscoverGamesBulkPatchResponse = z.object({
	updated: z.number().int(),
	skipped: z.number().int(),
	skippedPerField: z
		.object({
			assignee: z.number().int(),
			priority: z.number().int(),
			gameStatus: z.number().int(),
		})
		.partial(),
});
export type DiscoverGamesBulkPatchResponse = z.infer<
	typeof DiscoverGamesBulkPatchResponse
>;

export const ColorEnum = z.enum([
	"Black",
	"White",
	"Red",
	"Orange",
	"Yellow",
	"Green",
	"Blue",
	"Gray",
	"Brown",
	"Purple",
]);
export type ColorEnum = z.infer<typeof ColorEnum>;

export const GameStatus = z.enum([
	"ready_for_creation",
	"game_created_with_issues",
	"done",
	"waiting_for_video",
	"waiting_for_teamsheet",
	"new",
	"cancelled",
	"information_provided",
	"created_by_coach",
	"created_by_api",
]);
export type GameStatus = z.infer<typeof GameStatus>;

export const DiscoverGamePatchRequest = z.object({
	id: z.number().int(),
	isVip: z.boolean().nullish(),
	isHidden: z.boolean().nullish(),
	isHiddenForScout: z.boolean().nullish(),
	homeTeamStartsOnLeft: z.boolean().nullish(),
	homeColorShirt: ColorEnum.nullish(),
	homeColorShort: ColorEnum.nullish(),
	awayColorShirt: ColorEnum.nullish(),
	awayColorShort: ColorEnum.nullish(),
	gender: GenderEnum.nullish(),
	clubManagerId: z.number().int().nullish(),
	contentManagerId: z.number().int().nullish(),
	assigneeId: z.number().int().nullish(),
	priority: z.number().int().nullish(),
	gameStatus: GameStatus.nullish(),
	date: z.string().nullish(),
	homeScore: z.number().int().gte(0).nullish(),
	awayScore: z.number().int().gte(0).nullish(),
	type: z
		.enum(["championnat", "amical", "coupe", "eightaside", "poletest"])
		.nullish(),
	leagueId: z.number().int().gt(0).nullish(),
	ageCategory: AgeCategoryEnum.nullish(),
	ownerIds: z.array(z.number().int()).nullish(),
	requestedFields: z.array(z.string()).optional().default([]),
});
export type DiscoverGamePatchRequest = z.infer<typeof DiscoverGamePatchRequest>;

export const DiscoverGameLinkItemResponse = z.object({
	id: z.number().int(),
	link: z.string(),
});
export type DiscoverGameLinkItemResponse = z.infer<
	typeof DiscoverGameLinkItemResponse
>;

export const DiscoverGameV2Response = z.object({
	id: z.number().int(),
	ageCategory: AgeCategoryEnum.nullable(),
	description: z.string().nullable(),
	type: z.string().nullable(),
	date: z.string().datetime({ offset: true }).nullable(),
	createdAt: z.string().datetime({ offset: true }).nullable(),
	createdBy: SomeItemResponse.nullish(),
	home: DiscoverGameGameTeamResponse,
	away: DiscoverGameGameTeamResponse,
	gameDressKit: DiscoverGameDressKitResponse.nullish(),
	video: DiscoverGameVideoResponse.nullable(),
	isVip: z.boolean(),
	isHidden: z.boolean(),
	isHiddenForScout: z.boolean(),
	providerTag: z.string().nullable(),
	isAnalysisFinished: z.boolean().nullable(),
	isAnalysisApproved: z.boolean().nullable(),
	analysisNotApprovedReason: z.string().nullable(),
	isFrozen: z.boolean().nullable(),
	isDiscover: z.boolean(),
	analysisRequestedAt: z.string().datetime({ offset: true }).nullable(),
	homeTeamStartsOnLeft: z.boolean().nullable(),
	requestedProcessingLevel: ProcessingLevelEnum.nullable(),
	league: SomeItemResponse.nullable(),
	owners: z.array(SomeItemResponse),
	gameAnalysis: z.array(DiscoverGameGameAnalysisResponse).default([]),
	currentGameAnalysis: DiscoverGameGameAnalysisResponse.nullish().default(null),
	gameStatus: z.string().nullable().default(null),
	priority: z.number().int().nullable().default(null),
	countryManager: SomeItemResponse.nullish().default(null),
	contentManager: SomeItemResponse.nullish().default(null),
	assignee: SomeItemResponse.nullish().default(null),
	slaContent: z.number().int().nullable().default(null),
	slaExpansion: z.number().int().nullable().default(null),
	numberOfContentErrors: z.number().int().default(0),
	numberOfTaggingErrors: z.number().int().default(0),
	teamsheetLinks: z.array(DiscoverGameLinkItemResponse).default([]),
	videoLinks: z.array(DiscoverGameLinkItemResponse).default([]),
	veoLink: z.string().nullable().default(null),
});
export type DiscoverGameV2Response = z.infer<typeof DiscoverGameV2Response>;

export const DiscoverGameMetadataOptionResponse = z.object({
	value: z.union([z.number(), z.string()]),
	label: z.string(),
});
export type DiscoverGameMetadataOptionResponse = z.infer<
	typeof DiscoverGameMetadataOptionResponse
>;

export const DiscoverGameMetadataOptionsResponse = z.object({
	gameTypes: z.array(DiscoverGameMetadataOptionResponse),
	leagues: z.array(DiscoverGameMetadataOptionResponse),
	ageCategories: z.array(DiscoverGameMetadataOptionResponse),
	owners: z.array(DiscoverGameMetadataOptionResponse),
});
export type DiscoverGameMetadataOptionsResponse = z.infer<
	typeof DiscoverGameMetadataOptionsResponse
>;

export const DiscoverGameAnalysisDenyPatchRequest = z
	.object({ reason: z.string().nullable() })
	.partial();
export type DiscoverGameAnalysisDenyPatchRequest = z.infer<
	typeof DiscoverGameAnalysisDenyPatchRequest
>;

export const GameHistoryNoteAuthorResponse = z.object({
	id: z.number().int(),
	name: z.string(),
	photo: z.string().nullish().default(null),
});
export type GameHistoryNoteAuthorResponse = z.infer<
	typeof GameHistoryNoteAuthorResponse
>;

export const GameHistoryNoteFileResponse = z.object({
	id: z.number().int(),
	filename: z.string(),
	url: z.string(),
});
export type GameHistoryNoteFileResponse = z.infer<
	typeof GameHistoryNoteFileResponse
>;

export const GameHistoryLogSnapshotResponse = z
	.object({
		value: z.string().nullable().default(null),
		userId: z.string().nullable().default(null),
		fullName: z.string().nullable().default(null),
		email: z.string().nullable().default(null),
	})
	.partial();
export type GameHistoryLogSnapshotResponse = z.infer<
	typeof GameHistoryLogSnapshotResponse
>;

export const GameHistoryLogPayloadResponse = z.object({
	field: z.string(),
	actionScope: z.string().nullish(),
	old: GameHistoryLogSnapshotResponse,
	new: GameHistoryLogSnapshotResponse,
});
export type GameHistoryLogPayloadResponse = z.infer<
	typeof GameHistoryLogPayloadResponse
>;

export const GameHistoryNoteResponse = z.object({
	id: z.number().int(),
	type: z.enum(["internal", "log"]),
	note: z.string().nullish(),
	createdBy: GameHistoryNoteAuthorResponse.nullish(),
	createdAt: z.string(),
	files: z.array(GameHistoryNoteFileResponse),
	log: GameHistoryLogPayloadResponse.nullish().default(null),
});
export type GameHistoryNoteResponse = z.infer<typeof GameHistoryNoteResponse>;

export const GameHistoryNotesListResponse = z.object({
	items: z.array(GameHistoryNoteResponse),
	total: z.number().int(),
	page: z.number().int(),
	limit: z.number().int(),
});
export type GameHistoryNotesListResponse = z.infer<
	typeof GameHistoryNotesListResponse
>;

export const GameHistoryNotePostRequest = z
	.object({
		note: z
			.string()
			.describe(
				"Note text content (optional when at least one file is attached)",
			)
			.default(""),
		files: z
			.array(z.string())
			.max(15)
			.describe("Base64-encoded file contents")
			.default([]),
	})
	.partial();
export type GameHistoryNotePostRequest = z.infer<
	typeof GameHistoryNotePostRequest
>;

export const GameHistoryNotePatchRequest = z.object({
	note: z.string().describe("Updated note text"),
	files: z
		.array(z.string())
		.describe("Base64-encoded file contents to add")
		.optional()
		.default([]),
	deletedFileIds: z
		.array(z.number().int())
		.describe("IDs of existing files to remove")
		.optional()
		.default([]),
});
export type GameHistoryNotePatchRequest = z.infer<
	typeof GameHistoryNotePatchRequest
>;

export const GameLinkKind = z.enum(["teamsheet", "video"]);
export type GameLinkKind = z.infer<typeof GameLinkKind>;

export const DiscoverGameLinkCreateRequest = z.object({
	kind: GameLinkKind,
	link: z.string(),
});
export type DiscoverGameLinkCreateRequest = z.infer<
	typeof DiscoverGameLinkCreateRequest
>;

export const DiscoverGameLinkPatchRequest = z.object({ link: z.string() });
export type DiscoverGameLinkPatchRequest = z.infer<
	typeof DiscoverGameLinkPatchRequest
>;

export const filters__4 = z
	.array(
		z
			.object({
				type: z
					.enum([
						"exact",
						"search",
						"boolean",
						"exists",
						"inarray",
						"datetimerange",
						"numberrange",
					])
					.describe("Type of filter"),
				field: z
					.enum([
						"id",
						"playerData",
						"gender",
						"name",
						"firstName",
						"lastName",
						"membershipName",
						"club",
						"team",
						"playerType",
						"clubType",
						"position",
						"isVip",
					])
					.describe("Property to filter"),
				value: z
					.union([
						z.string(),
						z.number(),
						z.boolean(),
						z
							.object({
								start: z.string().datetime({ offset: true }),
								end: z.string().datetime({ offset: true }),
							})
							.partial(),
					])
					.describe(
						"Filter value, can be a string, integer, date or a date range",
					),
			})
			.partial(),
	)
	.optional();
export type filters__4 = z.infer<typeof filters__4>;

export const order__4 = z
	.object({
		field: z.enum(["id", "firstName", "lastName"]).describe("Sorting field"),
		direction: z.enum(["ASC", "DESC"]).describe("Sorting direction"),
	})
	.partial()

	.optional();
export type order__4 = z.infer<typeof order__4>;

export const PlayerType = z.enum(["partner", "nonpartner", "discover"]);
export type PlayerType = z.infer<typeof PlayerType>;

export const DiscoverPlayerResponse = z.object({
	id: z.number().int(),
	isDummy: z.boolean(),
	firstName: z.string().nullable(),
	lastName: z.string().nullable(),
	fullName: z.string().nullable(),
	gender: GenderEnum.nullable(),
	position: PositionEnum.nullable(),
	numberSign: z.number().int().nullable(),
	club: SomeItemResponse.nullable(),
	team: SomeItemResponse.nullable(),
	playerType: PlayerType,
	playerData: SomeItemResponse,
	isPlayerDataOwner: z.boolean(),
	isVip: z.boolean(),
	nationality: z.string().nullish(),
	nationality2: z.string().nullish(),
	tranLink: z.string().nullish(),
	federationLink: z.string().nullish(),
	strongestFoot: z.string().nullish(),
	birthday: z.string().datetime({ offset: true }).nullish(),
	playedInNationalTeam: z.boolean(),
	height: z.number().int().nullish(),
	weight: z.string().nullish(),
	photo: z.string().nullish(),
	picture: z.string().nullish(),
	userPermissions: z.array(UserPermissionItemResponse),
});
export type DiscoverPlayerResponse = z.infer<typeof DiscoverPlayerResponse>;

export const DiscoverPlayerDataResponse = z.object({
	id: z.number().int(),
	firstName: z.string().nullable(),
	lastName: z.string().nullable(),
	fullName: z.string(),
	gender: GenderEnum.nullable(),
	nationality: z.string().nullable(),
	nationality2: z.string().nullable(),
	tranLink: z.string().nullable(),
	federationLink: z.string().nullable(),
	strongestFoot: z.string().nullable(),
	birthday: z.string().datetime({ offset: true }).nullable(),
	playedInNationalTeam: z.boolean(),
	height: z.number().int().nullable(),
	weight: z.string().nullable(),
	photo: z.string().nullable(),
	picture: z.string().nullable(),
	players: z.array(DiscoverPlayerResponse),
});
export type DiscoverPlayerDataResponse = z.infer<
	typeof DiscoverPlayerDataResponse
>;

export const DiscoverPlayersDataResponse = z.object({
	page: z.number().int().default(0),
	limit: z.number().int().default(0),
	pages: z.number().int().default(0),
	total: z.number().int().default(0),
	items: z.array(DiscoverPlayerDataResponse).default([]),
});
export type DiscoverPlayersDataResponse = z.infer<
	typeof DiscoverPlayersDataResponse
>;

export const DiscoverPlayerDataPatchRequest = z.object({
	id: z.number().int(),
	firstName: z.string().max(255).nullish(),
	lastName: z.string().max(255).nullish(),
	strongestFoot: z.string().nullish(),
	gender: GenderEnum.nullish(),
	height: z.number().int().nullish(),
	weight: z.string().nullish(),
	nationality: z.string().nullish(),
	nationality2: z.string().nullish(),
	playedInNationalTeam: z.boolean().nullish(),
	birthday: z.string().datetime({ offset: true }).nullish(),
	tranLink: z.string().nullish(),
	federationLink: z.string().nullish(),
	requestedFields: z.array(z.string()).optional().default([]),
});
export type DiscoverPlayerDataPatchRequest = z.infer<
	typeof DiscoverPlayerDataPatchRequest
>;

export const DiscoverPlayerDataUpdateEtlResponse = z.object({
	gamesQueued: z
		.number()
		.int()
		.describe("Distinct discover games queued for data search ETL"),
});
export type DiscoverPlayerDataUpdateEtlResponse = z.infer<
	typeof DiscoverPlayerDataUpdateEtlResponse
>;

export const filters__5 = z
	.array(
		z
			.object({
				type: z
					.enum([
						"exact",
						"search",
						"boolean",
						"exists",
						"inarray",
						"datetimerange",
						"numberrange",
					])
					.describe("Type of filter"),
				field: z
					.enum([
						"id",
						"playerData",
						"name",
						"club",
						"team",
						"teamId",
						"entityType",
						"position",
						"numberSign",
						"isDummy",
					])
					.describe("Property to filter"),
				value: z
					.union([
						z.string(),
						z.number(),
						z.boolean(),
						z
							.object({
								start: z.string().datetime({ offset: true }),
								end: z.string().datetime({ offset: true }),
							})
							.partial(),
					])
					.describe(
						"Filter value, can be a string, integer, date or a date range",
					),
			})
			.partial(),
	)
	.optional();
export type filters__5 = z.infer<typeof filters__5>;

export const order__5 = z
	.object({
		field: z
			.enum(["id", "playerData", "createdAt", "name", "numberSign"])
			.describe("Sorting field"),
		direction: z.enum(["ASC", "DESC"]).describe("Sorting direction"),
	})
	.partial()

	.optional();
export type order__5 = z.infer<typeof order__5>;

export const DiscoverPlayersRowResponse = z.object({
	id: z.number().int().default(0),
	playerDataId: z.number().int().default(0),
	name: z.string().nullable(),
	position: z.string().nullable(),
	numberSign: z.number().int().nullable(),
	team: SomeItemResponse.nullable(),
	club: SomeItemResponse.nullable(),
	isDummy: z.boolean().default(true),
	isPlayerDataOwner: z.boolean().default(false),
	entityType: z.string(),
});
export type DiscoverPlayersRowResponse = z.infer<
	typeof DiscoverPlayersRowResponse
>;

export const DiscoverPlayersResponse = z.object({
	page: z.number().int().default(0),
	limit: z.number().int().default(0),
	pages: z.number().int().default(0),
	total: z.number().int().default(0),
	items: z.array(DiscoverPlayersRowResponse).default([]),
});
export type DiscoverPlayersResponse = z.infer<typeof DiscoverPlayersResponse>;

export const DiscoverPlayerPatchRequest = z.object({
	id: z.number().int(),
	firstName: z.string().max(255).nullish(),
	lastName: z.string().max(255).nullish(),
	position: PositionEnum.nullish(),
	gender: GenderEnum.nullish(),
	numberSign: z.number().int().nullish(),
	isPlayerDataOwner: z.boolean().nullish(),
	isVip: z.boolean().nullish(),
	requestedFields: z.array(z.string()).optional().default([]),
});
export type DiscoverPlayerPatchRequest = z.infer<
	typeof DiscoverPlayerPatchRequest
>;

export const filters__6 = z
	.array(
		z
			.object({
				type: z
					.enum([
						"exact",
						"search",
						"boolean",
						"exists",
						"inarray",
						"datetimerange",
						"numberrange",
					])
					.describe("Type of filter"),
				field: z
					.enum([
						"id",
						"user",
						"name",
						"phoneNumber",
						"email",
						"clubName",
						"role",
						"country",
						"userId",
					])
					.describe("Property to filter"),
				value: z
					.union([
						z.string(),
						z.number(),
						z.boolean(),
						z
							.object({
								start: z.string().datetime({ offset: true }),
								end: z.string().datetime({ offset: true }),
							})
							.partial(),
					])
					.describe(
						"Filter value, can be a string, integer, date or a date range",
					),
			})
			.partial(),
	)
	.optional();
export type filters__6 = z.infer<typeof filters__6>;

export const order__6 = z
	.object({
		field: z.literal("createdAt").describe("Sorting field"),
		direction: z.enum(["ASC", "DESC"]).describe("Sorting direction"),
	})
	.partial()

	.optional();
export type order__6 = z.infer<typeof order__6>;

export const DiscoverSignUpsRowResponse = z
	.object({
		id: z.number().int().default(0),
		firstName: z.string().default(""),
		middleName: z.string().nullable(),
		lastName: z.string().default(""),
		email: z.string().nullable(),
		phoneNumber: z.string().nullable(),
		userId: z.string().nullable(),
		userName: z.string().nullable(),
		language: z.string().default(""),
		country: z.string().default(""),
		role: z.string().default(""),
		clubName: z.string().nullable().default(""),
		birthday: z.string().datetime({ offset: true }).nullable(),
		createdAt: z.string().datetime({ offset: true }).nullable(),
	})
	.partial();
export type DiscoverSignUpsRowResponse = z.infer<
	typeof DiscoverSignUpsRowResponse
>;

export const DiscoverSignUpsResponse = z.object({
	page: z.number().int().default(0),
	limit: z.number().int().default(0),
	pages: z.number().int().default(0),
	total: z.number().int().default(0),
	items: z.array(DiscoverSignUpsRowResponse).default([]),
});
export type DiscoverSignUpsResponse = z.infer<typeof DiscoverSignUpsResponse>;

export const SecurityCodeResponse = z.object({
	id: z.number().int(),
	code: z.string(),
	recipient: z.string(),
	createdAt: z.string().datetime({ offset: true }),
	expiresAt: z.string().datetime({ offset: true }),
	type: z.string(),
});
export type SecurityCodeResponse = z.infer<typeof SecurityCodeResponse>;

export const DiscoverSignUpGetResponse = z.object({
	id: z.number().int(),
	email: z.string().nullable(),
	phoneNumber: z.string().nullable(),
	firstName: z.string(),
	middleName: z.string().nullable(),
	lastName: z.string(),
	birthday: z.string().datetime({ offset: true }).nullable(),
	clubName: z.string().nullable(),
	language: z.string(),
	country: SomeItemResponse,
	role: z.string(),
	createdAt: z.string().datetime({ offset: true }).nullable(),
	verifiedAt: z.string().datetime({ offset: true }).nullable(),
	user: SomeItemResponse.nullable(),
	securityCodes: z.array(SecurityCodeResponse),
});
export type DiscoverSignUpGetResponse = z.infer<
	typeof DiscoverSignUpGetResponse
>;

export const DiscoverSignUpPatchRequest = z.object({
	id: z.number().int(),
	email: z.string().nullish(),
	requestedFields: z.array(z.string()).optional().default([]),
});
export type DiscoverSignUpPatchRequest = z.infer<
	typeof DiscoverSignUpPatchRequest
>;

export const DiscoverSubscriptionProductResponse = z.object({
	id: z.number().int(),
	name: z.string(),
	tokenPrice: z.number().int(),
});
export type DiscoverSubscriptionProductResponse = z.infer<
	typeof DiscoverSubscriptionProductResponse
>;

export const DiscoverSubscriptionProductsResponse = z.object({
	items: z.array(DiscoverSubscriptionProductResponse).default([]),
});
export type DiscoverSubscriptionProductsResponse = z.infer<
	typeof DiscoverSubscriptionProductsResponse
>;

export const filters__7 = z
	.array(
		z
			.object({
				type: z
					.enum([
						"exact",
						"search",
						"boolean",
						"exists",
						"inarray",
						"datetimerange",
						"numberrange",
					])
					.describe("Type of filter"),
				field: z
					.enum([
						"id",
						"teamId",
						"clubId",
						"teamName",
						"productId",
						"productName",
						"tokenPrice",
						"isFree",
						"status",
						"renewalStatus",
						"expirationAt",
						"subscriberType",
						"comment",
					])
					.describe("Property to filter"),
				value: z
					.union([
						z.string(),
						z.number(),
						z.boolean(),
						z
							.object({
								start: z.string().datetime({ offset: true }),
								end: z.string().datetime({ offset: true }),
							})
							.partial(),
					])
					.describe(
						"Filter value, can be a string, integer, date or a date range",
					),
			})
			.partial(),
	)
	.optional();
export type filters__7 = z.infer<typeof filters__7>;

export const order__7 = z
	.object({
		field: z
			.enum([
				"id",
				"teamName",
				"productName",
				"tokenPrice",
				"isFree",
				"status",
				"renewalStatus",
				"isActive",
				"startAt",
				"expirationAt",
				"subscriberType",
				"comment",
				"ageCategory",
				"registeredPlayers",
			])
			.describe("Sorting field"),
		direction: z.enum(["ASC", "DESC"]).describe("Sorting direction"),
	})
	.partial()

	.optional();
export type order__7 = z.infer<typeof order__7>;

export const DiscoverSubscriptionRowResponse = z.object({
	id: z.number().int(),
	teamId: z.number().int(),
	teamName: z.string(),
	clubId: z.number().int().nullable(),
	clubName: z.string().nullable(),
	productId: z.number().int(),
	productName: z.string(),
	tokenPrice: z.number().int(),
	isFree: z.boolean(),
	renewalStatus: z.string(),
	status: z.string(),
	startDate: z.string().datetime({ offset: true }),
	expirationDate: z.string().datetime({ offset: true }),
	isActive: z.boolean(),
	subscriberType: z.string(),
	comment: z.string().nullable(),
	registeredPlayers: z.number().int().nullish(),
	ageCategory: z.string().nullish(),
	teamLogo: z.string().nullish(),
});
export type DiscoverSubscriptionRowResponse = z.infer<
	typeof DiscoverSubscriptionRowResponse
>;

export const DiscoverSubscriptionsResponse = z.object({
	page: z.number().int().default(0),
	limit: z.number().int().default(0),
	pages: z.number().int().default(0),
	total: z.number().int().default(0),
	tokensSum: z.number().int().default(0),
	tokensRenewal: z.number().int().default(0),
	tokensSufficient: z.boolean().default(false),
	product: DiscoverSubscriptionProductResponse.nullish().default(null),
	items: z.array(DiscoverSubscriptionRowResponse).default([]),
});
export type DiscoverSubscriptionsResponse = z.infer<
	typeof DiscoverSubscriptionsResponse
>;

export const filters__8 = z
	.array(
		z
			.object({
				type: z
					.enum([
						"exact",
						"search",
						"boolean",
						"exists",
						"inarray",
						"datetimerange",
						"numberrange",
					])
					.describe("Type of filter"),
				field: z
					.enum([
						"id",
						"name",
						"club",
						"ageCategory",
						"createdAt",
						"entityType",
						"country",
						"city",
					])
					.describe("Property to filter"),
				value: z
					.union([
						z.string(),
						z.number(),
						z.boolean(),
						z
							.object({
								start: z.string().datetime({ offset: true }),
								end: z.string().datetime({ offset: true }),
							})
							.partial(),
					])
					.describe(
						"Filter value, can be a string, integer, date or a date range",
					),
			})
			.partial(),
	)
	.optional();
export type filters__8 = z.infer<typeof filters__8>;

export const order__8 = z
	.object({
		field: z
			.enum(["id", "createdAt", "name", "country", "city", "club"])
			.describe("Sorting field"),
		direction: z.enum(["ASC", "DESC"]).describe("Sorting direction"),
	})
	.partial()

	.optional();
export type order__8 = z.infer<typeof order__8>;

export const DiscoverTeamsRowResponse = z.object({
	id: z.number().int().optional().default(0),
	name: z.string().optional().default(""),
	logo: z.string().nullish(),
	clubId: z.number().int().nullish(),
	clubName: z.string().nullish(),
	country: z.string().nullish(),
	region: z.string().nullish(),
	city: z.string().nullish(),
	ageCategory: AgeCategoryEnum.nullish(),
	createdAt: z.string().datetime({ offset: true }).nullish(),
	entityType: z.string(),
	usersCount: z.number().int().optional().default(0),
});
export type DiscoverTeamsRowResponse = z.infer<typeof DiscoverTeamsRowResponse>;

export const DiscoverTeamsResponse = z.object({
	page: z.number().int().default(0),
	limit: z.number().int().default(0),
	pages: z.number().int().default(0),
	total: z.number().int().default(0),
	items: z.array(DiscoverTeamsRowResponse).default([]),
});
export type DiscoverTeamsResponse = z.infer<typeof DiscoverTeamsResponse>;

export const DiscoverTeamGetResponse = z.object({
	id: z.number().int(),
	name: z.string(),
	club: SomeItemResponse.nullable(),
	isDiscover: z.boolean(),
	country: SomeItemResponse.nullable(),
	city: z.string().nullable(),
	region: z.string().nullable(),
	gender: GenderEnum.nullable(),
	ageCategory: AgeCategoryEnum.nullable(),
	cameraBrand: z.string().nullable(),
	isDiscoverable: z.boolean(),
	hasPlayerAccessToMateHighlights: z.boolean(),
	hasPlayerAccessToMetrics: z.boolean(),
	hasPlayerAccessToRatings: z.boolean(),
	playersCount: z.number().int().nullable(),
	tokenBalance: z.number().int(),
	tokenReadAccess: z.boolean(),
	tokenEditAccess: z.boolean(),
	subscriptionReadAccess: z.boolean(),
	subscriptionEditAccess: z.boolean(),
	teamGroupReadAccess: z.boolean(),
	teamGroupEditAccess: z.boolean(),
	isPlayerAppDisabled: z.boolean(),
	userPermissions: z.array(UserPermissionItemResponse).default([]),
	tokenMovements: z.array(TokenMovementResponse).default([]),
});
export type DiscoverTeamGetResponse = z.infer<typeof DiscoverTeamGetResponse>;

export const DiscoverTeamPatchRequest = z.object({
	id: z.number().int(),
	name: z.string().nullish(),
	ageCategory: AgeCategoryEnum.nullish(),
	gender: GenderEnum.nullish(),
	country: z.string().nullish(),
	region: z.string().nullish(),
	city: z.string().nullish(),
	cameraBrand: z.string().nullish(),
	isDiscoverable: z.boolean().nullish(),
	hasPlayerAccessToMateHighlights: z.boolean().nullish(),
	hasPlayerAccessToMetrics: z.boolean().nullish(),
	hasPlayerAccessToRatings: z.boolean().nullish(),
	playersCount: z.number().int().nullish(),
	isPlayerAppDisabled: z.boolean().nullish(),
	requestedFields: z.array(z.string()).optional().default([]),
});
export type DiscoverTeamPatchRequest = z.infer<typeof DiscoverTeamPatchRequest>;

export const filters__9 = z
	.array(
		z
			.object({
				type: z
					.enum([
						"exact",
						"search",
						"boolean",
						"exists",
						"inarray",
						"datetimerange",
						"numberrange",
					])
					.describe("Type of filter"),
				field: z.literal("user").describe("Property to filter"),
				value: z
					.union([
						z.string(),
						z.number(),
						z.boolean(),
						z
							.object({
								start: z.string().datetime({ offset: true }),
								end: z.string().datetime({ offset: true }),
							})
							.partial(),
					])
					.describe(
						"Filter value, can be a string, integer, date or a date range",
					),
			})
			.partial(),
	)
	.optional();
export type filters__9 = z.infer<typeof filters__9>;

export const DiscoverUserGuardianApprovalsRowResponse = z.object({
	id: z.string(),
	user: SomeItemResponse,
	player: SomeItemResponse.nullable(),
	guardianFullName: z.string().nullable(),
	guardianEmail: z.string().nullable(),
	guardianFilledFullName: z.string().nullable(),
	guardianFilledEmail: z.string().nullable(),
	guardianFilledRole: z.string().nullable(),
	status: z.string(),
	approvedAt: z.string().nullable(),
	createdAt: z.string().nullable(),
	updatedAt: z.string().nullable(),
});
export type DiscoverUserGuardianApprovalsRowResponse = z.infer<
	typeof DiscoverUserGuardianApprovalsRowResponse
>;

export const DiscoverUserGuardianApprovalsResponse = z.object({
	page: z.number().int().default(0),
	limit: z.number().int().default(0),
	pages: z.number().int().default(0),
	total: z.number().int().default(0),
	items: z.array(DiscoverUserGuardianApprovalsRowResponse).default([]),
});
export type DiscoverUserGuardianApprovalsResponse = z.infer<
	typeof DiscoverUserGuardianApprovalsResponse
>;

export const UserNotePostRequest = z.object({
	userId: z.number().int().describe("The user ID"),
	note: z.string().max(65535).describe("The note content"),
});
export type UserNotePostRequest = z.infer<typeof UserNotePostRequest>;

export const StatusMessageDetailResponse = z.object({
	key: z.string(),
	message: z.string(),
});
export type StatusMessageDetailResponse = z.infer<
	typeof StatusMessageDetailResponse
>;

export const StatusMessageResponse = z.object({
	message: z.string().describe("Status message"),
	httpCode: z.number().int().describe("HTTP status code"),
	details: z.array(StatusMessageDetailResponse).describe("Additional details"),
});
export type StatusMessageResponse = z.infer<typeof StatusMessageResponse>;

export const UserNotePatchRequest2 = z.object({
	note: z.string().describe("The note content"),
});
export type UserNotePatchRequest2 = z.infer<typeof UserNotePatchRequest2>;

export const DefaultPolicyName = z.enum([
	"admin",
	"coach",
	"player",
	"parent",
	"works_for_club",
	"member",
]);
export type DefaultPolicyName = z.infer<typeof DefaultPolicyName>;

export const DiscoverUserPermissionPostRequest = z.object({
	entityType: UserPermissionEntityType,
	entityId: z.number().int().gte(1),
	userId: z.number().int().gte(1),
	role: UserPermissionRole,
	policyName: DefaultPolicyName,
});
export type DiscoverUserPermissionPostRequest = z.infer<
	typeof DiscoverUserPermissionPostRequest
>;

export const filters__10 = z
	.array(
		z
			.object({
				type: z
					.enum([
						"exact",
						"search",
						"boolean",
						"exists",
						"inarray",
						"datetimerange",
						"numberrange",
					])
					.describe("Type of filter"),
				field: z
					.enum([
						"id",
						"firstName",
						"middleName",
						"lastName",
						"phoneNumber",
						"email",
						"scope",
						"isActive",
						"isMinor",
					])
					.describe("Property to filter"),
				value: z
					.union([
						z.string(),
						z.number(),
						z.boolean(),
						z
							.object({
								start: z.string().datetime({ offset: true }),
								end: z.string().datetime({ offset: true }),
							})
							.partial(),
					])
					.describe(
						"Filter value, can be a string, integer, date or a date range",
					),
			})
			.partial(),
	)
	.optional();
export type filters__10 = z.infer<typeof filters__10>;

export const order__9 = z
	.object({
		field: z
			.enum([
				"id",
				"createdAt",
				"name",
				"phoneNumber",
				"email",
				"passwordRequestedAt",
			])
			.describe("Sorting field"),
		direction: z.enum(["ASC", "DESC"]).describe("Sorting direction"),
	})
	.partial()

	.optional();
export type order__9 = z.infer<typeof order__9>;

export const DiscoverUserRowResponse = z.object({
	id: z.number().int().optional().default(0),
	firstName: z.string().optional().default(""),
	middleName: z.string().nullish(),
	lastName: z.string().optional().default(""),
	email: z.string().nullish(),
	phoneNumber: z.string().nullish(),
	isActive: z.boolean().optional().default(false),
	birthday: z.string().datetime({ offset: true }).nullish(),
	passwordRequestedAt: z.string().datetime({ offset: true }).nullish(),
	country: z.string().nullish(),
	discoverOrigin: z.boolean().optional().default(false),
	hasDiscover: z.boolean().optional().default(false),
	hasPlatform: z.boolean().optional().default(false),
	isMinor: z.number().int(),
	entityType: z.string(),
});
export type DiscoverUserRowResponse = z.infer<typeof DiscoverUserRowResponse>;

export const DiscoverUsersResponse = z.object({
	page: z.number().int().default(0),
	limit: z.number().int().default(0),
	pages: z.number().int().default(0),
	total: z.number().int().default(0),
	items: z.array(DiscoverUserRowResponse).default([]),
});
export type DiscoverUsersResponse = z.infer<typeof DiscoverUsersResponse>;

export const UserNoteCreatedByResponse = z.object({
	id: z.number().int(),
	firstName: z.string(),
	lastName: z.string(),
});
export type UserNoteCreatedByResponse = z.infer<
	typeof UserNoteCreatedByResponse
>;

export const UserNoteResponse = z.object({
	id: z.number().int(),
	note: z.string(),
	createdBy: UserNoteCreatedByResponse.nullish(),
	createdAt: z.string().datetime({ offset: true }),
});
export type UserNoteResponse = z.infer<typeof UserNoteResponse>;

export const DiscoverUserGetResponse = z.object({
	id: z.number().int(),
	firstName: z.string(),
	lastName: z.string(),
	email: z.string().nullable(),
	phoneNumber: z.string().nullable(),
	isActive: z.boolean(),
	birthday: z.string().datetime({ offset: true }).nullable(),
	createdAt: z.string().datetime({ offset: true }).nullable(),
	passwordRequestedAt: z.string().datetime({ offset: true }).nullable(),
	country: SomeItemResponse.nullable(),
	hasDiscover: z.boolean(),
	hasPortal: z.boolean(),
	isMinor: z.boolean(),
	clubPro: SomeItemResponse.nullable(),
	clubAmateur: SomeItemResponse.nullable(),
	team: SomeItemResponse.nullable(),
	player: SomeItemResponse.nullable(),
	roles: z.array(z.string()),
	userPermissions: z.array(UserPermissionItemResponse),
	passwordSecurityCodes: z.array(SecurityCodeResponse),
	userGuardianApprovalsCount: z.number().int(),
	pendingRemoval: z.boolean().nullish(),
	pendingRemovalReason: z.string().nullish(),
	pendingRemovalAt: z.string().datetime({ offset: true }).nullish(),
	isEnterprise: z.boolean(),
	canAccessUserNotes: z
		.boolean()
		.describe(
			"True when the current admin may view or manage discover user notes (READ or EDIT on discover section management).",
		),
	userNotes: z.array(UserNoteResponse),
});
export type DiscoverUserGetResponse = z.infer<typeof DiscoverUserGetResponse>;

export const DiscoverUserPatchRequest = z.object({
	id: z.number().int(),
	email: z.string().nullish(),
	phoneNumber: z.string().nullish(),
	firstName: z.string().max(255).nullish(),
	lastName: z.string().max(255).nullish(),
	birthday: z.string().datetime({ offset: true }).nullish(),
	isActive: z.boolean().nullish(),
	pendingRemoval: z.boolean().nullish(),
	pendingRemovalReason: z.string().max(255).nullish(),
	isEnterprise: z.boolean().nullish(),
	requestedFields: z.array(z.string()).optional().default([]),
});
export type DiscoverUserPatchRequest = z.infer<typeof DiscoverUserPatchRequest>;

export const DiscoverUserImpersonateResponse = z.object({ token: z.string() });
export type DiscoverUserImpersonateResponse = z.infer<
	typeof DiscoverUserImpersonateResponse
>;

export const TeamTaggedEnum = z.enum(["home", "away", "both"]);
export type TeamTaggedEnum = z.infer<typeof TeamTaggedEnum>;

export const GameAnalysisCreatePostRequest = z.object({
	provider: z.string(),
	priority: z.number().int().gte(1).lte(11),
	processingLevel: ProcessingLevelEnum.optional(),
	teamTagging: TeamTaggedEnum.nullish(),
	requestSportlogiqWithPhysical: z.boolean().optional().default(false),
});
export type GameAnalysisCreatePostRequest = z.infer<
	typeof GameAnalysisCreatePostRequest
>;

export const GameHomeAwayMappingSwapEligibilityResponse = z.object({
	canSwap: z.boolean(),
	ineligibilityReason: z.string().nullable(),
	swapped: z
		.boolean()
		.describe(
			"Whether the last completed Sportlogiq run swapped the sides; null when it has not reported yet",
		)
		.nullable(),
	keepMappingAllowed: z
		.boolean()
		.describe(
			"Whether all_keep_mapping is safe: false when mapping must be rebuilt for a swap or pending starting-side flip",
		),
});
export type GameHomeAwayMappingSwapEligibilityResponse = z.infer<
	typeof GameHomeAwayMappingSwapEligibilityResponse
>;

export const filters__11 = z
	.array(
		z
			.object({
				type: z
					.enum([
						"exact",
						"search",
						"boolean",
						"exists",
						"inarray",
						"datetimerange",
						"numberrange",
					])
					.describe("Type of filter"),
				field: z
					.enum([
						"continent",
						"country",
						"status",
						"totalPartnerClubs",
						"targetPartnerClubs",
						"numberOfScoutTeams",
						"numberOfDiscoverTeams",
						"ntPlayers",
						"totalGames",
						"gamesTagged",
						"viewsOfGames",
						"subscriptionsSold",
						"consistentRunningPlayerPercent",
						"variablePlayerQualityPercent",
						"percentOfGamesTagged",
						"proportionOfGameViews",
						"avgGamePriority",
						"avgGameAnalysisProcessing",
						"suggestedGamesUpdate",
						"playerToWatchUpdate",
						"seasonStart",
						"seasonEnd",
					])
					.describe("Field to filter on"),
				value: z
					.union([
						z.string(),
						z.number(),
						z.boolean(),
						z.array(z.string()),
						z
							.object({
								min: z.number().nullable(),
								max: z.number().nullable(),
								start: z.string().nullable(),
								end: z.string().nullable(),
							})
							.partial(),
					])
					.describe(
						"Filter value (string, integer, boolean, array for inarray, or object with min/max for numberrange or start/end for datetimerange)",
					),
			})
			.partial(),
	)
	.describe("Filter criteria")
	.optional();
export type filters__11 = z.infer<typeof filters__11>;

export const order__10 = z
	.object({
		field: z
			.enum([
				"continent",
				"country",
				"countryName",
				"status",
				"totalPartnerClubs",
				"targetPartnerClubs",
				"numberOfScoutTeams",
				"numberOfDiscoverTeams",
				"consistentRunningPlayerPercent",
				"variablePlayerQualityPercent",
				"ntPlayers",
				"suggestedGamesUpdate",
				"playerToWatchUpdate",
				"seasonStart",
				"seasonEnd",
				"totalGames",
				"gamesTagged",
				"percentOfGamesTagged",
				"avgGamePriority",
				"avgGameAnalysisProcessing",
				"viewsOfGames",
				"proportionOfGameViews",
				"subscriptionsSold",
				"refreshedAt",
			])
			.describe("Field to sort by"),
		direction: z.enum(["ASC", "DESC"]).describe("Sort direction"),
	})
	.partial()

	.describe("Sort configuration")
	.optional();
export type order__10 = z.infer<typeof order__10>;

export const filters__12 = z
	.array(
		z
			.object({
				type: z
					.enum([
						"exact",
						"search",
						"boolean",
						"exists",
						"inarray",
						"datetimerange",
						"numberrange",
					])
					.describe("Type of filter"),
				field: z
					.enum([
						"continent",
						"country",
						"club",
						"manager",
						"onboardedDate",
						"contract",
						"consent",
						"nonEbCamera",
						"teamsheetAvailability",
						"fixtureAvailability",
						"marketingOptIn",
						"numberOfScoutTeams",
						"numberOfDiscoverTeams",
						"ntPlayers",
						"totalGames",
						"gamesTagged",
						"viewsOfGames",
						"consistentRunningPlayerPercent",
						"variablePlayerQualityPercent",
						"percentOfGamesTagged",
						"proportionOfGameViews",
						"avgGamePriority",
						"avgGameAnalysisProcessing",
						"lastPartnersUsage",
						"contractEndDate",
					])
					.describe("Field to filter on"),
				value: z
					.union([
						z.string(),
						z.number(),
						z.boolean(),
						z.array(z.string()),
						z
							.object({
								min: z.number().nullable(),
								max: z.number().nullable(),
								start: z.string().nullable(),
								end: z.string().nullable(),
							})
							.partial(),
					])
					.describe(
						"Filter value (string, integer, boolean, array for inarray, or object with min/max for numberrange or start/end for datetimerange)",
					),
			})
			.partial(),
	)
	.describe("Filter criteria")
	.optional();
export type filters__12 = z.infer<typeof filters__12>;

export const order__11 = z
	.object({
		field: z
			.enum([
				"continent",
				"countryName",
				"club",
				"manager",
				"onboardedDate",
				"numberOfScoutTeams",
				"numberOfDiscoverTeams",
				"consistentRunningPlayerPercent",
				"variablePlayerQualityPercent",
				"ntPlayers",
				"nonEbCamera",
				"teamsheetAvailability",
				"fixtureAvailability",
				"lastPartnersUsage",
				"totalGames",
				"gamesTagged",
				"percentOfGamesTagged",
				"avgGamePriority",
				"avgGameAnalysisProcessing",
				"viewsOfGames",
				"proportionOfGameViews",
				"contract",
				"contractEndDate",
				"consent",
				"marketingOptIn",
				"refreshedAt",
			])
			.describe("Field to sort by"),
		direction: z.enum(["ASC", "DESC"]).describe("Sort direction"),
	})
	.partial()

	.describe("Sort configuration")
	.optional();
export type order__11 = z.infer<typeof order__11>;

export const JsonLoginRequest = z
	.object({
		email: z.string().nullable(),
		password: z.string().nullable(),
		accessToken: z.string().nullable(),
	})
	.partial();
export type JsonLoginRequest = z.infer<typeof JsonLoginRequest>;

export const Resource = z.enum([
	"product_management",
	"poles",
	"pole",
	"non_partner_clubs",
	"non_partner_club",
	"non_partner_players",
	"non_partner_player",
	"roles_permissions_management",
	"role_permission_management",
	"admins_creation",
	"admin_creation",
	"subscriptions",
	"subscription",
	"team_groups",
	"team_group",
	"notifications",
	"notification",
	"specific_notiifcations",
	"specific_notiifcation",
	"schedule_notifications",
	"schedule_notification",
	"countries_expansion",
	"country_expansion",
	"player_accounts_management",
	"player_account_management",
	"feed_area",
	"single_feed_area",
	"clubs_amateur",
	"club_amateur_single",
	"club_amateur_users",
	"club_amateur_user_directors",
	"club_amateur_user_director",
	"club_amateur_user_coachs",
	"club_amateur_user_coach",
	"club_amateur_teams",
	"club_amateur_team",
	"club_amateur_team_players",
	"club_amateur_team_player",
	"club_amateur_games",
	"club_amateur_game",
	"game_uploads",
	"game_upload_single",
	"clubs_pro",
	"clubs_pro_single",
	"clubs_pro_users",
	"club_pro_user_director",
	"discover_sections_management",
	"discover_section_management",
	"discover_token_management",
	"discover_subscription_management",
	"api_management",
	"die_ligen",
	"operations_management",
	"all",
	"amateur_clubs",
	"discover_user_enterprise_management",
	"club_pro_pipedrive_org_id",
	"subscription_deal_id",
	"operations_management_summary_tab",
	"log_in_as_user",
]);
export type Resource = z.infer<typeof Resource>;

export const AdminManagementPermission = z.literal("set_permissions");
export type AdminManagementPermission = z.infer<
	typeof AdminManagementPermission
>;

export const BundlePermission = z.literal("assign_bundle");
export type BundlePermission = z.infer<typeof BundlePermission>;

export const LogAsUserPermission = z.literal("log_in_as_user");
export type LogAsUserPermission = z.infer<typeof LogAsUserPermission>;

export const NonPartnerPermission = z.enum([
	"migrate_to_nonpartner",
	"migrate_to_partner",
	"convert_to_partner",
]);
export type NonPartnerPermission = z.infer<typeof NonPartnerPermission>;

export const NotificationPermission = z.enum([
	"test_email",
	"SMS",
	"Email",
	"Firebase",
	"Notification",
]);
export type NotificationPermission = z.infer<typeof NotificationPermission>;

export const Permission = z.enum([
	"read",
	"list",
	"create",
	"edit",
	"email_edit",
	"delete",
]);
export type Permission = z.infer<typeof Permission>;

export const PolePermission = z.literal("assign_pole");
export type PolePermission = z.infer<typeof PolePermission>;

export const SubscriptionPermission = z.literal("assign_subscription");
export type SubscriptionPermission = z.infer<typeof SubscriptionPermission>;

export const SuperAdminPermission = z.literal("all");
export type SuperAdminPermission = z.infer<typeof SuperAdminPermission>;

export const GamePermission = z.enum([
	"game_teamsheet",
	"game_vip",
	"game_hidden",
	"game_view",
	"game_view_mobile",
	"game_score",
	"game_analyze",
	"game_team_stat",
	"game_player_stat",
	"game_clip",
	"game_ai",
	"game_favorites",
	"game_admin_options",
	"game_view_events",
	"game_report_event_error",
	"game_view_event_comments",
	"game_download_events",
	"game_view_ratings",
	"game_download_video",
	"game_downloads",
	"game_download_player_stat",
	"game_download_team_stat",
	"game_download_team_sheet",
	"game_edit",
	"game_upload_video",
	"game_delete",
	"game_analytics",
	"game_analytics_mobile",
	"game_events_view_mobile",
	"game_view_data_insight",
	"game_view_physical_analysis",
	"game_player_grading",
	"game_create_game",
	"game_assignee_selection",
	"game_view_sla",
	"game_status_edit",
	"game_ai_summary_feature",
	"game_ai_summary_content",
	"game_access_ai_coach_review",
]);
export type GamePermission = z.infer<typeof GamePermission>;

export const UserPermission = z.object({
	id: z.number().int(),
	resource: Resource,
	permission: z.union([
		AdminManagementPermission,
		BundlePermission,
		LogAsUserPermission,
		NonPartnerPermission,
		NotificationPermission,
		Permission,
		PolePermission,
		SubscriptionPermission,
		SuperAdminPermission,
		GamePermission,
	]),
});
export type UserPermission = z.infer<typeof UserPermission>;

export const IntercomDataCustomAttributesResponse = z.object({
	d_UserPosition: z.string().nullable(),
	d_Logged: z.boolean(),
	d_UserID: z.number().int().nullable(),
	d_PlayerID: z.number().int().nullable(),
	d_ClubID: z.number().int().nullable(),
	d_PhoneNumber: z.string().nullable(),
	d_UserName: z.string().nullable(),
	d_Email: z.string().nullable(),
	d_Name: z.string().nullable(),
	d_ClubTeamName: z.string().nullable(),
	d_ClubType: z.string().nullable(),
	d_Product: z.string(),
	d_PaidSupport: z.boolean(),
	d_LastTokenID: z.string().nullable(),
	d_InviteLink: z.string().nullable(),
	d_TeamID: z.number().int().nullable(),
	d_TeamName: z.string().nullable(),
	d_SupportExpiration: z.string().nullable(),
	d_SupportValidFrom: z.string().nullable(),
	d_AccessType: z.string().nullable(),
	d_Migrated: z.string().nullable(),
});
export type IntercomDataCustomAttributesResponse = z.infer<
	typeof IntercomDataCustomAttributesResponse
>;

export const IntercomDataResponse = z.object({
	customAttributes: IntercomDataCustomAttributesResponse,
	name: z.string().nullable(),
	email: z.string().nullable(),
	phone: z.string().nullable(),
	userHash: z.string().nullable(),
	userId: z.string().nullish().default(null),
	intercomUserJwt: z.string().nullish().default(null),
});
export type IntercomDataResponse = z.infer<typeof IntercomDataResponse>;

export const UserSubscriptionOptions = z.enum(["full", "partial", "none"]);
export type UserSubscriptionOptions = z.infer<typeof UserSubscriptionOptions>;

export const AdminSubMenuItem = z.object({ id: z.string(), name: z.string() });
export type AdminSubMenuItem = z.infer<typeof AdminSubMenuItem>;

export const AdminMenuItem = z.object({
	id: z.string(),
	name: z.string(),
	icon: z.string(),
	hasSubMenu: z.boolean().default(false),
	subMenuItems: z.array(AdminSubMenuItem).default([]),
});
export type AdminMenuItem = z.infer<typeof AdminMenuItem>;

export const MenuData = z
	.object({
		admin_management: AdminMenuItem.nullable(),
		non_partner_dataset: AdminMenuItem.nullable(),
		poles: AdminMenuItem.nullable(),
		subscriptions: AdminMenuItem.nullable(),
		notifications: AdminMenuItem.nullable(),
		country_expansion: AdminMenuItem.nullable(),
		feed_area: AdminMenuItem.nullable(),
		player_account_management: AdminMenuItem.nullable(),
		game_uploads_management: AdminMenuItem.nullable(),
		discover_sections_management: AdminMenuItem.nullable(),
		api_management: AdminMenuItem.nullable(),
		product_quality_management: AdminMenuItem.nullable(),
	})
	.partial();
export type MenuData = z.infer<typeof MenuData>;

export const UserResponse = z.object({
	id: z.number().int(),
	email: z.string(),
	firstName: z.string(),
	lastName: z.string(),
	roles: z.array(z.string()),
	adminRoles: z.array(z.string()),
	role: z.string(),
	isVip: z.boolean(),
	position: z.string(),
	clubId: z.string(),
	clubName: z.string(),
	clubLogo: z.string().nullable(),
	photo: z.string().nullable(),
	locale: z.string(),
	hasAcceptedTermsOfUse: z.boolean(),
	permissions: z.array(UserPermission),
	intercomData: IntercomDataResponse,
	eyeballAddons: z.array(z.string()).default([]),
	isSuperAdmin: z.boolean(),
	isClubAdministrator: z.boolean(),
	playerStatsSearchSubscription:
		UserSubscriptionOptions.nullable().default(null),
	productManagementOptions: MenuData.nullish().default(null),
	termsOfUseLatestVersion: z.number().int().nullable().default(1),
	isMigratedToDiscover: z.boolean(),
	selectedGender: GenderEnum.nullable().default(null),
	selectedDataSource: z.string().nullable().default(null),
	selectedTeamGroups: z
		.union([z.array(z.string()), z.record(z.string())])
		.default([]),
});
export type UserResponse = z.infer<typeof UserResponse>;

export const JsonResetPasswordRequest = z.object({ newPassword: z.string() });
export type JsonResetPasswordRequest = z.infer<typeof JsonResetPasswordRequest>;

export const SearchLeagueResponse = z.object({
	label: z.string(),
	value: z.number().int(),
});
export type SearchLeagueResponse = z.infer<typeof SearchLeagueResponse>;

export const PermissionData = z.object({
	id: z.number().int(),
	permission: z.string(),
});
export type PermissionData = z.infer<typeof PermissionData>;

export const PermissionTreeResponse = z.object({
	label: z.string(),
	permission: z.array(PermissionData),
});
export type PermissionTreeResponse = z.infer<typeof PermissionTreeResponse>;

export const assignPermissionToRole_Body = z.object({
	permissionIds: z.array(z.number().int()),
});
export type assignPermissionToRole_Body = z.infer<
	typeof assignPermissionToRole_Body
>;

export const RolesData = z.object({ id: z.number().int(), name: z.string() });
export type RolesData = z.infer<typeof RolesData>;

export const AddUpdateRole = z.object({
	id: z.number().int().nullable(),
	name: z.string(),
});
export type AddUpdateRole = z.infer<typeof AddUpdateRole>;

export const addUpdateRole_Body = z.object({ role: AddUpdateRole }).partial();
export type addUpdateRole_Body = z.infer<typeof addUpdateRole_Body>;

export const AdminUserData = z.object({
	email: z.string(),
	firstName: z.string(),
	id: z.number().int(),
	isActive: z.boolean(),
	lastName: z.string(),
	phoneNumber: z.string().nullable(),
	photo: z.string().nullable(),
	roleId: z.number().int().nullable(),
	roles: z.string().nullish(),
});
export type AdminUserData = z.infer<typeof AdminUserData>;

export const AdminData = z.object({
	email: z.string(),
	id: z.number().int().nullish(),
	firstName: z.string(),
	lastName: z.string(),
	phoneNumber: z.string().nullable(),
	photo: z.string().nullable(),
	roles: z.array(z.number().int()),
	status: z.boolean(),
});
export type AdminData = z.infer<typeof AdminData>;

export const addUpdateAdminUsers_Body = z.object({ user: AdminData }).partial();
export type addUpdateAdminUsers_Body = z.infer<typeof addUpdateAdminUsers_Body>;

export const uploadProfilePhoto_Body = z
	.object({ id: z.number().int().nullable(), file: z.string() })
	.partial();
export type uploadProfilePhoto_Body = z.infer<typeof uploadProfilePhoto_Body>;

export const AllPoleData = z.object({
	id: z.number().int().nullish(),
	location: z.string(),
	timezone: z.string(),
	currentBattery: z.number().int(),
	serialNumber: z.string(),
	assignedClub: z.array(
		z
			.object({
				id: z.number().int().describe("club ID"),
				name: z.string().describe("club name"),
			})
			.partial(),
	),
	assignedClubCount: z.number().int(),
	sync: z.number().int().nullish(),
	action: z.number().int().nullish(),
});
export type AllPoleData = z.infer<typeof AllPoleData>;

export const AddUpdatePole = z.object({
	id: z.number().int(),
	location: z.string(),
	timezone: z.string(),
	serialNumber: z.string(),
	oldId: z.number().int().nullish(),
});
export type AddUpdatePole = z.infer<typeof AddUpdatePole>;

export const addUpdatePole_Body = z.object({ pole: AddUpdatePole }).partial();
export type addUpdatePole_Body = z.infer<typeof addUpdatePole_Body>;

export const PoleData = z.object({
	id: z.number().int().nullish(),
	location: z.string(),
	timezone: z.string(),
	currentBattery: z.number().int(),
	serialNumber: z.string(),
	assignedClub: z.array(
		z
			.object({
				id: z.number().int().describe("club ID"),
				name: z.string().describe("club name"),
			})
			.partial(),
	),
	assignedClubCount: z.number().int(),
});
export type PoleData = z.infer<typeof PoleData>;

export const TimezoneResponse = z.array(z.string());
export type TimezoneResponse = z.infer<typeof TimezoneResponse>;

export const DefaultResponseOptions = z
	.object({
		label: z.union([z.string(), z.number()]).nullable(),
		value: z.union([z.string(), z.number()]).nullable(),
	})
	.partial();
export type DefaultResponseOptions = z.infer<typeof DefaultResponseOptions>;

export const assignPole_Body = z
	.object({
		assign_pole: z.object({ club_id: z.number().int(), assign_to: z.string() }),
	})
	.partial();
export type assignPole_Body = z.infer<typeof assignPole_Body>;

export const FilterData = z.object({
	label: z.union([z.string(), z.number()]),
	value: z.union([z.string(), z.number()]),
});
export type FilterData = z.infer<typeof FilterData>;

export const ContinentCountryData = z.object({
	value: z.string(),
	label: z.string(),
	continent: z.string(),
});
export type ContinentCountryData = z.infer<typeof ContinentCountryData>;

export const ContinentCountryResponse = z.object({
	label: z.string(),
	options: z.array(ContinentCountryData),
});
export type ContinentCountryResponse = z.infer<typeof ContinentCountryResponse>;

export const ClubExternalLink = z.object({
	link: z.string(),
	type: z.string(),
});
export type ClubExternalLink = z.infer<typeof ClubExternalLink>;

export const ClubData = z.object({
	id: z.number().int().nullish(),
	name: z.string(),
	country: z.string(),
	logo: z.string().nullish(),
	description: z.string().nullish(),
	contactName: z.string().nullish(),
	contactPhoneNumber: z.string().nullish(),
	contactEmail: z.string().nullish(),
	city: z.string().nullish(),
	zipcode: z.string().nullish(),
	address: z.string().nullish(),
	showCase: z.boolean().nullish(),
	region: z.string().nullish(),
	geographicalArea: z.string().nullish(),
	gamesCount: z.number().int().nullish(),
	playersCount: z.number().int().nullish(),
	clubExternalLinks: z.array(ClubExternalLink).nullish(),
});
export type ClubData = z.infer<typeof ClubData>;

export const addUpdateNPClub_Body = z
	.object({ nonPartnerClub: ClubData })
	.partial();
export type addUpdateNPClub_Body = z.infer<typeof addUpdateNPClub_Body>;

export const PlayerData = z.object({
	id: z.number().int().nullish(),
	firstName: z.string(),
	lastName: z.string().nullish(),
	picture: z.string().nullish(),
	birthday: z.string().nullish(),
	nationality: z.string().nullish(),
	nationality2: z.string().nullish(),
	position: z.string().nullish(),
	strongestFoot: z.string().nullish(),
	playedInNationalTeam: z.boolean().nullish(),
	clubNonpartnerId: z.number().int().nullish(),
	npClubName: z.string().nullish(),
	npClubCountry: z.string().nullish(),
	npClubLogo: z.string().nullish(),
	tranLink: z.string().nullish(),
	federationLink: z.string().nullish(),
	gender: z.string().nullish(),
});
export type PlayerData = z.infer<typeof PlayerData>;

export const AddUpdateNPPlayerDto = z.object({
	clubNonpartnerId: z.number().int(),
	id: z.number().int().nullish(),
	firstName: z.string(),
	lastName: z.string(),
	picture: z.string().nullish(),
	birthday: z.string().nullish(),
	nationality: z.string().nullish(),
	nationality2: z.string().nullish(),
	position: z.string().nullish(),
	strongestFoot: z.string().nullish(),
	playedInNationalTeam: z.boolean().nullish().default(false),
	npClubName: z.string().nullish(),
	npClubCountry: z.string().nullish(),
	npClubLogo: z.string().nullish(),
	tranLink: z.string().nullish(),
	federationLink: z.string().nullish(),
	gender: z.string().nullish(),
	birthdayAsDateTime: z.string().datetime({ offset: true }).nullish(),
});
export type AddUpdateNPPlayerDto = z.infer<typeof AddUpdateNPPlayerDto>;

export const CreateNonPartnerPlayerDtoRequest = z.object({
	nonPartnerPlayer: AddUpdateNPPlayerDto,
});
export type CreateNonPartnerPlayerDtoRequest = z.infer<
	typeof CreateNonPartnerPlayerDtoRequest
>;

export const ClubOptionData = z.object({
	country: z.string().nullish(),
	label: z.string().nullish(),
	logo: z.string().nullish(),
	value: z.number().int(),
});
export type ClubOptionData = z.infer<typeof ClubOptionData>;

export const ClubPlayers = z
	.object({
		club: z.string().nullable(),
		clubCountry: z.string().nullable(),
		firstName: z.string().nullable(),
		id: z.number().int().nullable(),
		lastName: z.string().nullable(),
		picture: z.string().nullable(),
		teamName: z.string().nullable(),
		teamCategory: z.string().nullable(),
		gender: z.string().nullable(),
	})
	.partial();
export type ClubPlayers = z.infer<typeof ClubPlayers>;

export const executeNPClubMigration_Body = z
	.object({
		sourceClubId: z.number().int(),
		targetClubId: z.number().int(),
		sourcePlayerId: z.number().int().nullable(),
		isClubMigration: z.boolean(),
	})
	.partial();
export type executeNPClubMigration_Body = z.infer<
	typeof executeNPClubMigration_Body
>;

export const playerExistsTargetClub_Body = z
	.object({
		migrationId: z.number().int(),
		status: z.string(),
		targetPlayerId: z.number().int(),
	})
	.partial();
export type playerExistsTargetClub_Body = z.infer<
	typeof playerExistsTargetClub_Body
>;

export const getMigrationStatus_Body = z.object({
	sourceClubId: z.number().int(),
	targetClubId: z.number().int(),
	migrateAllPlayers: z.boolean(),
	migrationIds: z.array(z.number().int()),
});
export type getMigrationStatus_Body = z.infer<typeof getMigrationStatus_Body>;

export const createPartnerPlayerFromNonpartner_Body = z
	.object({
		partnerMigrationId: z.number().int(),
		sourcePlayerId: z.number().int(),
		teamId: z.number().int().nullable(),
	})
	.partial();
export type createPartnerPlayerFromNonpartner_Body = z.infer<
	typeof createPartnerPlayerFromNonpartner_Body
>;

export const partnerPlayerExistsTargetClub_Body = z
	.object({
		migrationId: z.number().int(),
		status: z.string(),
		targetPlayerId: z.number().int(),
		teamId: z.number().int().nullable(),
	})
	.partial();
export type partnerPlayerExistsTargetClub_Body = z.infer<
	typeof partnerPlayerExistsTargetClub_Body
>;

export const getPartnerMigrationStatus_Body = z.object({
	sourceClubId: z.number().int(),
	targetClubId: z.number().int(),
	migrationIds: z.array(z.number().int()),
	migrateAllPlayers: z.boolean().optional(),
});
export type getPartnerMigrationStatus_Body = z.infer<
	typeof getPartnerMigrationStatus_Body
>;

export const updateGameTeam_Body = z.object({
	gameId: z.number().int(),
	teamId: z.number().int().nullable(),
	targetClubId: z.number().int(),
});
export type updateGameTeam_Body = z.infer<typeof updateGameTeam_Body>;

export const beginMigration_Body = z.object({
	sourceClubId: z.number().int(),
	targetClubId: z.number().int(),
	sourceClubType: z.string(),
	targetClubType: z.string(),
	isClubMigration: z.boolean().optional(),
	sourcePlayerId: z.number().int().nullish(),
});
export type beginMigration_Body = z.infer<typeof beginMigration_Body>;

export const BeginMigration = z
	.object({
		status: z.union([z.string(), z.boolean()]).nullable(),
		percentage: z.number().int().nullable(),
		id: z.number().int().nullable(),
		targetPlayer: z
			.object({
				id: z.number().int().describe("ID of Target Player"),
				firstName: z.string().describe("First Name of Target Player"),
				lastName: z.string().describe("Last Name of Target Player"),
			})

			.nullable(),
		count: z
			.object({
				gamesCount: z.number().int().describe("count of migrated games"),
				playersCount: z.number().int().describe("count of players count"),
			})

			.nullable(),
		deleteClub: z.boolean().nullable(),
		message: z.string().nullable(),
	})
	.partial();
export type BeginMigration = z.infer<typeof BeginMigration>;

export const clubMigrationWithZeroPlayers_Body = z.object({
	sourceClubId: z.number().int(),
	targetClubId: z.number().int(),
	sourceClubType: z.string(),
	targetClubType: z.string(),
});
export type clubMigrationWithZeroPlayers_Body = z.infer<
	typeof clubMigrationWithZeroPlayers_Body
>;

export const MigrateZeroPlayerClub = z.object({
	id: z.number().int(),
	migratedClub: z.string(),
	category: z.string(),
	date: z.string(),
	awayClub: z.string(),
	homeClub: z.string(),
	awayTeam: z.object({
		label: z.string().nullable(),
		value: z.number().int().nullable(),
	}),
	homeTeam: FilterData,
});
export type MigrateZeroPlayerClub = z.infer<typeof MigrateZeroPlayerClub>;

export const completeMigration_Body = z.object({
	sourceClubId: z.number().int(),
	targetClubId: z.number().int(),
	sourceClubType: z.string(),
	targetClubType: z.string(),
	migrationIds: z.array(z.number()).optional(),
	isClubMigration: z.boolean().optional(),
});
export type completeMigration_Body = z.infer<typeof completeMigration_Body>;

export const CompleteMigration = z.object({
	failed: z.object({
		skippedPlayers: z
			.array(
				z.object({
					team: z.string().describe("Team name").nullish(),
					status: z.string().describe("Status"),
					firstName: z.string().describe("First Name of Target Player"),
					lastName: z.string().describe("Last Name of Target Player").nullish(),
				}),
			)
			.describe("skipped players"),
		skippedPlayersCount: z.number().int().describe("Count of skipped players"),
	}),
	success: z.object({
		migratedPlayers: z
			.array(
				z.object({
					team: z.string().describe("Team id").nullish(),
					status: z.string().describe("Status"),
					firstName: z
						.string()
						.describe("First Name of Target Player")
						.optional(),
					lastName: z.string().describe("Last Name of Target Player").nullish(),
				}),
			)
			.describe("migrated players"),
		migratedGames: z
			.array(
				z.object({
					id: z.number().int().describe("Game Id"),
					category: z.string().describe("Game Category"),
					homeClub: z.string().describe("Home club name"),
					awayClub: z.string().describe("Away Club name"),
					date: z.string().describe("Game Date"),
					homeTeam: DefaultResponseOptions,
					awayTeam: DefaultResponseOptions,
					migratedClub: z
						.string()
						.describe("migrated club is home/away")
						.nullable(),
				}),
			)
			.describe("migrated games"),
		migratedPlayersCount: z
			.number()
			.int()
			.describe("Count of migrated players"),
		migratedGamesCount: z.number().int().describe("Count of migrated games"),
	}),
});
export type CompleteMigration = z.infer<typeof CompleteMigration>;

export const playerMigrationToPartner_Body = z.object({
	partnerMigrationId: z.number().int(),
	sourcePlayerId: z.number().int(),
	teamId: z.number().int().nullish(),
});
export type playerMigrationToPartner_Body = z.infer<
	typeof playerMigrationToPartner_Body
>;

export const playerMigrationConflict_Body = z
	.object({
		migrationId: z.number().int(),
		partnerMigrationId: z.number().int(),
		targetPlayerId: z.number().int(),
		teamId: z.string(),
		status: z.string(),
	})
	.partial();
export type playerMigrationConflict_Body = z.infer<
	typeof playerMigrationConflict_Body
>;

export const AllCountryTooltipData = z.object({
	id: z.number().int().nullable(),
	countryCode: z.string(),
	countryName: z.string(),
	enabled: z.boolean(),
	maleEnabled: z.boolean(),
	femaleEnabled: z.boolean(),
	tooltip: z.string(),
	tooltipFemale: z.string().nullable(),
});
export type AllCountryTooltipData = z.infer<typeof AllCountryTooltipData>;

export const CountryTooltipData = z.object({
	id: z.number().int().nullish(),
	enabled: z
		.boolean()
		.describe(
			"Legacy aggregate flag; persisted enabled is true if this or either gender flag is true.",
		),
	femaleEnabled: z
		.boolean()
		.describe(
			"When omitted, treated as false; aggregate enabled uses this, maleEnabled, or legacy enabled.",
		),
	maleEnabled: z
		.boolean()
		.describe(
			"When omitted, treated as false; aggregate enabled uses this, femaleEnabled, or legacy enabled.",
		),
	tooltip: z.string(),
	tooltipFemale: z.string(),
	countries: z.array(
		z
			.object({
				value: z.string().describe("country code"),
				label: z.string().describe("country name"),
				continent: z.string().describe("continent name"),
			})
			.partial(),
	),
});
export type CountryTooltipData = z.infer<typeof CountryTooltipData>;

export const addUpdateCountry_Body = z
	.object({ countryTooltip: CountryTooltipData })
	.partial();
export type addUpdateCountry_Body = z.infer<typeof addUpdateCountry_Body>;

export const EnableDisableCountryTooltipRequest = z
	.object({
		enabled: z.boolean().nullable(),
		maleEnabled: z.boolean().nullable(),
		femaleEnabled: z.boolean().nullable(),
	})
	.partial();
export type EnableDisableCountryTooltipRequest = z.infer<
	typeof EnableDisableCountryTooltipRequest
>;

export const UpdateCountryTooltipData = z.object({
	id: z.number().int().nullable(),
	continent: z.string(),
	country: z.string(),
	enabled: z.boolean(),
	tooltip: z.string(),
});
export type UpdateCountryTooltipData = z.infer<typeof UpdateCountryTooltipData>;

export const UserInfo = z.object({
	id: z.number().int(),
	firstName: z.string(),
	lastName: z.string(),
	email: z.string(),
	phoneNumber: z.string().nullable(),
	timezone: z.string().nullable(),
	locale: z.string().nullish(),
	photo: z.string().nullish(),
	hasNewsletterSubscription: z.boolean(),
});
export type UserInfo = z.infer<typeof UserInfo>;

export const UserSessionLog = z.object({
	userId: z.number().int(),
	sessionId: z.string(),
	createdAt: z.string().datetime({ offset: true }).nullish(),
	updatedAt: z.string().datetime({ offset: true }).nullish(),
});
export type UserSessionLog = z.infer<typeof UserSessionLog>;

export const UserActivityLogRequest = z.record(
	z.union([z.string(), z.number()]),
);
export type UserActivityLogRequest = z.infer<typeof UserActivityLogRequest>;

export const ErrorResponse = z.object({ code: z.string() });
export type ErrorResponse = z.infer<typeof ErrorResponse>;

export const WidgetSettingItem = z.object({
	key: z.string(),
	mapType: z.string(),
	isCustom: z.boolean(),
	parameters: z
		.array(
			z
				.object({ key: z.string(), value: z.string(), eventType: z.string() })
				.partial(),
		)
		.optional()
		.default([]),
	category: z.string(),
	eventType: z.string(),
	group: z.string().optional().default(""),
	label: z.string().optional().default(""),
	isPlaceholder: z.boolean(),
});
export type WidgetSettingItem = z.infer<typeof WidgetSettingItem>;

export const DisplaySettingsResponse = z
	.object({
		offensive: z.array(WidgetSettingItem).default([]),
		defensive: z.array(WidgetSettingItem).default([]),
		distribution: z.array(WidgetSettingItem).default([]),
		goalkeeper: z.array(WidgetSettingItem).default([]),
		customGroup1: z.array(WidgetSettingItem).nullable().default([]),
		customGroup2: z.array(WidgetSettingItem).nullable().default([]),
		customGroup3: z.array(WidgetSettingItem).nullable().default([]),
		customGroup4: z.array(WidgetSettingItem).nullable().default([]),
		customGroup5: z.array(WidgetSettingItem).nullable().default([]),
		customGroup6: z.array(WidgetSettingItem).nullable().default([]),
		customGroup7: z.array(WidgetSettingItem).nullable().default([]),
		customGroup8: z.array(WidgetSettingItem).nullable().default([]),
		customGroup9: z.array(WidgetSettingItem).nullable().default([]),
		customGroup10: z.array(WidgetSettingItem).nullable().default([]),
		customGroup11: z.array(WidgetSettingItem).nullable().default([]),
		customGroup12: z.array(WidgetSettingItem).nullable().default([]),
		customGroup13: z.array(WidgetSettingItem).nullable().default([]),
		customGroup14: z.array(WidgetSettingItem).nullable().default([]),
		customGroup15: z.array(WidgetSettingItem).nullable().default([]),
		customGroup16: z.array(WidgetSettingItem).nullable().default([]),
	})
	.partial();
export type DisplaySettingsResponse = z.infer<typeof DisplaySettingsResponse>;

export const UserWidgetSettingsResponse = z.object({
	insightDisplaySettings: DisplaySettingsResponse.nullable(),
});
export type UserWidgetSettingsResponse = z.infer<
	typeof UserWidgetSettingsResponse
>;

export const CoachInfo = z.object({
	id: z.number().int(),
	firstName: z.string(),
	lastName: z.string(),
	email: z.string(),
});
export type CoachInfo = z.infer<typeof CoachInfo>;

export const ClubTeamInfo = z.object({
	coaches: z.array(CoachInfo),
	id: z.number().int(),
	name: z.string(),
	level: z.number().int(),
	playerCount: z.number().int(),
	clubId: z.number().int().nullable(),
	clubName: z.string().nullable(),
	category: z.string().nullable(),
	gender: GenderEnum.nullable().default(null),
});
export type ClubTeamInfo = z.infer<typeof ClubTeamInfo>;

export const TeamResponse = z.object({
	data: z.array(ClubTeamInfo),
	page: z.number().int(),
	limit: z.number().int(),
	pages: z.number().int(),
	total: z.number().int(),
});
export type TeamResponse = z.infer<typeof TeamResponse>;

export const addUpdateTeam_Body = z.object({
	id: z.number().int().nullish(),
	category: z.string(),
	name: z.string(),
	gender: z.string().nullish(),
});
export type addUpdateTeam_Body = z.infer<typeof addUpdateTeam_Body>;

export const DefaultSelectResponse = z.object({
	label: z.string(),
	value: z.union([z.string(), z.number(), z.number(), z.string()]),
	gender: z.string().nullish(),
});
export type DefaultSelectResponse = z.infer<typeof DefaultSelectResponse>;

export const order__12 = z
	.object({
		name: z.string().describe("Sorting column name"),
		sort: z.string().describe("Sorting column type"),
	})
	.partial()

	.optional();
export type order__12 = z.infer<typeof order__12>;

export const PlayerLastGameData = z
	.object({
		id: z.number().int().nullable().default(null),
		homeTeamTeamName: z.string().nullable().default(null),
		homeTeamClubName: z.string().nullable().default(null),
		homeTeamScore: z.number().int().nullable().default(null),
		awayTeamTeamName: z.string().nullable().default(null),
		awayTeamClubName: z.string().nullable().default(null),
		awayTeamScore: z.number().int().nullable().default(null),
		awayClubLogo: z.string().nullable().default(null),
		homeClubLogo: z.string().nullable().default(null),
		category: z.string().nullable().default(null),
		date: z.string().datetime({ offset: true }).nullable().default(null),
		type: z.string().nullable().default(null),
		rating: z.number().nullable().default(null),
		analysisApproved: z.boolean().nullable().default(null),
		providerTag: z.string().nullable().default(null),
		isAnalysisFinished: z.number().int().nullable(),
	})
	.partial();
export type PlayerLastGameData = z.infer<typeof PlayerLastGameData>;

export const ClubPlayersInfo = z.object({
	id: z.number().int(),
	playerDataId: z.number().int(),
	firstName: z.string(),
	lastName: z.string().nullable(),
	birthday: z.string().nullable(),
	birthdayPure: z.string().nullish(),
	strongestFoot: z.string().nullable(),
	height: z.union([z.string(), z.number(), z.number(), z.string()]).nullable(),
	weight: z.union([z.string(), z.number(), z.number(), z.string()]).nullable(),
	position: z.string().nullable(),
	position2: z.string().nullable(),
	photo: z.string().nullable(),
	nationality: z.string().nullable(),
	nationality2: z.string().nullable(),
	isRegistered: z.boolean().nullish(),
	editable: z.boolean().nullish(),
	notes: z.string().nullable(),
	vip: z.boolean().nullish(),
	playedInNationalTeam: z.boolean(),
	teamId: z.number().int().nullable(),
	teamName: z.string().nullable(),
	tranLink: z.string().nullish(),
	federationLink: z.string().nullish(),
	isPlayerDataEditable: z.boolean(),
	playerLastGame: PlayerLastGameData.nullish(),
	clubName: z.string().nullish(),
	gender: z.string().nullish().default(null),
});
export type ClubPlayersInfo = z.infer<typeof ClubPlayersInfo>;

export const ClubPlayersResponse = z.object({
	data: z.array(ClubPlayersInfo),
	countWithoutFilter: z.number().int(),
	page: z.number().int(),
	limit: z.number().int(),
	pages: z.number().int(),
	total: z.number().int(),
});
export type ClubPlayersResponse = z.infer<typeof ClubPlayersResponse>;

export const PlayerRequest = z.object({
	birthday: z.string(),
	checked: z.boolean(),
	firstName: z.string(),
	lastName: z.string(),
	height: z.number().int().nullable(),
	weight: z.number().int().nullable(),
	photo: z.string().nullable(),
	strongestFoot: z.string(),
	id: z.number().int().nullable(),
	teamId: z.number().int().nullable(),
	nationality: z.string(),
	nationality2: z.string().nullable(),
	position: z.string(),
	position2: z.string().nullable(),
	notes: z.string().nullable(),
	playedInNationalTeam: z.boolean().nullish(),
	tranLink: z.string().nullable(),
	federationLink: z.string().nullable(),
	gender: z.string().nullable(),
});
export type PlayerRequest = z.infer<typeof PlayerRequest>;

export const addUpdatePlayer_Body = z
	.object({ player: PlayerRequest })
	.partial();
export type addUpdatePlayer_Body = z.infer<typeof addUpdatePlayer_Body>;

export const uploadPlayerPhoto_Body = z
	.object({ file: z.string(), id: z.number().int().nullable() })
	.partial();
export type uploadPlayerPhoto_Body = z.infer<typeof uploadPlayerPhoto_Body>;

export const assignCoachesToTeam_Body = z
	.object({ coachIds: z.array(z.number().int()) })
	.partial();
export type assignCoachesToTeam_Body = z.infer<typeof assignCoachesToTeam_Body>;

export const batchUpdate_Body = z.object({
	playerIds: z.array(z.number().int()),
	teamId: z.number().int().nullish(),
	dob: z.string().nullish(),
	strongestFoot: z.string().nullish(),
	playedInNationalTeam: z.boolean().nullish(),
	position: z.string().nullish(),
	position2: z.string().nullish(),
	nationality: z.string().nullish(),
	nationality2: z.string().nullish(),
	gender: z.string().nullish(),
});
export type batchUpdate_Body = z.infer<typeof batchUpdate_Body>;

export const CreatePlayerPossibleDuplicateRequest = z.object({
	possibleDuplicateId: z
		.number()
		.int()
		.describe("The ID of the newly created player profile"),
	clubId: z
		.number()
		.int()
		.describe(
			"Club context for duplicate search; resolved from the logged-in user when omitted",
		)
		.nullish(),
	firstName: z.string(),
	lastName: z.string(),
	birthday: z.string().describe("Birthday used for duplicate search").nullish(),
	notes: z.string().describe("Optional notes about the duplicate").nullish(),
});
export type CreatePlayerPossibleDuplicateRequest = z.infer<
	typeof CreatePlayerPossibleDuplicateRequest
>;

export const PlayerPossibleDuplicateResponse = z.object({
	id: z.number().int(),
	playerId: z.number().int().nullish(),
	possibleDuplicateId: z.number().int().nullish(),
	similarityScore: z.string().nullish().default(null),
	notes: z.string().nullish().default(null),
});
export type PlayerPossibleDuplicateResponse = z.infer<
	typeof PlayerPossibleDuplicateResponse
>;

export const filters__13 = z
	.array(
		z
			.object({
				type: z
					.enum([
						"exact",
						"search",
						"boolean",
						"exists",
						"inarray",
						"datetimerange",
						"numberrange",
					])
					.describe("Type of filter"),
				field: z
					.enum([
						"possibleDuplicateId",
						"possibleDuplicateFirstName",
						"possibleDuplicateLastName",
						"playerFirstName",
						"playerLastName",
						"similarityScore",
						"notes",
						"status",
						"createdAt",
						"birthday",
					])
					.describe("Property to filter"),
				value: z
					.union([
						z.string(),
						z.number(),
						z.boolean(),
						z.array(z.string()),
						z
							.object({
								start: z.string().datetime({ offset: true }),
								end: z.string().datetime({ offset: true }),
							})
							.partial(),
					])
					.describe(
						"Filter value: string, integer, boolean, string array (e.g. inarray), or date-time range object",
					),
			})
			.partial(),
	)
	.optional();
export type filters__13 = z.infer<typeof filters__13>;

export const order__13 = z
	.object({
		field: z
			.enum([
				"possibleDuplicateFirstName",
				"possibleDuplicateLastName",
				"playerFirstName",
				"playerLastName",
				"similarityScore",
				"status",
				"createdAt",
			])
			.describe("Sorting field"),
		direction: z.enum(["ASC", "DESC"]).describe("Sorting direction"),
	})
	.partial()

	.optional();
export type order__13 = z.infer<typeof order__13>;

export const PlayerPossibleDuplicatesResponse = z
	.object({
		items: z.array(z.record(z.unknown().nullable())).default([]),
		page: z.number().int().default(0),
		limit: z.number().int().default(0),
		pages: z.number().int().default(0),
		total: z.number().int().default(0),
	})
	.partial();
export type PlayerPossibleDuplicatesResponse = z.infer<
	typeof PlayerPossibleDuplicatesResponse
>;

export const PlayerPossibleDuplicatePatchRequest = z
	.object({ status: z.string().nullable(), notes: z.string().nullable() })
	.partial();
export type PlayerPossibleDuplicatePatchRequest = z.infer<
	typeof PlayerPossibleDuplicatePatchRequest
>;

export const order__14 = z
	.object({
		name: z.enum([
			"name",
			"duration",
			"size",
			"createdAt",
			"worker",
			"gameId",
			"metadata.gameId",
			"processingStartedAt",
			"processingFinishedAt",
			"width",
			"bitRate",
			"approvedBy",
			"metadataAddedBy",
			"status",
			"lastEvent",
			"uploadedBy",
			"origin",
			"apiStatus",
			"deletedAt",
			"deletedBy",
			"providerMetadata.clubhouse_name",
			"providerMetadata.recording_slug",
			"veo_team",
		]),
		sort: z.enum(["asc", "desc"]),
	})
	.partial()
	.optional();
export type order__14 = z.infer<typeof order__14>;

export const filters__14 = z
	.array(
		z
			.object({
				field: z.string().describe("Field to filter on"),
				type: z.string().describe("Type of filter"),
				value: z.union([
					z.string(),
					z.number(),
					z
						.object({
							start: z.string().datetime({ offset: true }),
							end: z.string().datetime({ offset: true }),
						})
						.partial(),
					z.array(z.string()),
				]),
			})
			.partial(),
	)
	.optional();
export type filters__14 = z.infer<typeof filters__14>;

export const VideoStatusType = z.enum([
	"UPLOADED",
	"CANCELLED",
	"INGEST_STARTED",
	"INGEST_OK",
	"INGEST_ERROR",
	"PROCESSING_STARTED",
	"PROCESSING_ERROR",
	"PROCESSING_FINISHED",
	"VIDEO_DELETED",
	"VIDEO_ERROR",
]);
export type VideoStatusType = z.infer<typeof VideoStatusType>;

export const VideoEventLogStatusType = z.enum([
	"UPLOADED",
	"BACKUP_STARTED",
	"BACKUP_OK",
	"BACKUP_ERROR",
	"INGEST_STARTED",
	"INGEST_OK",
	"INGEST_ERROR",
	"PROCESSING_STARTED",
	"PROCESSING_ERROR",
	"PROCESSING_KILLED",
	"PROCESSING_FINISHED",
	"INTERMEDIATE_CREATED",
	"INTERMEDIATE_ERROR",
	"VIDEO_CONCAT_STARTED",
	"VIDEO_CONCAT_OK",
	"VIDEO_CONCAT_ERROR",
	"HLS_CREATION_STARTED",
	"HLS_CREATION_OK",
	"HLS_CREATION_ERROR",
	"THUMBNAILS_CREATION_STARTED",
	"THUMBNAILS_CREATION_OK",
	"THUMBNAILS_CREATION_ERROR",
	"POSTER_CREATION_STARTED",
	"POSTER_CREATION_OK",
	"POSTER_CREATION_ERROR",
	"PARTIAL_OUTPUT_UPLOAD_STARTED",
	"PARTIAL_OUTPUT_UPLOAD_OK",
	"PARTIAL_OUTPUT_UPLOAD_ERROR",
	"OUTPUT_UPLOAD_STARTED",
	"OUTPUT_UPLOAD_OK",
	"OUTPUT_UPLOAD_ERROR",
	"RESET_REQUESTED",
	"RETRY_REQUESTED",
	"UPLOAD_DELETED",
	"VIDEO_DELETED",
	"VIDEO_ERROR",
]);
export type VideoEventLogStatusType = z.infer<typeof VideoEventLogStatusType>;

export const VideoUpload = z.object({
	id: z.number().int(),
	name: z.string(),
	file: z.string(),
	size: z.string().nullish(),
	duration: z.string().nullish(),
	bitRate: z.string().nullish(),
	width: z.number().int().nullish(),
	height: z.number().int().nullish(),
	status: VideoStatusType,
	lastEvent: VideoEventLogStatusType.nullish(),
	message: z.string().nullish(),
	context: z.string().nullish(),
	provider: z.string().nullish(),
	origin: z.string().nullish(),
	apiStatus: z.string().nullish(),
	providerMetadata: z
		.union([
			z.string(),
			z.object({}).partial(),
			z.record(z.string()),
			z.string(),
		])
		.nullish(),
	metadata: z.string().nullish(),
	createdAt: z.string(),
	updatedAt: z.string().nullish(),
	backupStartedAt: z.string().nullish(),
	backupFinishedAt: z.string().nullish(),
	partialUploadStartedAt: z.string().nullish(),
	partialUploadFinishedAt: z.string().nullish(),
	uploadStartedAt: z.string().nullish(),
	uploadFinishedAt: z.string().nullish(),
	processingStartedAt: z.string().nullish(),
	processingFinishedAt: z.string().nullish(),
	metadataApprovedAt: z.string().nullish(),
	deletedAt: z.string().nullish(),
	deletedBy: z.string().nullish(),
	lastUpdatedBy: z.string().nullish(),
	metadataAddedBy: z.string().nullish(),
	uploadedBy: z.string().nullish(),
	approvedBy: z.string().nullish(),
	node: z.string().nullish(),
	gpuId: z.number().int().nullish(),
	workerId: z.number().int().nullish(),
});
export type VideoUpload = z.infer<typeof VideoUpload>;

export const VideoUploadsPaginatedResponse = z.object({
	videos: z.array(VideoUpload),
	limit: z.number().int(),
	page: z.number().int(),
	pages: z.number().int(),
	total: z.number().int(),
});
export type VideoUploadsPaginatedResponse = z.infer<
	typeof VideoUploadsPaginatedResponse
>;

export const VideoUploadersResponseDTO = z.object({
	uploaders: z.array(z.string()),
});
export type VideoUploadersResponseDTO = z.infer<
	typeof VideoUploadersResponseDTO
>;

export const VideoUploadLog = z.object({
	id: z.number().int(),
	name: z.string(),
	status: VideoEventLogStatusType,
	message: z.string().nullish(),
	context: z.string().nullish(),
	timestamp: z.string(),
});
export type VideoUploadLog = z.infer<typeof VideoUploadLog>;

export const VideoUploadDetail = z.object({
	logs: z.array(VideoUploadLog).optional().default([]),
	link: z.string().nullish(),
	id: z.number().int(),
	name: z.string(),
	file: z.string(),
	size: z.string().nullish(),
	duration: z.string().nullish(),
	bitRate: z.string().nullish(),
	width: z.number().int().nullish(),
	height: z.number().int().nullish(),
	status: VideoStatusType,
	lastEvent: VideoEventLogStatusType.nullish(),
	message: z.string().nullish(),
	context: z.string().nullish(),
	provider: z.string().nullish(),
	origin: z.string().nullish(),
	apiStatus: z.string().nullish(),
	providerMetadata: z
		.union([
			z.string(),
			z.object({}).partial(),
			z.record(z.string()),
			z.string(),
		])
		.nullish(),
	metadata: z.string().nullish(),
	createdAt: z.string(),
	updatedAt: z.string().nullish(),
	backupStartedAt: z.string().nullish(),
	backupFinishedAt: z.string().nullish(),
	partialUploadStartedAt: z.string().nullish(),
	partialUploadFinishedAt: z.string().nullish(),
	uploadStartedAt: z.string().nullish(),
	uploadFinishedAt: z.string().nullish(),
	processingStartedAt: z.string().nullish(),
	processingFinishedAt: z.string().nullish(),
	metadataApprovedAt: z.string().nullish(),
	deletedAt: z.string().nullish(),
	deletedBy: z.string().nullish(),
	lastUpdatedBy: z.string().nullish(),
	metadataAddedBy: z.string().nullish(),
	uploadedBy: z.string().nullish(),
	approvedBy: z.string().nullish(),
	node: z.string().nullish(),
	gpuId: z.number().int().nullish(),
	workerId: z.number().int().nullish(),
});
export type VideoUploadDetail = z.infer<typeof VideoUploadDetail>;

export const PeriodRequest = z.object({
	period: z.string(),
	start: z
		.string()
		.regex(/(?:(\d+):)?([0-5]?\d):([0-5]?\d)/)
		.nullish()
		.default(null),
	end: z
		.string()
		.regex(/(?:(\d+):)?([0-5]?\d):([0-5]?\d)/)
		.nullish()
		.default(null),
	startTime: z.string().nullish(),
	endTime: z.string().nullish(),
	startTimeInSeconds: z.number().int().nullish(),
	endTimeInSeconds: z.number().int().nullish(),
});
export type PeriodRequest = z.infer<typeof PeriodRequest>;

export const VideoProviderName = z.enum([
	"veo",
	"spiideo",
	"hudl",
	"pixellot",
	"manual",
	"die-ligen",
	"fuchs",
	"wyscout",
	"bepro",
	"sporttotal",
	"vimeo",
	"youtube",
	"facebook",
	"twitch",
	"fifatv",
	"panoris",
	"ljsp",
	"grosfichiers",
	"usf",
	"xbotgo",
	"traceup",
	"ceaf",
	"tccleague",
	"fotbolplay",
	"cloudfront",
	"googledrive",
	"onedrive",
	"dropbox",
	"sharepoint",
	"google",
	"hidrive",
	"wetransfer",
	"swisstransfer",
	"myairbridge",
	"cloudhome",
	"limewire",
	"unknown",
]);
export type VideoProviderName = z.infer<typeof VideoProviderName>;

export const VideoMetadataRequest = z.object({
	gameId: z.number().int().describe("ID of the game"),
	periods: z.array(PeriodRequest).nullish().default([]),
	provider: VideoProviderName.nullish(),
});
export type VideoMetadataRequest = z.infer<typeof VideoMetadataRequest>;

export const GameData = z.object({
	id: z.number().int(),
	isAnalysisFinished: z.boolean().nullable(),
	type: z.string().nullable(),
	date: z.string().nullable(),
	description: z.string().nullable(),
	homeTeamName: z.string().nullable(),
	awayTeamName: z.string().nullable(),
	homeClubName: z.string().nullable(),
	awayClubName: z.string().nullable(),
	homeTeamsScore: z.number().int().nullable(),
	awayTeamsScore: z.number().int().nullable(),
	homeClubLogo: z.string().nullable(),
	awayClubLogo: z.string().nullable(),
	isFavorite: z.boolean(),
	category: z.string().nullable().default(null),
	playerRating: z.number().nullish().default(null),
	minutesPlayed: z.number().int().nullish().default(null),
	goalsScored: z.number().int().nullish().default(null),
	videoUploadId: z.number().int().nullish().default(null),
	isClickable: z.boolean().nullish(),
	imageUrl: z.string().nullable().default(null),
	gender: z.string().nullable().default(null),
});
export type GameData = z.infer<typeof GameData>;

export const filters__15 = z
	.array(
		z
			.object({
				type: z
					.enum([
						"exact",
						"search",
						"boolean",
						"exists",
						"inarray",
						"datetimerange",
						"numberrange",
					])
					.describe("Type of filter"),
				field: z.enum(["clubId", "clubName"]).describe("Property to filter"),
				value: z
					.union([
						z.string(),
						z.number(),
						z.boolean(),
						z
							.object({
								start: z.string().datetime({ offset: true }),
								end: z.string().datetime({ offset: true }),
							})
							.partial(),
					])
					.describe(
						"Filter value, can be a string, integer, date or a date range",
					),
			})
			.partial(),
	)
	.optional();
export type filters__15 = z.infer<typeof filters__15>;

export const ClubDetailResponseDTO = z.object({
	id: z.number().int(),
	name: z.string(),
	country: z.string(),
	countryCode: z.string(),
	logo: z.string().nullable(),
	region: z.string().nullable(),
});
export type ClubDetailResponseDTO = z.infer<typeof ClubDetailResponseDTO>;

export const GameDetail = z.object({
	id: z.number().int(),
	homeTeamName: z.string().nullish(),
	homeTeamLogo: z.string().nullish(),
	homeTeamsScore: z.number().int().nullish(),
	awayTeamName: z.string().nullish(),
	awayTeamLogo: z.string().nullish(),
	awayTeamsScore: z.number().int().nullish(),
	category: z.string().nullish(),
	date: z.string(),
	type: z.string().nullish(),
	homeClubName: z.string().nullish(),
	awayClubName: z.string().nullish(),
	veoLink: z.string().nullish(),
});
export type GameDetail = z.infer<typeof GameDetail>;

export const VideoRenameRequest = z.object({
	name: z.string().max(255).describe("New name for the video").default(""),
});
export type VideoRenameRequest = z.infer<typeof VideoRenameRequest>;

export const VideoServerWorkerStatusResponse = z.object({
	id: z.string(),
	lastUpdate: z.string().datetime({ offset: true }),
	status: z.literal("idle").default("idle"),
});
export type VideoServerWorkerStatusResponse = z.infer<
	typeof VideoServerWorkerStatusResponse
>;

export const VideoServerBackupWorkerBusyResponse = z.object({
	videoId: z.string(),
	name: z.string(),
	file: z.string(),
	status: z.literal("busy").default("busy"),
	id: z.string(),
	lastUpdate: z.string().datetime({ offset: true }),
});
export type VideoServerBackupWorkerBusyResponse = z.infer<
	typeof VideoServerBackupWorkerBusyResponse
>;

export const VideoServerUploadWorkerBusyResponse = z.object({
	videoId: z.string(),
	name: z.string(),
	status: z.literal("busy").default("busy"),
	id: z.string(),
	lastUpdate: z.string().datetime({ offset: true }),
});
export type VideoServerUploadWorkerBusyResponse = z.infer<
	typeof VideoServerUploadWorkerBusyResponse
>;

export const VideoServerIngestWorkerBusyResponse = z.object({
	videoId: z.string(),
	name: z.string(),
	file: z.string(),
	uploadedBy: z.string(),
	status: z.literal("busy").default("busy"),
	id: z.string(),
	lastUpdate: z.string().datetime({ offset: true }),
});
export type VideoServerIngestWorkerBusyResponse = z.infer<
	typeof VideoServerIngestWorkerBusyResponse
>;

export const VideoServerProcessingWorkerBusyResponse = z.object({
	videoId: z.string(),
	name: z.string(),
	gpuId: z.string(),
	node: z.string(),
	status: z.literal("busy").default("busy"),
	id: z.string(),
	lastUpdate: z.string().datetime({ offset: true }),
});
export type VideoServerProcessingWorkerBusyResponse = z.infer<
	typeof VideoServerProcessingWorkerBusyResponse
>;

export const VideoServerStatusWorkersResponse = z.object({
	"backup-workers": z.array(
		z.discriminatedUnion("status", [
			VideoServerWorkerStatusResponse,
			VideoServerBackupWorkerBusyResponse,
		]),
	),
	"upload-workers": z.array(
		z.discriminatedUnion("status", [
			VideoServerWorkerStatusResponse,
			VideoServerUploadWorkerBusyResponse,
		]),
	),
	"ingest-workers": z.array(
		z.discriminatedUnion("status", [
			VideoServerWorkerStatusResponse,
			VideoServerIngestWorkerBusyResponse,
		]),
	),
	"processing-workers": z.record(
		z.array(
			z.discriminatedUnion("status", [
				VideoServerWorkerStatusResponse,
				VideoServerProcessingWorkerBusyResponse,
			]),
		),
	),
	"processing-workers-auto": z.record(
		z.array(
			z.discriminatedUnion("status", [
				VideoServerWorkerStatusResponse,
				VideoServerProcessingWorkerBusyResponse,
			]),
		),
	),
});
export type VideoServerStatusWorkersResponse = z.infer<
	typeof VideoServerStatusWorkersResponse
>;

export const VideoServerStatusGpuResponse = z.object({
	id: z.string(),
	name: z.string(),
	utilization: z.string(),
	memory_utilization: z.string(),
	total_memory: z.string(),
	used_memory: z.string(),
	free_memory: z.string(),
	temperature: z.string(),
	power_draw: z.string(),
	fan_speed: z.string(),
	core_clock: z.string(),
	memory_clock: z.string(),
	enc_utilization: z.string(),
	dec_utilization: z.string(),
	last_update: z.string().datetime({ offset: true }),
});
export type VideoServerStatusGpuResponse = z.infer<
	typeof VideoServerStatusGpuResponse
>;

export const VideoServerStatusGpusResponse = z.record(
	z.array(VideoServerStatusGpuResponse),
);
export type VideoServerStatusGpusResponse = z.infer<
	typeof VideoServerStatusGpusResponse
>;

export const VideoServerStatusResponse = z.object({
	workers: VideoServerStatusWorkersResponse,
	gpus: VideoServerStatusGpusResponse,
});
export type VideoServerStatusResponse = z.infer<
	typeof VideoServerStatusResponse
>;

export const migratePlayers_Body = z
	.object({ playerIds: z.array(z.number().int()), teamId: z.number().int() })
	.partial();
export type migratePlayers_Body = z.infer<typeof migratePlayers_Body>;

export const PlayerProfilePatchRequest = z.object({
	id: z.number().int(),
	nationality: z.string().nullish(),
	nationality2: z.string().nullish(),
	birthday: z.string().datetime({ offset: true }).nullish(),
	strongestFoot: z.enum(["left", "right"]).nullish(),
	height: z.number().int().nullish(),
	weight: z.number().int().nullish(),
	position: z.string().nullish(),
	position2: z.string().nullish(),
	ageGroup: z.string().nullish(),
	introVideo: z.string().nullish(),
	lineUpVideo: z.string().nullish(),
	requestedFields: z.array(z.string()).optional().default([]),
});
export type PlayerProfilePatchRequest = z.infer<
	typeof PlayerProfilePatchRequest
>;

export const filters__16 = z
	.array(
		z
			.object({
				type: z
					.enum([
						"exact",
						"search",
						"boolean",
						"exists",
						"inarray",
						"datetimerange",
						"numberrange",
					])
					.describe("Type of filter"),
				field: z
					.enum([
						"clubId",
						"firstName",
						"lastName",
						"phoneNumber",
						"email",
						"isActive",
						"acceptedTermsOfUse",
						"createdAt",
						"playerId",
					])
					.describe("Property to filter"),
				value: z
					.union([
						z.string(),
						z.number(),
						z.boolean(),
						z
							.object({
								start: z.string().datetime({ offset: true }),
								end: z.string().datetime({ offset: true }),
							})
							.partial(),
					])
					.describe(
						"Filter value, can be a string, integer, date or a date range",
					),
			})
			.partial(),
	)
	.optional();
export type filters__16 = z.infer<typeof filters__16>;

export const order__15 = z
	.object({
		field: z
			.enum([
				"clubName",
				"createdAt",
				"firstName",
				"lastName",
				"phoneNumber",
				"email",
				"playerId",
			])
			.describe("Sorting field"),
		direction: z.enum(["ASC", "DESC"]).describe("Sorting direction"),
	})
	.partial()

	.optional();
export type order__15 = z.infer<typeof order__15>;

export const PlayerUserRowResponse = z
	.object({
		id: z.number().int().default(0),
		clubId: z.number().int().nullable(),
		playerId: z.number().int().nullable(),
		clubName: z.string().nullable(),
		firstName: z.string().default(""),
		lastName: z.string().default(""),
		email: z.string().nullable(),
		phoneNumber: z.string().nullable(),
		isActive: z.boolean().default(false),
		acceptedTermsOfUse: z.boolean().default(false),
		createdAt: z.string().datetime({ offset: true }).nullable(),
	})
	.partial();
export type PlayerUserRowResponse = z.infer<typeof PlayerUserRowResponse>;

export const PlayerUsersResponse = z
	.object({
		items: z.array(PlayerUserRowResponse).default([]),
		page: z.number().int().default(0),
		limit: z.number().int().default(0),
		pages: z.number().int().default(0),
		total: z.number().int().default(0),
	})
	.partial();
export type PlayerUsersResponse = z.infer<typeof PlayerUsersResponse>;

export const PlayerUserPatchRequest = z.object({
	id: z.number().int(),
	clubId: z.number().int().nullish(),
	playerId: z.number().int().nullish(),
	email: z.string().nullish(),
	phoneNumber: z.string().nullish(),
	firstName: z.string().max(255).nullish(),
	lastName: z.string().max(255).nullish(),
	locale: z.string().nullish(),
	isActive: z.boolean().nullish(),
	requestedFields: z.array(z.string()).optional().default([]),
});
export type PlayerUserPatchRequest = z.infer<typeof PlayerUserPatchRequest>;

export const filters__17 = z
	.array(
		z
			.object({
				type: z.enum([
					"exact",
					"search",
					"boolean",
					"exists",
					"inarray",
					"datetimerange",
					"numberrange",
				]),
				field: z.enum([
					"product",
					"proClubId",
					"active",
					"startDate",
					"expirationDate",
					"pipedriveDealId",
					"id",
					"teamGroupId",
					"gameOptions",
					"eyeballAddons",
				]),
				value: z.union([
					z.string(),
					z.number(),
					z.boolean(),
					z.array(z.string()),
					z
						.object({
							start: z.string().nullable(),
							end: z.string().nullable(),
						})
						.partial(),
				]),
			})
			.partial(),
	)
	.optional();
export type filters__17 = z.infer<typeof filters__17>;

export const order__16 = z
	.object({
		field: z.enum([
			"product",
			"startDate",
			"expirationDate",
			"maxAccounts",
			"proClubName",
			"id",
		]),
		direction: z.enum(["ASC", "DESC"]),
	})
	.partial()

	.optional();
export type order__16 = z.infer<typeof order__16>;

export const EyeballAddons = z.object({
	ai_scout: z.boolean(),
	ai_support_pack: z.boolean(),
	ai_player_data: z.boolean(),
	efg: z.boolean(),
});
export type EyeballAddons = z.infer<typeof EyeballAddons>;

export const SubscriptionData = z.object({
	id: z.number().int(),
	comment: z.string().nullish(),
	product: z.string(),
	proClubName: z.string().nullish(),
	proClubId: z.number().int().nullish(),
	countries: z.array(
		z
			.object({
				label: z.string().describe("label"),
				value: z.string().describe("value"),
				continent: z.string().describe("continent"),
			})
			.partial(),
	),
	countriesCount: z.union([z.number(), z.string()]).nullish(),
	nationalCountries: z.array(
		z.object({ label: z.string(), value: z.string() }).partial(),
	),
	discoverCountries: z.array(z.string()),
	gameOptions: z
		.object({
			ai: z.boolean().describe("ai"),
			statistics_rating: z.boolean().describe("statistics_rating"),
			statistics_players: z.boolean().describe("statistics_players"),
			statistics_teams: z.boolean().describe("statistics_teams"),
			events: z.boolean().describe("events"),
			clips: z.boolean().describe("clips"),
			downloadable: z.boolean().describe("downloadable"),
			downloadable_gametags: z.boolean().describe("downloadable_gametags"),
			player_stats_search: z.boolean().describe("player_stats_search"),
			physical_metrics: z.boolean().describe("physical_metrics"),
			clips_across_games: z.boolean().describe("clips_across_games"),
		})
		.partial()

		.nullish(),
	eyeballAddons: EyeballAddons,
	maxAccounts: z.number().int().nullish(),
	startDate: z.string().nullish(),
	expirationDate: z.string().nullish(),
	active: z.boolean(),
	expiresIn: z.string().nullish(),
	ownerships: z.array(z.unknown()),
	pipedriveDealId: z.string().nullish(),
	genderOptions: z
		.object({
			Female: z.boolean().describe("Female"),
			Male: z.boolean().describe("Male"),
		})
		.partial()

		.nullish(),
	teamGroupId: z.number().int().nullish(),
	teamGroupName: z.string().nullish(),
});
export type SubscriptionData = z.infer<typeof SubscriptionData>;

export const SubscriptionRowResponse = SubscriptionData;
export type SubscriptionRowResponse = z.infer<typeof SubscriptionRowResponse>;

export const SubscriptionsResponse = z
	.object({
		items: z.array(SubscriptionRowResponse).default([]),
		page: z.number().int().default(0),
		limit: z.number().int().default(0),
		pages: z.number().int().default(0),
		total: z.number().int().default(0),
	})
	.partial();
export type SubscriptionsResponse = z.infer<typeof SubscriptionsResponse>;

export const SubscriptionUpsertRequest = z
	.object({
		comment: z.string().nullable(),
		product: z.string().nullable(),
		maxAccounts: z.number().int().nullable(),
		startDate: z.string().nullable(),
		expirationDate: z.string().nullable(),
		countries: z.record(z.array(z.string())).nullable(),
		nationalCountries: z.record(z.array(z.string())).nullable(),
		discoverCountries: z.array(z.string()).nullable(),
		gameOptions: z.record(z.boolean()).nullable(),
		ownership: z.array(z.record(z.union([z.number(), z.string()]))).nullable(),
		proClub: z.number().int().nullable(),
		pipedriveDealId: z.string().max(255).nullable(),
		eyeballAddons: z.record(z.boolean()).nullable(),
		genderOptions: z.record(z.boolean()).nullable(),
		teamGroupId: z.number().int().nullable(),
	})
	.partial();
export type SubscriptionUpsertRequest = z.infer<
	typeof SubscriptionUpsertRequest
>;

export const SubscriptionBehaviorResponse = z
	.object({
		hasCountries: z.boolean().default(false),
		hasNationalContinents: z.boolean().default(false),
		hasGameOptions: z.boolean().default(false),
		hasGameOptionsRequired: z.boolean().default(false),
		hasGenderOptions: z.boolean().default(false),
		hasOwnerships: z.boolean().default(false),
		hasAddons: z.boolean().default(false),
		hasAddonsRequired: z.boolean().default(false),
		hasTeamGroup: z.boolean().default(false),
		hasAccountsLimit: z.boolean().default(false),
		isCommentReadonly: z.boolean().default(false),
		hasDiscoverCountries: z.boolean().default(false),
		isDiscover: z.boolean().default(false),
	})
	.partial();
export type SubscriptionBehaviorResponse = z.infer<
	typeof SubscriptionBehaviorResponse
>;

export const SubscriptionProductConfigItemResponse = z.object({
	value: z.string().nullish(),
	label: z.string().optional().default(""),
	behavior: SubscriptionBehaviorResponse,
});
export type SubscriptionProductConfigItemResponse = z.infer<
	typeof SubscriptionProductConfigItemResponse
>;

export const SubscriptionProductConfigResponse = z.object({
	products: z
		.array(SubscriptionProductConfigItemResponse)
		.optional()
		.default([]),
	defaultProductConfig: SubscriptionProductConfigItemResponse,
});
export type SubscriptionProductConfigResponse = z.infer<
	typeof SubscriptionProductConfigResponse
>;

export const filters__18 = z
	.array(
		z
			.object({
				type: z.enum([
					"exact",
					"search",
					"boolean",
					"exists",
					"inarray",
					"datetimerange",
					"numberrange",
				]),
				field: z.enum([
					"id",
					"name",
					"teamId",
					"clubId",
					"scoutVisibility",
					"sharedAnalysis",
					"visibleTeamsheets",
				]),
				value: z.union([
					z.string(),
					z.number(),
					z.boolean(),
					z.array(z.number().int()),
					z.array(z.string()),
				]),
			})
			.partial(),
	)
	.optional();
export type filters__18 = z.infer<typeof filters__18>;

export const order__17 = z
	.object({
		field: z.enum([
			"id",
			"name",
			"scoutVisibility",
			"sharedAnalysis",
			"visibleTeamsheets",
			"createdAt",
		]),
		direction: z.enum(["ASC", "DESC"]),
	})
	.partial()

	.optional();
export type order__17 = z.infer<typeof order__17>;

export const TeamGroupTeamResponse = z
	.object({
		id: z.number().int().default(0),
		name: z.string().default(""),
		country: z.string().nullable(),
	})
	.partial();
export type TeamGroupTeamResponse = z.infer<typeof TeamGroupTeamResponse>;

export const TeamGroupClubResponse = z
	.object({
		id: z.number().int().default(0),
		name: z.string().default(""),
		countryCode: z.string().nullable(),
	})
	.partial();
export type TeamGroupClubResponse = z.infer<typeof TeamGroupClubResponse>;

export const TeamGroupRowResponse = z
	.object({
		id: z.number().int().default(0),
		name: z.string().default(""),
		notes: z.string().nullable(),
		scoutVisibility: z.boolean().default(false),
		sharedAnalysis: z.boolean().default(false),
		visibleTeamsheets: z.boolean().default(false),
		createdAt: z.string().datetime({ offset: true }).nullable(),
		teamCount: z.number().int().default(0),
		gameCount: z.number().int().default(0),
		directTeamNames: z.string().nullable(),
		allTeamNames: z.string().nullable(),
		clubNames: z.string().nullable(),
		teams: z.array(TeamGroupTeamResponse).default([]),
		clubs: z.array(TeamGroupClubResponse).default([]),
	})
	.partial();
export type TeamGroupRowResponse = z.infer<typeof TeamGroupRowResponse>;

export const TeamGroupsResponse = z
	.object({
		items: z.array(TeamGroupRowResponse).default([]),
		page: z.number().int().default(0),
		limit: z.number().int().default(0),
		pages: z.number().int().default(0),
		total: z.number().int().default(0),
	})
	.partial();
export type TeamGroupsResponse = z.infer<typeof TeamGroupsResponse>;

export const TeamGroupUpsertRequest = z
	.object({
		name: z.string().max(180).default(""),
		notes: z.string().max(180).nullable(),
		teamIds: z
			.union([z.array(z.number().int()), z.record(z.number().int())])
			.default([]),
		clubIds: z
			.union([z.array(z.number().int()), z.record(z.number().int())])
			.default([]),
		scoutVisibility: z.boolean().default(false),
		sharedAnalysis: z.boolean().default(false),
		visibleTeamsheets: z.boolean().default(false),
	})
	.partial();
export type TeamGroupUpsertRequest = z.infer<typeof TeamGroupUpsertRequest>;

export const filters__19 = z
	.array(
		z
			.object({
				type: z
					.enum([
						"exact",
						"search",
						"boolean",
						"exists",
						"inarray",
						"datetimerange",
						"numberrange",
					])
					.describe("Type of filter"),
				field: z
					.enum([
						"clubId",
						"playerUserId",
						"playerId",
						"firstName",
						"middleName",
						"lastName",
						"phoneNumber",
						"email",
						"createdAt",
					])
					.describe("Property to filter"),
				value: z
					.union([
						z.string(),
						z.number(),
						z.boolean(),
						z
							.object({
								start: z.string().datetime({ offset: true }),
								end: z.string().datetime({ offset: true }),
							})
							.partial(),
					])
					.describe(
						"Filter value, can be a string, integer, date or a date range",
					),
			})
			.partial(),
	)
	.optional();
export type filters__19 = z.infer<typeof filters__19>;

export const order__18 = z
	.object({
		field: z
			.enum([
				"clubName",
				"createdAt",
				"firstName",
				"middleName",
				"lastName",
				"phoneNumber",
				"email",
				"playerId",
				"playerUserId",
			])
			.describe("Sorting field"),
		direction: z.enum(["ASC", "DESC"]).describe("Sorting direction"),
	})
	.partial()

	.optional();
export type order__18 = z.infer<typeof order__18>;

export const SignUpRequestRowResponse = z
	.object({
		id: z.string().default(""),
		clubId: z.number().int().nullable(),
		clubName: z.string().nullable(),
		firstName: z.string().default(""),
		middleName: z.string().nullable(),
		lastName: z.string().default(""),
		email: z.string().nullable(),
		phoneNumber: z.string().nullable(),
		playerId: z.number().int().nullable(),
		playerUserId: z.number().int().nullable(),
		createdAt: z.string().datetime({ offset: true }).nullable(),
	})
	.partial();
export type SignUpRequestRowResponse = z.infer<typeof SignUpRequestRowResponse>;

export const SignUpRequestsResponse = z
	.object({
		items: z.array(SignUpRequestRowResponse).default([]),
		page: z.number().int().default(0),
		limit: z.number().int().default(0),
		pages: z.number().int().default(0),
		total: z.number().int().default(0),
	})
	.partial();
export type SignUpRequestsResponse = z.infer<typeof SignUpRequestsResponse>;

export const SignUpRequestPatchRequest = z.object({
	id: z.string(),
	clubId: z.number().int().nullish(),
	previousSeasonClubId: z.number().int().nullish(),
	clubName: z.string().max(255).nullish(),
	email: z.string().max(180).nullish(),
	phoneNumber: z.string().max(255).nullish(),
	firstName: z.string().max(255).nullish(),
	middleName: z.string().max(255).nullish(),
	lastName: z.string().max(255).nullish(),
	birthDate: z.string().datetime({ offset: true }).nullish(),
	countryOfResidence: z.string().nullish(),
	internalNote: z.string().nullish(),
	requestedFields: z.array(z.string()).optional().default([]),
});
export type SignUpRequestPatchRequest = z.infer<
	typeof SignUpRequestPatchRequest
>;

export const PartialPlayerResponse = z
	.object({ id: z.number().int().default(0), name: z.string().default("") })
	.partial();
export type PartialPlayerResponse = z.infer<typeof PartialPlayerResponse>;

export const PartialSignUpRequestResponse = z
	.object({
		id: z.string().default(""),
		player: PartialPlayerResponse.nullable().default(null),
		clubId: z.number().int().nullable().default(null),
		previousSeasonClubId: z.number().int().nullable().default(null),
		clubName: z.string().nullable().default(null),
		firstName: z.string().default(""),
		middleName: z.string().nullable().default(null),
		lastName: z.string().default(""),
		email: z.string().nullable().default(null),
		birthDate: z.string().datetime({ offset: true }).nullable().default(null),
		countryOfResidence: z.string().nullable().default(null),
		countryOfResidenceName: z.string().nullable().default(null),
		phoneNumber: z.string().nullable().default(null),
		otpVerified: z.boolean().default(false),
		stepName: z.string().nullable().default(null),
		termsOfConditions: z.boolean().default(false),
		playerCanBeMatched: z.boolean().nullable().default(null),
		playerNotMatched: z.boolean().nullable().default(null),
		internalNote: z.string().nullable().default(null),
		createdAt: z.string().datetime({ offset: true }).nullable().default(null),
		updatedAt: z.string().datetime({ offset: true }).nullable().default(null),
	})
	.partial();
export type PartialSignUpRequestResponse = z.infer<
	typeof PartialSignUpRequestResponse
>;

export const PartialUserResponse = z
	.object({ id: z.number().int().default(0), name: z.string().default("") })
	.partial();
export type PartialUserResponse = z.infer<typeof PartialUserResponse>;

export const PartialClubResponse = z
	.object({ id: z.number().int().default(0), name: z.string().default("") })
	.partial();
export type PartialClubResponse = z.infer<typeof PartialClubResponse>;

export const PartialTermsOfUseResponse = z
	.object({
		id: z.number().int().nullable().default(null),
		version: z.number().int().nullable().default(null),
	})
	.partial();
export type PartialTermsOfUseResponse = z.infer<
	typeof PartialTermsOfUseResponse
>;

export const PartialPlayerUserResponse = z.object({
	id: z.number().int().optional().default(0),
	isActive: z.boolean(),
	firstName: z.string().optional().default(""),
	lastName: z.string().optional().default(""),
	email: z.string().nullish().default(null),
	phoneNumber: z.string().nullish().default(null),
	locale: z.string().optional().default(""),
	timezone: z.string().nullish().default(null),
	acceptedTermsOfUse: z.boolean().optional().default(false),
	acceptedTermsOfUseAt: z
		.string()
		.datetime({ offset: true })
		.nullish()
		.default(null),
	resetPasswordToken: z.string().nullish().default(null),
	passwordMigrated: z.boolean().optional().default(false),
	resetPasswordTokenRequestedAt: z
		.string()
		.datetime({ offset: true })
		.nullish()
		.default(null),
	deactivateReason: z.string().nullish().default(null),
	remainingLoginAttempt: z.number().int().nullish().default(null),
	lastChangelogRead: z.string().nullish().default(null),
	photo: z.string().nullish().default(null),
	pendingRemoval: z.boolean().optional().default(false),
	pendingRemovalAt: z
		.string()
		.datetime({ offset: true })
		.nullish()
		.default(null),
	pendingRemovalReason: z.string().nullish().default(null),
	hasNewsletterSubscription: z.boolean().nullish(),
	guardianApprovalRequired: z.boolean().optional().default(false),
	createdAt: z.string().datetime({ offset: true }).nullish().default(null),
	createdBy: PartialUserResponse.nullish().default(null),
	updatedAt: z.string().datetime({ offset: true }).nullish().default(null),
	lastUpdatedBy: PartialUserResponse.nullish().default(null),
	deletedAt: z.string().datetime({ offset: true }).nullish().default(null),
	deletedBy: PartialUserResponse.nullish().default(null),
	player: PartialPlayerResponse.nullish().default(null),
	club: PartialClubResponse.nullish().default(null),
	termsOfUse: PartialTermsOfUseResponse.nullish().default(null),
});
export type PartialPlayerUserResponse = z.infer<
	typeof PartialPlayerUserResponse
>;

export const PartialPlayerProfileResponse = z
	.object({
		id: z.number().int().default(0),
		nationality: z.string().nullable().default(""),
		nationality2: z.string().nullable().default(""),
		birthday: z.string().datetime({ offset: true }).nullable().default(null),
		strongestFoot: z.string().nullable().default(null),
		height: z.number().int().nullable().default(null),
		weight: z.number().nullable().default(null),
		position: z.string().nullable().default(null),
		position2: z.string().nullable().default(null),
		ageGroup: z.string().nullable().default(null),
		introVideo: z.string().nullable().default(null),
		lineUpVideo: z.string().nullable().default(null),
	})
	.partial();
export type PartialPlayerProfileResponse = z.infer<
	typeof PartialPlayerProfileResponse
>;

export const PartialGuardianApprovalResponse = z
	.object({
		id: z.string().default(""),
		player: PartialPlayerResponse.nullable().default(null),
		guardianFirstName: z.string().nullable().default(""),
		guardianLastName: z.string().nullable().default(""),
		guardianEmail: z.string().nullable().default(""),
		guardianFilledFirstName: z.string().nullable().default(null),
		guardianFilledLastName: z.string().nullable().default(null),
		guardianFilledEmail: z.string().nullable().default(null),
		guardianFilledRole: z.string().nullable().default(null),
		createdAt: z.string().datetime({ offset: true }).nullable().default(null),
		updatedAt: z.string().datetime({ offset: true }).nullable().default(null),
		approvedAt: z.string().datetime({ offset: true }).nullable().default(null),
		status: z.string().default(""),
	})
	.partial();
export type PartialGuardianApprovalResponse = z.infer<
	typeof PartialGuardianApprovalResponse
>;

export const PartialPlayerMappingResultResponse = z.object({
	player: PartialPlayerResponse,
	photo: z.string().nullish().default(null),
	id: z.number().int().optional().default(0),
	score: z.number().int().optional().default(0),
	mandatoryConditionsMet: z.boolean().optional().default(false),
	resultData: z.string().optional().default("{}"),
	conditionsData: z.string().optional().default("{}"),
	createdAt: z.string().datetime({ offset: true }).nullish().default(null),
	updatedAt: z.string().datetime({ offset: true }).nullish().default(null),
});
export type PartialPlayerMappingResultResponse = z.infer<
	typeof PartialPlayerMappingResultResponse
>;

export const SignUpRequestDataResponse = z
	.object({
		signUpRequest: PartialSignUpRequestResponse.nullable().default(null),
		playerUser: PartialPlayerUserResponse.nullable().default(null),
		playerProfile: PartialPlayerProfileResponse.nullable().default(null),
		guardianApprovals: z.array(PartialGuardianApprovalResponse).default([]),
		playerMappingResults: z
			.array(PartialPlayerMappingResultResponse)
			.default([]),
		playerMappingResultsStatic: z
			.array(PartialPlayerMappingResultResponse)
			.default([]),
	})
	.partial();
export type SignUpRequestDataResponse = z.infer<
	typeof SignUpRequestDataResponse
>;

export const filters__20 = z
	.array(
		z
			.object({
				type: z
					.enum([
						"exact",
						"search",
						"boolean",
						"exists",
						"inarray",
						"datetimerange",
						"numberrange",
					])
					.describe("Type of filter"),
				field: z
					.enum([
						"id",
						"amount",
						"clubId",
						"clubName",
						"teamId",
						"teamName",
						"relatedClubId",
						"relatedClubName",
						"relatedTeamId",
						"relatedTeamName",
						"userId",
						"userName",
						"movementType",
						"authorType",
						"source",
						"purchasedAt",
						"createdAt",
						"note",
						"paymentLink",
					])
					.describe("Property to filter"),
				value: z
					.union([
						z.string(),
						z.number(),
						z.boolean(),
						z
							.object({
								start: z.string().datetime({ offset: true }),
								end: z.string().datetime({ offset: true }),
							})
							.partial(),
					])
					.describe(
						"Filter value, can be a string, integer, date or a date range",
					),
			})
			.partial(),
	)
	.optional();
export type filters__20 = z.infer<typeof filters__20>;

export const order__19 = z
	.object({
		field: z
			.enum([
				"id",
				"amount",
				"clubName",
				"teamName",
				"relatedClubName",
				"relatedTeamName",
				"userName",
				"movementType",
				"purchasedAt",
				"createdAt",
				"note",
				"authorType",
				"paymentLink",
			])
			.describe("Sorting field"),
		direction: z.enum(["ASC", "DESC"]).describe("Sorting direction"),
	})
	.partial()

	.optional();
export type order__19 = z.infer<typeof order__19>;

export const TokenMovementSource = z.enum([
	"eyeball_admin",
	"clubhouse_admin",
	"team_admin",
	"user",
	"system",
]);
export type TokenMovementSource = z.infer<typeof TokenMovementSource>;

export const DiscoverTokenMovementRowResponse = z.object({
	id: z.number().int(),
	clubId: z.number().int().nullish(),
	clubName: z.string().nullish(),
	teamId: z.number().int().nullish(),
	teamName: z.string().nullish(),
	relatedClubId: z.number().int().nullish(),
	relatedClubName: z.string().nullish(),
	relatedTeamId: z.number().int().nullish(),
	relatedTeamName: z.string().nullish(),
	userId: z.number().int().nullish(),
	userName: z.string().nullish(),
	amount: z.number().int().optional().default(0),
	movementType: TokenMovementType,
	authorType: TokenMovementAuthorType,
	note: z.string().nullish(),
	purchasedAt: z.string().datetime({ offset: true }).nullish(),
	createdAt: z.string().datetime({ offset: true }).nullish(),
	processingLevel: ProcessingLevelEnum.nullish(),
	homeClubName: z.string().nullish(),
	homeTeamName: z.string().nullish(),
	awayClubName: z.string().nullish(),
	awayTeamName: z.string().nullish(),
	receiptUrl: z.string().nullish(),
	source: TokenMovementSource,
});
export type DiscoverTokenMovementRowResponse = z.infer<
	typeof DiscoverTokenMovementRowResponse
>;

export const DiscoverTokenMovementsResponse = z.object({
	page: z.number().int().default(0),
	limit: z.number().int().default(0),
	pages: z.number().int().default(0),
	total: z.number().int().default(0),
	items: z.array(DiscoverTokenMovementRowResponse).default([]),
});
export type DiscoverTokenMovementsResponse = z.infer<
	typeof DiscoverTokenMovementsResponse
>;

export const DiscoverTokenMovementPostRequest = z.object({
	type: TokenMovementType,
	amount: z.number().int().gt(0),
	purchasedAt: z.string().datetime({ offset: true }).nullish().default(null),
	note: z.string().nullish().default(null),
	paymentLink: z.string().nullish().default(null),
	clubId: z.number().int().nullish().default(null),
	teamId: z.number().int().nullish().default(null),
	relatedClubId: z.number().int().nullish().default(null),
	relatedTeamIds: z.array(z.number().int()).optional().default([]),
	authorType: TokenMovementAuthorType.nullish().default("user"),
});
export type DiscoverTokenMovementPostRequest = z.infer<
	typeof DiscoverTokenMovementPostRequest
>;

export const FileResponse = z.object({
	fileName: z.string(),
	file: z.string(),
	fileType: z.string(),
});
export type FileResponse = z.infer<typeof FileResponse>;

export const UpdateGameLeftTeamRequest = z
	.object({
		homeTeamStartsOnLeft: z
			.union([z.literal(true), z.literal(false), z.literal(null)])
			.nullable(),
	})
	.partial();
export type UpdateGameLeftTeamRequest = z.infer<
	typeof UpdateGameLeftTeamRequest
>;

export const AnalysisStatus = z.enum(["approved", "disapproved"]);
export type AnalysisStatus = z.infer<typeof AnalysisStatus>;

export const UpdateAnalysisApprovalRequest = z.object({
	approvalStatus: AnalysisStatus,
});
export type UpdateAnalysisApprovalRequest = z.infer<
	typeof UpdateAnalysisApprovalRequest
>;

export const GameResyncRequest = z
	.object({
		abortResync: z.boolean().nullable().default(null),
		resyncAfter: z.string().nullable().default(null),
		resyncType: z.string().nullable().default(null),
		syncLocally: z.boolean().nullable().default(null),
		skipReview: z.boolean().nullable().default(null),
	})
	.partial();
export type GameResyncRequest = z.infer<typeof GameResyncRequest>;

export const shareClipLink_Body = z.object({
	expireDate: z.string(),
	password: z.string(),
	description: z.string(),
	isPasswordUpdated: z.boolean().optional(),
});
export type shareClipLink_Body = z.infer<typeof shareClipLink_Body>;

export const ItemToBuyResponse = z.object({
	key: z.string(),
	price: z.number().int(),
});
export type ItemToBuyResponse = z.infer<typeof ItemToBuyResponse>;

export const TokenBalanceResponse = z.object({
	tokenBalance: z.number().int(),
	itemsToBuy: z.array(ItemToBuyResponse).default([]),
});
export type TokenBalanceResponse = z.infer<typeof TokenBalanceResponse>;

export const VideoIngestorVideoStatus = z.enum([
	"pending",
	"queued",
	"initialized",
	"downloading",
	"downloaded",
	"migrated",
	"error",
	"optimized",
]);
export type VideoIngestorVideoStatus = z.infer<typeof VideoIngestorVideoStatus>;

export const filters__21 = z
	.object({
		league: z.string().nullable(),
		home: z.number().int().nullable(),
		away: z.number().int().nullable(),
		country: z.string().nullable(),
		season: z.string().nullable(),
		video_status: VideoIngestorVideoStatus.nullable(),
		teamsheet_status: z.string().nullable(),
		sort: z.string().nullable(),
		page: z.number().int().default(1),
		limit: z.number().int().default(10),
		eyeball_game_id: z.number().int().nullable(),
	})
	.partial()

	.describe("Filters")
	.optional();
export type filters__21 = z.infer<typeof filters__21>;

export const VideoIngestorDetail = z.object({
	video_id: z.string(),
	task_id: z.string(),
	match_id: z.string().nullish(),
	league: z.string().nullish(),
	league_country: z.string().nullish(),
	category: z.string().nullish(),
	home_team_id: z.number().int().nullish(),
	home_team: z.string().nullish(),
	away_team_id: z.number().int().nullish(),
	away_team: z.string().nullish(),
	video_status: z.enum([
		"pending",
		"queued",
		"initialized",
		"downloading",
		"downloaded",
		"migrated",
		"error",
		"optimized",
	]),
	teamsheet_status: z.enum([
		"pending",
		"not_available",
		"available",
		"mapped",
		"not_mapped",
		"partially_mapped",
		"home_club_not_mapped",
		"away_club_not_mapped",
		"both_club_not_mapped",
		"home_team_not_mapped",
		"away_team_not_mapped",
		"both_team_not_mapped",
	]),
	video_url: z.string().nullish(),
	eyeball_game_id: z.number().int().nullish(),
	game_date: z.string().nullish(),
	updated_at: z.string(),
});
export type VideoIngestorDetail = z.infer<typeof VideoIngestorDetail>;

export const VideoIngestorPaginatedResponse = z.object({
	videos: z.array(VideoIngestorDetail),
	limit: z.number().int(),
	page: z.number().int(),
	pages: z.number().int(),
	total: z.number().int(),
});
export type VideoIngestorPaginatedResponse = z.infer<
	typeof VideoIngestorPaginatedResponse
>;

export const TeamDTO = z.object({
	value: z.number().int().describe("Team ID"),
	label: z.string().describe("Team display name"),
});
export type TeamDTO = z.infer<typeof TeamDTO>;

export const VideoIngestorTeamsheetEyeballDto = z
	.object({
		id: z.number().int().nullable(),
		score: z.number().nullable(),
		search: z.array(z.object({}).partial()).nullable(),
		mappingStage: z.number().int().nullable(),
	})
	.partial();
export type VideoIngestorTeamsheetEyeballDto = z.infer<
	typeof VideoIngestorTeamsheetEyeballDto
>;

export const VideoIngestorTeamsheetProviderFormattedNameDto = z
	.object({
		last_name: z.string().nullable(),
		first_name: z.string().nullable(),
	})
	.partial();
export type VideoIngestorTeamsheetProviderFormattedNameDto = z.infer<
	typeof VideoIngestorTeamsheetProviderFormattedNameDto
>;

export const VideoIngestorTeamsheetProviderDto = z
	.object({
		id: z.string().nullable(),
		team: z.string().nullable(),
		height: z.number().int().nullable(),
		weight: z.number().int().nullable(),
		birthday: z.string().nullable(),
		starting: z.boolean().nullable(),
		last_name: z.string().nullable(),
		first_name: z.string().nullable(),
		heimspielId: z.number().int().nullable(),
		nationality: z.string().nullable(),
		shirtNumber: z.number().int().nullable(),
		matchRosterId: z.string().nullable(),
		formatted_name: VideoIngestorTeamsheetProviderFormattedNameDto.nullable(),
		seasonRosterId: z.string().nullable(),
	})
	.partial();
export type VideoIngestorTeamsheetProviderDto = z.infer<
	typeof VideoIngestorTeamsheetProviderDto
>;

export const VideoIngestorTeamsheetPlayerDataDto = z.object({
	eyeball: VideoIngestorTeamsheetEyeballDto,
	provider: VideoIngestorTeamsheetProviderDto,
});
export type VideoIngestorTeamsheetPlayerDataDto = z.infer<
	typeof VideoIngestorTeamsheetPlayerDataDto
>;

export const VideoIngestorTeamsheetPlayerDto = z.object({
	id: z.string(),
	created_at: z.string(),
	updated_at: z.string(),
	deleted: z.boolean(),
	task_id: z.string(),
	data: VideoIngestorTeamsheetPlayerDataDto,
	provider_player_id: z.string(),
	eyeball_player_id: z.number().int().nullish(),
	status: z.string(),
});
export type VideoIngestorTeamsheetPlayerDto = z.infer<
	typeof VideoIngestorTeamsheetPlayerDto
>;

export const VideoIngestorTeamsheetResponse = z.object({
	players: z.array(VideoIngestorTeamsheetPlayerDto),
});
export type VideoIngestorTeamsheetResponse = z.infer<
	typeof VideoIngestorTeamsheetResponse
>;

export const UpdatePlayerStatusRequestDto = z.object({
	eyeball_player_id: z.number().int().gt(0),
	status: z.string(),
});
export type UpdatePlayerStatusRequestDto = z.infer<
	typeof UpdatePlayerStatusRequestDto
>;

export const VideoIngestorUpdatePlayerStatusResponse = z.object({
	id: z.string(),
	created_at: z.string(),
	updated_at: z.string(),
	deleted: z.boolean(),
	task_id: z.string(),
	data: z.string(),
	provider_player_id: z.string().nullish(),
	eyeball_player_id: z.number().int().nullish(),
	status: z.string(),
});
export type VideoIngestorUpdatePlayerStatusResponse = z.infer<
	typeof VideoIngestorUpdatePlayerStatusResponse
>;

export const TeamDropdownDto = z.object({
	label: z.string().describe("Team name"),
	id: z.number().int().describe("Team ID"),
});
export type TeamDropdownDto = z.infer<typeof TeamDropdownDto>;

export const LeagueDropdownDto = z.object({
	label: z.string().describe("League label"),
	id: z.number().int().describe("League ID"),
});
export type LeagueDropdownDto = z.infer<typeof LeagueDropdownDto>;

export const PartnerPlayerRequest = z.object({
	id: z.number().int().nullish().default(null),
	firstName: z.string().default(""),
	lastName: z.string().default(""),
	checked: z.boolean().default(false),
	height: z.number().int().nullish().default(null),
	weight: z.number().int().nullish().default(null),
	teamId: z.number().int().nullish().default(null),
	birthday: z.string().default(""),
	nationality: z.string().default(""),
	nationality2: z.string().nullish().default(null),
	position: z.string().nullish().default(null),
	position2: z.string().nullish().default(null),
	strongestFoot: z.string().nullish().default(null),
	notes: z.string().nullish().default(null),
	photo: z.string().nullish().default(null),
	tranLink: z.string().nullish().default(null),
	federationLink: z.string().nullish().default(null),
	playedInNationalTeam: z.boolean().optional().default(false),
});
export type PartnerPlayerRequest = z.infer<typeof PartnerPlayerRequest>;

export const NonPartnerPlayerDto = z.object({
	id: z.number().int().nullish(),
	firstName: z.string(),
	lastName: z.string(),
	picture: z.string().nullish(),
	birthday: z.string().nullish(),
	nationality: z.string().nullish(),
	nationality2: z.string().nullish(),
	position: z.string().nullish(),
	strongestFoot: z.string().nullish(),
	playedInNationalTeam: z.boolean().nullish().default(false),
	npClubName: z.string().nullish(),
	npClubCountry: z.string().nullish(),
	npClubLogo: z.string().nullish(),
	tranLink: z.string().nullish(),
	federationLink: z.string().nullish(),
	gender: z.string().nullish(),
});
export type NonPartnerPlayerDto = z.infer<typeof NonPartnerPlayerDto>;

export const CreatePlayerRequest = z
	.object({
		player: PartnerPlayerRequest.nullable().default(null),
		nonPartnerPlayerData: NonPartnerPlayerDto.nullable().default(null),
	})
	.partial();
export type CreatePlayerRequest = z.infer<typeof CreatePlayerRequest>;

export const PlayerDropdownSearchRequest = z.object({
	searchTerm: z.string(),
	validSearchTerm: z.boolean(),
});
export type PlayerDropdownSearchRequest = z.infer<
	typeof PlayerDropdownSearchRequest
>;

export const PlayerDropdownItem = z.object({
	id: z.number().int(),
	firstName: z.string(),
	lastName: z.string(),
});
export type PlayerDropdownItem = z.infer<typeof PlayerDropdownItem>;

export const PlayerDropdownResponse = z.object({
	items: z.array(PlayerDropdownItem),
});
export type PlayerDropdownResponse = z.infer<typeof PlayerDropdownResponse>;

export const GameAnalysisPhysicalResponse = z.object({
	gameId: z.number().int().default(0),
	physicalMetricsVideoUrls: z
		.union([z.array(z.string()), z.record(z.string())])
		.default([]),
	physicalMetricsReportUrl: z.string().nullable().default(""),
	physicalMetricsPlayerDataUrl: z.string().nullable().default(""),
	physicalMetricsXyEventUrl: z.string().nullable().default(""),
	downloadableReportUrl: z.string().nullable().default(""),
	analysisStatus: z.string().nullable().default(""),
	rejectedReason: z.string().nullish().default(null),
});
export type GameAnalysisPhysicalResponse = z.infer<
	typeof GameAnalysisPhysicalResponse
>;

export const postGameAnalysisPhysicalVideoUrl_Body = z.object({
	gameId: z.number().int(),
	physicalMetricsVideoUrl: z.array(z.string()),
});
export type postGameAnalysisPhysicalVideoUrl_Body = z.infer<
	typeof postGameAnalysisPhysicalVideoUrl_Body
>;

export const GameStatsDebugJsonValue = z.union([
	z.null(),
	z.string(),
	z.number(),
	z.number(),
	z.boolean(),
	z.array(z.unknown()),
	z.object({}).partial(),
]);
export type GameStatsDebugJsonValue = z.infer<typeof GameStatsDebugJsonValue>;

export const GameEventsDebugEventResponse = z.object({
	id: z.number().int(),
	videoTime: z.number().int().nullish(),
	gamePlayerId: z.number().int().nullish(),
	playerName: z.string().nullish(),
	gameTeamId: z.number().int().nullish(),
	type: z.string().nullish(),
	half: z.number().int(),
	footballTime: z.number().int(),
	category: z.string().nullish(),
	details: GameStatsDebugJsonValue.nullish(),
	success: z.boolean().nullish(),
	underPressure: z.boolean().nullish(),
	x: z.number().nullish(),
	y: z.number().nullish(),
	providerEventId: z.string().nullish(),
	relatedProviderEventId: z.string().nullish(),
	providerEventUuid: z.string().nullish(),
	relatedProviderEventUuid: z.string().nullish(),
});
export type GameEventsDebugEventResponse = z.infer<
	typeof GameEventsDebugEventResponse
>;

export const GameEventsDebugResponse = z.object({
	gameId: z.number().int(),
	gameEventDataV3: z.array(GameEventsDebugEventResponse),
	gameEventDataV3Test: z.array(GameEventsDebugEventResponse),
	gameEventCore: z.array(GameEventsDebugEventResponse),
});
export type GameEventsDebugResponse = z.infer<typeof GameEventsDebugResponse>;

export const GameStatsDebugDbRowResponse = z.object({
	segment: z
		.string()
		.describe("Table group, e.g. game_team_stat, game_player_offensive_stat"),
	id: z.number().int(),
	gameTeamId: z.number().int().nullish(),
	gamePlayerId: z.number().int().nullish(),
	data: z.record(GameStatsDebugJsonValue),
});
export type GameStatsDebugDbRowResponse = z.infer<
	typeof GameStatsDebugDbRowResponse
>;

export const GameStatsDebugResponse = z.object({
	gameId: z.number().int(),
	statsTest: z.record(GameStatsDebugJsonValue).nullish(),
	dbRows: z.array(GameStatsDebugDbRowResponse),
});
export type GameStatsDebugResponse = z.infer<typeof GameStatsDebugResponse>;

export const GPDownloadTypeResponse = z.object({
	title: z.string(),
	type: z.string(),
});
export type GPDownloadTypeResponse = z.infer<typeof GPDownloadTypeResponse>;

export const GeneratedContentReadinessStatus = z.enum([
	"in_progress",
	"ready",
	"failed",
	"insufficient_data",
]);
export type GeneratedContentReadinessStatus = z.infer<
	typeof GeneratedContentReadinessStatus
>;

export const GPGameResponse = z.object({
	id: z.number().int(),
	category: z.string().nullable(),
	type: z.string().nullable(),
	date: z.string().datetime({ offset: true }).nullable(),
	isAnalysisFinished: z.boolean(),
	isAnalysisApproved: z.boolean(),
	isLockedForActualUser: z.boolean(),
	analysisSyncRunning: z.boolean(),
	leftSideStart: z.string().nullable(),
	userClipsCount: z.number().int(),
	isFavorite: z.boolean().nullable(),
	teamSheetDownloadTypes: z.array(GPDownloadTypeResponse).default([]),
	description: z.string().nullable().default(null),
	location: z.string().nullish().default(null),
	providerTag: z.string().nullish().default(null),
	processingLevel: ProcessingLevelEnum.nullish().default(null),
	aiSummaryStatus: GeneratedContentReadinessStatus.nullable().default(null),
	videoUploadId: z.number().int().nullish().default(null),
	teamTagged: TeamTaggedEnum.nullish().default(null),
});
export type GPGameResponse = z.infer<typeof GPGameResponse>;

export const ShirtPatternEnum = z.enum([
	"solid",
	"half-and-half",
	"stripes-vertical",
	"stripes-horizontal",
	"stripes-diagonal",
	"other",
]);
export type ShirtPatternEnum = z.infer<typeof ShirtPatternEnum>;

export const GPGameDressKitResponse = z.object({
	id: z.number().int(),
	name: z.string(),
	shirtPattern: ShirtPatternEnum,
	shortsColor: z.string(),
	shirtColorPrimary: z.string(),
	shirtColorSecondary: z.string().nullable().default(null),
});
export type GPGameDressKitResponse = z.infer<typeof GPGameDressKitResponse>;

export const GPClubResponse = z.object({
	id: z.number().int(),
	name: z.string().nullable(),
	slug: z.string().nullable(),
	logo: z.string().nullable(),
	country: z.string().nullable(),
	isPartner: z.boolean(),
});
export type GPClubResponse = z.infer<typeof GPClubResponse>;

export const GPTeamResponse = z.object({
	id: z.number().int(),
	name: z.string().nullable(),
	slug: z.string().nullable(),
	category: z.string().nullable(),
});
export type GPTeamResponse = z.infer<typeof GPTeamResponse>;

export const RatingIcon = z.enum(["good", "neutral", "bad"]);
export type RatingIcon = z.infer<typeof RatingIcon>;

export const RatingVisibility = z.enum([
	"coach",
	"player",
	"player_and_parent",
]);
export type RatingVisibility = z.infer<typeof RatingVisibility>;

export const GPCoachRatingResponse = z.object({
	icon: RatingIcon.nullable(),
	score: z.number().nullable(),
	badgeIds: z.array(z.number().int()),
	visibility: RatingVisibility.nullable(),
	ratedBy: z.string().nullable(),
	ratedAt: z.string().nullable(),
});
export type GPCoachRatingResponse = z.infer<typeof GPCoachRatingResponse>;

export const GPPlayerGradingResponse = z.object({
	id: z.number().int(),
	performanceGrade: z.number().int(),
	lastTimeGraded: z.string().datetime({ offset: true }),
	potentialGrade: z.number().int().nullable(),
	note: z.string().nullable(),
});
export type GPPlayerGradingResponse = z.infer<typeof GPPlayerGradingResponse>;

export const GPGamePlayerResponse = z.object({
	id: z.number().int().nullable(),
	playerId: z.number().int().nullable(),
	teamId: z.number().int().nullable(),
	name: z.string().nullable(),
	birthYear: z.string().nullable(),
	position: z.string().nullable(),
	positionKey: z.string().nullable(),
	numberSign: z.number().int().nullable(),
	isSubstitute: z.boolean(),
	rating: z.number().nullable(),
	goals: z.number().int().nullable(),
	minutesPlayed: z.number().int().nullable(),
	playerUrl: z.string().nullable(),
	coachRating: GPCoachRatingResponse.nullable(),
	photo: z.string().nullable(),
	isAnonymized: z.boolean(),
	gender: GenderEnum.nullable(),
	playerGrading: GPPlayerGradingResponse.nullish(),
	gradedByScouts: z.number().int().nullish().default(null),
});
export type GPGamePlayerResponse = z.infer<typeof GPGamePlayerResponse>;

export const GPTeamPermissionsResponse = z.object({
	editTeamSheet: z.boolean(),
	editColors: z.boolean(),
	ratingBadgeEdit: z.boolean(),
	reviewEdit: z.boolean(),
	removePlayers: z.boolean(),
	changeNumbers: z.boolean(),
	anonymizePlayers: z.boolean(),
	supportsPlainTeamSheet: z.boolean(),
	editGender: z.boolean(),
});
export type GPTeamPermissionsResponse = z.infer<
	typeof GPTeamPermissionsResponse
>;

export const GPTeamSheetFormationResponse = z.object({
	value: z.string(),
	name: z.string(),
});
export type GPTeamSheetFormationResponse = z.infer<
	typeof GPTeamSheetFormationResponse
>;

export const GPGameTeamResponse = z.object({
	id: z.number().int(),
	clubName: z.string().nullable(),
	teamName: z.string().nullable(),
	score: z.number().int().nullable(),
	hasStats: z.boolean(),
	colorShirt: ColorEnum.nullable(),
	colorShort: ColorEnum.nullable(),
	gameDressKit: GPGameDressKitResponse.nullable(),
	club: GPClubResponse.nullable(),
	team: GPTeamResponse.nullable(),
	players: z.array(GPGamePlayerResponse),
	permissions: GPTeamPermissionsResponse,
	teamSheetFormations: z.array(GPTeamSheetFormationResponse),
	clubPlayersCount: z.number().int(),
	url: z.string().nullable(),
	logo: z.string().nullable(),
	averageRating: z.number().nullable(),
	teamsheetHidden: z.boolean(),
});
export type GPGameTeamResponse = z.infer<typeof GPGameTeamResponse>;

export const GPVideoResponse = z.object({
	id: z.number().int(),
	url: z.string(),
	isStream: z.boolean(),
	poster: z.string().nullable(),
	thumbs: z.string().nullable(),
});
export type GPVideoResponse = z.infer<typeof GPVideoResponse>;

export const GPPermissionsResponse = z.object({
	showPlayerStats: z.boolean(),
	showTeamStats: z.boolean(),
	saveFavorites: z.boolean(),
	editScore: z.boolean(),
	useClips: z.boolean(),
	adminOptions: z.boolean(),
	showEvents: z.boolean(),
	reportEventError: z.boolean(),
	downloadEvents: z.boolean(),
	showEventComments: z.boolean(),
	downloadVideo: z.boolean(),
	downloadPlayerStats: z.boolean(),
	downloadTeamStats: z.boolean(),
	showRatings: z.boolean(),
	showCoachTooltip: z.boolean(),
	showDataInsights: z.boolean(),
	accessAiSummaryFeature: z.boolean(),
	accessAiSummaryContent: z.boolean(),
	allowedPlayers: z.array(z.number().int()).nullable(),
	showPhysicalAnalysis: z.boolean().default(false),
	playerGrading: z.boolean().default(false),
	showAiCoachReview: z.boolean().default(false),
	showCoachToolbar: z.boolean().default(false),
});
export type GPPermissionsResponse = z.infer<typeof GPPermissionsResponse>;

export const GPHalfResponse = z.object({
	id: z.number().int(),
	formalLength: z.number().int(),
	padding: z.number().int(),
	matchTimeStart: z.number().int(),
	matchTimeEnd: z.number().int(),
	videoTimeStart: z.number().int(),
	videoTimeEnd: z.number().int(),
	halfOffset: z.number().int(),
	isBase: z.boolean(),
});
export type GPHalfResponse = z.infer<typeof GPHalfResponse>;

export const GPPlayerPositionResponse = z.object({
	value: z.string(),
	title: z.string(),
});
export type GPPlayerPositionResponse = z.infer<typeof GPPlayerPositionResponse>;

export const RatingBadgeResponse = z.object({
	id: z.number().int(),
	key: z.string(),
	name: z.string(),
	description: z.string(),
	asset: z.string(),
});
export type RatingBadgeResponse = z.infer<typeof RatingBadgeResponse>;

export const GamePageResponse = z.object({
	game: GPGameResponse,
	homeGameTeam: GPGameTeamResponse,
	awayGameTeam: GPGameTeamResponse,
	video: GPVideoResponse.nullable(),
	permissions: GPPermissionsResponse,
	halves: z.array(GPHalfResponse),
	playerPositions: z.array(GPPlayerPositionResponse),
	ratingBadges: z.array(RatingBadgeResponse),
});
export type GamePageResponse = z.infer<typeof GamePageResponse>;

export const GameEventCommentResponse = z.object({
	id: z.number().int(),
	providerEventId: z.string(),
	text: z.string(),
	lockerRoomVisibility: z.string().nullish(),
	visibleToAll: z.boolean(),
	createdBy: z.string(),
	createdAt: z.string(),
	canEdit: z.boolean(),
	canDelete: z.boolean(),
});
export type GameEventCommentResponse = z.infer<typeof GameEventCommentResponse>;

export const GPEventResponse = z.object({
	id: z.number().int(),
	category: z.string(),
	type: z.string(),
	success: z.boolean().nullable(),
	underPressure: z.boolean().nullable(),
	gamePlayerId: z.number().int().nullable(),
	gameTeamId: z.number().int().nullable(),
	playerName: z.string().nullable(),
	backNumber: z.number().int().nullable(),
	x: z.number().nullable(),
	y: z.number().nullable(),
	half: z.number().int().nullable(),
	halfTime: z.number().int().nullable(),
	halfOvertime: z.number().int().nullable(),
	footballTime: z.number().int().nullable(),
	videoTime: z.number().int(),
	clipTime: z.number().int().nullable(),
	prepend: z.number().int().nullable(),
	append: z.number().int().nullable(),
	providerEventId: z.string().nullable(),
	relativeProviderEventId: z.string().nullable(),
	details: z.record(z.union([z.string(), z.boolean(), z.number()])).nullable(),
	groups: z.array(z.string()).nullable(),
	tags: z.array(z.string()).nullish(),
	isFavorite: z.boolean(),
	comments: z.array(GameEventCommentResponse).default([]),
	commentCount: z
		.number()
		.int()
		.describe("Number of Game Page comments on this event")
		.optional()
		.default(0),
	inCollection: z
		.boolean()
		.describe(
			"Whether this event is saved in any collection (null for desktop)",
		)
		.nullish()
		.default(null),
	teamClubLogo: z
		.string()
		.describe(
			"Club logo of the team the event belongs to. Resolved from the event own game, so it stays correct in cross game clips.",
		)
		.nullish()
		.default(null),
	teamShirtColor: z
		.string()
		.describe(
			"Shirt color of the team the event belongs to. Resolved from the event own game, so it stays correct in cross game clips.",
		)
		.nullish()
		.default(null),
});
export type GPEventResponse = z.infer<typeof GPEventResponse>;

export const GPCommentFormDefaultsResponse = z.object({
	lockerRoomVisibility: z.string().nullish(),
	visibleToAll: z.boolean(),
});
export type GPCommentFormDefaultsResponse = z.infer<
	typeof GPCommentFormDefaultsResponse
>;

export const GamePageLazyResponse = z.object({
	events: z.array(GPEventResponse),
	videoDownloadUrl: z.string().nullable(),
	commentFormDefaults: GPCommentFormDefaultsResponse.nullish().default(null),
});
export type GamePageLazyResponse = z.infer<typeof GamePageLazyResponse>;

export const GamePutScoreRequest = z.object({
	homeTeamScore: z.number().int().gte(0).lte(255),
	awayTeamScore: z.number().int().gte(0).lte(255),
});
export type GamePutScoreRequest = z.infer<typeof GamePutScoreRequest>;

export const GameTeamStatCategoryEnum = z.enum([
	"summary",
	"offensive",
	"defensive",
	"distribution",
]);
export type GameTeamStatCategoryEnum = z.infer<typeof GameTeamStatCategoryEnum>;

export const GameTeamStatTypeEnum = z.enum([
	"goals",
	"possession",
	"shots",
	"shotsOnTarget",
	"fouls",
	"offsides",
	"freeKicks",
	"corners",
	"averageRating",
	"blockedShots",
	"shotsOffTarget",
	"shotsInsidePa",
	"shotsOutsidePa",
	"takeOnsSuccessful",
	"takeOnsTotal",
	"tacklesSuccess",
	"tacklesTotal",
	"aerialDuelsSuccess",
	"aerialDuelsTotal",
	"groundDuelsSuccess",
	"groundDuelsTotal",
	"interceptions",
	"clearances",
	"interventions",
	"recoveries",
	"blocks",
	"mistakes",
	"foulsWon",
	"passesSuccess",
	"passesTotal",
	"passAccuracy",
	"keyPasses",
	"passesInFinalThirdSuccess",
	"passesInFinalThirdTotal",
	"passesInMiddleThirdSuccess",
	"passesInMiddleThirdTotal",
	"passesInDefensiveThirdSuccess",
	"passesInDefensiveThirdTotal",
	"longPassesSuccess",
	"longPassesTotal",
	"mediumRangePassesSuccess",
	"mediumRangePassesTotal",
	"shortPassesSuccess",
	"shortPassesTotal",
	"passesForwardSuccess",
	"passesForwardTotal",
	"passesSidewaysSuccess",
	"passesSidewaysTotal",
	"passesBackwardSuccess",
	"passesBackwardTotal",
	"crossesSuccess",
	"crossesTotal",
	"controlUnderPressure",
	"nonPenaltyXg",
	"nonPenaltyXgPerShot",
	"goalsXgRatio",
	"totalXa",
	"totalXg",
	"nonPenaltyShots",
]);
export type GameTeamStatTypeEnum = z.infer<typeof GameTeamStatTypeEnum>;

export const GameTeamStatValueTypeEnum = z.enum([
	"count",
	"percentage",
	"rating",
	"decimal",
]);
export type GameTeamStatValueTypeEnum = z.infer<
	typeof GameTeamStatValueTypeEnum
>;

export const GPTSItemResponse = z.object({
	category: GameTeamStatCategoryEnum,
	statType: GameTeamStatTypeEnum,
	valueType: GameTeamStatValueTypeEnum,
	homeTeamValue: z.union([z.number(), z.number()]).nullable(),
	awayTeamValue: z.union([z.number(), z.number()]).nullable(),
});
export type GPTSItemResponse = z.infer<typeof GPTSItemResponse>;

export const GPTSClubResponse = z.object({
	teamId: z.number().int(),
	name: z.string(),
	logo: z.string().nullable(),
});
export type GPTSClubResponse = z.infer<typeof GPTSClubResponse>;

export const GameTeamStatResponse = z.object({
	stats: z.array(GPTSItemResponse),
	homeTeam: GPTSClubResponse,
	awayTeam: GPTSClubResponse,
	downloadUrl: z.string().nullable(),
});
export type GameTeamStatResponse = z.infer<typeof GameTeamStatResponse>;

export const GamePlayerStatColumnEnum = z.enum([
	"position",
	"playerName",
	"backNumber",
	"uri",
	"rating",
	"minutesPlayed",
	"goalsScrored",
	"goalsOwn",
	"assists",
	"offsides",
	"shots",
	"freekicks",
	"corners",
	"throwIns",
	"fouls",
	"yellowCards",
	"redCards",
	"shotsOnTarget",
	"blockedShots",
	"shotsOffTarget",
	"shotsInsidePa",
	"shotsOutsidePa",
	"takeOnsSuccessful",
	"takeOnsTotal",
	"tacklesSuccess",
	"tacklesTotal",
	"aerialDuelsSuccess",
	"aerialDuelsTotal",
	"groundDuelsSuccess",
	"groundDuelsTotal",
	"interceptions",
	"clearances",
	"interventions",
	"recoveries",
	"blocks",
	"mistakes",
	"foulsWon",
	"passesSuccess",
	"passesTotal",
	"passAccuracy",
	"keyPasses",
	"passesInFinalThirdSuccess",
	"passesInFinalThirdTotal",
	"passesInMiddleThirdSuccess",
	"passesInMiddleThirdTotal",
	"passesInDefensiveThirdSuccess",
	"passesInDefensiveThirdTotal",
	"longPassesSuccess",
	"longPassesTotal",
	"mediumRangePassesSuccess",
	"mediumRangePassesTotal",
	"shortPassesSuccess",
	"shortPassesTotal",
	"passesForwardSuccess",
	"passesForwardTotal",
	"passesSidewaysSuccess",
	"passesSidewaysTotal",
	"passesBackwardSuccess",
	"passesBackwardTotal",
	"crossesSuccess",
	"crossesTotal",
	"controlUnderPressure",
	"goalsConceded",
	"catches",
	"punches",
	"goalsKicksSuccess",
	"goalsKicksTotal",
	"aerialClearancesSuccess",
	"aerialClearancesTotal",
	"nonPenaltyXg",
	"nonPenaltyXgPerShot",
	"goalsXgRatio",
	"totalXa",
	"totalXg",
	"nonPenaltyShots",
	"throwsSuccess",
	"throwsTotal",
	"throwsRatio",
	"averagePassLength",
	"launchPercent",
	"defensiveActionsOpaTotal",
	"averageDefensiveActionDistance",
	"crossesFacedTotal",
	"crossesStoppedTotal",
	"crossesStoppedRatio",
	"savePercent",
]);
export type GamePlayerStatColumnEnum = z.infer<typeof GamePlayerStatColumnEnum>;

export const GPPSCellResponse = z.object({
	type: GamePlayerStatColumnEnum,
	value: z.string().nullable(),
});
export type GPPSCellResponse = z.infer<typeof GPPSCellResponse>;

export const GPPSPlayerResponse = z.object({
	id: z.number().int(),
	teamId: z.number().int().nullable(),
	isGoalkeeper: z.boolean(),
	columns: z.array(GPPSCellResponse),
});
export type GPPSPlayerResponse = z.infer<typeof GPPSPlayerResponse>;

export const GPPSClubResponse = z.object({
	id: z.number().int(),
	name: z.string(),
	logo: z.string().nullable(),
});
export type GPPSClubResponse = z.infer<typeof GPPSClubResponse>;

export const GamePlayerStatResponse = z.object({
	players: z.array(GPPSPlayerResponse),
	homeClub: GPPSClubResponse,
	awayClub: GPPSClubResponse,
	downloadUrl: z.string().nullable(),
});
export type GamePlayerStatResponse = z.infer<typeof GamePlayerStatResponse>;

export const GameTeamPutColorsRequest = z
	.object({
		colorShort: ColorEnum.nullable().default(null),
		colorShirt: ColorEnum.nullable().default(null),
	})
	.partial();
export type GameTeamPutColorsRequest = z.infer<typeof GameTeamPutColorsRequest>;

export const RatingPutRequest = z.object({
	score: z.number(),
	visibility: RatingVisibility,
});
export type RatingPutRequest = z.infer<typeof RatingPutRequest>;

export const BadgesPutRequest = z
	.object({ badgeIds: z.array(z.number().int()).default([]) })
	.partial();
export type BadgesPutRequest = z.infer<typeof BadgesPutRequest>;

export const HighlightPostRequest = z.object({
	title: z.string().max(255),
	note: z.string(),
	timestamp: z.number().int(),
});
export type HighlightPostRequest = z.infer<typeof HighlightPostRequest>;

export const HighlightPatchRequest = z
	.object({
		title: z.string().max(255).nullable(),
		note: z.string().nullable(),
		timestamp: z.number().int().nullable(),
	})
	.partial();
export type HighlightPatchRequest = z.infer<typeof HighlightPatchRequest>;

export const filters__22 = z
	.array(
		z
			.object({
				type: z
					.enum([
						"exact",
						"search",
						"boolean",
						"exists",
						"inarray",
						"datetimerange",
						"numberrange",
					])
					.describe("Type of filter"),
				field: z
					.enum(["gameId", "author", "isFavorite", "tag"])
					.describe("Property to filter"),
				value: z
					.union([
						z.string(),
						z.number(),
						z.boolean(),
						z
							.object({
								start: z.string().datetime({ offset: true }),
								end: z.string().datetime({ offset: true }),
							})
							.partial(),
					])
					.describe(
						"Filter value, can be a string, integer, date or a date range",
					),
			})
			.partial(),
	)
	.optional();
export type filters__22 = z.infer<typeof filters__22>;

export const GameClipsRowResponse = z.object({
	id: z.number().int(),
	name: z.string(),
	description: z.string().nullable(),
	url: z.string().nullable(),
	createdAt: z.string().datetime({ offset: true }),
	eventsCount: z.number().int().default(0),
	isFinished: z.boolean(),
	expireLinkAt: z.string().datetime({ offset: true }).nullable(),
	shareSlug: z.string().nullable(),
	password: z.string().nullable(),
});
export type GameClipsRowResponse = z.infer<typeof GameClipsRowResponse>;

export const GameClipsResponse = z
	.object({
		items: z.array(GameClipsRowResponse).default([]),
		page: z.number().int().default(0),
		limit: z.number().int().default(0),
		pages: z.number().int().default(0),
		total: z.number().int().default(0),
	})
	.partial();
export type GameClipsResponse = z.infer<typeof GameClipsResponse>;

export const GameClipGameTeamResponse = z.object({
	id: z.number().int(),
	clubName: z.string().nullable(),
	teamName: z.string().nullable(),
	colorShirt: ColorEnum.nullable(),
	colorShort: ColorEnum.nullable(),
	logo: z.string().nullable(),
});
export type GameClipGameTeamResponse = z.infer<typeof GameClipGameTeamResponse>;

export const GPCreatedByResponse = z.object({
	userPhoto: z.string().nullable(),
	name: z.string(),
});
export type GPCreatedByResponse = z.infer<typeof GPCreatedByResponse>;

export const GameClipResponse = z.object({
	id: z.number().int(),
	name: z.string(),
	description: z.string().nullable(),
	isFinished: z.boolean(),
	url: z.string().nullable(),
	createdAt: z.string().datetime({ offset: true }),
	events: z.array(GPEventResponse),
	expireLinkAt: z.string().datetime({ offset: true }).nullable(),
	shareSlug: z.string().nullable(),
	password: z.string().nullable(),
	homeGameTeam: GameClipGameTeamResponse,
	awayGameTeam: GameClipGameTeamResponse,
	createdBy: GPCreatedByResponse,
});
export type GameClipResponse = z.infer<typeof GameClipResponse>;

export const GameClipPutRequest = z.object({
	name: z.string().max(255),
	description: z.string().max(255).nullish().default(null),
});
export type GameClipPutRequest = z.infer<typeof GameClipPutRequest>;

export const GameClipEventRequest = z.object({
	id: z.number().int(),
	prepend: z.number().int().gte(0).lte(120000),
	append: z.number().int().gte(0).lte(120000),
});
export type GameClipEventRequest = z.infer<typeof GameClipEventRequest>;

export const GameClipIntervalRequest = z.object({
	start: z.number().int(),
	end: z.number().int(),
	events: z.array(GameClipEventRequest),
});
export type GameClipIntervalRequest = z.infer<typeof GameClipIntervalRequest>;

export const GameClipPostRequest = z.object({
	name: z.string().max(255),
	description: z.string().max(255),
	intervals: z.array(GameClipIntervalRequest),
	tags: z.array(z.string()).nullish().default(null),
});
export type GameClipPostRequest = z.infer<typeof GameClipPostRequest>;

export const GamePlayerItemRequest = z.object({
	position: z.string().nullable().default(null),
	name: z.string().nullable().default(null),
	number: z.number().int().nullable().default(null),
	goals: z.number().int().nullable().default(null),
	idPlayer: z.string().nullish().default(null),
	id: z.string().nullish().default(null),
	substitute: z.boolean().optional().default(false),
	discover: z.boolean().optional().default(false),
	isAnonymized: z.boolean(),
	discovery: z.boolean(),
	valid: z.boolean(),
});
export type GamePlayerItemRequest = z.infer<typeof GamePlayerItemRequest>;

export const GameTeamSheetRequest = z.object({
	colors: GameTeamPutColorsRequest.nullable().default(null),
	squad: z.array(GamePlayerItemRequest).default([]),
	substitutes: z.array(GamePlayerItemRequest).default([]),
	saveAnyway: z.boolean().default(false),
	gender: z.string().nullable().default(null),
});
export type GameTeamSheetRequest = z.infer<typeof GameTeamSheetRequest>;

export const GTSCompositionPlayer = z.object({
	id: z.number().int(),
	position: z.string(),
	displayOrder: z.number().int(),
});
export type GTSCompositionPlayer = z.infer<typeof GTSCompositionPlayer>;

export const GTSCompositionPosition = z.object({
	position: z.string(),
	players: z.array(GTSCompositionPlayer),
});
export type GTSCompositionPosition = z.infer<typeof GTSCompositionPosition>;

export const GameTeamSheetCompositionPlayersResponse = z.object({
	compositionPlayers: z.array(GTSCompositionPosition),
});
export type GameTeamSheetCompositionPlayersResponse = z.infer<
	typeof GameTeamSheetCompositionPlayersResponse
>;

export const GSTGameTeamPlayerResponse = z.object({
	id: z.number().int(),
	name: z.string(),
});
export type GSTGameTeamPlayerResponse = z.infer<
	typeof GSTGameTeamPlayerResponse
>;

export const GameTeamSheetGameTeamPlayersSearchResponse = z.object({
	players: z.array(GSTGameTeamPlayerResponse),
});
export type GameTeamSheetGameTeamPlayersSearchResponse = z.infer<
	typeof GameTeamSheetGameTeamPlayersSearchResponse
>;

export const GTSPlayerResponse = z.object({
	id: z.number().int(),
	name: z.string().nullable(),
	birthDay: z.string().nullable(),
	position: z.string().nullable(),
});
export type GTSPlayerResponse = z.infer<typeof GTSPlayerResponse>;

export const GameTeamSheetPlayersResponse = z.object({
	players: z.array(GTSPlayerResponse),
});
export type GameTeamSheetPlayersResponse = z.infer<
	typeof GameTeamSheetPlayersResponse
>;

export const GamePutDescriptionRequest = z
	.object({ description: z.string().nullable().default(null) })
	.partial();
export type GamePutDescriptionRequest = z.infer<
	typeof GamePutDescriptionRequest
>;

export const GameStatsExportTypeEnum = z.enum(["playerStats", "teamStats"]);
export type GameStatsExportTypeEnum = z.infer<typeof GameStatsExportTypeEnum>;

export const FileExtensionEnum = z.enum(["csv", "json", "xlsx", "xml"]);
export type FileExtensionEnum = z.infer<typeof FileExtensionEnum>;

export const GameStatsExportCellRequest = z.object({
	key: z.string(),
	value: z.union([z.boolean(), z.number(), z.number(), z.string()]).nullable(),
});
export type GameStatsExportCellRequest = z.infer<
	typeof GameStatsExportCellRequest
>;

export const GameStatsExportRequest = z.object({
	type: GameStatsExportTypeEnum,
	format: FileExtensionEnum,
	name: z.string(),
	header: z.array(GameStatsExportCellRequest).default([]),
	rows: z.array(GameStatsExportCellRequest).default([]),
});
export type GameStatsExportRequest = z.infer<typeof GameStatsExportRequest>;

export const GameEventDataExportRequest = z.object({
	extension: FileExtensionEnum,
	events: z.array(z.number().int()).default([]),
});
export type GameEventDataExportRequest = z.infer<
	typeof GameEventDataExportRequest
>;

export const GameClipProtectedResponse = z.object({
	loggedIn: z.boolean(),
	protected: z.boolean(),
});
export type GameClipProtectedResponse = z.infer<
	typeof GameClipProtectedResponse
>;

export const GameClipPostPasswordRequest = z.object({ password: z.string() });
export type GameClipPostPasswordRequest = z.infer<
	typeof GameClipPostPasswordRequest
>;

export const GameEventFilterItemResponse = z.object({
	key: z.string(),
	value: z.array(z.string()),
});
export type GameEventFilterItemResponse = z.infer<
	typeof GameEventFilterItemResponse
>;

export const GameEventFilterResponse = z.object({
	id: z.number().int(),
	name: z.string(),
	filters: z.array(GameEventFilterItemResponse),
});
export type GameEventFilterResponse = z.infer<typeof GameEventFilterResponse>;

export const GameEventFilterItemPostRequest = z.object({
	key: z.string(),
	value: z.array(z.string()).min(1),
});
export type GameEventFilterItemPostRequest = z.infer<
	typeof GameEventFilterItemPostRequest
>;

export const GameEventFilterPostRequest = z.object({
	name: z.string().max(80),
	filters: z.array(GameEventFilterItemPostRequest).min(1),
});
export type GameEventFilterPostRequest = z.infer<
	typeof GameEventFilterPostRequest
>;

export const GameEventCommentPostRequest = z.object({
	text: z.string(),
	lockerRoomVisibility: z.enum([
		"players",
		"players_and_parents",
		"tagged_players",
		"tagged_players_and_their_parents",
	]),
	visibleToAll: z.boolean(),
	setChoicesAsDefault: z.boolean(),
});
export type GameEventCommentPostRequest = z.infer<
	typeof GameEventCommentPostRequest
>;

export const SingleCountryData = z.object({
	code: z.string(),
	name: z.string(),
	tooltip: z.string().nullable(),
	enabled: z.boolean(),
});
export type SingleCountryData = z.infer<typeof SingleCountryData>;

export const CountryData = z.object({
	africa: z.array(SingleCountryData),
	asia: z.array(SingleCountryData),
	europe: z.array(SingleCountryData),
	northAmerica: z.array(SingleCountryData),
	southAmerica: z.array(SingleCountryData),
	oceania: z.array(SingleCountryData),
	nationalContinents: z.array(SingleCountryData),
});
export type CountryData = z.infer<typeof CountryData>;

export const GenderViewResponse = z.object({
	available: z.boolean(),
	countries: CountryData.nullish(),
});
export type GenderViewResponse = z.infer<typeof GenderViewResponse>;

export const GendersViewResponse = z.object({
	male: GenderViewResponse,
	female: GenderViewResponse,
});
export type GendersViewResponse = z.infer<typeof GendersViewResponse>;

export const DataSourceOptionResponse = z.object({
	key: z.string(),
	label: z.string(),
	isSelectable: z.boolean(),
	genders: GendersViewResponse.nullish().default(null),
	logo: z.string().nullish().default(null),
	country: z.string().nullish().default(null),
	teamCount: z.number().int().optional().default(0),
});
export type DataSourceOptionResponse = z.infer<typeof DataSourceOptionResponse>;

export const DataSourceViewResponse = z.object({
	key: z.string(),
	label: z.string(),
	isSelectable: z.boolean(),
	genders: GendersViewResponse.nullish(),
	teamGroups: z.array(DataSourceOptionResponse).nullish(),
});
export type DataSourceViewResponse = z.infer<typeof DataSourceViewResponse>;

export const UserViewResponse = z.object({
	dataSources: z.array(DataSourceViewResponse),
});
export type UserViewResponse = z.infer<typeof UserViewResponse>;

export const UserViewRequest = z.object({
	gender: GenderEnum,
	scope: z.string(),
	teamGroups: z
		.union([z.array(z.string()), z.record(z.string())])
		.optional()
		.default([]),
});
export type UserViewRequest = z.infer<typeof UserViewRequest>;

export const MfaDomainScopeEnum = z.enum(["Included", "Excluded"]);
export type MfaDomainScopeEnum = z.infer<typeof MfaDomainScopeEnum>;

export const MfaDomainProductEnum = z.literal("Scout");
export type MfaDomainProductEnum = z.infer<typeof MfaDomainProductEnum>;

export const MfaDomainResponse = z.object({
	id: z.number().int().nullable(),
	domain: z.string(),
	enabled: z.boolean(),
	scope: MfaDomainScopeEnum,
	product: MfaDomainProductEnum.nullable(),
});
export type MfaDomainResponse = z.infer<typeof MfaDomainResponse>;

export const MfaDomainListResponse = z.object({
	domains: z.array(MfaDomainResponse),
});
export type MfaDomainListResponse = z.infer<typeof MfaDomainListResponse>;

export const MfaDomainCheckResponse = z.object({ isMfaRequired: z.boolean() });
export type MfaDomainCheckResponse = z.infer<typeof MfaDomainCheckResponse>;

export const ReportedTaggingIssuePostRequest = z.object({
	errorType: z.enum([
		"wrong player",
		"wrong event type",
		"event missing",
		"duplicated event",
		"other",
	]),
	videoTime: z.number().int().gte(0),
	gameEventDataV3Id: z.number().int().gt(0).nullish().default(null),
	description: z.string().optional().default(""),
	jerseyNumber: z.number().int().nullish().default(null),
	details: z.object({}).partial().nullish().default(null),
	selectedGameTeamId: z.number().int().nullish().default(null),
	selectedClubName: z.string().max(255).nullish().default(null),
	correctGamePlayerId: z.number().int().nullish().default(null),
	correctPlayerName: z.string().max(255).nullish().default(null),
	wrongEventType: z.string().max(63).nullish().default(null),
	wrongEventLabel: z.string().max(255).nullish().default(null),
	correctEventType: z.string().max(63).nullish().default(null),
	correctEventLabel: z.string().max(255).nullish().default(null),
});
export type ReportedTaggingIssuePostRequest = z.infer<
	typeof ReportedTaggingIssuePostRequest
>;

export const SelectedCountriesItemsResponse = z.object({
	africa: z.array(z.string()),
	asia: z.array(z.string()),
	europe: z.array(z.string()),
	northAmerica: z.array(z.string()),
	southAmerica: z.array(z.string()),
	oceania: z.array(z.string()),
	nationalContinents: z.array(z.string()),
});
export type SelectedCountriesItemsResponse = z.infer<
	typeof SelectedCountriesItemsResponse
>;

export const SelectedCountriesResponse = z.object({
	countries: SelectedCountriesItemsResponse,
	count: z.number().int(),
});
export type SelectedCountriesResponse = z.infer<
	typeof SelectedCountriesResponse
>;

export const UserCountriesPatchRequest = z.object({
	africa: z.array(z.string()).default([]),
	asia: z.array(z.string()).default([]),
	europe: z.array(z.string()).default([]),
	northAmerica: z.array(z.string()).default([]),
	southAmerica: z.array(z.string()).default([]),
	oceania: z.array(z.string()).default([]),
	nationalContinents: z.array(z.string()).default([]),
});
export type UserCountriesPatchRequest = z.infer<
	typeof UserCountriesPatchRequest
>;

export const TeamGroupTeamResponse2 = z.object({
	id: z.number().int(),
	name: z.string(),
	logo: z.string().nullish(),
	country: z.string().nullish(),
	gender: GenderEnum.nullish(),
});
export type TeamGroupTeamResponse2 = z.infer<typeof TeamGroupTeamResponse2>;

export const TeamGroupClubResponse2 = z.object({
	id: z.number().int(),
	name: z.string(),
	logo: z.string().nullish(),
	country: z.string().nullish(),
});
export type TeamGroupClubResponse2 = z.infer<typeof TeamGroupClubResponse2>;

export const TeamGroupVisibleEntitiesResponse = z.object({
	teams: z.array(TeamGroupTeamResponse2),
	clubs: z.array(TeamGroupClubResponse2),
});
export type TeamGroupVisibleEntitiesResponse = z.infer<
	typeof TeamGroupVisibleEntitiesResponse
>;

export const EventTypeItem = z.object({
	name: z.string(),
	eventValue: z.string(),
	count: z.number().int(),
});
export type EventTypeItem = z.infer<typeof EventTypeItem>;

export const EventTypeCategoryItem = z.object({
	categoryName: z.string(),
	items: z.array(EventTypeItem).nullable(),
});
export type EventTypeCategoryItem = z.infer<typeof EventTypeCategoryItem>;

export const EventTypes = z.object({
	events: z.array(EventTypeCategoryItem).nullable().default(null),
});
export type EventTypes = z.infer<typeof EventTypes>;

export const PlayerProfileSeasonFilterOption = z.object({
	label: z.string(),
	value: z.number().int().nullable(),
});
export type PlayerProfileSeasonFilterOption = z.infer<
	typeof PlayerProfileSeasonFilterOption
>;

export const PlayerProfileSeasonFilter = z.object({
	options: z.array(PlayerProfileSeasonFilterOption),
});
export type PlayerProfileSeasonFilter = z.infer<
	typeof PlayerProfileSeasonFilter
>;

export const PlayerProfileAgeGroupFilterOption = z.object({
	label: z.string(),
	value: z.number().int(),
});
export type PlayerProfileAgeGroupFilterOption = z.infer<
	typeof PlayerProfileAgeGroupFilterOption
>;

export const PlayerProfileAgeGroupFilter = z.object({
	options: z.array(PlayerProfileAgeGroupFilterOption),
});
export type PlayerProfileAgeGroupFilter = z.infer<
	typeof PlayerProfileAgeGroupFilter
>;

export const PlayerProfileClubFilterOption = z.object({
	label: z.string(),
	value: z.number().int(),
});
export type PlayerProfileClubFilterOption = z.infer<
	typeof PlayerProfileClubFilterOption
>;

export const PlayerProfileClubFilter = z.object({
	options: z.array(PlayerProfileClubFilterOption),
});
export type PlayerProfileClubFilter = z.infer<typeof PlayerProfileClubFilter>;

export const PlayerProfilePositionFilterOption = z.object({
	label: z.string(),
	value: z.string(),
});
export type PlayerProfilePositionFilterOption = z.infer<
	typeof PlayerProfilePositionFilterOption
>;

export const PlayerProfilePositionFilter = z.object({
	options: z.array(PlayerProfilePositionFilterOption),
});
export type PlayerProfilePositionFilter = z.infer<
	typeof PlayerProfilePositionFilter
>;

export const EventFilters = z.object({
	filterSeason: PlayerProfileSeasonFilter,
	filterAgeGroup: PlayerProfileAgeGroupFilter,
	filterClub: PlayerProfileClubFilter,
	filterPosition: PlayerProfilePositionFilter,
});
export type EventFilters = z.infer<typeof EventFilters>;

export const EventItemDetail = z.object({
	gameName: z.string(),
	date: z.string(),
	ageGroup: z.string().nullable(),
	league: z.string().nullable(),
	jerseyNumber: z.number().int().nullable(),
	eventType: z.string().nullable(),
	success: z.boolean(),
	position: z.string().nullable(),
	minute: z.string().nullable(),
	bodyPart: z.string().nullable(),
});
export type EventItemDetail = z.infer<typeof EventItemDetail>;

export const EventItem = z.object({
	gameId: z.number().int(),
	success: z.boolean(),
	name: z.string(),
	date: z.string(),
	gameName: z.string(),
	eventLength: z.string(),
	event: GPEventResponse,
	video: GPVideoResponse.nullish(),
	detail: EventItemDetail,
});
export type EventItem = z.infer<typeof EventItem>;

export const Events = z.object({
	events: z.array(EventItem).nullable(),
	total: z.number().int().default(0),
	page: z.number().int().default(1),
	pages: z.number().int().optional().default(1),
	limit: z.number().int().default(15),
});
export type Events = z.infer<typeof Events>;

export const KeyEventItem = z.object({
	categoryName: z.string(),
	events: z.array(EventItem).nullish(),
});
export type KeyEventItem = z.infer<typeof KeyEventItem>;

export const KeyEvents = z.object({
	events: z.array(KeyEventItem).nullable().default(null),
});
export type KeyEvents = z.infer<typeof KeyEvents>;

export const PlayerProfileCrossGameClipIntervalRequest = z.object({
	gameId: z.number().int().gt(0),
	start: z.number().int(),
	end: z.number().int(),
	events: z.array(GameClipEventRequest),
});
export type PlayerProfileCrossGameClipIntervalRequest = z.infer<
	typeof PlayerProfileCrossGameClipIntervalRequest
>;

export const PlayerProfileCrossGameClipPostRequest = z.object({
	name: z.string().max(255),
	description: z.string().max(255),
	intervals: z.array(PlayerProfileCrossGameClipIntervalRequest).min(1),
	tags: z.array(z.string()).nullish().default(null),
});
export type PlayerProfileCrossGameClipPostRequest = z.infer<
	typeof PlayerProfileCrossGameClipPostRequest
>;

export const ShadowTeamItem = z.object({
	id: z.number().int(),
	name: z.string(),
	playersCount: z.number().int(),
	formation: z.string(),
	inShadowTeam: z.boolean(),
});
export type ShadowTeamItem = z.infer<typeof ShadowTeamItem>;

export const ShadowTeamList = z.object({
	teams: z.array(ShadowTeamItem).nullable(),
});
export type ShadowTeamList = z.infer<typeof ShadowTeamList>;

export const ShadowTeamPlayerPostRequest = z.object({
	shadowTeamIds: z.array(z.number().int()),
});
export type ShadowTeamPlayerPostRequest = z.infer<
	typeof ShadowTeamPlayerPostRequest
>;

export const ShadowTeamPostRequest = z.object({
	name: z.string(),
	formation: z.string(),
});
export type ShadowTeamPostRequest = z.infer<typeof ShadowTeamPostRequest>;

export const PlayerProfileRecruitmentInformationItem = z.object({
	key: z
		.enum([
			"toeflScore",
			"ieltsScore",
			"gpa",
			"satScore",
			"graduationYear",
			"commitmentStatus",
		])
		.describe("Stable field identifier"),
	value: z.string().describe("Display-ready value"),
});
export type PlayerProfileRecruitmentInformationItem = z.infer<
	typeof PlayerProfileRecruitmentInformationItem
>;

export const PlayerProfileRecruitmentInformationResponse = z.object({
	hasRecruitmentInformation: z
		.boolean()
		.describe(
			"False when there is nothing to show, so the section can be hidden",
		),
	openToOpportunities: z
		.boolean()
		.describe(
			"Whether the player is open to US college recruitment opportunities",
		),
	items: z
		.array(PlayerProfileRecruitmentInformationItem)
		.describe("Populated answers only, in display order")
		.default([]),
	updatedAt: z
		.string()
		.datetime({ offset: true })
		.describe("When the academic answers were last saved")
		.nullish()
		.default(null),
});
export type PlayerProfileRecruitmentInformationResponse = z.infer<
	typeof PlayerProfileRecruitmentInformationResponse
>;

export const PlayerProfileBiometricsAvailabilityResponse = z.object({
	hasScans: z
		.boolean()
		.describe("The player has completed at least one Fitmatch scan."),
	isDataShared: z
		.boolean()
		.describe(
			"The player shares biometric data with the current viewer. Always false when hasScans is false, which is the cheap path and skips the visibility lookup.",
		),
	isAvailable: z
		.boolean()
		.describe(
			"Both conditions are met, so the Biometrics sub-tab may be rendered.",
		),
});
export type PlayerProfileBiometricsAvailabilityResponse = z.infer<
	typeof PlayerProfileBiometricsAvailabilityResponse
>;

export const PlayerProfileBodyMeasurementStat = z.object({
	key: z.string(),
	name: z.string(),
	value: z.number().nullable(),
	unit: z.string(),
});
export type PlayerProfileBodyMeasurementStat = z.infer<
	typeof PlayerProfileBodyMeasurementStat
>;

export const PlayerProfileBodyMeasurementsSummary = z.object({
	stats: z.array(PlayerProfileBodyMeasurementStat),
	scanFileUrl: z.string().nullish(),
});
export type PlayerProfileBodyMeasurementsSummary = z.infer<
	typeof PlayerProfileBodyMeasurementsSummary
>;

export const PlayerProfileBodyCircumferences = z.object({
	upperBody: z.array(PlayerProfileBodyMeasurementStat),
	lowerBody: z.array(PlayerProfileBodyMeasurementStat),
});
export type PlayerProfileBodyCircumferences = z.infer<
	typeof PlayerProfileBodyCircumferences
>;

export const PlayerProfileBodyMeasurementsResponse = z.object({
	summary: PlayerProfileBodyMeasurementsSummary,
	circumferences: PlayerProfileBodyCircumferences,
	fullBody: z.array(PlayerProfileBodyMeasurementStat),
	lastScanDate: z.string().nullish(),
});
export type PlayerProfileBodyMeasurementsResponse = z.infer<
	typeof PlayerProfileBodyMeasurementsResponse
>;

export const PlayerProfileBodyMetricTrendPagination = z.object({
	page: z
		.number()
		.int()
		.describe("Page 1 holds the newest scans, the last page the oldest ones."),
	pageCount: z.number().int(),
	limit: z.number().int().describe("Scans per page."),
});
export type PlayerProfileBodyMetricTrendPagination = z.infer<
	typeof PlayerProfileBodyMetricTrendPagination
>;

export const PlayerProfileBodyMetricTrendPoint = z.object({
	scanNumber: z.number().int(),
	scanDate: z.string(),
	value: z.number().nullable(),
});
export type PlayerProfileBodyMetricTrendPoint = z.infer<
	typeof PlayerProfileBodyMetricTrendPoint
>;

export const PlayerProfileBodyMetricFilterOption = z.object({
	label: z.string(),
	value: z.string(),
});
export type PlayerProfileBodyMetricFilterOption = z.infer<
	typeof PlayerProfileBodyMetricFilterOption
>;

export const PlayerProfileBodyMetricFilter = z.object({
	options: z.array(PlayerProfileBodyMetricFilterOption),
});
export type PlayerProfileBodyMetricFilter = z.infer<
	typeof PlayerProfileBodyMetricFilter
>;

export const PlayerProfileBodyMetricTrendResponse = z.object({
	selectedMetric: z.string(),
	unit: z.string(),
	hasEnoughScans: z.boolean(),
	pagination: PlayerProfileBodyMetricTrendPagination,
	points: z.array(PlayerProfileBodyMetricTrendPoint),
	filterMetric: PlayerProfileBodyMetricFilter,
});
export type PlayerProfileBodyMetricTrendResponse = z.infer<
	typeof PlayerProfileBodyMetricTrendResponse
>;

export const PlayerProfileScanReference = z.object({
	scanId: z.string(),
	scanNumber: z.number().int(),
	scanDate: z.string(),
	label: z.string(),
});
export type PlayerProfileScanReference = z.infer<
	typeof PlayerProfileScanReference
>;

export const PlayerProfileBodyCompositionChangeAxis = z.object({
	name: z.string(),
	unit: z.string(),
	firstValue: z.number().nullable(),
	secondValue: z.number().nullable(),
	axisMin: z.number().nullable(),
	axisMax: z.number().nullable(),
});
export type PlayerProfileBodyCompositionChangeAxis = z.infer<
	typeof PlayerProfileBodyCompositionChangeAxis
>;

export const PlayerProfileScanFilterOption = z.object({
	label: z.string(),
	value: z.string(),
	selected: z.boolean().default(false),
});
export type PlayerProfileScanFilterOption = z.infer<
	typeof PlayerProfileScanFilterOption
>;

export const PlayerProfileScanFilter = z.object({
	options: z.array(PlayerProfileScanFilterOption),
});
export type PlayerProfileScanFilter = z.infer<typeof PlayerProfileScanFilter>;

export const PlayerProfileBodyCompositionChangeResponse = z.object({
	hasEnoughScans: z.boolean(),
	firstScan: PlayerProfileScanReference.nullable(),
	secondScan: PlayerProfileScanReference.nullable(),
	axes: z.array(PlayerProfileBodyCompositionChangeAxis),
	fullBodyMeasurements: z.array(PlayerProfileBodyMeasurementStat),
	filterScan: PlayerProfileScanFilter,
});
export type PlayerProfileBodyCompositionChangeResponse = z.infer<
	typeof PlayerProfileBodyCompositionChangeResponse
>;

export const PlayerProfilePhysicalMetricsFiltersResponse = z.object({
	filterSeason: PlayerProfileSeasonFilter,
});
export type PlayerProfilePhysicalMetricsFiltersResponse = z.infer<
	typeof PlayerProfilePhysicalMetricsFiltersResponse
>;

export const PlayerProfilePhysicalMetricsGameReference = z.object({
	gameId: z.number().int(),
	opponentName: z.string(),
	playedAt: z.string(),
});
export type PlayerProfilePhysicalMetricsGameReference = z.infer<
	typeof PlayerProfilePhysicalMetricsGameReference
>;

export const PlayerProfilePhysicalMetricsTopSpeedPeak = z.object({
	value: z.number(),
	unit: z.string(),
	game: PlayerProfilePhysicalMetricsGameReference.describe(
		"Game where the peak was achieved.",
	).nullable(),
	isPeakRecordedThisSeason: z
		.boolean()
		.describe(
			"True when the all-time peak was achieved in the selected season.",
		),
});
export type PlayerProfilePhysicalMetricsTopSpeedPeak = z.infer<
	typeof PlayerProfilePhysicalMetricsTopSpeedPeak
>;

export const PlayerProfilePhysicalMetricsMetric = z.object({
	value: z.number(),
	unit: z.string().nullable(),
	changeVsPreviousSeason: z
		.number()
		.describe("Difference against the previous season, in the metric unit.")
		.nullable(),
});
export type PlayerProfilePhysicalMetricsMetric = z.infer<
	typeof PlayerProfilePhysicalMetricsMetric
>;

export const PlayerProfilePhysicalMetricsPeakPerformance = z.object({
	highestTopSpeed: PlayerProfilePhysicalMetricsTopSpeedPeak.nullable(),
	averageTopSpeed: PlayerProfilePhysicalMetricsMetric.describe(
		"Sum of per game top speeds divided by the number of games in the selected season.",
	).nullable(),
});
export type PlayerProfilePhysicalMetricsPeakPerformance = z.infer<
	typeof PlayerProfilePhysicalMetricsPeakPerformance
>;

export const PlayerProfilePhysicalMetricsVolumeAndLoad = z.object({
	averageDistanceCovered: PlayerProfilePhysicalMetricsMetric.describe(
		"Total distance per game normalised to 90 minutes, averaged across games in the selected season.",
	).nullable(),
	accelerations: PlayerProfilePhysicalMetricsMetric.describe(
		"Acceleration counts per game normalised to 90 minutes (threshold >= 10.8 km/hr).",
	).nullable(),
	decelerations: PlayerProfilePhysicalMetricsMetric.describe(
		"Deceleration counts per game normalised to 90 minutes (threshold <= -10.8 km/hr).",
	).nullable(),
});
export type PlayerProfilePhysicalMetricsVolumeAndLoad = z.infer<
	typeof PlayerProfilePhysicalMetricsVolumeAndLoad
>;

export const PlayerProfilePhysicalMetricsMovementZone = z.object({
	zone: z.enum([
		"standing",
		"walking",
		"jogging",
		"running",
		"highSpeedRunning",
		"sprinting",
	]),
	percentage: z
		.number()
		.describe("Share of the total time spent in the zone, 0-100."),
	minutesPer90: z
		.number()
		.describe("Minutes spent in the zone, normalised to 90 minutes."),
});
export type PlayerProfilePhysicalMetricsMovementZone = z.infer<
	typeof PlayerProfilePhysicalMetricsMovementZone
>;

export const PlayerProfilePhysicalMetricsDocument = z.object({
	id: z.number().int(),
	name: z.string(),
	gameId: z
		.number()
		.int()
		.describe("Null for documents not tied to a single game.")
		.nullable(),
	homeTeamName: z.string().nullable(),
	awayTeamName: z.string().nullable(),
	playedAt: z.string().nullable(),
	seasonLabel: z.string().nullable(),
	viewUrl: z
		.string()
		.describe("PDF opened in the viewer, same asset as used on the game page.")
		.nullable(),
	downloadUrl: z.string().nullable(),
});
export type PlayerProfilePhysicalMetricsDocument = z.infer<
	typeof PlayerProfilePhysicalMetricsDocument
>;

export const PlayerProfilePhysicalMetricsResponse = z.object({
	season: z
		.number()
		.int()
		.describe(
			"Selected season, null when metrics are aggregated across all seasons.",
		)
		.nullable(),
	seasonLabel: z.string().nullable(),
	games: z
		.number()
		.int()
		.describe("Games contributing to the aggregated metrics."),
	minutes: z
		.number()
		.int()
		.describe("Minutes contributing to the aggregated metrics."),
	positions: z.array(z.string()),
	peakPerformance: PlayerProfilePhysicalMetricsPeakPerformance,
	volumeAndLoad: PlayerProfilePhysicalMetricsVolumeAndLoad,
	movementDistribution: z
		.array(PlayerProfilePhysicalMetricsMovementZone)
		.describe(
			"Time zones averaged per 90 minutes, ordered from the slowest to the fastest zone.",
		),
	documents: z.array(PlayerProfilePhysicalMetricsDocument),
});
export type PlayerProfilePhysicalMetricsResponse = z.infer<
	typeof PlayerProfilePhysicalMetricsResponse
>;

export const PlayerProfileBehavioralPeriodFilterOption = z.object({
	label: z.string(),
	value: z.string(),
	isDefault: z.boolean(),
});
export type PlayerProfileBehavioralPeriodFilterOption = z.infer<
	typeof PlayerProfileBehavioralPeriodFilterOption
>;

export const PlayerProfileBehavioralPeriodFilter = z.object({
	options: z.array(PlayerProfileBehavioralPeriodFilterOption),
});
export type PlayerProfileBehavioralPeriodFilter = z.infer<
	typeof PlayerProfileBehavioralPeriodFilter
>;

export const PlayerProfileBehavioralSeasonFilterOption = z.object({
	label: z.string(),
	value: z.number().int().nullable(),
	isDefault: z
		.boolean()
		.describe(
			"Preselect this option. Set on the only season when a player has exactly one.",
		),
});
export type PlayerProfileBehavioralSeasonFilterOption = z.infer<
	typeof PlayerProfileBehavioralSeasonFilterOption
>;

export const PlayerProfileBehavioralSeasonFilter = z.object({
	options: z.array(PlayerProfileBehavioralSeasonFilterOption),
});
export type PlayerProfileBehavioralSeasonFilter = z.infer<
	typeof PlayerProfileBehavioralSeasonFilter
>;

export const PlayerProfileBehavioralCohortFilterOption = z.object({
	label: z.string(),
	value: z.string(),
	appliesTo: z
		.array(z.string())
		.describe("Period selections this cohort can be combined with."),
	requiresPosition: z
		.boolean()
		.describe(
			"Whether the position dropdown has to be shown next to this cohort.",
		),
	ageCategory: z.string().describe("Age category").nullable(),
	isDefault: z
		.boolean()
		.describe(
			"Whether this is the default cohort for the period selections it applies to.",
		),
});
export type PlayerProfileBehavioralCohortFilterOption = z.infer<
	typeof PlayerProfileBehavioralCohortFilterOption
>;

export const PlayerProfileBehavioralCohortFilter = z.object({
	options: z.array(PlayerProfileBehavioralCohortFilterOption),
});
export type PlayerProfileBehavioralCohortFilter = z.infer<
	typeof PlayerProfileBehavioralCohortFilter
>;

export const PlayerProfileBehavioralSignalsFiltersResponse = z.object({
	filterPeriod: PlayerProfileBehavioralPeriodFilter,
	filterSeason: PlayerProfileBehavioralSeasonFilter,
	filterCohort: PlayerProfileBehavioralCohortFilter,
	filterPosition: PlayerProfilePositionFilter,
	maximumSnapshotSeasons: z
		.number()
		.int()
		.describe("How many seasons the snapshot radar can overlay at once."),
	minimumAnalyzedMinutes: z
		.number()
		.int()
		.describe(
			"Analyzed minutes required before behavioral signals are calculated.",
		),
});
export type PlayerProfileBehavioralSignalsFiltersResponse = z.infer<
	typeof PlayerProfileBehavioralSignalsFiltersResponse
>;

export const PlayerProfileBehavioralSummary = z.object({
	header: z.string(),
	text: z.string(),
});
export type PlayerProfileBehavioralSummary = z.infer<
	typeof PlayerProfileBehavioralSummary
>;

export const PlayerProfileBehavioralChartPagination = z.object({
	page: z.number().int(),
	pageCount: z.number().int(),
	gamesPerPage: z.number().int(),
});
export type PlayerProfileBehavioralChartPagination = z.infer<
	typeof PlayerProfileBehavioralChartPagination
>;

export const PlayerProfileBehavioralChartPointScore = z.object({
	signal: z.string(),
	score: z.number().int().describe("Signal score on a 0-100 scale."),
});
export type PlayerProfileBehavioralChartPointScore = z.infer<
	typeof PlayerProfileBehavioralChartPointScore
>;

export const PlayerProfileBehavioralChartPoint = z.object({
	label: z
		.string()
		.describe("X-axis label, a match sequence label or a season label."),
	gameId: z
		.number()
		.int()
		.describe(
			"Null on the all-seasons chart, where a point aggregates a whole season.",
		)
		.nullable(),
	opponentName: z.string().nullable(),
	homeTeamName: z
		.string()
		.describe(
			"Home side of the fixture. Equals opponentName when the player played away.",
		)
		.nullable(),
	opponentClubId: z
		.number()
		.int()
		.describe("Opponent club, for the axis label logo.")
		.nullable(),
	opponentClubLogo: z.string().nullable(),
	playedAt: z.string().nullable(),
	season: z.number().int().nullable(),
	teamGoals: z
		.number()
		.int()
		.describe(
			"Goals scored by the player's team. Null on the all-seasons chart.",
		)
		.nullable(),
	opponentGoals: z
		.number()
		.int()
		.describe("Goals scored by the opponent. Null on the all-seasons chart.")
		.nullable(),
	scores: z.array(PlayerProfileBehavioralChartPointScore),
	cohortLowerQuartile: z
		.number()
		.int()
		.describe("Cohort 25th percentile, only set on single-signal charts.")
		.nullable(),
	cohortUpperQuartile: z
		.number()
		.int()
		.describe("Cohort 75th percentile, only set on single-signal charts.")
		.nullable(),
});
export type PlayerProfileBehavioralChartPoint = z.infer<
	typeof PlayerProfileBehavioralChartPoint
>;

export const PlayerProfileBehavioralChart = z.object({
	xAxisType: z.string().describe("What a single point represents."),
	yAxisMinimum: z
		.number()
		.int()
		.describe("Lowest plotted score padded by 10, never below 0."),
	yAxisMaximum: z
		.number()
		.int()
		.describe("Highest plotted score padded by 10, never above 100."),
	averageScore: z
		.number()
		.describe("Average score across the whole selected period")
		.nullable(),
	pagination: PlayerProfileBehavioralChartPagination.nullable(),
	points: z.array(PlayerProfileBehavioralChartPoint),
});
export type PlayerProfileBehavioralChart = z.infer<
	typeof PlayerProfileBehavioralChart
>;

export const PlayerProfileBehavioralSignalPanel = z.object({
	signal: z.string(),
	name: z.string(),
	description: z.string().describe("Tooltip copy explaining the signal."),
	score: z
		.number()
		.int()
		.describe("Current score on a 0-100 scale.")
		.nullable(),
	band: z.string().nullable(),
	bandLabel: z
		.string()
		.describe("Short label of the band, e.g. Clear response.")
		.nullable(),
	typicalRange: z.string().nullable(),
	trendDirection: z
		.string()
		.describe(
			"Null when there is no comparable preceding period, for example on the all-seasons chart.",
		)
		.nullable(),
	trendPercentage: z
		.number()
		.describe(
			"Signed change against the preceding period of the same length, in percent.",
		)
		.nullable(),
	cohortDifferencePercentage: z
		.number()
		.describe("Signed difference against the cohort median, in percent.")
		.nullable(),
});
export type PlayerProfileBehavioralSignalPanel = z.infer<
	typeof PlayerProfileBehavioralSignalPanel
>;

export const PlayerProfileBehavioralSignalsTrendResponse = z.object({
	hasEnoughData: z.boolean(),
	period: z.string(),
	season: z.number().int().nullable(),
	seasonLabel: z.string().nullable(),
	signal: z.string().nullable(),
	cohort: z
		.string()
		.describe(
			"Cohort the IQR band is calculated from. Null on the overview tab.",
		)
		.nullable(),
	position: z.string().describe("Position the trend is restricted.").nullable(),
	ageCategory: z
		.string()
		.describe("Age category the trend is restricted.")
		.nullable(),
	games: z
		.number()
		.int()
		.describe(
			"Games in the selected period. Null for the last-games period, which is not season bound.",
		)
		.nullable(),
	minutes: z
		.number()
		.int()
		.describe("Analyzed minutes in the selected period."),
	confidence: z.string().nullable(),
	summary: PlayerProfileBehavioralSummary.describe(
		"Combined summary on the overview tab, single-signal summary on the other tabs.",
	).nullable(),
	chart: PlayerProfileBehavioralChart.nullable(),
	signals: z.array(PlayerProfileBehavioralSignalPanel),
});
export type PlayerProfileBehavioralSignalsTrendResponse = z.infer<
	typeof PlayerProfileBehavioralSignalsTrendResponse
>;

export const PlayerProfileBehavioralSeasonReference = z.object({
	season: z.number().int(),
	seasonLabel: z.string(),
});
export type PlayerProfileBehavioralSeasonReference = z.infer<
	typeof PlayerProfileBehavioralSeasonReference
>;

export const PlayerProfileBehavioralSnapshotAxis = z.object({
	signal: z.string(),
	name: z.string(),
	score: z.number().int().describe("Signal score on a 0-100 scale."),
});
export type PlayerProfileBehavioralSnapshotAxis = z.infer<
	typeof PlayerProfileBehavioralSnapshotAxis
>;

export const PlayerProfileBehavioralSnapshotSeries = z.object({
	season: z.number().int().nullable(),
	seasonLabel: z.string(),
	games: z.number().int(),
	minutes: z.number().int(),
	axes: z.array(PlayerProfileBehavioralSnapshotAxis),
});
export type PlayerProfileBehavioralSnapshotSeries = z.infer<
	typeof PlayerProfileBehavioralSnapshotSeries
>;

export const PlayerProfileBehavioralSignalsSnapshotResponse = z.object({
	hasEnoughData: z.boolean(),
	period: z.string(),
	selectedSeasons: z
		.array(PlayerProfileBehavioralSeasonReference)
		.describe("Seasons"),
	games: z
		.number()
		.int()
		.describe("Games across all rendered series.")
		.nullable(),
	minutes: z
		.number()
		.int()
		.describe("Analyzed minutes across all rendered series."),
	confidence: z.string().nullable(),
	summary: PlayerProfileBehavioralSummary.describe(
		"Combined summary of the most recent rendered series.",
	).nullable(),
	series: z.array(PlayerProfileBehavioralSnapshotSeries),
	signals: z
		.array(PlayerProfileBehavioralSignalPanel)
		.describe("Right-hand panel rows for the most recent rendered series."),
});
export type PlayerProfileBehavioralSignalsSnapshotResponse = z.infer<
	typeof PlayerProfileBehavioralSignalsSnapshotResponse
>;

export const PlayerProfileArchetype = z.object({
	archetype: z.string(),
	name: z.string(),
	tagline: z.string(),
	alignmentScore: z
		.number()
		.int()
		.describe("Alignment with this archetype on a 0-100 scale."),
	tags: z.array(z.string()),
	shortDescription: z.string(),
	fullDescription: z.string(),
	observedPatterns: z.array(z.string()),
	developmentFocus: z.string(),
});
export type PlayerProfileArchetype = z.infer<typeof PlayerProfileArchetype>;

export const PlayerProfileArchetypesResponse = z.object({
	status: z
		.string()
		.describe("Archetype content is only returned for completedAndShared."),
	completedAt: z
		.string()
		.describe("Date the player last submitted the questionnaire.")
		.nullable(),
	summary: z.string().nullable(),
	dominantArchetype: PlayerProfileArchetype.nullable(),
	secondaryArchetypes: z.array(PlayerProfileArchetype),
});
export type PlayerProfileArchetypesResponse = z.infer<
	typeof PlayerProfileArchetypesResponse
>;

const denyGameAnalysisRequestBody = z
	.object({ reason: z.string().nullable() })
	.partial();
export type denyGameAnalysisRequestBody = z.infer<
	typeof denyGameAnalysisRequestBody
>;

const patchDiscoverGameLinkRequestBody = z.object({ link: z.string() });
export type patchDiscoverGameLinkRequestBody = z.infer<
	typeof patchDiscoverGameLinkRequestBody
>;

const patchAdminDiscoverUserNoteRequestBody = z.object({
	note: z.string().describe("The note content"),
});
export type patchAdminDiscoverUserNoteRequestBody = z.infer<
	typeof patchAdminDiscoverUserNoteRequestBody
>;

const forgotPasswordRequestBody = z.object({ email: z.string() }).partial();
export type forgotPasswordRequestBody = z.infer<
	typeof forgotPasswordRequestBody
>;

const resetPasswordRequestBody = z.object({ newPassword: z.string() });
export type resetPasswordRequestBody = z.infer<typeof resetPasswordRequestBody>;

const saveUserWidgetSettingsRequestBody = z
	.object({ insightDisplaySettings: z.object({}).partial() })
	.partial();
export type saveUserWidgetSettingsRequestBody = z.infer<
	typeof saveUserWidgetSettingsRequestBody
>;

const generateSignUpLinkRequestBody = z
	.object({ playerId: z.number().int() })
	.partial();
export type generateSignUpLinkRequestBody = z.infer<
	typeof generateSignUpLinkRequestBody
>;

const renameVideoRequestBody = z.object({
	name: z.string().max(255).describe("New name for the video").default(""),
});
export type renameVideoRequestBody = z.infer<typeof renameVideoRequestBody>;

const putGameDescriptionRequestBody = z
	.object({ description: z.string().nullable().default(null) })
	.partial();
export type putGameDescriptionRequestBody = z.infer<
	typeof putGameDescriptionRequestBody
>;

const postGameClipSharedPasswordValidationRequestBody = z.object({
	password: z.string(),
});
export type postGameClipSharedPasswordValidationRequestBody = z.infer<
	typeof postGameClipSharedPasswordValidationRequestBody
>;

const getDiscoverClubHousesResponse400 = z.string();
export type getDiscoverClubHousesResponse400 = z.infer<
	typeof getDiscoverClubHousesResponse400
>;

const getDiscoverClubHousesResponse403 = z.string();
export type getDiscoverClubHousesResponse403 = z.infer<
	typeof getDiscoverClubHousesResponse403
>;

const getDiscoverClubHouseResponse400 = z.string();
export type getDiscoverClubHouseResponse400 = z.infer<
	typeof getDiscoverClubHouseResponse400
>;

const getDiscoverClubHouseResponse403 = z.string();
export type getDiscoverClubHouseResponse403 = z.infer<
	typeof getDiscoverClubHouseResponse403
>;

const getDiscoverClubHouseResponse404 = z.string();
export type getDiscoverClubHouseResponse404 = z.infer<
	typeof getDiscoverClubHouseResponse404
>;

const patchDiscoverClubHouseResponse200 = z.string();
export type patchDiscoverClubHouseResponse200 = z.infer<
	typeof patchDiscoverClubHouseResponse200
>;

const patchDiscoverClubHouseResponse400 = z.string();
export type patchDiscoverClubHouseResponse400 = z.infer<
	typeof patchDiscoverClubHouseResponse400
>;

const patchDiscoverClubHouseResponse403 = z.string();
export type patchDiscoverClubHouseResponse403 = z.infer<
	typeof patchDiscoverClubHouseResponse403
>;

const patchDiscoverClubHouseResponse404 = z.string();
export type patchDiscoverClubHouseResponse404 = z.infer<
	typeof patchDiscoverClubHouseResponse404
>;

const getDiscoverCountriesResponse200 = z.array(ChoiceResponse);
export type getDiscoverCountriesResponse200 = z.infer<
	typeof getDiscoverCountriesResponse200
>;

const getDiscoverGamesResponse400 = z.string();
export type getDiscoverGamesResponse400 = z.infer<
	typeof getDiscoverGamesResponse400
>;

const getDiscoverGamesResponse403 = z.string();
export type getDiscoverGamesResponse403 = z.infer<
	typeof getDiscoverGamesResponse403
>;

const getDiscoverGamesDynamicResponse400 = z.string();
export type getDiscoverGamesDynamicResponse400 = z.infer<
	typeof getDiscoverGamesDynamicResponse400
>;

const getDiscoverGamesDynamicResponse403 = z.string();
export type getDiscoverGamesDynamicResponse403 = z.infer<
	typeof getDiscoverGamesDynamicResponse403
>;

const getDiscoverGamesV2FilterOptionsResponse200 = z.array(
	z.object({ value: z.string(), label: z.string() }).partial(),
);
export type getDiscoverGamesV2FilterOptionsResponse200 = z.infer<
	typeof getDiscoverGamesV2FilterOptionsResponse200
>;

const getDiscoverGamesV2FilterOptionsResponse400 = z.string();
export type getDiscoverGamesV2FilterOptionsResponse400 = z.infer<
	typeof getDiscoverGamesV2FilterOptionsResponse400
>;

const getDiscoverGamesV2FilterOptionsResponse403 = z.string();
export type getDiscoverGamesV2FilterOptionsResponse403 = z.infer<
	typeof getDiscoverGamesV2FilterOptionsResponse403
>;

const getDiscoverGamesV2AssigneeOptionsResponse200 = z.array(
	z.object({ value: z.string(), label: z.string() }).partial(),
);
export type getDiscoverGamesV2AssigneeOptionsResponse200 = z.infer<
	typeof getDiscoverGamesV2AssigneeOptionsResponse200
>;

const getDiscoverGamesV2AssigneeOptionsResponse403 = z.string();
export type getDiscoverGamesV2AssigneeOptionsResponse403 = z.infer<
	typeof getDiscoverGamesV2AssigneeOptionsResponse403
>;

const getDiscoverGamesV2ClubMetadataUserOptionsResponse200 = z.array(
	z.object({ value: z.string(), label: z.string() }).partial(),
);
export type getDiscoverGamesV2ClubMetadataUserOptionsResponse200 = z.infer<
	typeof getDiscoverGamesV2ClubMetadataUserOptionsResponse200
>;

const getDiscoverGamesV2ClubMetadataUserOptionsResponse403 = z.string();
export type getDiscoverGamesV2ClubMetadataUserOptionsResponse403 = z.infer<
	typeof getDiscoverGamesV2ClubMetadataUserOptionsResponse403
>;

const getDiscoverGamesV2GameStatusOptionsResponse200 = z.array(
	z.object({ value: z.string(), label: z.string() }).partial(),
);
export type getDiscoverGamesV2GameStatusOptionsResponse200 = z.infer<
	typeof getDiscoverGamesV2GameStatusOptionsResponse200
>;

const getDiscoverGamesV2GameStatusOptionsResponse403 = z.string();
export type getDiscoverGamesV2GameStatusOptionsResponse403 = z.infer<
	typeof getDiscoverGamesV2GameStatusOptionsResponse403
>;

const getDiscoverGamesV2OwnerFilterOptionsResponse200 = z.array(
	DiscoverGamesOwnerFilterOptionResponse,
);
export type getDiscoverGamesV2OwnerFilterOptionsResponse200 = z.infer<
	typeof getDiscoverGamesV2OwnerFilterOptionsResponse200
>;

const getDiscoverGamesV2OwnerFilterOptionsResponse403 = z.string();
export type getDiscoverGamesV2OwnerFilterOptionsResponse403 = z.infer<
	typeof getDiscoverGamesV2OwnerFilterOptionsResponse403
>;

const getDiscoverGamesProgramGameFixturesResponse200 = z.object({
	gameTypes: z.array(z.object({ value: z.string(), label: z.string() })),
});
export type getDiscoverGamesProgramGameFixturesResponse200 = z.infer<
	typeof getDiscoverGamesProgramGameFixturesResponse200
>;

const getDiscoverGamesProgramGameFixturesResponse403 = z.string();
export type getDiscoverGamesProgramGameFixturesResponse403 = z.infer<
	typeof getDiscoverGamesProgramGameFixturesResponse403
>;

const getDiscoverGamesProgramGameClubOptionsResponse200 = z.array(
	DiscoverProgramGameClubOptionResponse,
);
export type getDiscoverGamesProgramGameClubOptionsResponse200 = z.infer<
	typeof getDiscoverGamesProgramGameClubOptionsResponse200
>;

const getDiscoverGamesProgramGameClubOptionsResponse400 = z.string();
export type getDiscoverGamesProgramGameClubOptionsResponse400 = z.infer<
	typeof getDiscoverGamesProgramGameClubOptionsResponse400
>;

const getDiscoverGamesProgramGameClubOptionsResponse403 = z.string();
export type getDiscoverGamesProgramGameClubOptionsResponse403 = z.infer<
	typeof getDiscoverGamesProgramGameClubOptionsResponse403
>;

const getDiscoverGamesProgramGameNationalLeaguesResponse200 = z.array(
	DiscoverProgramGameNationalLeagueOptionResponse,
);
export type getDiscoverGamesProgramGameNationalLeaguesResponse200 = z.infer<
	typeof getDiscoverGamesProgramGameNationalLeaguesResponse200
>;

const getDiscoverGamesProgramGameNationalLeaguesResponse400 = z.string();
export type getDiscoverGamesProgramGameNationalLeaguesResponse400 = z.infer<
	typeof getDiscoverGamesProgramGameNationalLeaguesResponse400
>;

const getDiscoverGamesProgramGameNationalLeaguesResponse403 = z.string();
export type getDiscoverGamesProgramGameNationalLeaguesResponse403 = z.infer<
	typeof getDiscoverGamesProgramGameNationalLeaguesResponse403
>;

const postDiscoverProgramGameResponse200 = z.object({
	gameId: z.number().int(),
});
export type postDiscoverProgramGameResponse200 = z.infer<
	typeof postDiscoverProgramGameResponse200
>;

const postDiscoverProgramGameResponse400 = z.string();
export type postDiscoverProgramGameResponse400 = z.infer<
	typeof postDiscoverProgramGameResponse400
>;

const postDiscoverProgramGameResponse403 = z.string();
export type postDiscoverProgramGameResponse403 = z.infer<
	typeof postDiscoverProgramGameResponse403
>;

const getDiscoverGamesBulkEligibilityResponse400 = z.string();
export type getDiscoverGamesBulkEligibilityResponse400 = z.infer<
	typeof getDiscoverGamesBulkEligibilityResponse400
>;

const getDiscoverGamesBulkEligibilityResponse403 = z.string();
export type getDiscoverGamesBulkEligibilityResponse403 = z.infer<
	typeof getDiscoverGamesBulkEligibilityResponse403
>;

const postDiscoverGamesBulkPatchResponse400 = z.string();
export type postDiscoverGamesBulkPatchResponse400 = z.infer<
	typeof postDiscoverGamesBulkPatchResponse400
>;

const postDiscoverGamesBulkPatchResponse403 = z.string();
export type postDiscoverGamesBulkPatchResponse403 = z.infer<
	typeof postDiscoverGamesBulkPatchResponse403
>;

const getDiscoverGameResponse400 = z.string();
export type getDiscoverGameResponse400 = z.infer<
	typeof getDiscoverGameResponse400
>;

const getDiscoverGameResponse403 = z.string();
export type getDiscoverGameResponse403 = z.infer<
	typeof getDiscoverGameResponse403
>;

const getDiscoverGameResponse404 = z.string();
export type getDiscoverGameResponse404 = z.infer<
	typeof getDiscoverGameResponse404
>;

const patchDiscoverGameResponse200 = z.string();
export type patchDiscoverGameResponse200 = z.infer<
	typeof patchDiscoverGameResponse200
>;

const patchDiscoverGameResponse400 = z.string();
export type patchDiscoverGameResponse400 = z.infer<
	typeof patchDiscoverGameResponse400
>;

const patchDiscoverGameResponse403 = z.string();
export type patchDiscoverGameResponse403 = z.infer<
	typeof patchDiscoverGameResponse403
>;

const patchDiscoverGameResponse404 = z.string();
export type patchDiscoverGameResponse404 = z.infer<
	typeof patchDiscoverGameResponse404
>;

const getDiscoverGameV2Response404 = z.string();
export type getDiscoverGameV2Response404 = z.infer<
	typeof getDiscoverGameV2Response404
>;

const getDiscoverGameMetadataOptionsResponse403 = z.string();
export type getDiscoverGameMetadataOptionsResponse403 = z.infer<
	typeof getDiscoverGameMetadataOptionsResponse403
>;

const approveGameAnalysisResponse200 = z.string();
export type approveGameAnalysisResponse200 = z.infer<
	typeof approveGameAnalysisResponse200
>;

const approveGameAnalysisResponse400 = z.string();
export type approveGameAnalysisResponse400 = z.infer<
	typeof approveGameAnalysisResponse400
>;

const approveGameAnalysisResponse403 = z.string();
export type approveGameAnalysisResponse403 = z.infer<
	typeof approveGameAnalysisResponse403
>;

const approveGameAnalysisResponse404 = z.string();
export type approveGameAnalysisResponse404 = z.infer<
	typeof approveGameAnalysisResponse404
>;

const denyGameAnalysisResponse200 = z.string();
export type denyGameAnalysisResponse200 = z.infer<
	typeof denyGameAnalysisResponse200
>;

const denyGameAnalysisResponse400 = z.string();
export type denyGameAnalysisResponse400 = z.infer<
	typeof denyGameAnalysisResponse400
>;

const denyGameAnalysisResponse403 = z.string();
export type denyGameAnalysisResponse403 = z.infer<
	typeof denyGameAnalysisResponse403
>;

const denyGameAnalysisResponse404 = z.string();
export type denyGameAnalysisResponse404 = z.infer<
	typeof denyGameAnalysisResponse404
>;

const postDiscoverGameHistoryNoteResponse201 = z.unknown();
export type postDiscoverGameHistoryNoteResponse201 = z.infer<
	typeof postDiscoverGameHistoryNoteResponse201
>;

const postDiscoverGameLinkResponse201 = z.unknown();
export type postDiscoverGameLinkResponse201 = z.infer<
	typeof postDiscoverGameLinkResponse201
>;

const getDiscoverPlayersDataResponse400 = z.string();
export type getDiscoverPlayersDataResponse400 = z.infer<
	typeof getDiscoverPlayersDataResponse400
>;

const getDiscoverPlayersDataResponse403 = z.string();
export type getDiscoverPlayersDataResponse403 = z.infer<
	typeof getDiscoverPlayersDataResponse403
>;

const getDiscoverPlayerDataResponse400 = z.string();
export type getDiscoverPlayerDataResponse400 = z.infer<
	typeof getDiscoverPlayerDataResponse400
>;

const getDiscoverPlayerDataResponse403 = z.string();
export type getDiscoverPlayerDataResponse403 = z.infer<
	typeof getDiscoverPlayerDataResponse403
>;

const getDiscoverPlayerDataResponse404 = z.string();
export type getDiscoverPlayerDataResponse404 = z.infer<
	typeof getDiscoverPlayerDataResponse404
>;

const patchDiscoverPlayerDataResponse200 = z.string();
export type patchDiscoverPlayerDataResponse200 = z.infer<
	typeof patchDiscoverPlayerDataResponse200
>;

const patchDiscoverPlayerDataResponse400 = z.string();
export type patchDiscoverPlayerDataResponse400 = z.infer<
	typeof patchDiscoverPlayerDataResponse400
>;

const patchDiscoverPlayerDataResponse403 = z.string();
export type patchDiscoverPlayerDataResponse403 = z.infer<
	typeof patchDiscoverPlayerDataResponse403
>;

const patchDiscoverPlayerDataResponse404 = z.string();
export type patchDiscoverPlayerDataResponse404 = z.infer<
	typeof patchDiscoverPlayerDataResponse404
>;

const postDiscoverPlayerDataUpdateEtlResponse200 = z.object({
	gamesQueued: z
		.number()
		.int()
		.describe("Distinct discover games queued for data search ETL"),
});
export type postDiscoverPlayerDataUpdateEtlResponse200 = z.infer<
	typeof postDiscoverPlayerDataUpdateEtlResponse200
>;

const postDiscoverPlayerDataUpdateEtlResponse400 = z.string();
export type postDiscoverPlayerDataUpdateEtlResponse400 = z.infer<
	typeof postDiscoverPlayerDataUpdateEtlResponse400
>;

const postDiscoverPlayerDataUpdateEtlResponse403 = z.string();
export type postDiscoverPlayerDataUpdateEtlResponse403 = z.infer<
	typeof postDiscoverPlayerDataUpdateEtlResponse403
>;

const postDiscoverPlayerDataUpdateEtlResponse404 = z.string();
export type postDiscoverPlayerDataUpdateEtlResponse404 = z.infer<
	typeof postDiscoverPlayerDataUpdateEtlResponse404
>;

const getDiscoverPlayersResponse400 = z.string();
export type getDiscoverPlayersResponse400 = z.infer<
	typeof getDiscoverPlayersResponse400
>;

const getDiscoverPlayersResponse403 = z.string();
export type getDiscoverPlayersResponse403 = z.infer<
	typeof getDiscoverPlayersResponse403
>;

const getDiscoverPlayerResponse400 = z.string();
export type getDiscoverPlayerResponse400 = z.infer<
	typeof getDiscoverPlayerResponse400
>;

const getDiscoverPlayerResponse403 = z.string();
export type getDiscoverPlayerResponse403 = z.infer<
	typeof getDiscoverPlayerResponse403
>;

const getDiscoverPlayerResponse404 = z.string();
export type getDiscoverPlayerResponse404 = z.infer<
	typeof getDiscoverPlayerResponse404
>;

const patchDiscoverPlayerResponse200 = z.string();
export type patchDiscoverPlayerResponse200 = z.infer<
	typeof patchDiscoverPlayerResponse200
>;

const patchDiscoverPlayerResponse400 = z.string();
export type patchDiscoverPlayerResponse400 = z.infer<
	typeof patchDiscoverPlayerResponse400
>;

const patchDiscoverPlayerResponse403 = z.string();
export type patchDiscoverPlayerResponse403 = z.infer<
	typeof patchDiscoverPlayerResponse403
>;

const patchDiscoverPlayerResponse404 = z.string();
export type patchDiscoverPlayerResponse404 = z.infer<
	typeof patchDiscoverPlayerResponse404
>;

const getDiscoverSignUpsResponse400 = z.string();
export type getDiscoverSignUpsResponse400 = z.infer<
	typeof getDiscoverSignUpsResponse400
>;

const getDiscoverSignUpsResponse403 = z.string();
export type getDiscoverSignUpsResponse403 = z.infer<
	typeof getDiscoverSignUpsResponse403
>;

const getDiscoverSignUpResponse400 = z.string();
export type getDiscoverSignUpResponse400 = z.infer<
	typeof getDiscoverSignUpResponse400
>;

const getDiscoverSignUpResponse403 = z.string();
export type getDiscoverSignUpResponse403 = z.infer<
	typeof getDiscoverSignUpResponse403
>;

const getDiscoverSignUpResponse404 = z.string();
export type getDiscoverSignUpResponse404 = z.infer<
	typeof getDiscoverSignUpResponse404
>;

const patchDiscoverSignUpResponse200 = z.string();
export type patchDiscoverSignUpResponse200 = z.infer<
	typeof patchDiscoverSignUpResponse200
>;

const patchDiscoverSignUpResponse400 = z.string();
export type patchDiscoverSignUpResponse400 = z.infer<
	typeof patchDiscoverSignUpResponse400
>;

const patchDiscoverSignUpResponse403 = z.string();
export type patchDiscoverSignUpResponse403 = z.infer<
	typeof patchDiscoverSignUpResponse403
>;

const patchDiscoverSignUpResponse404 = z.string();
export type patchDiscoverSignUpResponse404 = z.infer<
	typeof patchDiscoverSignUpResponse404
>;

const adminDiscoverSubscriptionProductsGetResponse403 = z.string();
export type adminDiscoverSubscriptionProductsGetResponse403 = z.infer<
	typeof adminDiscoverSubscriptionProductsGetResponse403
>;

const adminDiscoverSubscriptionsByTeamGetResponse400 = z.string();
export type adminDiscoverSubscriptionsByTeamGetResponse400 = z.infer<
	typeof adminDiscoverSubscriptionsByTeamGetResponse400
>;

const adminDiscoverSubscriptionsByTeamGetResponse403 = z.string();
export type adminDiscoverSubscriptionsByTeamGetResponse403 = z.infer<
	typeof adminDiscoverSubscriptionsByTeamGetResponse403
>;

const adminDiscoverSubscriptionsByTeamGetResponse404 = z.string();
export type adminDiscoverSubscriptionsByTeamGetResponse404 = z.infer<
	typeof adminDiscoverSubscriptionsByTeamGetResponse404
>;

const adminDiscoverSubscriptionsByClubGetResponse400 = z.string();
export type adminDiscoverSubscriptionsByClubGetResponse400 = z.infer<
	typeof adminDiscoverSubscriptionsByClubGetResponse400
>;

const adminDiscoverSubscriptionsByClubGetResponse403 = z.string();
export type adminDiscoverSubscriptionsByClubGetResponse403 = z.infer<
	typeof adminDiscoverSubscriptionsByClubGetResponse403
>;

const adminDiscoverSubscriptionsByClubGetResponse404 = z.string();
export type adminDiscoverSubscriptionsByClubGetResponse404 = z.infer<
	typeof adminDiscoverSubscriptionsByClubGetResponse404
>;

const getDiscoverTeamsResponse400 = z.string();
export type getDiscoverTeamsResponse400 = z.infer<
	typeof getDiscoverTeamsResponse400
>;

const getDiscoverTeamsResponse403 = z.string();
export type getDiscoverTeamsResponse403 = z.infer<
	typeof getDiscoverTeamsResponse403
>;

const getDiscoverTeamResponse400 = z.string();
export type getDiscoverTeamResponse400 = z.infer<
	typeof getDiscoverTeamResponse400
>;

const getDiscoverTeamResponse403 = z.string();
export type getDiscoverTeamResponse403 = z.infer<
	typeof getDiscoverTeamResponse403
>;

const getDiscoverTeamResponse404 = z.string();
export type getDiscoverTeamResponse404 = z.infer<
	typeof getDiscoverTeamResponse404
>;

const patchDiscoverTeamResponse200 = z.string();
export type patchDiscoverTeamResponse200 = z.infer<
	typeof patchDiscoverTeamResponse200
>;

const patchDiscoverTeamResponse400 = z.string();
export type patchDiscoverTeamResponse400 = z.infer<
	typeof patchDiscoverTeamResponse400
>;

const patchDiscoverTeamResponse403 = z.string();
export type patchDiscoverTeamResponse403 = z.infer<
	typeof patchDiscoverTeamResponse403
>;

const patchDiscoverTeamResponse404 = z.string();
export type patchDiscoverTeamResponse404 = z.infer<
	typeof patchDiscoverTeamResponse404
>;

const getDiscoverUserGuardianApprovalsResponse400 = z.string();
export type getDiscoverUserGuardianApprovalsResponse400 = z.infer<
	typeof getDiscoverUserGuardianApprovalsResponse400
>;

const getDiscoverUserGuardianApprovalsResponse403 = z.string();
export type getDiscoverUserGuardianApprovalsResponse403 = z.infer<
	typeof getDiscoverUserGuardianApprovalsResponse403
>;

const postAdminDiscoverUserNotesResponse201 = z.unknown();
export type postAdminDiscoverUserNotesResponse201 = z.infer<
	typeof postAdminDiscoverUserNotesResponse201
>;

const postAdminDiscoverUserNotesResponse400 = z.string();
export type postAdminDiscoverUserNotesResponse400 = z.infer<
	typeof postAdminDiscoverUserNotesResponse400
>;

const postAdminDiscoverUserNotesResponse403 = z.string();
export type postAdminDiscoverUserNotesResponse403 = z.infer<
	typeof postAdminDiscoverUserNotesResponse403
>;

const postAdminDiscoverUserNotesResponse404 = z.string();
export type postAdminDiscoverUserNotesResponse404 = z.infer<
	typeof postAdminDiscoverUserNotesResponse404
>;

const deleteAdminDiscoverUserNoteResponse400 = z.string();
export type deleteAdminDiscoverUserNoteResponse400 = z.infer<
	typeof deleteAdminDiscoverUserNoteResponse400
>;

const deleteAdminDiscoverUserNoteResponse403 = z.string();
export type deleteAdminDiscoverUserNoteResponse403 = z.infer<
	typeof deleteAdminDiscoverUserNoteResponse403
>;

const deleteAdminDiscoverUserNoteResponse404 = z.string();
export type deleteAdminDiscoverUserNoteResponse404 = z.infer<
	typeof deleteAdminDiscoverUserNoteResponse404
>;

const patchAdminDiscoverUserNoteResponse200 = z.string();
export type patchAdminDiscoverUserNoteResponse200 = z.infer<
	typeof patchAdminDiscoverUserNoteResponse200
>;

const patchAdminDiscoverUserNoteResponse400 = z.string();
export type patchAdminDiscoverUserNoteResponse400 = z.infer<
	typeof patchAdminDiscoverUserNoteResponse400
>;

const patchAdminDiscoverUserNoteResponse403 = z.string();
export type patchAdminDiscoverUserNoteResponse403 = z.infer<
	typeof patchAdminDiscoverUserNoteResponse403
>;

const patchAdminDiscoverUserNoteResponse404 = z.string();
export type patchAdminDiscoverUserNoteResponse404 = z.infer<
	typeof patchAdminDiscoverUserNoteResponse404
>;

const postDiscoverUserPermissionResponse200 = z.string();
export type postDiscoverUserPermissionResponse200 = z.infer<
	typeof postDiscoverUserPermissionResponse200
>;

const postDiscoverUserPermissionResponse400 = z.string();
export type postDiscoverUserPermissionResponse400 = z.infer<
	typeof postDiscoverUserPermissionResponse400
>;

const postDiscoverUserPermissionResponse403 = z.string();
export type postDiscoverUserPermissionResponse403 = z.infer<
	typeof postDiscoverUserPermissionResponse403
>;

const deleteDiscoverUserPermissionResponse200 = z.string();
export type deleteDiscoverUserPermissionResponse200 = z.infer<
	typeof deleteDiscoverUserPermissionResponse200
>;

const deleteDiscoverUserPermissionResponse400 = z.string();
export type deleteDiscoverUserPermissionResponse400 = z.infer<
	typeof deleteDiscoverUserPermissionResponse400
>;

const deleteDiscoverUserPermissionResponse403 = z.string();
export type deleteDiscoverUserPermissionResponse403 = z.infer<
	typeof deleteDiscoverUserPermissionResponse403
>;

const getDiscoverUsersResponse400 = z.string();
export type getDiscoverUsersResponse400 = z.infer<
	typeof getDiscoverUsersResponse400
>;

const getDiscoverUsersResponse403 = z.string();
export type getDiscoverUsersResponse403 = z.infer<
	typeof getDiscoverUsersResponse403
>;

const getDiscoverUserResponse400 = z.string();
export type getDiscoverUserResponse400 = z.infer<
	typeof getDiscoverUserResponse400
>;

const getDiscoverUserResponse403 = z.string();
export type getDiscoverUserResponse403 = z.infer<
	typeof getDiscoverUserResponse403
>;

const getDiscoverUserResponse404 = z.string();
export type getDiscoverUserResponse404 = z.infer<
	typeof getDiscoverUserResponse404
>;

const patchDiscoverUserResponse200 = z.string();
export type patchDiscoverUserResponse200 = z.infer<
	typeof patchDiscoverUserResponse200
>;

const patchDiscoverUserResponse400 = z.string();
export type patchDiscoverUserResponse400 = z.infer<
	typeof patchDiscoverUserResponse400
>;

const patchDiscoverUserResponse403 = z.string();
export type patchDiscoverUserResponse403 = z.infer<
	typeof patchDiscoverUserResponse403
>;

const patchDiscoverUserResponse404 = z.string();
export type patchDiscoverUserResponse404 = z.infer<
	typeof patchDiscoverUserResponse404
>;

const postDiscoverUserImpersonateResponse200 = z.object({ token: z.string() });
export type postDiscoverUserImpersonateResponse200 = z.infer<
	typeof postDiscoverUserImpersonateResponse200
>;

const postDiscoverUserImpersonateResponse403 = z.string();
export type postDiscoverUserImpersonateResponse403 = z.infer<
	typeof postDiscoverUserImpersonateResponse403
>;

const postDiscoverUserImpersonateResponse404 = z.string();
export type postDiscoverUserImpersonateResponse404 = z.infer<
	typeof postDiscoverUserImpersonateResponse404
>;

const rejectGameAnalysisResponse200 = z.string();
export type rejectGameAnalysisResponse200 = z.infer<
	typeof rejectGameAnalysisResponse200
>;

const rejectGameAnalysisResponse400 = z.string();
export type rejectGameAnalysisResponse400 = z.infer<
	typeof rejectGameAnalysisResponse400
>;

const rejectGameAnalysisResponse403 = z.string();
export type rejectGameAnalysisResponse403 = z.infer<
	typeof rejectGameAnalysisResponse403
>;

const rejectGameAnalysisResponse404 = z.string();
export type rejectGameAnalysisResponse404 = z.infer<
	typeof rejectGameAnalysisResponse404
>;

const createGameAnalysisResponse201 = z.unknown();
export type createGameAnalysisResponse201 = z.infer<
	typeof createGameAnalysisResponse201
>;

const createGameAnalysisResponse400 = z.string();
export type createGameAnalysisResponse400 = z.infer<
	typeof createGameAnalysisResponse400
>;

const createGameAnalysisResponse403 = z.string();
export type createGameAnalysisResponse403 = z.infer<
	typeof createGameAnalysisResponse403
>;

const createGameAnalysisResponse404 = z.string();
export type createGameAnalysisResponse404 = z.infer<
	typeof createGameAnalysisResponse404
>;

const swapGameHomeAwayMappingResponse200 = z.string();
export type swapGameHomeAwayMappingResponse200 = z.infer<
	typeof swapGameHomeAwayMappingResponse200
>;

const swapGameHomeAwayMappingResponse400 = z.string();
export type swapGameHomeAwayMappingResponse400 = z.infer<
	typeof swapGameHomeAwayMappingResponse400
>;

const swapGameHomeAwayMappingResponse403 = z.string();
export type swapGameHomeAwayMappingResponse403 = z.infer<
	typeof swapGameHomeAwayMappingResponse403
>;

const swapGameHomeAwayMappingResponse404 = z.string();
export type swapGameHomeAwayMappingResponse404 = z.infer<
	typeof swapGameHomeAwayMappingResponse404
>;

const getGameHomeAwayMappingSwapEligibilityResponse403 = z.string();
export type getGameHomeAwayMappingSwapEligibilityResponse403 = z.infer<
	typeof getGameHomeAwayMappingSwapEligibilityResponse403
>;

const getGameHomeAwayMappingSwapEligibilityResponse404 = z.string();
export type getGameHomeAwayMappingSwapEligibilityResponse404 = z.infer<
	typeof getGameHomeAwayMappingSwapEligibilityResponse404
>;

const getOperationsManagementSummaryByCountryResponse400 = z.string();
export type getOperationsManagementSummaryByCountryResponse400 = z.infer<
	typeof getOperationsManagementSummaryByCountryResponse400
>;

const getOperationsManagementSummaryByCountryResponse403 = z.string();
export type getOperationsManagementSummaryByCountryResponse403 = z.infer<
	typeof getOperationsManagementSummaryByCountryResponse403
>;

const getOperationsManagementSummaryByCountryFilterOptionsResponse200 = z.array(
	z.object({ value: z.string(), label: z.string() }).partial(),
);
export type getOperationsManagementSummaryByCountryFilterOptionsResponse200 =
	z.infer<
		typeof getOperationsManagementSummaryByCountryFilterOptionsResponse200
	>;

const getOperationsManagementSummaryByCountryFilterOptionsResponse400 =
	z.string();
export type getOperationsManagementSummaryByCountryFilterOptionsResponse400 =
	z.infer<
		typeof getOperationsManagementSummaryByCountryFilterOptionsResponse400
	>;

const getOperationsManagementSummaryByCountryFilterOptionsResponse403 =
	z.string();
export type getOperationsManagementSummaryByCountryFilterOptionsResponse403 =
	z.infer<
		typeof getOperationsManagementSummaryByCountryFilterOptionsResponse403
	>;

const getOperationsManagementSummaryByClubResponse400 = z.string();
export type getOperationsManagementSummaryByClubResponse400 = z.infer<
	typeof getOperationsManagementSummaryByClubResponse400
>;

const getOperationsManagementSummaryByClubResponse403 = z.string();
export type getOperationsManagementSummaryByClubResponse403 = z.infer<
	typeof getOperationsManagementSummaryByClubResponse403
>;

const getOperationsManagementSummaryByClubFilterOptionsResponse200 = z.array(
	z.object({ value: z.string(), label: z.string() }).partial(),
);
export type getOperationsManagementSummaryByClubFilterOptionsResponse200 =
	z.infer<typeof getOperationsManagementSummaryByClubFilterOptionsResponse200>;

const getOperationsManagementSummaryByClubFilterOptionsResponse400 = z.string();
export type getOperationsManagementSummaryByClubFilterOptionsResponse400 =
	z.infer<typeof getOperationsManagementSummaryByClubFilterOptionsResponse400>;

const getOperationsManagementSummaryByClubFilterOptionsResponse403 = z.string();
export type getOperationsManagementSummaryByClubFilterOptionsResponse403 =
	z.infer<typeof getOperationsManagementSummaryByClubFilterOptionsResponse403>;

const patchPlayerMembershipResponse400 = z.string();
export type patchPlayerMembershipResponse400 = z.infer<
	typeof patchPlayerMembershipResponse400
>;

const patchPlayerMembershipResponse403 = z.string();
export type patchPlayerMembershipResponse403 = z.infer<
	typeof patchPlayerMembershipResponse403
>;

const patchPlayerMembershipResponse404 = z.string();
export type patchPlayerMembershipResponse404 = z.infer<
	typeof patchPlayerMembershipResponse404
>;

const getCurrentUserResponse401 = z
	.object({ message: z.string().describe("Error message") })
	.partial();
export type getCurrentUserResponse401 = z.infer<
	typeof getCurrentUserResponse401
>;

const getCurrentUserResponse403 = z.array(
	z
		.object({
			status: z.string().describe("EventStatus"),
			comment: z.string().describe("Change Status Comment"),
		})
		.partial(),
);
export type getCurrentUserResponse403 = z.infer<
	typeof getCurrentUserResponse403
>;

const adminJsonLogoutResponse200 = z
	.object({ message: z.string().describe("message") })
	.partial();
export type adminJsonLogoutResponse200 = z.infer<
	typeof adminJsonLogoutResponse200
>;

const adminJsonLogoutResponse401 = z
	.object({ message: z.string().describe("Error message") })
	.partial();
export type adminJsonLogoutResponse401 = z.infer<
	typeof adminJsonLogoutResponse401
>;

const setUserLocaleResponse200 = z.object({ locale: z.string() }).partial();
export type setUserLocaleResponse200 = z.infer<typeof setUserLocaleResponse200>;

const forgotPasswordResponse200 = z.unknown();
export type forgotPasswordResponse200 = z.infer<
	typeof forgotPasswordResponse200
>;

const forgotPasswordResponse404 = z.unknown();
export type forgotPasswordResponse404 = z.infer<
	typeof forgotPasswordResponse404
>;

const resetPasswordResponse200 = z.unknown();
export type resetPasswordResponse200 = z.infer<typeof resetPasswordResponse200>;

const resetPasswordResponse404 = z.unknown();
export type resetPasswordResponse404 = z.infer<typeof resetPasswordResponse404>;

const validateUserPasswordResetResponse200 = z.unknown();
export type validateUserPasswordResetResponse200 = z.infer<
	typeof validateUserPasswordResetResponse200
>;

const validateUserPasswordResetResponse403 = z.unknown();
export type validateUserPasswordResetResponse403 = z.infer<
	typeof validateUserPasswordResetResponse403
>;

const searchNationalLeaguesResponse200 = z.array(SearchLeagueResponse);
export type searchNationalLeaguesResponse200 = z.infer<
	typeof searchNationalLeaguesResponse200
>;

const getPermissionTreeResponse200 = z.array(PermissionTreeResponse);
export type getPermissionTreeResponse200 = z.infer<
	typeof getPermissionTreeResponse200
>;

const getPermissionTreeResponse403 = z.unknown();
export type getPermissionTreeResponse403 = z.infer<
	typeof getPermissionTreeResponse403
>;

const getPermissionByRoleResponse200 = z
	.object({ data: z.array(z.number().int().describe("ID of the object")) })
	.partial();
export type getPermissionByRoleResponse200 = z.infer<
	typeof getPermissionByRoleResponse200
>;

const getPermissionByRoleResponse403 = z.unknown();
export type getPermissionByRoleResponse403 = z.infer<
	typeof getPermissionByRoleResponse403
>;

const assignPermissionToRoleResponse200 = z
	.object({
		status: z.string().describe("status"),
		message: z.string().describe("message"),
	})
	.partial();
export type assignPermissionToRoleResponse200 = z.infer<
	typeof assignPermissionToRoleResponse200
>;

const assignPermissionToRoleResponse403 = z.unknown();
export type assignPermissionToRoleResponse403 = z.infer<
	typeof assignPermissionToRoleResponse403
>;

const deleteRoleResponse200 = z
	.object({
		status: z.string().describe("status"),
		message: z.string().describe("message"),
		data: z.array(RolesData),
	})
	.partial();
export type deleteRoleResponse200 = z.infer<typeof deleteRoleResponse200>;

const deleteRoleResponse403 = z.unknown();
export type deleteRoleResponse403 = z.infer<typeof deleteRoleResponse403>;

const getRolesResponse200 = z.array(RolesData);
export type getRolesResponse200 = z.infer<typeof getRolesResponse200>;

const getRolesResponse403 = z.unknown();
export type getRolesResponse403 = z.infer<typeof getRolesResponse403>;

const getUserRolesResponse200 = z.object({ data: RolesData }).partial();
export type getUserRolesResponse200 = z.infer<typeof getUserRolesResponse200>;

const getUserRolesResponse403 = z.unknown();
export type getUserRolesResponse403 = z.infer<typeof getUserRolesResponse403>;

const addUpdateRoleResponse200 = z
	.object({
		status: z.string().describe("status"),
		message: z.string().describe("message"),
		data: z.array(RolesData),
	})
	.partial();
export type addUpdateRoleResponse200 = z.infer<typeof addUpdateRoleResponse200>;

const addUpdateRoleResponse403 = z.unknown();
export type addUpdateRoleResponse403 = z.infer<typeof addUpdateRoleResponse403>;

const getAllAdminUsersResponse200 = z
	.object({
		status: z.string(),
		data: z.array(AdminUserData),
		recordsFiltered: z.number().int(),
		recordsTotal: z.number().int(),
	})
	.partial();
export type getAllAdminUsersResponse200 = z.infer<
	typeof getAllAdminUsersResponse200
>;

const getAllAdminUsersResponse403 = z.unknown();
export type getAllAdminUsersResponse403 = z.infer<
	typeof getAllAdminUsersResponse403
>;

const addUpdateAdminUsersResponse200 = z
	.object({
		status: z.string().describe("status"),
		message: z.string().describe("message"),
	})
	.partial();
export type addUpdateAdminUsersResponse200 = z.infer<
	typeof addUpdateAdminUsersResponse200
>;

const addUpdateAdminUsersResponse403 = z.unknown();
export type addUpdateAdminUsersResponse403 = z.infer<
	typeof addUpdateAdminUsersResponse403
>;

const deleteAdminUserResponse200 = z
	.object({
		status: z.string().describe("DeleteStatus"),
		message: z.string().describe("Change Status Message"),
	})
	.partial();
export type deleteAdminUserResponse200 = z.infer<
	typeof deleteAdminUserResponse200
>;

const deleteAdminUserResponse400 = z.unknown();
export type deleteAdminUserResponse400 = z.infer<
	typeof deleteAdminUserResponse400
>;

const deleteAdminUserResponse403 = z.unknown();
export type deleteAdminUserResponse403 = z.infer<
	typeof deleteAdminUserResponse403
>;

const uploadProfilePhotoResponse200 = z
	.object({
		status: z.string().describe("status"),
		message: z.string().describe("message"),
		url: z.string().describe("message"),
	})
	.partial();
export type uploadProfilePhotoResponse200 = z.infer<
	typeof uploadProfilePhotoResponse200
>;

const uploadProfilePhotoResponse403 = z.unknown();
export type uploadProfilePhotoResponse403 = z.infer<
	typeof uploadProfilePhotoResponse403
>;

const getAllPolesResponse200 = z
	.object({
		status: z.string(),
		data: z.array(AllPoleData),
		recordsFiltered: z.number().int(),
		recordsTotal: z.number().int(),
	})
	.partial();
export type getAllPolesResponse200 = z.infer<typeof getAllPolesResponse200>;

const getAllPolesResponse403 = z.unknown();
export type getAllPolesResponse403 = z.infer<typeof getAllPolesResponse403>;

const addUpdatePoleResponse200 = z
	.object({
		data: PoleData,
		status: z.string().describe("status"),
		message: z.string().describe("message"),
	})
	.partial();
export type addUpdatePoleResponse200 = z.infer<typeof addUpdatePoleResponse200>;

const addUpdatePoleResponse403 = z.unknown();
export type addUpdatePoleResponse403 = z.infer<typeof addUpdatePoleResponse403>;

const getTimezonesResponse200 = z.array(z.string());
export type getTimezonesResponse200 = z.infer<typeof getTimezonesResponse200>;

const deletePoleResponse200 = z
	.object({
		status: z.string().describe("status"),
		message: z.string().describe("message"),
		sync: z.boolean().describe("sync"),
	})
	.partial();
export type deletePoleResponse200 = z.infer<typeof deletePoleResponse200>;

const deletePoleResponse400 = z.unknown();
export type deletePoleResponse400 = z.infer<typeof deletePoleResponse400>;

const deletePoleResponse403 = z.unknown();
export type deletePoleResponse403 = z.infer<typeof deletePoleResponse403>;

const getSelectedPoleByClubResponse200 = z.array(DefaultResponseOptions);
export type getSelectedPoleByClubResponse200 = z.infer<
	typeof getSelectedPoleByClubResponse200
>;

const getSelectedPoleByClubResponse400 = z.unknown();
export type getSelectedPoleByClubResponse400 = z.infer<
	typeof getSelectedPoleByClubResponse400
>;

const assignPoleResponse200 = z
	.object({
		data: PoleData.describe("pole data").nullable(),
		message: z.string().describe("Pole Assignment Message"),
		status: z.string().describe("success"),
		sync: z.boolean().describe("sync status").nullable(),
	})
	.partial();
export type assignPoleResponse200 = z.infer<typeof assignPoleResponse200>;

const assignPoleResponse403 = z.unknown();
export type assignPoleResponse403 = z.infer<typeof assignPoleResponse403>;

const unAssignPoleResponse200 = z
	.object({
		data: PoleData,
		message: z.string().describe("Pole Un Assignment Message"),
		status: z.string().describe("success"),
		sync: z.boolean().describe("sync status").nullable(),
	})
	.partial();
export type unAssignPoleResponse200 = z.infer<typeof unAssignPoleResponse200>;

const unAssignPoleResponse403 = z.unknown();
export type unAssignPoleResponse403 = z.infer<typeof unAssignPoleResponse403>;

const syncPoleResponse200 = z
	.object({
		message: z.string().describe("pole synced Message"),
		status: z.string().describe("success"),
		sync: z.boolean().describe("sync status").nullable(),
	})
	.partial();
export type syncPoleResponse200 = z.infer<typeof syncPoleResponse200>;

const syncPoleResponse400 = z.unknown();
export type syncPoleResponse400 = z.infer<typeof syncPoleResponse400>;

const getClubListResponse200 = z.array(FilterData);
export type getClubListResponse200 = z.infer<typeof getClubListResponse200>;

const getClubListResponse403 = z.unknown();
export type getClubListResponse403 = z.infer<typeof getClubListResponse403>;

const getCountriesResponse200 = z.unknown();
export type getCountriesResponse200 = z.infer<typeof getCountriesResponse200>;

const getCountriesResponse403 = z.unknown();
export type getCountriesResponse403 = z.infer<typeof getCountriesResponse403>;

const getContinentCountryResponse200 = z.array(ContinentCountryResponse);
export type getContinentCountryResponse200 = z.infer<
	typeof getContinentCountryResponse200
>;

const getContinentCountryResponse403 = z.unknown();
export type getContinentCountryResponse403 = z.infer<
	typeof getContinentCountryResponse403
>;

const getNPClubsResponse200 = z
	.object({
		status: z.string(),
		data: z.array(ClubData),
		recordsFiltered: z.number().int(),
		recordsTotal: z.number().int(),
	})
	.partial();
export type getNPClubsResponse200 = z.infer<typeof getNPClubsResponse200>;

const getNPClubsResponse403 = z.unknown();
export type getNPClubsResponse403 = z.infer<typeof getNPClubsResponse403>;

const addUpdateNPClubResponse200 = z.object({
	data: ClubData.optional(),
	status: z.string().describe("status"),
	message: z.string().describe("message"),
});
export type addUpdateNPClubResponse200 = z.infer<
	typeof addUpdateNPClubResponse200
>;

const addUpdateNPClubResponse403 = z.unknown();
export type addUpdateNPClubResponse403 = z.infer<
	typeof addUpdateNPClubResponse403
>;

const deleteNPClubResponse200 = z
	.object({
		status: z.string().describe("status"),
		message: z.string().describe("message"),
	})
	.partial();
export type deleteNPClubResponse200 = z.infer<typeof deleteNPClubResponse200>;

const deleteNPClubResponse403 = z.unknown();
export type deleteNPClubResponse403 = z.infer<typeof deleteNPClubResponse403>;

const uploadNPClubPhotoResponse200 = z
	.object({
		status: z.string().describe("status"),
		message: z.string().describe("message"),
		url: z.string().describe("message"),
	})
	.partial();
export type uploadNPClubPhotoResponse200 = z.infer<
	typeof uploadNPClubPhotoResponse200
>;

const uploadNPClubPhotoResponse403 = z.unknown();
export type uploadNPClubPhotoResponse403 = z.infer<
	typeof uploadNPClubPhotoResponse403
>;

const uploadNPPlayerPictureResponse200 = z
	.object({
		status: z.string().describe("status"),
		message: z.string().describe("message"),
		url: z.string().describe("message"),
	})
	.partial();
export type uploadNPPlayerPictureResponse200 = z.infer<
	typeof uploadNPPlayerPictureResponse200
>;

const uploadNPPlayerPictureResponse403 = z.unknown();
export type uploadNPPlayerPictureResponse403 = z.infer<
	typeof uploadNPPlayerPictureResponse403
>;

const getNPPlayersResponse200 = z
	.object({
		status: z.string(),
		data: z.array(PlayerData),
		recordsFiltered: z.number().int(),
		recordsTotal: z.number().int(),
	})
	.partial();
export type getNPPlayersResponse200 = z.infer<typeof getNPPlayersResponse200>;

const getNPPlayersResponse403 = z.unknown();
export type getNPPlayersResponse403 = z.infer<typeof getNPPlayersResponse403>;

const getNationalityListResponse200 = z.array(DefaultResponseOptions);
export type getNationalityListResponse200 = z.infer<
	typeof getNationalityListResponse200
>;

const getNationalityListResponse403 = z.unknown();
export type getNationalityListResponse403 = z.infer<
	typeof getNationalityListResponse403
>;

const getPositionListResponse200 = z.array(DefaultResponseOptions);
export type getPositionListResponse200 = z.infer<
	typeof getPositionListResponse200
>;

const getPositionListResponse403 = z.unknown();
export type getPositionListResponse403 = z.infer<
	typeof getPositionListResponse403
>;

const getGeographicalAreaListResponse200 = z.array(DefaultResponseOptions);
export type getGeographicalAreaListResponse200 = z.infer<
	typeof getGeographicalAreaListResponse200
>;

const getGeographicalAreaListResponse403 = z.unknown();
export type getGeographicalAreaListResponse403 = z.infer<
	typeof getGeographicalAreaListResponse403
>;

const addUpdateNPPlayerResponse200 = z
	.object({
		data: ClubData,
		status: z.string().describe("status"),
		message: z.string().describe("message"),
	})
	.partial();
export type addUpdateNPPlayerResponse200 = z.infer<
	typeof addUpdateNPPlayerResponse200
>;

const addUpdateNPPlayerResponse403 = z.unknown();
export type addUpdateNPPlayerResponse403 = z.infer<
	typeof addUpdateNPPlayerResponse403
>;

const deleteNPPlayerResponse200 = z
	.object({
		status: z.string().describe("status"),
		message: z.string().describe("message"),
	})
	.partial();
export type deleteNPPlayerResponse200 = z.infer<
	typeof deleteNPPlayerResponse200
>;

const deleteNPPlayerResponse403 = z.unknown();
export type deleteNPPlayerResponse403 = z.infer<
	typeof deleteNPPlayerResponse403
>;

const getAllNPClubsResponse200 = z.array(ClubOptionData);
export type getAllNPClubsResponse200 = z.infer<typeof getAllNPClubsResponse200>;

const getAllNPClubsResponse403 = z.unknown();
export type getAllNPClubsResponse403 = z.infer<typeof getAllNPClubsResponse403>;

const getClubPlayersResponse200 = z.array(ClubPlayers);
export type getClubPlayersResponse200 = z.infer<
	typeof getClubPlayersResponse200
>;

const executeNPClubMigrationResponse200 = z.unknown();
export type executeNPClubMigrationResponse200 = z.infer<
	typeof executeNPClubMigrationResponse200
>;

const executeNPClubMigrationResponse403 = z.unknown();
export type executeNPClubMigrationResponse403 = z.infer<
	typeof executeNPClubMigrationResponse403
>;

const getTeamsByTargetClubResponse200 = z.array(FilterData);
export type getTeamsByTargetClubResponse200 = z.infer<
	typeof getTeamsByTargetClubResponse200
>;

const getTeamsByTargetClubResponse403 = z.unknown();
export type getTeamsByTargetClubResponse403 = z.infer<
	typeof getTeamsByTargetClubResponse403
>;

const playerExistsTargetClubResponse200 = z
	.object({
		status: z.string(),
		percentage: z.number().int(),
		id: z.number().int(),
		targetPlayer: z.number().int().nullable(),
	})
	.partial();
export type playerExistsTargetClubResponse200 = z.infer<
	typeof playerExistsTargetClubResponse200
>;

const playerExistsTargetClubResponse403 = z.unknown();
export type playerExistsTargetClubResponse403 = z.infer<
	typeof playerExistsTargetClubResponse403
>;

const getMigrationStatusResponse200 = z
	.object({
		failed: z
			.object({
				skippedPlayersCount: z.number().int(),
				skippedPlayers: z.array(z.unknown()),
			})
			.partial(),
		success: z
			.object({
				migratedPlayersCount: z.number().int(),
				migratedPlayers: z.array(z.unknown()),
				migratedGamesCount: z.number().int(),
				migratedGames: z.array(z.unknown()),
			})
			.partial(),
	})
	.partial();
export type getMigrationStatusResponse200 = z.infer<
	typeof getMigrationStatusResponse200
>;

const getMigrationStatusResponse403 = z.unknown();
export type getMigrationStatusResponse403 = z.infer<
	typeof getMigrationStatusResponse403
>;

const executePartnerClubMigrationResponse200 = z.unknown();
export type executePartnerClubMigrationResponse200 = z.infer<
	typeof executePartnerClubMigrationResponse200
>;

const executePartnerClubMigrationResponse403 = z.unknown();
export type executePartnerClubMigrationResponse403 = z.infer<
	typeof executePartnerClubMigrationResponse403
>;

const createPartnerPlayerFromNonpartnerResponse200 = z.object({
	id: z.number().int(),
	percentage: z.number().int(),
	status: z.string(),
	targetPlayer: z
		.array(
			z.object({
				team: z.string().describe("Team name"),
				status: z.string().describe("Status"),
				firstName: z.string().describe("First Name of Target Player"),
				lastName: z.string().describe("Last Name of Target Player"),
			}),
		)
		.nullable(),
});
export type createPartnerPlayerFromNonpartnerResponse200 = z.infer<
	typeof createPartnerPlayerFromNonpartnerResponse200
>;

const partnerPlayerExistsTargetClubResponse200 = z
	.object({
		status: z.string(),
		percentage: z.number().int(),
		id: z.number().int(),
		targetPlayer: z.number().int().nullable(),
	})
	.partial();
export type partnerPlayerExistsTargetClubResponse200 = z.infer<
	typeof partnerPlayerExistsTargetClubResponse200
>;

const partnerPlayerExistsTargetClubResponse403 = z.unknown();
export type partnerPlayerExistsTargetClubResponse403 = z.infer<
	typeof partnerPlayerExistsTargetClubResponse403
>;

const getPartnerMigrationStatusResponse200 = z
	.object({
		failed: z
			.object({
				skippedPlayersCount: z.number().int(),
				skippedPlayers: z.array(z.unknown()),
			})
			.partial(),
		success: z
			.object({
				migratedPlayersCount: z.number().int(),
				migratedPlayers: z.array(z.unknown()),
				migratedGamesCount: z.number().int(),
				migratedGames: z.array(z.unknown()),
			})
			.partial(),
	})
	.partial();
export type getPartnerMigrationStatusResponse200 = z.infer<
	typeof getPartnerMigrationStatusResponse200
>;

const getPartnerMigrationStatusResponse403 = z.unknown();
export type getPartnerMigrationStatusResponse403 = z.infer<
	typeof getPartnerMigrationStatusResponse403
>;

const updateGameTeamResponse200 = z
	.object({
		status: z.string(),
		message: z.string(),
		data: z.object({}).partial(),
	})
	.partial();
export type updateGameTeamResponse200 = z.infer<
	typeof updateGameTeamResponse200
>;

const updateGameTeamResponse403 = z.unknown();
export type updateGameTeamResponse403 = z.infer<
	typeof updateGameTeamResponse403
>;

const migrateSourceClubNoPlayersResponse200 = z
	.object({
		status: z.string().describe("status"),
		message: z.string().describe("message"),
		data: z.array(z.object({}).partial()).describe("data"),
	})
	.partial();
export type migrateSourceClubNoPlayersResponse200 = z.infer<
	typeof migrateSourceClubNoPlayersResponse200
>;

const migrateSourceClubNoPlayersResponse403 = z.unknown();
export type migrateSourceClubNoPlayersResponse403 = z.infer<
	typeof migrateSourceClubNoPlayersResponse403
>;

const clubMigrationWithZeroPlayersResponse200 = z.object({
	data: z.array(MigrateZeroPlayerClub),
	status: z.string().optional(),
	message: z.string().optional(),
});
export type clubMigrationWithZeroPlayersResponse200 = z.infer<
	typeof clubMigrationWithZeroPlayersResponse200
>;

const playerMigrationToPartnerResponse200 = z.object({
	id: z.number().int(),
	percentage: z.number().int(),
	status: z.string(),
	targetPlayer: z
		.array(
			z.object({
				team: z.string().describe("Team name"),
				status: z.string().describe("Status"),
				firstName: z.string().describe("First Name of Target Player"),
				lastName: z.string().describe("Last Name of Target Player"),
			}),
		)
		.nullable(),
});
export type playerMigrationToPartnerResponse200 = z.infer<
	typeof playerMigrationToPartnerResponse200
>;

const playerMigrationConflictResponse200 = z
	.object({
		id: z.number().int().nullable(),
		percentage: z.number().int().nullable(),
		status: z.union([z.string(), z.number()]),
		message: z.string().nullable(),
		targetPlayer: z
			.array(
				z.object({
					team: z.string().describe("Team name"),
					status: z.string().describe("Status"),
					firstName: z.string().describe("First Name of Target Player"),
					lastName: z.string().describe("Last Name of Target Player"),
				}),
			)
			.nullable(),
	})
	.partial();
export type playerMigrationConflictResponse200 = z.infer<
	typeof playerMigrationConflictResponse200
>;

const getAllCountriesTooltipResponse200 = z
	.object({
		status: z.string(),
		data: z.array(AllCountryTooltipData),
		recordsFiltered: z.number().int(),
		recordsTotal: z.number().int(),
	})
	.partial();
export type getAllCountriesTooltipResponse200 = z.infer<
	typeof getAllCountriesTooltipResponse200
>;

const getAllCountriesTooltipResponse403 = z.unknown();
export type getAllCountriesTooltipResponse403 = z.infer<
	typeof getAllCountriesTooltipResponse403
>;

const addUpdateCountryResponse200 = z
	.object({
		message: z.string().describe("message"),
		status: z.string().describe("status"),
	})
	.partial();
export type addUpdateCountryResponse200 = z.infer<
	typeof addUpdateCountryResponse200
>;

const addUpdateCountryResponse403 = z.unknown();
export type addUpdateCountryResponse403 = z.infer<
	typeof addUpdateCountryResponse403
>;

const deleteCountryResponse200 = z
	.object({
		message: z.string().describe("message"),
		status: z.string().describe("status"),
	})
	.partial();
export type deleteCountryResponse200 = z.infer<typeof deleteCountryResponse200>;

const deleteCountryResponse400 = z.unknown();
export type deleteCountryResponse400 = z.infer<typeof deleteCountryResponse400>;

const deleteCountryResponse403 = z.unknown();
export type deleteCountryResponse403 = z.infer<typeof deleteCountryResponse403>;

const allCountriesResponse200 = z.unknown();
export type allCountriesResponse200 = z.infer<typeof allCountriesResponse200>;

const allCountriesResponse403 = z.unknown();
export type allCountriesResponse403 = z.infer<typeof allCountriesResponse403>;

const enableDisableCountryResponse200 = z
	.object({
		message: z.string().describe("message"),
		status: z.string().describe("status"),
		data: UpdateCountryTooltipData,
	})
	.partial();
export type enableDisableCountryResponse200 = z.infer<
	typeof enableDisableCountryResponse200
>;

const enableDisableCountryResponse403 = z.unknown();
export type enableDisableCountryResponse403 = z.infer<
	typeof enableDisableCountryResponse403
>;

const loadOwnershipsResponse200 = z
	.object({
		data: z.array(
			z.object({ label: z.string(), value: z.number().int() }).partial(),
		),
	})
	.partial();
export type loadOwnershipsResponse200 = z.infer<
	typeof loadOwnershipsResponse200
>;

const loadOwnershipsResponse403 = z.unknown();
export type loadOwnershipsResponse403 = z.infer<
	typeof loadOwnershipsResponse403
>;

const loadTeamGroupsResponse200 = z
	.object({
		data: z.array(
			z.object({ label: z.string(), value: z.number().int() }).partial(),
		),
	})
	.partial();
export type loadTeamGroupsResponse200 = z.infer<
	typeof loadTeamGroupsResponse200
>;

const loadTeamGroupsResponse403 = z.unknown();
export type loadTeamGroupsResponse403 = z.infer<
	typeof loadTeamGroupsResponse403
>;

const getSubscriptionFilterDataResponse200 = z
	.object({ clubs: z.array(FilterData), products: z.array(FilterData) })
	.partial();
export type getSubscriptionFilterDataResponse200 = z.infer<
	typeof getSubscriptionFilterDataResponse200
>;

const getSubscriptionFilterDataResponse403 = z.unknown();
export type getSubscriptionFilterDataResponse403 = z.infer<
	typeof getSubscriptionFilterDataResponse403
>;

const assignSubscriptionToClubResponse200 = z
	.object({ message: z.string(), status: z.string() })
	.partial();
export type assignSubscriptionToClubResponse200 = z.infer<
	typeof assignSubscriptionToClubResponse200
>;

const assignSubscriptionToClubResponse403 = z.unknown();
export type assignSubscriptionToClubResponse403 = z.infer<
	typeof assignSubscriptionToClubResponse403
>;

const getAllLanguagesResponse200 = z.array(DefaultResponseOptions);
export type getAllLanguagesResponse200 = z.infer<
	typeof getAllLanguagesResponse200
>;

const userSearchResponse200 = z.array(FilterData);
export type userSearchResponse200 = z.infer<typeof userSearchResponse200>;

const userSearchResponse403 = z.unknown();
export type userSearchResponse403 = z.infer<typeof userSearchResponse403>;

const clubAmateurSearchResponse200 = z.array(FilterData);
export type clubAmateurSearchResponse200 = z.infer<
	typeof clubAmateurSearchResponse200
>;

const clubAmateurSearchResponse403 = z.unknown();
export type clubAmateurSearchResponse403 = z.infer<
	typeof clubAmateurSearchResponse403
>;

const clubProSearchResponse200 = z.array(FilterData);
export type clubProSearchResponse200 = z.infer<typeof clubProSearchResponse200>;

const clubProSearchResponse403 = z.unknown();
export type clubProSearchResponse403 = z.infer<typeof clubProSearchResponse403>;

const getAllEyeballAddonsResponse200 = z.array(DefaultResponseOptions);
export type getAllEyeballAddonsResponse200 = z.infer<
	typeof getAllEyeballAddonsResponse200
>;

const getUserSessionsResponse200 = z.array(UserSessionLog);
export type getUserSessionsResponse200 = z.infer<
	typeof getUserSessionsResponse200
>;

const revokeSessionsResponse200 = z
	.object({ sessionsDeleted: z.array(z.string()) })
	.partial();
export type revokeSessionsResponse200 = z.infer<
	typeof revokeSessionsResponse200
>;

const getUserSignInsResponse200 = z.array(
	z
		.object({
			id: z.string(),
			location: z
				.object({
					city: z.string(),
					state: z.string(),
					countryOrRegion: z.string(),
				})
				.partial(),
			device: z
				.object({
					deviceId: z.string(),
					displayName: z.string(),
					operatingSystem: z.string(),
					browser: z.string(),
					isCompliant: z.boolean(),
					isManaged: z.boolean(),
					trustType: z.string(),
				})
				.partial(),
			appDisplayName: z.string(),
			createdDateTime: z.string(),
		})
		.partial(),
);
export type getUserSignInsResponse200 = z.infer<
	typeof getUserSignInsResponse200
>;

const logUserActivityResponse400 = z.object({ code: z.string() });
export type logUserActivityResponse400 = z.infer<
	typeof logUserActivityResponse400
>;

const getUserWidgetSettingsResponse200 = z.union([
	UserWidgetSettingsResponse,
	z.null(),
]);
export type getUserWidgetSettingsResponse200 = z.infer<
	typeof getUserWidgetSettingsResponse200
>;

const saveUserWidgetSettingsResponse200 = z
	.object({ insightDisplaySettings: DisplaySettingsResponse })
	.partial();
export type saveUserWidgetSettingsResponse200 = z.infer<
	typeof saveUserWidgetSettingsResponse200
>;

const getClubAllTeamsResponse400 = z
	.object({ property_path: z.string(), message: z.string() })
	.partial();
export type getClubAllTeamsResponse400 = z.infer<
	typeof getClubAllTeamsResponse400
>;

const getClubAllTeamsResponse403 = z
	.object({ status: z.number().int(), message: z.string() })
	.partial();
export type getClubAllTeamsResponse403 = z.infer<
	typeof getClubAllTeamsResponse403
>;

const getTeamByIdAndCoachIdResponse200 = z
	.object({
		status: z.number().int().describe("status"),
		data: z.array(ClubTeamInfo).describe("team details"),
	})
	.partial();
export type getTeamByIdAndCoachIdResponse200 = z.infer<
	typeof getTeamByIdAndCoachIdResponse200
>;

const getTeamByIdAndCoachIdResponse403 = z
	.object({ status: z.number().int(), message: z.string() })
	.partial();
export type getTeamByIdAndCoachIdResponse403 = z.infer<
	typeof getTeamByIdAndCoachIdResponse403
>;

const addUpdateTeamResponse200 = z
	.object({
		status: z.number().int().describe("status"),
		message: z.string().describe("message"),
	})
	.partial();
export type addUpdateTeamResponse200 = z.infer<typeof addUpdateTeamResponse200>;

const addUpdateTeamResponse400 = z
	.object({ property_path: z.string(), message: z.string() })
	.partial();
export type addUpdateTeamResponse400 = z.infer<typeof addUpdateTeamResponse400>;

const addUpdateTeamResponse403 = z
	.object({ status: z.number().int(), message: z.string() })
	.partial();
export type addUpdateTeamResponse403 = z.infer<typeof addUpdateTeamResponse403>;

const addUpdateTeamResponse409 = z.unknown();
export type addUpdateTeamResponse409 = z.infer<typeof addUpdateTeamResponse409>;

const deleteTeamResponse200 = z
	.object({
		status: z.number().int().describe("status"),
		message: z.string().describe("message"),
	})
	.partial();
export type deleteTeamResponse200 = z.infer<typeof deleteTeamResponse200>;

const deleteTeamResponse403 = z
	.object({ status: z.number().int(), message: z.string() })
	.partial();
export type deleteTeamResponse403 = z.infer<typeof deleteTeamResponse403>;

const deleteTeamResponse404 = z.unknown();
export type deleteTeamResponse404 = z.infer<typeof deleteTeamResponse404>;

const clubCoachesResponse200 = z.array(DefaultSelectResponse);
export type clubCoachesResponse200 = z.infer<typeof clubCoachesResponse200>;

const clubCoachesResponse403 = z
	.object({ status: z.number().int(), message: z.string() })
	.partial();
export type clubCoachesResponse403 = z.infer<typeof clubCoachesResponse403>;

const getClubTeamsListResponse200 = z.array(DefaultSelectResponse);
export type getClubTeamsListResponse200 = z.infer<
	typeof getClubTeamsListResponse200
>;

const getClubTeamsListResponse403 = z
	.object({ status: z.number().int(), message: z.string() })
	.partial();
export type getClubTeamsListResponse403 = z.infer<
	typeof getClubTeamsListResponse403
>;

const teamPlayersResponse400 = z
	.object({ property_path: z.string(), message: z.string() })
	.partial();
export type teamPlayersResponse400 = z.infer<typeof teamPlayersResponse400>;

const teamPlayersResponse403 = z
	.object({ status: z.number().int(), message: z.string() })
	.partial();
export type teamPlayersResponse403 = z.infer<typeof teamPlayersResponse403>;

const addUpdatePlayerResponse200 = z
	.object({
		status: z.number().int().describe("status"),
		message: z.string().describe("message"),
		data: ClubPlayersInfo,
	})
	.partial();
export type addUpdatePlayerResponse200 = z.infer<
	typeof addUpdatePlayerResponse200
>;

const addUpdatePlayerResponse400 = z
	.object({ property_path: z.string(), message: z.string() })
	.partial();
export type addUpdatePlayerResponse400 = z.infer<
	typeof addUpdatePlayerResponse400
>;

const addUpdatePlayerResponse403 = z
	.object({ status: z.number().int(), message: z.string() })
	.partial();
export type addUpdatePlayerResponse403 = z.infer<
	typeof addUpdatePlayerResponse403
>;

const addUpdatePlayerResponse409 = z
	.object({
		status: z.number().int().describe("status"),
		message: z.string().describe("message"),
		data: z.array(ClubPlayersInfo).describe("Player Data"),
	})
	.partial();
export type addUpdatePlayerResponse409 = z.infer<
	typeof addUpdatePlayerResponse409
>;

const updateVipResponse200 = z
	.object({
		status: z.number().int().describe("status"),
		message: z.string().describe("message"),
	})
	.partial();
export type updateVipResponse200 = z.infer<typeof updateVipResponse200>;

const updateVipResponse403 = z
	.object({ status: z.number().int(), message: z.string() })
	.partial();
export type updateVipResponse403 = z.infer<typeof updateVipResponse403>;

const updateVipResponse404 = z.unknown();
export type updateVipResponse404 = z.infer<typeof updateVipResponse404>;

const updateLevelResponse200 = z
	.object({
		status: z.number().int().describe("status"),
		message: z.string().describe("message"),
	})
	.partial();
export type updateLevelResponse200 = z.infer<typeof updateLevelResponse200>;

const updateLevelResponse403 = z
	.object({ status: z.number().int(), message: z.string() })
	.partial();
export type updateLevelResponse403 = z.infer<typeof updateLevelResponse403>;

const updateLevelResponse404 = z.unknown();
export type updateLevelResponse404 = z.infer<typeof updateLevelResponse404>;

const deletePlayerResponse200 = z
	.object({
		status: z.number().int().describe("status"),
		message: z.string().describe("message"),
	})
	.partial();
export type deletePlayerResponse200 = z.infer<typeof deletePlayerResponse200>;

const deletePlayerResponse403 = z
	.object({
		status: z.number().int().describe("status"),
		message: z.string().describe("message"),
	})
	.partial();
export type deletePlayerResponse403 = z.infer<typeof deletePlayerResponse403>;

const deletePlayerResponse404 = z.unknown();
export type deletePlayerResponse404 = z.infer<typeof deletePlayerResponse404>;

const formerPlayersResponse400 = z
	.object({ property_path: z.string(), message: z.string() })
	.partial();
export type formerPlayersResponse400 = z.infer<typeof formerPlayersResponse400>;

const formerPlayersResponse403 = z
	.object({ status: z.number().int(), message: z.string() })
	.partial();
export type formerPlayersResponse403 = z.infer<typeof formerPlayersResponse403>;

const uploadPlayerPhotoResponse200 = z
	.object({
		status: z.number().int().describe("status"),
		message: z.string().describe("message"),
		url: z.string().describe("message"),
	})
	.partial();
export type uploadPlayerPhotoResponse200 = z.infer<
	typeof uploadPlayerPhotoResponse200
>;

const uploadPlayerPhotoResponse400 = z.unknown();
export type uploadPlayerPhotoResponse400 = z.infer<
	typeof uploadPlayerPhotoResponse400
>;

const uploadPlayerPhotoResponse403 = z
	.object({ status: z.number().int(), message: z.string() })
	.partial();
export type uploadPlayerPhotoResponse403 = z.infer<
	typeof uploadPlayerPhotoResponse403
>;

const assignCoachesToTeamResponse200 = z
	.object({
		status: z.number().int().describe("status"),
		message: z.string().describe("message"),
	})
	.partial();
export type assignCoachesToTeamResponse200 = z.infer<
	typeof assignCoachesToTeamResponse200
>;

const assignCoachesToTeamResponse403 = z
	.object({ status: z.number().int(), message: z.string() })
	.partial();
export type assignCoachesToTeamResponse403 = z.infer<
	typeof assignCoachesToTeamResponse403
>;

const getNationalitiesResponse200 = z.array(DefaultSelectResponse);
export type getNationalitiesResponse200 = z.infer<
	typeof getNationalitiesResponse200
>;

const getCategoriesResponse200 = z.array(DefaultSelectResponse);
export type getCategoriesResponse200 = z.infer<typeof getCategoriesResponse200>;

const batchUpdateResponse200 = z.unknown();
export type batchUpdateResponse200 = z.infer<typeof batchUpdateResponse200>;

const batchUpdateResponse400 = z.unknown();
export type batchUpdateResponse400 = z.infer<typeof batchUpdateResponse400>;

const batchUpdateResponse403 = z.unknown();
export type batchUpdateResponse403 = z.infer<typeof batchUpdateResponse403>;

const generateSignUpLinkResponse200 = z.object({
	shortLink: z.string().describe("Mobile app sign-up short URL"),
	previewLink: z.string().describe("Mobile app sign-up preview URL"),
});
export type generateSignUpLinkResponse200 = z.infer<
	typeof generateSignUpLinkResponse200
>;

const generateSignUpLinkResponse400 = z.unknown();
export type generateSignUpLinkResponse400 = z.infer<
	typeof generateSignUpLinkResponse400
>;

const generateSignUpLinkResponse403 = z.unknown();
export type generateSignUpLinkResponse403 = z.infer<
	typeof generateSignUpLinkResponse403
>;

const generateSignUpLinkResponse404 = z.unknown();
export type generateSignUpLinkResponse404 = z.infer<
	typeof generateSignUpLinkResponse404
>;

const generateSignUpLinkResponse409 = z.unknown();
export type generateSignUpLinkResponse409 = z.infer<
	typeof generateSignUpLinkResponse409
>;

const getPlayerLatestLogEntriesResponse200 = z.object({
	height: z.string().nullable(),
	weight: z.string().nullable(),
	photo: z.string().nullable(),
});
export type getPlayerLatestLogEntriesResponse200 = z.infer<
	typeof getPlayerLatestLogEntriesResponse200
>;

const adminCreatePlayerPossibleDuplicateResponse201 = z.unknown();
export type adminCreatePlayerPossibleDuplicateResponse201 = z.infer<
	typeof adminCreatePlayerPossibleDuplicateResponse201
>;

const adminCreatePlayerPossibleDuplicateResponse400 = z
	.object({ property_path: z.string(), message: z.string() })
	.partial();
export type adminCreatePlayerPossibleDuplicateResponse400 = z.infer<
	typeof adminCreatePlayerPossibleDuplicateResponse400
>;

const adminCreatePlayerPossibleDuplicateResponse403 = z
	.object({ status: z.number().int(), message: z.string() })
	.partial();
export type adminCreatePlayerPossibleDuplicateResponse403 = z.infer<
	typeof adminCreatePlayerPossibleDuplicateResponse403
>;

const adminCreatePlayerPossibleDuplicateResponse404 = z
	.object({ status: z.number().int(), message: z.string() })
	.partial();
export type adminCreatePlayerPossibleDuplicateResponse404 = z.infer<
	typeof adminCreatePlayerPossibleDuplicateResponse404
>;

const getPlayerPossibleDuplicatesResponse400 = z.string();
export type getPlayerPossibleDuplicatesResponse400 = z.infer<
	typeof getPlayerPossibleDuplicatesResponse400
>;

const getPlayerPossibleDuplicatesResponse403 = z.string();
export type getPlayerPossibleDuplicatesResponse403 = z.infer<
	typeof getPlayerPossibleDuplicatesResponse403
>;

const patchPlayerPossibleDuplicateResponse200 = z.string();
export type patchPlayerPossibleDuplicateResponse200 = z.infer<
	typeof patchPlayerPossibleDuplicateResponse200
>;

const patchPlayerPossibleDuplicateResponse400 = z
	.object({ status: z.number().int(), message: z.string() })
	.partial();
export type patchPlayerPossibleDuplicateResponse400 = z.infer<
	typeof patchPlayerPossibleDuplicateResponse400
>;

const patchPlayerPossibleDuplicateResponse403 = z.string();
export type patchPlayerPossibleDuplicateResponse403 = z.infer<
	typeof patchPlayerPossibleDuplicateResponse403
>;

const patchPlayerPossibleDuplicateResponse404 = z
	.object({ status: z.number().int(), message: z.string() })
	.partial();
export type patchPlayerPossibleDuplicateResponse404 = z.infer<
	typeof patchPlayerPossibleDuplicateResponse404
>;

const getGamesResponse200 = z.array(GameData);
export type getGamesResponse200 = z.infer<typeof getGamesResponse200>;

const getClubsResponse200 = z.array(ClubDetailResponseDTO);
export type getClubsResponse200 = z.infer<typeof getClubsResponse200>;

const getVideoUploadsNotificationsResponse200 = z.array(VideoUploadLog);
export type getVideoUploadsNotificationsResponse200 = z.infer<
	typeof getVideoUploadsNotificationsResponse200
>;

const getTestVideosResponse200 = z.array(
	z.object({ host: z.string(), path: z.string() }),
);
export type getTestVideosResponse200 = z.infer<typeof getTestVideosResponse200>;

const migratePlayersResponse200 = z
	.object({
		status: z.number().int().describe("status"),
		message: z.string().describe("message"),
	})
	.partial();
export type migratePlayersResponse200 = z.infer<
	typeof migratePlayersResponse200
>;

const migratePlayersResponse400 = z.array(
	z.object({ property_path: z.string(), message: z.string() }).partial(),
);
export type migratePlayersResponse400 = z.infer<
	typeof migratePlayersResponse400
>;

const migratePlayersResponse403 = z
	.object({ status: z.number().int(), message: z.string() })
	.partial();
export type migratePlayersResponse403 = z.infer<
	typeof migratePlayersResponse403
>;

const getClubDetailResponse400 = z
	.object({ property_path: z.string(), message: z.string() })
	.partial();
export type getClubDetailResponse400 = z.infer<typeof getClubDetailResponse400>;

const getClubDetailResponse403 = z
	.object({ status: z.number().int(), message: z.string() })
	.partial();
export type getClubDetailResponse403 = z.infer<typeof getClubDetailResponse403>;

const postPlayerMappingResultsResponse201 = z.unknown();
export type postPlayerMappingResultsResponse201 = z.infer<
	typeof postPlayerMappingResultsResponse201
>;

const postPlayerMappingResultsResponse400 = z.string();
export type postPlayerMappingResultsResponse400 = z.infer<
	typeof postPlayerMappingResultsResponse400
>;

const postPlayerMappingResultsResponse403 = z.unknown();
export type postPlayerMappingResultsResponse403 = z.infer<
	typeof postPlayerMappingResultsResponse403
>;

const patchPlayerProfileResponse200 = z.string();
export type patchPlayerProfileResponse200 = z.infer<
	typeof patchPlayerProfileResponse200
>;

const patchPlayerProfileResponse400 = z.string();
export type patchPlayerProfileResponse400 = z.infer<
	typeof patchPlayerProfileResponse400
>;

const patchPlayerProfileResponse403 = z.string();
export type patchPlayerProfileResponse403 = z.infer<
	typeof patchPlayerProfileResponse403
>;

const patchPlayerProfileResponse404 = z.string();
export type patchPlayerProfileResponse404 = z.infer<
	typeof patchPlayerProfileResponse404
>;

const getPlayerUsersResponse400 = z.string();
export type getPlayerUsersResponse400 = z.infer<
	typeof getPlayerUsersResponse400
>;

const getPlayerUsersResponse403 = z.string();
export type getPlayerUsersResponse403 = z.infer<
	typeof getPlayerUsersResponse403
>;

const patchPlayerUserResponse200 = z.string();
export type patchPlayerUserResponse200 = z.infer<
	typeof patchPlayerUserResponse200
>;

const patchPlayerUserResponse400 = z.string();
export type patchPlayerUserResponse400 = z.infer<
	typeof patchPlayerUserResponse400
>;

const patchPlayerUserResponse403 = z.string();
export type patchPlayerUserResponse403 = z.infer<
	typeof patchPlayerUserResponse403
>;

const patchPlayerUserResponse404 = z.string();
export type patchPlayerUserResponse404 = z.infer<
	typeof patchPlayerUserResponse404
>;

const getSubscriptionsResponse400 = z.string();
export type getSubscriptionsResponse400 = z.infer<
	typeof getSubscriptionsResponse400
>;

const getSubscriptionsResponse403 = z.string();
export type getSubscriptionsResponse403 = z.infer<
	typeof getSubscriptionsResponse403
>;

const postSubscriptionResponse200 = z.string();
export type postSubscriptionResponse200 = z.infer<
	typeof postSubscriptionResponse200
>;

const postSubscriptionResponse400 = z.string();
export type postSubscriptionResponse400 = z.infer<
	typeof postSubscriptionResponse400
>;

const postSubscriptionResponse403 = z.string();
export type postSubscriptionResponse403 = z.infer<
	typeof postSubscriptionResponse403
>;

const getSubscriptionResponse403 = z.string();
export type getSubscriptionResponse403 = z.infer<
	typeof getSubscriptionResponse403
>;

const getSubscriptionResponse404 = z.string();
export type getSubscriptionResponse404 = z.infer<
	typeof getSubscriptionResponse404
>;

const putSubscriptionResponse200 = z.string();
export type putSubscriptionResponse200 = z.infer<
	typeof putSubscriptionResponse200
>;

const putSubscriptionResponse400 = z.string();
export type putSubscriptionResponse400 = z.infer<
	typeof putSubscriptionResponse400
>;

const putSubscriptionResponse403 = z.string();
export type putSubscriptionResponse403 = z.infer<
	typeof putSubscriptionResponse403
>;

const putSubscriptionResponse404 = z.string();
export type putSubscriptionResponse404 = z.infer<
	typeof putSubscriptionResponse404
>;

const deleteSubscriptionV2Response200 = z.string();
export type deleteSubscriptionV2Response200 = z.infer<
	typeof deleteSubscriptionV2Response200
>;

const deleteSubscriptionV2Response403 = z.string();
export type deleteSubscriptionV2Response403 = z.infer<
	typeof deleteSubscriptionV2Response403
>;

const deleteSubscriptionV2Response404 = z.string();
export type deleteSubscriptionV2Response404 = z.infer<
	typeof deleteSubscriptionV2Response404
>;

const getSubscriptionProductsConfigResponse403 = z.string();
export type getSubscriptionProductsConfigResponse403 = z.infer<
	typeof getSubscriptionProductsConfigResponse403
>;

const getSubscriptionDiscoverCountriesResponse200 = z.array(ChoiceResponse);
export type getSubscriptionDiscoverCountriesResponse200 = z.infer<
	typeof getSubscriptionDiscoverCountriesResponse200
>;

const getSubscriptionDiscoverCountriesResponse403 = z.string();
export type getSubscriptionDiscoverCountriesResponse403 = z.infer<
	typeof getSubscriptionDiscoverCountriesResponse403
>;

const getTeamGroupsResponse400 = z.string();
export type getTeamGroupsResponse400 = z.infer<typeof getTeamGroupsResponse400>;

const getTeamGroupsResponse403 = z.string();
export type getTeamGroupsResponse403 = z.infer<typeof getTeamGroupsResponse403>;

const postTeamGroupResponse200 = z.string();
export type postTeamGroupResponse200 = z.infer<typeof postTeamGroupResponse200>;

const postTeamGroupResponse400 = z.string();
export type postTeamGroupResponse400 = z.infer<typeof postTeamGroupResponse400>;

const postTeamGroupResponse403 = z.string();
export type postTeamGroupResponse403 = z.infer<typeof postTeamGroupResponse403>;

const getTeamGroupDiscoverTeamOptionsResponse200 = z
	.object({
		teams: z.array(
			z.object({ label: z.string(), value: z.number().int() }).partial(),
		),
		clubs: z.array(
			z.object({ label: z.string(), value: z.number().int() }).partial(),
		),
	})
	.partial();
export type getTeamGroupDiscoverTeamOptionsResponse200 = z.infer<
	typeof getTeamGroupDiscoverTeamOptionsResponse200
>;

const getTeamGroupDiscoverTeamOptionsResponse403 = z.string();
export type getTeamGroupDiscoverTeamOptionsResponse403 = z.infer<
	typeof getTeamGroupDiscoverTeamOptionsResponse403
>;

const getTeamGroupResponse403 = z.string();
export type getTeamGroupResponse403 = z.infer<typeof getTeamGroupResponse403>;

const getTeamGroupResponse404 = z.string();
export type getTeamGroupResponse404 = z.infer<typeof getTeamGroupResponse404>;

const putTeamGroupResponse200 = z.string();
export type putTeamGroupResponse200 = z.infer<typeof putTeamGroupResponse200>;

const putTeamGroupResponse400 = z.string();
export type putTeamGroupResponse400 = z.infer<typeof putTeamGroupResponse400>;

const putTeamGroupResponse403 = z.string();
export type putTeamGroupResponse403 = z.infer<typeof putTeamGroupResponse403>;

const putTeamGroupResponse404 = z.string();
export type putTeamGroupResponse404 = z.infer<typeof putTeamGroupResponse404>;

const deleteTeamGroupResponse200 = z.string();
export type deleteTeamGroupResponse200 = z.infer<
	typeof deleteTeamGroupResponse200
>;

const deleteTeamGroupResponse403 = z.string();
export type deleteTeamGroupResponse403 = z.infer<
	typeof deleteTeamGroupResponse403
>;

const deleteTeamGroupResponse404 = z.string();
export type deleteTeamGroupResponse404 = z.infer<
	typeof deleteTeamGroupResponse404
>;

const getSignUpRequestsResponse400 = z.string();
export type getSignUpRequestsResponse400 = z.infer<
	typeof getSignUpRequestsResponse400
>;

const getSignUpRequestsResponse403 = z.string();
export type getSignUpRequestsResponse403 = z.infer<
	typeof getSignUpRequestsResponse403
>;

const patchSignUpRequestResponse200 = z.string();
export type patchSignUpRequestResponse200 = z.infer<
	typeof patchSignUpRequestResponse200
>;

const patchSignUpRequestResponse400 = z.string();
export type patchSignUpRequestResponse400 = z.infer<
	typeof patchSignUpRequestResponse400
>;

const patchSignUpRequestResponse403 = z.string();
export type patchSignUpRequestResponse403 = z.infer<
	typeof patchSignUpRequestResponse403
>;

const patchSignUpRequestResponse404 = z.string();
export type patchSignUpRequestResponse404 = z.infer<
	typeof patchSignUpRequestResponse404
>;

const getSignUpRequestDataResponse400 = z.string();
export type getSignUpRequestDataResponse400 = z.infer<
	typeof getSignUpRequestDataResponse400
>;

const getSignUpRequestDataResponse403 = z.string();
export type getSignUpRequestDataResponse403 = z.infer<
	typeof getSignUpRequestDataResponse403
>;

const getSignUpRequestDataResponse404 = z.string();
export type getSignUpRequestDataResponse404 = z.infer<
	typeof getSignUpRequestDataResponse404
>;

const getTokenMovementsResponse400 = z.string();
export type getTokenMovementsResponse400 = z.infer<
	typeof getTokenMovementsResponse400
>;

const getTokenMovementsResponse403 = z.string();
export type getTokenMovementsResponse403 = z.infer<
	typeof getTokenMovementsResponse403
>;

const postTokenMovementResponse200 = z.string();
export type postTokenMovementResponse200 = z.infer<
	typeof postTokenMovementResponse200
>;

const postTokenMovementResponse400 = z.string();
export type postTokenMovementResponse400 = z.infer<
	typeof postTokenMovementResponse400
>;

const postTokenMovementResponse403 = z.string();
export type postTokenMovementResponse403 = z.infer<
	typeof postTokenMovementResponse403
>;

const getOptionsClubsOfSignUpRequestsResponse200 = z.array(
	DefaultSelectResponse,
);
export type getOptionsClubsOfSignUpRequestsResponse200 = z.infer<
	typeof getOptionsClubsOfSignUpRequestsResponse200
>;

const getOptionsClubsOfSignUpRequestsResponse400 = z.string();
export type getOptionsClubsOfSignUpRequestsResponse400 = z.infer<
	typeof getOptionsClubsOfSignUpRequestsResponse400
>;

const getOptionsClubsOfSignUpRequestsResponse403 = z.string();
export type getOptionsClubsOfSignUpRequestsResponse403 = z.infer<
	typeof getOptionsClubsOfSignUpRequestsResponse403
>;

const getOptionsClubsOfPlayerUsersResponse200 = z.array(DefaultSelectResponse);
export type getOptionsClubsOfPlayerUsersResponse200 = z.infer<
	typeof getOptionsClubsOfPlayerUsersResponse200
>;

const getOptionsClubsOfPlayerUsersResponse400 = z.string();
export type getOptionsClubsOfPlayerUsersResponse400 = z.infer<
	typeof getOptionsClubsOfPlayerUsersResponse400
>;

const getOptionsClubsOfPlayerUsersResponse403 = z.string();
export type getOptionsClubsOfPlayerUsersResponse403 = z.infer<
	typeof getOptionsClubsOfPlayerUsersResponse403
>;

const postGameTeamSheetExportResponse400 = z.string();
export type postGameTeamSheetExportResponse400 = z.infer<
	typeof postGameTeamSheetExportResponse400
>;

const postGameTeamSheetExportResponse403 = z.string();
export type postGameTeamSheetExportResponse403 = z.infer<
	typeof postGameTeamSheetExportResponse403
>;

const postGameTeamSheetExportResponse404 = z.string();
export type postGameTeamSheetExportResponse404 = z.infer<
	typeof postGameTeamSheetExportResponse404
>;

const putUpdateGameLeftTeamResponse200 = z
	.object({
		message: z.string().describe("message"),
		success: z.boolean().describe("success"),
	})
	.partial();
export type putUpdateGameLeftTeamResponse200 = z.infer<
	typeof putUpdateGameLeftTeamResponse200
>;

const putGameAnalysisApprovalResponse200 = z
	.object({
		message: z.string().describe("message"),
		success: z.boolean().describe("success"),
	})
	.partial();
export type putGameAnalysisApprovalResponse200 = z.infer<
	typeof putGameAnalysisApprovalResponse200
>;

const putGameResyncResponse200 = z
	.object({
		message: z.string().describe("message"),
		success: z.boolean().describe("success"),
	})
	.partial();
export type putGameResyncResponse200 = z.infer<typeof putGameResyncResponse200>;

const shareClipLinkResponse200 = z
	.object({
		status: z.string(),
		message: z.string(),
		update: z.number().int(),
		data: z.object({ clip_link: z.string() }).partial(),
	})
	.partial();
export type shareClipLinkResponse200 = z.infer<typeof shareClipLinkResponse200>;

const shareClipLinkResponse404 = z.unknown();
export type shareClipLinkResponse404 = z.infer<typeof shareClipLinkResponse404>;

const getTeamTokenBalanceResponse400 = z
	.object({ property_path: z.string(), message: z.string() })
	.partial();
export type getTeamTokenBalanceResponse400 = z.infer<
	typeof getTeamTokenBalanceResponse400
>;

const getTeamTokenBalanceResponse403 = z
	.object({ status: z.number().int(), message: z.string() })
	.partial();
export type getTeamTokenBalanceResponse403 = z.infer<
	typeof getTeamTokenBalanceResponse403
>;

const getClubTokenBalanceResponse400 = z
	.object({ property_path: z.string(), message: z.string() })
	.partial();
export type getClubTokenBalanceResponse400 = z.infer<
	typeof getClubTokenBalanceResponse400
>;

const getClubTokenBalanceResponse403 = z
	.object({ status: z.number().int(), message: z.string() })
	.partial();
export type getClubTokenBalanceResponse403 = z.infer<
	typeof getClubTokenBalanceResponse403
>;

const getTasksResponse403 = z.object({}).partial();
export type getTasksResponse403 = z.infer<typeof getTasksResponse403>;

const getAmatuerClubsResponse200 = z.array(TeamDTO);
export type getAmatuerClubsResponse200 = z.infer<
	typeof getAmatuerClubsResponse200
>;

const getAmatuerClubsResponse403 = z
	.object({ status: z.number().int(), message: z.string() })
	.partial();
export type getAmatuerClubsResponse403 = z.infer<
	typeof getAmatuerClubsResponse403
>;

const getTeamsheetResponse403 = z.object({}).partial();
export type getTeamsheetResponse403 = z.infer<typeof getTeamsheetResponse403>;

const getTeamsheetResponse404 = z.object({}).partial();
export type getTeamsheetResponse404 = z.infer<typeof getTeamsheetResponse404>;

const updatePlayerStatusResponse403 = z.object({}).partial();
export type updatePlayerStatusResponse403 = z.infer<
	typeof updatePlayerStatusResponse403
>;

const getTeamsForDropdownResponse200 = z.array(TeamDropdownDto);
export type getTeamsForDropdownResponse200 = z.infer<
	typeof getTeamsForDropdownResponse200
>;

const getTeamsForDropdownResponse403 = z.object({}).partial();
export type getTeamsForDropdownResponse403 = z.infer<
	typeof getTeamsForDropdownResponse403
>;

const getLeaguesForDropdownResponse200 = z.array(LeagueDropdownDto);
export type getLeaguesForDropdownResponse200 = z.infer<
	typeof getLeaguesForDropdownResponse200
>;

const getLeaguesForDropdownResponse403 = z.object({}).partial();
export type getLeaguesForDropdownResponse403 = z.infer<
	typeof getLeaguesForDropdownResponse403
>;

const getCountriesForDropdownResponse200 = z.array(
	z.object({ label: z.string() }).partial(),
);
export type getCountriesForDropdownResponse200 = z.infer<
	typeof getCountriesForDropdownResponse200
>;

const getCountriesForDropdownResponse403 = z.object({}).partial();
export type getCountriesForDropdownResponse403 = z.infer<
	typeof getCountriesForDropdownResponse403
>;

const createPlayerResponse200 = z
	.object({
		status: z
			.union([z.number(), z.string()])
			.describe(
				"Response status (integer for partner clubs, string for non-partner clubs)",
			),
		message: z.string().describe("Response message"),
		data: z.union([ClubPlayersInfo, ClubData]).describe("Player data"),
	})
	.partial();
export type createPlayerResponse200 = z.infer<typeof createPlayerResponse200>;

const createPlayerResponse403 = z.object({}).partial();
export type createPlayerResponse403 = z.infer<typeof createPlayerResponse403>;

const createPlayerResponse404 = z.object({}).partial();
export type createPlayerResponse404 = z.infer<typeof createPlayerResponse404>;

const playerSearchDropdownResponse400 = z
	.object({ error: z.string(), message: z.string() })
	.partial();
export type playerSearchDropdownResponse400 = z.infer<
	typeof playerSearchDropdownResponse400
>;

const playerSearchDropdownResponse403 = z.object({}).partial();
export type playerSearchDropdownResponse403 = z.infer<
	typeof playerSearchDropdownResponse403
>;

const adminGetGameAnalysisPhysicalResponse401 = z
	.object({ error: z.string() })
	.partial();
export type adminGetGameAnalysisPhysicalResponse401 = z.infer<
	typeof adminGetGameAnalysisPhysicalResponse401
>;

const adminGetGameAnalysisPhysicalResponse403 = z
	.object({ error: z.string() })
	.partial();
export type adminGetGameAnalysisPhysicalResponse403 = z.infer<
	typeof adminGetGameAnalysisPhysicalResponse403
>;

const adminGetGameAnalysisPhysicalResponse404 = z
	.object({ error: z.string() })
	.partial();
export type adminGetGameAnalysisPhysicalResponse404 = z.infer<
	typeof adminGetGameAnalysisPhysicalResponse404
>;

const postGameAnalysisPhysicalVideoUrlResponse400 = z
	.object({ message: z.string() })
	.partial();
export type postGameAnalysisPhysicalVideoUrlResponse400 = z.infer<
	typeof postGameAnalysisPhysicalVideoUrlResponse400
>;

const postGameAnalysisPhysicalVideoUrlResponse401 = z
	.object({ message: z.string() })
	.partial();
export type postGameAnalysisPhysicalVideoUrlResponse401 = z.infer<
	typeof postGameAnalysisPhysicalVideoUrlResponse401
>;

const postGameAnalysisPhysicalVideoUrlResponse403 = z
	.object({ message: z.string() })
	.partial();
export type postGameAnalysisPhysicalVideoUrlResponse403 = z.infer<
	typeof postGameAnalysisPhysicalVideoUrlResponse403
>;

const postGameAnalysisPhysicalVideoUrlResponse404 = z
	.object({ message: z.string() })
	.partial();
export type postGameAnalysisPhysicalVideoUrlResponse404 = z.infer<
	typeof postGameAnalysisPhysicalVideoUrlResponse404
>;

const getGamePageResponse400 = z.string();
export type getGamePageResponse400 = z.infer<typeof getGamePageResponse400>;

const getGamePageResponse403 = z.string();
export type getGamePageResponse403 = z.infer<typeof getGamePageResponse403>;

const getGamePageResponse404 = z.string();
export type getGamePageResponse404 = z.infer<typeof getGamePageResponse404>;

const getGamePageLazyResponse400 = z.string();
export type getGamePageLazyResponse400 = z.infer<
	typeof getGamePageLazyResponse400
>;

const getGamePageLazyResponse403 = z.string();
export type getGamePageLazyResponse403 = z.infer<
	typeof getGamePageLazyResponse403
>;

const getGamePageLazyResponse404 = z.string();
export type getGamePageLazyResponse404 = z.infer<
	typeof getGamePageLazyResponse404
>;

const putGameScoreResponse400 = z.string();
export type putGameScoreResponse400 = z.infer<typeof putGameScoreResponse400>;

const putGameScoreResponse403 = z.string();
export type putGameScoreResponse403 = z.infer<typeof putGameScoreResponse403>;

const putGameScoreResponse404 = z.string();
export type putGameScoreResponse404 = z.infer<typeof putGameScoreResponse404>;

const getGameTeamStatResponse400 = z.string();
export type getGameTeamStatResponse400 = z.infer<
	typeof getGameTeamStatResponse400
>;

const getGameTeamStatResponse403 = z.string();
export type getGameTeamStatResponse403 = z.infer<
	typeof getGameTeamStatResponse403
>;

const getGameTeamStatResponse404 = z.string();
export type getGameTeamStatResponse404 = z.infer<
	typeof getGameTeamStatResponse404
>;

const getGamePlayerStatResponse400 = z.string();
export type getGamePlayerStatResponse400 = z.infer<
	typeof getGamePlayerStatResponse400
>;

const getGamePlayerStatResponse403 = z.string();
export type getGamePlayerStatResponse403 = z.infer<
	typeof getGamePlayerStatResponse403
>;

const getGamePlayerStatResponse404 = z.string();
export type getGamePlayerStatResponse404 = z.infer<
	typeof getGamePlayerStatResponse404
>;

const putGameTeamColorsResponse400 = z.string();
export type putGameTeamColorsResponse400 = z.infer<
	typeof putGameTeamColorsResponse400
>;

const putGameTeamColorsResponse403 = z.string();
export type putGameTeamColorsResponse403 = z.infer<
	typeof putGameTeamColorsResponse403
>;

const putGameTeamColorsResponse404 = z.string();
export type putGameTeamColorsResponse404 = z.infer<
	typeof putGameTeamColorsResponse404
>;

const setRatingToGamePlayerResponse400 = z.string();
export type setRatingToGamePlayerResponse400 = z.infer<
	typeof setRatingToGamePlayerResponse400
>;

const setRatingToGamePlayerResponse403 = z.string();
export type setRatingToGamePlayerResponse403 = z.infer<
	typeof setRatingToGamePlayerResponse403
>;

const setRatingToGamePlayerResponse404 = z.string();
export type setRatingToGamePlayerResponse404 = z.infer<
	typeof setRatingToGamePlayerResponse404
>;

const setBadgesToGamePlayerResponse400 = z.string();
export type setBadgesToGamePlayerResponse400 = z.infer<
	typeof setBadgesToGamePlayerResponse400
>;

const setBadgesToGamePlayerResponse403 = z.string();
export type setBadgesToGamePlayerResponse403 = z.infer<
	typeof setBadgesToGamePlayerResponse403
>;

const setBadgesToGamePlayerResponse404 = z.string();
export type setBadgesToGamePlayerResponse404 = z.infer<
	typeof setBadgesToGamePlayerResponse404
>;

const postGameHighlightResponse400 = z.string();
export type postGameHighlightResponse400 = z.infer<
	typeof postGameHighlightResponse400
>;

const postGameHighlightResponse403 = z.string();
export type postGameHighlightResponse403 = z.infer<
	typeof postGameHighlightResponse403
>;

const postGameHighlightResponse404 = z.string();
export type postGameHighlightResponse404 = z.infer<
	typeof postGameHighlightResponse404
>;

const deleteGameHighlightResponse400 = z.string();
export type deleteGameHighlightResponse400 = z.infer<
	typeof deleteGameHighlightResponse400
>;

const deleteGameHighlightResponse403 = z.string();
export type deleteGameHighlightResponse403 = z.infer<
	typeof deleteGameHighlightResponse403
>;

const deleteGameHighlightResponse404 = z.string();
export type deleteGameHighlightResponse404 = z.infer<
	typeof deleteGameHighlightResponse404
>;

const patchGameHighlightResponse400 = z.string();
export type patchGameHighlightResponse400 = z.infer<
	typeof patchGameHighlightResponse400
>;

const patchGameHighlightResponse403 = z.string();
export type patchGameHighlightResponse403 = z.infer<
	typeof patchGameHighlightResponse403
>;

const patchGameHighlightResponse404 = z.string();
export type patchGameHighlightResponse404 = z.infer<
	typeof patchGameHighlightResponse404
>;

const getGameClipsResponse400 = z.string();
export type getGameClipsResponse400 = z.infer<typeof getGameClipsResponse400>;

const getGameClipsResponse403 = z.string();
export type getGameClipsResponse403 = z.infer<typeof getGameClipsResponse403>;

const getGameClipResponse400 = z.string();
export type getGameClipResponse400 = z.infer<typeof getGameClipResponse400>;

const getGameClipResponse403 = z.string();
export type getGameClipResponse403 = z.infer<typeof getGameClipResponse403>;

const putGameClipResponse400 = z.string();
export type putGameClipResponse400 = z.infer<typeof putGameClipResponse400>;

const putGameClipResponse403 = z.string();
export type putGameClipResponse403 = z.infer<typeof putGameClipResponse403>;

const putGameClipResponse404 = z.string();
export type putGameClipResponse404 = z.infer<typeof putGameClipResponse404>;

const deleteGameClipResponse400 = z.string();
export type deleteGameClipResponse400 = z.infer<
	typeof deleteGameClipResponse400
>;

const deleteGameClipResponse403 = z.string();
export type deleteGameClipResponse403 = z.infer<
	typeof deleteGameClipResponse403
>;

const deleteGameClipResponse404 = z.string();
export type deleteGameClipResponse404 = z.infer<
	typeof deleteGameClipResponse404
>;

const postGameClipResponse400 = z.string();
export type postGameClipResponse400 = z.infer<typeof postGameClipResponse400>;

const postGameClipResponse403 = z.string();
export type postGameClipResponse403 = z.infer<typeof postGameClipResponse403>;

const postGameClipResponse404 = z.string();
export type postGameClipResponse404 = z.infer<typeof postGameClipResponse404>;

const putGameTeamSheetResponse400 = z.string();
export type putGameTeamSheetResponse400 = z.infer<
	typeof putGameTeamSheetResponse400
>;

const putGameTeamSheetResponse403 = z.string();
export type putGameTeamSheetResponse403 = z.infer<
	typeof putGameTeamSheetResponse403
>;

const putGameTeamSheetResponse404 = z.string();
export type putGameTeamSheetResponse404 = z.infer<
	typeof putGameTeamSheetResponse404
>;

const getGameTeamSheetCompositionPlayersResponse400 = z.string();
export type getGameTeamSheetCompositionPlayersResponse400 = z.infer<
	typeof getGameTeamSheetCompositionPlayersResponse400
>;

const getGameTeamSheetCompositionPlayersResponse403 = z.string();
export type getGameTeamSheetCompositionPlayersResponse403 = z.infer<
	typeof getGameTeamSheetCompositionPlayersResponse403
>;

const getGameTeamSheetCompositionPlayersResponse404 = z.string();
export type getGameTeamSheetCompositionPlayersResponse404 = z.infer<
	typeof getGameTeamSheetCompositionPlayersResponse404
>;

const getGameTeamPlayersSearchResponse400 = z.string();
export type getGameTeamPlayersSearchResponse400 = z.infer<
	typeof getGameTeamPlayersSearchResponse400
>;

const getGameTeamPlayersSearchResponse403 = z.string();
export type getGameTeamPlayersSearchResponse403 = z.infer<
	typeof getGameTeamPlayersSearchResponse403
>;

const getGameTeamPlayersSearchResponse404 = z.string();
export type getGameTeamPlayersSearchResponse404 = z.infer<
	typeof getGameTeamPlayersSearchResponse404
>;

const getGameTeamSheetClubPlayersResponse400 = z.string();
export type getGameTeamSheetClubPlayersResponse400 = z.infer<
	typeof getGameTeamSheetClubPlayersResponse400
>;

const getGameTeamSheetClubPlayersResponse403 = z.string();
export type getGameTeamSheetClubPlayersResponse403 = z.infer<
	typeof getGameTeamSheetClubPlayersResponse403
>;

const getGameTeamSheetClubPlayersResponse404 = z.string();
export type getGameTeamSheetClubPlayersResponse404 = z.infer<
	typeof getGameTeamSheetClubPlayersResponse404
>;

const putGameDescriptionResponse400 = z.string();
export type putGameDescriptionResponse400 = z.infer<
	typeof putGameDescriptionResponse400
>;

const putGameDescriptionResponse403 = z.string();
export type putGameDescriptionResponse403 = z.infer<
	typeof putGameDescriptionResponse403
>;

const putGameDescriptionResponse404 = z.string();
export type putGameDescriptionResponse404 = z.infer<
	typeof putGameDescriptionResponse404
>;

const postGameStatsExportResponse400 = z.string();
export type postGameStatsExportResponse400 = z.infer<
	typeof postGameStatsExportResponse400
>;

const postGameStatsExportResponse403 = z.string();
export type postGameStatsExportResponse403 = z.infer<
	typeof postGameStatsExportResponse403
>;

const postGameStatsExportResponse404 = z.string();
export type postGameStatsExportResponse404 = z.infer<
	typeof postGameStatsExportResponse404
>;

const postGameEventDataExportResponse400 = z.string();
export type postGameEventDataExportResponse400 = z.infer<
	typeof postGameEventDataExportResponse400
>;

const postGameEventDataExportResponse403 = z.string();
export type postGameEventDataExportResponse403 = z.infer<
	typeof postGameEventDataExportResponse403
>;

const postGameEventDataExportResponse404 = z.string();
export type postGameEventDataExportResponse404 = z.infer<
	typeof postGameEventDataExportResponse404
>;

const getSharedGameClipIsProtectedResponse400 = z.string();
export type getSharedGameClipIsProtectedResponse400 = z.infer<
	typeof getSharedGameClipIsProtectedResponse400
>;

const getSharedGameClipIsProtectedResponse401 = z.string();
export type getSharedGameClipIsProtectedResponse401 = z.infer<
	typeof getSharedGameClipIsProtectedResponse401
>;

const getSharedGameClipIsProtectedResponse403 = z.string();
export type getSharedGameClipIsProtectedResponse403 = z.infer<
	typeof getSharedGameClipIsProtectedResponse403
>;

const getSharedGameClipResponse400 = z.string();
export type getSharedGameClipResponse400 = z.infer<
	typeof getSharedGameClipResponse400
>;

const getSharedGameClipResponse403 = z.string();
export type getSharedGameClipResponse403 = z.infer<
	typeof getSharedGameClipResponse403
>;

const postGameClipSharedPasswordValidationResponse400 = z.string();
export type postGameClipSharedPasswordValidationResponse400 = z.infer<
	typeof postGameClipSharedPasswordValidationResponse400
>;

const postGameClipSharedPasswordValidationResponse403 = z.string();
export type postGameClipSharedPasswordValidationResponse403 = z.infer<
	typeof postGameClipSharedPasswordValidationResponse403
>;

const postGameClipSharedPasswordValidationResponse404 = z.string();
export type postGameClipSharedPasswordValidationResponse404 = z.infer<
	typeof postGameClipSharedPasswordValidationResponse404
>;

const getGameEventFiltersResponse200 = z.array(GameEventFilterResponse);
export type getGameEventFiltersResponse200 = z.infer<
	typeof getGameEventFiltersResponse200
>;

const getGameEventFiltersResponse400 = z.string();
export type getGameEventFiltersResponse400 = z.infer<
	typeof getGameEventFiltersResponse400
>;

const getGameEventFiltersResponse403 = z.string();
export type getGameEventFiltersResponse403 = z.infer<
	typeof getGameEventFiltersResponse403
>;

const postGameEventFilterResponse400 = z.string();
export type postGameEventFilterResponse400 = z.infer<
	typeof postGameEventFilterResponse400
>;

const postGameEventFilterResponse403 = z.string();
export type postGameEventFilterResponse403 = z.infer<
	typeof postGameEventFilterResponse403
>;

const postGameEventFilterResponse404 = z.string();
export type postGameEventFilterResponse404 = z.infer<
	typeof postGameEventFilterResponse404
>;

const deleteGameEventFilterResponse400 = z.string();
export type deleteGameEventFilterResponse400 = z.infer<
	typeof deleteGameEventFilterResponse400
>;

const deleteGameEventFilterResponse403 = z.string();
export type deleteGameEventFilterResponse403 = z.infer<
	typeof deleteGameEventFilterResponse403
>;

const deleteGameEventFilterResponse404 = z.string();
export type deleteGameEventFilterResponse404 = z.infer<
	typeof deleteGameEventFilterResponse404
>;

const addCommentToGameEventResponse201 = z.unknown();
export type addCommentToGameEventResponse201 = z.infer<
	typeof addCommentToGameEventResponse201
>;

const updateSelectedViewResponse400 = z.string();
export type updateSelectedViewResponse400 = z.infer<
	typeof updateSelectedViewResponse400
>;

const updateSelectedViewResponse403 = z.string();
export type updateSelectedViewResponse403 = z.infer<
	typeof updateSelectedViewResponse403
>;

const getMfaDomainListResponse403 = z.string();
export type getMfaDomainListResponse403 = z.infer<
	typeof getMfaDomainListResponse403
>;

const getMfaDomainRequiredCheckResponse200 = z.object({
	isMfaRequired: z.boolean(),
});
export type getMfaDomainRequiredCheckResponse200 = z.infer<
	typeof getMfaDomainRequiredCheckResponse200
>;

const getMfaDomainRequiredCheckResponse403 = z.string();
export type getMfaDomainRequiredCheckResponse403 = z.infer<
	typeof getMfaDomainRequiredCheckResponse403
>;

const postReportedTaggingIssueResponse400 = z.string();
export type postReportedTaggingIssueResponse400 = z.infer<
	typeof postReportedTaggingIssueResponse400
>;

const postReportedTaggingIssueResponse403 = z.string();
export type postReportedTaggingIssueResponse403 = z.infer<
	typeof postReportedTaggingIssueResponse403
>;

const postReportedTaggingIssueResponse404 = z.string();
export type postReportedTaggingIssueResponse404 = z.infer<
	typeof postReportedTaggingIssueResponse404
>;

const updateUserSelectedCountriesResponse200 = z
	.object({
		status: z.union([z.string(), z.boolean()]).describe("status"),
		message: z.string().describe("message"),
	})
	.partial();
export type updateUserSelectedCountriesResponse200 = z.infer<
	typeof updateUserSelectedCountriesResponse200
>;

const updateUserSelectedCountriesResponse400 = z.unknown();
export type updateUserSelectedCountriesResponse400 = z.infer<
	typeof updateUserSelectedCountriesResponse400
>;

const updateUserSelectedCountriesResponse403 = z.unknown();
export type updateUserSelectedCountriesResponse403 = z.infer<
	typeof updateUserSelectedCountriesResponse403
>;

const getPlayerProfileEventTypesResponse403 = z.unknown();
export type getPlayerProfileEventTypesResponse403 = z.infer<
	typeof getPlayerProfileEventTypesResponse403
>;

const getPlayerProfileEventTypesResponse404 = z.unknown();
export type getPlayerProfileEventTypesResponse404 = z.infer<
	typeof getPlayerProfileEventTypesResponse404
>;

const getPlayerProfileEventFiltersResponse403 = z.unknown();
export type getPlayerProfileEventFiltersResponse403 = z.infer<
	typeof getPlayerProfileEventFiltersResponse403
>;

const getPlayerProfileEventFiltersResponse404 = z.unknown();
export type getPlayerProfileEventFiltersResponse404 = z.infer<
	typeof getPlayerProfileEventFiltersResponse404
>;

const getPlayerProfileEventsResponse403 = z.unknown();
export type getPlayerProfileEventsResponse403 = z.infer<
	typeof getPlayerProfileEventsResponse403
>;

const getPlayerProfileEventsResponse404 = z.unknown();
export type getPlayerProfileEventsResponse404 = z.infer<
	typeof getPlayerProfileEventsResponse404
>;

const getPlayerProfileKeyEventsResponse403 = z.unknown();
export type getPlayerProfileKeyEventsResponse403 = z.infer<
	typeof getPlayerProfileKeyEventsResponse403
>;

const getPlayerProfileKeyEventsResponse404 = z.unknown();
export type getPlayerProfileKeyEventsResponse404 = z.infer<
	typeof getPlayerProfileKeyEventsResponse404
>;

const postPlayerProfileCrossGameClipResponse400 = z.string();
export type postPlayerProfileCrossGameClipResponse400 = z.infer<
	typeof postPlayerProfileCrossGameClipResponse400
>;

const postPlayerProfileCrossGameClipResponse403 = z.string();
export type postPlayerProfileCrossGameClipResponse403 = z.infer<
	typeof postPlayerProfileCrossGameClipResponse403
>;

const postPlayerProfileCrossGameClipResponse404 = z.string();
export type postPlayerProfileCrossGameClipResponse404 = z.infer<
	typeof postPlayerProfileCrossGameClipResponse404
>;

const getPlayerProfileShadowTeamListResponse403 = z.unknown();
export type getPlayerProfileShadowTeamListResponse403 = z.infer<
	typeof getPlayerProfileShadowTeamListResponse403
>;

const getPlayerProfileShadowTeamListResponse404 = z.unknown();
export type getPlayerProfileShadowTeamListResponse404 = z.infer<
	typeof getPlayerProfileShadowTeamListResponse404
>;

const postPlayerProfileShadowTeamPlayerResponse403 = z.unknown();
export type postPlayerProfileShadowTeamPlayerResponse403 = z.infer<
	typeof postPlayerProfileShadowTeamPlayerResponse403
>;

const postPlayerProfileShadowTeamPlayerResponse404 = z.unknown();
export type postPlayerProfileShadowTeamPlayerResponse404 = z.infer<
	typeof postPlayerProfileShadowTeamPlayerResponse404
>;

const postPlayerProfileShadowTeamResponse201 = z.unknown();
export type postPlayerProfileShadowTeamResponse201 = z.infer<
	typeof postPlayerProfileShadowTeamResponse201
>;

const postPlayerProfileShadowTeamResponse400 = z.unknown();
export type postPlayerProfileShadowTeamResponse400 = z.infer<
	typeof postPlayerProfileShadowTeamResponse400
>;

const postPlayerProfileShadowTeamResponse403 = z.unknown();
export type postPlayerProfileShadowTeamResponse403 = z.infer<
	typeof postPlayerProfileShadowTeamResponse403
>;

const getPlayerProfileRecruitmentInformationResponse403 = z.unknown();
export type getPlayerProfileRecruitmentInformationResponse403 = z.infer<
	typeof getPlayerProfileRecruitmentInformationResponse403
>;

const getPlayerProfileRecruitmentInformationResponse404 = z.unknown();
export type getPlayerProfileRecruitmentInformationResponse404 = z.infer<
	typeof getPlayerProfileRecruitmentInformationResponse404
>;

const getPlayerProfileBiometricsAvailabilityResponse403 = z.unknown();
export type getPlayerProfileBiometricsAvailabilityResponse403 = z.infer<
	typeof getPlayerProfileBiometricsAvailabilityResponse403
>;

const getPlayerProfileBiometricsAvailabilityResponse404 = z.unknown();
export type getPlayerProfileBiometricsAvailabilityResponse404 = z.infer<
	typeof getPlayerProfileBiometricsAvailabilityResponse404
>;

const getPlayerProfileBodyMeasurementsResponse403 = z.unknown();
export type getPlayerProfileBodyMeasurementsResponse403 = z.infer<
	typeof getPlayerProfileBodyMeasurementsResponse403
>;

const getPlayerProfileBodyMeasurementsResponse404 = z.unknown();
export type getPlayerProfileBodyMeasurementsResponse404 = z.infer<
	typeof getPlayerProfileBodyMeasurementsResponse404
>;

const getPlayerProfileBodyMetricTrendResponse403 = z.unknown();
export type getPlayerProfileBodyMetricTrendResponse403 = z.infer<
	typeof getPlayerProfileBodyMetricTrendResponse403
>;

const getPlayerProfileBodyMetricTrendResponse404 = z.unknown();
export type getPlayerProfileBodyMetricTrendResponse404 = z.infer<
	typeof getPlayerProfileBodyMetricTrendResponse404
>;

const getPlayerProfileBodyCompositionChangeResponse403 = z.unknown();
export type getPlayerProfileBodyCompositionChangeResponse403 = z.infer<
	typeof getPlayerProfileBodyCompositionChangeResponse403
>;

const getPlayerProfileBodyCompositionChangeResponse404 = z.unknown();
export type getPlayerProfileBodyCompositionChangeResponse404 = z.infer<
	typeof getPlayerProfileBodyCompositionChangeResponse404
>;

const getPlayerProfilePhysicalMetricsFiltersResponse403 = z.unknown();
export type getPlayerProfilePhysicalMetricsFiltersResponse403 = z.infer<
	typeof getPlayerProfilePhysicalMetricsFiltersResponse403
>;

const getPlayerProfilePhysicalMetricsFiltersResponse404 = z.unknown();
export type getPlayerProfilePhysicalMetricsFiltersResponse404 = z.infer<
	typeof getPlayerProfilePhysicalMetricsFiltersResponse404
>;

const getPlayerProfilePhysicalMetricsResponse403 = z.unknown();
export type getPlayerProfilePhysicalMetricsResponse403 = z.infer<
	typeof getPlayerProfilePhysicalMetricsResponse403
>;

const getPlayerProfilePhysicalMetricsResponse404 = z.unknown();
export type getPlayerProfilePhysicalMetricsResponse404 = z.infer<
	typeof getPlayerProfilePhysicalMetricsResponse404
>;

const getPlayerProfileBehavioralSignalsFiltersResponse403 = z.unknown();
export type getPlayerProfileBehavioralSignalsFiltersResponse403 = z.infer<
	typeof getPlayerProfileBehavioralSignalsFiltersResponse403
>;

const getPlayerProfileBehavioralSignalsFiltersResponse404 = z.unknown();
export type getPlayerProfileBehavioralSignalsFiltersResponse404 = z.infer<
	typeof getPlayerProfileBehavioralSignalsFiltersResponse404
>;

const getPlayerProfileBehavioralSignalsTrendResponse403 = z.unknown();
export type getPlayerProfileBehavioralSignalsTrendResponse403 = z.infer<
	typeof getPlayerProfileBehavioralSignalsTrendResponse403
>;

const getPlayerProfileBehavioralSignalsTrendResponse404 = z.unknown();
export type getPlayerProfileBehavioralSignalsTrendResponse404 = z.infer<
	typeof getPlayerProfileBehavioralSignalsTrendResponse404
>;

const getPlayerProfileBehavioralSignalsSnapshotResponse403 = z.unknown();
export type getPlayerProfileBehavioralSignalsSnapshotResponse403 = z.infer<
	typeof getPlayerProfileBehavioralSignalsSnapshotResponse403
>;

const getPlayerProfileBehavioralSignalsSnapshotResponse404 = z.unknown();
export type getPlayerProfileBehavioralSignalsSnapshotResponse404 = z.infer<
	typeof getPlayerProfileBehavioralSignalsSnapshotResponse404
>;

const getPlayerProfileBehavioralArchetypesResponse403 = z.unknown();
export type getPlayerProfileBehavioralArchetypesResponse403 = z.infer<
	typeof getPlayerProfileBehavioralArchetypesResponse403
>;

const getPlayerProfileBehavioralArchetypesResponse404 = z.unknown();
export type getPlayerProfileBehavioralArchetypesResponse404 = z.infer<
	typeof getPlayerProfileBehavioralArchetypesResponse404
>;

const getDiscoverClubHouses = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: {
			page?: number;
			limit?: number;
			filters?: object[];
			order?: object;
		},
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<DiscoverClubHousesResponse>> => {
		const args = z
			.object({
				page: z.number().int().optional(),
				limit: z.number().int().optional(),
				filters: z
					.array(
						z.object({
							type: z
								.enum([
									"exact",
									"search",
									"boolean",
									"exists",
									"inarray",
									"datetimerange",
									"numberrange",
								])
								.describe("Type of filter")
								.optional(),
							field: z
								.enum([
									"id",
									"name",
									"type",
									"country",
									"city",
									"entityType",
									"isNational",
								])
								.describe("Property to filter")
								.optional(),
							value: z
								.any()
								.superRefine((x, ctx) => {
									const schemas = [
										z.string(),
										z.number().int(),
										z.boolean(),
										z.object({
											start: z.string().datetime({ offset: true }).optional(),
											end: z.string().datetime({ offset: true }).optional(),
										}),
									];
									const errors = schemas.reduce<z.ZodError[]>(
										(errors, schema) =>
											((result) =>
												result.error ? [...errors, result.error] : errors)(
												schema.safeParse(x),
											),
										[],
									);
									if (schemas.length - errors.length !== 1) {
										ctx.addIssue({
											path: ctx.path,
											code: "invalid_union",
											unionErrors: errors,
											message: "Invalid input: Should pass single schema",
										});
									}
								})
								.describe(
									"Filter value, can be a string, integer, date or a date range",
								)
								.optional(),
						}),
					)
					.optional(),
				order: z
					.object({
						field: z
							.enum([
								"id",
								"name",
								"type",
								"country",
								"region",
								"city",
								"createdAt",
							])
							.describe("Sorting field")
							.optional(),
						direction: z
							.enum(["ASC", "DESC"])
							.describe("Sorting direction")
							.optional(),
					})
					.optional(),
			})
			.parse(params);
		const { page, limit, filters, order } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url("/admin/discover/club-houses")
				.query(qs.stringify({ page, limit, filters, order }))
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = DiscoverClubHousesResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = getDiscoverClubHousesResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = getDiscoverClubHousesResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: {
				page?: number;
				limit?: number;
				filters?: object[];
				order?: object;
			},
			options?: Omit<
				UseQueryOptions<
					ApiResponse<DiscoverClubHousesResponse>,
					GenericWretchError<
						getDiscoverClubHousesResponse400 | getDiscoverClubHousesResponse403
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<DiscoverClubHousesResponse>,
			GenericWretchError<
				getDiscoverClubHousesResponse400 | getDiscoverClubHousesResponse403
			>
		> => {
			const key = [
				"getDiscoverClubHouses",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<ApiResponse<DiscoverClubHousesResponse>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<DiscoverClubHousesResponse>,
				GenericWretchError<
					getDiscoverClubHousesResponse400 | getDiscoverClubHousesResponse403
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<DiscoverClubHousesResponse>,
				GenericWretchError<
					getDiscoverClubHousesResponse400 | getDiscoverClubHousesResponse403
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: {
				page?: number;
				limit?: number;
				filters?: object[];
				order?: object;
			},
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<DiscoverClubHousesResponse>,
					GenericWretchError<
						getDiscoverClubHousesResponse400 | getDiscoverClubHousesResponse403
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<DiscoverClubHousesResponse>,
			GenericWretchError<
				getDiscoverClubHousesResponse400 | getDiscoverClubHousesResponse403
			>
		> => {
			const key = [
				"getDiscoverClubHouses",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<ApiResponse<DiscoverClubHousesResponse>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<DiscoverClubHousesResponse>,
				GenericWretchError<
					getDiscoverClubHousesResponse400 | getDiscoverClubHousesResponse403
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<DiscoverClubHousesResponse>,
				GenericWretchError<
					getDiscoverClubHousesResponse400 | getDiscoverClubHousesResponse403
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getDiscoverClubHouse = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: { id: number },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<DiscoverClubHouseGetResponse>> => {
		const args = z.object({ id: z.number().int() }).parse(params);
		const { id } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/admin/discover/club-house/${id}`)
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = DiscoverClubHouseGetResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = getDiscoverClubHouseResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = getDiscoverClubHouseResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = getDiscoverClubHouseResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { id: number },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<DiscoverClubHouseGetResponse>,
					GenericWretchError<
						| getDiscoverClubHouseResponse400
						| getDiscoverClubHouseResponse403
						| getDiscoverClubHouseResponse404
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<DiscoverClubHouseGetResponse>,
			GenericWretchError<
				| getDiscoverClubHouseResponse400
				| getDiscoverClubHouseResponse403
				| getDiscoverClubHouseResponse404
			>
		> => {
			const key = [
				"getDiscoverClubHouse",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<ApiResponse<DiscoverClubHouseGetResponse>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<DiscoverClubHouseGetResponse>,
				GenericWretchError<
					| getDiscoverClubHouseResponse400
					| getDiscoverClubHouseResponse403
					| getDiscoverClubHouseResponse404
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<DiscoverClubHouseGetResponse>,
				GenericWretchError<
					| getDiscoverClubHouseResponse400
					| getDiscoverClubHouseResponse403
					| getDiscoverClubHouseResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { id: number },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<DiscoverClubHouseGetResponse>,
					GenericWretchError<
						| getDiscoverClubHouseResponse400
						| getDiscoverClubHouseResponse403
						| getDiscoverClubHouseResponse404
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<DiscoverClubHouseGetResponse>,
			GenericWretchError<
				| getDiscoverClubHouseResponse400
				| getDiscoverClubHouseResponse403
				| getDiscoverClubHouseResponse404
			>
		> => {
			const key = [
				"getDiscoverClubHouse",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<ApiResponse<DiscoverClubHouseGetResponse>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<DiscoverClubHouseGetResponse>,
				GenericWretchError<
					| getDiscoverClubHouseResponse400
					| getDiscoverClubHouseResponse403
					| getDiscoverClubHouseResponse404
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<DiscoverClubHouseGetResponse>,
				GenericWretchError<
					| getDiscoverClubHouseResponse400
					| getDiscoverClubHouseResponse403
					| getDiscoverClubHouseResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const patchDiscoverClubHouse = (wretch: Http) => {
	const mutation = async (params: {
		id: number;
		body: DiscoverClubHousePatchRequest;
	}) => {
		const args = z
			.object({ id: z.number().int(), body: z.any() })
			.parse(params);
		const { id, body } = args;

		try {
			const res = await wretch
				.url(`/admin/discover/club-house/${id}`)
				.json(body)
				.patch()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = patchDiscoverClubHouseResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = patchDiscoverClubHouseResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = patchDiscoverClubHouseResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = patchDiscoverClubHouseResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<patchDiscoverClubHouseResponse200> | undefined,
					GenericWretchError<
						| patchDiscoverClubHouseResponse400
						| patchDiscoverClubHouseResponse403
						| patchDiscoverClubHouseResponse404
					>,
					{ id: number; body: DiscoverClubHousePatchRequest }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<patchDiscoverClubHouseResponse200> | undefined,
				GenericWretchError<
					| patchDiscoverClubHouseResponse400
					| patchDiscoverClubHouseResponse403
					| patchDiscoverClubHouseResponse404
				>,
				{ id: number; body: DiscoverClubHousePatchRequest }
			>(mutation, options || {});
		},
	};
};

const getDiscoverCountries = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: { language?: string },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<getDiscoverCountriesResponse200>> => {
		const args = z.object({ language: z.string().optional() }).parse(params);
		const { language } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url("/admin/discover/countries")
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = getDiscoverCountriesResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { language?: string },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<getDiscoverCountriesResponse200>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<getDiscoverCountriesResponse200>,
			GenericWretchError<null>
		> => {
			const key = [
				"getDiscoverCountries",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<ApiResponse<getDiscoverCountriesResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<getDiscoverCountriesResponse200>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<getDiscoverCountriesResponse200>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { language?: string },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<getDiscoverCountriesResponse200>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<getDiscoverCountriesResponse200>,
			GenericWretchError<null>
		> => {
			const key = [
				"getDiscoverCountries",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<ApiResponse<getDiscoverCountriesResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<getDiscoverCountriesResponse200>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<getDiscoverCountriesResponse200>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getDiscoverGames = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: {
			page?: number;
			limit?: number;
			filters?: object[];
			order?: object;
		},
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<DiscoverGamesResponse>> => {
		const args = z
			.object({
				page: z.number().int().optional(),
				limit: z.number().int().optional(),
				filters: z
					.array(
						z.object({
							type: z
								.enum([
									"exact",
									"search",
									"boolean",
									"exists",
									"inarray",
									"datetimerange",
									"numberrange",
								])
								.describe("Type of filter")
								.optional(),
							field: z
								.enum([
									"id",
									"date",
									"createdAt",
									"ageCategory",
									"homeClub",
									"homeTeam",
									"awayClub",
									"awayTeam",
									"anyClubId",
									"anyTeamId",
									"playerId",
									"isVip",
									"isHidden",
									"isHiddenForScout",
									"isDiscover",
									"providerTag",
									"hasVideo",
									"analysisRequestedAt",
									"gameAnalysisStatus",
									"priority",
									"gameStatus",
									"requestedProcessingLevel",
									"owner",
									"country",
									"clubManager",
									"contentManager",
									"assignee",
								])
								.describe("Property to filter")
								.optional(),
							value: z
								.any()
								.superRefine((x, ctx) => {
									const schemas = [
										z.string(),
										z.number().int(),
										z.boolean(),
										z.object({
											start: z.string().datetime({ offset: true }).optional(),
											end: z.string().datetime({ offset: true }).optional(),
										}),
									];
									const errors = schemas.reduce<z.ZodError[]>(
										(errors, schema) =>
											((result) =>
												result.error ? [...errors, result.error] : errors)(
												schema.safeParse(x),
											),
										[],
									);
									if (schemas.length - errors.length !== 1) {
										ctx.addIssue({
											path: ctx.path,
											code: "invalid_union",
											unionErrors: errors,
											message: "Invalid input: Should pass single schema",
										});
									}
								})
								.describe(
									"Filter value, can be a string, integer, date or a date range",
								)
								.optional(),
						}),
					)
					.optional(),
				order: z
					.object({
						field: z
							.enum([
								"id",
								"date",
								"createdAt",
								"ageCategory",
								"homeClubCountry",
								"homeClubName",
								"homeTeamName",
								"awayClubName",
								"awayTeamName",
								"clubManagerName",
								"contentManagerName",
								"assigneeName",
								"analysisRequestedAt",
								"priority",
								"gameAnalysisStatus",
								"analysisTurnaround",
								"analysisSla",
								"gameStatus",
								"isVip",
								"isHidden",
								"isHiddenForScout",
								"isDiscover",
								"providerTag",
								"hasVideo",
								"gameHistoriesCount",
								"physicalMetricsRequested",
								"homeIsTeamsheetFilled",
								"slaContent",
								"slaExpansion",
								"numberOfContentErrors",
								"numberOfTaggingErrors",
								"owners",
							])
							.describe("Sorting field")
							.optional(),
						direction: z
							.enum(["ASC", "DESC"])
							.describe("Sorting direction")
							.optional(),
					})
					.optional(),
			})
			.parse(params);
		const { page, limit, filters, order } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url("/admin/discover/games")
				.query(qs.stringify({ page, limit, filters, order }))
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = DiscoverGamesResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = getDiscoverGamesResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = getDiscoverGamesResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: {
				page?: number;
				limit?: number;
				filters?: object[];
				order?: object;
			},
			options?: Omit<
				UseQueryOptions<
					ApiResponse<DiscoverGamesResponse>,
					GenericWretchError<
						getDiscoverGamesResponse400 | getDiscoverGamesResponse403
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<DiscoverGamesResponse>,
			GenericWretchError<
				getDiscoverGamesResponse400 | getDiscoverGamesResponse403
			>
		> => {
			const key = ["getDiscoverGames", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<DiscoverGamesResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<DiscoverGamesResponse>,
				GenericWretchError<
					getDiscoverGamesResponse400 | getDiscoverGamesResponse403
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<DiscoverGamesResponse>,
				GenericWretchError<
					getDiscoverGamesResponse400 | getDiscoverGamesResponse403
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: {
				page?: number;
				limit?: number;
				filters?: object[];
				order?: object;
			},
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<DiscoverGamesResponse>,
					GenericWretchError<
						getDiscoverGamesResponse400 | getDiscoverGamesResponse403
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<DiscoverGamesResponse>,
			GenericWretchError<
				getDiscoverGamesResponse400 | getDiscoverGamesResponse403
			>
		> => {
			const key = ["getDiscoverGames", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<DiscoverGamesResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<DiscoverGamesResponse>,
				GenericWretchError<
					getDiscoverGamesResponse400 | getDiscoverGamesResponse403
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<DiscoverGamesResponse>,
				GenericWretchError<
					getDiscoverGamesResponse400 | getDiscoverGamesResponse403
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getDiscoverGamesDynamic = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: {
			page?: number;
			limit?: number;
			columns?: string[];
			filters?: object[];
			order?: object;
		},
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<DynamicDataGridResponse>> => {
		const args = z
			.object({
				page: z.number().int().describe("Page number (1-indexed)").default(1),
				limit: z
					.number()
					.int()
					.gte(1)
					.lte(100)
					.describe("Number of items per page")
					.default(20),
				columns: z
					.array(
						z.enum([
							"id",
							"date",
							"createdAt",
							"ageCategory",
							"homeClubCountry",
							"awayClubCountry",
							"homeTeamCountry",
							"awayTeamCountry",
							"homeClubId",
							"homeClubIsPartnerClub",
							"homeClubName",
							"homeGameTeamClubName",
							"homeTeamId",
							"homeTeamName",
							"homeGameTeamTeamName",
							"homeScore",
							"homeIsTeamsheetFilled",
							"homePlayersCount",
							"awayClubId",
							"awayClubName",
							"awayGameTeamClubName",
							"awayTeamId",
							"awayTeamName",
							"awayGameTeamTeamName",
							"awayScore",
							"awayIsTeamsheetFilled",
							"awayPlayersCount",
							"clubManagerId",
							"clubManagerName",
							"contentManagerId",
							"contentManagerName",
							"assigneeId",
							"assigneeName",
							"isVip",
							"isHidden",
							"isHiddenForScout",
							"isDiscover",
							"providerTag",
							"hasVideo",
							"isAnalysisFinished",
							"isAnalysisApproved",
							"analysisRequestedAt",
							"requestedProcessingLevel",
							"gameAnalysisStatus",
							"priority",
							"analysisTurnaround",
							"analysisSlaHours",
							"analysisSla",
							"gameStatus",
							"gameHistoriesCount",
							"physicalMetricsRequested",
							"owners",
							"slaContent",
							"slaExpansion",
							"numberOfContentErrors",
							"numberOfTaggingErrors",
						]),
					)
					.describe("Columns to include in response")
					.optional(),
				filters: z
					.array(
						z.object({
							type: z
								.enum([
									"exact",
									"search",
									"boolean",
									"exists",
									"inarray",
									"datetimerange",
									"numberrange",
								])
								.describe("Type of filter")
								.optional(),
							field: z
								.enum([
									"id",
									"date",
									"createdAt",
									"ageCategory",
									"homeClub",
									"homeTeam",
									"awayClub",
									"awayTeam",
									"anyClubId",
									"anyTeamId",
									"playerId",
									"isVip",
									"isHidden",
									"isHiddenForScout",
									"isDiscover",
									"providerTag",
									"hasVideo",
									"analysisRequestedAt",
									"gameAnalysisStatus",
									"priority",
									"gameStatus",
									"requestedProcessingLevel",
									"owner",
									"country",
									"clubManager",
									"contentManager",
									"assignee",
								])
								.describe("Field to filter on")
								.optional(),
							value: z
								.any()
								.superRefine((x, ctx) => {
									const schemas = [
										z.string(),
										z.number().int(),
										z.boolean(),
										z.array(
											z.any().superRefine((x, ctx) => {
												const schemas = [z.string(), z.number().int()];
												const errors = schemas.reduce<z.ZodError[]>(
													(errors, schema) =>
														((result) =>
															result.error
																? [...errors, result.error]
																: errors)(schema.safeParse(x)),
													[],
												);
												if (schemas.length - errors.length !== 1) {
													ctx.addIssue({
														path: ctx.path,
														code: "invalid_union",
														unionErrors: errors,
														message: "Invalid input: Should pass single schema",
													});
												}
											}),
										),
										z.object({
											start: z.string().datetime({ offset: true }).optional(),
											end: z.string().datetime({ offset: true }).optional(),
										}),
									];
									const errors = schemas.reduce<z.ZodError[]>(
										(errors, schema) =>
											((result) =>
												result.error ? [...errors, result.error] : errors)(
												schema.safeParse(x),
											),
										[],
									);
									if (schemas.length - errors.length !== 1) {
										ctx.addIssue({
											path: ctx.path,
											code: "invalid_union",
											unionErrors: errors,
											message: "Invalid input: Should pass single schema",
										});
									}
								})
								.describe(
									"Filter value (string, integer, boolean, string[] for inarray multiselect, or date range object)",
								)
								.optional(),
						}),
					)
					.describe("Filter criteria")
					.optional(),
				order: z
					.object({
						field: z
							.enum([
								"id",
								"date",
								"createdAt",
								"ageCategory",
								"homeClubCountry",
								"homeClubName",
								"homeTeamName",
								"awayClubName",
								"awayTeamName",
								"clubManagerName",
								"contentManagerName",
								"assigneeName",
								"analysisRequestedAt",
								"priority",
								"gameAnalysisStatus",
								"analysisTurnaround",
								"analysisSla",
								"gameStatus",
								"isVip",
								"isHidden",
								"isHiddenForScout",
								"isDiscover",
								"providerTag",
								"hasVideo",
								"gameHistoriesCount",
								"physicalMetricsRequested",
								"homeIsTeamsheetFilled",
								"slaContent",
								"slaExpansion",
								"numberOfContentErrors",
								"numberOfTaggingErrors",
								"owners",
							])
							.describe("Field to sort by")
							.optional(),
						direction: z
							.enum(["ASC", "DESC"])
							.describe("Sort direction")
							.optional(),
					})
					.describe("Sort configuration")
					.optional(),
			})
			.parse(params);
		const { page, limit, columns, filters, order } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url("/admin/discover/games/v2")
				.query(qs.stringify({ page, limit, columns, filters, order }))
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = DynamicDataGridResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = getDiscoverGamesDynamicResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = getDiscoverGamesDynamicResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: {
				page?: number;
				limit?: number;
				columns?: string[];
				filters?: object[];
				order?: object;
			},
			options?: Omit<
				UseQueryOptions<
					ApiResponse<DynamicDataGridResponse>,
					GenericWretchError<
						| getDiscoverGamesDynamicResponse400
						| getDiscoverGamesDynamicResponse403
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<DynamicDataGridResponse>,
			GenericWretchError<
				getDiscoverGamesDynamicResponse400 | getDiscoverGamesDynamicResponse403
			>
		> => {
			const key = [
				"getDiscoverGamesDynamic",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<ApiResponse<DynamicDataGridResponse>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<DynamicDataGridResponse>,
				GenericWretchError<
					| getDiscoverGamesDynamicResponse400
					| getDiscoverGamesDynamicResponse403
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<DynamicDataGridResponse>,
				GenericWretchError<
					| getDiscoverGamesDynamicResponse400
					| getDiscoverGamesDynamicResponse403
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: {
				page?: number;
				limit?: number;
				columns?: string[];
				filters?: object[];
				order?: object;
			},
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<DynamicDataGridResponse>,
					GenericWretchError<
						| getDiscoverGamesDynamicResponse400
						| getDiscoverGamesDynamicResponse403
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<DynamicDataGridResponse>,
			GenericWretchError<
				getDiscoverGamesDynamicResponse400 | getDiscoverGamesDynamicResponse403
			>
		> => {
			const key = [
				"getDiscoverGamesDynamic",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<ApiResponse<DynamicDataGridResponse>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<DynamicDataGridResponse>,
				GenericWretchError<
					| getDiscoverGamesDynamicResponse400
					| getDiscoverGamesDynamicResponse403
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<DynamicDataGridResponse>,
				GenericWretchError<
					| getDiscoverGamesDynamicResponse400
					| getDiscoverGamesDynamicResponse403
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getDiscoverGamesV2FilterOptions = (
	wretch: Http,
	queryClient: QueryClient,
) => {
	const queryFn = async (
		params: { filter: string },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<getDiscoverGamesV2FilterOptionsResponse200>> => {
		const args = z
			.object({
				filter: z
					.enum(["clubManager", "contentManager", "assignee"])
					.describe("Which filter to load options for"),
			})
			.parse(params);
		const { filter } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url("/admin/discover/games/v2/filter-options")
				.query(qs.stringify({ filter }))
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = getDiscoverGamesV2FilterOptionsResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = getDiscoverGamesV2FilterOptionsResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = getDiscoverGamesV2FilterOptionsResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { filter: string },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<getDiscoverGamesV2FilterOptionsResponse200>,
					GenericWretchError<
						| getDiscoverGamesV2FilterOptionsResponse400
						| getDiscoverGamesV2FilterOptionsResponse403
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<getDiscoverGamesV2FilterOptionsResponse200>,
			GenericWretchError<
				| getDiscoverGamesV2FilterOptionsResponse400
				| getDiscoverGamesV2FilterOptionsResponse403
			>
		> => {
			const key = [
				"getDiscoverGamesV2FilterOptions",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<getDiscoverGamesV2FilterOptionsResponse200>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<getDiscoverGamesV2FilterOptionsResponse200>,
				GenericWretchError<
					| getDiscoverGamesV2FilterOptionsResponse400
					| getDiscoverGamesV2FilterOptionsResponse403
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<getDiscoverGamesV2FilterOptionsResponse200>,
				GenericWretchError<
					| getDiscoverGamesV2FilterOptionsResponse400
					| getDiscoverGamesV2FilterOptionsResponse403
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { filter: string },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<getDiscoverGamesV2FilterOptionsResponse200>,
					GenericWretchError<
						| getDiscoverGamesV2FilterOptionsResponse400
						| getDiscoverGamesV2FilterOptionsResponse403
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<getDiscoverGamesV2FilterOptionsResponse200>,
			GenericWretchError<
				| getDiscoverGamesV2FilterOptionsResponse400
				| getDiscoverGamesV2FilterOptionsResponse403
			>
		> => {
			const key = [
				"getDiscoverGamesV2FilterOptions",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<getDiscoverGamesV2FilterOptionsResponse200>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<getDiscoverGamesV2FilterOptionsResponse200>,
				GenericWretchError<
					| getDiscoverGamesV2FilterOptionsResponse400
					| getDiscoverGamesV2FilterOptionsResponse403
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<getDiscoverGamesV2FilterOptionsResponse200>,
				GenericWretchError<
					| getDiscoverGamesV2FilterOptionsResponse400
					| getDiscoverGamesV2FilterOptionsResponse403
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getDiscoverGamesV2AssigneeOptions = (
	wretch: Http,
	queryClient: QueryClient,
) => {
	const queryFn = async (
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<getDiscoverGamesV2AssigneeOptionsResponse200>> => {
		try {
			const res = await wretch
				.options({ signal })
				.url("/admin/discover/games/v2/assignee-options")
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = getDiscoverGamesV2AssigneeOptionsResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 403: {
					err.json = getDiscoverGamesV2AssigneeOptionsResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			options?: Omit<
				UseQueryOptions<
					ApiResponse<getDiscoverGamesV2AssigneeOptionsResponse200>,
					GenericWretchError<getDiscoverGamesV2AssigneeOptionsResponse403>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<getDiscoverGamesV2AssigneeOptionsResponse200>,
			GenericWretchError<getDiscoverGamesV2AssigneeOptionsResponse403>
		> => {
			const key = [
				"getDiscoverGamesV2AssigneeOptions",
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<getDiscoverGamesV2AssigneeOptionsResponse200>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<getDiscoverGamesV2AssigneeOptionsResponse200>,
				GenericWretchError<getDiscoverGamesV2AssigneeOptionsResponse403>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<getDiscoverGamesV2AssigneeOptionsResponse200>,
				GenericWretchError<getDiscoverGamesV2AssigneeOptionsResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<getDiscoverGamesV2AssigneeOptionsResponse200>,
					GenericWretchError<getDiscoverGamesV2AssigneeOptionsResponse403>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<getDiscoverGamesV2AssigneeOptionsResponse200>,
			GenericWretchError<getDiscoverGamesV2AssigneeOptionsResponse403>
		> => {
			const key = [
				"getDiscoverGamesV2AssigneeOptions",
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<getDiscoverGamesV2AssigneeOptionsResponse200>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<getDiscoverGamesV2AssigneeOptionsResponse200>,
				GenericWretchError<getDiscoverGamesV2AssigneeOptionsResponse403>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<getDiscoverGamesV2AssigneeOptionsResponse200>,
				GenericWretchError<getDiscoverGamesV2AssigneeOptionsResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getDiscoverGamesV2ClubMetadataUserOptions = (
	wretch: Http,
	queryClient: QueryClient,
) => {
	const queryFn = async (
		signal?: AbortSignal | undefined,
	): Promise<
		ApiResponse<getDiscoverGamesV2ClubMetadataUserOptionsResponse200>
	> => {
		try {
			const res = await wretch
				.options({ signal })
				.url("/admin/discover/games/v2/club-metadata-user-options")
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data =
						getDiscoverGamesV2ClubMetadataUserOptionsResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 403: {
					err.json =
						getDiscoverGamesV2ClubMetadataUserOptionsResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			options?: Omit<
				UseQueryOptions<
					ApiResponse<getDiscoverGamesV2ClubMetadataUserOptionsResponse200>,
					GenericWretchError<getDiscoverGamesV2ClubMetadataUserOptionsResponse403>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<getDiscoverGamesV2ClubMetadataUserOptionsResponse200>,
			GenericWretchError<getDiscoverGamesV2ClubMetadataUserOptionsResponse403>
		> => {
			const key = [
				"getDiscoverGamesV2ClubMetadataUserOptions",
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<getDiscoverGamesV2ClubMetadataUserOptionsResponse200>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<getDiscoverGamesV2ClubMetadataUserOptionsResponse200>,
				GenericWretchError<getDiscoverGamesV2ClubMetadataUserOptionsResponse403>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<getDiscoverGamesV2ClubMetadataUserOptionsResponse200>,
				GenericWretchError<getDiscoverGamesV2ClubMetadataUserOptionsResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<getDiscoverGamesV2ClubMetadataUserOptionsResponse200>,
					GenericWretchError<getDiscoverGamesV2ClubMetadataUserOptionsResponse403>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<getDiscoverGamesV2ClubMetadataUserOptionsResponse200>,
			GenericWretchError<getDiscoverGamesV2ClubMetadataUserOptionsResponse403>
		> => {
			const key = [
				"getDiscoverGamesV2ClubMetadataUserOptions",
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<getDiscoverGamesV2ClubMetadataUserOptionsResponse200>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<getDiscoverGamesV2ClubMetadataUserOptionsResponse200>,
				GenericWretchError<getDiscoverGamesV2ClubMetadataUserOptionsResponse403>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<getDiscoverGamesV2ClubMetadataUserOptionsResponse200>,
				GenericWretchError<getDiscoverGamesV2ClubMetadataUserOptionsResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getDiscoverGamesV2GameStatusOptions = (
	wretch: Http,
	queryClient: QueryClient,
) => {
	const queryFn = async (
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<getDiscoverGamesV2GameStatusOptionsResponse200>> => {
		try {
			const res = await wretch
				.options({ signal })
				.url("/admin/discover/games/v2/game-status-options")
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data =
						getDiscoverGamesV2GameStatusOptionsResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 403: {
					err.json = getDiscoverGamesV2GameStatusOptionsResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			options?: Omit<
				UseQueryOptions<
					ApiResponse<getDiscoverGamesV2GameStatusOptionsResponse200>,
					GenericWretchError<getDiscoverGamesV2GameStatusOptionsResponse403>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<getDiscoverGamesV2GameStatusOptionsResponse200>,
			GenericWretchError<getDiscoverGamesV2GameStatusOptionsResponse403>
		> => {
			const key = [
				"getDiscoverGamesV2GameStatusOptions",
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<getDiscoverGamesV2GameStatusOptionsResponse200>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<getDiscoverGamesV2GameStatusOptionsResponse200>,
				GenericWretchError<getDiscoverGamesV2GameStatusOptionsResponse403>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<getDiscoverGamesV2GameStatusOptionsResponse200>,
				GenericWretchError<getDiscoverGamesV2GameStatusOptionsResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<getDiscoverGamesV2GameStatusOptionsResponse200>,
					GenericWretchError<getDiscoverGamesV2GameStatusOptionsResponse403>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<getDiscoverGamesV2GameStatusOptionsResponse200>,
			GenericWretchError<getDiscoverGamesV2GameStatusOptionsResponse403>
		> => {
			const key = [
				"getDiscoverGamesV2GameStatusOptions",
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<getDiscoverGamesV2GameStatusOptionsResponse200>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<getDiscoverGamesV2GameStatusOptionsResponse200>,
				GenericWretchError<getDiscoverGamesV2GameStatusOptionsResponse403>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<getDiscoverGamesV2GameStatusOptionsResponse200>,
				GenericWretchError<getDiscoverGamesV2GameStatusOptionsResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getDiscoverGamesV2OwnerFilterOptions = (
	wretch: Http,
	queryClient: QueryClient,
) => {
	const queryFn = async (
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<getDiscoverGamesV2OwnerFilterOptionsResponse200>> => {
		try {
			const res = await wretch
				.options({ signal })
				.url("/admin/discover/games/v2/owner-filter-options")
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data =
						getDiscoverGamesV2OwnerFilterOptionsResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 403: {
					err.json =
						getDiscoverGamesV2OwnerFilterOptionsResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			options?: Omit<
				UseQueryOptions<
					ApiResponse<getDiscoverGamesV2OwnerFilterOptionsResponse200>,
					GenericWretchError<getDiscoverGamesV2OwnerFilterOptionsResponse403>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<getDiscoverGamesV2OwnerFilterOptionsResponse200>,
			GenericWretchError<getDiscoverGamesV2OwnerFilterOptionsResponse403>
		> => {
			const key = [
				"getDiscoverGamesV2OwnerFilterOptions",
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<getDiscoverGamesV2OwnerFilterOptionsResponse200>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<getDiscoverGamesV2OwnerFilterOptionsResponse200>,
				GenericWretchError<getDiscoverGamesV2OwnerFilterOptionsResponse403>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<getDiscoverGamesV2OwnerFilterOptionsResponse200>,
				GenericWretchError<getDiscoverGamesV2OwnerFilterOptionsResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<getDiscoverGamesV2OwnerFilterOptionsResponse200>,
					GenericWretchError<getDiscoverGamesV2OwnerFilterOptionsResponse403>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<getDiscoverGamesV2OwnerFilterOptionsResponse200>,
			GenericWretchError<getDiscoverGamesV2OwnerFilterOptionsResponse403>
		> => {
			const key = [
				"getDiscoverGamesV2OwnerFilterOptions",
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<getDiscoverGamesV2OwnerFilterOptionsResponse200>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<getDiscoverGamesV2OwnerFilterOptionsResponse200>,
				GenericWretchError<getDiscoverGamesV2OwnerFilterOptionsResponse403>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<getDiscoverGamesV2OwnerFilterOptionsResponse200>,
				GenericWretchError<getDiscoverGamesV2OwnerFilterOptionsResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getDiscoverGamesProgramGameFixtures = (
	wretch: Http,
	queryClient: QueryClient,
) => {
	const queryFn = async (
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<getDiscoverGamesProgramGameFixturesResponse200>> => {
		try {
			const res = await wretch
				.options({ signal })
				.url("/admin/discover/games/v2/program-game-fixtures")
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data =
						getDiscoverGamesProgramGameFixturesResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 403: {
					err.json = getDiscoverGamesProgramGameFixturesResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			options?: Omit<
				UseQueryOptions<
					ApiResponse<getDiscoverGamesProgramGameFixturesResponse200>,
					GenericWretchError<getDiscoverGamesProgramGameFixturesResponse403>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<getDiscoverGamesProgramGameFixturesResponse200>,
			GenericWretchError<getDiscoverGamesProgramGameFixturesResponse403>
		> => {
			const key = [
				"getDiscoverGamesProgramGameFixtures",
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<getDiscoverGamesProgramGameFixturesResponse200>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<getDiscoverGamesProgramGameFixturesResponse200>,
				GenericWretchError<getDiscoverGamesProgramGameFixturesResponse403>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<getDiscoverGamesProgramGameFixturesResponse200>,
				GenericWretchError<getDiscoverGamesProgramGameFixturesResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<getDiscoverGamesProgramGameFixturesResponse200>,
					GenericWretchError<getDiscoverGamesProgramGameFixturesResponse403>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<getDiscoverGamesProgramGameFixturesResponse200>,
			GenericWretchError<getDiscoverGamesProgramGameFixturesResponse403>
		> => {
			const key = [
				"getDiscoverGamesProgramGameFixtures",
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<getDiscoverGamesProgramGameFixturesResponse200>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<getDiscoverGamesProgramGameFixturesResponse200>,
				GenericWretchError<getDiscoverGamesProgramGameFixturesResponse403>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<getDiscoverGamesProgramGameFixturesResponse200>,
				GenericWretchError<getDiscoverGamesProgramGameFixturesResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getDiscoverGamesProgramGameClubOptions = (
	wretch: Http,
	queryClient: QueryClient,
) => {
	const queryFn = async (
		params: { search?: string; limit?: number },
		signal?: AbortSignal | undefined,
	): Promise<
		ApiResponse<getDiscoverGamesProgramGameClubOptionsResponse200>
	> => {
		const args = z
			.object({
				search: z
					.string()
					.describe("Optional club name substring filter")
					.optional(),
				limit: z
					.number()
					.int()
					.gte(1)
					.lte(50)
					.describe("Max rows (capped at 50)")
					.optional(),
			})
			.parse(params);
		const { search, limit } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url("/admin/discover/games/v2/program-game-club-options")
				.query(qs.stringify({ search, limit }))
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data =
						getDiscoverGamesProgramGameClubOptionsResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json =
						getDiscoverGamesProgramGameClubOptionsResponse400.parse(json);
					break;
				}
				case 403: {
					err.json =
						getDiscoverGamesProgramGameClubOptionsResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { search?: string; limit?: number },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<getDiscoverGamesProgramGameClubOptionsResponse200>,
					GenericWretchError<
						| getDiscoverGamesProgramGameClubOptionsResponse400
						| getDiscoverGamesProgramGameClubOptionsResponse403
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<getDiscoverGamesProgramGameClubOptionsResponse200>,
			GenericWretchError<
				| getDiscoverGamesProgramGameClubOptionsResponse400
				| getDiscoverGamesProgramGameClubOptionsResponse403
			>
		> => {
			const key = [
				"getDiscoverGamesProgramGameClubOptions",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<getDiscoverGamesProgramGameClubOptionsResponse200>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<getDiscoverGamesProgramGameClubOptionsResponse200>,
				GenericWretchError<
					| getDiscoverGamesProgramGameClubOptionsResponse400
					| getDiscoverGamesProgramGameClubOptionsResponse403
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<getDiscoverGamesProgramGameClubOptionsResponse200>,
				GenericWretchError<
					| getDiscoverGamesProgramGameClubOptionsResponse400
					| getDiscoverGamesProgramGameClubOptionsResponse403
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { search?: string; limit?: number },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<getDiscoverGamesProgramGameClubOptionsResponse200>,
					GenericWretchError<
						| getDiscoverGamesProgramGameClubOptionsResponse400
						| getDiscoverGamesProgramGameClubOptionsResponse403
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<getDiscoverGamesProgramGameClubOptionsResponse200>,
			GenericWretchError<
				| getDiscoverGamesProgramGameClubOptionsResponse400
				| getDiscoverGamesProgramGameClubOptionsResponse403
			>
		> => {
			const key = [
				"getDiscoverGamesProgramGameClubOptions",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<getDiscoverGamesProgramGameClubOptionsResponse200>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<getDiscoverGamesProgramGameClubOptionsResponse200>,
				GenericWretchError<
					| getDiscoverGamesProgramGameClubOptionsResponse400
					| getDiscoverGamesProgramGameClubOptionsResponse403
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<getDiscoverGamesProgramGameClubOptionsResponse200>,
				GenericWretchError<
					| getDiscoverGamesProgramGameClubOptionsResponse400
					| getDiscoverGamesProgramGameClubOptionsResponse403
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getDiscoverGamesProgramGameNationalLeagues = (
	wretch: Http,
	queryClient: QueryClient,
) => {
	const queryFn = async (
		params: { homeClubId: number; awayClubId?: number },
		signal?: AbortSignal | undefined,
	): Promise<
		ApiResponse<getDiscoverGamesProgramGameNationalLeaguesResponse200>
	> => {
		const args = z
			.object({
				homeClubId: z
					.number()
					.int()
					.describe("Home club id (its country is included in the filter)"),
				awayClubId: z
					.number()
					.int()
					.describe(
						"Away club id (its country is included in the filter, when provided)",
					)
					.optional(),
			})
			.parse(params);
		const { homeClubId, awayClubId } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url("/admin/discover/games/v2/program-game-national-leagues")
				.query(qs.stringify({ homeClubId, awayClubId }))
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data =
						getDiscoverGamesProgramGameNationalLeaguesResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json =
						getDiscoverGamesProgramGameNationalLeaguesResponse400.parse(json);
					break;
				}
				case 403: {
					err.json =
						getDiscoverGamesProgramGameNationalLeaguesResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { homeClubId: number; awayClubId?: number },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<getDiscoverGamesProgramGameNationalLeaguesResponse200>,
					GenericWretchError<
						| getDiscoverGamesProgramGameNationalLeaguesResponse400
						| getDiscoverGamesProgramGameNationalLeaguesResponse403
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<getDiscoverGamesProgramGameNationalLeaguesResponse200>,
			GenericWretchError<
				| getDiscoverGamesProgramGameNationalLeaguesResponse400
				| getDiscoverGamesProgramGameNationalLeaguesResponse403
			>
		> => {
			const key = [
				"getDiscoverGamesProgramGameNationalLeagues",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<getDiscoverGamesProgramGameNationalLeaguesResponse200>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<getDiscoverGamesProgramGameNationalLeaguesResponse200>,
				GenericWretchError<
					| getDiscoverGamesProgramGameNationalLeaguesResponse400
					| getDiscoverGamesProgramGameNationalLeaguesResponse403
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<getDiscoverGamesProgramGameNationalLeaguesResponse200>,
				GenericWretchError<
					| getDiscoverGamesProgramGameNationalLeaguesResponse400
					| getDiscoverGamesProgramGameNationalLeaguesResponse403
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { homeClubId: number; awayClubId?: number },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<getDiscoverGamesProgramGameNationalLeaguesResponse200>,
					GenericWretchError<
						| getDiscoverGamesProgramGameNationalLeaguesResponse400
						| getDiscoverGamesProgramGameNationalLeaguesResponse403
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<getDiscoverGamesProgramGameNationalLeaguesResponse200>,
			GenericWretchError<
				| getDiscoverGamesProgramGameNationalLeaguesResponse400
				| getDiscoverGamesProgramGameNationalLeaguesResponse403
			>
		> => {
			const key = [
				"getDiscoverGamesProgramGameNationalLeagues",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<getDiscoverGamesProgramGameNationalLeaguesResponse200>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<getDiscoverGamesProgramGameNationalLeaguesResponse200>,
				GenericWretchError<
					| getDiscoverGamesProgramGameNationalLeaguesResponse400
					| getDiscoverGamesProgramGameNationalLeaguesResponse403
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<getDiscoverGamesProgramGameNationalLeaguesResponse200>,
				GenericWretchError<
					| getDiscoverGamesProgramGameNationalLeaguesResponse400
					| getDiscoverGamesProgramGameNationalLeaguesResponse403
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const postDiscoverProgramGame = (wretch: Http) => {
	const mutation = async (params: { body: DiscoverProgramGamePostRequest }) => {
		const args = z.object({ body: z.any() }).parse(params);
		const { body } = args;

		try {
			const res = await wretch
				.url("/admin/discover/games/v2/program-game")
				.json(body)
				.post()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = postDiscoverProgramGameResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = postDiscoverProgramGameResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = postDiscoverProgramGameResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<postDiscoverProgramGameResponse200> | undefined,
					GenericWretchError<
						| postDiscoverProgramGameResponse400
						| postDiscoverProgramGameResponse403
					>,
					{ body: DiscoverProgramGamePostRequest }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<postDiscoverProgramGameResponse200> | undefined,
				GenericWretchError<
					| postDiscoverProgramGameResponse400
					| postDiscoverProgramGameResponse403
				>,
				{ body: DiscoverProgramGamePostRequest }
			>(mutation, options || {});
		},
	};
};

const getDiscoverGamesBulkEligibility = (
	wretch: Http,
	queryClient: QueryClient,
) => {
	const queryFn = async (
		params: { ids: integer[] },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<DiscoverGamesBulkEligibilityResponse>> => {
		const args = z
			.object({
				ids: z
					.array(z.number().int())
					.describe(
						"Selected game ids to evaluate. Pass repeated as ids[]=1&ids[]=2.",
					),
			})
			.parse(params);
		const { ids } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url("/admin/discover/games/v2/bulk-eligibility")
				.query(qs.stringify({ ids }))
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = DiscoverGamesBulkEligibilityResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = getDiscoverGamesBulkEligibilityResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = getDiscoverGamesBulkEligibilityResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { ids: integer[] },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<DiscoverGamesBulkEligibilityResponse>,
					GenericWretchError<
						| getDiscoverGamesBulkEligibilityResponse400
						| getDiscoverGamesBulkEligibilityResponse403
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<DiscoverGamesBulkEligibilityResponse>,
			GenericWretchError<
				| getDiscoverGamesBulkEligibilityResponse400
				| getDiscoverGamesBulkEligibilityResponse403
			>
		> => {
			const key = [
				"getDiscoverGamesBulkEligibility",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<DiscoverGamesBulkEligibilityResponse>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<DiscoverGamesBulkEligibilityResponse>,
				GenericWretchError<
					| getDiscoverGamesBulkEligibilityResponse400
					| getDiscoverGamesBulkEligibilityResponse403
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<DiscoverGamesBulkEligibilityResponse>,
				GenericWretchError<
					| getDiscoverGamesBulkEligibilityResponse400
					| getDiscoverGamesBulkEligibilityResponse403
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { ids: integer[] },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<DiscoverGamesBulkEligibilityResponse>,
					GenericWretchError<
						| getDiscoverGamesBulkEligibilityResponse400
						| getDiscoverGamesBulkEligibilityResponse403
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<DiscoverGamesBulkEligibilityResponse>,
			GenericWretchError<
				| getDiscoverGamesBulkEligibilityResponse400
				| getDiscoverGamesBulkEligibilityResponse403
			>
		> => {
			const key = [
				"getDiscoverGamesBulkEligibility",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<DiscoverGamesBulkEligibilityResponse>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<DiscoverGamesBulkEligibilityResponse>,
				GenericWretchError<
					| getDiscoverGamesBulkEligibilityResponse400
					| getDiscoverGamesBulkEligibilityResponse403
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<DiscoverGamesBulkEligibilityResponse>,
				GenericWretchError<
					| getDiscoverGamesBulkEligibilityResponse400
					| getDiscoverGamesBulkEligibilityResponse403
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const postDiscoverGamesBulkPatch = (wretch: Http) => {
	const mutation = async (params: { body: DiscoverGamesBulkPatchRequest }) => {
		const args = z.object({ body: z.any() }).parse(params);
		const { body } = args;

		try {
			const res = await wretch
				.url("/admin/discover/games/v2/bulk-patch")
				.json(body)
				.post()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = DiscoverGamesBulkPatchResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = postDiscoverGamesBulkPatchResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = postDiscoverGamesBulkPatchResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<DiscoverGamesBulkPatchResponse> | undefined,
					GenericWretchError<
						| postDiscoverGamesBulkPatchResponse400
						| postDiscoverGamesBulkPatchResponse403
					>,
					{ body: DiscoverGamesBulkPatchRequest }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<DiscoverGamesBulkPatchResponse> | undefined,
				GenericWretchError<
					| postDiscoverGamesBulkPatchResponse400
					| postDiscoverGamesBulkPatchResponse403
				>,
				{ body: DiscoverGamesBulkPatchRequest }
			>(mutation, options || {});
		},
	};
};

const getDiscoverGame = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: { id: number },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<DiscoverGameResponse>> => {
		const args = z.object({ id: z.number().int() }).parse(params);
		const { id } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/admin/discover/game/${id}`)
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = DiscoverGameResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = getDiscoverGameResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = getDiscoverGameResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = getDiscoverGameResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { id: number },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<DiscoverGameResponse>,
					GenericWretchError<
						| getDiscoverGameResponse400
						| getDiscoverGameResponse403
						| getDiscoverGameResponse404
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<DiscoverGameResponse>,
			GenericWretchError<
				| getDiscoverGameResponse400
				| getDiscoverGameResponse403
				| getDiscoverGameResponse404
			>
		> => {
			const key = ["getDiscoverGame", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<DiscoverGameResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<DiscoverGameResponse>,
				GenericWretchError<
					| getDiscoverGameResponse400
					| getDiscoverGameResponse403
					| getDiscoverGameResponse404
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<DiscoverGameResponse>,
				GenericWretchError<
					| getDiscoverGameResponse400
					| getDiscoverGameResponse403
					| getDiscoverGameResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { id: number },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<DiscoverGameResponse>,
					GenericWretchError<
						| getDiscoverGameResponse400
						| getDiscoverGameResponse403
						| getDiscoverGameResponse404
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<DiscoverGameResponse>,
			GenericWretchError<
				| getDiscoverGameResponse400
				| getDiscoverGameResponse403
				| getDiscoverGameResponse404
			>
		> => {
			const key = ["getDiscoverGame", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<DiscoverGameResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<DiscoverGameResponse>,
				GenericWretchError<
					| getDiscoverGameResponse400
					| getDiscoverGameResponse403
					| getDiscoverGameResponse404
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<DiscoverGameResponse>,
				GenericWretchError<
					| getDiscoverGameResponse400
					| getDiscoverGameResponse403
					| getDiscoverGameResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const patchDiscoverGame = (wretch: Http) => {
	const mutation = async (params: {
		id: number;
		body: DiscoverGamePatchRequest;
	}) => {
		const args = z
			.object({ id: z.number().int(), body: z.any() })
			.parse(params);
		const { id, body } = args;

		try {
			const res = await wretch
				.url(`/admin/discover/game/${id}`)
				.json(body)
				.patch()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = patchDiscoverGameResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = patchDiscoverGameResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = patchDiscoverGameResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = patchDiscoverGameResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<patchDiscoverGameResponse200> | undefined,
					GenericWretchError<
						| patchDiscoverGameResponse400
						| patchDiscoverGameResponse403
						| patchDiscoverGameResponse404
					>,
					{ id: number; body: DiscoverGamePatchRequest }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<patchDiscoverGameResponse200> | undefined,
				GenericWretchError<
					| patchDiscoverGameResponse400
					| patchDiscoverGameResponse403
					| patchDiscoverGameResponse404
				>,
				{ id: number; body: DiscoverGamePatchRequest }
			>(mutation, options || {});
		},
	};
};

const getDiscoverGameV2 = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: { id: number },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<DiscoverGameV2Response>> => {
		const args = z.object({ id: z.number().int() }).parse(params);
		const { id } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/admin/discover/game/v2/${id}`)
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = DiscoverGameV2Response.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 404: {
					err.json = getDiscoverGameV2Response404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { id: number },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<DiscoverGameV2Response>,
					GenericWretchError<getDiscoverGameV2Response404>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<DiscoverGameV2Response>,
			GenericWretchError<getDiscoverGameV2Response404>
		> => {
			const key = ["getDiscoverGameV2", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<DiscoverGameV2Response>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<DiscoverGameV2Response>,
				GenericWretchError<getDiscoverGameV2Response404>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<DiscoverGameV2Response>,
				GenericWretchError<getDiscoverGameV2Response404>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { id: number },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<DiscoverGameV2Response>,
					GenericWretchError<getDiscoverGameV2Response404>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<DiscoverGameV2Response>,
			GenericWretchError<getDiscoverGameV2Response404>
		> => {
			const key = ["getDiscoverGameV2", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<DiscoverGameV2Response>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<DiscoverGameV2Response>,
				GenericWretchError<getDiscoverGameV2Response404>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<DiscoverGameV2Response>,
				GenericWretchError<getDiscoverGameV2Response404>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getDiscoverGameMetadataOptions = (
	wretch: Http,
	queryClient: QueryClient,
) => {
	const queryFn = async (
		params: { id: string },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<DiscoverGameMetadataOptionsResponse>> => {
		const args = z.object({ id: z.string() }).parse(params);
		const { id } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/admin/discover/game/${id}/metadata-options`)
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = DiscoverGameMetadataOptionsResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 403: {
					err.json = getDiscoverGameMetadataOptionsResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { id: string },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<DiscoverGameMetadataOptionsResponse>,
					GenericWretchError<getDiscoverGameMetadataOptionsResponse403>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<DiscoverGameMetadataOptionsResponse>,
			GenericWretchError<getDiscoverGameMetadataOptionsResponse403>
		> => {
			const key = [
				"getDiscoverGameMetadataOptions",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<DiscoverGameMetadataOptionsResponse>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<DiscoverGameMetadataOptionsResponse>,
				GenericWretchError<getDiscoverGameMetadataOptionsResponse403>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<DiscoverGameMetadataOptionsResponse>,
				GenericWretchError<getDiscoverGameMetadataOptionsResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { id: string },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<DiscoverGameMetadataOptionsResponse>,
					GenericWretchError<getDiscoverGameMetadataOptionsResponse403>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<DiscoverGameMetadataOptionsResponse>,
			GenericWretchError<getDiscoverGameMetadataOptionsResponse403>
		> => {
			const key = [
				"getDiscoverGameMetadataOptions",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<DiscoverGameMetadataOptionsResponse>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<DiscoverGameMetadataOptionsResponse>,
				GenericWretchError<getDiscoverGameMetadataOptionsResponse403>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<DiscoverGameMetadataOptionsResponse>,
				GenericWretchError<getDiscoverGameMetadataOptionsResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const approveGameAnalysis = (wretch: Http) => {
	const mutation = async (params: { id: number }) => {
		const args = z.object({ id: z.number().int() }).parse(params);
		const { id } = args;

		try {
			const res = await wretch
				.url(`/admin/discover/game/${id}/analysis/approve`)
				.patch()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = approveGameAnalysisResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = approveGameAnalysisResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = approveGameAnalysisResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = approveGameAnalysisResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<approveGameAnalysisResponse200> | undefined,
					GenericWretchError<
						| approveGameAnalysisResponse400
						| approveGameAnalysisResponse403
						| approveGameAnalysisResponse404
					>,
					{ id: number }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<approveGameAnalysisResponse200> | undefined,
				GenericWretchError<
					| approveGameAnalysisResponse400
					| approveGameAnalysisResponse403
					| approveGameAnalysisResponse404
				>,
				{ id: number }
			>(mutation, options || {});
		},
	};
};

const denyGameAnalysis = (wretch: Http) => {
	const mutation = async (params: {
		id: number;
		body: denyGameAnalysisRequestBody;
	}) => {
		const args = z
			.object({ id: z.number().int(), body: z.any() })
			.parse(params);
		const { id, body } = args;

		try {
			const res = await wretch
				.url(`/admin/discover/game/${id}/analysis/deny`)
				.json(body)
				.patch()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = denyGameAnalysisResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = denyGameAnalysisResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = denyGameAnalysisResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = denyGameAnalysisResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<denyGameAnalysisResponse200> | undefined,
					GenericWretchError<
						| denyGameAnalysisResponse400
						| denyGameAnalysisResponse403
						| denyGameAnalysisResponse404
					>,
					{ id: number; body: denyGameAnalysisRequestBody }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<denyGameAnalysisResponse200> | undefined,
				GenericWretchError<
					| denyGameAnalysisResponse400
					| denyGameAnalysisResponse403
					| denyGameAnalysisResponse404
				>,
				{ id: number; body: denyGameAnalysisRequestBody }
			>(mutation, options || {});
		},
	};
};

const getDiscoverGameHistoryNotes = (
	wretch: Http,
	queryClient: QueryClient,
) => {
	const queryFn = async (
		params: { id: number; type?: string; page?: number; limit?: number },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<GameHistoryNotesListResponse>> => {
		const args = z
			.object({
				id: z.number().int(),
				type: z.enum(["all", "internal", "log"]).default("all"),
				page: z.number().int().default(1),
				limit: z.number().int().default(10),
			})
			.parse(params);
		const { id, type, page, limit } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/admin/discover/game/${id}/history/notes`)
				.query(qs.stringify({ type, page, limit }))
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = GameHistoryNotesListResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { id: number; type?: string; page?: number; limit?: number },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<GameHistoryNotesListResponse>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<GameHistoryNotesListResponse>,
			GenericWretchError<null>
		> => {
			const key = [
				"getDiscoverGameHistoryNotes",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<ApiResponse<GameHistoryNotesListResponse>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<GameHistoryNotesListResponse>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<GameHistoryNotesListResponse>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { id: number; type?: string; page?: number; limit?: number },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<GameHistoryNotesListResponse>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<GameHistoryNotesListResponse>,
			GenericWretchError<null>
		> => {
			const key = [
				"getDiscoverGameHistoryNotes",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<ApiResponse<GameHistoryNotesListResponse>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<GameHistoryNotesListResponse>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<GameHistoryNotesListResponse>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const postDiscoverGameHistoryNote = (wretch: Http) => {
	const mutation = async (params: {
		id: number;
		body: GameHistoryNotePostRequest;
	}) => {
		const args = z
			.object({ id: z.number().int(), body: z.any() })
			.parse(params);
		const { id, body } = args;

		try {
			const res = await wretch
				.url(`/admin/discover/game/${id}/history/notes`)
				.json(body)
				.post()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 201: {
					const data = postDiscoverGameHistoryNoteResponse201.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<postDiscoverGameHistoryNoteResponse201> | undefined,
					GenericWretchError<null>,
					{ id: number; body: GameHistoryNotePostRequest }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<postDiscoverGameHistoryNoteResponse201> | undefined,
				GenericWretchError<null>,
				{ id: number; body: GameHistoryNotePostRequest }
			>(mutation, options || {});
		},
	};
};

const deleteDiscoverGameHistoryNote = (wretch: Http) => {
	const mutation = async (params: { noteId: number }) => {
		const args = z.object({ noteId: z.number().int() }).parse(params);
		const { noteId } = args;

		try {
			const res = await wretch
				.url(`/admin/discover/game/history/notes/${noteId}`)
				.delete()
				.res();
			switch (res.status) {
				case 204: {
					return {
						status: res.status,
						data: null,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<null> | undefined,
					GenericWretchError<null>,
					{ noteId: number }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<null> | undefined,
				GenericWretchError<null>,
				{ noteId: number }
			>(mutation, options || {});
		},
	};
};

const patchDiscoverGameHistoryNote = (wretch: Http) => {
	const mutation = async (params: {
		noteId: number;
		body: GameHistoryNotePatchRequest;
	}) => {
		const args = z
			.object({ noteId: z.number().int(), body: z.any() })
			.parse(params);
		const { noteId, body } = args;

		try {
			const res = await wretch
				.url(`/admin/discover/game/history/notes/${noteId}`)
				.json(body)
				.patch()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = GameHistoryNoteResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<GameHistoryNoteResponse> | undefined,
					GenericWretchError<null>,
					{ noteId: number; body: GameHistoryNotePatchRequest }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<GameHistoryNoteResponse> | undefined,
				GenericWretchError<null>,
				{ noteId: number; body: GameHistoryNotePatchRequest }
			>(mutation, options || {});
		},
	};
};

const postDiscoverGameLink = (wretch: Http) => {
	const mutation = async (params: {
		id: number;
		body: DiscoverGameLinkCreateRequest;
	}) => {
		const args = z
			.object({ id: z.number().int(), body: z.any() })
			.parse(params);
		const { id, body } = args;

		try {
			const res = await wretch
				.url(`/admin/discover/game/${id}/links`)
				.json(body)
				.post()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 201: {
					const data = postDiscoverGameLinkResponse201.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<postDiscoverGameLinkResponse201> | undefined,
					GenericWretchError<null>,
					{ id: number; body: DiscoverGameLinkCreateRequest }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<postDiscoverGameLinkResponse201> | undefined,
				GenericWretchError<null>,
				{ id: number; body: DiscoverGameLinkCreateRequest }
			>(mutation, options || {});
		},
	};
};

const deleteDiscoverGameLink = (wretch: Http) => {
	const mutation = async (params: { linkId: number }) => {
		const args = z.object({ linkId: z.number().int() }).parse(params);
		const { linkId } = args;

		try {
			const res = await wretch
				.url(`/admin/discover/game/links/${linkId}`)
				.delete()
				.res();
			switch (res.status) {
				case 204: {
					return {
						status: res.status,
						data: null,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<null> | undefined,
					GenericWretchError<null>,
					{ linkId: number }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<null> | undefined,
				GenericWretchError<null>,
				{ linkId: number }
			>(mutation, options || {});
		},
	};
};

const patchDiscoverGameLink = (wretch: Http) => {
	const mutation = async (params: {
		linkId: number;
		body: patchDiscoverGameLinkRequestBody;
	}) => {
		const args = z
			.object({ linkId: z.number().int(), body: z.any() })
			.parse(params);
		const { linkId, body } = args;

		try {
			const res = await wretch
				.url(`/admin/discover/game/links/${linkId}`)
				.json(body)
				.patch()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = DiscoverGameLinkItemResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<DiscoverGameLinkItemResponse> | undefined,
					GenericWretchError<null>,
					{ linkId: number; body: patchDiscoverGameLinkRequestBody }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<DiscoverGameLinkItemResponse> | undefined,
				GenericWretchError<null>,
				{ linkId: number; body: patchDiscoverGameLinkRequestBody }
			>(mutation, options || {});
		},
	};
};

const getDiscoverPlayersData = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: {
			page?: number;
			limit?: number;
			filters?: object[];
			order?: object;
		},
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<DiscoverPlayersDataResponse>> => {
		const args = z
			.object({
				page: z.number().int().optional(),
				limit: z.number().int().optional(),
				filters: z
					.array(
						z.object({
							type: z
								.enum([
									"exact",
									"search",
									"boolean",
									"exists",
									"inarray",
									"datetimerange",
									"numberrange",
								])
								.describe("Type of filter")
								.optional(),
							field: z
								.enum([
									"id",
									"playerData",
									"gender",
									"name",
									"firstName",
									"lastName",
									"membershipName",
									"club",
									"team",
									"playerType",
									"clubType",
									"position",
									"isVip",
								])
								.describe("Property to filter")
								.optional(),
							value: z
								.any()
								.superRefine((x, ctx) => {
									const schemas = [
										z.string(),
										z.number().int(),
										z.boolean(),
										z.object({
											start: z.string().datetime({ offset: true }).optional(),
											end: z.string().datetime({ offset: true }).optional(),
										}),
									];
									const errors = schemas.reduce<z.ZodError[]>(
										(errors, schema) =>
											((result) =>
												result.error ? [...errors, result.error] : errors)(
												schema.safeParse(x),
											),
										[],
									);
									if (schemas.length - errors.length !== 1) {
										ctx.addIssue({
											path: ctx.path,
											code: "invalid_union",
											unionErrors: errors,
											message: "Invalid input: Should pass single schema",
										});
									}
								})
								.describe(
									"Filter value, can be a string, integer, date or a date range",
								)
								.optional(),
						}),
					)
					.optional(),
				order: z
					.object({
						field: z
							.enum(["id", "firstName", "lastName"])
							.describe("Sorting field")
							.optional(),
						direction: z
							.enum(["ASC", "DESC"])
							.describe("Sorting direction")
							.optional(),
					})
					.optional(),
			})
			.parse(params);
		const { page, limit, filters, order } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url("/admin/discover/players-data")
				.query(qs.stringify({ page, limit, filters, order }))
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = DiscoverPlayersDataResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = getDiscoverPlayersDataResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = getDiscoverPlayersDataResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: {
				page?: number;
				limit?: number;
				filters?: object[];
				order?: object;
			},
			options?: Omit<
				UseQueryOptions<
					ApiResponse<DiscoverPlayersDataResponse>,
					GenericWretchError<
						| getDiscoverPlayersDataResponse400
						| getDiscoverPlayersDataResponse403
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<DiscoverPlayersDataResponse>,
			GenericWretchError<
				getDiscoverPlayersDataResponse400 | getDiscoverPlayersDataResponse403
			>
		> => {
			const key = [
				"getDiscoverPlayersData",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<ApiResponse<DiscoverPlayersDataResponse>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<DiscoverPlayersDataResponse>,
				GenericWretchError<
					getDiscoverPlayersDataResponse400 | getDiscoverPlayersDataResponse403
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<DiscoverPlayersDataResponse>,
				GenericWretchError<
					getDiscoverPlayersDataResponse400 | getDiscoverPlayersDataResponse403
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: {
				page?: number;
				limit?: number;
				filters?: object[];
				order?: object;
			},
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<DiscoverPlayersDataResponse>,
					GenericWretchError<
						| getDiscoverPlayersDataResponse400
						| getDiscoverPlayersDataResponse403
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<DiscoverPlayersDataResponse>,
			GenericWretchError<
				getDiscoverPlayersDataResponse400 | getDiscoverPlayersDataResponse403
			>
		> => {
			const key = [
				"getDiscoverPlayersData",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<ApiResponse<DiscoverPlayersDataResponse>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<DiscoverPlayersDataResponse>,
				GenericWretchError<
					getDiscoverPlayersDataResponse400 | getDiscoverPlayersDataResponse403
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<DiscoverPlayersDataResponse>,
				GenericWretchError<
					getDiscoverPlayersDataResponse400 | getDiscoverPlayersDataResponse403
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getDiscoverPlayerData = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: { id: number },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<DiscoverPlayerDataResponse>> => {
		const args = z.object({ id: z.number().int() }).parse(params);
		const { id } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/admin/discover/player-data/${id}`)
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = DiscoverPlayerDataResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = getDiscoverPlayerDataResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = getDiscoverPlayerDataResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = getDiscoverPlayerDataResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { id: number },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<DiscoverPlayerDataResponse>,
					GenericWretchError<
						| getDiscoverPlayerDataResponse400
						| getDiscoverPlayerDataResponse403
						| getDiscoverPlayerDataResponse404
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<DiscoverPlayerDataResponse>,
			GenericWretchError<
				| getDiscoverPlayerDataResponse400
				| getDiscoverPlayerDataResponse403
				| getDiscoverPlayerDataResponse404
			>
		> => {
			const key = [
				"getDiscoverPlayerData",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<ApiResponse<DiscoverPlayerDataResponse>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<DiscoverPlayerDataResponse>,
				GenericWretchError<
					| getDiscoverPlayerDataResponse400
					| getDiscoverPlayerDataResponse403
					| getDiscoverPlayerDataResponse404
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<DiscoverPlayerDataResponse>,
				GenericWretchError<
					| getDiscoverPlayerDataResponse400
					| getDiscoverPlayerDataResponse403
					| getDiscoverPlayerDataResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { id: number },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<DiscoverPlayerDataResponse>,
					GenericWretchError<
						| getDiscoverPlayerDataResponse400
						| getDiscoverPlayerDataResponse403
						| getDiscoverPlayerDataResponse404
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<DiscoverPlayerDataResponse>,
			GenericWretchError<
				| getDiscoverPlayerDataResponse400
				| getDiscoverPlayerDataResponse403
				| getDiscoverPlayerDataResponse404
			>
		> => {
			const key = [
				"getDiscoverPlayerData",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<ApiResponse<DiscoverPlayerDataResponse>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<DiscoverPlayerDataResponse>,
				GenericWretchError<
					| getDiscoverPlayerDataResponse400
					| getDiscoverPlayerDataResponse403
					| getDiscoverPlayerDataResponse404
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<DiscoverPlayerDataResponse>,
				GenericWretchError<
					| getDiscoverPlayerDataResponse400
					| getDiscoverPlayerDataResponse403
					| getDiscoverPlayerDataResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const patchDiscoverPlayerData = (wretch: Http) => {
	const mutation = async (params: {
		id: number;
		body: DiscoverPlayerDataPatchRequest;
	}) => {
		const args = z
			.object({ id: z.number().int(), body: z.any() })
			.parse(params);
		const { id, body } = args;

		try {
			const res = await wretch
				.url(`/admin/discover/player-data/${id}`)
				.json(body)
				.patch()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = patchDiscoverPlayerDataResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = patchDiscoverPlayerDataResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = patchDiscoverPlayerDataResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = patchDiscoverPlayerDataResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<patchDiscoverPlayerDataResponse200> | undefined,
					GenericWretchError<
						| patchDiscoverPlayerDataResponse400
						| patchDiscoverPlayerDataResponse403
						| patchDiscoverPlayerDataResponse404
					>,
					{ id: number; body: DiscoverPlayerDataPatchRequest }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<patchDiscoverPlayerDataResponse200> | undefined,
				GenericWretchError<
					| patchDiscoverPlayerDataResponse400
					| patchDiscoverPlayerDataResponse403
					| patchDiscoverPlayerDataResponse404
				>,
				{ id: number; body: DiscoverPlayerDataPatchRequest }
			>(mutation, options || {});
		},
	};
};

const postDiscoverPlayerDataUpdateEtl = (wretch: Http) => {
	const mutation = async (params: { id: number }) => {
		const args = z.object({ id: z.number().int() }).parse(params);
		const { id } = args;

		try {
			const res = await wretch
				.url(`/admin/discover/player-data/${id}/update-etl`)
				.post()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = postDiscoverPlayerDataUpdateEtlResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = postDiscoverPlayerDataUpdateEtlResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = postDiscoverPlayerDataUpdateEtlResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = postDiscoverPlayerDataUpdateEtlResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<postDiscoverPlayerDataUpdateEtlResponse200> | undefined,
					GenericWretchError<
						| postDiscoverPlayerDataUpdateEtlResponse400
						| postDiscoverPlayerDataUpdateEtlResponse403
						| postDiscoverPlayerDataUpdateEtlResponse404
					>,
					{ id: number }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<postDiscoverPlayerDataUpdateEtlResponse200> | undefined,
				GenericWretchError<
					| postDiscoverPlayerDataUpdateEtlResponse400
					| postDiscoverPlayerDataUpdateEtlResponse403
					| postDiscoverPlayerDataUpdateEtlResponse404
				>,
				{ id: number }
			>(mutation, options || {});
		},
	};
};

const getDiscoverPlayers = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: {
			page?: number;
			limit?: number;
			filters?: object[];
			order?: object;
		},
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<DiscoverPlayersResponse>> => {
		const args = z
			.object({
				page: z.number().int().optional(),
				limit: z.number().int().optional(),
				filters: z
					.array(
						z.object({
							type: z
								.enum([
									"exact",
									"search",
									"boolean",
									"exists",
									"inarray",
									"datetimerange",
									"numberrange",
								])
								.describe("Type of filter")
								.optional(),
							field: z
								.enum([
									"id",
									"playerData",
									"name",
									"club",
									"team",
									"teamId",
									"entityType",
									"position",
									"numberSign",
									"isDummy",
								])
								.describe("Property to filter")
								.optional(),
							value: z
								.any()
								.superRefine((x, ctx) => {
									const schemas = [
										z.string(),
										z.number().int(),
										z.boolean(),
										z.object({
											start: z.string().datetime({ offset: true }).optional(),
											end: z.string().datetime({ offset: true }).optional(),
										}),
									];
									const errors = schemas.reduce<z.ZodError[]>(
										(errors, schema) =>
											((result) =>
												result.error ? [...errors, result.error] : errors)(
												schema.safeParse(x),
											),
										[],
									);
									if (schemas.length - errors.length !== 1) {
										ctx.addIssue({
											path: ctx.path,
											code: "invalid_union",
											unionErrors: errors,
											message: "Invalid input: Should pass single schema",
										});
									}
								})
								.describe(
									"Filter value, can be a string, integer, date or a date range",
								)
								.optional(),
						}),
					)
					.optional(),
				order: z
					.object({
						field: z
							.enum(["id", "playerData", "createdAt", "name", "numberSign"])
							.describe("Sorting field")
							.optional(),
						direction: z
							.enum(["ASC", "DESC"])
							.describe("Sorting direction")
							.optional(),
					})
					.optional(),
			})
			.parse(params);
		const { page, limit, filters, order } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url("/admin/discover/players")
				.query(qs.stringify({ page, limit, filters, order }))
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = DiscoverPlayersResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = getDiscoverPlayersResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = getDiscoverPlayersResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: {
				page?: number;
				limit?: number;
				filters?: object[];
				order?: object;
			},
			options?: Omit<
				UseQueryOptions<
					ApiResponse<DiscoverPlayersResponse>,
					GenericWretchError<
						getDiscoverPlayersResponse400 | getDiscoverPlayersResponse403
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<DiscoverPlayersResponse>,
			GenericWretchError<
				getDiscoverPlayersResponse400 | getDiscoverPlayersResponse403
			>
		> => {
			const key = ["getDiscoverPlayers", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<DiscoverPlayersResponse>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<DiscoverPlayersResponse>,
				GenericWretchError<
					getDiscoverPlayersResponse400 | getDiscoverPlayersResponse403
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<DiscoverPlayersResponse>,
				GenericWretchError<
					getDiscoverPlayersResponse400 | getDiscoverPlayersResponse403
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: {
				page?: number;
				limit?: number;
				filters?: object[];
				order?: object;
			},
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<DiscoverPlayersResponse>,
					GenericWretchError<
						getDiscoverPlayersResponse400 | getDiscoverPlayersResponse403
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<DiscoverPlayersResponse>,
			GenericWretchError<
				getDiscoverPlayersResponse400 | getDiscoverPlayersResponse403
			>
		> => {
			const key = ["getDiscoverPlayers", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<DiscoverPlayersResponse>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<DiscoverPlayersResponse>,
				GenericWretchError<
					getDiscoverPlayersResponse400 | getDiscoverPlayersResponse403
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<DiscoverPlayersResponse>,
				GenericWretchError<
					getDiscoverPlayersResponse400 | getDiscoverPlayersResponse403
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getDiscoverPlayer = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: { id: number },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<DiscoverPlayerResponse>> => {
		const args = z.object({ id: z.number().int() }).parse(params);
		const { id } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/admin/discover/player/${id}`)
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = DiscoverPlayerResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = getDiscoverPlayerResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = getDiscoverPlayerResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = getDiscoverPlayerResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { id: number },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<DiscoverPlayerResponse>,
					GenericWretchError<
						| getDiscoverPlayerResponse400
						| getDiscoverPlayerResponse403
						| getDiscoverPlayerResponse404
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<DiscoverPlayerResponse>,
			GenericWretchError<
				| getDiscoverPlayerResponse400
				| getDiscoverPlayerResponse403
				| getDiscoverPlayerResponse404
			>
		> => {
			const key = ["getDiscoverPlayer", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<DiscoverPlayerResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<DiscoverPlayerResponse>,
				GenericWretchError<
					| getDiscoverPlayerResponse400
					| getDiscoverPlayerResponse403
					| getDiscoverPlayerResponse404
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<DiscoverPlayerResponse>,
				GenericWretchError<
					| getDiscoverPlayerResponse400
					| getDiscoverPlayerResponse403
					| getDiscoverPlayerResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { id: number },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<DiscoverPlayerResponse>,
					GenericWretchError<
						| getDiscoverPlayerResponse400
						| getDiscoverPlayerResponse403
						| getDiscoverPlayerResponse404
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<DiscoverPlayerResponse>,
			GenericWretchError<
				| getDiscoverPlayerResponse400
				| getDiscoverPlayerResponse403
				| getDiscoverPlayerResponse404
			>
		> => {
			const key = ["getDiscoverPlayer", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<DiscoverPlayerResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<DiscoverPlayerResponse>,
				GenericWretchError<
					| getDiscoverPlayerResponse400
					| getDiscoverPlayerResponse403
					| getDiscoverPlayerResponse404
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<DiscoverPlayerResponse>,
				GenericWretchError<
					| getDiscoverPlayerResponse400
					| getDiscoverPlayerResponse403
					| getDiscoverPlayerResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const patchDiscoverPlayer = (wretch: Http) => {
	const mutation = async (params: {
		id: number;
		body: DiscoverPlayerPatchRequest;
	}) => {
		const args = z
			.object({ id: z.number().int(), body: z.any() })
			.parse(params);
		const { id, body } = args;

		try {
			const res = await wretch
				.url(`/admin/discover/player/${id}`)
				.json(body)
				.patch()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = patchDiscoverPlayerResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = patchDiscoverPlayerResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = patchDiscoverPlayerResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = patchDiscoverPlayerResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<patchDiscoverPlayerResponse200> | undefined,
					GenericWretchError<
						| patchDiscoverPlayerResponse400
						| patchDiscoverPlayerResponse403
						| patchDiscoverPlayerResponse404
					>,
					{ id: number; body: DiscoverPlayerPatchRequest }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<patchDiscoverPlayerResponse200> | undefined,
				GenericWretchError<
					| patchDiscoverPlayerResponse400
					| patchDiscoverPlayerResponse403
					| patchDiscoverPlayerResponse404
				>,
				{ id: number; body: DiscoverPlayerPatchRequest }
			>(mutation, options || {});
		},
	};
};

const getDiscoverSignUps = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: {
			page?: number;
			limit?: number;
			filters?: object[];
			order?: object;
		},
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<DiscoverSignUpsResponse>> => {
		const args = z
			.object({
				page: z.number().int().optional(),
				limit: z.number().int().optional(),
				filters: z
					.array(
						z.object({
							type: z
								.enum([
									"exact",
									"search",
									"boolean",
									"exists",
									"inarray",
									"datetimerange",
									"numberrange",
								])
								.describe("Type of filter")
								.optional(),
							field: z
								.enum([
									"id",
									"user",
									"name",
									"phoneNumber",
									"email",
									"clubName",
									"role",
									"country",
									"userId",
								])
								.describe("Property to filter")
								.optional(),
							value: z
								.any()
								.superRefine((x, ctx) => {
									const schemas = [
										z.string(),
										z.number().int(),
										z.boolean(),
										z.object({
											start: z.string().datetime({ offset: true }).optional(),
											end: z.string().datetime({ offset: true }).optional(),
										}),
									];
									const errors = schemas.reduce<z.ZodError[]>(
										(errors, schema) =>
											((result) =>
												result.error ? [...errors, result.error] : errors)(
												schema.safeParse(x),
											),
										[],
									);
									if (schemas.length - errors.length !== 1) {
										ctx.addIssue({
											path: ctx.path,
											code: "invalid_union",
											unionErrors: errors,
											message: "Invalid input: Should pass single schema",
										});
									}
								})
								.describe(
									"Filter value, can be a string, integer, date or a date range",
								)
								.optional(),
						}),
					)
					.optional(),
				order: z
					.object({
						field: z.literal("createdAt").describe("Sorting field").optional(),
						direction: z
							.enum(["ASC", "DESC"])
							.describe("Sorting direction")
							.optional(),
					})
					.optional(),
			})
			.parse(params);
		const { page, limit, filters, order } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url("/admin/discover/sign-ups")
				.query(qs.stringify({ page, limit, filters, order }))
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = DiscoverSignUpsResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = getDiscoverSignUpsResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = getDiscoverSignUpsResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: {
				page?: number;
				limit?: number;
				filters?: object[];
				order?: object;
			},
			options?: Omit<
				UseQueryOptions<
					ApiResponse<DiscoverSignUpsResponse>,
					GenericWretchError<
						getDiscoverSignUpsResponse400 | getDiscoverSignUpsResponse403
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<DiscoverSignUpsResponse>,
			GenericWretchError<
				getDiscoverSignUpsResponse400 | getDiscoverSignUpsResponse403
			>
		> => {
			const key = ["getDiscoverSignUps", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<DiscoverSignUpsResponse>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<DiscoverSignUpsResponse>,
				GenericWretchError<
					getDiscoverSignUpsResponse400 | getDiscoverSignUpsResponse403
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<DiscoverSignUpsResponse>,
				GenericWretchError<
					getDiscoverSignUpsResponse400 | getDiscoverSignUpsResponse403
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: {
				page?: number;
				limit?: number;
				filters?: object[];
				order?: object;
			},
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<DiscoverSignUpsResponse>,
					GenericWretchError<
						getDiscoverSignUpsResponse400 | getDiscoverSignUpsResponse403
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<DiscoverSignUpsResponse>,
			GenericWretchError<
				getDiscoverSignUpsResponse400 | getDiscoverSignUpsResponse403
			>
		> => {
			const key = ["getDiscoverSignUps", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<DiscoverSignUpsResponse>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<DiscoverSignUpsResponse>,
				GenericWretchError<
					getDiscoverSignUpsResponse400 | getDiscoverSignUpsResponse403
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<DiscoverSignUpsResponse>,
				GenericWretchError<
					getDiscoverSignUpsResponse400 | getDiscoverSignUpsResponse403
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getDiscoverSignUp = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: { id: number },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<DiscoverSignUpGetResponse>> => {
		const args = z.object({ id: z.number().int() }).parse(params);
		const { id } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/admin/discover/sign-up/${id}`)
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = DiscoverSignUpGetResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = getDiscoverSignUpResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = getDiscoverSignUpResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = getDiscoverSignUpResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { id: number },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<DiscoverSignUpGetResponse>,
					GenericWretchError<
						| getDiscoverSignUpResponse400
						| getDiscoverSignUpResponse403
						| getDiscoverSignUpResponse404
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<DiscoverSignUpGetResponse>,
			GenericWretchError<
				| getDiscoverSignUpResponse400
				| getDiscoverSignUpResponse403
				| getDiscoverSignUpResponse404
			>
		> => {
			const key = ["getDiscoverSignUp", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<DiscoverSignUpGetResponse>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<DiscoverSignUpGetResponse>,
				GenericWretchError<
					| getDiscoverSignUpResponse400
					| getDiscoverSignUpResponse403
					| getDiscoverSignUpResponse404
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<DiscoverSignUpGetResponse>,
				GenericWretchError<
					| getDiscoverSignUpResponse400
					| getDiscoverSignUpResponse403
					| getDiscoverSignUpResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { id: number },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<DiscoverSignUpGetResponse>,
					GenericWretchError<
						| getDiscoverSignUpResponse400
						| getDiscoverSignUpResponse403
						| getDiscoverSignUpResponse404
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<DiscoverSignUpGetResponse>,
			GenericWretchError<
				| getDiscoverSignUpResponse400
				| getDiscoverSignUpResponse403
				| getDiscoverSignUpResponse404
			>
		> => {
			const key = ["getDiscoverSignUp", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<DiscoverSignUpGetResponse>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<DiscoverSignUpGetResponse>,
				GenericWretchError<
					| getDiscoverSignUpResponse400
					| getDiscoverSignUpResponse403
					| getDiscoverSignUpResponse404
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<DiscoverSignUpGetResponse>,
				GenericWretchError<
					| getDiscoverSignUpResponse400
					| getDiscoverSignUpResponse403
					| getDiscoverSignUpResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const patchDiscoverSignUp = (wretch: Http) => {
	const mutation = async (params: {
		id: number;
		body: DiscoverSignUpPatchRequest;
	}) => {
		const args = z
			.object({ id: z.number().int(), body: z.any() })
			.parse(params);
		const { id, body } = args;

		try {
			const res = await wretch
				.url(`/admin/discover/sign-up/${id}`)
				.json(body)
				.patch()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = patchDiscoverSignUpResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = patchDiscoverSignUpResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = patchDiscoverSignUpResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = patchDiscoverSignUpResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<patchDiscoverSignUpResponse200> | undefined,
					GenericWretchError<
						| patchDiscoverSignUpResponse400
						| patchDiscoverSignUpResponse403
						| patchDiscoverSignUpResponse404
					>,
					{ id: number; body: DiscoverSignUpPatchRequest }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<patchDiscoverSignUpResponse200> | undefined,
				GenericWretchError<
					| patchDiscoverSignUpResponse400
					| patchDiscoverSignUpResponse403
					| patchDiscoverSignUpResponse404
				>,
				{ id: number; body: DiscoverSignUpPatchRequest }
			>(mutation, options || {});
		},
	};
};

const adminDiscoverSubscriptionProductsGet = (
	wretch: Http,
	queryClient: QueryClient,
) => {
	const queryFn = async (
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<DiscoverSubscriptionProductsResponse>> => {
		try {
			const res = await wretch
				.options({ signal })
				.url("/admin/discover/subscription/products")
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = DiscoverSubscriptionProductsResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 403: {
					err.json =
						adminDiscoverSubscriptionProductsGetResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			options?: Omit<
				UseQueryOptions<
					ApiResponse<DiscoverSubscriptionProductsResponse>,
					GenericWretchError<adminDiscoverSubscriptionProductsGetResponse403>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<DiscoverSubscriptionProductsResponse>,
			GenericWretchError<adminDiscoverSubscriptionProductsGetResponse403>
		> => {
			const key = [
				"adminDiscoverSubscriptionProductsGet",
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<DiscoverSubscriptionProductsResponse>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<DiscoverSubscriptionProductsResponse>,
				GenericWretchError<adminDiscoverSubscriptionProductsGetResponse403>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<DiscoverSubscriptionProductsResponse>,
				GenericWretchError<adminDiscoverSubscriptionProductsGetResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<DiscoverSubscriptionProductsResponse>,
					GenericWretchError<adminDiscoverSubscriptionProductsGetResponse403>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<DiscoverSubscriptionProductsResponse>,
			GenericWretchError<adminDiscoverSubscriptionProductsGetResponse403>
		> => {
			const key = [
				"adminDiscoverSubscriptionProductsGet",
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<DiscoverSubscriptionProductsResponse>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<DiscoverSubscriptionProductsResponse>,
				GenericWretchError<adminDiscoverSubscriptionProductsGetResponse403>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<DiscoverSubscriptionProductsResponse>,
				GenericWretchError<adminDiscoverSubscriptionProductsGetResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const adminDiscoverSubscriptionsByTeamGet = (
	wretch: Http,
	queryClient: QueryClient,
) => {
	const queryFn = async (
		params: {
			teamId: number;
			subscriberType?: string;
			page?: number;
			limit?: number;
			filters?: object[];
			order?: object;
		},
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<DiscoverSubscriptionsResponse>> => {
		const args = z
			.object({
				teamId: z.number().int(),
				subscriberType: z.enum(["team", "clubhouse"]).optional(),
				page: z.number().int().optional(),
				limit: z.number().int().optional(),
				filters: z
					.array(
						z.object({
							type: z
								.enum([
									"exact",
									"search",
									"boolean",
									"exists",
									"inarray",
									"datetimerange",
									"numberrange",
								])
								.describe("Type of filter")
								.optional(),
							field: z
								.enum([
									"id",
									"teamId",
									"clubId",
									"teamName",
									"productId",
									"productName",
									"tokenPrice",
									"isFree",
									"status",
									"renewalStatus",
									"expirationAt",
									"subscriberType",
									"comment",
								])
								.describe("Property to filter")
								.optional(),
							value: z
								.any()
								.superRefine((x, ctx) => {
									const schemas = [
										z.string(),
										z.number().int(),
										z.boolean(),
										z.object({
											start: z.string().datetime({ offset: true }).optional(),
											end: z.string().datetime({ offset: true }).optional(),
										}),
									];
									const errors = schemas.reduce<z.ZodError[]>(
										(errors, schema) =>
											((result) =>
												result.error ? [...errors, result.error] : errors)(
												schema.safeParse(x),
											),
										[],
									);
									if (schemas.length - errors.length !== 1) {
										ctx.addIssue({
											path: ctx.path,
											code: "invalid_union",
											unionErrors: errors,
											message: "Invalid input: Should pass single schema",
										});
									}
								})
								.describe(
									"Filter value, can be a string, integer, date or a date range",
								)
								.optional(),
						}),
					)
					.optional(),
				order: z
					.object({
						field: z
							.enum([
								"id",
								"teamName",
								"productName",
								"tokenPrice",
								"isFree",
								"status",
								"renewalStatus",
								"isActive",
								"startAt",
								"expirationAt",
								"subscriberType",
								"comment",
								"ageCategory",
								"registeredPlayers",
							])
							.describe("Sorting field")
							.optional(),
						direction: z
							.enum(["ASC", "DESC"])
							.describe("Sorting direction")
							.optional(),
					})
					.optional(),
			})
			.parse(params);
		const { teamId, subscriberType, page, limit, filters, order } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/admin/discover/subscriptions/team/${teamId}`)
				.query(qs.stringify({ subscriberType, page, limit, filters, order }))
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = DiscoverSubscriptionsResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = adminDiscoverSubscriptionsByTeamGetResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = adminDiscoverSubscriptionsByTeamGetResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = adminDiscoverSubscriptionsByTeamGetResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: {
				teamId: number;
				subscriberType?: string;
				page?: number;
				limit?: number;
				filters?: object[];
				order?: object;
			},
			options?: Omit<
				UseQueryOptions<
					ApiResponse<DiscoverSubscriptionsResponse>,
					GenericWretchError<
						| adminDiscoverSubscriptionsByTeamGetResponse400
						| adminDiscoverSubscriptionsByTeamGetResponse403
						| adminDiscoverSubscriptionsByTeamGetResponse404
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<DiscoverSubscriptionsResponse>,
			GenericWretchError<
				| adminDiscoverSubscriptionsByTeamGetResponse400
				| adminDiscoverSubscriptionsByTeamGetResponse403
				| adminDiscoverSubscriptionsByTeamGetResponse404
			>
		> => {
			const key = [
				"adminDiscoverSubscriptionsByTeamGet",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<ApiResponse<DiscoverSubscriptionsResponse>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<DiscoverSubscriptionsResponse>,
				GenericWretchError<
					| adminDiscoverSubscriptionsByTeamGetResponse400
					| adminDiscoverSubscriptionsByTeamGetResponse403
					| adminDiscoverSubscriptionsByTeamGetResponse404
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<DiscoverSubscriptionsResponse>,
				GenericWretchError<
					| adminDiscoverSubscriptionsByTeamGetResponse400
					| adminDiscoverSubscriptionsByTeamGetResponse403
					| adminDiscoverSubscriptionsByTeamGetResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: {
				teamId: number;
				subscriberType?: string;
				page?: number;
				limit?: number;
				filters?: object[];
				order?: object;
			},
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<DiscoverSubscriptionsResponse>,
					GenericWretchError<
						| adminDiscoverSubscriptionsByTeamGetResponse400
						| adminDiscoverSubscriptionsByTeamGetResponse403
						| adminDiscoverSubscriptionsByTeamGetResponse404
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<DiscoverSubscriptionsResponse>,
			GenericWretchError<
				| adminDiscoverSubscriptionsByTeamGetResponse400
				| adminDiscoverSubscriptionsByTeamGetResponse403
				| adminDiscoverSubscriptionsByTeamGetResponse404
			>
		> => {
			const key = [
				"adminDiscoverSubscriptionsByTeamGet",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<ApiResponse<DiscoverSubscriptionsResponse>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<DiscoverSubscriptionsResponse>,
				GenericWretchError<
					| adminDiscoverSubscriptionsByTeamGetResponse400
					| adminDiscoverSubscriptionsByTeamGetResponse403
					| adminDiscoverSubscriptionsByTeamGetResponse404
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<DiscoverSubscriptionsResponse>,
				GenericWretchError<
					| adminDiscoverSubscriptionsByTeamGetResponse400
					| adminDiscoverSubscriptionsByTeamGetResponse403
					| adminDiscoverSubscriptionsByTeamGetResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const adminDiscoverSubscriptionsByClubGet = (
	wretch: Http,
	queryClient: QueryClient,
) => {
	const queryFn = async (
		params: {
			clubId: number;
			subscriberType?: string;
			page?: number;
			limit?: number;
			filters?: object[];
			order?: object;
		},
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<DiscoverSubscriptionsResponse>> => {
		const args = z
			.object({
				clubId: z.number().int(),
				subscriberType: z.enum(["team", "clubhouse"]).optional(),
				page: z.number().int().optional(),
				limit: z.number().int().optional(),
				filters: z
					.array(
						z.object({
							type: z
								.enum([
									"exact",
									"search",
									"boolean",
									"exists",
									"inarray",
									"datetimerange",
									"numberrange",
								])
								.describe("Type of filter")
								.optional(),
							field: z
								.enum([
									"id",
									"teamId",
									"clubId",
									"teamName",
									"productId",
									"productName",
									"tokenPrice",
									"isFree",
									"status",
									"renewalStatus",
									"expirationAt",
									"subscriberType",
									"comment",
								])
								.describe("Property to filter")
								.optional(),
							value: z
								.any()
								.superRefine((x, ctx) => {
									const schemas = [
										z.string(),
										z.number().int(),
										z.boolean(),
										z.object({
											start: z.string().datetime({ offset: true }).optional(),
											end: z.string().datetime({ offset: true }).optional(),
										}),
									];
									const errors = schemas.reduce<z.ZodError[]>(
										(errors, schema) =>
											((result) =>
												result.error ? [...errors, result.error] : errors)(
												schema.safeParse(x),
											),
										[],
									);
									if (schemas.length - errors.length !== 1) {
										ctx.addIssue({
											path: ctx.path,
											code: "invalid_union",
											unionErrors: errors,
											message: "Invalid input: Should pass single schema",
										});
									}
								})
								.describe(
									"Filter value, can be a string, integer, date or a date range",
								)
								.optional(),
						}),
					)
					.optional(),
				order: z
					.object({
						field: z
							.enum([
								"id",
								"teamName",
								"productName",
								"tokenPrice",
								"isFree",
								"status",
								"renewalStatus",
								"isActive",
								"startAt",
								"expirationAt",
								"subscriberType",
								"comment",
								"ageCategory",
								"registeredPlayers",
							])
							.describe("Sorting field")
							.optional(),
						direction: z
							.enum(["ASC", "DESC"])
							.describe("Sorting direction")
							.optional(),
					})
					.optional(),
			})
			.parse(params);
		const { clubId, subscriberType, page, limit, filters, order } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/admin/discover/subscriptions/club/${clubId}`)
				.query(qs.stringify({ subscriberType, page, limit, filters, order }))
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = DiscoverSubscriptionsResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = adminDiscoverSubscriptionsByClubGetResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = adminDiscoverSubscriptionsByClubGetResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = adminDiscoverSubscriptionsByClubGetResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: {
				clubId: number;
				subscriberType?: string;
				page?: number;
				limit?: number;
				filters?: object[];
				order?: object;
			},
			options?: Omit<
				UseQueryOptions<
					ApiResponse<DiscoverSubscriptionsResponse>,
					GenericWretchError<
						| adminDiscoverSubscriptionsByClubGetResponse400
						| adminDiscoverSubscriptionsByClubGetResponse403
						| adminDiscoverSubscriptionsByClubGetResponse404
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<DiscoverSubscriptionsResponse>,
			GenericWretchError<
				| adminDiscoverSubscriptionsByClubGetResponse400
				| adminDiscoverSubscriptionsByClubGetResponse403
				| adminDiscoverSubscriptionsByClubGetResponse404
			>
		> => {
			const key = [
				"adminDiscoverSubscriptionsByClubGet",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<ApiResponse<DiscoverSubscriptionsResponse>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<DiscoverSubscriptionsResponse>,
				GenericWretchError<
					| adminDiscoverSubscriptionsByClubGetResponse400
					| adminDiscoverSubscriptionsByClubGetResponse403
					| adminDiscoverSubscriptionsByClubGetResponse404
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<DiscoverSubscriptionsResponse>,
				GenericWretchError<
					| adminDiscoverSubscriptionsByClubGetResponse400
					| adminDiscoverSubscriptionsByClubGetResponse403
					| adminDiscoverSubscriptionsByClubGetResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: {
				clubId: number;
				subscriberType?: string;
				page?: number;
				limit?: number;
				filters?: object[];
				order?: object;
			},
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<DiscoverSubscriptionsResponse>,
					GenericWretchError<
						| adminDiscoverSubscriptionsByClubGetResponse400
						| adminDiscoverSubscriptionsByClubGetResponse403
						| adminDiscoverSubscriptionsByClubGetResponse404
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<DiscoverSubscriptionsResponse>,
			GenericWretchError<
				| adminDiscoverSubscriptionsByClubGetResponse400
				| adminDiscoverSubscriptionsByClubGetResponse403
				| adminDiscoverSubscriptionsByClubGetResponse404
			>
		> => {
			const key = [
				"adminDiscoverSubscriptionsByClubGet",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<ApiResponse<DiscoverSubscriptionsResponse>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<DiscoverSubscriptionsResponse>,
				GenericWretchError<
					| adminDiscoverSubscriptionsByClubGetResponse400
					| adminDiscoverSubscriptionsByClubGetResponse403
					| adminDiscoverSubscriptionsByClubGetResponse404
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<DiscoverSubscriptionsResponse>,
				GenericWretchError<
					| adminDiscoverSubscriptionsByClubGetResponse400
					| adminDiscoverSubscriptionsByClubGetResponse403
					| adminDiscoverSubscriptionsByClubGetResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const adminDiscoverSubscriptionByTeamPost = (wretch: Http) => {
	const mutation = async (params: {
		productId: number;
		teamId: number;
		isFree: boolean;
	}) => {
		const args = z
			.object({
				productId: z.number().int(),
				teamId: z.number().int(),
				isFree: z.enum(["0", "1"]),
			})
			.parse(params);
		const { productId, teamId, isFree } = args;

		try {
			const res = await wretch
				.url(
					`/admin/discover/subscription/product/${productId}/team/${teamId}/is-free/${isFree}`,
				)
				.post()
				.res();
			switch (res.status) {
				case 201: {
					return {
						status: res.status,
						data: null,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			switch (err.status) {
				case 400: {
					break;
				}
				case 401: {
					break;
				}
				case 403: {
					break;
				}
				case 422: {
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<null> | undefined,
					GenericWretchError<null>,
					{ productId: number; teamId: number; isFree: boolean }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<null> | undefined,
				GenericWretchError<null>,
				{ productId: number; teamId: number; isFree: boolean }
			>(mutation, options || {});
		},
	};
};

const adminDiscoverSubscriptionSetToFreePost = (wretch: Http) => {
	const mutation = async (params: { subscriptionId: number }) => {
		const args = z.object({ subscriptionId: z.number().int() }).parse(params);
		const { subscriptionId } = args;

		try {
			const res = await wretch
				.url(`/admin/discover/subscription/${subscriptionId}/set-to-free`)
				.post()
				.res();
			switch (res.status) {
				case 204: {
					return {
						status: res.status,
						data: null,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			switch (err.status) {
				case 401: {
					break;
				}
				case 403: {
					break;
				}
				case 404: {
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<null> | undefined,
					GenericWretchError<null>,
					{ subscriptionId: number }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<null> | undefined,
				GenericWretchError<null>,
				{ subscriptionId: number }
			>(mutation, options || {});
		},
	};
};

const adminDiscoverSubscriptionSetToPaidPost = (wretch: Http) => {
	const mutation = async (params: { subscriptionId: number }) => {
		const args = z.object({ subscriptionId: z.number().int() }).parse(params);
		const { subscriptionId } = args;

		try {
			const res = await wretch
				.url(`/admin/discover/subscription/${subscriptionId}/set-to-paid`)
				.post()
				.res();
			switch (res.status) {
				case 204: {
					return {
						status: res.status,
						data: null,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			switch (err.status) {
				case 401: {
					break;
				}
				case 403: {
					break;
				}
				case 404: {
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<null> | undefined,
					GenericWretchError<null>,
					{ subscriptionId: number }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<null> | undefined,
				GenericWretchError<null>,
				{ subscriptionId: number }
			>(mutation, options || {});
		},
	};
};

const adminDiscoverSubscriptionActivatePost = (wretch: Http) => {
	const mutation = async (params: { subscriptionId: number }) => {
		const args = z.object({ subscriptionId: z.number().int() }).parse(params);
		const { subscriptionId } = args;

		try {
			const res = await wretch
				.url(`/admin/discover/subscription/${subscriptionId}/activate`)
				.post()
				.res();
			switch (res.status) {
				case 204: {
					return {
						status: res.status,
						data: null,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			switch (err.status) {
				case 401: {
					break;
				}
				case 403: {
					break;
				}
				case 404: {
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<null> | undefined,
					GenericWretchError<null>,
					{ subscriptionId: number }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<null> | undefined,
				GenericWretchError<null>,
				{ subscriptionId: number }
			>(mutation, options || {});
		},
	};
};

const adminDiscoverSubscriptionStopPost = (wretch: Http) => {
	const mutation = async (params: { subscriptionId: number }) => {
		const args = z.object({ subscriptionId: z.number().int() }).parse(params);
		const { subscriptionId } = args;

		try {
			const res = await wretch
				.url(`/admin/discover/subscription/${subscriptionId}/stop`)
				.post()
				.res();
			switch (res.status) {
				case 204: {
					return {
						status: res.status,
						data: null,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			switch (err.status) {
				case 401: {
					break;
				}
				case 403: {
					break;
				}
				case 404: {
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<null> | undefined,
					GenericWretchError<null>,
					{ subscriptionId: number }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<null> | undefined,
				GenericWretchError<null>,
				{ subscriptionId: number }
			>(mutation, options || {});
		},
	};
};

const adminDiscoverSubscriptionActivateRenewalPost = (wretch: Http) => {
	const mutation = async (params: { subscriptionId: number }) => {
		const args = z.object({ subscriptionId: z.number().int() }).parse(params);
		const { subscriptionId } = args;

		try {
			const res = await wretch
				.url(`/admin/discover/subscription/${subscriptionId}/activate-renewal`)
				.post()
				.res();
			switch (res.status) {
				case 204: {
					return {
						status: res.status,
						data: null,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			switch (err.status) {
				case 401: {
					break;
				}
				case 403: {
					break;
				}
				case 404: {
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<null> | undefined,
					GenericWretchError<null>,
					{ subscriptionId: number }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<null> | undefined,
				GenericWretchError<null>,
				{ subscriptionId: number }
			>(mutation, options || {});
		},
	};
};

const adminDiscoverSubscriptionPauseRenewalPost = (wretch: Http) => {
	const mutation = async (params: { subscriptionId: number }) => {
		const args = z.object({ subscriptionId: z.number().int() }).parse(params);
		const { subscriptionId } = args;

		try {
			const res = await wretch
				.url(`/admin/discover/subscription/${subscriptionId}/pause-renewal`)
				.post()
				.res();
			switch (res.status) {
				case 204: {
					return {
						status: res.status,
						data: null,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			switch (err.status) {
				case 401: {
					break;
				}
				case 403: {
					break;
				}
				case 404: {
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<null> | undefined,
					GenericWretchError<null>,
					{ subscriptionId: number }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<null> | undefined,
				GenericWretchError<null>,
				{ subscriptionId: number }
			>(mutation, options || {});
		},
	};
};

const getDiscoverTeams = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: {
			page?: number;
			limit?: number;
			filters?: object[];
			order?: object;
		},
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<DiscoverTeamsResponse>> => {
		const args = z
			.object({
				page: z.number().int().optional(),
				limit: z.number().int().optional(),
				filters: z
					.array(
						z.object({
							type: z
								.enum([
									"exact",
									"search",
									"boolean",
									"exists",
									"inarray",
									"datetimerange",
									"numberrange",
								])
								.describe("Type of filter")
								.optional(),
							field: z
								.enum([
									"id",
									"name",
									"club",
									"ageCategory",
									"createdAt",
									"entityType",
									"country",
									"city",
								])
								.describe("Property to filter")
								.optional(),
							value: z
								.any()
								.superRefine((x, ctx) => {
									const schemas = [
										z.string(),
										z.number().int(),
										z.boolean(),
										z.object({
											start: z.string().datetime({ offset: true }).optional(),
											end: z.string().datetime({ offset: true }).optional(),
										}),
									];
									const errors = schemas.reduce<z.ZodError[]>(
										(errors, schema) =>
											((result) =>
												result.error ? [...errors, result.error] : errors)(
												schema.safeParse(x),
											),
										[],
									);
									if (schemas.length - errors.length !== 1) {
										ctx.addIssue({
											path: ctx.path,
											code: "invalid_union",
											unionErrors: errors,
											message: "Invalid input: Should pass single schema",
										});
									}
								})
								.describe(
									"Filter value, can be a string, integer, date or a date range",
								)
								.optional(),
						}),
					)
					.optional(),
				order: z
					.object({
						field: z
							.enum(["id", "createdAt", "name", "country", "city", "club"])
							.describe("Sorting field")
							.optional(),
						direction: z
							.enum(["ASC", "DESC"])
							.describe("Sorting direction")
							.optional(),
					})
					.optional(),
			})
			.parse(params);
		const { page, limit, filters, order } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url("/admin/discover/teams")
				.query(qs.stringify({ page, limit, filters, order }))
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = DiscoverTeamsResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = getDiscoverTeamsResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = getDiscoverTeamsResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: {
				page?: number;
				limit?: number;
				filters?: object[];
				order?: object;
			},
			options?: Omit<
				UseQueryOptions<
					ApiResponse<DiscoverTeamsResponse>,
					GenericWretchError<
						getDiscoverTeamsResponse400 | getDiscoverTeamsResponse403
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<DiscoverTeamsResponse>,
			GenericWretchError<
				getDiscoverTeamsResponse400 | getDiscoverTeamsResponse403
			>
		> => {
			const key = ["getDiscoverTeams", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<DiscoverTeamsResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<DiscoverTeamsResponse>,
				GenericWretchError<
					getDiscoverTeamsResponse400 | getDiscoverTeamsResponse403
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<DiscoverTeamsResponse>,
				GenericWretchError<
					getDiscoverTeamsResponse400 | getDiscoverTeamsResponse403
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: {
				page?: number;
				limit?: number;
				filters?: object[];
				order?: object;
			},
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<DiscoverTeamsResponse>,
					GenericWretchError<
						getDiscoverTeamsResponse400 | getDiscoverTeamsResponse403
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<DiscoverTeamsResponse>,
			GenericWretchError<
				getDiscoverTeamsResponse400 | getDiscoverTeamsResponse403
			>
		> => {
			const key = ["getDiscoverTeams", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<DiscoverTeamsResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<DiscoverTeamsResponse>,
				GenericWretchError<
					getDiscoverTeamsResponse400 | getDiscoverTeamsResponse403
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<DiscoverTeamsResponse>,
				GenericWretchError<
					getDiscoverTeamsResponse400 | getDiscoverTeamsResponse403
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getDiscoverTeam = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: { id: number },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<DiscoverTeamGetResponse>> => {
		const args = z.object({ id: z.number().int() }).parse(params);
		const { id } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/admin/discover/team/${id}`)
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = DiscoverTeamGetResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = getDiscoverTeamResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = getDiscoverTeamResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = getDiscoverTeamResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { id: number },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<DiscoverTeamGetResponse>,
					GenericWretchError<
						| getDiscoverTeamResponse400
						| getDiscoverTeamResponse403
						| getDiscoverTeamResponse404
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<DiscoverTeamGetResponse>,
			GenericWretchError<
				| getDiscoverTeamResponse400
				| getDiscoverTeamResponse403
				| getDiscoverTeamResponse404
			>
		> => {
			const key = ["getDiscoverTeam", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<DiscoverTeamGetResponse>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<DiscoverTeamGetResponse>,
				GenericWretchError<
					| getDiscoverTeamResponse400
					| getDiscoverTeamResponse403
					| getDiscoverTeamResponse404
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<DiscoverTeamGetResponse>,
				GenericWretchError<
					| getDiscoverTeamResponse400
					| getDiscoverTeamResponse403
					| getDiscoverTeamResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { id: number },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<DiscoverTeamGetResponse>,
					GenericWretchError<
						| getDiscoverTeamResponse400
						| getDiscoverTeamResponse403
						| getDiscoverTeamResponse404
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<DiscoverTeamGetResponse>,
			GenericWretchError<
				| getDiscoverTeamResponse400
				| getDiscoverTeamResponse403
				| getDiscoverTeamResponse404
			>
		> => {
			const key = ["getDiscoverTeam", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<DiscoverTeamGetResponse>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<DiscoverTeamGetResponse>,
				GenericWretchError<
					| getDiscoverTeamResponse400
					| getDiscoverTeamResponse403
					| getDiscoverTeamResponse404
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<DiscoverTeamGetResponse>,
				GenericWretchError<
					| getDiscoverTeamResponse400
					| getDiscoverTeamResponse403
					| getDiscoverTeamResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const patchDiscoverTeam = (wretch: Http) => {
	const mutation = async (params: {
		id: number;
		body: DiscoverTeamPatchRequest;
	}) => {
		const args = z
			.object({ id: z.number().int(), body: z.any() })
			.parse(params);
		const { id, body } = args;

		try {
			const res = await wretch
				.url(`/admin/discover/team/${id}`)
				.json(body)
				.patch()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = patchDiscoverTeamResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = patchDiscoverTeamResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = patchDiscoverTeamResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = patchDiscoverTeamResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<patchDiscoverTeamResponse200> | undefined,
					GenericWretchError<
						| patchDiscoverTeamResponse400
						| patchDiscoverTeamResponse403
						| patchDiscoverTeamResponse404
					>,
					{ id: number; body: DiscoverTeamPatchRequest }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<patchDiscoverTeamResponse200> | undefined,
				GenericWretchError<
					| patchDiscoverTeamResponse400
					| patchDiscoverTeamResponse403
					| patchDiscoverTeamResponse404
				>,
				{ id: number; body: DiscoverTeamPatchRequest }
			>(mutation, options || {});
		},
	};
};

const getDiscoverUserGuardianApprovals = (
	wretch: Http,
	queryClient: QueryClient,
) => {
	const queryFn = async (
		params: { page?: number; limit?: number; filters?: object[] },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<DiscoverUserGuardianApprovalsResponse>> => {
		const args = z
			.object({
				page: z.number().int().optional(),
				limit: z.number().int().optional(),
				filters: z
					.array(
						z.object({
							type: z
								.enum([
									"exact",
									"search",
									"boolean",
									"exists",
									"inarray",
									"datetimerange",
									"numberrange",
								])
								.describe("Type of filter")
								.optional(),
							field: z
								.literal("user")
								.describe("Property to filter")
								.optional(),
							value: z
								.any()
								.superRefine((x, ctx) => {
									const schemas = [
										z.string(),
										z.number().int(),
										z.boolean(),
										z.object({
											start: z.string().datetime({ offset: true }).optional(),
											end: z.string().datetime({ offset: true }).optional(),
										}),
									];
									const errors = schemas.reduce<z.ZodError[]>(
										(errors, schema) =>
											((result) =>
												result.error ? [...errors, result.error] : errors)(
												schema.safeParse(x),
											),
										[],
									);
									if (schemas.length - errors.length !== 1) {
										ctx.addIssue({
											path: ctx.path,
											code: "invalid_union",
											unionErrors: errors,
											message: "Invalid input: Should pass single schema",
										});
									}
								})
								.describe(
									"Filter value, can be a string, integer, date or a date range",
								)
								.optional(),
						}),
					)
					.optional(),
			})
			.parse(params);
		const { page, limit, filters } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url("/admin/discover/user-guardian-approvals")
				.query(qs.stringify({ page, limit, filters }))
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = DiscoverUserGuardianApprovalsResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = getDiscoverUserGuardianApprovalsResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = getDiscoverUserGuardianApprovalsResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { page?: number; limit?: number; filters?: object[] },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<DiscoverUserGuardianApprovalsResponse>,
					GenericWretchError<
						| getDiscoverUserGuardianApprovalsResponse400
						| getDiscoverUserGuardianApprovalsResponse403
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<DiscoverUserGuardianApprovalsResponse>,
			GenericWretchError<
				| getDiscoverUserGuardianApprovalsResponse400
				| getDiscoverUserGuardianApprovalsResponse403
			>
		> => {
			const key = [
				"getDiscoverUserGuardianApprovals",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<DiscoverUserGuardianApprovalsResponse>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<DiscoverUserGuardianApprovalsResponse>,
				GenericWretchError<
					| getDiscoverUserGuardianApprovalsResponse400
					| getDiscoverUserGuardianApprovalsResponse403
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<DiscoverUserGuardianApprovalsResponse>,
				GenericWretchError<
					| getDiscoverUserGuardianApprovalsResponse400
					| getDiscoverUserGuardianApprovalsResponse403
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { page?: number; limit?: number; filters?: object[] },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<DiscoverUserGuardianApprovalsResponse>,
					GenericWretchError<
						| getDiscoverUserGuardianApprovalsResponse400
						| getDiscoverUserGuardianApprovalsResponse403
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<DiscoverUserGuardianApprovalsResponse>,
			GenericWretchError<
				| getDiscoverUserGuardianApprovalsResponse400
				| getDiscoverUserGuardianApprovalsResponse403
			>
		> => {
			const key = [
				"getDiscoverUserGuardianApprovals",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<DiscoverUserGuardianApprovalsResponse>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<DiscoverUserGuardianApprovalsResponse>,
				GenericWretchError<
					| getDiscoverUserGuardianApprovalsResponse400
					| getDiscoverUserGuardianApprovalsResponse403
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<DiscoverUserGuardianApprovalsResponse>,
				GenericWretchError<
					| getDiscoverUserGuardianApprovalsResponse400
					| getDiscoverUserGuardianApprovalsResponse403
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const postAdminDiscoverUserNotes = (wretch: Http) => {
	const mutation = async (params: { body: UserNotePostRequest }) => {
		const args = z.object({ body: z.any() }).parse(params);
		const { body } = args;

		try {
			const res = await wretch
				.url("/admin/discover/user/notes")
				.json(body)
				.post()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 201: {
					const data = postAdminDiscoverUserNotesResponse201.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = postAdminDiscoverUserNotesResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = postAdminDiscoverUserNotesResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = postAdminDiscoverUserNotesResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<postAdminDiscoverUserNotesResponse201> | undefined,
					GenericWretchError<
						| postAdminDiscoverUserNotesResponse400
						| postAdminDiscoverUserNotesResponse403
						| postAdminDiscoverUserNotesResponse404
					>,
					{ body: UserNotePostRequest }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<postAdminDiscoverUserNotesResponse201> | undefined,
				GenericWretchError<
					| postAdminDiscoverUserNotesResponse400
					| postAdminDiscoverUserNotesResponse403
					| postAdminDiscoverUserNotesResponse404
				>,
				{ body: UserNotePostRequest }
			>(mutation, options || {});
		},
	};
};

const deleteAdminDiscoverUserNote = (wretch: Http) => {
	const mutation = async (params: { id: number }) => {
		const args = z.object({ id: z.number().int() }).parse(params);
		const { id } = args;

		try {
			const res = await wretch
				.url(`/admin/discover/user/note/${id}`)
				.delete()
				.res();
			switch (res.status) {
				case 204: {
					return {
						status: res.status,
						data: null,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = deleteAdminDiscoverUserNoteResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = deleteAdminDiscoverUserNoteResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = deleteAdminDiscoverUserNoteResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<null> | undefined,
					GenericWretchError<
						| deleteAdminDiscoverUserNoteResponse400
						| deleteAdminDiscoverUserNoteResponse403
						| deleteAdminDiscoverUserNoteResponse404
					>,
					{ id: number }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<null> | undefined,
				GenericWretchError<
					| deleteAdminDiscoverUserNoteResponse400
					| deleteAdminDiscoverUserNoteResponse403
					| deleteAdminDiscoverUserNoteResponse404
				>,
				{ id: number }
			>(mutation, options || {});
		},
	};
};

const patchAdminDiscoverUserNote = (wretch: Http) => {
	const mutation = async (params: {
		id: number;
		body: patchAdminDiscoverUserNoteRequestBody;
	}) => {
		const args = z
			.object({ id: z.number().int(), body: z.any() })
			.parse(params);
		const { id, body } = args;

		try {
			const res = await wretch
				.url(`/admin/discover/user/note/${id}`)
				.json(body)
				.patch()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = patchAdminDiscoverUserNoteResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = patchAdminDiscoverUserNoteResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = patchAdminDiscoverUserNoteResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = patchAdminDiscoverUserNoteResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<patchAdminDiscoverUserNoteResponse200> | undefined,
					GenericWretchError<
						| patchAdminDiscoverUserNoteResponse400
						| patchAdminDiscoverUserNoteResponse403
						| patchAdminDiscoverUserNoteResponse404
					>,
					{ id: number; body: patchAdminDiscoverUserNoteRequestBody }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<patchAdminDiscoverUserNoteResponse200> | undefined,
				GenericWretchError<
					| patchAdminDiscoverUserNoteResponse400
					| patchAdminDiscoverUserNoteResponse403
					| patchAdminDiscoverUserNoteResponse404
				>,
				{ id: number; body: patchAdminDiscoverUserNoteRequestBody }
			>(mutation, options || {});
		},
	};
};

const postDiscoverUserPermission = (wretch: Http) => {
	const mutation = async (params: {
		body: DiscoverUserPermissionPostRequest;
	}) => {
		const args = z.object({ body: z.any() }).parse(params);
		const { body } = args;

		try {
			const res = await wretch
				.url("/admin/discover/user-permission")
				.json(body)
				.post()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = postDiscoverUserPermissionResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = postDiscoverUserPermissionResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = postDiscoverUserPermissionResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<postDiscoverUserPermissionResponse200> | undefined,
					GenericWretchError<
						| postDiscoverUserPermissionResponse400
						| postDiscoverUserPermissionResponse403
					>,
					{ body: DiscoverUserPermissionPostRequest }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<postDiscoverUserPermissionResponse200> | undefined,
				GenericWretchError<
					| postDiscoverUserPermissionResponse400
					| postDiscoverUserPermissionResponse403
				>,
				{ body: DiscoverUserPermissionPostRequest }
			>(mutation, options || {});
		},
	};
};

const deleteDiscoverUserPermission = (wretch: Http) => {
	const mutation = async (params: { id: number }) => {
		const args = z.object({ id: z.number().int() }).parse(params);
		const { id } = args;

		try {
			const res = await wretch
				.url(`/admin/discover/user-permission/${id}`)
				.delete()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = deleteDiscoverUserPermissionResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = deleteDiscoverUserPermissionResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = deleteDiscoverUserPermissionResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<deleteDiscoverUserPermissionResponse200> | undefined,
					GenericWretchError<
						| deleteDiscoverUserPermissionResponse400
						| deleteDiscoverUserPermissionResponse403
					>,
					{ id: number }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<deleteDiscoverUserPermissionResponse200> | undefined,
				GenericWretchError<
					| deleteDiscoverUserPermissionResponse400
					| deleteDiscoverUserPermissionResponse403
				>,
				{ id: number }
			>(mutation, options || {});
		},
	};
};

const getDiscoverUsers = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: {
			page?: number;
			limit?: number;
			filters?: object[];
			order?: object;
		},
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<DiscoverUsersResponse>> => {
		const args = z
			.object({
				page: z.number().int().optional(),
				limit: z.number().int().optional(),
				filters: z
					.array(
						z.object({
							type: z
								.enum([
									"exact",
									"search",
									"boolean",
									"exists",
									"inarray",
									"datetimerange",
									"numberrange",
								])
								.describe("Type of filter")
								.optional(),
							field: z
								.enum([
									"id",
									"firstName",
									"middleName",
									"lastName",
									"phoneNumber",
									"email",
									"scope",
									"isActive",
									"isMinor",
								])
								.describe("Property to filter")
								.optional(),
							value: z
								.any()
								.superRefine((x, ctx) => {
									const schemas = [
										z.string(),
										z.number().int(),
										z.boolean(),
										z.object({
											start: z.string().datetime({ offset: true }).optional(),
											end: z.string().datetime({ offset: true }).optional(),
										}),
									];
									const errors = schemas.reduce<z.ZodError[]>(
										(errors, schema) =>
											((result) =>
												result.error ? [...errors, result.error] : errors)(
												schema.safeParse(x),
											),
										[],
									);
									if (schemas.length - errors.length !== 1) {
										ctx.addIssue({
											path: ctx.path,
											code: "invalid_union",
											unionErrors: errors,
											message: "Invalid input: Should pass single schema",
										});
									}
								})
								.describe(
									"Filter value, can be a string, integer, date or a date range",
								)
								.optional(),
						}),
					)
					.optional(),
				order: z
					.object({
						field: z
							.enum([
								"id",
								"createdAt",
								"name",
								"phoneNumber",
								"email",
								"passwordRequestedAt",
							])
							.describe("Sorting field")
							.optional(),
						direction: z
							.enum(["ASC", "DESC"])
							.describe("Sorting direction")
							.optional(),
					})
					.optional(),
			})
			.parse(params);
		const { page, limit, filters, order } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url("/admin/discover/users")
				.query(qs.stringify({ page, limit, filters, order }))
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = DiscoverUsersResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = getDiscoverUsersResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = getDiscoverUsersResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: {
				page?: number;
				limit?: number;
				filters?: object[];
				order?: object;
			},
			options?: Omit<
				UseQueryOptions<
					ApiResponse<DiscoverUsersResponse>,
					GenericWretchError<
						getDiscoverUsersResponse400 | getDiscoverUsersResponse403
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<DiscoverUsersResponse>,
			GenericWretchError<
				getDiscoverUsersResponse400 | getDiscoverUsersResponse403
			>
		> => {
			const key = ["getDiscoverUsers", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<DiscoverUsersResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<DiscoverUsersResponse>,
				GenericWretchError<
					getDiscoverUsersResponse400 | getDiscoverUsersResponse403
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<DiscoverUsersResponse>,
				GenericWretchError<
					getDiscoverUsersResponse400 | getDiscoverUsersResponse403
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: {
				page?: number;
				limit?: number;
				filters?: object[];
				order?: object;
			},
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<DiscoverUsersResponse>,
					GenericWretchError<
						getDiscoverUsersResponse400 | getDiscoverUsersResponse403
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<DiscoverUsersResponse>,
			GenericWretchError<
				getDiscoverUsersResponse400 | getDiscoverUsersResponse403
			>
		> => {
			const key = ["getDiscoverUsers", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<DiscoverUsersResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<DiscoverUsersResponse>,
				GenericWretchError<
					getDiscoverUsersResponse400 | getDiscoverUsersResponse403
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<DiscoverUsersResponse>,
				GenericWretchError<
					getDiscoverUsersResponse400 | getDiscoverUsersResponse403
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getDiscoverUser = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: { id: number },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<DiscoverUserGetResponse>> => {
		const args = z.object({ id: z.number().int() }).parse(params);
		const { id } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/admin/discover/user/${id}`)
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = DiscoverUserGetResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = getDiscoverUserResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = getDiscoverUserResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = getDiscoverUserResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { id: number },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<DiscoverUserGetResponse>,
					GenericWretchError<
						| getDiscoverUserResponse400
						| getDiscoverUserResponse403
						| getDiscoverUserResponse404
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<DiscoverUserGetResponse>,
			GenericWretchError<
				| getDiscoverUserResponse400
				| getDiscoverUserResponse403
				| getDiscoverUserResponse404
			>
		> => {
			const key = ["getDiscoverUser", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<DiscoverUserGetResponse>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<DiscoverUserGetResponse>,
				GenericWretchError<
					| getDiscoverUserResponse400
					| getDiscoverUserResponse403
					| getDiscoverUserResponse404
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<DiscoverUserGetResponse>,
				GenericWretchError<
					| getDiscoverUserResponse400
					| getDiscoverUserResponse403
					| getDiscoverUserResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { id: number },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<DiscoverUserGetResponse>,
					GenericWretchError<
						| getDiscoverUserResponse400
						| getDiscoverUserResponse403
						| getDiscoverUserResponse404
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<DiscoverUserGetResponse>,
			GenericWretchError<
				| getDiscoverUserResponse400
				| getDiscoverUserResponse403
				| getDiscoverUserResponse404
			>
		> => {
			const key = ["getDiscoverUser", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<DiscoverUserGetResponse>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<DiscoverUserGetResponse>,
				GenericWretchError<
					| getDiscoverUserResponse400
					| getDiscoverUserResponse403
					| getDiscoverUserResponse404
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<DiscoverUserGetResponse>,
				GenericWretchError<
					| getDiscoverUserResponse400
					| getDiscoverUserResponse403
					| getDiscoverUserResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const patchDiscoverUser = (wretch: Http) => {
	const mutation = async (params: {
		id: number;
		body: DiscoverUserPatchRequest;
	}) => {
		const args = z
			.object({ id: z.number().int(), body: z.any() })
			.parse(params);
		const { id, body } = args;

		try {
			const res = await wretch
				.url(`/admin/discover/user/${id}`)
				.json(body)
				.patch()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = patchDiscoverUserResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = patchDiscoverUserResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = patchDiscoverUserResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = patchDiscoverUserResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<patchDiscoverUserResponse200> | undefined,
					GenericWretchError<
						| patchDiscoverUserResponse400
						| patchDiscoverUserResponse403
						| patchDiscoverUserResponse404
					>,
					{ id: number; body: DiscoverUserPatchRequest }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<patchDiscoverUserResponse200> | undefined,
				GenericWretchError<
					| patchDiscoverUserResponse400
					| patchDiscoverUserResponse403
					| patchDiscoverUserResponse404
				>,
				{ id: number; body: DiscoverUserPatchRequest }
			>(mutation, options || {});
		},
	};
};

const postDiscoverUserImpersonate = (wretch: Http) => {
	const mutation = async (params: { id: number }) => {
		const args = z.object({ id: z.number().int() }).parse(params);
		const { id } = args;

		try {
			const res = await wretch
				.url(`/admin/discover/user/${id}/impersonate`)
				.post()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = postDiscoverUserImpersonateResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 403: {
					err.json = postDiscoverUserImpersonateResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = postDiscoverUserImpersonateResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<postDiscoverUserImpersonateResponse200> | undefined,
					GenericWretchError<
						| postDiscoverUserImpersonateResponse403
						| postDiscoverUserImpersonateResponse404
					>,
					{ id: number }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<postDiscoverUserImpersonateResponse200> | undefined,
				GenericWretchError<
					| postDiscoverUserImpersonateResponse403
					| postDiscoverUserImpersonateResponse404
				>,
				{ id: number }
			>(mutation, options || {});
		},
	};
};

const rejectGameAnalysis = (wretch: Http) => {
	const mutation = async (params: { id: number }) => {
		const args = z.object({ id: z.number().int() }).parse(params);
		const { id } = args;

		try {
			const res = await wretch
				.url(`/admin/game-analysis/${id}/reject`)
				.patch()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = rejectGameAnalysisResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = rejectGameAnalysisResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = rejectGameAnalysisResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = rejectGameAnalysisResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<rejectGameAnalysisResponse200> | undefined,
					GenericWretchError<
						| rejectGameAnalysisResponse400
						| rejectGameAnalysisResponse403
						| rejectGameAnalysisResponse404
					>,
					{ id: number }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<rejectGameAnalysisResponse200> | undefined,
				GenericWretchError<
					| rejectGameAnalysisResponse400
					| rejectGameAnalysisResponse403
					| rejectGameAnalysisResponse404
				>,
				{ id: number }
			>(mutation, options || {});
		},
	};
};

const createGameAnalysis = (wretch: Http) => {
	const mutation = async (params: {
		gameId: number;
		body: GameAnalysisCreatePostRequest;
	}) => {
		const args = z
			.object({ gameId: z.number().int(), body: z.any() })
			.parse(params);
		const { gameId, body } = args;

		try {
			const res = await wretch
				.url(`/admin/game-analysis/${gameId}`)
				.json(body)
				.post()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 201: {
					const data = createGameAnalysisResponse201.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = createGameAnalysisResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = createGameAnalysisResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = createGameAnalysisResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<createGameAnalysisResponse201> | undefined,
					GenericWretchError<
						| createGameAnalysisResponse400
						| createGameAnalysisResponse403
						| createGameAnalysisResponse404
					>,
					{ gameId: number; body: GameAnalysisCreatePostRequest }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<createGameAnalysisResponse201> | undefined,
				GenericWretchError<
					| createGameAnalysisResponse400
					| createGameAnalysisResponse403
					| createGameAnalysisResponse404
				>,
				{ gameId: number; body: GameAnalysisCreatePostRequest }
			>(mutation, options || {});
		},
	};
};

const swapGameHomeAwayMapping = (wretch: Http) => {
	const mutation = async (params: { id: number }) => {
		const args = z.object({ id: z.number().int() }).parse(params);
		const { id } = args;

		try {
			const res = await wretch
				.url(`/admin/game/${id}/swap-home-away-mapping`)
				.post()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = swapGameHomeAwayMappingResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = swapGameHomeAwayMappingResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = swapGameHomeAwayMappingResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = swapGameHomeAwayMappingResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<swapGameHomeAwayMappingResponse200> | undefined,
					GenericWretchError<
						| swapGameHomeAwayMappingResponse400
						| swapGameHomeAwayMappingResponse403
						| swapGameHomeAwayMappingResponse404
					>,
					{ id: number }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<swapGameHomeAwayMappingResponse200> | undefined,
				GenericWretchError<
					| swapGameHomeAwayMappingResponse400
					| swapGameHomeAwayMappingResponse403
					| swapGameHomeAwayMappingResponse404
				>,
				{ id: number }
			>(mutation, options || {});
		},
	};
};

const getGameHomeAwayMappingSwapEligibility = (
	wretch: Http,
	queryClient: QueryClient,
) => {
	const queryFn = async (
		params: { id: number },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<GameHomeAwayMappingSwapEligibilityResponse>> => {
		const args = z.object({ id: z.number().int() }).parse(params);
		const { id } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/admin/game/${id}/swap-home-away-mapping/eligibility`)
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = GameHomeAwayMappingSwapEligibilityResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 403: {
					err.json =
						getGameHomeAwayMappingSwapEligibilityResponse403.parse(json);
					break;
				}
				case 404: {
					err.json =
						getGameHomeAwayMappingSwapEligibilityResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { id: number },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<GameHomeAwayMappingSwapEligibilityResponse>,
					GenericWretchError<
						| getGameHomeAwayMappingSwapEligibilityResponse403
						| getGameHomeAwayMappingSwapEligibilityResponse404
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<GameHomeAwayMappingSwapEligibilityResponse>,
			GenericWretchError<
				| getGameHomeAwayMappingSwapEligibilityResponse403
				| getGameHomeAwayMappingSwapEligibilityResponse404
			>
		> => {
			const key = [
				"getGameHomeAwayMappingSwapEligibility",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<GameHomeAwayMappingSwapEligibilityResponse>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<GameHomeAwayMappingSwapEligibilityResponse>,
				GenericWretchError<
					| getGameHomeAwayMappingSwapEligibilityResponse403
					| getGameHomeAwayMappingSwapEligibilityResponse404
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<GameHomeAwayMappingSwapEligibilityResponse>,
				GenericWretchError<
					| getGameHomeAwayMappingSwapEligibilityResponse403
					| getGameHomeAwayMappingSwapEligibilityResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { id: number },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<GameHomeAwayMappingSwapEligibilityResponse>,
					GenericWretchError<
						| getGameHomeAwayMappingSwapEligibilityResponse403
						| getGameHomeAwayMappingSwapEligibilityResponse404
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<GameHomeAwayMappingSwapEligibilityResponse>,
			GenericWretchError<
				| getGameHomeAwayMappingSwapEligibilityResponse403
				| getGameHomeAwayMappingSwapEligibilityResponse404
			>
		> => {
			const key = [
				"getGameHomeAwayMappingSwapEligibility",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<GameHomeAwayMappingSwapEligibilityResponse>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<GameHomeAwayMappingSwapEligibilityResponse>,
				GenericWretchError<
					| getGameHomeAwayMappingSwapEligibilityResponse403
					| getGameHomeAwayMappingSwapEligibilityResponse404
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<GameHomeAwayMappingSwapEligibilityResponse>,
				GenericWretchError<
					| getGameHomeAwayMappingSwapEligibilityResponse403
					| getGameHomeAwayMappingSwapEligibilityResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getOperationsManagementSummaryByCountry = (
	wretch: Http,
	queryClient: QueryClient,
) => {
	const queryFn = async (
		params: {
			page?: number;
			limit?: number;
			columns?: string[];
			filters?: object[];
			order?: object;
		},
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<DynamicDataGridResponse>> => {
		const args = z
			.object({
				page: z.number().int().describe("Page number (1-indexed)").default(1),
				limit: z
					.number()
					.int()
					.gte(1)
					.describe('Number of items per page (no limit for "all" option)')
					.default(20),
				columns: z
					.array(
						z.enum([
							"continent",
							"country",
							"countryName",
							"status",
							"totalPartnerClubs",
							"targetPartnerClubs",
							"numberOfScoutTeams",
							"numberOfDiscoverTeams",
							"consistentRunningPlayerPercent",
							"variablePlayerQualityPercent",
							"ntPlayers",
							"suggestedGamesUpdate",
							"playerToWatchUpdate",
							"seasonStart",
							"seasonEnd",
							"totalGames",
							"gamesTagged",
							"percentOfGamesTagged",
							"avgGamePriority",
							"avgGameAnalysisProcessing",
							"viewsOfGames",
							"proportionOfGameViews",
							"subscriptionsSold",
							"bronzeSold",
							"silverSold",
							"goldSold",
							"platinumSold",
							"otherSold",
							"refreshedAt",
						]),
					)
					.describe("Columns to include in response")
					.optional(),
				filters: z
					.array(
						z.object({
							type: z
								.enum([
									"exact",
									"search",
									"boolean",
									"exists",
									"inarray",
									"datetimerange",
									"numberrange",
								])
								.describe("Type of filter")
								.optional(),
							field: z
								.enum([
									"continent",
									"country",
									"status",
									"totalPartnerClubs",
									"targetPartnerClubs",
									"numberOfScoutTeams",
									"numberOfDiscoverTeams",
									"ntPlayers",
									"totalGames",
									"gamesTagged",
									"viewsOfGames",
									"subscriptionsSold",
									"consistentRunningPlayerPercent",
									"variablePlayerQualityPercent",
									"percentOfGamesTagged",
									"proportionOfGameViews",
									"avgGamePriority",
									"avgGameAnalysisProcessing",
									"suggestedGamesUpdate",
									"playerToWatchUpdate",
									"seasonStart",
									"seasonEnd",
								])
								.describe("Field to filter on")
								.optional(),
							value: z
								.any()
								.superRefine((x, ctx) => {
									const schemas = [
										z.string(),
										z.number().int(),
										z.boolean(),
										z.array(z.string()),
										z.object({
											min: z.number().nullable().optional(),
											max: z.number().nullable().optional(),
											start: z.string().nullable().optional(),
											end: z.string().nullable().optional(),
										}),
									];
									const errors = schemas.reduce<z.ZodError[]>(
										(errors, schema) =>
											((result) =>
												result.error ? [...errors, result.error] : errors)(
												schema.safeParse(x),
											),
										[],
									);
									if (schemas.length - errors.length !== 1) {
										ctx.addIssue({
											path: ctx.path,
											code: "invalid_union",
											unionErrors: errors,
											message: "Invalid input: Should pass single schema",
										});
									}
								})
								.describe(
									"Filter value (string, integer, boolean, array for inarray, or object with min/max for numberrange or start/end for datetimerange)",
								)
								.optional(),
						}),
					)
					.describe("Filter criteria")
					.optional(),
				order: z
					.object({
						field: z
							.enum([
								"continent",
								"country",
								"countryName",
								"status",
								"totalPartnerClubs",
								"targetPartnerClubs",
								"numberOfScoutTeams",
								"numberOfDiscoverTeams",
								"consistentRunningPlayerPercent",
								"variablePlayerQualityPercent",
								"ntPlayers",
								"suggestedGamesUpdate",
								"playerToWatchUpdate",
								"seasonStart",
								"seasonEnd",
								"totalGames",
								"gamesTagged",
								"percentOfGamesTagged",
								"avgGamePriority",
								"avgGameAnalysisProcessing",
								"viewsOfGames",
								"proportionOfGameViews",
								"subscriptionsSold",
								"refreshedAt",
							])
							.describe("Field to sort by")
							.optional(),
						direction: z
							.enum(["ASC", "DESC"])
							.describe("Sort direction")
							.optional(),
					})
					.describe("Sort configuration")
					.optional(),
			})
			.parse(params);
		const { page, limit, columns, filters, order } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url("/admin/operations-management/summary/by-country")
				.query(qs.stringify({ page, limit, columns, filters, order }))
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = DynamicDataGridResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json =
						getOperationsManagementSummaryByCountryResponse400.parse(json);
					break;
				}
				case 403: {
					err.json =
						getOperationsManagementSummaryByCountryResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: {
				page?: number;
				limit?: number;
				columns?: string[];
				filters?: object[];
				order?: object;
			},
			options?: Omit<
				UseQueryOptions<
					ApiResponse<DynamicDataGridResponse>,
					GenericWretchError<
						| getOperationsManagementSummaryByCountryResponse400
						| getOperationsManagementSummaryByCountryResponse403
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<DynamicDataGridResponse>,
			GenericWretchError<
				| getOperationsManagementSummaryByCountryResponse400
				| getOperationsManagementSummaryByCountryResponse403
			>
		> => {
			const key = [
				"getOperationsManagementSummaryByCountry",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<ApiResponse<DynamicDataGridResponse>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<DynamicDataGridResponse>,
				GenericWretchError<
					| getOperationsManagementSummaryByCountryResponse400
					| getOperationsManagementSummaryByCountryResponse403
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<DynamicDataGridResponse>,
				GenericWretchError<
					| getOperationsManagementSummaryByCountryResponse400
					| getOperationsManagementSummaryByCountryResponse403
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: {
				page?: number;
				limit?: number;
				columns?: string[];
				filters?: object[];
				order?: object;
			},
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<DynamicDataGridResponse>,
					GenericWretchError<
						| getOperationsManagementSummaryByCountryResponse400
						| getOperationsManagementSummaryByCountryResponse403
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<DynamicDataGridResponse>,
			GenericWretchError<
				| getOperationsManagementSummaryByCountryResponse400
				| getOperationsManagementSummaryByCountryResponse403
			>
		> => {
			const key = [
				"getOperationsManagementSummaryByCountry",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<ApiResponse<DynamicDataGridResponse>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<DynamicDataGridResponse>,
				GenericWretchError<
					| getOperationsManagementSummaryByCountryResponse400
					| getOperationsManagementSummaryByCountryResponse403
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<DynamicDataGridResponse>,
				GenericWretchError<
					| getOperationsManagementSummaryByCountryResponse400
					| getOperationsManagementSummaryByCountryResponse403
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getOperationsManagementSummaryByCountryFilterOptions = (
	wretch: Http,
	queryClient: QueryClient,
) => {
	const queryFn = async (
		params: { filter: string },
		signal?: AbortSignal | undefined,
	): Promise<
		ApiResponse<getOperationsManagementSummaryByCountryFilterOptionsResponse200>
	> => {
		const args = z
			.object({
				filter: z
					.enum([
						"continent",
						"country",
						"status",
						"totalPartnerClubs",
						"targetPartnerClubs",
						"numberOfScoutTeams",
						"numberOfDiscoverTeams",
						"ntPlayers",
						"totalGames",
						"gamesTagged",
						"viewsOfGames",
						"subscriptionsSold",
						"consistentRunningPlayerPercent",
						"variablePlayerQualityPercent",
						"percentOfGamesTagged",
						"proportionOfGameViews",
						"avgGamePriority",
						"avgGameAnalysisProcessing",
						"suggestedGamesUpdate",
						"playerToWatchUpdate",
						"seasonStart",
						"seasonEnd",
					])
					.describe("The filter field to get distinct values for"),
			})
			.parse(params);
		const { filter } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url("/admin/operations-management/summary/by-country/filter-options")
				.query(qs.stringify({ filter }))
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data =
						getOperationsManagementSummaryByCountryFilterOptionsResponse200.parse(
							json,
						);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json =
						getOperationsManagementSummaryByCountryFilterOptionsResponse400.parse(
							json,
						);
					break;
				}
				case 403: {
					err.json =
						getOperationsManagementSummaryByCountryFilterOptionsResponse403.parse(
							json,
						);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { filter: string },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<getOperationsManagementSummaryByCountryFilterOptionsResponse200>,
					GenericWretchError<
						| getOperationsManagementSummaryByCountryFilterOptionsResponse400
						| getOperationsManagementSummaryByCountryFilterOptionsResponse403
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<getOperationsManagementSummaryByCountryFilterOptionsResponse200>,
			GenericWretchError<
				| getOperationsManagementSummaryByCountryFilterOptionsResponse400
				| getOperationsManagementSummaryByCountryFilterOptionsResponse403
			>
		> => {
			const key = [
				"getOperationsManagementSummaryByCountryFilterOptions",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<getOperationsManagementSummaryByCountryFilterOptionsResponse200>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<getOperationsManagementSummaryByCountryFilterOptionsResponse200>,
				GenericWretchError<
					| getOperationsManagementSummaryByCountryFilterOptionsResponse400
					| getOperationsManagementSummaryByCountryFilterOptionsResponse403
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<getOperationsManagementSummaryByCountryFilterOptionsResponse200>,
				GenericWretchError<
					| getOperationsManagementSummaryByCountryFilterOptionsResponse400
					| getOperationsManagementSummaryByCountryFilterOptionsResponse403
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { filter: string },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<getOperationsManagementSummaryByCountryFilterOptionsResponse200>,
					GenericWretchError<
						| getOperationsManagementSummaryByCountryFilterOptionsResponse400
						| getOperationsManagementSummaryByCountryFilterOptionsResponse403
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<getOperationsManagementSummaryByCountryFilterOptionsResponse200>,
			GenericWretchError<
				| getOperationsManagementSummaryByCountryFilterOptionsResponse400
				| getOperationsManagementSummaryByCountryFilterOptionsResponse403
			>
		> => {
			const key = [
				"getOperationsManagementSummaryByCountryFilterOptions",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<getOperationsManagementSummaryByCountryFilterOptionsResponse200>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<getOperationsManagementSummaryByCountryFilterOptionsResponse200>,
				GenericWretchError<
					| getOperationsManagementSummaryByCountryFilterOptionsResponse400
					| getOperationsManagementSummaryByCountryFilterOptionsResponse403
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<getOperationsManagementSummaryByCountryFilterOptionsResponse200>,
				GenericWretchError<
					| getOperationsManagementSummaryByCountryFilterOptionsResponse400
					| getOperationsManagementSummaryByCountryFilterOptionsResponse403
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const get_admin_operations_management_summary_by_country_export = (
	wretch: Http,
	queryClient: QueryClient,
) => {
	const queryFn = async (
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<null>> => {
		try {
			const res = await wretch
				.options({ signal })
				.url("/admin/operations-management/summary/by-country/export")
				.get()
				.res();
		} catch (e) {
			const err = error(e);
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			options?: Omit<
				UseQueryOptions<ApiResponse<null>, GenericWretchError<null>>,
				"queryFn"
			>,
		): UseQueryResult<ApiResponse<null>, GenericWretchError<null>> => {
			const key = [
				"get_admin_operations_management_summary_by_country_export",
				...(options?.queryKey || []),
			];
			const query: QueryFunction<ApiResponse<null>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<ApiResponse<null>, GenericWretchError<null>>(
				key,
				query,
				options || {},
			) as UseQueryResult<ApiResponse<null>, GenericWretchError<null>>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			options?: Omit<
				UseInfiniteQueryOptions<ApiResponse<null>, GenericWretchError<null>>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<ApiResponse<null>, GenericWretchError<null>> => {
			const key = [
				"get_admin_operations_management_summary_by_country_export",
				...(options?.queryKey || []),
			];
			const query: QueryFunction<ApiResponse<null>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<null>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<null>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getOperationsManagementSummaryByClub = (
	wretch: Http,
	queryClient: QueryClient,
) => {
	const queryFn = async (
		params: {
			page?: number;
			limit?: number;
			columns?: string[];
			filters?: object[];
			order?: object;
		},
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<DynamicDataGridResponse>> => {
		const args = z
			.object({
				page: z.number().int().describe("Page number (1-indexed)").default(1),
				limit: z
					.number()
					.int()
					.gte(1)
					.describe('Number of items per page (no limit for "all" option)')
					.default(20),
				columns: z
					.array(
						z.enum([
							"continent",
							"country",
							"countryName",
							"clubId",
							"club",
							"manager",
							"onboardedDate",
							"numberOfScoutTeams",
							"numberOfDiscoverTeams",
							"consistentRunningPlayerPercent",
							"variablePlayerQualityPercent",
							"ntPlayers",
							"nonEbCamera",
							"teamsheetAvailability",
							"fixtureAvailability",
							"lastPartnersUsage",
							"totalGames",
							"gamesTagged",
							"percentOfGamesTagged",
							"avgGamePriority",
							"avgGameAnalysisProcessing",
							"viewsOfGames",
							"proportionOfGameViews",
							"contract",
							"contractEndDate",
							"consent",
							"marketingOptIn",
							"refreshedAt",
						]),
					)
					.describe("Columns to include in response")
					.optional(),
				filters: z
					.array(
						z.object({
							type: z
								.enum([
									"exact",
									"search",
									"boolean",
									"exists",
									"inarray",
									"datetimerange",
									"numberrange",
								])
								.describe("Type of filter")
								.optional(),
							field: z
								.enum([
									"continent",
									"country",
									"club",
									"manager",
									"onboardedDate",
									"contract",
									"consent",
									"nonEbCamera",
									"teamsheetAvailability",
									"fixtureAvailability",
									"marketingOptIn",
									"numberOfScoutTeams",
									"numberOfDiscoverTeams",
									"ntPlayers",
									"totalGames",
									"gamesTagged",
									"viewsOfGames",
									"consistentRunningPlayerPercent",
									"variablePlayerQualityPercent",
									"percentOfGamesTagged",
									"proportionOfGameViews",
									"avgGamePriority",
									"avgGameAnalysisProcessing",
									"lastPartnersUsage",
									"contractEndDate",
								])
								.describe("Field to filter on")
								.optional(),
							value: z
								.any()
								.superRefine((x, ctx) => {
									const schemas = [
										z.string(),
										z.number().int(),
										z.boolean(),
										z.array(z.string()),
										z.object({
											min: z.number().nullable().optional(),
											max: z.number().nullable().optional(),
											start: z.string().nullable().optional(),
											end: z.string().nullable().optional(),
										}),
									];
									const errors = schemas.reduce<z.ZodError[]>(
										(errors, schema) =>
											((result) =>
												result.error ? [...errors, result.error] : errors)(
												schema.safeParse(x),
											),
										[],
									);
									if (schemas.length - errors.length !== 1) {
										ctx.addIssue({
											path: ctx.path,
											code: "invalid_union",
											unionErrors: errors,
											message: "Invalid input: Should pass single schema",
										});
									}
								})
								.describe(
									"Filter value (string, integer, boolean, array for inarray, or object with min/max for numberrange or start/end for datetimerange)",
								)
								.optional(),
						}),
					)
					.describe("Filter criteria")
					.optional(),
				order: z
					.object({
						field: z
							.enum([
								"continent",
								"countryName",
								"club",
								"manager",
								"onboardedDate",
								"numberOfScoutTeams",
								"numberOfDiscoverTeams",
								"consistentRunningPlayerPercent",
								"variablePlayerQualityPercent",
								"ntPlayers",
								"nonEbCamera",
								"teamsheetAvailability",
								"fixtureAvailability",
								"lastPartnersUsage",
								"totalGames",
								"gamesTagged",
								"percentOfGamesTagged",
								"avgGamePriority",
								"avgGameAnalysisProcessing",
								"viewsOfGames",
								"proportionOfGameViews",
								"contract",
								"contractEndDate",
								"consent",
								"marketingOptIn",
								"refreshedAt",
							])
							.describe("Field to sort by")
							.optional(),
						direction: z
							.enum(["ASC", "DESC"])
							.describe("Sort direction")
							.optional(),
					})
					.describe("Sort configuration")
					.optional(),
			})
			.parse(params);
		const { page, limit, columns, filters, order } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url("/admin/operations-management/summary/by-club")
				.query(qs.stringify({ page, limit, columns, filters, order }))
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = DynamicDataGridResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json =
						getOperationsManagementSummaryByClubResponse400.parse(json);
					break;
				}
				case 403: {
					err.json =
						getOperationsManagementSummaryByClubResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: {
				page?: number;
				limit?: number;
				columns?: string[];
				filters?: object[];
				order?: object;
			},
			options?: Omit<
				UseQueryOptions<
					ApiResponse<DynamicDataGridResponse>,
					GenericWretchError<
						| getOperationsManagementSummaryByClubResponse400
						| getOperationsManagementSummaryByClubResponse403
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<DynamicDataGridResponse>,
			GenericWretchError<
				| getOperationsManagementSummaryByClubResponse400
				| getOperationsManagementSummaryByClubResponse403
			>
		> => {
			const key = [
				"getOperationsManagementSummaryByClub",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<ApiResponse<DynamicDataGridResponse>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<DynamicDataGridResponse>,
				GenericWretchError<
					| getOperationsManagementSummaryByClubResponse400
					| getOperationsManagementSummaryByClubResponse403
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<DynamicDataGridResponse>,
				GenericWretchError<
					| getOperationsManagementSummaryByClubResponse400
					| getOperationsManagementSummaryByClubResponse403
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: {
				page?: number;
				limit?: number;
				columns?: string[];
				filters?: object[];
				order?: object;
			},
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<DynamicDataGridResponse>,
					GenericWretchError<
						| getOperationsManagementSummaryByClubResponse400
						| getOperationsManagementSummaryByClubResponse403
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<DynamicDataGridResponse>,
			GenericWretchError<
				| getOperationsManagementSummaryByClubResponse400
				| getOperationsManagementSummaryByClubResponse403
			>
		> => {
			const key = [
				"getOperationsManagementSummaryByClub",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<ApiResponse<DynamicDataGridResponse>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<DynamicDataGridResponse>,
				GenericWretchError<
					| getOperationsManagementSummaryByClubResponse400
					| getOperationsManagementSummaryByClubResponse403
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<DynamicDataGridResponse>,
				GenericWretchError<
					| getOperationsManagementSummaryByClubResponse400
					| getOperationsManagementSummaryByClubResponse403
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getOperationsManagementSummaryByClubFilterOptions = (
	wretch: Http,
	queryClient: QueryClient,
) => {
	const queryFn = async (
		params: { filter: string },
		signal?: AbortSignal | undefined,
	): Promise<
		ApiResponse<getOperationsManagementSummaryByClubFilterOptionsResponse200>
	> => {
		const args = z
			.object({
				filter: z
					.enum([
						"continent",
						"country",
						"club",
						"manager",
						"onboardedDate",
						"contract",
						"consent",
						"nonEbCamera",
						"teamsheetAvailability",
						"fixtureAvailability",
						"marketingOptIn",
						"numberOfScoutTeams",
						"numberOfDiscoverTeams",
						"ntPlayers",
						"totalGames",
						"gamesTagged",
						"viewsOfGames",
						"consistentRunningPlayerPercent",
						"variablePlayerQualityPercent",
						"percentOfGamesTagged",
						"proportionOfGameViews",
						"avgGamePriority",
						"avgGameAnalysisProcessing",
						"lastPartnersUsage",
						"contractEndDate",
					])
					.describe("The filter field to get distinct values for"),
			})
			.parse(params);
		const { filter } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url("/admin/operations-management/summary/by-club/filter-options")
				.query(qs.stringify({ filter }))
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data =
						getOperationsManagementSummaryByClubFilterOptionsResponse200.parse(
							json,
						);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json =
						getOperationsManagementSummaryByClubFilterOptionsResponse400.parse(
							json,
						);
					break;
				}
				case 403: {
					err.json =
						getOperationsManagementSummaryByClubFilterOptionsResponse403.parse(
							json,
						);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { filter: string },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<getOperationsManagementSummaryByClubFilterOptionsResponse200>,
					GenericWretchError<
						| getOperationsManagementSummaryByClubFilterOptionsResponse400
						| getOperationsManagementSummaryByClubFilterOptionsResponse403
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<getOperationsManagementSummaryByClubFilterOptionsResponse200>,
			GenericWretchError<
				| getOperationsManagementSummaryByClubFilterOptionsResponse400
				| getOperationsManagementSummaryByClubFilterOptionsResponse403
			>
		> => {
			const key = [
				"getOperationsManagementSummaryByClubFilterOptions",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<getOperationsManagementSummaryByClubFilterOptionsResponse200>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<getOperationsManagementSummaryByClubFilterOptionsResponse200>,
				GenericWretchError<
					| getOperationsManagementSummaryByClubFilterOptionsResponse400
					| getOperationsManagementSummaryByClubFilterOptionsResponse403
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<getOperationsManagementSummaryByClubFilterOptionsResponse200>,
				GenericWretchError<
					| getOperationsManagementSummaryByClubFilterOptionsResponse400
					| getOperationsManagementSummaryByClubFilterOptionsResponse403
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { filter: string },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<getOperationsManagementSummaryByClubFilterOptionsResponse200>,
					GenericWretchError<
						| getOperationsManagementSummaryByClubFilterOptionsResponse400
						| getOperationsManagementSummaryByClubFilterOptionsResponse403
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<getOperationsManagementSummaryByClubFilterOptionsResponse200>,
			GenericWretchError<
				| getOperationsManagementSummaryByClubFilterOptionsResponse400
				| getOperationsManagementSummaryByClubFilterOptionsResponse403
			>
		> => {
			const key = [
				"getOperationsManagementSummaryByClubFilterOptions",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<getOperationsManagementSummaryByClubFilterOptionsResponse200>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<getOperationsManagementSummaryByClubFilterOptionsResponse200>,
				GenericWretchError<
					| getOperationsManagementSummaryByClubFilterOptionsResponse400
					| getOperationsManagementSummaryByClubFilterOptionsResponse403
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<getOperationsManagementSummaryByClubFilterOptionsResponse200>,
				GenericWretchError<
					| getOperationsManagementSummaryByClubFilterOptionsResponse400
					| getOperationsManagementSummaryByClubFilterOptionsResponse403
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const get_admin_operations_management_summary_by_club_export = (
	wretch: Http,
	queryClient: QueryClient,
) => {
	const queryFn = async (
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<null>> => {
		try {
			const res = await wretch
				.options({ signal })
				.url("/admin/operations-management/summary/by-club/export")
				.get()
				.res();
		} catch (e) {
			const err = error(e);
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			options?: Omit<
				UseQueryOptions<ApiResponse<null>, GenericWretchError<null>>,
				"queryFn"
			>,
		): UseQueryResult<ApiResponse<null>, GenericWretchError<null>> => {
			const key = [
				"get_admin_operations_management_summary_by_club_export",
				...(options?.queryKey || []),
			];
			const query: QueryFunction<ApiResponse<null>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<ApiResponse<null>, GenericWretchError<null>>(
				key,
				query,
				options || {},
			) as UseQueryResult<ApiResponse<null>, GenericWretchError<null>>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			options?: Omit<
				UseInfiniteQueryOptions<ApiResponse<null>, GenericWretchError<null>>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<ApiResponse<null>, GenericWretchError<null>> => {
			const key = [
				"get_admin_operations_management_summary_by_club_export",
				...(options?.queryKey || []),
			];
			const query: QueryFunction<ApiResponse<null>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<null>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<null>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const patchPlayerMembership = (wretch: Http) => {
	const mutation = async (params: {
		id: number;
		sourcePlayerDataId: number;
		targetPlayerDataId: number;
	}) => {
		const args = z
			.object({
				id: z.number().int(),
				sourcePlayerDataId: z.number().int(),
				targetPlayerDataId: z.number().int(),
			})
			.parse(params);
		const { id, sourcePlayerDataId, targetPlayerDataId } = args;

		try {
			const res = await wretch
				.url(
					`/admin/player/${id}/membership/from/${sourcePlayerDataId}/to/${targetPlayerDataId}`,
				)
				.patch()
				.res();
			switch (res.status) {
				case 204: {
					return {
						status: res.status,
						data: null,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = patchPlayerMembershipResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = patchPlayerMembershipResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = patchPlayerMembershipResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<null> | undefined,
					GenericWretchError<
						| patchPlayerMembershipResponse400
						| patchPlayerMembershipResponse403
						| patchPlayerMembershipResponse404
					>,
					{ id: number; sourcePlayerDataId: number; targetPlayerDataId: number }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<null> | undefined,
				GenericWretchError<
					| patchPlayerMembershipResponse400
					| patchPlayerMembershipResponse403
					| patchPlayerMembershipResponse404
				>,
				{ id: number; sourcePlayerDataId: number; targetPlayerDataId: number }
			>(mutation, options || {});
		},
	};
};

const jsonLogin = (wretch: Http) => {
	const mutation = async (params: { body: JsonLoginRequest }) => {
		const args = z.object({ body: z.any() }).parse(params);
		const { body } = args;

		try {
			const res = await wretch.url("/admin/json_login").json(body).post().res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = UserResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<UserResponse> | undefined,
					GenericWretchError<null>,
					{ body: JsonLoginRequest }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<UserResponse> | undefined,
				GenericWretchError<null>,
				{ body: JsonLoginRequest }
			>(mutation, options || {});
		},
	};
};

const getCurrentUser = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<UserResponse>> => {
		try {
			const res = await wretch.options({ signal }).url("/admin/me").get().res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = UserResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 401: {
					err.json = getCurrentUserResponse401.parse(json);
					break;
				}
				case 403: {
					err.json = getCurrentUserResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			options?: Omit<
				UseQueryOptions<
					ApiResponse<UserResponse>,
					GenericWretchError<
						getCurrentUserResponse401 | getCurrentUserResponse403
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<UserResponse>,
			GenericWretchError<getCurrentUserResponse401 | getCurrentUserResponse403>
		> => {
			const key = ["getCurrentUser", ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<UserResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<UserResponse>,
				GenericWretchError<
					getCurrentUserResponse401 | getCurrentUserResponse403
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<UserResponse>,
				GenericWretchError<
					getCurrentUserResponse401 | getCurrentUserResponse403
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<UserResponse>,
					GenericWretchError<
						getCurrentUserResponse401 | getCurrentUserResponse403
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<UserResponse>,
			GenericWretchError<getCurrentUserResponse401 | getCurrentUserResponse403>
		> => {
			const key = ["getCurrentUser", ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<UserResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<UserResponse>,
				GenericWretchError<
					getCurrentUserResponse401 | getCurrentUserResponse403
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<UserResponse>,
				GenericWretchError<
					getCurrentUserResponse401 | getCurrentUserResponse403
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const adminJsonLogout = (wretch: Http) => {
	const mutation = async () => {
		try {
			const res = await wretch.url("/admin/json_logout").post().res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = adminJsonLogoutResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 401: {
					err.json = adminJsonLogoutResponse401.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<adminJsonLogoutResponse200> | undefined,
					GenericWretchError<adminJsonLogoutResponse401>,
					void
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<adminJsonLogoutResponse200> | undefined,
				GenericWretchError<adminJsonLogoutResponse401>,
				void
			>(mutation, options || {});
		},
	};
};

const setUserLocale = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: { locale: string },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<setUserLocaleResponse200>> => {
		const args = z.object({ locale: z.string() }).parse(params);
		const { locale } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url("/admin/update_locale")
				.query(qs.stringify({ locale }))
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = setUserLocaleResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { locale: string },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<setUserLocaleResponse200>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<setUserLocaleResponse200>,
			GenericWretchError<null>
		> => {
			const key = ["setUserLocale", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<setUserLocaleResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<setUserLocaleResponse200>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<setUserLocaleResponse200>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { locale: string },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<setUserLocaleResponse200>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<setUserLocaleResponse200>,
			GenericWretchError<null>
		> => {
			const key = ["setUserLocale", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<setUserLocaleResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<setUserLocaleResponse200>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<setUserLocaleResponse200>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const forgotPassword = (wretch: Http) => {
	const mutation = async (params: { body: forgotPasswordRequestBody }) => {
		const args = z.object({ body: z.any() }).parse(params);
		const { body } = args;

		try {
			const res = await wretch
				.url("/admin/api/requestPassword")
				.json(body)
				.post()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = forgotPasswordResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 404: {
					err.json = forgotPasswordResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<forgotPasswordResponse200> | undefined,
					GenericWretchError<forgotPasswordResponse404>,
					{ body: forgotPasswordRequestBody }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<forgotPasswordResponse200> | undefined,
				GenericWretchError<forgotPasswordResponse404>,
				{ body: forgotPasswordRequestBody }
			>(mutation, options || {});
		},
	};
};

const resetPassword = (wretch: Http) => {
	const mutation = async (params: {
		token: string;
		body: resetPasswordRequestBody;
	}) => {
		const args = z.object({ token: z.string(), body: z.any() }).parse(params);
		const { token, body } = args;

		try {
			const res = await wretch
				.url(`/admin/api/resetPassword/${token}`)
				.json(body)
				.post()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = resetPasswordResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 404: {
					err.json = resetPasswordResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<resetPasswordResponse200> | undefined,
					GenericWretchError<resetPasswordResponse404>,
					{ token: string; body: resetPasswordRequestBody }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<resetPasswordResponse200> | undefined,
				GenericWretchError<resetPasswordResponse404>,
				{ token: string; body: resetPasswordRequestBody }
			>(mutation, options || {});
		},
	};
};

const validateUserPasswordReset = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: { token: string },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<validateUserPasswordResetResponse200>> => {
		const args = z.object({ token: z.string() }).parse(params);
		const { token } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/admin/api/validateUserPasswordReset/${token}`)
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = validateUserPasswordResetResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 403: {
					err.json = validateUserPasswordResetResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { token: string },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<validateUserPasswordResetResponse200>,
					GenericWretchError<validateUserPasswordResetResponse403>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<validateUserPasswordResetResponse200>,
			GenericWretchError<validateUserPasswordResetResponse403>
		> => {
			const key = [
				"validateUserPasswordReset",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<validateUserPasswordResetResponse200>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<validateUserPasswordResetResponse200>,
				GenericWretchError<validateUserPasswordResetResponse403>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<validateUserPasswordResetResponse200>,
				GenericWretchError<validateUserPasswordResetResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { token: string },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<validateUserPasswordResetResponse200>,
					GenericWretchError<validateUserPasswordResetResponse403>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<validateUserPasswordResetResponse200>,
			GenericWretchError<validateUserPasswordResetResponse403>
		> => {
			const key = [
				"validateUserPasswordReset",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<validateUserPasswordResetResponse200>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<validateUserPasswordResetResponse200>,
				GenericWretchError<validateUserPasswordResetResponse403>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<validateUserPasswordResetResponse200>,
				GenericWretchError<validateUserPasswordResetResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const searchNationalLeagues = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: { category?: string; club_id?: number; type?: string },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<searchNationalLeaguesResponse200>> => {
		const args = z
			.object({
				category: z.string().optional(),
				club_id: z.number().optional(),
				type: z.string().optional(),
			})
			.parse(params);
		const { category, club_id, type } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url("/admin/api/searchNationalLeagues")
				.query(qs.stringify({ category, club_id, type }))
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = searchNationalLeaguesResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { category?: string; club_id?: number; type?: string },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<searchNationalLeaguesResponse200>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<searchNationalLeaguesResponse200>,
			GenericWretchError<null>
		> => {
			const key = [
				"searchNationalLeagues",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<searchNationalLeaguesResponse200>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<searchNationalLeaguesResponse200>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<searchNationalLeaguesResponse200>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { category?: string; club_id?: number; type?: string },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<searchNationalLeaguesResponse200>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<searchNationalLeaguesResponse200>,
			GenericWretchError<null>
		> => {
			const key = [
				"searchNationalLeagues",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<searchNationalLeaguesResponse200>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<searchNationalLeaguesResponse200>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<searchNationalLeaguesResponse200>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getPermissionTree = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<getPermissionTreeResponse200>> => {
		try {
			const res = await wretch
				.options({ signal })
				.url("/admin/api/permissionTree")
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = getPermissionTreeResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 403: {
					err.json = getPermissionTreeResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			options?: Omit<
				UseQueryOptions<
					ApiResponse<getPermissionTreeResponse200>,
					GenericWretchError<getPermissionTreeResponse403>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<getPermissionTreeResponse200>,
			GenericWretchError<getPermissionTreeResponse403>
		> => {
			const key = ["getPermissionTree", ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<getPermissionTreeResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<getPermissionTreeResponse200>,
				GenericWretchError<getPermissionTreeResponse403>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<getPermissionTreeResponse200>,
				GenericWretchError<getPermissionTreeResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<getPermissionTreeResponse200>,
					GenericWretchError<getPermissionTreeResponse403>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<getPermissionTreeResponse200>,
			GenericWretchError<getPermissionTreeResponse403>
		> => {
			const key = ["getPermissionTree", ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<getPermissionTreeResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<getPermissionTreeResponse200>,
				GenericWretchError<getPermissionTreeResponse403>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<getPermissionTreeResponse200>,
				GenericWretchError<getPermissionTreeResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getPermissionByRole = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: { roleId: number },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<getPermissionByRoleResponse200>> => {
		const args = z.object({ roleId: z.number().int() }).parse(params);
		const { roleId } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/admin/api/permissionByRole/${roleId}`)
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = getPermissionByRoleResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 403: {
					err.json = getPermissionByRoleResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { roleId: number },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<getPermissionByRoleResponse200>,
					GenericWretchError<getPermissionByRoleResponse403>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<getPermissionByRoleResponse200>,
			GenericWretchError<getPermissionByRoleResponse403>
		> => {
			const key = ["getPermissionByRole", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<getPermissionByRoleResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<getPermissionByRoleResponse200>,
				GenericWretchError<getPermissionByRoleResponse403>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<getPermissionByRoleResponse200>,
				GenericWretchError<getPermissionByRoleResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { roleId: number },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<getPermissionByRoleResponse200>,
					GenericWretchError<getPermissionByRoleResponse403>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<getPermissionByRoleResponse200>,
			GenericWretchError<getPermissionByRoleResponse403>
		> => {
			const key = ["getPermissionByRole", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<getPermissionByRoleResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<getPermissionByRoleResponse200>,
				GenericWretchError<getPermissionByRoleResponse403>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<getPermissionByRoleResponse200>,
				GenericWretchError<getPermissionByRoleResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const assignPermissionToRole = (wretch: Http) => {
	const mutation = async (params: {
		roleId: number;
		body: assignPermissionToRole_Body;
	}) => {
		const args = z
			.object({ roleId: z.number().int(), body: z.any() })
			.parse(params);
		const { roleId, body } = args;

		try {
			const res = await wretch
				.url(`/admin/api/assignPermissionToRole/${roleId}`)
				.json(body)
				.post()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = assignPermissionToRoleResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 403: {
					err.json = assignPermissionToRoleResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<assignPermissionToRoleResponse200> | undefined,
					GenericWretchError<assignPermissionToRoleResponse403>,
					{ roleId: number; body: assignPermissionToRole_Body }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<assignPermissionToRoleResponse200> | undefined,
				GenericWretchError<assignPermissionToRoleResponse403>,
				{ roleId: number; body: assignPermissionToRole_Body }
			>(mutation, options || {});
		},
	};
};

const deleteRole = (wretch: Http) => {
	const mutation = async (params: { roleId: number }) => {
		const args = z.object({ roleId: z.number().int() }).parse(params);
		const { roleId } = args;

		try {
			const res = await wretch.url(`/admin/api/role/${roleId}`).delete().res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = deleteRoleResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 403: {
					err.json = deleteRoleResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<deleteRoleResponse200> | undefined,
					GenericWretchError<deleteRoleResponse403>,
					{ roleId: number }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<deleteRoleResponse200> | undefined,
				GenericWretchError<deleteRoleResponse403>,
				{ roleId: number }
			>(mutation, options || {});
		},
	};
};

const getRoles = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<getRolesResponse200>> => {
		try {
			const res = await wretch
				.options({ signal })
				.url("/admin/api/roles")
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = getRolesResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 403: {
					err.json = getRolesResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			options?: Omit<
				UseQueryOptions<
					ApiResponse<getRolesResponse200>,
					GenericWretchError<getRolesResponse403>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<getRolesResponse200>,
			GenericWretchError<getRolesResponse403>
		> => {
			const key = ["getRoles", ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<getRolesResponse200>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<getRolesResponse200>,
				GenericWretchError<getRolesResponse403>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<getRolesResponse200>,
				GenericWretchError<getRolesResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<getRolesResponse200>,
					GenericWretchError<getRolesResponse403>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<getRolesResponse200>,
			GenericWretchError<getRolesResponse403>
		> => {
			const key = ["getRoles", ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<getRolesResponse200>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<getRolesResponse200>,
				GenericWretchError<getRolesResponse403>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<getRolesResponse200>,
				GenericWretchError<getRolesResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getUserRoles = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<getUserRolesResponse200>> => {
		try {
			const res = await wretch
				.options({ signal })
				.url("/admin/api/user/roles")
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = getUserRolesResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 403: {
					err.json = getUserRolesResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			options?: Omit<
				UseQueryOptions<
					ApiResponse<getUserRolesResponse200>,
					GenericWretchError<getUserRolesResponse403>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<getUserRolesResponse200>,
			GenericWretchError<getUserRolesResponse403>
		> => {
			const key = ["getUserRoles", ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<getUserRolesResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<getUserRolesResponse200>,
				GenericWretchError<getUserRolesResponse403>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<getUserRolesResponse200>,
				GenericWretchError<getUserRolesResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<getUserRolesResponse200>,
					GenericWretchError<getUserRolesResponse403>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<getUserRolesResponse200>,
			GenericWretchError<getUserRolesResponse403>
		> => {
			const key = ["getUserRoles", ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<getUserRolesResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<getUserRolesResponse200>,
				GenericWretchError<getUserRolesResponse403>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<getUserRolesResponse200>,
				GenericWretchError<getUserRolesResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const addUpdateRole = (wretch: Http) => {
	const mutation = async (params: { body: addUpdateRole_Body }) => {
		const args = z.object({ body: z.any() }).parse(params);
		const { body } = args;

		try {
			const res = await wretch
				.url("/admin/api/addUpdateRole")
				.json(body)
				.post()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = addUpdateRoleResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 403: {
					err.json = addUpdateRoleResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<addUpdateRoleResponse200> | undefined,
					GenericWretchError<addUpdateRoleResponse403>,
					{ body: addUpdateRole_Body }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<addUpdateRoleResponse200> | undefined,
				GenericWretchError<addUpdateRoleResponse403>,
				{ body: addUpdateRole_Body }
			>(mutation, options || {});
		},
	};
};

const getAllAdminUsers = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: { start: number; length: number; search?: string },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<getAllAdminUsersResponse200>> => {
		const args = z
			.object({
				start: z.number().int(),
				length: z.number().int(),
				search: z.string().optional(),
			})
			.parse(params);
		const { start, length, search } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url("/admin/api/users")
				.query(qs.stringify({ start, length, search }))
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = getAllAdminUsersResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 403: {
					err.json = getAllAdminUsersResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { start: number; length: number; search?: string },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<getAllAdminUsersResponse200>,
					GenericWretchError<getAllAdminUsersResponse403>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<getAllAdminUsersResponse200>,
			GenericWretchError<getAllAdminUsersResponse403>
		> => {
			const key = ["getAllAdminUsers", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<getAllAdminUsersResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<getAllAdminUsersResponse200>,
				GenericWretchError<getAllAdminUsersResponse403>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<getAllAdminUsersResponse200>,
				GenericWretchError<getAllAdminUsersResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { start: number; length: number; search?: string },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<getAllAdminUsersResponse200>,
					GenericWretchError<getAllAdminUsersResponse403>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<getAllAdminUsersResponse200>,
			GenericWretchError<getAllAdminUsersResponse403>
		> => {
			const key = ["getAllAdminUsers", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<getAllAdminUsersResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<getAllAdminUsersResponse200>,
				GenericWretchError<getAllAdminUsersResponse403>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<getAllAdminUsersResponse200>,
				GenericWretchError<getAllAdminUsersResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const addUpdateAdminUsers = (wretch: Http) => {
	const mutation = async (params: { body: addUpdateAdminUsers_Body }) => {
		const args = z.object({ body: z.any() }).parse(params);
		const { body } = args;

		try {
			const res = await wretch
				.url("/admin/api/addUpdateAdminUser")
				.json(body)
				.post()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = addUpdateAdminUsersResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 403: {
					err.json = addUpdateAdminUsersResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<addUpdateAdminUsersResponse200> | undefined,
					GenericWretchError<addUpdateAdminUsersResponse403>,
					{ body: addUpdateAdminUsers_Body }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<addUpdateAdminUsersResponse200> | undefined,
				GenericWretchError<addUpdateAdminUsersResponse403>,
				{ body: addUpdateAdminUsers_Body }
			>(mutation, options || {});
		},
	};
};

const deleteAdminUser = (wretch: Http) => {
	const mutation = async (params: { id: number }) => {
		const args = z.object({ id: z.number().int() }).parse(params);
		const { id } = args;

		try {
			const res = await wretch.url(`/admin/api/user/${id}`).delete().res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = deleteAdminUserResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = deleteAdminUserResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = deleteAdminUserResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<deleteAdminUserResponse200> | undefined,
					GenericWretchError<
						deleteAdminUserResponse400 | deleteAdminUserResponse403
					>,
					{ id: number }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<deleteAdminUserResponse200> | undefined,
				GenericWretchError<
					deleteAdminUserResponse400 | deleteAdminUserResponse403
				>,
				{ id: number }
			>(mutation, options || {});
		},
	};
};

const uploadProfilePhoto = (wretch: Http) => {
	const mutation = async (params: { body: uploadProfilePhoto_Body }) => {
		const args = z.object({ body: z.any() }).parse(params);
		const { body } = args;

		try {
			const res = await wretch
				.url("/admin/api/user/upload-photo")
				.json(body)
				.post()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = uploadProfilePhotoResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 403: {
					err.json = uploadProfilePhotoResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<uploadProfilePhotoResponse200> | undefined,
					GenericWretchError<uploadProfilePhotoResponse403>,
					{ body: uploadProfilePhoto_Body }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<uploadProfilePhotoResponse200> | undefined,
				GenericWretchError<uploadProfilePhotoResponse403>,
				{ body: uploadProfilePhoto_Body }
			>(mutation, options || {});
		},
	};
};

const getAllPoles = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: { start: number; length: number; search?: string },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<getAllPolesResponse200>> => {
		const args = z
			.object({
				start: z.number().int(),
				length: z.number().int(),
				search: z.string().optional(),
			})
			.parse(params);
		const { start, length, search } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url("/admin/api/poles")
				.query(qs.stringify({ start, length, search }))
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = getAllPolesResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 403: {
					err.json = getAllPolesResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { start: number; length: number; search?: string },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<getAllPolesResponse200>,
					GenericWretchError<getAllPolesResponse403>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<getAllPolesResponse200>,
			GenericWretchError<getAllPolesResponse403>
		> => {
			const key = ["getAllPoles", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<getAllPolesResponse200>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<getAllPolesResponse200>,
				GenericWretchError<getAllPolesResponse403>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<getAllPolesResponse200>,
				GenericWretchError<getAllPolesResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { start: number; length: number; search?: string },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<getAllPolesResponse200>,
					GenericWretchError<getAllPolesResponse403>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<getAllPolesResponse200>,
			GenericWretchError<getAllPolesResponse403>
		> => {
			const key = ["getAllPoles", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<getAllPolesResponse200>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<getAllPolesResponse200>,
				GenericWretchError<getAllPolesResponse403>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<getAllPolesResponse200>,
				GenericWretchError<getAllPolesResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const addUpdatePole = (wretch: Http) => {
	const mutation = async (params: { body: addUpdatePole_Body }) => {
		const args = z.object({ body: z.any() }).parse(params);
		const { body } = args;

		try {
			const res = await wretch
				.url("/admin/api/addUpdatePole")
				.json(body)
				.post()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = addUpdatePoleResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 403: {
					err.json = addUpdatePoleResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<addUpdatePoleResponse200> | undefined,
					GenericWretchError<addUpdatePoleResponse403>,
					{ body: addUpdatePole_Body }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<addUpdatePoleResponse200> | undefined,
				GenericWretchError<addUpdatePoleResponse403>,
				{ body: addUpdatePole_Body }
			>(mutation, options || {});
		},
	};
};

const getTimezones = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<getTimezonesResponse200>> => {
		try {
			const res = await wretch
				.options({ signal })
				.url("/admin/api/getTimezones")
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = getTimezonesResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			options?: Omit<
				UseQueryOptions<
					ApiResponse<getTimezonesResponse200>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<getTimezonesResponse200>,
			GenericWretchError<null>
		> => {
			const key = ["getTimezones", ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<getTimezonesResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<getTimezonesResponse200>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<getTimezonesResponse200>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<getTimezonesResponse200>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<getTimezonesResponse200>,
			GenericWretchError<null>
		> => {
			const key = ["getTimezones", ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<getTimezonesResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<getTimezonesResponse200>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<getTimezonesResponse200>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const deletePole = (wretch: Http) => {
	const mutation = async (params: { id: number }) => {
		const args = z.object({ id: z.number().int() }).parse(params);
		const { id } = args;

		try {
			const res = await wretch.url(`/admin/api/pole/${id}`).delete().res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = deletePoleResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = deletePoleResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = deletePoleResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<deletePoleResponse200> | undefined,
					GenericWretchError<deletePoleResponse400 | deletePoleResponse403>,
					{ id: number }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<deletePoleResponse200> | undefined,
				GenericWretchError<deletePoleResponse400 | deletePoleResponse403>,
				{ id: number }
			>(mutation, options || {});
		},
	};
};

const getSelectedPoleByClub = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: { clubId: number },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<getSelectedPoleByClubResponse200>> => {
		const args = z.object({ clubId: z.number().int() }).parse(params);
		const { clubId } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/admin/api/pole/club/${clubId}`)
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = getSelectedPoleByClubResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = getSelectedPoleByClubResponse400.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { clubId: number },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<getSelectedPoleByClubResponse200>,
					GenericWretchError<getSelectedPoleByClubResponse400>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<getSelectedPoleByClubResponse200>,
			GenericWretchError<getSelectedPoleByClubResponse400>
		> => {
			const key = [
				"getSelectedPoleByClub",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<getSelectedPoleByClubResponse200>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<getSelectedPoleByClubResponse200>,
				GenericWretchError<getSelectedPoleByClubResponse400>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<getSelectedPoleByClubResponse200>,
				GenericWretchError<getSelectedPoleByClubResponse400>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { clubId: number },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<getSelectedPoleByClubResponse200>,
					GenericWretchError<getSelectedPoleByClubResponse400>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<getSelectedPoleByClubResponse200>,
			GenericWretchError<getSelectedPoleByClubResponse400>
		> => {
			const key = [
				"getSelectedPoleByClub",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<getSelectedPoleByClubResponse200>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<getSelectedPoleByClubResponse200>,
				GenericWretchError<getSelectedPoleByClubResponse400>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<getSelectedPoleByClubResponse200>,
				GenericWretchError<getSelectedPoleByClubResponse400>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const assignPole = (wretch: Http) => {
	const mutation = async (params: { id: number; body: assignPole_Body }) => {
		const args = z
			.object({ id: z.number().int(), body: z.any() })
			.parse(params);
		const { id, body } = args;

		try {
			const res = await wretch
				.url(`/admin/api/pole/${id}/assign`)
				.json(body)
				.put()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = assignPoleResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 403: {
					err.json = assignPoleResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<assignPoleResponse200> | undefined,
					GenericWretchError<assignPoleResponse403>,
					{ id: number; body: assignPole_Body }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<assignPoleResponse200> | undefined,
				GenericWretchError<assignPoleResponse403>,
				{ id: number; body: assignPole_Body }
			>(mutation, options || {});
		},
	};
};

const unAssignPole = (wretch: Http) => {
	const mutation = async (params: { id: number; clubId: number }) => {
		const args = z
			.object({ id: z.number().int(), clubId: z.number().int() })
			.parse(params);
		const { id, clubId } = args;

		try {
			const res = await wretch
				.url(`/admin/api/pole/${id}/un-assign/${clubId}`)
				.put()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = unAssignPoleResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 403: {
					err.json = unAssignPoleResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<unAssignPoleResponse200> | undefined,
					GenericWretchError<unAssignPoleResponse403>,
					{ id: number; clubId: number }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<unAssignPoleResponse200> | undefined,
				GenericWretchError<unAssignPoleResponse403>,
				{ id: number; clubId: number }
			>(mutation, options || {});
		},
	};
};

const syncPole = (wretch: Http) => {
	const mutation = async (params: { id: number }) => {
		const args = z.object({ id: z.number().int() }).parse(params);
		const { id } = args;

		try {
			const res = await wretch.url(`/admin/api/pole/${id}/sync`).put().res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = syncPoleResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = syncPoleResponse400.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<syncPoleResponse200> | undefined,
					GenericWretchError<syncPoleResponse400>,
					{ id: number }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<syncPoleResponse200> | undefined,
				GenericWretchError<syncPoleResponse400>,
				{ id: number }
			>(mutation, options || {});
		},
	};
};

const getClubList = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<getClubListResponse200>> => {
		try {
			const res = await wretch
				.options({ signal })
				.url("/admin/api/club")
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = getClubListResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 403: {
					err.json = getClubListResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			options?: Omit<
				UseQueryOptions<
					ApiResponse<getClubListResponse200>,
					GenericWretchError<getClubListResponse403>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<getClubListResponse200>,
			GenericWretchError<getClubListResponse403>
		> => {
			const key = ["getClubList", ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<getClubListResponse200>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<getClubListResponse200>,
				GenericWretchError<getClubListResponse403>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<getClubListResponse200>,
				GenericWretchError<getClubListResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<getClubListResponse200>,
					GenericWretchError<getClubListResponse403>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<getClubListResponse200>,
			GenericWretchError<getClubListResponse403>
		> => {
			const key = ["getClubList", ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<getClubListResponse200>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<getClubListResponse200>,
				GenericWretchError<getClubListResponse403>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<getClubListResponse200>,
				GenericWretchError<getClubListResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getCountries = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<getCountriesResponse200>> => {
		try {
			const res = await wretch
				.options({ signal })
				.url("/admin/api/nonPartnerClub/countries")
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = getCountriesResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 403: {
					err.json = getCountriesResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			options?: Omit<
				UseQueryOptions<
					ApiResponse<getCountriesResponse200>,
					GenericWretchError<getCountriesResponse403>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<getCountriesResponse200>,
			GenericWretchError<getCountriesResponse403>
		> => {
			const key = ["getCountries", ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<getCountriesResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<getCountriesResponse200>,
				GenericWretchError<getCountriesResponse403>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<getCountriesResponse200>,
				GenericWretchError<getCountriesResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<getCountriesResponse200>,
					GenericWretchError<getCountriesResponse403>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<getCountriesResponse200>,
			GenericWretchError<getCountriesResponse403>
		> => {
			const key = ["getCountries", ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<getCountriesResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<getCountriesResponse200>,
				GenericWretchError<getCountriesResponse403>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<getCountriesResponse200>,
				GenericWretchError<getCountriesResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getContinentCountry = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<getContinentCountryResponse200>> => {
		try {
			const res = await wretch
				.options({ signal })
				.url("/admin/api/getContinentCountry")
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = getContinentCountryResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 403: {
					err.json = getContinentCountryResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			options?: Omit<
				UseQueryOptions<
					ApiResponse<getContinentCountryResponse200>,
					GenericWretchError<getContinentCountryResponse403>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<getContinentCountryResponse200>,
			GenericWretchError<getContinentCountryResponse403>
		> => {
			const key = ["getContinentCountry", ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<getContinentCountryResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<getContinentCountryResponse200>,
				GenericWretchError<getContinentCountryResponse403>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<getContinentCountryResponse200>,
				GenericWretchError<getContinentCountryResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<getContinentCountryResponse200>,
					GenericWretchError<getContinentCountryResponse403>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<getContinentCountryResponse200>,
			GenericWretchError<getContinentCountryResponse403>
		> => {
			const key = ["getContinentCountry", ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<getContinentCountryResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<getContinentCountryResponse200>,
				GenericWretchError<getContinentCountryResponse403>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<getContinentCountryResponse200>,
				GenericWretchError<getContinentCountryResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getNPClubs = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: {
			start: number;
			length: number;
			search?: string;
			country?: string;
			showcase?: number;
		},
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<getNPClubsResponse200>> => {
		const args = z
			.object({
				start: z.number().int(),
				length: z.number().int(),
				search: z.string().nullable().optional(),
				country: z.string().nullable().optional(),
				showcase: z.number().int().nullable().optional(),
			})
			.parse(params);
		const { start, length, search, country, showcase } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url("/admin/api/nonPartnerClubs")
				.query(qs.stringify({ start, length, search, country, showcase }))
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = getNPClubsResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 403: {
					err.json = getNPClubsResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: {
				start: number;
				length: number;
				search?: string;
				country?: string;
				showcase?: number;
			},
			options?: Omit<
				UseQueryOptions<
					ApiResponse<getNPClubsResponse200>,
					GenericWretchError<getNPClubsResponse403>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<getNPClubsResponse200>,
			GenericWretchError<getNPClubsResponse403>
		> => {
			const key = ["getNPClubs", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<getNPClubsResponse200>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<getNPClubsResponse200>,
				GenericWretchError<getNPClubsResponse403>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<getNPClubsResponse200>,
				GenericWretchError<getNPClubsResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: {
				start: number;
				length: number;
				search?: string;
				country?: string;
				showcase?: number;
			},
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<getNPClubsResponse200>,
					GenericWretchError<getNPClubsResponse403>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<getNPClubsResponse200>,
			GenericWretchError<getNPClubsResponse403>
		> => {
			const key = ["getNPClubs", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<getNPClubsResponse200>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<getNPClubsResponse200>,
				GenericWretchError<getNPClubsResponse403>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<getNPClubsResponse200>,
				GenericWretchError<getNPClubsResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const addUpdateNPClub = (wretch: Http) => {
	const mutation = async (params: { body: addUpdateNPClub_Body }) => {
		const args = z.object({ body: z.any() }).parse(params);
		const { body } = args;

		try {
			const res = await wretch
				.url("/admin/api/addUpdateNPClub")
				.json(body)
				.post()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = addUpdateNPClubResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 403: {
					err.json = addUpdateNPClubResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<addUpdateNPClubResponse200> | undefined,
					GenericWretchError<addUpdateNPClubResponse403>,
					{ body: addUpdateNPClub_Body }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<addUpdateNPClubResponse200> | undefined,
				GenericWretchError<addUpdateNPClubResponse403>,
				{ body: addUpdateNPClub_Body }
			>(mutation, options || {});
		},
	};
};

const deleteNPClub = (wretch: Http) => {
	const mutation = async (params: { clubId: number }) => {
		const args = z.object({ clubId: z.number().int() }).parse(params);
		const { clubId } = args;

		try {
			const res = await wretch
				.url(`/admin/api/nonPartnerClub/${clubId}`)
				.delete()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = deleteNPClubResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 403: {
					err.json = deleteNPClubResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<deleteNPClubResponse200> | undefined,
					GenericWretchError<deleteNPClubResponse403>,
					{ clubId: number }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<deleteNPClubResponse200> | undefined,
				GenericWretchError<deleteNPClubResponse403>,
				{ clubId: number }
			>(mutation, options || {});
		},
	};
};

const uploadNPClubPhoto = (wretch: Http) => {
	const mutation = async (params: { body: uploadProfilePhoto_Body }) => {
		const args = z.object({ body: z.any() }).parse(params);
		const { body } = args;

		try {
			const res = await wretch
				.url("/admin/api/uploadNonPartnerClubLogo")
				.json(body)
				.post()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = uploadNPClubPhotoResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 403: {
					err.json = uploadNPClubPhotoResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<uploadNPClubPhotoResponse200> | undefined,
					GenericWretchError<uploadNPClubPhotoResponse403>,
					{ body: uploadProfilePhoto_Body }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<uploadNPClubPhotoResponse200> | undefined,
				GenericWretchError<uploadNPClubPhotoResponse403>,
				{ body: uploadProfilePhoto_Body }
			>(mutation, options || {});
		},
	};
};

const uploadNPPlayerPicture = (wretch: Http) => {
	const mutation = async (params: { body: uploadProfilePhoto_Body }) => {
		const args = z.object({ body: z.any() }).parse(params);
		const { body } = args;

		try {
			const res = await wretch
				.url("/admin/api/uploadNonPartnerPlayerPicture")
				.json(body)
				.post()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = uploadNPPlayerPictureResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 403: {
					err.json = uploadNPPlayerPictureResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<uploadNPPlayerPictureResponse200> | undefined,
					GenericWretchError<uploadNPPlayerPictureResponse403>,
					{ body: uploadProfilePhoto_Body }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<uploadNPPlayerPictureResponse200> | undefined,
				GenericWretchError<uploadNPPlayerPictureResponse403>,
				{ body: uploadProfilePhoto_Body }
			>(mutation, options || {});
		},
	};
};

const getNPPlayers = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: {
			start: number;
			length: number;
			search?: string;
			country?: string;
			clubNonpartnerId?: string;
			gender?: string;
		},
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<getNPPlayersResponse200>> => {
		const args = z
			.object({
				start: z.number().int(),
				length: z.number().int(),
				search: z.string().optional(),
				country: z.string().optional(),
				clubNonpartnerId: z.string().optional(),
				gender: z.string().optional(),
			})
			.parse(params);
		const { start, length, search, country, clubNonpartnerId, gender } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url("/admin/api/nonPartnerPlayers")
				.query(
					qs.stringify({
						start,
						length,
						search,
						country,
						clubNonpartnerId,
						gender,
					}),
				)
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = getNPPlayersResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 403: {
					err.json = getNPPlayersResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: {
				start: number;
				length: number;
				search?: string;
				country?: string;
				clubNonpartnerId?: string;
				gender?: string;
			},
			options?: Omit<
				UseQueryOptions<
					ApiResponse<getNPPlayersResponse200>,
					GenericWretchError<getNPPlayersResponse403>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<getNPPlayersResponse200>,
			GenericWretchError<getNPPlayersResponse403>
		> => {
			const key = ["getNPPlayers", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<getNPPlayersResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<getNPPlayersResponse200>,
				GenericWretchError<getNPPlayersResponse403>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<getNPPlayersResponse200>,
				GenericWretchError<getNPPlayersResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: {
				start: number;
				length: number;
				search?: string;
				country?: string;
				clubNonpartnerId?: string;
				gender?: string;
			},
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<getNPPlayersResponse200>,
					GenericWretchError<getNPPlayersResponse403>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<getNPPlayersResponse200>,
			GenericWretchError<getNPPlayersResponse403>
		> => {
			const key = ["getNPPlayers", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<getNPPlayersResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<getNPPlayersResponse200>,
				GenericWretchError<getNPPlayersResponse403>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<getNPPlayersResponse200>,
				GenericWretchError<getNPPlayersResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getNationalityList = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<getNationalityListResponse200>> => {
		try {
			const res = await wretch
				.options({ signal })
				.url("/admin/api/getNationalityList")
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = getNationalityListResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 403: {
					err.json = getNationalityListResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			options?: Omit<
				UseQueryOptions<
					ApiResponse<getNationalityListResponse200>,
					GenericWretchError<getNationalityListResponse403>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<getNationalityListResponse200>,
			GenericWretchError<getNationalityListResponse403>
		> => {
			const key = ["getNationalityList", ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<getNationalityListResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<getNationalityListResponse200>,
				GenericWretchError<getNationalityListResponse403>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<getNationalityListResponse200>,
				GenericWretchError<getNationalityListResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<getNationalityListResponse200>,
					GenericWretchError<getNationalityListResponse403>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<getNationalityListResponse200>,
			GenericWretchError<getNationalityListResponse403>
		> => {
			const key = ["getNationalityList", ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<getNationalityListResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<getNationalityListResponse200>,
				GenericWretchError<getNationalityListResponse403>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<getNationalityListResponse200>,
				GenericWretchError<getNationalityListResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getPositionList = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<getPositionListResponse200>> => {
		try {
			const res = await wretch
				.options({ signal })
				.url("/admin/api/getPositionList")
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = getPositionListResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 403: {
					err.json = getPositionListResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			options?: Omit<
				UseQueryOptions<
					ApiResponse<getPositionListResponse200>,
					GenericWretchError<getPositionListResponse403>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<getPositionListResponse200>,
			GenericWretchError<getPositionListResponse403>
		> => {
			const key = ["getPositionList", ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<getPositionListResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<getPositionListResponse200>,
				GenericWretchError<getPositionListResponse403>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<getPositionListResponse200>,
				GenericWretchError<getPositionListResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<getPositionListResponse200>,
					GenericWretchError<getPositionListResponse403>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<getPositionListResponse200>,
			GenericWretchError<getPositionListResponse403>
		> => {
			const key = ["getPositionList", ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<getPositionListResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<getPositionListResponse200>,
				GenericWretchError<getPositionListResponse403>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<getPositionListResponse200>,
				GenericWretchError<getPositionListResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getGeographicalAreaList = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<getGeographicalAreaListResponse200>> => {
		try {
			const res = await wretch
				.options({ signal })
				.url("/admin/api/getGeographicalAreaList")
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = getGeographicalAreaListResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 403: {
					err.json = getGeographicalAreaListResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			options?: Omit<
				UseQueryOptions<
					ApiResponse<getGeographicalAreaListResponse200>,
					GenericWretchError<getGeographicalAreaListResponse403>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<getGeographicalAreaListResponse200>,
			GenericWretchError<getGeographicalAreaListResponse403>
		> => {
			const key = ["getGeographicalAreaList", ...(options?.queryKey || [])];
			const query: QueryFunction<
				ApiResponse<getGeographicalAreaListResponse200>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<getGeographicalAreaListResponse200>,
				GenericWretchError<getGeographicalAreaListResponse403>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<getGeographicalAreaListResponse200>,
				GenericWretchError<getGeographicalAreaListResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<getGeographicalAreaListResponse200>,
					GenericWretchError<getGeographicalAreaListResponse403>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<getGeographicalAreaListResponse200>,
			GenericWretchError<getGeographicalAreaListResponse403>
		> => {
			const key = ["getGeographicalAreaList", ...(options?.queryKey || [])];
			const query: QueryFunction<
				ApiResponse<getGeographicalAreaListResponse200>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<getGeographicalAreaListResponse200>,
				GenericWretchError<getGeographicalAreaListResponse403>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<getGeographicalAreaListResponse200>,
				GenericWretchError<getGeographicalAreaListResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const addUpdateNPPlayer = (wretch: Http) => {
	const mutation = async (params: {
		body: CreateNonPartnerPlayerDtoRequest;
	}) => {
		const args = z.object({ body: z.any() }).parse(params);
		const { body } = args;

		try {
			const res = await wretch
				.url("/admin/api/addUpdateNPPlayer")
				.json(body)
				.post()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = addUpdateNPPlayerResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 403: {
					err.json = addUpdateNPPlayerResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<addUpdateNPPlayerResponse200> | undefined,
					GenericWretchError<addUpdateNPPlayerResponse403>,
					{ body: CreateNonPartnerPlayerDtoRequest }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<addUpdateNPPlayerResponse200> | undefined,
				GenericWretchError<addUpdateNPPlayerResponse403>,
				{ body: CreateNonPartnerPlayerDtoRequest }
			>(mutation, options || {});
		},
	};
};

const deleteNPPlayer = (wretch: Http) => {
	const mutation = async (params: { playerId: number }) => {
		const args = z.object({ playerId: z.number().int() }).parse(params);
		const { playerId } = args;

		try {
			const res = await wretch
				.url(`/admin/api/nonPartnerPlayer/${playerId}`)
				.delete()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = deleteNPPlayerResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 403: {
					err.json = deleteNPPlayerResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<deleteNPPlayerResponse200> | undefined,
					GenericWretchError<deleteNPPlayerResponse403>,
					{ playerId: number }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<deleteNPPlayerResponse200> | undefined,
				GenericWretchError<deleteNPPlayerResponse403>,
				{ playerId: number }
			>(mutation, options || {});
		},
	};
};

const getAllNPClubs = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<getAllNPClubsResponse200>> => {
		try {
			const res = await wretch
				.options({ signal })
				.url("/admin/api/nonPartnerPlayer/clubOptions")
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = getAllNPClubsResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 403: {
					err.json = getAllNPClubsResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			options?: Omit<
				UseQueryOptions<
					ApiResponse<getAllNPClubsResponse200>,
					GenericWretchError<getAllNPClubsResponse403>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<getAllNPClubsResponse200>,
			GenericWretchError<getAllNPClubsResponse403>
		> => {
			const key = ["getAllNPClubs", ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<getAllNPClubsResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<getAllNPClubsResponse200>,
				GenericWretchError<getAllNPClubsResponse403>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<getAllNPClubsResponse200>,
				GenericWretchError<getAllNPClubsResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<getAllNPClubsResponse200>,
					GenericWretchError<getAllNPClubsResponse403>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<getAllNPClubsResponse200>,
			GenericWretchError<getAllNPClubsResponse403>
		> => {
			const key = ["getAllNPClubs", ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<getAllNPClubsResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<getAllNPClubsResponse200>,
				GenericWretchError<getAllNPClubsResponse403>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<getAllNPClubsResponse200>,
				GenericWretchError<getAllNPClubsResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getClubPlayers = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: { clubId: number; type: string },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<getClubPlayersResponse200>> => {
		const args = z
			.object({ clubId: z.number().int(), type: z.string() })
			.parse(params);
		const { clubId, type } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/admin/api/${clubId}/players`)
				.query(qs.stringify({ type }))
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = getClubPlayersResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { clubId: number; type: string },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<getClubPlayersResponse200>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<getClubPlayersResponse200>,
			GenericWretchError<null>
		> => {
			const key = ["getClubPlayers", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<getClubPlayersResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<getClubPlayersResponse200>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<getClubPlayersResponse200>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { clubId: number; type: string },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<getClubPlayersResponse200>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<getClubPlayersResponse200>,
			GenericWretchError<null>
		> => {
			const key = ["getClubPlayers", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<getClubPlayersResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<getClubPlayersResponse200>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<getClubPlayersResponse200>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const executeNPClubMigration = (wretch: Http) => {
	const mutation = async (params: { body: executeNPClubMigration_Body }) => {
		const args = z.object({ body: z.any() }).parse(params);
		const { body } = args;

		try {
			const res = await wretch
				.url("/admin/api/executeNonPartnerClubMigration")
				.json(body)
				.post()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = executeNPClubMigrationResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 403: {
					err.json = executeNPClubMigrationResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<executeNPClubMigrationResponse200> | undefined,
					GenericWretchError<executeNPClubMigrationResponse403>,
					{ body: executeNPClubMigration_Body }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<executeNPClubMigrationResponse200> | undefined,
				GenericWretchError<executeNPClubMigrationResponse403>,
				{ body: executeNPClubMigration_Body }
			>(mutation, options || {});
		},
	};
};

const getTeamsByTargetClub = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: { clubId: number },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<getTeamsByTargetClubResponse200>> => {
		const args = z.object({ clubId: z.number().int() }).parse(params);
		const { clubId } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/admin/api/getTeamsByTargetClub/${clubId}`)
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = getTeamsByTargetClubResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 403: {
					err.json = getTeamsByTargetClubResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { clubId: number },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<getTeamsByTargetClubResponse200>,
					GenericWretchError<getTeamsByTargetClubResponse403>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<getTeamsByTargetClubResponse200>,
			GenericWretchError<getTeamsByTargetClubResponse403>
		> => {
			const key = [
				"getTeamsByTargetClub",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<ApiResponse<getTeamsByTargetClubResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<getTeamsByTargetClubResponse200>,
				GenericWretchError<getTeamsByTargetClubResponse403>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<getTeamsByTargetClubResponse200>,
				GenericWretchError<getTeamsByTargetClubResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { clubId: number },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<getTeamsByTargetClubResponse200>,
					GenericWretchError<getTeamsByTargetClubResponse403>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<getTeamsByTargetClubResponse200>,
			GenericWretchError<getTeamsByTargetClubResponse403>
		> => {
			const key = [
				"getTeamsByTargetClub",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<ApiResponse<getTeamsByTargetClubResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<getTeamsByTargetClubResponse200>,
				GenericWretchError<getTeamsByTargetClubResponse403>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<getTeamsByTargetClubResponse200>,
				GenericWretchError<getTeamsByTargetClubResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const playerExistsTargetClub = (wretch: Http) => {
	const mutation = async (params: { body: playerExistsTargetClub_Body }) => {
		const args = z.object({ body: z.any() }).parse(params);
		const { body } = args;

		try {
			const res = await wretch
				.url("/admin/api/playerExistsTargetClub")
				.json(body)
				.post()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = playerExistsTargetClubResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 403: {
					err.json = playerExistsTargetClubResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<playerExistsTargetClubResponse200> | undefined,
					GenericWretchError<playerExistsTargetClubResponse403>,
					{ body: playerExistsTargetClub_Body }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<playerExistsTargetClubResponse200> | undefined,
				GenericWretchError<playerExistsTargetClubResponse403>,
				{ body: playerExistsTargetClub_Body }
			>(mutation, options || {});
		},
	};
};

const getMigrationStatus = (wretch: Http) => {
	const mutation = async (params: { body: getMigrationStatus_Body }) => {
		const args = z.object({ body: z.any() }).parse(params);
		const { body } = args;

		try {
			const res = await wretch
				.url("/admin/api/getMigrationStatus")
				.json(body)
				.post()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = getMigrationStatusResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 403: {
					err.json = getMigrationStatusResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<getMigrationStatusResponse200> | undefined,
					GenericWretchError<getMigrationStatusResponse403>,
					{ body: getMigrationStatus_Body }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<getMigrationStatusResponse200> | undefined,
				GenericWretchError<getMigrationStatusResponse403>,
				{ body: getMigrationStatus_Body }
			>(mutation, options || {});
		},
	};
};

const executePartnerClubMigration = (wretch: Http) => {
	const mutation = async (params: { body: executeNPClubMigration_Body }) => {
		const args = z.object({ body: z.any() }).parse(params);
		const { body } = args;

		try {
			const res = await wretch
				.url("/admin/api/executePartnerClubMigration")
				.json(body)
				.post()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = executePartnerClubMigrationResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 403: {
					err.json = executePartnerClubMigrationResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<executePartnerClubMigrationResponse200> | undefined,
					GenericWretchError<executePartnerClubMigrationResponse403>,
					{ body: executeNPClubMigration_Body }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<executePartnerClubMigrationResponse200> | undefined,
				GenericWretchError<executePartnerClubMigrationResponse403>,
				{ body: executeNPClubMigration_Body }
			>(mutation, options || {});
		},
	};
};

const createPartnerPlayerFromNonpartner = (wretch: Http) => {
	const mutation = async (params: {
		body: createPartnerPlayerFromNonpartner_Body;
	}) => {
		const args = z.object({ body: z.any() }).parse(params);
		const { body } = args;

		try {
			const res = await wretch
				.url("/admin/api/createPartnerPlayerFromNonpartner")
				.json(body)
				.post()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = createPartnerPlayerFromNonpartnerResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<createPartnerPlayerFromNonpartnerResponse200> | undefined,
					GenericWretchError<null>,
					{ body: createPartnerPlayerFromNonpartner_Body }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<createPartnerPlayerFromNonpartnerResponse200> | undefined,
				GenericWretchError<null>,
				{ body: createPartnerPlayerFromNonpartner_Body }
			>(mutation, options || {});
		},
	};
};

const partnerPlayerExistsTargetClub = (wretch: Http) => {
	const mutation = async (params: {
		body: partnerPlayerExistsTargetClub_Body;
	}) => {
		const args = z.object({ body: z.any() }).parse(params);
		const { body } = args;

		try {
			const res = await wretch
				.url("/admin/api/partnerPlayerExistsTargetClub")
				.json(body)
				.post()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = partnerPlayerExistsTargetClubResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 403: {
					err.json = partnerPlayerExistsTargetClubResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<partnerPlayerExistsTargetClubResponse200> | undefined,
					GenericWretchError<partnerPlayerExistsTargetClubResponse403>,
					{ body: partnerPlayerExistsTargetClub_Body }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<partnerPlayerExistsTargetClubResponse200> | undefined,
				GenericWretchError<partnerPlayerExistsTargetClubResponse403>,
				{ body: partnerPlayerExistsTargetClub_Body }
			>(mutation, options || {});
		},
	};
};

const getPartnerMigrationStatus = (wretch: Http) => {
	const mutation = async (params: { body: getPartnerMigrationStatus_Body }) => {
		const args = z.object({ body: z.any() }).parse(params);
		const { body } = args;

		try {
			const res = await wretch
				.url("/admin/api/getPartnerMigrationStatus")
				.json(body)
				.post()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = getPartnerMigrationStatusResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 403: {
					err.json = getPartnerMigrationStatusResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<getPartnerMigrationStatusResponse200> | undefined,
					GenericWretchError<getPartnerMigrationStatusResponse403>,
					{ body: getPartnerMigrationStatus_Body }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<getPartnerMigrationStatusResponse200> | undefined,
				GenericWretchError<getPartnerMigrationStatusResponse403>,
				{ body: getPartnerMigrationStatus_Body }
			>(mutation, options || {});
		},
	};
};

const updateGameTeam = (wretch: Http) => {
	const mutation = async (params: { body: updateGameTeam_Body }) => {
		const args = z.object({ body: z.any() }).parse(params);
		const { body } = args;

		try {
			const res = await wretch
				.url("/admin/api/updateGameTeam")
				.json(body)
				.post()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = updateGameTeamResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 403: {
					err.json = updateGameTeamResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<updateGameTeamResponse200> | undefined,
					GenericWretchError<updateGameTeamResponse403>,
					{ body: updateGameTeam_Body }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<updateGameTeamResponse200> | undefined,
				GenericWretchError<updateGameTeamResponse403>,
				{ body: updateGameTeam_Body }
			>(mutation, options || {});
		},
	};
};

const migrateSourceClubNoPlayers = (wretch: Http) => {
	const mutation = async (params: {
		sourceClubId: number;
		targetClubId: number;
		type: string;
	}) => {
		const args = z
			.object({
				sourceClubId: z.number().int(),
				targetClubId: z.number().int(),
				type: z.string(),
			})
			.parse(params);
		const { sourceClubId, targetClubId, type } = args;

		try {
			const res = await wretch
				.url("/admin/api/migrateSourceClubNoPlayers")
				.query(qs.stringify({ sourceClubId, targetClubId, type }))
				.delete()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = migrateSourceClubNoPlayersResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 403: {
					err.json = migrateSourceClubNoPlayersResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<migrateSourceClubNoPlayersResponse200> | undefined,
					GenericWretchError<migrateSourceClubNoPlayersResponse403>,
					{ sourceClubId: number; targetClubId: number; type: string }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<migrateSourceClubNoPlayersResponse200> | undefined,
				GenericWretchError<migrateSourceClubNoPlayersResponse403>,
				{ sourceClubId: number; targetClubId: number; type: string }
			>(mutation, options || {});
		},
	};
};

const beginMigration = (wretch: Http) => {
	const mutation = async (params: { body: beginMigration_Body }) => {
		const args = z.object({ body: z.any() }).parse(params);
		const { body } = args;

		try {
			const res = await wretch
				.url("/admin/api/beginMigration")
				.json(body)
				.post()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = BeginMigration.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<BeginMigration> | undefined,
					GenericWretchError<null>,
					{ body: beginMigration_Body }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<BeginMigration> | undefined,
				GenericWretchError<null>,
				{ body: beginMigration_Body }
			>(mutation, options || {});
		},
	};
};

const clubMigrationWithZeroPlayers = (wretch: Http) => {
	const mutation = async (params: {
		body: clubMigrationWithZeroPlayers_Body;
	}) => {
		const args = z.object({ body: z.any() }).parse(params);
		const { body } = args;

		try {
			const res = await wretch
				.url("/admin/api/clubMigrationWithZeroPlayers")
				.json(body)
				.post()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = clubMigrationWithZeroPlayersResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<clubMigrationWithZeroPlayersResponse200> | undefined,
					GenericWretchError<null>,
					{ body: clubMigrationWithZeroPlayers_Body }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<clubMigrationWithZeroPlayersResponse200> | undefined,
				GenericWretchError<null>,
				{ body: clubMigrationWithZeroPlayers_Body }
			>(mutation, options || {});
		},
	};
};

const completeMigration = (wretch: Http) => {
	const mutation = async (params: { body: completeMigration_Body }) => {
		const args = z.object({ body: z.any() }).parse(params);
		const { body } = args;

		try {
			const res = await wretch
				.url("/admin/api/completeMigration")
				.json(body)
				.post()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = CompleteMigration.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<CompleteMigration> | undefined,
					GenericWretchError<null>,
					{ body: completeMigration_Body }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<CompleteMigration> | undefined,
				GenericWretchError<null>,
				{ body: completeMigration_Body }
			>(mutation, options || {});
		},
	};
};

const playerMigrationToPartner = (wretch: Http) => {
	const mutation = async (params: { body: playerMigrationToPartner_Body }) => {
		const args = z.object({ body: z.any() }).parse(params);
		const { body } = args;

		try {
			const res = await wretch
				.url("/admin/api/playerMigrationToPartner")
				.json(body)
				.post()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = playerMigrationToPartnerResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<playerMigrationToPartnerResponse200> | undefined,
					GenericWretchError<null>,
					{ body: playerMigrationToPartner_Body }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<playerMigrationToPartnerResponse200> | undefined,
				GenericWretchError<null>,
				{ body: playerMigrationToPartner_Body }
			>(mutation, options || {});
		},
	};
};

const playerMigrationConflict = (wretch: Http) => {
	const mutation = async (params: { body: playerMigrationConflict_Body }) => {
		const args = z.object({ body: z.any().optional() }).parse(params);
		const { body } = args;

		try {
			const res = await wretch
				.url("/admin/api/playerMigrationConflict")
				.json(body)
				.post()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = playerMigrationConflictResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<playerMigrationConflictResponse200> | undefined,
					GenericWretchError<null>,
					{ body: playerMigrationConflict_Body }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<playerMigrationConflictResponse200> | undefined,
				GenericWretchError<null>,
				{ body: playerMigrationConflict_Body }
			>(mutation, options || {});
		},
	};
};

const getAllCountriesTooltip = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: {
			start: number;
			length: number;
			search?: string;
			country?: string;
			enabled?: string;
			maleEnabled?: string;
			femaleEnabled?: string;
		},
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<getAllCountriesTooltipResponse200>> => {
		const args = z
			.object({
				start: z.number().int(),
				length: z.number().int(),
				search: z.string().optional(),
				country: z.string().optional(),
				enabled: z.string().optional(),
				maleEnabled: z.string().optional(),
				femaleEnabled: z.string().optional(),
			})
			.parse(params);
		const {
			start,
			length,
			search,
			country,
			enabled,
			maleEnabled,
			femaleEnabled,
		} = args;
		try {
			const res = await wretch
				.options({ signal })
				.url("/admin/api/countries/tooltip")
				.query(
					qs.stringify({
						start,
						length,
						search,
						country,
						enabled,
						maleEnabled,
						femaleEnabled,
					}),
				)
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = getAllCountriesTooltipResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 403: {
					err.json = getAllCountriesTooltipResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: {
				start: number;
				length: number;
				search?: string;
				country?: string;
				enabled?: string;
				maleEnabled?: string;
				femaleEnabled?: string;
			},
			options?: Omit<
				UseQueryOptions<
					ApiResponse<getAllCountriesTooltipResponse200>,
					GenericWretchError<getAllCountriesTooltipResponse403>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<getAllCountriesTooltipResponse200>,
			GenericWretchError<getAllCountriesTooltipResponse403>
		> => {
			const key = [
				"getAllCountriesTooltip",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<getAllCountriesTooltipResponse200>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<getAllCountriesTooltipResponse200>,
				GenericWretchError<getAllCountriesTooltipResponse403>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<getAllCountriesTooltipResponse200>,
				GenericWretchError<getAllCountriesTooltipResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: {
				start: number;
				length: number;
				search?: string;
				country?: string;
				enabled?: string;
				maleEnabled?: string;
				femaleEnabled?: string;
			},
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<getAllCountriesTooltipResponse200>,
					GenericWretchError<getAllCountriesTooltipResponse403>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<getAllCountriesTooltipResponse200>,
			GenericWretchError<getAllCountriesTooltipResponse403>
		> => {
			const key = [
				"getAllCountriesTooltip",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<getAllCountriesTooltipResponse200>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<getAllCountriesTooltipResponse200>,
				GenericWretchError<getAllCountriesTooltipResponse403>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<getAllCountriesTooltipResponse200>,
				GenericWretchError<getAllCountriesTooltipResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const addUpdateCountry = (wretch: Http) => {
	const mutation = async (params: { body: addUpdateCountry_Body }) => {
		const args = z.object({ body: z.any() }).parse(params);
		const { body } = args;

		try {
			const res = await wretch
				.url("/admin/api/countries")
				.json(body)
				.post()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = addUpdateCountryResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 403: {
					err.json = addUpdateCountryResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<addUpdateCountryResponse200> | undefined,
					GenericWretchError<addUpdateCountryResponse403>,
					{ body: addUpdateCountry_Body }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<addUpdateCountryResponse200> | undefined,
				GenericWretchError<addUpdateCountryResponse403>,
				{ body: addUpdateCountry_Body }
			>(mutation, options || {});
		},
	};
};

const deleteCountry = (wretch: Http) => {
	const mutation = async (params: { id: number }) => {
		const args = z.object({ id: z.number().int() }).parse(params);
		const { id } = args;

		try {
			const res = await wretch.url(`/admin/api/countries/${id}`).delete().res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = deleteCountryResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = deleteCountryResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = deleteCountryResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<deleteCountryResponse200> | undefined,
					GenericWretchError<
						deleteCountryResponse400 | deleteCountryResponse403
					>,
					{ id: number }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<deleteCountryResponse200> | undefined,
				GenericWretchError<deleteCountryResponse400 | deleteCountryResponse403>,
				{ id: number }
			>(mutation, options || {});
		},
	};
};

const allCountries = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<allCountriesResponse200>> => {
		try {
			const res = await wretch
				.options({ signal })
				.url("/admin/api/all-countries")
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = allCountriesResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 403: {
					err.json = allCountriesResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			options?: Omit<
				UseQueryOptions<
					ApiResponse<allCountriesResponse200>,
					GenericWretchError<allCountriesResponse403>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<allCountriesResponse200>,
			GenericWretchError<allCountriesResponse403>
		> => {
			const key = ["allCountries", ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<allCountriesResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<allCountriesResponse200>,
				GenericWretchError<allCountriesResponse403>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<allCountriesResponse200>,
				GenericWretchError<allCountriesResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<allCountriesResponse200>,
					GenericWretchError<allCountriesResponse403>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<allCountriesResponse200>,
			GenericWretchError<allCountriesResponse403>
		> => {
			const key = ["allCountries", ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<allCountriesResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<allCountriesResponse200>,
				GenericWretchError<allCountriesResponse403>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<allCountriesResponse200>,
				GenericWretchError<allCountriesResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const enableDisableCountry = (wretch: Http) => {
	const mutation = async (params: {
		id: number;
		body: EnableDisableCountryTooltipRequest;
	}) => {
		const args = z
			.object({ id: z.number().int(), body: z.any() })
			.parse(params);
		const { id, body } = args;

		try {
			const res = await wretch
				.url(`/admin/api/countries/${id}/activate`)
				.json(body)
				.put()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = enableDisableCountryResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 403: {
					err.json = enableDisableCountryResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<enableDisableCountryResponse200> | undefined,
					GenericWretchError<enableDisableCountryResponse403>,
					{ id: number; body: EnableDisableCountryTooltipRequest }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<enableDisableCountryResponse200> | undefined,
				GenericWretchError<enableDisableCountryResponse403>,
				{ id: number; body: EnableDisableCountryTooltipRequest }
			>(mutation, options || {});
		},
	};
};

const loadOwnerships = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<loadOwnershipsResponse200>> => {
		try {
			const res = await wretch
				.options({ signal })
				.url("/admin/api/ownerships")
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = loadOwnershipsResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 403: {
					err.json = loadOwnershipsResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			options?: Omit<
				UseQueryOptions<
					ApiResponse<loadOwnershipsResponse200>,
					GenericWretchError<loadOwnershipsResponse403>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<loadOwnershipsResponse200>,
			GenericWretchError<loadOwnershipsResponse403>
		> => {
			const key = ["loadOwnerships", ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<loadOwnershipsResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<loadOwnershipsResponse200>,
				GenericWretchError<loadOwnershipsResponse403>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<loadOwnershipsResponse200>,
				GenericWretchError<loadOwnershipsResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<loadOwnershipsResponse200>,
					GenericWretchError<loadOwnershipsResponse403>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<loadOwnershipsResponse200>,
			GenericWretchError<loadOwnershipsResponse403>
		> => {
			const key = ["loadOwnerships", ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<loadOwnershipsResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<loadOwnershipsResponse200>,
				GenericWretchError<loadOwnershipsResponse403>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<loadOwnershipsResponse200>,
				GenericWretchError<loadOwnershipsResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const loadTeamGroups = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<loadTeamGroupsResponse200>> => {
		try {
			const res = await wretch
				.options({ signal })
				.url("/admin/api/team-groups")
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = loadTeamGroupsResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 403: {
					err.json = loadTeamGroupsResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			options?: Omit<
				UseQueryOptions<
					ApiResponse<loadTeamGroupsResponse200>,
					GenericWretchError<loadTeamGroupsResponse403>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<loadTeamGroupsResponse200>,
			GenericWretchError<loadTeamGroupsResponse403>
		> => {
			const key = ["loadTeamGroups", ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<loadTeamGroupsResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<loadTeamGroupsResponse200>,
				GenericWretchError<loadTeamGroupsResponse403>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<loadTeamGroupsResponse200>,
				GenericWretchError<loadTeamGroupsResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<loadTeamGroupsResponse200>,
					GenericWretchError<loadTeamGroupsResponse403>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<loadTeamGroupsResponse200>,
			GenericWretchError<loadTeamGroupsResponse403>
		> => {
			const key = ["loadTeamGroups", ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<loadTeamGroupsResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<loadTeamGroupsResponse200>,
				GenericWretchError<loadTeamGroupsResponse403>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<loadTeamGroupsResponse200>,
				GenericWretchError<loadTeamGroupsResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getSubscriptionFilterData = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<getSubscriptionFilterDataResponse200>> => {
		try {
			const res = await wretch
				.options({ signal })
				.url("/admin/api/subscriptionsFilters")
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = getSubscriptionFilterDataResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 403: {
					err.json = getSubscriptionFilterDataResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			options?: Omit<
				UseQueryOptions<
					ApiResponse<getSubscriptionFilterDataResponse200>,
					GenericWretchError<getSubscriptionFilterDataResponse403>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<getSubscriptionFilterDataResponse200>,
			GenericWretchError<getSubscriptionFilterDataResponse403>
		> => {
			const key = ["getSubscriptionFilterData", ...(options?.queryKey || [])];
			const query: QueryFunction<
				ApiResponse<getSubscriptionFilterDataResponse200>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<getSubscriptionFilterDataResponse200>,
				GenericWretchError<getSubscriptionFilterDataResponse403>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<getSubscriptionFilterDataResponse200>,
				GenericWretchError<getSubscriptionFilterDataResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<getSubscriptionFilterDataResponse200>,
					GenericWretchError<getSubscriptionFilterDataResponse403>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<getSubscriptionFilterDataResponse200>,
			GenericWretchError<getSubscriptionFilterDataResponse403>
		> => {
			const key = ["getSubscriptionFilterData", ...(options?.queryKey || [])];
			const query: QueryFunction<
				ApiResponse<getSubscriptionFilterDataResponse200>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<getSubscriptionFilterDataResponse200>,
				GenericWretchError<getSubscriptionFilterDataResponse403>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<getSubscriptionFilterDataResponse200>,
				GenericWretchError<getSubscriptionFilterDataResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const assignSubscriptionToClub = (wretch: Http) => {
	const mutation = async (params: {
		clubId: number;
		subscriptionId: number;
		unAssign?: number;
	}) => {
		const args = z
			.object({
				clubId: z.number().int(),
				subscriptionId: z.number().int(),
				unAssign: z.number().int().optional(),
			})
			.parse(params);
		const { clubId, subscriptionId, unAssign } = args;

		try {
			const res = await wretch
				.url("/admin/api/assignSubscriptionToClub")
				.query(qs.stringify({ clubId, subscriptionId, unAssign }))
				.put()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = assignSubscriptionToClubResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 403: {
					err.json = assignSubscriptionToClubResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<assignSubscriptionToClubResponse200> | undefined,
					GenericWretchError<assignSubscriptionToClubResponse403>,
					{ clubId: number; subscriptionId: number; unAssign?: number }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<assignSubscriptionToClubResponse200> | undefined,
				GenericWretchError<assignSubscriptionToClubResponse403>,
				{ clubId: number; subscriptionId: number; unAssign?: number }
			>(mutation, options || {});
		},
	};
};

const getAllLanguages = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<getAllLanguagesResponse200>> => {
		try {
			const res = await wretch
				.options({ signal })
				.url("/admin/api/languages")
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = getAllLanguagesResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			options?: Omit<
				UseQueryOptions<
					ApiResponse<getAllLanguagesResponse200>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<getAllLanguagesResponse200>,
			GenericWretchError<null>
		> => {
			const key = ["getAllLanguages", ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<getAllLanguagesResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<getAllLanguagesResponse200>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<getAllLanguagesResponse200>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<getAllLanguagesResponse200>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<getAllLanguagesResponse200>,
			GenericWretchError<null>
		> => {
			const key = ["getAllLanguages", ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<getAllLanguagesResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<getAllLanguagesResponse200>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<getAllLanguagesResponse200>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const userSearch = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: { search: string },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<userSearchResponse200>> => {
		const args = z.object({ search: z.string() }).parse(params);
		const { search } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url("/admin/api/user/search")
				.query(qs.stringify({ search }))
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = userSearchResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 403: {
					err.json = userSearchResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { search: string },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<userSearchResponse200>,
					GenericWretchError<userSearchResponse403>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<userSearchResponse200>,
			GenericWretchError<userSearchResponse403>
		> => {
			const key = ["userSearch", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<userSearchResponse200>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<userSearchResponse200>,
				GenericWretchError<userSearchResponse403>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<userSearchResponse200>,
				GenericWretchError<userSearchResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { search: string },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<userSearchResponse200>,
					GenericWretchError<userSearchResponse403>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<userSearchResponse200>,
			GenericWretchError<userSearchResponse403>
		> => {
			const key = ["userSearch", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<userSearchResponse200>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<userSearchResponse200>,
				GenericWretchError<userSearchResponse403>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<userSearchResponse200>,
				GenericWretchError<userSearchResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const clubAmateurSearch = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: { search: string; checkUserExists: number },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<clubAmateurSearchResponse200>> => {
		const args = z
			.object({ search: z.string(), checkUserExists: z.number().int() })
			.parse(params);
		const { search, checkUserExists } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url("/admin/api/club-amateur/search")
				.query(qs.stringify({ search, checkUserExists }))
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = clubAmateurSearchResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 403: {
					err.json = clubAmateurSearchResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { search: string; checkUserExists: number },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<clubAmateurSearchResponse200>,
					GenericWretchError<clubAmateurSearchResponse403>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<clubAmateurSearchResponse200>,
			GenericWretchError<clubAmateurSearchResponse403>
		> => {
			const key = ["clubAmateurSearch", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<clubAmateurSearchResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<clubAmateurSearchResponse200>,
				GenericWretchError<clubAmateurSearchResponse403>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<clubAmateurSearchResponse200>,
				GenericWretchError<clubAmateurSearchResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { search: string; checkUserExists: number },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<clubAmateurSearchResponse200>,
					GenericWretchError<clubAmateurSearchResponse403>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<clubAmateurSearchResponse200>,
			GenericWretchError<clubAmateurSearchResponse403>
		> => {
			const key = ["clubAmateurSearch", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<clubAmateurSearchResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<clubAmateurSearchResponse200>,
				GenericWretchError<clubAmateurSearchResponse403>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<clubAmateurSearchResponse200>,
				GenericWretchError<clubAmateurSearchResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const clubProSearch = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: { search: string; checkUserExists?: number },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<clubProSearchResponse200>> => {
		const args = z
			.object({
				search: z.string(),
				checkUserExists: z.number().int().optional(),
			})
			.parse(params);
		const { search, checkUserExists } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url("/admin/api/club-pro/search")
				.query(qs.stringify({ search, checkUserExists }))
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = clubProSearchResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 403: {
					err.json = clubProSearchResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { search: string; checkUserExists?: number },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<clubProSearchResponse200>,
					GenericWretchError<clubProSearchResponse403>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<clubProSearchResponse200>,
			GenericWretchError<clubProSearchResponse403>
		> => {
			const key = ["clubProSearch", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<clubProSearchResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<clubProSearchResponse200>,
				GenericWretchError<clubProSearchResponse403>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<clubProSearchResponse200>,
				GenericWretchError<clubProSearchResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { search: string; checkUserExists?: number },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<clubProSearchResponse200>,
					GenericWretchError<clubProSearchResponse403>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<clubProSearchResponse200>,
			GenericWretchError<clubProSearchResponse403>
		> => {
			const key = ["clubProSearch", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<clubProSearchResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<clubProSearchResponse200>,
				GenericWretchError<clubProSearchResponse403>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<clubProSearchResponse200>,
				GenericWretchError<clubProSearchResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getAllEyeballAddons = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<getAllEyeballAddonsResponse200>> => {
		try {
			const res = await wretch
				.options({ signal })
				.url("/admin/api/eyeball-addons")
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = getAllEyeballAddonsResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			options?: Omit<
				UseQueryOptions<
					ApiResponse<getAllEyeballAddonsResponse200>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<getAllEyeballAddonsResponse200>,
			GenericWretchError<null>
		> => {
			const key = ["getAllEyeballAddons", ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<getAllEyeballAddonsResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<getAllEyeballAddonsResponse200>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<getAllEyeballAddonsResponse200>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<getAllEyeballAddonsResponse200>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<getAllEyeballAddonsResponse200>,
			GenericWretchError<null>
		> => {
			const key = ["getAllEyeballAddons", ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<getAllEyeballAddonsResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<getAllEyeballAddonsResponse200>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<getAllEyeballAddonsResponse200>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getUserDetail = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: { id: number },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<UserInfo>> => {
		const args = z.object({ id: z.number().int() }).parse(params);
		const { id } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/admin/user/${id}`)
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = UserInfo.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { id: number },
			options?: Omit<
				UseQueryOptions<ApiResponse<UserInfo>, GenericWretchError<null>>,
				"queryFn"
			>,
		): UseQueryResult<ApiResponse<UserInfo>, GenericWretchError<null>> => {
			const key = ["getUserDetail", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<UserInfo>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<UserInfo>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<UserInfo>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { id: number },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<UserInfo>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<UserInfo>,
			GenericWretchError<null>
		> => {
			const key = ["getUserDetail", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<UserInfo>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<UserInfo>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<UserInfo>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getUserSessions = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: { id: number },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<getUserSessionsResponse200>> => {
		const args = z.object({ id: z.number().int() }).parse(params);
		const { id } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/admin/user/${id}/sessions`)
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = getUserSessionsResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { id: number },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<getUserSessionsResponse200>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<getUserSessionsResponse200>,
			GenericWretchError<null>
		> => {
			const key = ["getUserSessions", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<getUserSessionsResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<getUserSessionsResponse200>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<getUserSessionsResponse200>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { id: number },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<getUserSessionsResponse200>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<getUserSessionsResponse200>,
			GenericWretchError<null>
		> => {
			const key = ["getUserSessions", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<getUserSessionsResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<getUserSessionsResponse200>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<getUserSessionsResponse200>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const revokeSessions = (wretch: Http) => {
	const mutation = async (params: { id: number }) => {
		const args = z.object({ id: z.number().int() }).parse(params);
		const { id } = args;

		try {
			const res = await wretch.url(`/admin/user/${id}/sessions`).delete().res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = revokeSessionsResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<revokeSessionsResponse200> | undefined,
					GenericWretchError<null>,
					{ id: number }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<revokeSessionsResponse200> | undefined,
				GenericWretchError<null>,
				{ id: number }
			>(mutation, options || {});
		},
	};
};

const getUserSignIns = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: { id: number },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<getUserSignInsResponse200>> => {
		const args = z.object({ id: z.number().int() }).parse(params);
		const { id } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/admin/user/${id}/signins`)
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = getUserSignInsResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { id: number },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<getUserSignInsResponse200>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<getUserSignInsResponse200>,
			GenericWretchError<null>
		> => {
			const key = ["getUserSignIns", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<getUserSignInsResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<getUserSignInsResponse200>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<getUserSignInsResponse200>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { id: number },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<getUserSignInsResponse200>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<getUserSignInsResponse200>,
			GenericWretchError<null>
		> => {
			const key = ["getUserSignIns", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<getUserSignInsResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<getUserSignInsResponse200>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<getUserSignInsResponse200>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const logUserActivity = (wretch: Http) => {
	const mutation = async (params: { body: UserActivityLogRequest }) => {
		const args = z.object({ body: z.any() }).parse(params);
		const { body } = args;

		try {
			const res = await wretch.url("/admin/telemetry").json(body).post().res();
			switch (res.status) {
				case 201: {
					return {
						status: res.status,
						data: null,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = logUserActivityResponse400.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<null> | undefined,
					GenericWretchError<logUserActivityResponse400>,
					{ body: UserActivityLogRequest }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<null> | undefined,
				GenericWretchError<logUserActivityResponse400>,
				{ body: UserActivityLogRequest }
			>(mutation, options || {});
		},
	};
};

const getUserWidgetSettings = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<getUserWidgetSettingsResponse200>> => {
		try {
			const res = await wretch
				.options({ signal })
				.url("/admin/widget-settings")
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = getUserWidgetSettingsResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			options?: Omit<
				UseQueryOptions<
					ApiResponse<getUserWidgetSettingsResponse200>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<getUserWidgetSettingsResponse200>,
			GenericWretchError<null>
		> => {
			const key = ["getUserWidgetSettings", ...(options?.queryKey || [])];
			const query: QueryFunction<
				ApiResponse<getUserWidgetSettingsResponse200>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<getUserWidgetSettingsResponse200>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<getUserWidgetSettingsResponse200>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<getUserWidgetSettingsResponse200>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<getUserWidgetSettingsResponse200>,
			GenericWretchError<null>
		> => {
			const key = ["getUserWidgetSettings", ...(options?.queryKey || [])];
			const query: QueryFunction<
				ApiResponse<getUserWidgetSettingsResponse200>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<getUserWidgetSettingsResponse200>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<getUserWidgetSettingsResponse200>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const saveUserWidgetSettings = (wretch: Http) => {
	const mutation = async (params: {
		body: saveUserWidgetSettingsRequestBody;
	}) => {
		const args = z.object({ body: z.any() }).parse(params);
		const { body } = args;

		try {
			const res = await wretch
				.url("/admin/widget-settings")
				.json(body)
				.post()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = saveUserWidgetSettingsResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<saveUserWidgetSettingsResponse200> | undefined,
					GenericWretchError<null>,
					{ body: saveUserWidgetSettingsRequestBody }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<saveUserWidgetSettingsResponse200> | undefined,
				GenericWretchError<null>,
				{ body: saveUserWidgetSettingsRequestBody }
			>(mutation, options || {});
		},
	};
};

const getClubAllTeams = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: { id: number; page: number; limit: number },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<TeamResponse>> => {
		const args = z
			.object({
				id: z.number().int(),
				page: z.number().int(),
				limit: z.number().int(),
			})
			.parse(params);
		const { id, page, limit } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/admin/api/club/${id}/teams`)
				.query(qs.stringify({ page, limit }))
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = TeamResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = getClubAllTeamsResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = getClubAllTeamsResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { id: number; page: number; limit: number },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<TeamResponse>,
					GenericWretchError<
						getClubAllTeamsResponse400 | getClubAllTeamsResponse403
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<TeamResponse>,
			GenericWretchError<
				getClubAllTeamsResponse400 | getClubAllTeamsResponse403
			>
		> => {
			const key = ["getClubAllTeams", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<TeamResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<TeamResponse>,
				GenericWretchError<
					getClubAllTeamsResponse400 | getClubAllTeamsResponse403
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<TeamResponse>,
				GenericWretchError<
					getClubAllTeamsResponse400 | getClubAllTeamsResponse403
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { id: number; page: number; limit: number },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<TeamResponse>,
					GenericWretchError<
						getClubAllTeamsResponse400 | getClubAllTeamsResponse403
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<TeamResponse>,
			GenericWretchError<
				getClubAllTeamsResponse400 | getClubAllTeamsResponse403
			>
		> => {
			const key = ["getClubAllTeams", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<TeamResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<TeamResponse>,
				GenericWretchError<
					getClubAllTeamsResponse400 | getClubAllTeamsResponse403
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<TeamResponse>,
				GenericWretchError<
					getClubAllTeamsResponse400 | getClubAllTeamsResponse403
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getTeamByIdAndCoachId = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: { clubId: number; id?: number; coachId?: number; gender?: string },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<getTeamByIdAndCoachIdResponse200>> => {
		const args = z
			.object({
				clubId: z.number().int(),
				id: z.number().int().optional(),
				coachId: z.number().int().optional(),
				gender: z.string().optional(),
			})
			.parse(params);
		const { clubId, id, coachId, gender } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/admin/club/${clubId}/team`)
				.query(qs.stringify({ id, coachId, gender }))
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = getTeamByIdAndCoachIdResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 403: {
					err.json = getTeamByIdAndCoachIdResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: {
				clubId: number;
				id?: number;
				coachId?: number;
				gender?: string;
			},
			options?: Omit<
				UseQueryOptions<
					ApiResponse<getTeamByIdAndCoachIdResponse200>,
					GenericWretchError<getTeamByIdAndCoachIdResponse403>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<getTeamByIdAndCoachIdResponse200>,
			GenericWretchError<getTeamByIdAndCoachIdResponse403>
		> => {
			const key = [
				"getTeamByIdAndCoachId",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<getTeamByIdAndCoachIdResponse200>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<getTeamByIdAndCoachIdResponse200>,
				GenericWretchError<getTeamByIdAndCoachIdResponse403>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<getTeamByIdAndCoachIdResponse200>,
				GenericWretchError<getTeamByIdAndCoachIdResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: {
				clubId: number;
				id?: number;
				coachId?: number;
				gender?: string;
			},
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<getTeamByIdAndCoachIdResponse200>,
					GenericWretchError<getTeamByIdAndCoachIdResponse403>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<getTeamByIdAndCoachIdResponse200>,
			GenericWretchError<getTeamByIdAndCoachIdResponse403>
		> => {
			const key = [
				"getTeamByIdAndCoachId",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<getTeamByIdAndCoachIdResponse200>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<getTeamByIdAndCoachIdResponse200>,
				GenericWretchError<getTeamByIdAndCoachIdResponse403>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<getTeamByIdAndCoachIdResponse200>,
				GenericWretchError<getTeamByIdAndCoachIdResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const addUpdateTeam = (wretch: Http) => {
	const mutation = async (params: {
		clubId: number;
		body: addUpdateTeam_Body;
	}) => {
		const args = z
			.object({ clubId: z.number().int(), body: z.any() })
			.parse(params);
		const { clubId, body } = args;

		try {
			const res = await wretch
				.url(`/admin/club/${clubId}/team`)
				.json(body)
				.post()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = addUpdateTeamResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = addUpdateTeamResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = addUpdateTeamResponse403.parse(json);
					break;
				}
				case 409: {
					err.json = addUpdateTeamResponse409.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<addUpdateTeamResponse200> | undefined,
					GenericWretchError<
						| addUpdateTeamResponse400
						| addUpdateTeamResponse403
						| addUpdateTeamResponse409
					>,
					{ clubId: number; body: addUpdateTeam_Body }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<addUpdateTeamResponse200> | undefined,
				GenericWretchError<
					| addUpdateTeamResponse400
					| addUpdateTeamResponse403
					| addUpdateTeamResponse409
				>,
				{ clubId: number; body: addUpdateTeam_Body }
			>(mutation, options || {});
		},
	};
};

const deleteTeam = (wretch: Http) => {
	const mutation = async (params: { id: number }) => {
		const args = z.object({ id: z.number().int() }).parse(params);
		const { id } = args;

		try {
			const res = await wretch.url(`/admin/team/${id}`).delete().res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = deleteTeamResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 403: {
					err.json = deleteTeamResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = deleteTeamResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<deleteTeamResponse200> | undefined,
					GenericWretchError<deleteTeamResponse403 | deleteTeamResponse404>,
					{ id: number }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<deleteTeamResponse200> | undefined,
				GenericWretchError<deleteTeamResponse403 | deleteTeamResponse404>,
				{ id: number }
			>(mutation, options || {});
		},
	};
};

const clubCoaches = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: { id: number },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<clubCoachesResponse200>> => {
		const args = z.object({ id: z.number().int() }).parse(params);
		const { id } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/admin/club/${id}/coaches`)
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = clubCoachesResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 403: {
					err.json = clubCoachesResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { id: number },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<clubCoachesResponse200>,
					GenericWretchError<clubCoachesResponse403>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<clubCoachesResponse200>,
			GenericWretchError<clubCoachesResponse403>
		> => {
			const key = ["clubCoaches", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<clubCoachesResponse200>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<clubCoachesResponse200>,
				GenericWretchError<clubCoachesResponse403>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<clubCoachesResponse200>,
				GenericWretchError<clubCoachesResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { id: number },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<clubCoachesResponse200>,
					GenericWretchError<clubCoachesResponse403>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<clubCoachesResponse200>,
			GenericWretchError<clubCoachesResponse403>
		> => {
			const key = ["clubCoaches", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<clubCoachesResponse200>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<clubCoachesResponse200>,
				GenericWretchError<clubCoachesResponse403>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<clubCoachesResponse200>,
				GenericWretchError<clubCoachesResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getClubTeamsList = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: { id: number },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<getClubTeamsListResponse200>> => {
		const args = z.object({ id: z.number().int() }).parse(params);
		const { id } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/admin/club/${id}/club-teams`)
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = getClubTeamsListResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 403: {
					err.json = getClubTeamsListResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { id: number },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<getClubTeamsListResponse200>,
					GenericWretchError<getClubTeamsListResponse403>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<getClubTeamsListResponse200>,
			GenericWretchError<getClubTeamsListResponse403>
		> => {
			const key = ["getClubTeamsList", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<getClubTeamsListResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<getClubTeamsListResponse200>,
				GenericWretchError<getClubTeamsListResponse403>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<getClubTeamsListResponse200>,
				GenericWretchError<getClubTeamsListResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { id: number },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<getClubTeamsListResponse200>,
					GenericWretchError<getClubTeamsListResponse403>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<getClubTeamsListResponse200>,
			GenericWretchError<getClubTeamsListResponse403>
		> => {
			const key = ["getClubTeamsList", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<getClubTeamsListResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<getClubTeamsListResponse200>,
				GenericWretchError<getClubTeamsListResponse403>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<getClubTeamsListResponse200>,
				GenericWretchError<getClubTeamsListResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const teamPlayers = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: {
			clubId: number;
			id: number;
			page: number;
			limit: number;
			order?: object;
			position?: string;
			search?: string;
			strongestFoot?: string;
			nationality?: string;
		},
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<ClubPlayersResponse>> => {
		const args = z
			.object({
				clubId: z.number().int(),
				page: z.number().int(),
				limit: z.number().int(),
				id: z.number().int(),
				order: z
					.object({
						name: z.string().describe("Sorting column name").optional(),
						sort: z.string().describe("Sorting column type").optional(),
					})
					.optional(),
				position: z.string().optional(),
				search: z.string().optional(),
				strongestFoot: z.string().optional(),
				nationality: z.string().optional(),
			})
			.parse(params);
		const {
			clubId,
			id,
			page,
			limit,
			order,
			position,
			search,
			strongestFoot,
			nationality,
		} = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/admin/club/${clubId}/team/${id}/players`)
				.query(
					qs.stringify({
						page,
						limit,
						order,
						position,
						search,
						strongestFoot,
						nationality,
					}),
				)
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = ClubPlayersResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = teamPlayersResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = teamPlayersResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: {
				clubId: number;
				id: number;
				page: number;
				limit: number;
				order?: object;
				position?: string;
				search?: string;
				strongestFoot?: string;
				nationality?: string;
			},
			options?: Omit<
				UseQueryOptions<
					ApiResponse<ClubPlayersResponse>,
					GenericWretchError<teamPlayersResponse400 | teamPlayersResponse403>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<ClubPlayersResponse>,
			GenericWretchError<teamPlayersResponse400 | teamPlayersResponse403>
		> => {
			const key = ["teamPlayers", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<ClubPlayersResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<ClubPlayersResponse>,
				GenericWretchError<teamPlayersResponse400 | teamPlayersResponse403>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<ClubPlayersResponse>,
				GenericWretchError<teamPlayersResponse400 | teamPlayersResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: {
				clubId: number;
				id: number;
				page: number;
				limit: number;
				order?: object;
				position?: string;
				search?: string;
				strongestFoot?: string;
				nationality?: string;
			},
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<ClubPlayersResponse>,
					GenericWretchError<teamPlayersResponse400 | teamPlayersResponse403>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<ClubPlayersResponse>,
			GenericWretchError<teamPlayersResponse400 | teamPlayersResponse403>
		> => {
			const key = ["teamPlayers", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<ClubPlayersResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<ClubPlayersResponse>,
				GenericWretchError<teamPlayersResponse400 | teamPlayersResponse403>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<ClubPlayersResponse>,
				GenericWretchError<teamPlayersResponse400 | teamPlayersResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const addUpdatePlayer = (wretch: Http) => {
	const mutation = async (params: {
		clubId: number;
		body: addUpdatePlayer_Body;
	}) => {
		const args = z
			.object({ clubId: z.number().int(), body: z.any() })
			.parse(params);
		const { clubId, body } = args;

		try {
			const res = await wretch
				.url(`/admin/club/${clubId}/player`)
				.json(body)
				.post()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = addUpdatePlayerResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = addUpdatePlayerResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = addUpdatePlayerResponse403.parse(json);
					break;
				}
				case 409: {
					err.json = addUpdatePlayerResponse409.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<addUpdatePlayerResponse200> | undefined,
					GenericWretchError<
						| addUpdatePlayerResponse400
						| addUpdatePlayerResponse403
						| addUpdatePlayerResponse409
					>,
					{ clubId: number; body: addUpdatePlayer_Body }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<addUpdatePlayerResponse200> | undefined,
				GenericWretchError<
					| addUpdatePlayerResponse400
					| addUpdatePlayerResponse403
					| addUpdatePlayerResponse409
				>,
				{ clubId: number; body: addUpdatePlayer_Body }
			>(mutation, options || {});
		},
	};
};

const updateVip = (wretch: Http) => {
	const mutation = async (params: { id: number; vip: boolean }) => {
		const args = z
			.object({ id: z.number().int(), vip: z.boolean() })
			.parse(params);
		const { id, vip } = args;

		try {
			const res = await wretch
				.url(`/admin/player/${id}/update-vip`)
				.query(qs.stringify({ vip }))
				.put()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = updateVipResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 403: {
					err.json = updateVipResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = updateVipResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<updateVipResponse200> | undefined,
					GenericWretchError<updateVipResponse403 | updateVipResponse404>,
					{ id: number; vip: boolean }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<updateVipResponse200> | undefined,
				GenericWretchError<updateVipResponse403 | updateVipResponse404>,
				{ id: number; vip: boolean }
			>(mutation, options || {});
		},
	};
};

const updateLevel = (wretch: Http) => {
	const mutation = async (params: { id: number; level: number }) => {
		const args = z
			.object({ id: z.number().int(), level: z.number().int() })
			.parse(params);
		const { id, level } = args;

		try {
			const res = await wretch
				.url(`/admin/team/${id}/update-level`)
				.query(qs.stringify({ level }))
				.put()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = updateLevelResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 403: {
					err.json = updateLevelResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = updateLevelResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<updateLevelResponse200> | undefined,
					GenericWretchError<updateLevelResponse403 | updateLevelResponse404>,
					{ id: number; level: number }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<updateLevelResponse200> | undefined,
				GenericWretchError<updateLevelResponse403 | updateLevelResponse404>,
				{ id: number; level: number }
			>(mutation, options || {});
		},
	};
};

const deletePlayer = (wretch: Http) => {
	const mutation = async (params: { id: number }) => {
		const args = z.object({ id: z.number().int() }).parse(params);
		const { id } = args;

		try {
			const res = await wretch.url(`/admin/player/${id}`).delete().res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = deletePlayerResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 403: {
					err.json = deletePlayerResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = deletePlayerResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<deletePlayerResponse200> | undefined,
					GenericWretchError<deletePlayerResponse403 | deletePlayerResponse404>,
					{ id: number }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<deletePlayerResponse200> | undefined,
				GenericWretchError<deletePlayerResponse403 | deletePlayerResponse404>,
				{ id: number }
			>(mutation, options || {});
		},
	};
};

const formerPlayers = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: {
			clubId: number;
			page: number;
			limit: number;
			position?: string;
			search?: string;
			strongestFoot?: string;
			nationality?: string;
			order?: object;
		},
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<ClubPlayersResponse>> => {
		const args = z
			.object({
				clubId: z.number().int(),
				page: z.number().int(),
				limit: z.number().int(),
				position: z.string().optional(),
				search: z.string().optional(),
				strongestFoot: z.string().optional(),
				nationality: z.string().optional(),
				order: z
					.object({
						name: z.string().describe("Sorting column name").optional(),
						sort: z.string().describe("Sorting column type").optional(),
					})
					.optional(),
			})
			.parse(params);
		const {
			clubId,
			page,
			limit,
			position,
			search,
			strongestFoot,
			nationality,
			order,
		} = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/admin/club/${clubId}/former-players`)
				.query(
					qs.stringify({
						page,
						limit,
						position,
						search,
						strongestFoot,
						nationality,
						order,
					}),
				)
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = ClubPlayersResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = formerPlayersResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = formerPlayersResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: {
				clubId: number;
				page: number;
				limit: number;
				position?: string;
				search?: string;
				strongestFoot?: string;
				nationality?: string;
				order?: object;
			},
			options?: Omit<
				UseQueryOptions<
					ApiResponse<ClubPlayersResponse>,
					GenericWretchError<
						formerPlayersResponse400 | formerPlayersResponse403
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<ClubPlayersResponse>,
			GenericWretchError<formerPlayersResponse400 | formerPlayersResponse403>
		> => {
			const key = ["formerPlayers", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<ClubPlayersResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<ClubPlayersResponse>,
				GenericWretchError<formerPlayersResponse400 | formerPlayersResponse403>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<ClubPlayersResponse>,
				GenericWretchError<formerPlayersResponse400 | formerPlayersResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: {
				clubId: number;
				page: number;
				limit: number;
				position?: string;
				search?: string;
				strongestFoot?: string;
				nationality?: string;
				order?: object;
			},
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<ClubPlayersResponse>,
					GenericWretchError<
						formerPlayersResponse400 | formerPlayersResponse403
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<ClubPlayersResponse>,
			GenericWretchError<formerPlayersResponse400 | formerPlayersResponse403>
		> => {
			const key = ["formerPlayers", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<ClubPlayersResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<ClubPlayersResponse>,
				GenericWretchError<formerPlayersResponse400 | formerPlayersResponse403>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<ClubPlayersResponse>,
				GenericWretchError<formerPlayersResponse400 | formerPlayersResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const uploadPlayerPhoto = (wretch: Http) => {
	const mutation = async (params: { body: uploadPlayerPhoto_Body }) => {
		const args = z.object({ body: z.any() }).parse(params);
		const { body } = args;

		try {
			const res = await wretch
				.url("/admin/player/upload-profile-picture")
				.json(body)
				.put()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = uploadPlayerPhotoResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = uploadPlayerPhotoResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = uploadPlayerPhotoResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<uploadPlayerPhotoResponse200> | undefined,
					GenericWretchError<
						uploadPlayerPhotoResponse400 | uploadPlayerPhotoResponse403
					>,
					{ body: uploadPlayerPhoto_Body }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<uploadPlayerPhotoResponse200> | undefined,
				GenericWretchError<
					uploadPlayerPhotoResponse400 | uploadPlayerPhotoResponse403
				>,
				{ body: uploadPlayerPhoto_Body }
			>(mutation, options || {});
		},
	};
};

const assignCoachesToTeam = (wretch: Http) => {
	const mutation = async (params: {
		id: number;
		body: assignCoachesToTeam_Body;
	}) => {
		const args = z
			.object({ id: z.number().int(), body: z.any() })
			.parse(params);
		const { id, body } = args;

		try {
			const res = await wretch
				.url(`/admin/team/${id}/assign-coaches`)
				.json(body)
				.post()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = assignCoachesToTeamResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 403: {
					err.json = assignCoachesToTeamResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<assignCoachesToTeamResponse200> | undefined,
					GenericWretchError<assignCoachesToTeamResponse403>,
					{ id: number; body: assignCoachesToTeam_Body }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<assignCoachesToTeamResponse200> | undefined,
				GenericWretchError<assignCoachesToTeamResponse403>,
				{ id: number; body: assignCoachesToTeam_Body }
			>(mutation, options || {});
		},
	};
};

const getNationalities = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<getNationalitiesResponse200>> => {
		try {
			const res = await wretch
				.options({ signal })
				.url("/admin/api/nationalities")
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = getNationalitiesResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			options?: Omit<
				UseQueryOptions<
					ApiResponse<getNationalitiesResponse200>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<getNationalitiesResponse200>,
			GenericWretchError<null>
		> => {
			const key = ["getNationalities", ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<getNationalitiesResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<getNationalitiesResponse200>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<getNationalitiesResponse200>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<getNationalitiesResponse200>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<getNationalitiesResponse200>,
			GenericWretchError<null>
		> => {
			const key = ["getNationalities", ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<getNationalitiesResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<getNationalitiesResponse200>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<getNationalitiesResponse200>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getCategories = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<getCategoriesResponse200>> => {
		try {
			const res = await wretch
				.options({ signal })
				.url("/admin/api/categories")
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = getCategoriesResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			options?: Omit<
				UseQueryOptions<
					ApiResponse<getCategoriesResponse200>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<getCategoriesResponse200>,
			GenericWretchError<null>
		> => {
			const key = ["getCategories", ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<getCategoriesResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<getCategoriesResponse200>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<getCategoriesResponse200>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<getCategoriesResponse200>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<getCategoriesResponse200>,
			GenericWretchError<null>
		> => {
			const key = ["getCategories", ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<getCategoriesResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<getCategoriesResponse200>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<getCategoriesResponse200>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const batchUpdate = (wretch: Http) => {
	const mutation = async (params: { body: batchUpdate_Body }) => {
		const args = z.object({ body: z.any() }).parse(params);
		const { body } = args;

		try {
			const res = await wretch
				.url("/admin/api/player/batch-update")
				.json(body)
				.post()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = batchUpdateResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = batchUpdateResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = batchUpdateResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<batchUpdateResponse200> | undefined,
					GenericWretchError<batchUpdateResponse400 | batchUpdateResponse403>,
					{ body: batchUpdate_Body }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<batchUpdateResponse200> | undefined,
				GenericWretchError<batchUpdateResponse400 | batchUpdateResponse403>,
				{ body: batchUpdate_Body }
			>(mutation, options || {});
		},
	};
};

const generateSignUpLink = (wretch: Http) => {
	const mutation = async (params: { body: generateSignUpLinkRequestBody }) => {
		const args = z.object({ body: z.any() }).parse(params);
		const { body } = args;

		try {
			const res = await wretch
				.url("/admin/sign-up/player/generate-link")
				.json(body)
				.post()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = generateSignUpLinkResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = generateSignUpLinkResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = generateSignUpLinkResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = generateSignUpLinkResponse404.parse(json);
					break;
				}
				case 409: {
					err.json = generateSignUpLinkResponse409.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<generateSignUpLinkResponse200> | undefined,
					GenericWretchError<
						| generateSignUpLinkResponse400
						| generateSignUpLinkResponse403
						| generateSignUpLinkResponse404
						| generateSignUpLinkResponse409
					>,
					{ body: generateSignUpLinkRequestBody }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<generateSignUpLinkResponse200> | undefined,
				GenericWretchError<
					| generateSignUpLinkResponse400
					| generateSignUpLinkResponse403
					| generateSignUpLinkResponse404
					| generateSignUpLinkResponse409
				>,
				{ body: generateSignUpLinkRequestBody }
			>(mutation, options || {});
		},
	};
};

const getPlayerLatestLogEntries = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: { id: number; type: string },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<getPlayerLatestLogEntriesResponse200>> => {
		const args = z
			.object({ id: z.number().int(), type: z.string() })
			.parse(params);
		const { id, type } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url("/admin/player/latest-updates")
				.query(qs.stringify({ id, type }))
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = getPlayerLatestLogEntriesResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { id: number; type: string },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<getPlayerLatestLogEntriesResponse200>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<getPlayerLatestLogEntriesResponse200>,
			GenericWretchError<null>
		> => {
			const key = [
				"getPlayerLatestLogEntries",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<getPlayerLatestLogEntriesResponse200>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<getPlayerLatestLogEntriesResponse200>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<getPlayerLatestLogEntriesResponse200>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { id: number; type: string },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<getPlayerLatestLogEntriesResponse200>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<getPlayerLatestLogEntriesResponse200>,
			GenericWretchError<null>
		> => {
			const key = [
				"getPlayerLatestLogEntries",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<getPlayerLatestLogEntriesResponse200>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<getPlayerLatestLogEntriesResponse200>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<getPlayerLatestLogEntriesResponse200>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const adminCreatePlayerPossibleDuplicate = (wretch: Http) => {
	const mutation = async (params: {
		body: CreatePlayerPossibleDuplicateRequest;
	}) => {
		const args = z.object({ body: z.any() }).parse(params);
		const { body } = args;

		try {
			const res = await wretch
				.url("/admin/api/player/possible-duplicate")
				.json(body)
				.post()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 201: {
					const data =
						adminCreatePlayerPossibleDuplicateResponse201.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = adminCreatePlayerPossibleDuplicateResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = adminCreatePlayerPossibleDuplicateResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = adminCreatePlayerPossibleDuplicateResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					| ApiResponse<adminCreatePlayerPossibleDuplicateResponse201>
					| undefined,
					GenericWretchError<
						| adminCreatePlayerPossibleDuplicateResponse400
						| adminCreatePlayerPossibleDuplicateResponse403
						| adminCreatePlayerPossibleDuplicateResponse404
					>,
					{ body: CreatePlayerPossibleDuplicateRequest }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<adminCreatePlayerPossibleDuplicateResponse201> | undefined,
				GenericWretchError<
					| adminCreatePlayerPossibleDuplicateResponse400
					| adminCreatePlayerPossibleDuplicateResponse403
					| adminCreatePlayerPossibleDuplicateResponse404
				>,
				{ body: CreatePlayerPossibleDuplicateRequest }
			>(mutation, options || {});
		},
	};
};

const getPlayerPossibleDuplicates = (
	wretch: Http,
	queryClient: QueryClient,
) => {
	const queryFn = async (
		params: {
			page?: number;
			limit?: number;
			filters?: object[];
			order?: object;
		},
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<PlayerPossibleDuplicatesResponse>> => {
		const args = z
			.object({
				page: z.number().int().optional(),
				limit: z.number().int().optional(),
				filters: z
					.array(
						z.object({
							type: z
								.enum([
									"exact",
									"search",
									"boolean",
									"exists",
									"inarray",
									"datetimerange",
									"numberrange",
								])
								.describe("Type of filter")
								.optional(),
							field: z
								.enum([
									"possibleDuplicateId",
									"possibleDuplicateFirstName",
									"possibleDuplicateLastName",
									"playerFirstName",
									"playerLastName",
									"similarityScore",
									"notes",
									"status",
									"createdAt",
									"birthday",
								])
								.describe("Property to filter")
								.optional(),
							value: z
								.any()
								.superRefine((x, ctx) => {
									const schemas = [
										z.string(),
										z.number().int(),
										z.boolean(),
										z.array(z.string()),
										z.object({
											start: z.string().datetime({ offset: true }).optional(),
											end: z.string().datetime({ offset: true }).optional(),
										}),
									];
									const errors = schemas.reduce<z.ZodError[]>(
										(errors, schema) =>
											((result) =>
												result.error ? [...errors, result.error] : errors)(
												schema.safeParse(x),
											),
										[],
									);
									if (schemas.length - errors.length !== 1) {
										ctx.addIssue({
											path: ctx.path,
											code: "invalid_union",
											unionErrors: errors,
											message: "Invalid input: Should pass single schema",
										});
									}
								})
								.describe(
									"Filter value: string, integer, boolean, string array (e.g. inarray), or date-time range object",
								)
								.optional(),
						}),
					)
					.optional(),
				order: z
					.object({
						field: z
							.enum([
								"possibleDuplicateFirstName",
								"possibleDuplicateLastName",
								"playerFirstName",
								"playerLastName",
								"similarityScore",
								"status",
								"createdAt",
							])
							.describe("Sorting field")
							.optional(),
						direction: z
							.enum(["ASC", "DESC"])
							.describe("Sorting direction")
							.optional(),
					})
					.optional(),
			})
			.parse(params);
		const { page, limit, filters, order } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url("/admin/api/player/possible-duplicates")
				.query(qs.stringify({ page, limit, filters, order }))
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = PlayerPossibleDuplicatesResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = getPlayerPossibleDuplicatesResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = getPlayerPossibleDuplicatesResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: {
				page?: number;
				limit?: number;
				filters?: object[];
				order?: object;
			},
			options?: Omit<
				UseQueryOptions<
					ApiResponse<PlayerPossibleDuplicatesResponse>,
					GenericWretchError<
						| getPlayerPossibleDuplicatesResponse400
						| getPlayerPossibleDuplicatesResponse403
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<PlayerPossibleDuplicatesResponse>,
			GenericWretchError<
				| getPlayerPossibleDuplicatesResponse400
				| getPlayerPossibleDuplicatesResponse403
			>
		> => {
			const key = [
				"getPlayerPossibleDuplicates",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<PlayerPossibleDuplicatesResponse>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<PlayerPossibleDuplicatesResponse>,
				GenericWretchError<
					| getPlayerPossibleDuplicatesResponse400
					| getPlayerPossibleDuplicatesResponse403
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<PlayerPossibleDuplicatesResponse>,
				GenericWretchError<
					| getPlayerPossibleDuplicatesResponse400
					| getPlayerPossibleDuplicatesResponse403
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: {
				page?: number;
				limit?: number;
				filters?: object[];
				order?: object;
			},
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<PlayerPossibleDuplicatesResponse>,
					GenericWretchError<
						| getPlayerPossibleDuplicatesResponse400
						| getPlayerPossibleDuplicatesResponse403
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<PlayerPossibleDuplicatesResponse>,
			GenericWretchError<
				| getPlayerPossibleDuplicatesResponse400
				| getPlayerPossibleDuplicatesResponse403
			>
		> => {
			const key = [
				"getPlayerPossibleDuplicates",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<PlayerPossibleDuplicatesResponse>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<PlayerPossibleDuplicatesResponse>,
				GenericWretchError<
					| getPlayerPossibleDuplicatesResponse400
					| getPlayerPossibleDuplicatesResponse403
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<PlayerPossibleDuplicatesResponse>,
				GenericWretchError<
					| getPlayerPossibleDuplicatesResponse400
					| getPlayerPossibleDuplicatesResponse403
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const patchPlayerPossibleDuplicate = (wretch: Http) => {
	const mutation = async (params: {
		id: number;
		body: PlayerPossibleDuplicatePatchRequest;
	}) => {
		const args = z
			.object({ id: z.number().int(), body: z.any() })
			.parse(params);
		const { id, body } = args;

		try {
			const res = await wretch
				.url(`/admin/api/player/possible-duplicate/${id}`)
				.json(body)
				.patch()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = patchPlayerPossibleDuplicateResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = patchPlayerPossibleDuplicateResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = patchPlayerPossibleDuplicateResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = patchPlayerPossibleDuplicateResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<patchPlayerPossibleDuplicateResponse200> | undefined,
					GenericWretchError<
						| patchPlayerPossibleDuplicateResponse400
						| patchPlayerPossibleDuplicateResponse403
						| patchPlayerPossibleDuplicateResponse404
					>,
					{ id: number; body: PlayerPossibleDuplicatePatchRequest }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<patchPlayerPossibleDuplicateResponse200> | undefined,
				GenericWretchError<
					| patchPlayerPossibleDuplicateResponse400
					| patchPlayerPossibleDuplicateResponse403
					| patchPlayerPossibleDuplicateResponse404
				>,
				{ id: number; body: PlayerPossibleDuplicatePatchRequest }
			>(mutation, options || {});
		},
	};
};

const getVideoUploads = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: {
			preset: string;
			page?: number;
			limit?: number;
			order?: object;
			filters?: object[];
		},
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<VideoUploadsPaginatedResponse>> => {
		const args = z
			.object({
				preset: z.enum([
					"waiting-for-approval",
					"approved",
					"in-processing",
					"errored",
					"finished",
					"deleted",
					"",
				]),
				page: z.number().int().optional(),
				limit: z.number().int().optional(),
				order: z
					.object({
						name: z
							.enum([
								"name",
								"duration",
								"size",
								"createdAt",
								"worker",
								"gameId",
								"metadata.gameId",
								"processingStartedAt",
								"processingFinishedAt",
								"width",
								"bitRate",
								"approvedBy",
								"metadataAddedBy",
								"status",
								"lastEvent",
								"uploadedBy",
								"origin",
								"apiStatus",
								"deletedAt",
								"deletedBy",
								"providerMetadata.clubhouse_name",
								"providerMetadata.recording_slug",
								"veo_team",
							])
							.optional(),
						sort: z.enum(["asc", "desc"]).optional(),
					})
					.strict()
					.optional(),
				filters: z
					.array(
						z.object({
							field: z.string().describe("Field to filter on").optional(),
							type: z.string().describe("Type of filter").optional(),
							value: z
								.any()
								.superRefine((x, ctx) => {
									const schemas = [
										z.string(),
										z.number().int(),
										z.object({
											start: z.string().datetime({ offset: true }).optional(),
											end: z.string().datetime({ offset: true }).optional(),
										}),
										z.array(z.string()),
									];
									const errors = schemas.reduce<z.ZodError[]>(
										(errors, schema) =>
											((result) =>
												result.error ? [...errors, result.error] : errors)(
												schema.safeParse(x),
											),
										[],
									);
									if (schemas.length - errors.length !== 1) {
										ctx.addIssue({
											path: ctx.path,
											code: "invalid_union",
											unionErrors: errors,
											message: "Invalid input: Should pass single schema",
										});
									}
								})
								.optional(),
						}),
					)
					.optional(),
			})
			.parse(params);
		const { preset, page, limit, order, filters } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/admin/video-uploads/${preset}`)
				.query(qs.stringify({ page, limit, order, filters }))
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = VideoUploadsPaginatedResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: {
				preset: string;
				page?: number;
				limit?: number;
				order?: object;
				filters?: object[];
			},
			options?: Omit<
				UseQueryOptions<
					ApiResponse<VideoUploadsPaginatedResponse>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<VideoUploadsPaginatedResponse>,
			GenericWretchError<null>
		> => {
			const key = ["getVideoUploads", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<VideoUploadsPaginatedResponse>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<VideoUploadsPaginatedResponse>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<VideoUploadsPaginatedResponse>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: {
				preset: string;
				page?: number;
				limit?: number;
				order?: object;
				filters?: object[];
			},
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<VideoUploadsPaginatedResponse>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<VideoUploadsPaginatedResponse>,
			GenericWretchError<null>
		> => {
			const key = ["getVideoUploads", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<VideoUploadsPaginatedResponse>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<VideoUploadsPaginatedResponse>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<VideoUploadsPaginatedResponse>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getVideoUploaders = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: { table: string },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<VideoUploadersResponseDTO>> => {
		const args = z
			.object({
				table: z.enum([
					"waiting-for-approval",
					"approved",
					"in-processing",
					"errored",
					"finished",
					"deleted",
					"",
				]),
			})
			.parse(params);
		const { table } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/admin/video-uploads/uploaders/${table}`)
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = VideoUploadersResponseDTO.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { table: string },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<VideoUploadersResponseDTO>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<VideoUploadersResponseDTO>,
			GenericWretchError<null>
		> => {
			const key = ["getVideoUploaders", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<VideoUploadersResponseDTO>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<VideoUploadersResponseDTO>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<VideoUploadersResponseDTO>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { table: string },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<VideoUploadersResponseDTO>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<VideoUploadersResponseDTO>,
			GenericWretchError<null>
		> => {
			const key = ["getVideoUploaders", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<VideoUploadersResponseDTO>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<VideoUploadersResponseDTO>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<VideoUploadersResponseDTO>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getVideo = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: { id: number },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<VideoUploadDetail>> => {
		const args = z
			.object({ id: z.number().int().describe("VideoUpload ID") })
			.parse(params);
		const { id } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/admin/video-uploads/${id}`)
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = VideoUploadDetail.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			switch (err.status) {
				case 404: {
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { id: number },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<VideoUploadDetail>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<VideoUploadDetail>,
			GenericWretchError<null>
		> => {
			const key = ["getVideo", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<VideoUploadDetail>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<VideoUploadDetail>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<VideoUploadDetail>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { id: number },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<VideoUploadDetail>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<VideoUploadDetail>,
			GenericWretchError<null>
		> => {
			const key = ["getVideo", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<VideoUploadDetail>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<VideoUploadDetail>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<VideoUploadDetail>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const deleteVideoUpload = (wretch: Http) => {
	const mutation = async (params: { id: number }) => {
		const args = z
			.object({ id: z.number().int().describe("VideoUpload ID") })
			.parse(params);
		const { id } = args;

		try {
			const res = await wretch.url(`/admin/video-uploads/${id}`).delete().res();
			switch (res.status) {
				case 200: {
					return {
						status: res.status,
						data: null,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			switch (err.status) {
				case 404: {
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<null> | undefined,
					GenericWretchError<null>,
					{ id: number }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<null> | undefined,
				GenericWretchError<null>,
				{ id: number }
			>(mutation, options || {});
		},
	};
};

const addVideoMetadata = (wretch: Http) => {
	const mutation = async (params: {
		id: number;
		body: VideoMetadataRequest;
	}) => {
		const args = z
			.object({
				id: z.number().int().describe("VideoUpload ID"),
				body: z.any(),
			})
			.parse(params);
		const { id, body } = args;

		try {
			const res = await wretch
				.url(`/admin/video-uploads/${id}/add-metadata`)
				.json(body)
				.post()
				.res();
			switch (res.status) {
				case 200: {
					return {
						status: res.status,
						data: null,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<null> | undefined,
					GenericWretchError<null>,
					{ id: number; body: VideoMetadataRequest }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<null> | undefined,
				GenericWretchError<null>,
				{ id: number; body: VideoMetadataRequest }
			>(mutation, options || {});
		},
	};
};

const approveVideoMetadata = (wretch: Http) => {
	const mutation = async (params: { id: number }) => {
		const args = z
			.object({ id: z.number().int().describe("VideoUpload ID") })
			.parse(params);
		const { id } = args;

		try {
			const res = await wretch
				.url(`/admin/video-uploads/${id}/metadata-approval`)
				.post()
				.res();
			switch (res.status) {
				case 200: {
					return {
						status: res.status,
						data: null,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<null> | undefined,
					GenericWretchError<null>,
					{ id: number }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<null> | undefined,
				GenericWretchError<null>,
				{ id: number }
			>(mutation, options || {});
		},
	};
};

const removeApproveVideoMetadata = (wretch: Http) => {
	const mutation = async (params: { id: number }) => {
		const args = z
			.object({ id: z.number().int().describe("VideoUpload ID") })
			.parse(params);
		const { id } = args;

		try {
			const res = await wretch
				.url(`/admin/video-uploads/${id}/metadata-approval`)
				.delete()
				.res();
			switch (res.status) {
				case 200: {
					return {
						status: res.status,
						data: null,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			switch (err.status) {
				case 404: {
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<null> | undefined,
					GenericWretchError<null>,
					{ id: number }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<null> | undefined,
				GenericWretchError<null>,
				{ id: number }
			>(mutation, options || {});
		},
	};
};

const getGames = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: {
			clubId: number;
			page?: number;
			limit?: number;
			order?: string;
			withVideo?: number;
			searchTerm?: string;
			searchDateFrom?: string;
			searchDateTo?: string;
		},
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<getGamesResponse200>> => {
		const args = z
			.object({
				clubId: z.number().int(),
				page: z.number().int().optional(),
				limit: z.number().int().optional(),
				order: z.enum(["ASC", "DESC"]).optional(),
				withVideo: z.number().int().nullable().optional(),
				searchTerm: z.string().nullable().optional(),
				searchDateFrom: z.string().nullable().optional(),
				searchDateTo: z.string().nullable().optional(),
			})
			.parse(params);
		const {
			clubId,
			page,
			limit,
			order,
			withVideo,
			searchTerm,
			searchDateFrom,
			searchDateTo,
		} = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/admin/video-uploads/games/${clubId}`)
				.query(
					qs.stringify({
						page,
						limit,
						order,
						withVideo,
						searchTerm,
						searchDateFrom,
						searchDateTo,
					}),
				)
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = getGamesResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: {
				clubId: number;
				page?: number;
				limit?: number;
				order?: string;
				withVideo?: number;
				searchTerm?: string;
				searchDateFrom?: string;
				searchDateTo?: string;
			},
			options?: Omit<
				UseQueryOptions<
					ApiResponse<getGamesResponse200>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<getGamesResponse200>,
			GenericWretchError<null>
		> => {
			const key = ["getGames", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<getGamesResponse200>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<getGamesResponse200>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<getGamesResponse200>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: {
				clubId: number;
				page?: number;
				limit?: number;
				order?: string;
				withVideo?: number;
				searchTerm?: string;
				searchDateFrom?: string;
				searchDateTo?: string;
			},
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<getGamesResponse200>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<getGamesResponse200>,
			GenericWretchError<null>
		> => {
			const key = ["getGames", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<getGamesResponse200>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<getGamesResponse200>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<getGamesResponse200>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getClubs = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: { withVideo?: number; filters?: object[] },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<getClubsResponse200>> => {
		const args = z
			.object({
				withVideo: z.number().int().nullable().optional(),
				filters: z
					.array(
						z.object({
							type: z
								.enum([
									"exact",
									"search",
									"boolean",
									"exists",
									"inarray",
									"datetimerange",
									"numberrange",
								])
								.describe("Type of filter")
								.optional(),
							field: z
								.enum(["clubId", "clubName"])
								.describe("Property to filter")
								.optional(),
							value: z
								.any()
								.superRefine((x, ctx) => {
									const schemas = [
										z.string(),
										z.number().int(),
										z.boolean(),
										z.object({
											start: z.string().datetime({ offset: true }).optional(),
											end: z.string().datetime({ offset: true }).optional(),
										}),
									];
									const errors = schemas.reduce<z.ZodError[]>(
										(errors, schema) =>
											((result) =>
												result.error ? [...errors, result.error] : errors)(
												schema.safeParse(x),
											),
										[],
									);
									if (schemas.length - errors.length !== 1) {
										ctx.addIssue({
											path: ctx.path,
											code: "invalid_union",
											unionErrors: errors,
											message: "Invalid input: Should pass single schema",
										});
									}
								})
								.describe(
									"Filter value, can be a string, integer, date or a date range",
								)
								.optional(),
						}),
					)
					.optional(),
			})
			.parse(params);
		const { withVideo, filters } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url("/admin/video-uploads/clubs")
				.query(qs.stringify({ withVideo, filters }))
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = getClubsResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { withVideo?: number; filters?: object[] },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<getClubsResponse200>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<getClubsResponse200>,
			GenericWretchError<null>
		> => {
			const key = ["getClubs", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<getClubsResponse200>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<getClubsResponse200>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<getClubsResponse200>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { withVideo?: number; filters?: object[] },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<getClubsResponse200>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<getClubsResponse200>,
			GenericWretchError<null>
		> => {
			const key = ["getClubs", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<getClubsResponse200>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<getClubsResponse200>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<getClubsResponse200>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getVideoUploadsNotifications = (
	wretch: Http,
	queryClient: QueryClient,
) => {
	const queryFn = async (
		params: { limit?: number },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<getVideoUploadsNotificationsResponse200>> => {
		const args = z
			.object({ limit: z.number().int().gte(10).default(10) })
			.parse(params);
		const { limit } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url("/admin/video-uploads/notifications")
				.query(qs.stringify({ limit }))
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = getVideoUploadsNotificationsResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { limit?: number },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<getVideoUploadsNotificationsResponse200>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<getVideoUploadsNotificationsResponse200>,
			GenericWretchError<null>
		> => {
			const key = [
				"getVideoUploadsNotifications",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<getVideoUploadsNotificationsResponse200>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<getVideoUploadsNotificationsResponse200>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<getVideoUploadsNotificationsResponse200>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { limit?: number },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<getVideoUploadsNotificationsResponse200>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<getVideoUploadsNotificationsResponse200>,
			GenericWretchError<null>
		> => {
			const key = [
				"getVideoUploadsNotifications",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<getVideoUploadsNotificationsResponse200>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<getVideoUploadsNotificationsResponse200>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<getVideoUploadsNotificationsResponse200>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getTestVideos = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<getTestVideosResponse200>> => {
		try {
			const res = await wretch
				.options({ signal })
				.url("/admin/video-uploads/test")
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = getTestVideosResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			options?: Omit<
				UseQueryOptions<
					ApiResponse<getTestVideosResponse200>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<getTestVideosResponse200>,
			GenericWretchError<null>
		> => {
			const key = ["getTestVideos", ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<getTestVideosResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<getTestVideosResponse200>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<getTestVideosResponse200>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<getTestVideosResponse200>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<getTestVideosResponse200>,
			GenericWretchError<null>
		> => {
			const key = ["getTestVideos", ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<getTestVideosResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<getTestVideosResponse200>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<getTestVideosResponse200>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getVideoGameDetail = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: { id: number },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<GameDetail>> => {
		const args = z
			.object({ id: z.number().int().describe("Game ID") })
			.parse(params);
		const { id } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/admin/video-uploads/game-detail/${id}`)
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = GameDetail.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			switch (err.status) {
				case 404: {
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { id: number },
			options?: Omit<
				UseQueryOptions<ApiResponse<GameDetail>, GenericWretchError<null>>,
				"queryFn"
			>,
		): UseQueryResult<ApiResponse<GameDetail>, GenericWretchError<null>> => {
			const key = ["getVideoGameDetail", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<GameDetail>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<GameDetail>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<GameDetail>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { id: number },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<GameDetail>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<GameDetail>,
			GenericWretchError<null>
		> => {
			const key = ["getVideoGameDetail", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<GameDetail>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<GameDetail>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<GameDetail>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const videoProcessingReset = (wretch: Http) => {
	const mutation = async (params: { id: number }) => {
		const args = z
			.object({ id: z.number().int().describe("VideoUpload ID") })
			.parse(params);
		const { id } = args;

		try {
			const res = await wretch
				.url(`/admin/video-uploads/${id}/processing-reset`)
				.post()
				.res();
			switch (res.status) {
				case 200: {
					return {
						status: res.status,
						data: null,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			switch (err.status) {
				case 404: {
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<null> | undefined,
					GenericWretchError<null>,
					{ id: number }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<null> | undefined,
				GenericWretchError<null>,
				{ id: number }
			>(mutation, options || {});
		},
	};
};

const videoProcessingRetry = (wretch: Http) => {
	const mutation = async (params: { id: number }) => {
		const args = z
			.object({ id: z.number().int().describe("VideoUpload ID") })
			.parse(params);
		const { id } = args;

		try {
			const res = await wretch
				.url(`/admin/video-uploads/${id}/processing-retry`)
				.post()
				.res();
			switch (res.status) {
				case 200: {
					return {
						status: res.status,
						data: null,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			switch (err.status) {
				case 404: {
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<null> | undefined,
					GenericWretchError<null>,
					{ id: number }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<null> | undefined,
				GenericWretchError<null>,
				{ id: number }
			>(mutation, options || {});
		},
	};
};

const renameVideo = (wretch: Http) => {
	const mutation = async (params: {
		id: number;
		body: renameVideoRequestBody;
	}) => {
		const args = z
			.object({
				id: z.number().int().describe("VideoUpload ID"),
				body: z.any(),
			})
			.parse(params);
		const { id, body } = args;

		try {
			const res = await wretch
				.url(`/admin/video-uploads/${id}/rename`)
				.json(body)
				.post()
				.res();
			switch (res.status) {
				case 200: {
					return {
						status: res.status,
						data: null,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			switch (err.status) {
				case 404: {
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<null> | undefined,
					GenericWretchError<null>,
					{ id: number; body: renameVideoRequestBody }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<null> | undefined,
				GenericWretchError<null>,
				{ id: number; body: renameVideoRequestBody }
			>(mutation, options || {});
		},
	};
};

const getVideoServerStatus = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<VideoServerStatusResponse>> => {
		try {
			const res = await wretch
				.options({ signal })
				.url("/admin/video-uploads/server-status")
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = VideoServerStatusResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			options?: Omit<
				UseQueryOptions<
					ApiResponse<VideoServerStatusResponse>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<VideoServerStatusResponse>,
			GenericWretchError<null>
		> => {
			const key = ["getVideoServerStatus", ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<VideoServerStatusResponse>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<VideoServerStatusResponse>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<VideoServerStatusResponse>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<VideoServerStatusResponse>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<VideoServerStatusResponse>,
			GenericWretchError<null>
		> => {
			const key = ["getVideoServerStatus", ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<VideoServerStatusResponse>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<VideoServerStatusResponse>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<VideoServerStatusResponse>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const migratePlayers = (wretch: Http) => {
	const mutation = async (params: {
		id: number;
		body: migratePlayers_Body;
	}) => {
		const args = z
			.object({ id: z.number().int(), body: z.any() })
			.parse(params);
		const { id, body } = args;

		try {
			const res = await wretch
				.url(`/admin/club/${id}/team/migrate-players`)
				.json(body)
				.post()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = migratePlayersResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = migratePlayersResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = migratePlayersResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<migratePlayersResponse200> | undefined,
					GenericWretchError<
						migratePlayersResponse400 | migratePlayersResponse403
					>,
					{ id: number; body: migratePlayers_Body }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<migratePlayersResponse200> | undefined,
				GenericWretchError<
					migratePlayersResponse400 | migratePlayersResponse403
				>,
				{ id: number; body: migratePlayers_Body }
			>(mutation, options || {});
		},
	};
};

const getClubDetail = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: { clubId: number },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<ClubDetailResponseDTO>> => {
		const args = z.object({ clubId: z.number().int() }).parse(params);
		const { clubId } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/admin/api/club/${clubId}`)
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = ClubDetailResponseDTO.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = getClubDetailResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = getClubDetailResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { clubId: number },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<ClubDetailResponseDTO>,
					GenericWretchError<
						getClubDetailResponse400 | getClubDetailResponse403
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<ClubDetailResponseDTO>,
			GenericWretchError<getClubDetailResponse400 | getClubDetailResponse403>
		> => {
			const key = ["getClubDetail", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<ClubDetailResponseDTO>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<ClubDetailResponseDTO>,
				GenericWretchError<getClubDetailResponse400 | getClubDetailResponse403>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<ClubDetailResponseDTO>,
				GenericWretchError<getClubDetailResponse400 | getClubDetailResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { clubId: number },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<ClubDetailResponseDTO>,
					GenericWretchError<
						getClubDetailResponse400 | getClubDetailResponse403
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<ClubDetailResponseDTO>,
			GenericWretchError<getClubDetailResponse400 | getClubDetailResponse403>
		> => {
			const key = ["getClubDetail", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<ClubDetailResponseDTO>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<ClubDetailResponseDTO>,
				GenericWretchError<getClubDetailResponse400 | getClubDetailResponse403>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<ClubDetailResponseDTO>,
				GenericWretchError<getClubDetailResponse400 | getClubDetailResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const postPlayerMappingResults = (wretch: Http) => {
	const mutation = async (params: { id: number }) => {
		const args = z.object({ id: z.number().int() }).parse(params);
		const { id } = args;

		try {
			const res = await wretch
				.url(`/admin/player-user/${id}/mapping-results`)
				.post()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 201: {
					const data = postPlayerMappingResultsResponse201.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = postPlayerMappingResultsResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = postPlayerMappingResultsResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<postPlayerMappingResultsResponse201> | undefined,
					GenericWretchError<
						| postPlayerMappingResultsResponse400
						| postPlayerMappingResultsResponse403
					>,
					{ id: number }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<postPlayerMappingResultsResponse201> | undefined,
				GenericWretchError<
					| postPlayerMappingResultsResponse400
					| postPlayerMappingResultsResponse403
				>,
				{ id: number }
			>(mutation, options || {});
		},
	};
};

const patchPlayerProfile = (wretch: Http) => {
	const mutation = async (params: {
		id: number;
		body: PlayerProfilePatchRequest;
	}) => {
		const args = z
			.object({ id: z.number().int(), body: z.any() })
			.parse(params);
		const { id, body } = args;

		try {
			const res = await wretch
				.url(`/admin/player-profile/${id}`)
				.json(body)
				.patch()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = patchPlayerProfileResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = patchPlayerProfileResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = patchPlayerProfileResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = patchPlayerProfileResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<patchPlayerProfileResponse200> | undefined,
					GenericWretchError<
						| patchPlayerProfileResponse400
						| patchPlayerProfileResponse403
						| patchPlayerProfileResponse404
					>,
					{ id: number; body: PlayerProfilePatchRequest }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<patchPlayerProfileResponse200> | undefined,
				GenericWretchError<
					| patchPlayerProfileResponse400
					| patchPlayerProfileResponse403
					| patchPlayerProfileResponse404
				>,
				{ id: number; body: PlayerProfilePatchRequest }
			>(mutation, options || {});
		},
	};
};

const getPlayerUsers = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: {
			page?: number;
			limit?: number;
			filters?: object[];
			order?: object;
		},
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<PlayerUsersResponse>> => {
		const args = z
			.object({
				page: z.number().int().optional(),
				limit: z.number().int().optional(),
				filters: z
					.array(
						z.object({
							type: z
								.enum([
									"exact",
									"search",
									"boolean",
									"exists",
									"inarray",
									"datetimerange",
									"numberrange",
								])
								.describe("Type of filter")
								.optional(),
							field: z
								.enum([
									"clubId",
									"firstName",
									"lastName",
									"phoneNumber",
									"email",
									"isActive",
									"acceptedTermsOfUse",
									"createdAt",
									"playerId",
								])
								.describe("Property to filter")
								.optional(),
							value: z
								.any()
								.superRefine((x, ctx) => {
									const schemas = [
										z.string(),
										z.number().int(),
										z.boolean(),
										z.object({
											start: z.string().datetime({ offset: true }).optional(),
											end: z.string().datetime({ offset: true }).optional(),
										}),
									];
									const errors = schemas.reduce<z.ZodError[]>(
										(errors, schema) =>
											((result) =>
												result.error ? [...errors, result.error] : errors)(
												schema.safeParse(x),
											),
										[],
									);
									if (schemas.length - errors.length !== 1) {
										ctx.addIssue({
											path: ctx.path,
											code: "invalid_union",
											unionErrors: errors,
											message: "Invalid input: Should pass single schema",
										});
									}
								})
								.describe(
									"Filter value, can be a string, integer, date or a date range",
								)
								.optional(),
						}),
					)
					.optional(),
				order: z
					.object({
						field: z
							.enum([
								"clubName",
								"createdAt",
								"firstName",
								"lastName",
								"phoneNumber",
								"email",
								"playerId",
							])
							.describe("Sorting field")
							.optional(),
						direction: z
							.enum(["ASC", "DESC"])
							.describe("Sorting direction")
							.optional(),
					})
					.optional(),
			})
			.parse(params);
		const { page, limit, filters, order } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url("/admin/player-users")
				.query(qs.stringify({ page, limit, filters, order }))
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = PlayerUsersResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = getPlayerUsersResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = getPlayerUsersResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: {
				page?: number;
				limit?: number;
				filters?: object[];
				order?: object;
			},
			options?: Omit<
				UseQueryOptions<
					ApiResponse<PlayerUsersResponse>,
					GenericWretchError<
						getPlayerUsersResponse400 | getPlayerUsersResponse403
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<PlayerUsersResponse>,
			GenericWretchError<getPlayerUsersResponse400 | getPlayerUsersResponse403>
		> => {
			const key = ["getPlayerUsers", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<PlayerUsersResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<PlayerUsersResponse>,
				GenericWretchError<
					getPlayerUsersResponse400 | getPlayerUsersResponse403
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<PlayerUsersResponse>,
				GenericWretchError<
					getPlayerUsersResponse400 | getPlayerUsersResponse403
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: {
				page?: number;
				limit?: number;
				filters?: object[];
				order?: object;
			},
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<PlayerUsersResponse>,
					GenericWretchError<
						getPlayerUsersResponse400 | getPlayerUsersResponse403
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<PlayerUsersResponse>,
			GenericWretchError<getPlayerUsersResponse400 | getPlayerUsersResponse403>
		> => {
			const key = ["getPlayerUsers", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<PlayerUsersResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<PlayerUsersResponse>,
				GenericWretchError<
					getPlayerUsersResponse400 | getPlayerUsersResponse403
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<PlayerUsersResponse>,
				GenericWretchError<
					getPlayerUsersResponse400 | getPlayerUsersResponse403
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const patchPlayerUser = (wretch: Http) => {
	const mutation = async (params: {
		id: number;
		body: PlayerUserPatchRequest;
	}) => {
		const args = z
			.object({ id: z.number().int(), body: z.any() })
			.parse(params);
		const { id, body } = args;

		try {
			const res = await wretch
				.url(`/admin/player-user/${id}`)
				.json(body)
				.patch()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = patchPlayerUserResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = patchPlayerUserResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = patchPlayerUserResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = patchPlayerUserResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<patchPlayerUserResponse200> | undefined,
					GenericWretchError<
						| patchPlayerUserResponse400
						| patchPlayerUserResponse403
						| patchPlayerUserResponse404
					>,
					{ id: number; body: PlayerUserPatchRequest }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<patchPlayerUserResponse200> | undefined,
				GenericWretchError<
					| patchPlayerUserResponse400
					| patchPlayerUserResponse403
					| patchPlayerUserResponse404
				>,
				{ id: number; body: PlayerUserPatchRequest }
			>(mutation, options || {});
		},
	};
};

const getSubscriptions = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: {
			page?: number;
			limit?: number;
			filters?: object[];
			order?: object;
		},
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<SubscriptionsResponse>> => {
		const args = z
			.object({
				page: z.number().int().optional(),
				limit: z.number().int().optional(),
				filters: z
					.array(
						z.object({
							type: z
								.enum([
									"exact",
									"search",
									"boolean",
									"exists",
									"inarray",
									"datetimerange",
									"numberrange",
								])
								.optional(),
							field: z
								.enum([
									"product",
									"proClubId",
									"active",
									"startDate",
									"expirationDate",
									"pipedriveDealId",
									"id",
									"teamGroupId",
									"gameOptions",
									"eyeballAddons",
								])
								.optional(),
							value: z
								.any()
								.superRefine((x, ctx) => {
									const schemas = [
										z.string(),
										z.number().int(),
										z.boolean(),
										z.array(z.string()),
										z.object({
											start: z.string().nullable().optional(),
											end: z.string().nullable().optional(),
										}),
									];
									const errors = schemas.reduce<z.ZodError[]>(
										(errors, schema) =>
											((result) =>
												result.error ? [...errors, result.error] : errors)(
												schema.safeParse(x),
											),
										[],
									);
									if (schemas.length - errors.length !== 1) {
										ctx.addIssue({
											path: ctx.path,
											code: "invalid_union",
											unionErrors: errors,
											message: "Invalid input: Should pass single schema",
										});
									}
								})
								.optional(),
						}),
					)
					.optional(),
				order: z
					.object({
						field: z
							.enum([
								"product",
								"startDate",
								"expirationDate",
								"maxAccounts",
								"proClubName",
								"id",
							])
							.optional(),
						direction: z.enum(["ASC", "DESC"]).optional(),
					})
					.optional(),
			})
			.parse(params);
		const { page, limit, filters, order } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url("/admin/subscriptions")
				.query(qs.stringify({ page, limit, filters, order }))
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = SubscriptionsResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = getSubscriptionsResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = getSubscriptionsResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: {
				page?: number;
				limit?: number;
				filters?: object[];
				order?: object;
			},
			options?: Omit<
				UseQueryOptions<
					ApiResponse<SubscriptionsResponse>,
					GenericWretchError<
						getSubscriptionsResponse400 | getSubscriptionsResponse403
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<SubscriptionsResponse>,
			GenericWretchError<
				getSubscriptionsResponse400 | getSubscriptionsResponse403
			>
		> => {
			const key = ["getSubscriptions", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<SubscriptionsResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<SubscriptionsResponse>,
				GenericWretchError<
					getSubscriptionsResponse400 | getSubscriptionsResponse403
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<SubscriptionsResponse>,
				GenericWretchError<
					getSubscriptionsResponse400 | getSubscriptionsResponse403
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: {
				page?: number;
				limit?: number;
				filters?: object[];
				order?: object;
			},
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<SubscriptionsResponse>,
					GenericWretchError<
						getSubscriptionsResponse400 | getSubscriptionsResponse403
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<SubscriptionsResponse>,
			GenericWretchError<
				getSubscriptionsResponse400 | getSubscriptionsResponse403
			>
		> => {
			const key = ["getSubscriptions", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<SubscriptionsResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<SubscriptionsResponse>,
				GenericWretchError<
					getSubscriptionsResponse400 | getSubscriptionsResponse403
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<SubscriptionsResponse>,
				GenericWretchError<
					getSubscriptionsResponse400 | getSubscriptionsResponse403
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const postSubscription = (wretch: Http) => {
	const mutation = async (params: { body: SubscriptionUpsertRequest }) => {
		const args = z.object({ body: z.any() }).parse(params);
		const { body } = args;

		try {
			const res = await wretch
				.url("/admin/subscriptions")
				.json(body)
				.post()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = postSubscriptionResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = postSubscriptionResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = postSubscriptionResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<postSubscriptionResponse200> | undefined,
					GenericWretchError<
						postSubscriptionResponse400 | postSubscriptionResponse403
					>,
					{ body: SubscriptionUpsertRequest }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<postSubscriptionResponse200> | undefined,
				GenericWretchError<
					postSubscriptionResponse400 | postSubscriptionResponse403
				>,
				{ body: SubscriptionUpsertRequest }
			>(mutation, options || {});
		},
	};
};

const getSubscription = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: { id: number },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<SubscriptionData>> => {
		const args = z.object({ id: z.number().int() }).parse(params);
		const { id } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/admin/subscriptions/${id}`)
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = SubscriptionData.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 403: {
					err.json = getSubscriptionResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = getSubscriptionResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { id: number },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<SubscriptionData>,
					GenericWretchError<
						getSubscriptionResponse403 | getSubscriptionResponse404
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<SubscriptionData>,
			GenericWretchError<
				getSubscriptionResponse403 | getSubscriptionResponse404
			>
		> => {
			const key = ["getSubscription", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<SubscriptionData>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<SubscriptionData>,
				GenericWretchError<
					getSubscriptionResponse403 | getSubscriptionResponse404
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<SubscriptionData>,
				GenericWretchError<
					getSubscriptionResponse403 | getSubscriptionResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { id: number },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<SubscriptionData>,
					GenericWretchError<
						getSubscriptionResponse403 | getSubscriptionResponse404
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<SubscriptionData>,
			GenericWretchError<
				getSubscriptionResponse403 | getSubscriptionResponse404
			>
		> => {
			const key = ["getSubscription", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<SubscriptionData>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<SubscriptionData>,
				GenericWretchError<
					getSubscriptionResponse403 | getSubscriptionResponse404
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<SubscriptionData>,
				GenericWretchError<
					getSubscriptionResponse403 | getSubscriptionResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const putSubscription = (wretch: Http) => {
	const mutation = async (params: {
		id: number;
		body: SubscriptionUpsertRequest;
	}) => {
		const args = z
			.object({ id: z.number().int(), body: z.any() })
			.parse(params);
		const { id, body } = args;

		try {
			const res = await wretch
				.url(`/admin/subscriptions/${id}`)
				.json(body)
				.put()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = putSubscriptionResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = putSubscriptionResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = putSubscriptionResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = putSubscriptionResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<putSubscriptionResponse200> | undefined,
					GenericWretchError<
						| putSubscriptionResponse400
						| putSubscriptionResponse403
						| putSubscriptionResponse404
					>,
					{ id: number; body: SubscriptionUpsertRequest }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<putSubscriptionResponse200> | undefined,
				GenericWretchError<
					| putSubscriptionResponse400
					| putSubscriptionResponse403
					| putSubscriptionResponse404
				>,
				{ id: number; body: SubscriptionUpsertRequest }
			>(mutation, options || {});
		},
	};
};

const deleteSubscriptionV2 = (wretch: Http) => {
	const mutation = async (params: { id: number }) => {
		const args = z.object({ id: z.number().int() }).parse(params);
		const { id } = args;

		try {
			const res = await wretch.url(`/admin/subscriptions/${id}`).delete().res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = deleteSubscriptionV2Response200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 403: {
					err.json = deleteSubscriptionV2Response403.parse(json);
					break;
				}
				case 404: {
					err.json = deleteSubscriptionV2Response404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<deleteSubscriptionV2Response200> | undefined,
					GenericWretchError<
						deleteSubscriptionV2Response403 | deleteSubscriptionV2Response404
					>,
					{ id: number }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<deleteSubscriptionV2Response200> | undefined,
				GenericWretchError<
					deleteSubscriptionV2Response403 | deleteSubscriptionV2Response404
				>,
				{ id: number }
			>(mutation, options || {});
		},
	};
};

const getSubscriptionProductsConfig = (
	wretch: Http,
	queryClient: QueryClient,
) => {
	const queryFn = async (
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<SubscriptionProductConfigResponse>> => {
		try {
			const res = await wretch
				.options({ signal })
				.url("/admin/subscription-products/config")
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = SubscriptionProductConfigResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 403: {
					err.json = getSubscriptionProductsConfigResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			options?: Omit<
				UseQueryOptions<
					ApiResponse<SubscriptionProductConfigResponse>,
					GenericWretchError<getSubscriptionProductsConfigResponse403>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<SubscriptionProductConfigResponse>,
			GenericWretchError<getSubscriptionProductsConfigResponse403>
		> => {
			const key = [
				"getSubscriptionProductsConfig",
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<SubscriptionProductConfigResponse>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<SubscriptionProductConfigResponse>,
				GenericWretchError<getSubscriptionProductsConfigResponse403>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<SubscriptionProductConfigResponse>,
				GenericWretchError<getSubscriptionProductsConfigResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<SubscriptionProductConfigResponse>,
					GenericWretchError<getSubscriptionProductsConfigResponse403>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<SubscriptionProductConfigResponse>,
			GenericWretchError<getSubscriptionProductsConfigResponse403>
		> => {
			const key = [
				"getSubscriptionProductsConfig",
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<SubscriptionProductConfigResponse>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<SubscriptionProductConfigResponse>,
				GenericWretchError<getSubscriptionProductsConfigResponse403>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<SubscriptionProductConfigResponse>,
				GenericWretchError<getSubscriptionProductsConfigResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getSubscriptionDiscoverCountries = (
	wretch: Http,
	queryClient: QueryClient,
) => {
	const queryFn = async (
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<getSubscriptionDiscoverCountriesResponse200>> => {
		try {
			const res = await wretch
				.options({ signal })
				.url("/admin/subscriptions/discover-countries")
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = getSubscriptionDiscoverCountriesResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 403: {
					err.json = getSubscriptionDiscoverCountriesResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			options?: Omit<
				UseQueryOptions<
					ApiResponse<getSubscriptionDiscoverCountriesResponse200>,
					GenericWretchError<getSubscriptionDiscoverCountriesResponse403>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<getSubscriptionDiscoverCountriesResponse200>,
			GenericWretchError<getSubscriptionDiscoverCountriesResponse403>
		> => {
			const key = [
				"getSubscriptionDiscoverCountries",
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<getSubscriptionDiscoverCountriesResponse200>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<getSubscriptionDiscoverCountriesResponse200>,
				GenericWretchError<getSubscriptionDiscoverCountriesResponse403>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<getSubscriptionDiscoverCountriesResponse200>,
				GenericWretchError<getSubscriptionDiscoverCountriesResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<getSubscriptionDiscoverCountriesResponse200>,
					GenericWretchError<getSubscriptionDiscoverCountriesResponse403>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<getSubscriptionDiscoverCountriesResponse200>,
			GenericWretchError<getSubscriptionDiscoverCountriesResponse403>
		> => {
			const key = [
				"getSubscriptionDiscoverCountries",
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<getSubscriptionDiscoverCountriesResponse200>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<getSubscriptionDiscoverCountriesResponse200>,
				GenericWretchError<getSubscriptionDiscoverCountriesResponse403>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<getSubscriptionDiscoverCountriesResponse200>,
				GenericWretchError<getSubscriptionDiscoverCountriesResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getTeamGroups = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: {
			page?: number;
			limit?: number;
			filters?: object[];
			order?: object;
		},
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<TeamGroupsResponse>> => {
		const args = z
			.object({
				page: z.number().int().optional(),
				limit: z.number().int().optional(),
				filters: z
					.array(
						z.object({
							type: z
								.enum([
									"exact",
									"search",
									"boolean",
									"exists",
									"inarray",
									"datetimerange",
									"numberrange",
								])
								.optional(),
							field: z
								.enum([
									"id",
									"name",
									"teamId",
									"clubId",
									"scoutVisibility",
									"sharedAnalysis",
									"visibleTeamsheets",
								])
								.optional(),
							value: z
								.any()
								.superRefine((x, ctx) => {
									const schemas = [
										z.string(),
										z.number().int(),
										z.boolean(),
										z.array(z.number().int()),
										z.array(z.string()),
									];
									const errors = schemas.reduce<z.ZodError[]>(
										(errors, schema) =>
											((result) =>
												result.error ? [...errors, result.error] : errors)(
												schema.safeParse(x),
											),
										[],
									);
									if (schemas.length - errors.length !== 1) {
										ctx.addIssue({
											path: ctx.path,
											code: "invalid_union",
											unionErrors: errors,
											message: "Invalid input: Should pass single schema",
										});
									}
								})
								.optional(),
						}),
					)
					.optional(),
				order: z
					.object({
						field: z
							.enum([
								"id",
								"name",
								"scoutVisibility",
								"sharedAnalysis",
								"visibleTeamsheets",
								"createdAt",
							])
							.optional(),
						direction: z.enum(["ASC", "DESC"]).optional(),
					})
					.optional(),
			})
			.parse(params);
		const { page, limit, filters, order } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url("/admin/team-groups")
				.query(qs.stringify({ page, limit, filters, order }))
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = TeamGroupsResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = getTeamGroupsResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = getTeamGroupsResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: {
				page?: number;
				limit?: number;
				filters?: object[];
				order?: object;
			},
			options?: Omit<
				UseQueryOptions<
					ApiResponse<TeamGroupsResponse>,
					GenericWretchError<
						getTeamGroupsResponse400 | getTeamGroupsResponse403
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<TeamGroupsResponse>,
			GenericWretchError<getTeamGroupsResponse400 | getTeamGroupsResponse403>
		> => {
			const key = ["getTeamGroups", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<TeamGroupsResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<TeamGroupsResponse>,
				GenericWretchError<getTeamGroupsResponse400 | getTeamGroupsResponse403>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<TeamGroupsResponse>,
				GenericWretchError<getTeamGroupsResponse400 | getTeamGroupsResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: {
				page?: number;
				limit?: number;
				filters?: object[];
				order?: object;
			},
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<TeamGroupsResponse>,
					GenericWretchError<
						getTeamGroupsResponse400 | getTeamGroupsResponse403
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<TeamGroupsResponse>,
			GenericWretchError<getTeamGroupsResponse400 | getTeamGroupsResponse403>
		> => {
			const key = ["getTeamGroups", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<TeamGroupsResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<TeamGroupsResponse>,
				GenericWretchError<getTeamGroupsResponse400 | getTeamGroupsResponse403>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<TeamGroupsResponse>,
				GenericWretchError<getTeamGroupsResponse400 | getTeamGroupsResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const postTeamGroup = (wretch: Http) => {
	const mutation = async (params: { body: TeamGroupUpsertRequest }) => {
		const args = z.object({ body: z.any() }).parse(params);
		const { body } = args;

		try {
			const res = await wretch
				.url("/admin/team-groups")
				.json(body)
				.post()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = postTeamGroupResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = postTeamGroupResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = postTeamGroupResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<postTeamGroupResponse200> | undefined,
					GenericWretchError<
						postTeamGroupResponse400 | postTeamGroupResponse403
					>,
					{ body: TeamGroupUpsertRequest }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<postTeamGroupResponse200> | undefined,
				GenericWretchError<postTeamGroupResponse400 | postTeamGroupResponse403>,
				{ body: TeamGroupUpsertRequest }
			>(mutation, options || {});
		},
	};
};

const getTeamGroupDiscoverTeamOptions = (
	wretch: Http,
	queryClient: QueryClient,
) => {
	const queryFn = async (
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<getTeamGroupDiscoverTeamOptionsResponse200>> => {
		try {
			const res = await wretch
				.options({ signal })
				.url("/admin/team-groups/discover-team-options")
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = getTeamGroupDiscoverTeamOptionsResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 403: {
					err.json = getTeamGroupDiscoverTeamOptionsResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			options?: Omit<
				UseQueryOptions<
					ApiResponse<getTeamGroupDiscoverTeamOptionsResponse200>,
					GenericWretchError<getTeamGroupDiscoverTeamOptionsResponse403>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<getTeamGroupDiscoverTeamOptionsResponse200>,
			GenericWretchError<getTeamGroupDiscoverTeamOptionsResponse403>
		> => {
			const key = [
				"getTeamGroupDiscoverTeamOptions",
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<getTeamGroupDiscoverTeamOptionsResponse200>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<getTeamGroupDiscoverTeamOptionsResponse200>,
				GenericWretchError<getTeamGroupDiscoverTeamOptionsResponse403>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<getTeamGroupDiscoverTeamOptionsResponse200>,
				GenericWretchError<getTeamGroupDiscoverTeamOptionsResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<getTeamGroupDiscoverTeamOptionsResponse200>,
					GenericWretchError<getTeamGroupDiscoverTeamOptionsResponse403>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<getTeamGroupDiscoverTeamOptionsResponse200>,
			GenericWretchError<getTeamGroupDiscoverTeamOptionsResponse403>
		> => {
			const key = [
				"getTeamGroupDiscoverTeamOptions",
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<getTeamGroupDiscoverTeamOptionsResponse200>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<getTeamGroupDiscoverTeamOptionsResponse200>,
				GenericWretchError<getTeamGroupDiscoverTeamOptionsResponse403>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<getTeamGroupDiscoverTeamOptionsResponse200>,
				GenericWretchError<getTeamGroupDiscoverTeamOptionsResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getTeamGroup = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: { id: number },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<TeamGroupRowResponse>> => {
		const args = z.object({ id: z.number().int() }).parse(params);
		const { id } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/admin/team-groups/${id}`)
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = TeamGroupRowResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 403: {
					err.json = getTeamGroupResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = getTeamGroupResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { id: number },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<TeamGroupRowResponse>,
					GenericWretchError<getTeamGroupResponse403 | getTeamGroupResponse404>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<TeamGroupRowResponse>,
			GenericWretchError<getTeamGroupResponse403 | getTeamGroupResponse404>
		> => {
			const key = ["getTeamGroup", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<TeamGroupRowResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<TeamGroupRowResponse>,
				GenericWretchError<getTeamGroupResponse403 | getTeamGroupResponse404>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<TeamGroupRowResponse>,
				GenericWretchError<getTeamGroupResponse403 | getTeamGroupResponse404>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { id: number },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<TeamGroupRowResponse>,
					GenericWretchError<getTeamGroupResponse403 | getTeamGroupResponse404>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<TeamGroupRowResponse>,
			GenericWretchError<getTeamGroupResponse403 | getTeamGroupResponse404>
		> => {
			const key = ["getTeamGroup", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<TeamGroupRowResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<TeamGroupRowResponse>,
				GenericWretchError<getTeamGroupResponse403 | getTeamGroupResponse404>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<TeamGroupRowResponse>,
				GenericWretchError<getTeamGroupResponse403 | getTeamGroupResponse404>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const putTeamGroup = (wretch: Http) => {
	const mutation = async (params: {
		id: number;
		body: TeamGroupUpsertRequest;
	}) => {
		const args = z
			.object({ id: z.number().int(), body: z.any() })
			.parse(params);
		const { id, body } = args;

		try {
			const res = await wretch
				.url(`/admin/team-groups/${id}`)
				.json(body)
				.put()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = putTeamGroupResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = putTeamGroupResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = putTeamGroupResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = putTeamGroupResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<putTeamGroupResponse200> | undefined,
					GenericWretchError<
						| putTeamGroupResponse400
						| putTeamGroupResponse403
						| putTeamGroupResponse404
					>,
					{ id: number; body: TeamGroupUpsertRequest }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<putTeamGroupResponse200> | undefined,
				GenericWretchError<
					| putTeamGroupResponse400
					| putTeamGroupResponse403
					| putTeamGroupResponse404
				>,
				{ id: number; body: TeamGroupUpsertRequest }
			>(mutation, options || {});
		},
	};
};

const deleteTeamGroup = (wretch: Http) => {
	const mutation = async (params: { id: number }) => {
		const args = z.object({ id: z.number().int() }).parse(params);
		const { id } = args;

		try {
			const res = await wretch.url(`/admin/team-groups/${id}`).delete().res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = deleteTeamGroupResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 403: {
					err.json = deleteTeamGroupResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = deleteTeamGroupResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<deleteTeamGroupResponse200> | undefined,
					GenericWretchError<
						deleteTeamGroupResponse403 | deleteTeamGroupResponse404
					>,
					{ id: number }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<deleteTeamGroupResponse200> | undefined,
				GenericWretchError<
					deleteTeamGroupResponse403 | deleteTeamGroupResponse404
				>,
				{ id: number }
			>(mutation, options || {});
		},
	};
};

const getSignUpRequests = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: {
			page?: number;
			limit?: number;
			filters?: object[];
			order?: object;
		},
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<SignUpRequestsResponse>> => {
		const args = z
			.object({
				page: z.number().int().optional(),
				limit: z.number().int().optional(),
				filters: z
					.array(
						z.object({
							type: z
								.enum([
									"exact",
									"search",
									"boolean",
									"exists",
									"inarray",
									"datetimerange",
									"numberrange",
								])
								.describe("Type of filter")
								.optional(),
							field: z
								.enum([
									"clubId",
									"playerUserId",
									"playerId",
									"firstName",
									"middleName",
									"lastName",
									"phoneNumber",
									"email",
									"createdAt",
								])
								.describe("Property to filter")
								.optional(),
							value: z
								.any()
								.superRefine((x, ctx) => {
									const schemas = [
										z.string(),
										z.number().int(),
										z.boolean(),
										z.object({
											start: z.string().datetime({ offset: true }).optional(),
											end: z.string().datetime({ offset: true }).optional(),
										}),
									];
									const errors = schemas.reduce<z.ZodError[]>(
										(errors, schema) =>
											((result) =>
												result.error ? [...errors, result.error] : errors)(
												schema.safeParse(x),
											),
										[],
									);
									if (schemas.length - errors.length !== 1) {
										ctx.addIssue({
											path: ctx.path,
											code: "invalid_union",
											unionErrors: errors,
											message: "Invalid input: Should pass single schema",
										});
									}
								})
								.describe(
									"Filter value, can be a string, integer, date or a date range",
								)
								.optional(),
						}),
					)
					.optional(),
				order: z
					.object({
						field: z
							.enum([
								"clubName",
								"createdAt",
								"firstName",
								"middleName",
								"lastName",
								"phoneNumber",
								"email",
								"playerId",
								"playerUserId",
							])
							.describe("Sorting field")
							.optional(),
						direction: z
							.enum(["ASC", "DESC"])
							.describe("Sorting direction")
							.optional(),
					})
					.optional(),
			})
			.parse(params);
		const { page, limit, filters, order } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url("/admin/signup-requests")
				.query(qs.stringify({ page, limit, filters, order }))
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = SignUpRequestsResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = getSignUpRequestsResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = getSignUpRequestsResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: {
				page?: number;
				limit?: number;
				filters?: object[];
				order?: object;
			},
			options?: Omit<
				UseQueryOptions<
					ApiResponse<SignUpRequestsResponse>,
					GenericWretchError<
						getSignUpRequestsResponse400 | getSignUpRequestsResponse403
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<SignUpRequestsResponse>,
			GenericWretchError<
				getSignUpRequestsResponse400 | getSignUpRequestsResponse403
			>
		> => {
			const key = ["getSignUpRequests", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<SignUpRequestsResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<SignUpRequestsResponse>,
				GenericWretchError<
					getSignUpRequestsResponse400 | getSignUpRequestsResponse403
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<SignUpRequestsResponse>,
				GenericWretchError<
					getSignUpRequestsResponse400 | getSignUpRequestsResponse403
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: {
				page?: number;
				limit?: number;
				filters?: object[];
				order?: object;
			},
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<SignUpRequestsResponse>,
					GenericWretchError<
						getSignUpRequestsResponse400 | getSignUpRequestsResponse403
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<SignUpRequestsResponse>,
			GenericWretchError<
				getSignUpRequestsResponse400 | getSignUpRequestsResponse403
			>
		> => {
			const key = ["getSignUpRequests", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<SignUpRequestsResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<SignUpRequestsResponse>,
				GenericWretchError<
					getSignUpRequestsResponse400 | getSignUpRequestsResponse403
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<SignUpRequestsResponse>,
				GenericWretchError<
					getSignUpRequestsResponse400 | getSignUpRequestsResponse403
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const patchSignUpRequest = (wretch: Http) => {
	const mutation = async (params: {
		id: string;
		body: SignUpRequestPatchRequest;
	}) => {
		const args = z.object({ id: z.string(), body: z.any() }).parse(params);
		const { id, body } = args;

		try {
			const res = await wretch
				.url(`/admin/signup-request/${id}`)
				.json(body)
				.patch()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = patchSignUpRequestResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = patchSignUpRequestResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = patchSignUpRequestResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = patchSignUpRequestResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<patchSignUpRequestResponse200> | undefined,
					GenericWretchError<
						| patchSignUpRequestResponse400
						| patchSignUpRequestResponse403
						| patchSignUpRequestResponse404
					>,
					{ id: string; body: SignUpRequestPatchRequest }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<patchSignUpRequestResponse200> | undefined,
				GenericWretchError<
					| patchSignUpRequestResponse400
					| patchSignUpRequestResponse403
					| patchSignUpRequestResponse404
				>,
				{ id: string; body: SignUpRequestPatchRequest }
			>(mutation, options || {});
		},
	};
};

const getSignUpRequestData = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: { idOrUuid: string },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<SignUpRequestDataResponse>> => {
		const args = z.object({ idOrUuid: z.string() }).parse(params);
		const { idOrUuid } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/admin/signup-request-data/${idOrUuid}`)
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = SignUpRequestDataResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = getSignUpRequestDataResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = getSignUpRequestDataResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = getSignUpRequestDataResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { idOrUuid: string },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<SignUpRequestDataResponse>,
					GenericWretchError<
						| getSignUpRequestDataResponse400
						| getSignUpRequestDataResponse403
						| getSignUpRequestDataResponse404
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<SignUpRequestDataResponse>,
			GenericWretchError<
				| getSignUpRequestDataResponse400
				| getSignUpRequestDataResponse403
				| getSignUpRequestDataResponse404
			>
		> => {
			const key = [
				"getSignUpRequestData",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<ApiResponse<SignUpRequestDataResponse>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<SignUpRequestDataResponse>,
				GenericWretchError<
					| getSignUpRequestDataResponse400
					| getSignUpRequestDataResponse403
					| getSignUpRequestDataResponse404
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<SignUpRequestDataResponse>,
				GenericWretchError<
					| getSignUpRequestDataResponse400
					| getSignUpRequestDataResponse403
					| getSignUpRequestDataResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { idOrUuid: string },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<SignUpRequestDataResponse>,
					GenericWretchError<
						| getSignUpRequestDataResponse400
						| getSignUpRequestDataResponse403
						| getSignUpRequestDataResponse404
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<SignUpRequestDataResponse>,
			GenericWretchError<
				| getSignUpRequestDataResponse400
				| getSignUpRequestDataResponse403
				| getSignUpRequestDataResponse404
			>
		> => {
			const key = [
				"getSignUpRequestData",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<ApiResponse<SignUpRequestDataResponse>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<SignUpRequestDataResponse>,
				GenericWretchError<
					| getSignUpRequestDataResponse400
					| getSignUpRequestDataResponse403
					| getSignUpRequestDataResponse404
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<SignUpRequestDataResponse>,
				GenericWretchError<
					| getSignUpRequestDataResponse400
					| getSignUpRequestDataResponse403
					| getSignUpRequestDataResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getTokenMovements = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: {
			page?: number;
			limit?: number;
			filters?: object[];
			order?: object;
		},
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<DiscoverTokenMovementsResponse>> => {
		const args = z
			.object({
				page: z.number().int().optional(),
				limit: z.number().int().optional(),
				filters: z
					.array(
						z.object({
							type: z
								.enum([
									"exact",
									"search",
									"boolean",
									"exists",
									"inarray",
									"datetimerange",
									"numberrange",
								])
								.describe("Type of filter")
								.optional(),
							field: z
								.enum([
									"id",
									"amount",
									"clubId",
									"clubName",
									"teamId",
									"teamName",
									"relatedClubId",
									"relatedClubName",
									"relatedTeamId",
									"relatedTeamName",
									"userId",
									"userName",
									"movementType",
									"authorType",
									"source",
									"purchasedAt",
									"createdAt",
									"note",
									"paymentLink",
								])
								.describe("Property to filter")
								.optional(),
							value: z
								.any()
								.superRefine((x, ctx) => {
									const schemas = [
										z.string(),
										z.number().int(),
										z.boolean(),
										z.object({
											start: z.string().datetime({ offset: true }).optional(),
											end: z.string().datetime({ offset: true }).optional(),
										}),
									];
									const errors = schemas.reduce<z.ZodError[]>(
										(errors, schema) =>
											((result) =>
												result.error ? [...errors, result.error] : errors)(
												schema.safeParse(x),
											),
										[],
									);
									if (schemas.length - errors.length !== 1) {
										ctx.addIssue({
											path: ctx.path,
											code: "invalid_union",
											unionErrors: errors,
											message: "Invalid input: Should pass single schema",
										});
									}
								})
								.describe(
									"Filter value, can be a string, integer, date or a date range",
								)
								.optional(),
						}),
					)
					.optional(),
				order: z
					.object({
						field: z
							.enum([
								"id",
								"amount",
								"clubName",
								"teamName",
								"relatedClubName",
								"relatedTeamName",
								"userName",
								"movementType",
								"purchasedAt",
								"createdAt",
								"note",
								"authorType",
								"paymentLink",
							])
							.describe("Sorting field")
							.optional(),
						direction: z
							.enum(["ASC", "DESC"])
							.describe("Sorting direction")
							.optional(),
					})
					.optional(),
			})
			.parse(params);
		const { page, limit, filters, order } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url("/admin/token-movements")
				.query(qs.stringify({ page, limit, filters, order }))
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = DiscoverTokenMovementsResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = getTokenMovementsResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = getTokenMovementsResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: {
				page?: number;
				limit?: number;
				filters?: object[];
				order?: object;
			},
			options?: Omit<
				UseQueryOptions<
					ApiResponse<DiscoverTokenMovementsResponse>,
					GenericWretchError<
						getTokenMovementsResponse400 | getTokenMovementsResponse403
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<DiscoverTokenMovementsResponse>,
			GenericWretchError<
				getTokenMovementsResponse400 | getTokenMovementsResponse403
			>
		> => {
			const key = ["getTokenMovements", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<DiscoverTokenMovementsResponse>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<DiscoverTokenMovementsResponse>,
				GenericWretchError<
					getTokenMovementsResponse400 | getTokenMovementsResponse403
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<DiscoverTokenMovementsResponse>,
				GenericWretchError<
					getTokenMovementsResponse400 | getTokenMovementsResponse403
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: {
				page?: number;
				limit?: number;
				filters?: object[];
				order?: object;
			},
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<DiscoverTokenMovementsResponse>,
					GenericWretchError<
						getTokenMovementsResponse400 | getTokenMovementsResponse403
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<DiscoverTokenMovementsResponse>,
			GenericWretchError<
				getTokenMovementsResponse400 | getTokenMovementsResponse403
			>
		> => {
			const key = ["getTokenMovements", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<DiscoverTokenMovementsResponse>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<DiscoverTokenMovementsResponse>,
				GenericWretchError<
					getTokenMovementsResponse400 | getTokenMovementsResponse403
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<DiscoverTokenMovementsResponse>,
				GenericWretchError<
					getTokenMovementsResponse400 | getTokenMovementsResponse403
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const postTokenMovement = (wretch: Http) => {
	const mutation = async (params: {
		body: DiscoverTokenMovementPostRequest;
	}) => {
		const args = z.object({ body: z.any() }).parse(params);
		const { body } = args;

		try {
			const res = await wretch
				.url("/admin/token-movement")
				.json(body)
				.post()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = postTokenMovementResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = postTokenMovementResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = postTokenMovementResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<postTokenMovementResponse200> | undefined,
					GenericWretchError<
						postTokenMovementResponse400 | postTokenMovementResponse403
					>,
					{ body: DiscoverTokenMovementPostRequest }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<postTokenMovementResponse200> | undefined,
				GenericWretchError<
					postTokenMovementResponse400 | postTokenMovementResponse403
				>,
				{ body: DiscoverTokenMovementPostRequest }
			>(mutation, options || {});
		},
	};
};

const getOptionsClubsOfSignUpRequests = (
	wretch: Http,
	queryClient: QueryClient,
) => {
	const queryFn = async (
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<getOptionsClubsOfSignUpRequestsResponse200>> => {
		try {
			const res = await wretch
				.options({ signal })
				.url("/admin/options/clubs/of/signup-requests")
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = getOptionsClubsOfSignUpRequestsResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = getOptionsClubsOfSignUpRequestsResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = getOptionsClubsOfSignUpRequestsResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			options?: Omit<
				UseQueryOptions<
					ApiResponse<getOptionsClubsOfSignUpRequestsResponse200>,
					GenericWretchError<
						| getOptionsClubsOfSignUpRequestsResponse400
						| getOptionsClubsOfSignUpRequestsResponse403
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<getOptionsClubsOfSignUpRequestsResponse200>,
			GenericWretchError<
				| getOptionsClubsOfSignUpRequestsResponse400
				| getOptionsClubsOfSignUpRequestsResponse403
			>
		> => {
			const key = [
				"getOptionsClubsOfSignUpRequests",
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<getOptionsClubsOfSignUpRequestsResponse200>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<getOptionsClubsOfSignUpRequestsResponse200>,
				GenericWretchError<
					| getOptionsClubsOfSignUpRequestsResponse400
					| getOptionsClubsOfSignUpRequestsResponse403
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<getOptionsClubsOfSignUpRequestsResponse200>,
				GenericWretchError<
					| getOptionsClubsOfSignUpRequestsResponse400
					| getOptionsClubsOfSignUpRequestsResponse403
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<getOptionsClubsOfSignUpRequestsResponse200>,
					GenericWretchError<
						| getOptionsClubsOfSignUpRequestsResponse400
						| getOptionsClubsOfSignUpRequestsResponse403
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<getOptionsClubsOfSignUpRequestsResponse200>,
			GenericWretchError<
				| getOptionsClubsOfSignUpRequestsResponse400
				| getOptionsClubsOfSignUpRequestsResponse403
			>
		> => {
			const key = [
				"getOptionsClubsOfSignUpRequests",
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<getOptionsClubsOfSignUpRequestsResponse200>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<getOptionsClubsOfSignUpRequestsResponse200>,
				GenericWretchError<
					| getOptionsClubsOfSignUpRequestsResponse400
					| getOptionsClubsOfSignUpRequestsResponse403
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<getOptionsClubsOfSignUpRequestsResponse200>,
				GenericWretchError<
					| getOptionsClubsOfSignUpRequestsResponse400
					| getOptionsClubsOfSignUpRequestsResponse403
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getOptionsClubsOfPlayerUsers = (
	wretch: Http,
	queryClient: QueryClient,
) => {
	const queryFn = async (
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<getOptionsClubsOfPlayerUsersResponse200>> => {
		try {
			const res = await wretch
				.options({ signal })
				.url("/admin/options/clubs/of/player-users")
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = getOptionsClubsOfPlayerUsersResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = getOptionsClubsOfPlayerUsersResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = getOptionsClubsOfPlayerUsersResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			options?: Omit<
				UseQueryOptions<
					ApiResponse<getOptionsClubsOfPlayerUsersResponse200>,
					GenericWretchError<
						| getOptionsClubsOfPlayerUsersResponse400
						| getOptionsClubsOfPlayerUsersResponse403
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<getOptionsClubsOfPlayerUsersResponse200>,
			GenericWretchError<
				| getOptionsClubsOfPlayerUsersResponse400
				| getOptionsClubsOfPlayerUsersResponse403
			>
		> => {
			const key = [
				"getOptionsClubsOfPlayerUsers",
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<getOptionsClubsOfPlayerUsersResponse200>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<getOptionsClubsOfPlayerUsersResponse200>,
				GenericWretchError<
					| getOptionsClubsOfPlayerUsersResponse400
					| getOptionsClubsOfPlayerUsersResponse403
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<getOptionsClubsOfPlayerUsersResponse200>,
				GenericWretchError<
					| getOptionsClubsOfPlayerUsersResponse400
					| getOptionsClubsOfPlayerUsersResponse403
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<getOptionsClubsOfPlayerUsersResponse200>,
					GenericWretchError<
						| getOptionsClubsOfPlayerUsersResponse400
						| getOptionsClubsOfPlayerUsersResponse403
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<getOptionsClubsOfPlayerUsersResponse200>,
			GenericWretchError<
				| getOptionsClubsOfPlayerUsersResponse400
				| getOptionsClubsOfPlayerUsersResponse403
			>
		> => {
			const key = [
				"getOptionsClubsOfPlayerUsers",
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<getOptionsClubsOfPlayerUsersResponse200>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<getOptionsClubsOfPlayerUsersResponse200>,
				GenericWretchError<
					| getOptionsClubsOfPlayerUsersResponse400
					| getOptionsClubsOfPlayerUsersResponse403
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<getOptionsClubsOfPlayerUsersResponse200>,
				GenericWretchError<
					| getOptionsClubsOfPlayerUsersResponse400
					| getOptionsClubsOfPlayerUsersResponse403
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const postGameTeamSheetExport = (wretch: Http) => {
	const mutation = async (params: { id: number; exportType: string }) => {
		const args = z
			.object({
				id: z.number().int().describe("Game ID"),
				exportType: z.enum(["csv", "pdf", "xlsx"]).describe("Export type"),
			})
			.parse(params);
		const { id, exportType } = args;

		try {
			const res = await wretch
				.url(`/game/${id}/team-sheet/export/${exportType}`)
				.post()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = FileResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = postGameTeamSheetExportResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = postGameTeamSheetExportResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = postGameTeamSheetExportResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<FileResponse> | undefined,
					GenericWretchError<
						| postGameTeamSheetExportResponse400
						| postGameTeamSheetExportResponse403
						| postGameTeamSheetExportResponse404
					>,
					{ id: number; exportType: string }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<FileResponse> | undefined,
				GenericWretchError<
					| postGameTeamSheetExportResponse400
					| postGameTeamSheetExportResponse403
					| postGameTeamSheetExportResponse404
				>,
				{ id: number; exportType: string }
			>(mutation, options || {});
		},
	};
};

const putUpdateGameLeftTeam = (wretch: Http) => {
	const mutation = async (params: {
		gameId: number;
		body: UpdateGameLeftTeamRequest;
	}) => {
		const args = z
			.object({ gameId: z.number().int(), body: z.any() })
			.parse(params);
		const { gameId, body } = args;

		try {
			const res = await wretch
				.url(`/videos/games/${gameId}/update-left-team`)
				.json(body)
				.put()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = putUpdateGameLeftTeamResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<putUpdateGameLeftTeamResponse200> | undefined,
					GenericWretchError<null>,
					{ gameId: number; body: UpdateGameLeftTeamRequest }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<putUpdateGameLeftTeamResponse200> | undefined,
				GenericWretchError<null>,
				{ gameId: number; body: UpdateGameLeftTeamRequest }
			>(mutation, options || {});
		},
	};
};

const putGameAnalysisApproval = (wretch: Http) => {
	const mutation = async (params: {
		id: number;
		body: UpdateAnalysisApprovalRequest;
	}) => {
		const args = z
			.object({ id: z.number().int(), body: z.any() })
			.parse(params);
		const { id, body } = args;

		try {
			const res = await wretch
				.url(`/videos/games/${id}/game-analysis-approval`)
				.json(body)
				.put()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = putGameAnalysisApprovalResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<putGameAnalysisApprovalResponse200> | undefined,
					GenericWretchError<null>,
					{ id: number; body: UpdateAnalysisApprovalRequest }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<putGameAnalysisApprovalResponse200> | undefined,
				GenericWretchError<null>,
				{ id: number; body: UpdateAnalysisApprovalRequest }
			>(mutation, options || {});
		},
	};
};

const putGameResync = (wretch: Http) => {
	const mutation = async (params: { id: number; body: GameResyncRequest }) => {
		const args = z
			.object({ id: z.number().int(), body: z.any() })
			.parse(params);
		const { id, body } = args;

		try {
			const res = await wretch
				.url(`/videos/games/${id}/game-resync`)
				.json(body)
				.put()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = putGameResyncResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<putGameResyncResponse200> | undefined,
					GenericWretchError<null>,
					{ id: number; body: GameResyncRequest }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<putGameResyncResponse200> | undefined,
				GenericWretchError<null>,
				{ id: number; body: GameResyncRequest }
			>(mutation, options || {});
		},
	};
};

const shareClipLink = (wretch: Http) => {
	const mutation = async (params: { id: number; body: shareClipLink_Body }) => {
		const args = z
			.object({ id: z.number().int(), body: z.any() })
			.parse(params);
		const { id, body } = args;

		try {
			const res = await wretch
				.url(`/admin/me/clips/${id}/share`)
				.json(body)
				.post()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = shareClipLinkResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 404: {
					err.json = shareClipLinkResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<shareClipLinkResponse200> | undefined,
					GenericWretchError<shareClipLinkResponse404>,
					{ id: number; body: shareClipLink_Body }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<shareClipLinkResponse200> | undefined,
				GenericWretchError<shareClipLinkResponse404>,
				{ id: number; body: shareClipLink_Body }
			>(mutation, options || {});
		},
	};
};

const getTeamTokenBalance = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: { id: number },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<TokenBalanceResponse>> => {
		const args = z.object({ id: z.number().int() }).parse(params);
		const { id } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/admin/team/${id}/token-balance`)
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = TokenBalanceResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = getTeamTokenBalanceResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = getTeamTokenBalanceResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { id: number },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<TokenBalanceResponse>,
					GenericWretchError<
						getTeamTokenBalanceResponse400 | getTeamTokenBalanceResponse403
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<TokenBalanceResponse>,
			GenericWretchError<
				getTeamTokenBalanceResponse400 | getTeamTokenBalanceResponse403
			>
		> => {
			const key = ["getTeamTokenBalance", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<TokenBalanceResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<TokenBalanceResponse>,
				GenericWretchError<
					getTeamTokenBalanceResponse400 | getTeamTokenBalanceResponse403
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<TokenBalanceResponse>,
				GenericWretchError<
					getTeamTokenBalanceResponse400 | getTeamTokenBalanceResponse403
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { id: number },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<TokenBalanceResponse>,
					GenericWretchError<
						getTeamTokenBalanceResponse400 | getTeamTokenBalanceResponse403
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<TokenBalanceResponse>,
			GenericWretchError<
				getTeamTokenBalanceResponse400 | getTeamTokenBalanceResponse403
			>
		> => {
			const key = ["getTeamTokenBalance", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<TokenBalanceResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<TokenBalanceResponse>,
				GenericWretchError<
					getTeamTokenBalanceResponse400 | getTeamTokenBalanceResponse403
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<TokenBalanceResponse>,
				GenericWretchError<
					getTeamTokenBalanceResponse400 | getTeamTokenBalanceResponse403
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getClubTokenBalance = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: { id: number },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<TokenBalanceResponse>> => {
		const args = z.object({ id: z.number().int() }).parse(params);
		const { id } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/admin/club/${id}/token-balance`)
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = TokenBalanceResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = getClubTokenBalanceResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = getClubTokenBalanceResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { id: number },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<TokenBalanceResponse>,
					GenericWretchError<
						getClubTokenBalanceResponse400 | getClubTokenBalanceResponse403
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<TokenBalanceResponse>,
			GenericWretchError<
				getClubTokenBalanceResponse400 | getClubTokenBalanceResponse403
			>
		> => {
			const key = ["getClubTokenBalance", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<TokenBalanceResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<TokenBalanceResponse>,
				GenericWretchError<
					getClubTokenBalanceResponse400 | getClubTokenBalanceResponse403
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<TokenBalanceResponse>,
				GenericWretchError<
					getClubTokenBalanceResponse400 | getClubTokenBalanceResponse403
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { id: number },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<TokenBalanceResponse>,
					GenericWretchError<
						getClubTokenBalanceResponse400 | getClubTokenBalanceResponse403
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<TokenBalanceResponse>,
			GenericWretchError<
				getClubTokenBalanceResponse400 | getClubTokenBalanceResponse403
			>
		> => {
			const key = ["getClubTokenBalance", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<TokenBalanceResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<TokenBalanceResponse>,
				GenericWretchError<
					getClubTokenBalanceResponse400 | getClubTokenBalanceResponse403
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<TokenBalanceResponse>,
				GenericWretchError<
					getClubTokenBalanceResponse400 | getClubTokenBalanceResponse403
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getTasks = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: { filters?: undefined },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<VideoIngestorPaginatedResponse>> => {
		const args = z.object({ filters: z.any().optional() }).parse(params);
		const { filters } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url("/admin/video-tasks")
				.query(qs.stringify({ filters }))
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = VideoIngestorPaginatedResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 403: {
					err.json = getTasksResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { filters?: undefined },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<VideoIngestorPaginatedResponse>,
					GenericWretchError<getTasksResponse403>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<VideoIngestorPaginatedResponse>,
			GenericWretchError<getTasksResponse403>
		> => {
			const key = ["getTasks", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<VideoIngestorPaginatedResponse>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<VideoIngestorPaginatedResponse>,
				GenericWretchError<getTasksResponse403>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<VideoIngestorPaginatedResponse>,
				GenericWretchError<getTasksResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { filters?: undefined },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<VideoIngestorPaginatedResponse>,
					GenericWretchError<getTasksResponse403>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<VideoIngestorPaginatedResponse>,
			GenericWretchError<getTasksResponse403>
		> => {
			const key = ["getTasks", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<VideoIngestorPaginatedResponse>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<VideoIngestorPaginatedResponse>,
				GenericWretchError<getTasksResponse403>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<VideoIngestorPaginatedResponse>,
				GenericWretchError<getTasksResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getAmatuerClubs = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<getAmatuerClubsResponse200>> => {
		try {
			const res = await wretch
				.options({ signal })
				.url("/admin/amateur-clubs")
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = getAmatuerClubsResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 403: {
					err.json = getAmatuerClubsResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			options?: Omit<
				UseQueryOptions<
					ApiResponse<getAmatuerClubsResponse200>,
					GenericWretchError<getAmatuerClubsResponse403>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<getAmatuerClubsResponse200>,
			GenericWretchError<getAmatuerClubsResponse403>
		> => {
			const key = ["getAmatuerClubs", ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<getAmatuerClubsResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<getAmatuerClubsResponse200>,
				GenericWretchError<getAmatuerClubsResponse403>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<getAmatuerClubsResponse200>,
				GenericWretchError<getAmatuerClubsResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<getAmatuerClubsResponse200>,
					GenericWretchError<getAmatuerClubsResponse403>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<getAmatuerClubsResponse200>,
			GenericWretchError<getAmatuerClubsResponse403>
		> => {
			const key = ["getAmatuerClubs", ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<getAmatuerClubsResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<getAmatuerClubsResponse200>,
				GenericWretchError<getAmatuerClubsResponse403>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<getAmatuerClubsResponse200>,
				GenericWretchError<getAmatuerClubsResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getTeamsheet = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: { id: number },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<VideoIngestorTeamsheetResponse>> => {
		const args = z
			.object({ id: z.number().int().describe("Task ID") })
			.parse(params);
		const { id } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/admin/video-tasks/${id}/teamsheet`)
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = VideoIngestorTeamsheetResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 403: {
					err.json = getTeamsheetResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = getTeamsheetResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { id: number },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<VideoIngestorTeamsheetResponse>,
					GenericWretchError<getTeamsheetResponse403 | getTeamsheetResponse404>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<VideoIngestorTeamsheetResponse>,
			GenericWretchError<getTeamsheetResponse403 | getTeamsheetResponse404>
		> => {
			const key = ["getTeamsheet", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<VideoIngestorTeamsheetResponse>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<VideoIngestorTeamsheetResponse>,
				GenericWretchError<getTeamsheetResponse403 | getTeamsheetResponse404>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<VideoIngestorTeamsheetResponse>,
				GenericWretchError<getTeamsheetResponse403 | getTeamsheetResponse404>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { id: number },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<VideoIngestorTeamsheetResponse>,
					GenericWretchError<getTeamsheetResponse403 | getTeamsheetResponse404>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<VideoIngestorTeamsheetResponse>,
			GenericWretchError<getTeamsheetResponse403 | getTeamsheetResponse404>
		> => {
			const key = ["getTeamsheet", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<VideoIngestorTeamsheetResponse>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<VideoIngestorTeamsheetResponse>,
				GenericWretchError<getTeamsheetResponse403 | getTeamsheetResponse404>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<VideoIngestorTeamsheetResponse>,
				GenericWretchError<getTeamsheetResponse403 | getTeamsheetResponse404>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const updatePlayerStatus = (wretch: Http) => {
	const mutation = async (params: {
		id: number;
		body: UpdatePlayerStatusRequestDto;
	}) => {
		const args = z
			.object({
				id: z
					.number()
					.int()
					.describe("Player ID from players table in ingestor"),
				body: z.any(),
			})
			.parse(params);
		const { id, body } = args;

		try {
			const res = await wretch
				.url(`/admin/video-tasks/players/${id}`)
				.json(body)
				.put()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = VideoIngestorUpdatePlayerStatusResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 403: {
					err.json = updatePlayerStatusResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<VideoIngestorUpdatePlayerStatusResponse> | undefined,
					GenericWretchError<updatePlayerStatusResponse403>,
					{ id: number; body: UpdatePlayerStatusRequestDto }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<VideoIngestorUpdatePlayerStatusResponse> | undefined,
				GenericWretchError<updatePlayerStatusResponse403>,
				{ id: number; body: UpdatePlayerStatusRequestDto }
			>(mutation, options || {});
		},
	};
};

const getTeamsForDropdown = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<getTeamsForDropdownResponse200>> => {
		try {
			const res = await wretch
				.options({ signal })
				.url("/admin/video-tasks/teams")
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = getTeamsForDropdownResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 403: {
					err.json = getTeamsForDropdownResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			options?: Omit<
				UseQueryOptions<
					ApiResponse<getTeamsForDropdownResponse200>,
					GenericWretchError<getTeamsForDropdownResponse403>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<getTeamsForDropdownResponse200>,
			GenericWretchError<getTeamsForDropdownResponse403>
		> => {
			const key = ["getTeamsForDropdown", ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<getTeamsForDropdownResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<getTeamsForDropdownResponse200>,
				GenericWretchError<getTeamsForDropdownResponse403>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<getTeamsForDropdownResponse200>,
				GenericWretchError<getTeamsForDropdownResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<getTeamsForDropdownResponse200>,
					GenericWretchError<getTeamsForDropdownResponse403>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<getTeamsForDropdownResponse200>,
			GenericWretchError<getTeamsForDropdownResponse403>
		> => {
			const key = ["getTeamsForDropdown", ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<getTeamsForDropdownResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<getTeamsForDropdownResponse200>,
				GenericWretchError<getTeamsForDropdownResponse403>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<getTeamsForDropdownResponse200>,
				GenericWretchError<getTeamsForDropdownResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getLeaguesForDropdown = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<getLeaguesForDropdownResponse200>> => {
		try {
			const res = await wretch
				.options({ signal })
				.url("/admin/video-tasks/leagues")
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = getLeaguesForDropdownResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 403: {
					err.json = getLeaguesForDropdownResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			options?: Omit<
				UseQueryOptions<
					ApiResponse<getLeaguesForDropdownResponse200>,
					GenericWretchError<getLeaguesForDropdownResponse403>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<getLeaguesForDropdownResponse200>,
			GenericWretchError<getLeaguesForDropdownResponse403>
		> => {
			const key = ["getLeaguesForDropdown", ...(options?.queryKey || [])];
			const query: QueryFunction<
				ApiResponse<getLeaguesForDropdownResponse200>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<getLeaguesForDropdownResponse200>,
				GenericWretchError<getLeaguesForDropdownResponse403>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<getLeaguesForDropdownResponse200>,
				GenericWretchError<getLeaguesForDropdownResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<getLeaguesForDropdownResponse200>,
					GenericWretchError<getLeaguesForDropdownResponse403>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<getLeaguesForDropdownResponse200>,
			GenericWretchError<getLeaguesForDropdownResponse403>
		> => {
			const key = ["getLeaguesForDropdown", ...(options?.queryKey || [])];
			const query: QueryFunction<
				ApiResponse<getLeaguesForDropdownResponse200>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<getLeaguesForDropdownResponse200>,
				GenericWretchError<getLeaguesForDropdownResponse403>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<getLeaguesForDropdownResponse200>,
				GenericWretchError<getLeaguesForDropdownResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getCountriesForDropdown = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<getCountriesForDropdownResponse200>> => {
		try {
			const res = await wretch
				.options({ signal })
				.url("/admin/video-tasks/countries")
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = getCountriesForDropdownResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 403: {
					err.json = getCountriesForDropdownResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			options?: Omit<
				UseQueryOptions<
					ApiResponse<getCountriesForDropdownResponse200>,
					GenericWretchError<getCountriesForDropdownResponse403>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<getCountriesForDropdownResponse200>,
			GenericWretchError<getCountriesForDropdownResponse403>
		> => {
			const key = ["getCountriesForDropdown", ...(options?.queryKey || [])];
			const query: QueryFunction<
				ApiResponse<getCountriesForDropdownResponse200>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<getCountriesForDropdownResponse200>,
				GenericWretchError<getCountriesForDropdownResponse403>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<getCountriesForDropdownResponse200>,
				GenericWretchError<getCountriesForDropdownResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<getCountriesForDropdownResponse200>,
					GenericWretchError<getCountriesForDropdownResponse403>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<getCountriesForDropdownResponse200>,
			GenericWretchError<getCountriesForDropdownResponse403>
		> => {
			const key = ["getCountriesForDropdown", ...(options?.queryKey || [])];
			const query: QueryFunction<
				ApiResponse<getCountriesForDropdownResponse200>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<getCountriesForDropdownResponse200>,
				GenericWretchError<getCountriesForDropdownResponse403>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<getCountriesForDropdownResponse200>,
				GenericWretchError<getCountriesForDropdownResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const createPlayer = (wretch: Http) => {
	const mutation = async (params: {
		id: number;
		body: CreatePlayerRequest;
	}) => {
		const args = z
			.object({ id: z.number().int().describe("Club ID"), body: z.any() })
			.parse(params);
		const { id, body } = args;

		try {
			const res = await wretch
				.url(`/admin/video-tasks/club/${id}/create-player`)
				.json(body)
				.post()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = createPlayerResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 403: {
					err.json = createPlayerResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = createPlayerResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<createPlayerResponse200> | undefined,
					GenericWretchError<createPlayerResponse403 | createPlayerResponse404>,
					{ id: number; body: CreatePlayerRequest }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<createPlayerResponse200> | undefined,
				GenericWretchError<createPlayerResponse403 | createPlayerResponse404>,
				{ id: number; body: CreatePlayerRequest }
			>(mutation, options || {});
		},
	};
};

const playerSearchDropdown = (wretch: Http) => {
	const mutation = async (params: { body: PlayerDropdownSearchRequest }) => {
		const args = z.object({ body: z.any() }).parse(params);
		const { body } = args;

		try {
			const res = await wretch
				.url("/admin/video-tasks/player-search-dropdown")
				.json(body)
				.post()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = PlayerDropdownResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = playerSearchDropdownResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = playerSearchDropdownResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<PlayerDropdownResponse> | undefined,
					GenericWretchError<
						playerSearchDropdownResponse400 | playerSearchDropdownResponse403
					>,
					{ body: PlayerDropdownSearchRequest }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<PlayerDropdownResponse> | undefined,
				GenericWretchError<
					playerSearchDropdownResponse400 | playerSearchDropdownResponse403
				>,
				{ body: PlayerDropdownSearchRequest }
			>(mutation, options || {});
		},
	};
};

const adminGetGameAnalysisPhysical = (
	wretch: Http,
	queryClient: QueryClient,
) => {
	const queryFn = async (
		params: { gameId: number },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<GameAnalysisPhysicalResponse>> => {
		const args = z
			.object({
				gameId: z
					.number()
					.int()
					.describe("The ID of the game to retrieve physical analysis for."),
			})
			.parse(params);
		const { gameId } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/admin/game-analysis-physical/${gameId}`)
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = GameAnalysisPhysicalResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 401: {
					err.json = adminGetGameAnalysisPhysicalResponse401.parse(json);
					break;
				}
				case 403: {
					err.json = adminGetGameAnalysisPhysicalResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = adminGetGameAnalysisPhysicalResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { gameId: number },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<GameAnalysisPhysicalResponse>,
					GenericWretchError<
						| adminGetGameAnalysisPhysicalResponse401
						| adminGetGameAnalysisPhysicalResponse403
						| adminGetGameAnalysisPhysicalResponse404
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<GameAnalysisPhysicalResponse>,
			GenericWretchError<
				| adminGetGameAnalysisPhysicalResponse401
				| adminGetGameAnalysisPhysicalResponse403
				| adminGetGameAnalysisPhysicalResponse404
			>
		> => {
			const key = [
				"adminGetGameAnalysisPhysical",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<ApiResponse<GameAnalysisPhysicalResponse>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<GameAnalysisPhysicalResponse>,
				GenericWretchError<
					| adminGetGameAnalysisPhysicalResponse401
					| adminGetGameAnalysisPhysicalResponse403
					| adminGetGameAnalysisPhysicalResponse404
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<GameAnalysisPhysicalResponse>,
				GenericWretchError<
					| adminGetGameAnalysisPhysicalResponse401
					| adminGetGameAnalysisPhysicalResponse403
					| adminGetGameAnalysisPhysicalResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { gameId: number },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<GameAnalysisPhysicalResponse>,
					GenericWretchError<
						| adminGetGameAnalysisPhysicalResponse401
						| adminGetGameAnalysisPhysicalResponse403
						| adminGetGameAnalysisPhysicalResponse404
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<GameAnalysisPhysicalResponse>,
			GenericWretchError<
				| adminGetGameAnalysisPhysicalResponse401
				| adminGetGameAnalysisPhysicalResponse403
				| adminGetGameAnalysisPhysicalResponse404
			>
		> => {
			const key = [
				"adminGetGameAnalysisPhysical",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<ApiResponse<GameAnalysisPhysicalResponse>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<GameAnalysisPhysicalResponse>,
				GenericWretchError<
					| adminGetGameAnalysisPhysicalResponse401
					| adminGetGameAnalysisPhysicalResponse403
					| adminGetGameAnalysisPhysicalResponse404
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<GameAnalysisPhysicalResponse>,
				GenericWretchError<
					| adminGetGameAnalysisPhysicalResponse401
					| adminGetGameAnalysisPhysicalResponse403
					| adminGetGameAnalysisPhysicalResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const postGameAnalysisPhysicalVideoUrl = (wretch: Http) => {
	const mutation = async (params: {
		body: postGameAnalysisPhysicalVideoUrl_Body;
	}) => {
		const args = z.object({ body: z.any() }).parse(params);
		const { body } = args;

		try {
			const res = await wretch
				.url("/admin/game-analysis-physical/video-url")
				.json(body)
				.post()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = GameAnalysisPhysicalResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = postGameAnalysisPhysicalVideoUrlResponse400.parse(json);
					break;
				}
				case 401: {
					err.json = postGameAnalysisPhysicalVideoUrlResponse401.parse(json);
					break;
				}
				case 403: {
					err.json = postGameAnalysisPhysicalVideoUrlResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = postGameAnalysisPhysicalVideoUrlResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<GameAnalysisPhysicalResponse> | undefined,
					GenericWretchError<
						| postGameAnalysisPhysicalVideoUrlResponse400
						| postGameAnalysisPhysicalVideoUrlResponse401
						| postGameAnalysisPhysicalVideoUrlResponse403
						| postGameAnalysisPhysicalVideoUrlResponse404
					>,
					{ body: postGameAnalysisPhysicalVideoUrl_Body }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<GameAnalysisPhysicalResponse> | undefined,
				GenericWretchError<
					| postGameAnalysisPhysicalVideoUrlResponse400
					| postGameAnalysisPhysicalVideoUrlResponse401
					| postGameAnalysisPhysicalVideoUrlResponse403
					| postGameAnalysisPhysicalVideoUrlResponse404
				>,
				{ body: postGameAnalysisPhysicalVideoUrl_Body }
			>(mutation, options || {});
		},
	};
};

const getGameEventsDebug = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: { gameId: number },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<GameEventsDebugResponse>> => {
		const args = z.object({ gameId: z.number().int() }).parse(params);
		const { gameId } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/admin/api/game-events-debug/${gameId}`)
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = GameEventsDebugResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			switch (err.status) {
				case 404: {
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { gameId: number },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<GameEventsDebugResponse>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<GameEventsDebugResponse>,
			GenericWretchError<null>
		> => {
			const key = ["getGameEventsDebug", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<GameEventsDebugResponse>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<GameEventsDebugResponse>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<GameEventsDebugResponse>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { gameId: number },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<GameEventsDebugResponse>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<GameEventsDebugResponse>,
			GenericWretchError<null>
		> => {
			const key = ["getGameEventsDebug", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<GameEventsDebugResponse>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<GameEventsDebugResponse>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<GameEventsDebugResponse>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getGameStatsDebug = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: { gameId: number },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<GameStatsDebugResponse>> => {
		const args = z.object({ gameId: z.number().int() }).parse(params);
		const { gameId } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/admin/api/game-stats-debug/${gameId}`)
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = GameStatsDebugResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			switch (err.status) {
				case 404: {
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { gameId: number },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<GameStatsDebugResponse>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<GameStatsDebugResponse>,
			GenericWretchError<null>
		> => {
			const key = ["getGameStatsDebug", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<GameStatsDebugResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<GameStatsDebugResponse>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<GameStatsDebugResponse>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { gameId: number },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<GameStatsDebugResponse>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<GameStatsDebugResponse>,
			GenericWretchError<null>
		> => {
			const key = ["getGameStatsDebug", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<GameStatsDebugResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<GameStatsDebugResponse>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<GameStatsDebugResponse>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getGamePage = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: { id: number },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<GamePageResponse>> => {
		const args = z.object({ id: z.number().int() }).parse(params);
		const { id } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/game/${id}/page`)
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = GamePageResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = getGamePageResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = getGamePageResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = getGamePageResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { id: number },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<GamePageResponse>,
					GenericWretchError<
						| getGamePageResponse400
						| getGamePageResponse403
						| getGamePageResponse404
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<GamePageResponse>,
			GenericWretchError<
				getGamePageResponse400 | getGamePageResponse403 | getGamePageResponse404
			>
		> => {
			const key = ["getGamePage", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<GamePageResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<GamePageResponse>,
				GenericWretchError<
					| getGamePageResponse400
					| getGamePageResponse403
					| getGamePageResponse404
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<GamePageResponse>,
				GenericWretchError<
					| getGamePageResponse400
					| getGamePageResponse403
					| getGamePageResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { id: number },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<GamePageResponse>,
					GenericWretchError<
						| getGamePageResponse400
						| getGamePageResponse403
						| getGamePageResponse404
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<GamePageResponse>,
			GenericWretchError<
				getGamePageResponse400 | getGamePageResponse403 | getGamePageResponse404
			>
		> => {
			const key = ["getGamePage", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<GamePageResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<GamePageResponse>,
				GenericWretchError<
					| getGamePageResponse400
					| getGamePageResponse403
					| getGamePageResponse404
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<GamePageResponse>,
				GenericWretchError<
					| getGamePageResponse400
					| getGamePageResponse403
					| getGamePageResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getGamePageLazy = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: { id: number },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<GamePageLazyResponse>> => {
		const args = z.object({ id: z.number().int() }).parse(params);
		const { id } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/game/${id}/page/lazy`)
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = GamePageLazyResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = getGamePageLazyResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = getGamePageLazyResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = getGamePageLazyResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { id: number },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<GamePageLazyResponse>,
					GenericWretchError<
						| getGamePageLazyResponse400
						| getGamePageLazyResponse403
						| getGamePageLazyResponse404
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<GamePageLazyResponse>,
			GenericWretchError<
				| getGamePageLazyResponse400
				| getGamePageLazyResponse403
				| getGamePageLazyResponse404
			>
		> => {
			const key = ["getGamePageLazy", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<GamePageLazyResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<GamePageLazyResponse>,
				GenericWretchError<
					| getGamePageLazyResponse400
					| getGamePageLazyResponse403
					| getGamePageLazyResponse404
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<GamePageLazyResponse>,
				GenericWretchError<
					| getGamePageLazyResponse400
					| getGamePageLazyResponse403
					| getGamePageLazyResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { id: number },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<GamePageLazyResponse>,
					GenericWretchError<
						| getGamePageLazyResponse400
						| getGamePageLazyResponse403
						| getGamePageLazyResponse404
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<GamePageLazyResponse>,
			GenericWretchError<
				| getGamePageLazyResponse400
				| getGamePageLazyResponse403
				| getGamePageLazyResponse404
			>
		> => {
			const key = ["getGamePageLazy", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<GamePageLazyResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<GamePageLazyResponse>,
				GenericWretchError<
					| getGamePageLazyResponse400
					| getGamePageLazyResponse403
					| getGamePageLazyResponse404
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<GamePageLazyResponse>,
				GenericWretchError<
					| getGamePageLazyResponse400
					| getGamePageLazyResponse403
					| getGamePageLazyResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const putGameScore = (wretch: Http) => {
	const mutation = async (params: {
		id: number;
		body: GamePutScoreRequest;
	}) => {
		const args = z
			.object({ id: z.number().int(), body: z.any() })
			.parse(params);
		const { id, body } = args;

		try {
			const res = await wretch.url(`/game/${id}/score`).json(body).put().res();
			switch (res.status) {
				case 204: {
					return {
						status: res.status,
						data: null,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = putGameScoreResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = putGameScoreResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = putGameScoreResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<null> | undefined,
					GenericWretchError<
						| putGameScoreResponse400
						| putGameScoreResponse403
						| putGameScoreResponse404
					>,
					{ id: number; body: GamePutScoreRequest }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<null> | undefined,
				GenericWretchError<
					| putGameScoreResponse400
					| putGameScoreResponse403
					| putGameScoreResponse404
				>,
				{ id: number; body: GamePutScoreRequest }
			>(mutation, options || {});
		},
	};
};

const getGameTeamStat = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: { id: number },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<GameTeamStatResponse>> => {
		const args = z.object({ id: z.number().int() }).parse(params);
		const { id } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/game/${id}/team-stat`)
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = GameTeamStatResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = getGameTeamStatResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = getGameTeamStatResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = getGameTeamStatResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { id: number },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<GameTeamStatResponse>,
					GenericWretchError<
						| getGameTeamStatResponse400
						| getGameTeamStatResponse403
						| getGameTeamStatResponse404
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<GameTeamStatResponse>,
			GenericWretchError<
				| getGameTeamStatResponse400
				| getGameTeamStatResponse403
				| getGameTeamStatResponse404
			>
		> => {
			const key = ["getGameTeamStat", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<GameTeamStatResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<GameTeamStatResponse>,
				GenericWretchError<
					| getGameTeamStatResponse400
					| getGameTeamStatResponse403
					| getGameTeamStatResponse404
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<GameTeamStatResponse>,
				GenericWretchError<
					| getGameTeamStatResponse400
					| getGameTeamStatResponse403
					| getGameTeamStatResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { id: number },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<GameTeamStatResponse>,
					GenericWretchError<
						| getGameTeamStatResponse400
						| getGameTeamStatResponse403
						| getGameTeamStatResponse404
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<GameTeamStatResponse>,
			GenericWretchError<
				| getGameTeamStatResponse400
				| getGameTeamStatResponse403
				| getGameTeamStatResponse404
			>
		> => {
			const key = ["getGameTeamStat", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<GameTeamStatResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<GameTeamStatResponse>,
				GenericWretchError<
					| getGameTeamStatResponse400
					| getGameTeamStatResponse403
					| getGameTeamStatResponse404
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<GameTeamStatResponse>,
				GenericWretchError<
					| getGameTeamStatResponse400
					| getGameTeamStatResponse403
					| getGameTeamStatResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getGamePlayerStat = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: { id: number },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<GamePlayerStatResponse>> => {
		const args = z.object({ id: z.number().int() }).parse(params);
		const { id } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/game/${id}/player-stat`)
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = GamePlayerStatResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = getGamePlayerStatResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = getGamePlayerStatResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = getGamePlayerStatResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { id: number },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<GamePlayerStatResponse>,
					GenericWretchError<
						| getGamePlayerStatResponse400
						| getGamePlayerStatResponse403
						| getGamePlayerStatResponse404
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<GamePlayerStatResponse>,
			GenericWretchError<
				| getGamePlayerStatResponse400
				| getGamePlayerStatResponse403
				| getGamePlayerStatResponse404
			>
		> => {
			const key = ["getGamePlayerStat", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<GamePlayerStatResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<GamePlayerStatResponse>,
				GenericWretchError<
					| getGamePlayerStatResponse400
					| getGamePlayerStatResponse403
					| getGamePlayerStatResponse404
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<GamePlayerStatResponse>,
				GenericWretchError<
					| getGamePlayerStatResponse400
					| getGamePlayerStatResponse403
					| getGamePlayerStatResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { id: number },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<GamePlayerStatResponse>,
					GenericWretchError<
						| getGamePlayerStatResponse400
						| getGamePlayerStatResponse403
						| getGamePlayerStatResponse404
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<GamePlayerStatResponse>,
			GenericWretchError<
				| getGamePlayerStatResponse400
				| getGamePlayerStatResponse403
				| getGamePlayerStatResponse404
			>
		> => {
			const key = ["getGamePlayerStat", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<GamePlayerStatResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<GamePlayerStatResponse>,
				GenericWretchError<
					| getGamePlayerStatResponse400
					| getGamePlayerStatResponse403
					| getGamePlayerStatResponse404
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<GamePlayerStatResponse>,
				GenericWretchError<
					| getGamePlayerStatResponse400
					| getGamePlayerStatResponse403
					| getGamePlayerStatResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const putGameTeamColors = (wretch: Http) => {
	const mutation = async (params: {
		id: number;
		body: GameTeamPutColorsRequest;
	}) => {
		const args = z
			.object({ id: z.number().int(), body: z.any() })
			.parse(params);
		const { id, body } = args;

		try {
			const res = await wretch
				.url(`/game-team/${id}/colors`)
				.json(body)
				.put()
				.res();
			switch (res.status) {
				case 204: {
					return {
						status: res.status,
						data: null,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = putGameTeamColorsResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = putGameTeamColorsResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = putGameTeamColorsResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<null> | undefined,
					GenericWretchError<
						| putGameTeamColorsResponse400
						| putGameTeamColorsResponse403
						| putGameTeamColorsResponse404
					>,
					{ id: number; body: GameTeamPutColorsRequest }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<null> | undefined,
				GenericWretchError<
					| putGameTeamColorsResponse400
					| putGameTeamColorsResponse403
					| putGameTeamColorsResponse404
				>,
				{ id: number; body: GameTeamPutColorsRequest }
			>(mutation, options || {});
		},
	};
};

const setRatingToGamePlayer = (wretch: Http) => {
	const mutation = async (params: { id: number; body: RatingPutRequest }) => {
		const args = z
			.object({
				id: z.number().int().describe("ID of the game player"),
				body: z.any(),
			})
			.parse(params);
		const { id, body } = args;

		try {
			const res = await wretch
				.url(`/game-player/${id}/rating`)
				.json(body)
				.put()
				.res();
			switch (res.status) {
				case 204: {
					return {
						status: res.status,
						data: null,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = setRatingToGamePlayerResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = setRatingToGamePlayerResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = setRatingToGamePlayerResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<null> | undefined,
					GenericWretchError<
						| setRatingToGamePlayerResponse400
						| setRatingToGamePlayerResponse403
						| setRatingToGamePlayerResponse404
					>,
					{ id: number; body: RatingPutRequest }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<null> | undefined,
				GenericWretchError<
					| setRatingToGamePlayerResponse400
					| setRatingToGamePlayerResponse403
					| setRatingToGamePlayerResponse404
				>,
				{ id: number; body: RatingPutRequest }
			>(mutation, options || {});
		},
	};
};

const setBadgesToGamePlayer = (wretch: Http) => {
	const mutation = async (params: { id: number; body: BadgesPutRequest }) => {
		const args = z
			.object({
				id: z.number().int().describe("ID of the game player"),
				body: z.any(),
			})
			.parse(params);
		const { id, body } = args;

		try {
			const res = await wretch
				.url(`/game-player/${id}/badges`)
				.json(body)
				.put()
				.res();
			switch (res.status) {
				case 204: {
					return {
						status: res.status,
						data: null,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = setBadgesToGamePlayerResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = setBadgesToGamePlayerResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = setBadgesToGamePlayerResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<null> | undefined,
					GenericWretchError<
						| setBadgesToGamePlayerResponse400
						| setBadgesToGamePlayerResponse403
						| setBadgesToGamePlayerResponse404
					>,
					{ id: number; body: BadgesPutRequest }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<null> | undefined,
				GenericWretchError<
					| setBadgesToGamePlayerResponse400
					| setBadgesToGamePlayerResponse403
					| setBadgesToGamePlayerResponse404
				>,
				{ id: number; body: BadgesPutRequest }
			>(mutation, options || {});
		},
	};
};

const postGameHighlight = (wretch: Http) => {
	const mutation = async (params: {
		gameId: number;
		body: HighlightPostRequest;
	}) => {
		const args = z
			.object({ gameId: z.number().int(), body: z.any() })
			.parse(params);
		const { gameId, body } = args;

		try {
			const res = await wretch
				.url(`/highlight/create/${gameId}`)
				.json(body)
				.post()
				.res();
			switch (res.status) {
				case 204: {
					return {
						status: res.status,
						data: null,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = postGameHighlightResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = postGameHighlightResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = postGameHighlightResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<null> | undefined,
					GenericWretchError<
						| postGameHighlightResponse400
						| postGameHighlightResponse403
						| postGameHighlightResponse404
					>,
					{ gameId: number; body: HighlightPostRequest }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<null> | undefined,
				GenericWretchError<
					| postGameHighlightResponse400
					| postGameHighlightResponse403
					| postGameHighlightResponse404
				>,
				{ gameId: number; body: HighlightPostRequest }
			>(mutation, options || {});
		},
	};
};

const deleteGameHighlight = (wretch: Http) => {
	const mutation = async (params: { id: number }) => {
		const args = z.object({ id: z.number().int() }).parse(params);
		const { id } = args;

		try {
			const res = await wretch.url(`/highlight/${id}`).delete().res();
			switch (res.status) {
				case 204: {
					return {
						status: res.status,
						data: null,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = deleteGameHighlightResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = deleteGameHighlightResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = deleteGameHighlightResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<null> | undefined,
					GenericWretchError<
						| deleteGameHighlightResponse400
						| deleteGameHighlightResponse403
						| deleteGameHighlightResponse404
					>,
					{ id: number }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<null> | undefined,
				GenericWretchError<
					| deleteGameHighlightResponse400
					| deleteGameHighlightResponse403
					| deleteGameHighlightResponse404
				>,
				{ id: number }
			>(mutation, options || {});
		},
	};
};

const patchGameHighlight = (wretch: Http) => {
	const mutation = async (params: {
		id: number;
		body: HighlightPatchRequest;
	}) => {
		const args = z
			.object({ id: z.number().int(), body: z.any() })
			.parse(params);
		const { id, body } = args;

		try {
			const res = await wretch.url(`/highlight/${id}`).json(body).patch().res();
			switch (res.status) {
				case 204: {
					return {
						status: res.status,
						data: null,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = patchGameHighlightResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = patchGameHighlightResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = patchGameHighlightResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<null> | undefined,
					GenericWretchError<
						| patchGameHighlightResponse400
						| patchGameHighlightResponse403
						| patchGameHighlightResponse404
					>,
					{ id: number; body: HighlightPatchRequest }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<null> | undefined,
				GenericWretchError<
					| patchGameHighlightResponse400
					| patchGameHighlightResponse403
					| patchGameHighlightResponse404
				>,
				{ id: number; body: HighlightPatchRequest }
			>(mutation, options || {});
		},
	};
};

const getGameClips = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: {
			page?: number;
			limit?: number;
			filters?: object[];
			order?: object;
		},
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<GameClipsResponse>> => {
		const args = z
			.object({
				page: z.number().int().optional(),
				limit: z.number().int().optional(),
				filters: z
					.array(
						z.object({
							type: z
								.enum([
									"exact",
									"search",
									"boolean",
									"exists",
									"inarray",
									"datetimerange",
									"numberrange",
								])
								.describe("Type of filter")
								.optional(),
							field: z
								.enum(["gameId", "author", "isFavorite", "tag"])
								.describe("Property to filter")
								.optional(),
							value: z
								.any()
								.superRefine((x, ctx) => {
									const schemas = [
										z.string(),
										z.number().int(),
										z.boolean(),
										z.object({
											start: z.string().datetime({ offset: true }).optional(),
											end: z.string().datetime({ offset: true }).optional(),
										}),
									];
									const errors = schemas.reduce<z.ZodError[]>(
										(errors, schema) =>
											((result) =>
												result.error ? [...errors, result.error] : errors)(
												schema.safeParse(x),
											),
										[],
									);
									if (schemas.length - errors.length !== 1) {
										ctx.addIssue({
											path: ctx.path,
											code: "invalid_union",
											unionErrors: errors,
											message: "Invalid input: Should pass single schema",
										});
									}
								})
								.describe(
									"Filter value, can be a string, integer, date or a date range",
								)
								.optional(),
						}),
					)
					.optional(),
				order: z
					.object({
						field: z.literal("createdAt").describe("Sorting field").optional(),
						direction: z
							.enum(["ASC", "DESC"])
							.describe("Sorting direction")
							.optional(),
					})
					.optional(),
			})
			.parse(params);
		const { page, limit, filters, order } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url("/game-clip")
				.query(qs.stringify({ page, limit, filters, order }))
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = GameClipsResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = getGameClipsResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = getGameClipsResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: {
				page?: number;
				limit?: number;
				filters?: object[];
				order?: object;
			},
			options?: Omit<
				UseQueryOptions<
					ApiResponse<GameClipsResponse>,
					GenericWretchError<getGameClipsResponse400 | getGameClipsResponse403>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<GameClipsResponse>,
			GenericWretchError<getGameClipsResponse400 | getGameClipsResponse403>
		> => {
			const key = ["getGameClips", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<GameClipsResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<GameClipsResponse>,
				GenericWretchError<getGameClipsResponse400 | getGameClipsResponse403>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<GameClipsResponse>,
				GenericWretchError<getGameClipsResponse400 | getGameClipsResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: {
				page?: number;
				limit?: number;
				filters?: object[];
				order?: object;
			},
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<GameClipsResponse>,
					GenericWretchError<getGameClipsResponse400 | getGameClipsResponse403>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<GameClipsResponse>,
			GenericWretchError<getGameClipsResponse400 | getGameClipsResponse403>
		> => {
			const key = ["getGameClips", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<GameClipsResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<GameClipsResponse>,
				GenericWretchError<getGameClipsResponse400 | getGameClipsResponse403>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<GameClipsResponse>,
				GenericWretchError<getGameClipsResponse400 | getGameClipsResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getGameClip = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: { id: number },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<GameClipResponse>> => {
		const args = z.object({ id: z.number().int() }).parse(params);
		const { id } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/game-clip/${id}`)
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = GameClipResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = getGameClipResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = getGameClipResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { id: number },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<GameClipResponse>,
					GenericWretchError<getGameClipResponse400 | getGameClipResponse403>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<GameClipResponse>,
			GenericWretchError<getGameClipResponse400 | getGameClipResponse403>
		> => {
			const key = ["getGameClip", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<GameClipResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<GameClipResponse>,
				GenericWretchError<getGameClipResponse400 | getGameClipResponse403>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<GameClipResponse>,
				GenericWretchError<getGameClipResponse400 | getGameClipResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { id: number },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<GameClipResponse>,
					GenericWretchError<getGameClipResponse400 | getGameClipResponse403>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<GameClipResponse>,
			GenericWretchError<getGameClipResponse400 | getGameClipResponse403>
		> => {
			const key = ["getGameClip", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<GameClipResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<GameClipResponse>,
				GenericWretchError<getGameClipResponse400 | getGameClipResponse403>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<GameClipResponse>,
				GenericWretchError<getGameClipResponse400 | getGameClipResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const putGameClip = (wretch: Http) => {
	const mutation = async (params: { id: number; body: GameClipPutRequest }) => {
		const args = z
			.object({ id: z.number().int(), body: z.any() })
			.parse(params);
		const { id, body } = args;

		try {
			const res = await wretch.url(`/game-clip/${id}`).json(body).put().res();
			switch (res.status) {
				case 204: {
					return {
						status: res.status,
						data: null,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = putGameClipResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = putGameClipResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = putGameClipResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<null> | undefined,
					GenericWretchError<
						| putGameClipResponse400
						| putGameClipResponse403
						| putGameClipResponse404
					>,
					{ id: number; body: GameClipPutRequest }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<null> | undefined,
				GenericWretchError<
					| putGameClipResponse400
					| putGameClipResponse403
					| putGameClipResponse404
				>,
				{ id: number; body: GameClipPutRequest }
			>(mutation, options || {});
		},
	};
};

const deleteGameClip = (wretch: Http) => {
	const mutation = async (params: { id: number }) => {
		const args = z.object({ id: z.number().int() }).parse(params);
		const { id } = args;

		try {
			const res = await wretch.url(`/game-clip/${id}`).delete().res();
			switch (res.status) {
				case 204: {
					return {
						status: res.status,
						data: null,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = deleteGameClipResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = deleteGameClipResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = deleteGameClipResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<null> | undefined,
					GenericWretchError<
						| deleteGameClipResponse400
						| deleteGameClipResponse403
						| deleteGameClipResponse404
					>,
					{ id: number }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<null> | undefined,
				GenericWretchError<
					| deleteGameClipResponse400
					| deleteGameClipResponse403
					| deleteGameClipResponse404
				>,
				{ id: number }
			>(mutation, options || {});
		},
	};
};

const postGameClip = (wretch: Http) => {
	const mutation = async (params: {
		gameId: number;
		body: GameClipPostRequest;
	}) => {
		const args = z
			.object({ gameId: z.number().int(), body: z.any() })
			.parse(params);
		const { gameId, body } = args;

		try {
			const res = await wretch
				.url(`/game-clip/create/${gameId}`)
				.json(body)
				.post()
				.res();
			switch (res.status) {
				case 204: {
					return {
						status: res.status,
						data: null,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = postGameClipResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = postGameClipResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = postGameClipResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<null> | undefined,
					GenericWretchError<
						| postGameClipResponse400
						| postGameClipResponse403
						| postGameClipResponse404
					>,
					{ gameId: number; body: GameClipPostRequest }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<null> | undefined,
				GenericWretchError<
					| postGameClipResponse400
					| postGameClipResponse403
					| postGameClipResponse404
				>,
				{ gameId: number; body: GameClipPostRequest }
			>(mutation, options || {});
		},
	};
};

const putGameTeamSheet = (wretch: Http) => {
	const mutation = async (params: {
		id: number;
		body: GameTeamSheetRequest;
	}) => {
		const args = z
			.object({ id: z.number().int(), body: z.any() })
			.parse(params);
		const { id, body } = args;

		try {
			const res = await wretch
				.url(`/game-team/${id}/team-sheet`)
				.json(body)
				.put()
				.res();
			switch (res.status) {
				case 204: {
					return {
						status: res.status,
						data: null,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = putGameTeamSheetResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = putGameTeamSheetResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = putGameTeamSheetResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<null> | undefined,
					GenericWretchError<
						| putGameTeamSheetResponse400
						| putGameTeamSheetResponse403
						| putGameTeamSheetResponse404
					>,
					{ id: number; body: GameTeamSheetRequest }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<null> | undefined,
				GenericWretchError<
					| putGameTeamSheetResponse400
					| putGameTeamSheetResponse403
					| putGameTeamSheetResponse404
				>,
				{ id: number; body: GameTeamSheetRequest }
			>(mutation, options || {});
		},
	};
};

const getGameTeamSheetCompositionPlayers = (
	wretch: Http,
	queryClient: QueryClient,
) => {
	const queryFn = async (
		params: { id: number; composition: string },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<GameTeamSheetCompositionPlayersResponse>> => {
		const args = z
			.object({
				id: z.number().int().describe("Game team ID"),
				composition: z
					.enum([
						"TEAM_SHEET_EMPTY",
						"TEAM_SHEET_4_3_3_OFFENSIF",
						"TEAM_SHEET_4_3_3",
						"TEAM_SHEET_4_3_2_1",
						"TEAM_SHEET_4_3_1_2",
						"TEAM_SHEET_4_4_2_PLAT",
						"TEAM_SHEET_4_4_2_LOSANGE",
						"TEAM_SHEET_4_2_4",
						"TEAM_SHEET_4_4_1_1",
						"TEAM_SHEET_4_5_1",
						"TEAM_SHEET_3_5_2",
						"TEAM_SHEET_3_4_3",
						"TEAM_SHEET_3_4_1_2",
						"TEAM_SHEET_5_3_2",
						"TEAM_SHEET_5_4_1",
						"TEAM_SHEET_5_2_1_2",
					])
					.describe("Composition type"),
			})
			.parse(params);
		const { id, composition } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/game-team/${id}/team-sheet/${composition}`)
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = GameTeamSheetCompositionPlayersResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = getGameTeamSheetCompositionPlayersResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = getGameTeamSheetCompositionPlayersResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = getGameTeamSheetCompositionPlayersResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { id: number; composition: string },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<GameTeamSheetCompositionPlayersResponse>,
					GenericWretchError<
						| getGameTeamSheetCompositionPlayersResponse400
						| getGameTeamSheetCompositionPlayersResponse403
						| getGameTeamSheetCompositionPlayersResponse404
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<GameTeamSheetCompositionPlayersResponse>,
			GenericWretchError<
				| getGameTeamSheetCompositionPlayersResponse400
				| getGameTeamSheetCompositionPlayersResponse403
				| getGameTeamSheetCompositionPlayersResponse404
			>
		> => {
			const key = [
				"getGameTeamSheetCompositionPlayers",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<GameTeamSheetCompositionPlayersResponse>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<GameTeamSheetCompositionPlayersResponse>,
				GenericWretchError<
					| getGameTeamSheetCompositionPlayersResponse400
					| getGameTeamSheetCompositionPlayersResponse403
					| getGameTeamSheetCompositionPlayersResponse404
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<GameTeamSheetCompositionPlayersResponse>,
				GenericWretchError<
					| getGameTeamSheetCompositionPlayersResponse400
					| getGameTeamSheetCompositionPlayersResponse403
					| getGameTeamSheetCompositionPlayersResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { id: number; composition: string },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<GameTeamSheetCompositionPlayersResponse>,
					GenericWretchError<
						| getGameTeamSheetCompositionPlayersResponse400
						| getGameTeamSheetCompositionPlayersResponse403
						| getGameTeamSheetCompositionPlayersResponse404
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<GameTeamSheetCompositionPlayersResponse>,
			GenericWretchError<
				| getGameTeamSheetCompositionPlayersResponse400
				| getGameTeamSheetCompositionPlayersResponse403
				| getGameTeamSheetCompositionPlayersResponse404
			>
		> => {
			const key = [
				"getGameTeamSheetCompositionPlayers",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<GameTeamSheetCompositionPlayersResponse>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<GameTeamSheetCompositionPlayersResponse>,
				GenericWretchError<
					| getGameTeamSheetCompositionPlayersResponse400
					| getGameTeamSheetCompositionPlayersResponse403
					| getGameTeamSheetCompositionPlayersResponse404
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<GameTeamSheetCompositionPlayersResponse>,
				GenericWretchError<
					| getGameTeamSheetCompositionPlayersResponse400
					| getGameTeamSheetCompositionPlayersResponse403
					| getGameTeamSheetCompositionPlayersResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getGameTeamPlayersSearch = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: { id: number; limit?: number; filter?: string },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<GameTeamSheetGameTeamPlayersSearchResponse>> => {
		const args = z
			.object({
				id: z.number().int().describe("Game team ID"),
				limit: z.number().int().describe("limit").optional(),
				filter: z.string().describe("filter - player name").optional(),
			})
			.parse(params);
		const { id, limit, filter } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/game-team/${id}/team-sheet/players`)
				.query(qs.stringify({ limit, filter }))
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = GameTeamSheetGameTeamPlayersSearchResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = getGameTeamPlayersSearchResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = getGameTeamPlayersSearchResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = getGameTeamPlayersSearchResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { id: number; limit?: number; filter?: string },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<GameTeamSheetGameTeamPlayersSearchResponse>,
					GenericWretchError<
						| getGameTeamPlayersSearchResponse400
						| getGameTeamPlayersSearchResponse403
						| getGameTeamPlayersSearchResponse404
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<GameTeamSheetGameTeamPlayersSearchResponse>,
			GenericWretchError<
				| getGameTeamPlayersSearchResponse400
				| getGameTeamPlayersSearchResponse403
				| getGameTeamPlayersSearchResponse404
			>
		> => {
			const key = [
				"getGameTeamPlayersSearch",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<GameTeamSheetGameTeamPlayersSearchResponse>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<GameTeamSheetGameTeamPlayersSearchResponse>,
				GenericWretchError<
					| getGameTeamPlayersSearchResponse400
					| getGameTeamPlayersSearchResponse403
					| getGameTeamPlayersSearchResponse404
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<GameTeamSheetGameTeamPlayersSearchResponse>,
				GenericWretchError<
					| getGameTeamPlayersSearchResponse400
					| getGameTeamPlayersSearchResponse403
					| getGameTeamPlayersSearchResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { id: number; limit?: number; filter?: string },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<GameTeamSheetGameTeamPlayersSearchResponse>,
					GenericWretchError<
						| getGameTeamPlayersSearchResponse400
						| getGameTeamPlayersSearchResponse403
						| getGameTeamPlayersSearchResponse404
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<GameTeamSheetGameTeamPlayersSearchResponse>,
			GenericWretchError<
				| getGameTeamPlayersSearchResponse400
				| getGameTeamPlayersSearchResponse403
				| getGameTeamPlayersSearchResponse404
			>
		> => {
			const key = [
				"getGameTeamPlayersSearch",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<GameTeamSheetGameTeamPlayersSearchResponse>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<GameTeamSheetGameTeamPlayersSearchResponse>,
				GenericWretchError<
					| getGameTeamPlayersSearchResponse400
					| getGameTeamPlayersSearchResponse403
					| getGameTeamPlayersSearchResponse404
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<GameTeamSheetGameTeamPlayersSearchResponse>,
				GenericWretchError<
					| getGameTeamPlayersSearchResponse400
					| getGameTeamPlayersSearchResponse403
					| getGameTeamPlayersSearchResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getGameTeamSheetClubPlayers = (
	wretch: Http,
	queryClient: QueryClient,
) => {
	const queryFn = async (
		params: { id: number },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<GameTeamSheetPlayersResponse>> => {
		const args = z
			.object({ id: z.number().int().describe("Game team ID") })
			.parse(params);
		const { id } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/game-team/${id}/team-sheet/club-players`)
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = GameTeamSheetPlayersResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = getGameTeamSheetClubPlayersResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = getGameTeamSheetClubPlayersResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = getGameTeamSheetClubPlayersResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { id: number },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<GameTeamSheetPlayersResponse>,
					GenericWretchError<
						| getGameTeamSheetClubPlayersResponse400
						| getGameTeamSheetClubPlayersResponse403
						| getGameTeamSheetClubPlayersResponse404
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<GameTeamSheetPlayersResponse>,
			GenericWretchError<
				| getGameTeamSheetClubPlayersResponse400
				| getGameTeamSheetClubPlayersResponse403
				| getGameTeamSheetClubPlayersResponse404
			>
		> => {
			const key = [
				"getGameTeamSheetClubPlayers",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<ApiResponse<GameTeamSheetPlayersResponse>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<GameTeamSheetPlayersResponse>,
				GenericWretchError<
					| getGameTeamSheetClubPlayersResponse400
					| getGameTeamSheetClubPlayersResponse403
					| getGameTeamSheetClubPlayersResponse404
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<GameTeamSheetPlayersResponse>,
				GenericWretchError<
					| getGameTeamSheetClubPlayersResponse400
					| getGameTeamSheetClubPlayersResponse403
					| getGameTeamSheetClubPlayersResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { id: number },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<GameTeamSheetPlayersResponse>,
					GenericWretchError<
						| getGameTeamSheetClubPlayersResponse400
						| getGameTeamSheetClubPlayersResponse403
						| getGameTeamSheetClubPlayersResponse404
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<GameTeamSheetPlayersResponse>,
			GenericWretchError<
				| getGameTeamSheetClubPlayersResponse400
				| getGameTeamSheetClubPlayersResponse403
				| getGameTeamSheetClubPlayersResponse404
			>
		> => {
			const key = [
				"getGameTeamSheetClubPlayers",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<ApiResponse<GameTeamSheetPlayersResponse>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<GameTeamSheetPlayersResponse>,
				GenericWretchError<
					| getGameTeamSheetClubPlayersResponse400
					| getGameTeamSheetClubPlayersResponse403
					| getGameTeamSheetClubPlayersResponse404
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<GameTeamSheetPlayersResponse>,
				GenericWretchError<
					| getGameTeamSheetClubPlayersResponse400
					| getGameTeamSheetClubPlayersResponse403
					| getGameTeamSheetClubPlayersResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const putGameDescription = (wretch: Http) => {
	const mutation = async (params: {
		id: number;
		body: putGameDescriptionRequestBody;
	}) => {
		const args = z
			.object({ id: z.number().int(), body: z.any() })
			.parse(params);
		const { id, body } = args;

		try {
			const res = await wretch
				.url(`/game/${id}/description`)
				.json(body)
				.put()
				.res();
			switch (res.status) {
				case 204: {
					return {
						status: res.status,
						data: null,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = putGameDescriptionResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = putGameDescriptionResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = putGameDescriptionResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<null> | undefined,
					GenericWretchError<
						| putGameDescriptionResponse400
						| putGameDescriptionResponse403
						| putGameDescriptionResponse404
					>,
					{ id: number; body: putGameDescriptionRequestBody }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<null> | undefined,
				GenericWretchError<
					| putGameDescriptionResponse400
					| putGameDescriptionResponse403
					| putGameDescriptionResponse404
				>,
				{ id: number; body: putGameDescriptionRequestBody }
			>(mutation, options || {});
		},
	};
};

const postGameStatsExport = (wretch: Http) => {
	const mutation = async (params: {
		id: number;
		body: GameStatsExportRequest;
	}) => {
		const args = z
			.object({ id: z.number().int(), body: z.any() })
			.parse(params);
		const { id, body } = args;

		try {
			const res = await wretch
				.url(`/game/${id}/export/stats`)
				.json(body)
				.post()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = FileResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = postGameStatsExportResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = postGameStatsExportResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = postGameStatsExportResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<FileResponse> | undefined,
					GenericWretchError<
						| postGameStatsExportResponse400
						| postGameStatsExportResponse403
						| postGameStatsExportResponse404
					>,
					{ id: number; body: GameStatsExportRequest }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<FileResponse> | undefined,
				GenericWretchError<
					| postGameStatsExportResponse400
					| postGameStatsExportResponse403
					| postGameStatsExportResponse404
				>,
				{ id: number; body: GameStatsExportRequest }
			>(mutation, options || {});
		},
	};
};

const postGameEventDataExport = (wretch: Http) => {
	const mutation = async (params: {
		id: number;
		body: GameEventDataExportRequest;
	}) => {
		const args = z
			.object({ id: z.number().int(), body: z.any() })
			.parse(params);
		const { id, body } = args;

		try {
			const res = await wretch
				.url(`/game/${id}/export/events`)
				.json(body)
				.post()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = FileResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = postGameEventDataExportResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = postGameEventDataExportResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = postGameEventDataExportResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<FileResponse> | undefined,
					GenericWretchError<
						| postGameEventDataExportResponse400
						| postGameEventDataExportResponse403
						| postGameEventDataExportResponse404
					>,
					{ id: number; body: GameEventDataExportRequest }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<FileResponse> | undefined,
				GenericWretchError<
					| postGameEventDataExportResponse400
					| postGameEventDataExportResponse403
					| postGameEventDataExportResponse404
				>,
				{ id: number; body: GameEventDataExportRequest }
			>(mutation, options || {});
		},
	};
};

const getSharedGameClipIsProtected = (
	wretch: Http,
	queryClient: QueryClient,
) => {
	const queryFn = async (
		params: { shareSlug: string },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<GameClipProtectedResponse>> => {
		const args = z.object({ shareSlug: z.string() }).parse(params);
		const { shareSlug } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/game-clip/shared/protected/${shareSlug}`)
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = GameClipProtectedResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = getSharedGameClipIsProtectedResponse400.parse(json);
					break;
				}
				case 401: {
					err.json = getSharedGameClipIsProtectedResponse401.parse(json);
					break;
				}
				case 403: {
					err.json = getSharedGameClipIsProtectedResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { shareSlug: string },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<GameClipProtectedResponse>,
					GenericWretchError<
						| getSharedGameClipIsProtectedResponse400
						| getSharedGameClipIsProtectedResponse401
						| getSharedGameClipIsProtectedResponse403
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<GameClipProtectedResponse>,
			GenericWretchError<
				| getSharedGameClipIsProtectedResponse400
				| getSharedGameClipIsProtectedResponse401
				| getSharedGameClipIsProtectedResponse403
			>
		> => {
			const key = [
				"getSharedGameClipIsProtected",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<ApiResponse<GameClipProtectedResponse>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<GameClipProtectedResponse>,
				GenericWretchError<
					| getSharedGameClipIsProtectedResponse400
					| getSharedGameClipIsProtectedResponse401
					| getSharedGameClipIsProtectedResponse403
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<GameClipProtectedResponse>,
				GenericWretchError<
					| getSharedGameClipIsProtectedResponse400
					| getSharedGameClipIsProtectedResponse401
					| getSharedGameClipIsProtectedResponse403
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { shareSlug: string },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<GameClipProtectedResponse>,
					GenericWretchError<
						| getSharedGameClipIsProtectedResponse400
						| getSharedGameClipIsProtectedResponse401
						| getSharedGameClipIsProtectedResponse403
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<GameClipProtectedResponse>,
			GenericWretchError<
				| getSharedGameClipIsProtectedResponse400
				| getSharedGameClipIsProtectedResponse401
				| getSharedGameClipIsProtectedResponse403
			>
		> => {
			const key = [
				"getSharedGameClipIsProtected",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<ApiResponse<GameClipProtectedResponse>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<GameClipProtectedResponse>,
				GenericWretchError<
					| getSharedGameClipIsProtectedResponse400
					| getSharedGameClipIsProtectedResponse401
					| getSharedGameClipIsProtectedResponse403
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<GameClipProtectedResponse>,
				GenericWretchError<
					| getSharedGameClipIsProtectedResponse400
					| getSharedGameClipIsProtectedResponse401
					| getSharedGameClipIsProtectedResponse403
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getSharedGameClip = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: { shareSlug: string },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<GameClipResponse>> => {
		const args = z.object({ shareSlug: z.string() }).parse(params);
		const { shareSlug } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/game-clip/shared/${shareSlug}`)
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = GameClipResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = getSharedGameClipResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = getSharedGameClipResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { shareSlug: string },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<GameClipResponse>,
					GenericWretchError<
						getSharedGameClipResponse400 | getSharedGameClipResponse403
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<GameClipResponse>,
			GenericWretchError<
				getSharedGameClipResponse400 | getSharedGameClipResponse403
			>
		> => {
			const key = ["getSharedGameClip", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<GameClipResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<GameClipResponse>,
				GenericWretchError<
					getSharedGameClipResponse400 | getSharedGameClipResponse403
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<GameClipResponse>,
				GenericWretchError<
					getSharedGameClipResponse400 | getSharedGameClipResponse403
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { shareSlug: string },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<GameClipResponse>,
					GenericWretchError<
						getSharedGameClipResponse400 | getSharedGameClipResponse403
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<GameClipResponse>,
			GenericWretchError<
				getSharedGameClipResponse400 | getSharedGameClipResponse403
			>
		> => {
			const key = ["getSharedGameClip", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<GameClipResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<GameClipResponse>,
				GenericWretchError<
					getSharedGameClipResponse400 | getSharedGameClipResponse403
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<GameClipResponse>,
				GenericWretchError<
					getSharedGameClipResponse400 | getSharedGameClipResponse403
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const postGameClipSharedPasswordValidation = (wretch: Http) => {
	const mutation = async (params: {
		shareSlug: string;
		body: postGameClipSharedPasswordValidationRequestBody;
	}) => {
		const args = z
			.object({ shareSlug: z.string(), body: z.any() })
			.parse(params);
		const { shareSlug, body } = args;

		try {
			const res = await wretch
				.url(`/game-clip/shared/password-validation/${shareSlug}`)
				.json(body)
				.post()
				.res();
			switch (res.status) {
				case 204: {
					return {
						status: res.status,
						data: null,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json =
						postGameClipSharedPasswordValidationResponse400.parse(json);
					break;
				}
				case 403: {
					err.json =
						postGameClipSharedPasswordValidationResponse403.parse(json);
					break;
				}
				case 404: {
					err.json =
						postGameClipSharedPasswordValidationResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<null> | undefined,
					GenericWretchError<
						| postGameClipSharedPasswordValidationResponse400
						| postGameClipSharedPasswordValidationResponse403
						| postGameClipSharedPasswordValidationResponse404
					>,
					{
						shareSlug: string;
						body: postGameClipSharedPasswordValidationRequestBody;
					}
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<null> | undefined,
				GenericWretchError<
					| postGameClipSharedPasswordValidationResponse400
					| postGameClipSharedPasswordValidationResponse403
					| postGameClipSharedPasswordValidationResponse404
				>,
				{
					shareSlug: string;
					body: postGameClipSharedPasswordValidationRequestBody;
				}
			>(mutation, options || {});
		},
	};
};

const getGameEventFilters = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<getGameEventFiltersResponse200>> => {
		try {
			const res = await wretch
				.options({ signal })
				.url("/game-event-filter")
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = getGameEventFiltersResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = getGameEventFiltersResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = getGameEventFiltersResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			options?: Omit<
				UseQueryOptions<
					ApiResponse<getGameEventFiltersResponse200>,
					GenericWretchError<
						getGameEventFiltersResponse400 | getGameEventFiltersResponse403
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<getGameEventFiltersResponse200>,
			GenericWretchError<
				getGameEventFiltersResponse400 | getGameEventFiltersResponse403
			>
		> => {
			const key = ["getGameEventFilters", ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<getGameEventFiltersResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<getGameEventFiltersResponse200>,
				GenericWretchError<
					getGameEventFiltersResponse400 | getGameEventFiltersResponse403
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<getGameEventFiltersResponse200>,
				GenericWretchError<
					getGameEventFiltersResponse400 | getGameEventFiltersResponse403
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<getGameEventFiltersResponse200>,
					GenericWretchError<
						getGameEventFiltersResponse400 | getGameEventFiltersResponse403
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<getGameEventFiltersResponse200>,
			GenericWretchError<
				getGameEventFiltersResponse400 | getGameEventFiltersResponse403
			>
		> => {
			const key = ["getGameEventFilters", ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<getGameEventFiltersResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<getGameEventFiltersResponse200>,
				GenericWretchError<
					getGameEventFiltersResponse400 | getGameEventFiltersResponse403
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<getGameEventFiltersResponse200>,
				GenericWretchError<
					getGameEventFiltersResponse400 | getGameEventFiltersResponse403
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const postGameEventFilter = (wretch: Http) => {
	const mutation = async (params: { body: GameEventFilterPostRequest }) => {
		const args = z.object({ body: z.any() }).parse(params);
		const { body } = args;

		try {
			const res = await wretch
				.url("/game-event-filter")
				.json(body)
				.post()
				.res();
			switch (res.status) {
				case 204: {
					return {
						status: res.status,
						data: null,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = postGameEventFilterResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = postGameEventFilterResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = postGameEventFilterResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<null> | undefined,
					GenericWretchError<
						| postGameEventFilterResponse400
						| postGameEventFilterResponse403
						| postGameEventFilterResponse404
					>,
					{ body: GameEventFilterPostRequest }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<null> | undefined,
				GenericWretchError<
					| postGameEventFilterResponse400
					| postGameEventFilterResponse403
					| postGameEventFilterResponse404
				>,
				{ body: GameEventFilterPostRequest }
			>(mutation, options || {});
		},
	};
};

const deleteGameEventFilter = (wretch: Http) => {
	const mutation = async (params: { id: number }) => {
		const args = z.object({ id: z.number().int() }).parse(params);
		const { id } = args;

		try {
			const res = await wretch.url(`/game-event-filter/${id}`).delete().res();
			switch (res.status) {
				case 204: {
					return {
						status: res.status,
						data: null,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = deleteGameEventFilterResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = deleteGameEventFilterResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = deleteGameEventFilterResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<null> | undefined,
					GenericWretchError<
						| deleteGameEventFilterResponse400
						| deleteGameEventFilterResponse403
						| deleteGameEventFilterResponse404
					>,
					{ id: number }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<null> | undefined,
				GenericWretchError<
					| deleteGameEventFilterResponse400
					| deleteGameEventFilterResponse403
					| deleteGameEventFilterResponse404
				>,
				{ id: number }
			>(mutation, options || {});
		},
	};
};

const addCommentToGameEvent = (wretch: Http) => {
	const mutation = async (params: {
		id: number;
		body: GameEventCommentPostRequest;
	}) => {
		const args = z
			.object({ id: z.number().int(), body: z.any() })
			.parse(params);
		const { id, body } = args;

		try {
			const res = await wretch
				.url(`/game-event-comment/create/${id}`)
				.json(body)
				.post()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 201: {
					const data = addCommentToGameEventResponse201.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = StatusMessageResponse.parse(json);
					break;
				}
				case 401: {
					err.json = StatusMessageResponse.parse(json);
					break;
				}
				case 403: {
					err.json = StatusMessageResponse.parse(json);
					break;
				}
				case 404: {
					err.json = StatusMessageResponse.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<addCommentToGameEventResponse201> | undefined,
					GenericWretchError<
						| StatusMessageResponse
						| StatusMessageResponse
						| StatusMessageResponse
						| StatusMessageResponse
					>,
					{ id: number; body: GameEventCommentPostRequest }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<addCommentToGameEventResponse201> | undefined,
				GenericWretchError<
					| StatusMessageResponse
					| StatusMessageResponse
					| StatusMessageResponse
					| StatusMessageResponse
				>,
				{ id: number; body: GameEventCommentPostRequest }
			>(mutation, options || {});
		},
	};
};

const deleteGameEventComment = (wretch: Http) => {
	const mutation = async (params: { id: number }) => {
		const args = z.object({ id: z.number().int() }).parse(params);
		const { id } = args;

		try {
			const res = await wretch.url(`/game-event-comment/${id}`).delete().res();
			switch (res.status) {
				case 204: {
					return {
						status: res.status,
						data: null,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = StatusMessageResponse.parse(json);
					break;
				}
				case 401: {
					err.json = StatusMessageResponse.parse(json);
					break;
				}
				case 403: {
					err.json = StatusMessageResponse.parse(json);
					break;
				}
				case 404: {
					err.json = StatusMessageResponse.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<null> | undefined,
					GenericWretchError<
						| StatusMessageResponse
						| StatusMessageResponse
						| StatusMessageResponse
						| StatusMessageResponse
					>,
					{ id: number }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<null> | undefined,
				GenericWretchError<
					| StatusMessageResponse
					| StatusMessageResponse
					| StatusMessageResponse
					| StatusMessageResponse
				>,
				{ id: number }
			>(mutation, options || {});
		},
	};
};

const getUserView = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<UserViewResponse>> => {
		try {
			const res = await wretch
				.options({ signal })
				.url("/user/view")
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = UserViewResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			options?: Omit<
				UseQueryOptions<
					ApiResponse<UserViewResponse>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<UserViewResponse>,
			GenericWretchError<null>
		> => {
			const key = ["getUserView", ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<UserViewResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<UserViewResponse>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<UserViewResponse>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<UserViewResponse>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<UserViewResponse>,
			GenericWretchError<null>
		> => {
			const key = ["getUserView", ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<UserViewResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<UserViewResponse>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<UserViewResponse>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const updateSelectedView = (wretch: Http) => {
	const mutation = async (params: { body: UserViewRequest }) => {
		const args = z.object({ body: z.any() }).parse(params);
		const { body } = args;

		try {
			const res = await wretch.url("/user/view").json(body).patch().res();
			switch (res.status) {
				case 204: {
					return {
						status: res.status,
						data: null,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = updateSelectedViewResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = updateSelectedViewResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<null> | undefined,
					GenericWretchError<
						updateSelectedViewResponse400 | updateSelectedViewResponse403
					>,
					{ body: UserViewRequest }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<null> | undefined,
				GenericWretchError<
					updateSelectedViewResponse400 | updateSelectedViewResponse403
				>,
				{ body: UserViewRequest }
			>(mutation, options || {});
		},
	};
};

const getMfaDomainList = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: { product?: string },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<MfaDomainListResponse>> => {
		const args = z
			.object({ product: z.literal("Scout").optional() })
			.parse(params);
		const { product } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url("/mfa/domains")
				.query(qs.stringify({ product }))
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = MfaDomainListResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 403: {
					err.json = getMfaDomainListResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { product?: string },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<MfaDomainListResponse>,
					GenericWretchError<getMfaDomainListResponse403>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<MfaDomainListResponse>,
			GenericWretchError<getMfaDomainListResponse403>
		> => {
			const key = ["getMfaDomainList", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<MfaDomainListResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<MfaDomainListResponse>,
				GenericWretchError<getMfaDomainListResponse403>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<MfaDomainListResponse>,
				GenericWretchError<getMfaDomainListResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { product?: string },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<MfaDomainListResponse>,
					GenericWretchError<getMfaDomainListResponse403>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<MfaDomainListResponse>,
			GenericWretchError<getMfaDomainListResponse403>
		> => {
			const key = ["getMfaDomainList", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<MfaDomainListResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<MfaDomainListResponse>,
				GenericWretchError<getMfaDomainListResponse403>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<MfaDomainListResponse>,
				GenericWretchError<getMfaDomainListResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getMfaDomainRequiredCheck = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: { email: string; product?: string },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<getMfaDomainRequiredCheckResponse200>> => {
		const args = z
			.object({
				email: z
					.string()
					.describe("user email — domain is extracted on the server side"),
				product: z.string().describe("optional product filter").optional(),
			})
			.parse(params);
		const { email, product } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url("/mfa/domain/required")
				.query(qs.stringify({ email, product }))
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = getMfaDomainRequiredCheckResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 403: {
					err.json = getMfaDomainRequiredCheckResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { email: string; product?: string },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<getMfaDomainRequiredCheckResponse200>,
					GenericWretchError<getMfaDomainRequiredCheckResponse403>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<getMfaDomainRequiredCheckResponse200>,
			GenericWretchError<getMfaDomainRequiredCheckResponse403>
		> => {
			const key = [
				"getMfaDomainRequiredCheck",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<getMfaDomainRequiredCheckResponse200>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<getMfaDomainRequiredCheckResponse200>,
				GenericWretchError<getMfaDomainRequiredCheckResponse403>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<getMfaDomainRequiredCheckResponse200>,
				GenericWretchError<getMfaDomainRequiredCheckResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { email: string; product?: string },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<getMfaDomainRequiredCheckResponse200>,
					GenericWretchError<getMfaDomainRequiredCheckResponse403>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<getMfaDomainRequiredCheckResponse200>,
			GenericWretchError<getMfaDomainRequiredCheckResponse403>
		> => {
			const key = [
				"getMfaDomainRequiredCheck",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<getMfaDomainRequiredCheckResponse200>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<getMfaDomainRequiredCheckResponse200>,
				GenericWretchError<getMfaDomainRequiredCheckResponse403>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<getMfaDomainRequiredCheckResponse200>,
				GenericWretchError<getMfaDomainRequiredCheckResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const postReportedTaggingIssue = (wretch: Http) => {
	const mutation = async (params: {
		gameId: number;
		body: ReportedTaggingIssuePostRequest;
	}) => {
		const args = z
			.object({ gameId: z.number().int(), body: z.any() })
			.parse(params);
		const { gameId, body } = args;

		try {
			const res = await wretch
				.url(`/reported-tagging-issue/create/${gameId}`)
				.json(body)
				.post()
				.res();
			switch (res.status) {
				case 204: {
					return {
						status: res.status,
						data: null,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = postReportedTaggingIssueResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = postReportedTaggingIssueResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = postReportedTaggingIssueResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<null> | undefined,
					GenericWretchError<
						| postReportedTaggingIssueResponse400
						| postReportedTaggingIssueResponse403
						| postReportedTaggingIssueResponse404
					>,
					{ gameId: number; body: ReportedTaggingIssuePostRequest }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<null> | undefined,
				GenericWretchError<
					| postReportedTaggingIssueResponse400
					| postReportedTaggingIssueResponse403
					| postReportedTaggingIssueResponse404
				>,
				{ gameId: number; body: ReportedTaggingIssuePostRequest }
			>(mutation, options || {});
		},
	};
};

const getUserAllCountries = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: { gender?: string; scope?: string; teamGroups?: string },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<CountryData>> => {
		const args = z
			.object({
				gender: z.enum(["Male", "Female"]).nullable().default(null),
				scope: z.string().nullable().default(null),
				teamGroups: z.string().nullable().default(null),
			})
			.parse(params);
		const { gender, scope, teamGroups } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url("/user/countries/all")
				.query(qs.stringify({ gender, scope, teamGroups }))
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = CountryData.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { gender?: string; scope?: string; teamGroups?: string },
			options?: Omit<
				UseQueryOptions<ApiResponse<CountryData>, GenericWretchError<null>>,
				"queryFn"
			>,
		): UseQueryResult<ApiResponse<CountryData>, GenericWretchError<null>> => {
			const key = ["getUserAllCountries", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<CountryData>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<CountryData>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<CountryData>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { gender?: string; scope?: string; teamGroups?: string },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<CountryData>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<CountryData>,
			GenericWretchError<null>
		> => {
			const key = ["getUserAllCountries", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<CountryData>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<CountryData>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<CountryData>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getUserSelectedCountries = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<SelectedCountriesResponse>> => {
		try {
			const res = await wretch
				.options({ signal })
				.url("/user/countries/selected")
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = SelectedCountriesResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			options?: Omit<
				UseQueryOptions<
					ApiResponse<SelectedCountriesResponse>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<SelectedCountriesResponse>,
			GenericWretchError<null>
		> => {
			const key = ["getUserSelectedCountries", ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<SelectedCountriesResponse>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<SelectedCountriesResponse>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<SelectedCountriesResponse>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<SelectedCountriesResponse>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<SelectedCountriesResponse>,
			GenericWretchError<null>
		> => {
			const key = ["getUserSelectedCountries", ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<SelectedCountriesResponse>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<SelectedCountriesResponse>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<SelectedCountriesResponse>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const updateUserSelectedCountries = (wretch: Http) => {
	const mutation = async (params: { body: UserCountriesPatchRequest }) => {
		const args = z.object({ body: z.any() }).parse(params);
		const { body } = args;

		try {
			const res = await wretch
				.url("/user/countries/selected")
				.json(body)
				.patch()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = updateUserSelectedCountriesResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = updateUserSelectedCountriesResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = updateUserSelectedCountriesResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<updateUserSelectedCountriesResponse200> | undefined,
					GenericWretchError<
						| updateUserSelectedCountriesResponse400
						| updateUserSelectedCountriesResponse403
					>,
					{ body: UserCountriesPatchRequest }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<updateUserSelectedCountriesResponse200> | undefined,
				GenericWretchError<
					| updateUserSelectedCountriesResponse400
					| updateUserSelectedCountriesResponse403
				>,
				{ body: UserCountriesPatchRequest }
			>(mutation, options || {});
		},
	};
};

const getTeamGroupVisibleEntities = (
	wretch: Http,
	queryClient: QueryClient,
) => {
	const queryFn = async (
		params: { gender: string; teamGroups: string; countries: string },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<TeamGroupVisibleEntitiesResponse>> => {
		const args = z
			.object({
				gender: z.enum(["Male", "Female"]),
				teamGroups: z.string(),
				countries: z.string(),
			})
			.parse(params);
		const { gender, teamGroups, countries } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url("/user/team-groups/visible-entities")
				.query(qs.stringify({ gender, teamGroups, countries }))
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = TeamGroupVisibleEntitiesResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { gender: string; teamGroups: string; countries: string },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<TeamGroupVisibleEntitiesResponse>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<TeamGroupVisibleEntitiesResponse>,
			GenericWretchError<null>
		> => {
			const key = [
				"getTeamGroupVisibleEntities",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<TeamGroupVisibleEntitiesResponse>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<TeamGroupVisibleEntitiesResponse>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<TeamGroupVisibleEntitiesResponse>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { gender: string; teamGroups: string; countries: string },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<TeamGroupVisibleEntitiesResponse>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<TeamGroupVisibleEntitiesResponse>,
			GenericWretchError<null>
		> => {
			const key = [
				"getTeamGroupVisibleEntities",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<TeamGroupVisibleEntitiesResponse>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<TeamGroupVisibleEntitiesResponse>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<TeamGroupVisibleEntitiesResponse>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getPlayerProfileEventTypes = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: {
			id: number;
			season?: number;
			startDate?: string;
			endDate?: string;
			ageGroup?: string[];
			club?: number[];
			position?: string[];
		},
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<EventTypes>> => {
		const args = z
			.object({
				id: z.number().int(),
				season: z.number().int().nullable().optional(),
				startDate: z.string().nullable().optional(),
				endDate: z.string().nullable().optional(),
				ageGroup: z.array(z.string()).optional(),
				club: z.array(z.number()).optional(),
				position: z.array(z.string()).optional(),
			})
			.parse(params);
		const { id, season, startDate, endDate, ageGroup, club, position } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/player-profile/${id}/event-types`)
				.query(
					qs.stringify({
						season,
						startDate,
						endDate,
						ageGroup,
						club,
						position,
					}),
				)
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = EventTypes.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 401: {
					err.json = StatusMessageResponse.parse(json);
					break;
				}
				case 403: {
					err.json = getPlayerProfileEventTypesResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = getPlayerProfileEventTypesResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: {
				id: number;
				season?: number;
				startDate?: string;
				endDate?: string;
				ageGroup?: string[];
				club?: number[];
				position?: string[];
			},
			options?: Omit<
				UseQueryOptions<
					ApiResponse<EventTypes>,
					GenericWretchError<
						| StatusMessageResponse
						| getPlayerProfileEventTypesResponse403
						| getPlayerProfileEventTypesResponse404
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<EventTypes>,
			GenericWretchError<
				| StatusMessageResponse
				| getPlayerProfileEventTypesResponse403
				| getPlayerProfileEventTypesResponse404
			>
		> => {
			const key = [
				"getPlayerProfileEventTypes",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<ApiResponse<EventTypes>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<EventTypes>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileEventTypesResponse403
					| getPlayerProfileEventTypesResponse404
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<EventTypes>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileEventTypesResponse403
					| getPlayerProfileEventTypesResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: {
				id: number;
				season?: number;
				startDate?: string;
				endDate?: string;
				ageGroup?: string[];
				club?: number[];
				position?: string[];
			},
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<EventTypes>,
					GenericWretchError<
						| StatusMessageResponse
						| getPlayerProfileEventTypesResponse403
						| getPlayerProfileEventTypesResponse404
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<EventTypes>,
			GenericWretchError<
				| StatusMessageResponse
				| getPlayerProfileEventTypesResponse403
				| getPlayerProfileEventTypesResponse404
			>
		> => {
			const key = [
				"getPlayerProfileEventTypes",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<ApiResponse<EventTypes>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<EventTypes>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileEventTypesResponse403
					| getPlayerProfileEventTypesResponse404
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<EventTypes>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileEventTypesResponse403
					| getPlayerProfileEventTypesResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getPlayerProfileEventFilters = (
	wretch: Http,
	queryClient: QueryClient,
) => {
	const queryFn = async (
		params: { id: number },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<EventFilters>> => {
		const args = z.object({ id: z.number().int() }).parse(params);
		const { id } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/player-profile/${id}/event-filters`)
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = EventFilters.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 401: {
					err.json = StatusMessageResponse.parse(json);
					break;
				}
				case 403: {
					err.json = getPlayerProfileEventFiltersResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = getPlayerProfileEventFiltersResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { id: number },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<EventFilters>,
					GenericWretchError<
						| StatusMessageResponse
						| getPlayerProfileEventFiltersResponse403
						| getPlayerProfileEventFiltersResponse404
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<EventFilters>,
			GenericWretchError<
				| StatusMessageResponse
				| getPlayerProfileEventFiltersResponse403
				| getPlayerProfileEventFiltersResponse404
			>
		> => {
			const key = [
				"getPlayerProfileEventFilters",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<ApiResponse<EventFilters>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<EventFilters>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileEventFiltersResponse403
					| getPlayerProfileEventFiltersResponse404
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<EventFilters>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileEventFiltersResponse403
					| getPlayerProfileEventFiltersResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { id: number },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<EventFilters>,
					GenericWretchError<
						| StatusMessageResponse
						| getPlayerProfileEventFiltersResponse403
						| getPlayerProfileEventFiltersResponse404
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<EventFilters>,
			GenericWretchError<
				| StatusMessageResponse
				| getPlayerProfileEventFiltersResponse403
				| getPlayerProfileEventFiltersResponse404
			>
		> => {
			const key = [
				"getPlayerProfileEventFilters",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<ApiResponse<EventFilters>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<EventFilters>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileEventFiltersResponse403
					| getPlayerProfileEventFiltersResponse404
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<EventFilters>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileEventFiltersResponse403
					| getPlayerProfileEventFiltersResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getPlayerProfileEvents = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: {
			id: number;
			season?: number;
			startDate?: string;
			endDate?: string;
			ageGroup?: string[];
			club?: number[];
			position?: string[];
			outcome?: boolean;
			eventTypes?: string[];
			excludeEventIds?: number[];
			page?: number;
			limit?: number;
		},
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<Events>> => {
		const args = z
			.object({
				id: z.number().int(),
				season: z.number().int().nullable().optional(),
				startDate: z.string().nullable().optional(),
				endDate: z.string().nullable().optional(),
				ageGroup: z.array(z.string()).optional(),
				club: z.array(z.number()).optional(),
				position: z.array(z.string()).optional(),
				outcome: z.boolean().nullable().optional(),
				eventTypes: z.array(z.string()).nullable().optional(),
				excludeEventIds: z
					.array(z.number())
					.nullable()
					.describe("Event ids to leave out of the list and the total")
					.optional(),
				page: z.number().int().gte(1).default(1),
				limit: z.number().int().gte(1).lte(100).default(15),
			})
			.parse(params);
		const {
			id,
			season,
			startDate,
			endDate,
			ageGroup,
			club,
			position,
			outcome,
			eventTypes,
			excludeEventIds,
			page,
			limit,
		} = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/player-profile/${id}/events`)
				.query(
					qs.stringify({
						season,
						startDate,
						endDate,
						ageGroup,
						club,
						position,
						outcome,
						eventTypes,
						excludeEventIds,
						page,
						limit,
					}),
				)
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = Events.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 401: {
					err.json = StatusMessageResponse.parse(json);
					break;
				}
				case 403: {
					err.json = getPlayerProfileEventsResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = getPlayerProfileEventsResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: {
				id: number;
				season?: number;
				startDate?: string;
				endDate?: string;
				ageGroup?: string[];
				club?: number[];
				position?: string[];
				outcome?: boolean;
				eventTypes?: string[];
				excludeEventIds?: number[];
				page?: number;
				limit?: number;
			},
			options?: Omit<
				UseQueryOptions<
					ApiResponse<Events>,
					GenericWretchError<
						| StatusMessageResponse
						| getPlayerProfileEventsResponse403
						| getPlayerProfileEventsResponse404
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<Events>,
			GenericWretchError<
				| StatusMessageResponse
				| getPlayerProfileEventsResponse403
				| getPlayerProfileEventsResponse404
			>
		> => {
			const key = [
				"getPlayerProfileEvents",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<ApiResponse<Events>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<Events>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileEventsResponse403
					| getPlayerProfileEventsResponse404
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<Events>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileEventsResponse403
					| getPlayerProfileEventsResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: {
				id: number;
				season?: number;
				startDate?: string;
				endDate?: string;
				ageGroup?: string[];
				club?: number[];
				position?: string[];
				outcome?: boolean;
				eventTypes?: string[];
				excludeEventIds?: number[];
				page?: number;
				limit?: number;
			},
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<Events>,
					GenericWretchError<
						| StatusMessageResponse
						| getPlayerProfileEventsResponse403
						| getPlayerProfileEventsResponse404
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<Events>,
			GenericWretchError<
				| StatusMessageResponse
				| getPlayerProfileEventsResponse403
				| getPlayerProfileEventsResponse404
			>
		> => {
			const key = [
				"getPlayerProfileEvents",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<ApiResponse<Events>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<Events>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileEventsResponse403
					| getPlayerProfileEventsResponse404
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<Events>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileEventsResponse403
					| getPlayerProfileEventsResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getPlayerProfileKeyEvents = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: { id: number },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<KeyEvents>> => {
		const args = z.object({ id: z.number().int() }).parse(params);
		const { id } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/player-profile/${id}/key-events`)
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = KeyEvents.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 401: {
					err.json = StatusMessageResponse.parse(json);
					break;
				}
				case 403: {
					err.json = getPlayerProfileKeyEventsResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = getPlayerProfileKeyEventsResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { id: number },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<KeyEvents>,
					GenericWretchError<
						| StatusMessageResponse
						| getPlayerProfileKeyEventsResponse403
						| getPlayerProfileKeyEventsResponse404
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<KeyEvents>,
			GenericWretchError<
				| StatusMessageResponse
				| getPlayerProfileKeyEventsResponse403
				| getPlayerProfileKeyEventsResponse404
			>
		> => {
			const key = [
				"getPlayerProfileKeyEvents",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<ApiResponse<KeyEvents>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<KeyEvents>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileKeyEventsResponse403
					| getPlayerProfileKeyEventsResponse404
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<KeyEvents>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileKeyEventsResponse403
					| getPlayerProfileKeyEventsResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { id: number },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<KeyEvents>,
					GenericWretchError<
						| StatusMessageResponse
						| getPlayerProfileKeyEventsResponse403
						| getPlayerProfileKeyEventsResponse404
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<KeyEvents>,
			GenericWretchError<
				| StatusMessageResponse
				| getPlayerProfileKeyEventsResponse403
				| getPlayerProfileKeyEventsResponse404
			>
		> => {
			const key = [
				"getPlayerProfileKeyEvents",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<ApiResponse<KeyEvents>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<KeyEvents>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileKeyEventsResponse403
					| getPlayerProfileKeyEventsResponse404
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<KeyEvents>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileKeyEventsResponse403
					| getPlayerProfileKeyEventsResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const postPlayerProfileCrossGameClip = (wretch: Http) => {
	const mutation = async (params: {
		body: PlayerProfileCrossGameClipPostRequest;
	}) => {
		const args = z.object({ body: z.any() }).parse(params);
		const { body } = args;

		try {
			const res = await wretch
				.url("/player-profile/cross-game-clip")
				.json(body)
				.post()
				.res();
			switch (res.status) {
				case 204: {
					return {
						status: res.status,
						data: null,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = postPlayerProfileCrossGameClipResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = postPlayerProfileCrossGameClipResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = postPlayerProfileCrossGameClipResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<null> | undefined,
					GenericWretchError<
						| postPlayerProfileCrossGameClipResponse400
						| postPlayerProfileCrossGameClipResponse403
						| postPlayerProfileCrossGameClipResponse404
					>,
					{ body: PlayerProfileCrossGameClipPostRequest }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<null> | undefined,
				GenericWretchError<
					| postPlayerProfileCrossGameClipResponse400
					| postPlayerProfileCrossGameClipResponse403
					| postPlayerProfileCrossGameClipResponse404
				>,
				{ body: PlayerProfileCrossGameClipPostRequest }
			>(mutation, options || {});
		},
	};
};

const getPlayerProfileShadowTeamList = (
	wretch: Http,
	queryClient: QueryClient,
) => {
	const queryFn = async (
		params: { id: number; search?: string },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<ShadowTeamList>> => {
		const args = z
			.object({
				id: z.number().int(),
				search: z.string().nullable().default(null),
			})
			.parse(params);
		const { id, search } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/player-profile/${id}/shadow-team-list`)
				.query(qs.stringify({ search }))
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = ShadowTeamList.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 401: {
					err.json = StatusMessageResponse.parse(json);
					break;
				}
				case 403: {
					err.json = getPlayerProfileShadowTeamListResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = getPlayerProfileShadowTeamListResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { id: number; search?: string },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<ShadowTeamList>,
					GenericWretchError<
						| StatusMessageResponse
						| getPlayerProfileShadowTeamListResponse403
						| getPlayerProfileShadowTeamListResponse404
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<ShadowTeamList>,
			GenericWretchError<
				| StatusMessageResponse
				| getPlayerProfileShadowTeamListResponse403
				| getPlayerProfileShadowTeamListResponse404
			>
		> => {
			const key = [
				"getPlayerProfileShadowTeamList",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<ApiResponse<ShadowTeamList>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<ShadowTeamList>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileShadowTeamListResponse403
					| getPlayerProfileShadowTeamListResponse404
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<ShadowTeamList>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileShadowTeamListResponse403
					| getPlayerProfileShadowTeamListResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { id: number; search?: string },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<ShadowTeamList>,
					GenericWretchError<
						| StatusMessageResponse
						| getPlayerProfileShadowTeamListResponse403
						| getPlayerProfileShadowTeamListResponse404
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<ShadowTeamList>,
			GenericWretchError<
				| StatusMessageResponse
				| getPlayerProfileShadowTeamListResponse403
				| getPlayerProfileShadowTeamListResponse404
			>
		> => {
			const key = [
				"getPlayerProfileShadowTeamList",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<ApiResponse<ShadowTeamList>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<ShadowTeamList>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileShadowTeamListResponse403
					| getPlayerProfileShadowTeamListResponse404
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<ShadowTeamList>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileShadowTeamListResponse403
					| getPlayerProfileShadowTeamListResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const postPlayerProfileShadowTeamPlayer = (wretch: Http) => {
	const mutation = async (params: {
		id: number;
		body: ShadowTeamPlayerPostRequest;
	}) => {
		const args = z
			.object({ id: z.number().int(), body: z.any() })
			.parse(params);
		const { id, body } = args;

		try {
			const res = await wretch
				.url(`/player-profile/${id}/shadow-team-player`)
				.json(body)
				.post()
				.res();
			switch (res.status) {
				case 204: {
					return {
						status: res.status,
						data: null,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 401: {
					err.json = StatusMessageResponse.parse(json);
					break;
				}
				case 403: {
					err.json = postPlayerProfileShadowTeamPlayerResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = postPlayerProfileShadowTeamPlayerResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<null> | undefined,
					GenericWretchError<
						| StatusMessageResponse
						| postPlayerProfileShadowTeamPlayerResponse403
						| postPlayerProfileShadowTeamPlayerResponse404
					>,
					{ id: number; body: ShadowTeamPlayerPostRequest }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<null> | undefined,
				GenericWretchError<
					| StatusMessageResponse
					| postPlayerProfileShadowTeamPlayerResponse403
					| postPlayerProfileShadowTeamPlayerResponse404
				>,
				{ id: number; body: ShadowTeamPlayerPostRequest }
			>(mutation, options || {});
		},
	};
};

const postPlayerProfileShadowTeam = (wretch: Http) => {
	const mutation = async (params: { body: ShadowTeamPostRequest }) => {
		const args = z.object({ body: z.any() }).parse(params);
		const { body } = args;

		try {
			const res = await wretch
				.url("/player-profile/shadow-team")
				.json(body)
				.post()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 201: {
					const data = postPlayerProfileShadowTeamResponse201.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = postPlayerProfileShadowTeamResponse400.parse(json);
					break;
				}
				case 401: {
					err.json = StatusMessageResponse.parse(json);
					break;
				}
				case 403: {
					err.json = postPlayerProfileShadowTeamResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<postPlayerProfileShadowTeamResponse201> | undefined,
					GenericWretchError<
						| postPlayerProfileShadowTeamResponse400
						| StatusMessageResponse
						| postPlayerProfileShadowTeamResponse403
					>,
					{ body: ShadowTeamPostRequest }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<postPlayerProfileShadowTeamResponse201> | undefined,
				GenericWretchError<
					| postPlayerProfileShadowTeamResponse400
					| StatusMessageResponse
					| postPlayerProfileShadowTeamResponse403
				>,
				{ body: ShadowTeamPostRequest }
			>(mutation, options || {});
		},
	};
};

const getPlayerProfileRecruitmentInformation = (
	wretch: Http,
	queryClient: QueryClient,
) => {
	const queryFn = async (
		params: { id: number },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<PlayerProfileRecruitmentInformationResponse>> => {
		const args = z.object({ id: z.number().int() }).parse(params);
		const { id } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/player-profile/${id}/recruitment-information`)
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = PlayerProfileRecruitmentInformationResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 401: {
					err.json = StatusMessageResponse.parse(json);
					break;
				}
				case 403: {
					err.json =
						getPlayerProfileRecruitmentInformationResponse403.parse(json);
					break;
				}
				case 404: {
					err.json =
						getPlayerProfileRecruitmentInformationResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { id: number },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<PlayerProfileRecruitmentInformationResponse>,
					GenericWretchError<
						| StatusMessageResponse
						| getPlayerProfileRecruitmentInformationResponse403
						| getPlayerProfileRecruitmentInformationResponse404
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<PlayerProfileRecruitmentInformationResponse>,
			GenericWretchError<
				| StatusMessageResponse
				| getPlayerProfileRecruitmentInformationResponse403
				| getPlayerProfileRecruitmentInformationResponse404
			>
		> => {
			const key = [
				"getPlayerProfileRecruitmentInformation",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<PlayerProfileRecruitmentInformationResponse>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<PlayerProfileRecruitmentInformationResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileRecruitmentInformationResponse403
					| getPlayerProfileRecruitmentInformationResponse404
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<PlayerProfileRecruitmentInformationResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileRecruitmentInformationResponse403
					| getPlayerProfileRecruitmentInformationResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { id: number },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<PlayerProfileRecruitmentInformationResponse>,
					GenericWretchError<
						| StatusMessageResponse
						| getPlayerProfileRecruitmentInformationResponse403
						| getPlayerProfileRecruitmentInformationResponse404
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<PlayerProfileRecruitmentInformationResponse>,
			GenericWretchError<
				| StatusMessageResponse
				| getPlayerProfileRecruitmentInformationResponse403
				| getPlayerProfileRecruitmentInformationResponse404
			>
		> => {
			const key = [
				"getPlayerProfileRecruitmentInformation",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<PlayerProfileRecruitmentInformationResponse>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<PlayerProfileRecruitmentInformationResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileRecruitmentInformationResponse403
					| getPlayerProfileRecruitmentInformationResponse404
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<PlayerProfileRecruitmentInformationResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileRecruitmentInformationResponse403
					| getPlayerProfileRecruitmentInformationResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getPlayerProfileBiometricsAvailability = (
	wretch: Http,
	queryClient: QueryClient,
) => {
	const queryFn = async (
		params: { id: number },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<PlayerProfileBiometricsAvailabilityResponse>> => {
		const args = z.object({ id: z.number().int() }).parse(params);
		const { id } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/player-profile/${id}/biometrics-availability`)
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = PlayerProfileBiometricsAvailabilityResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 401: {
					err.json = StatusMessageResponse.parse(json);
					break;
				}
				case 403: {
					err.json =
						getPlayerProfileBiometricsAvailabilityResponse403.parse(json);
					break;
				}
				case 404: {
					err.json =
						getPlayerProfileBiometricsAvailabilityResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { id: number },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<PlayerProfileBiometricsAvailabilityResponse>,
					GenericWretchError<
						| StatusMessageResponse
						| getPlayerProfileBiometricsAvailabilityResponse403
						| getPlayerProfileBiometricsAvailabilityResponse404
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<PlayerProfileBiometricsAvailabilityResponse>,
			GenericWretchError<
				| StatusMessageResponse
				| getPlayerProfileBiometricsAvailabilityResponse403
				| getPlayerProfileBiometricsAvailabilityResponse404
			>
		> => {
			const key = [
				"getPlayerProfileBiometricsAvailability",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<PlayerProfileBiometricsAvailabilityResponse>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<PlayerProfileBiometricsAvailabilityResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileBiometricsAvailabilityResponse403
					| getPlayerProfileBiometricsAvailabilityResponse404
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<PlayerProfileBiometricsAvailabilityResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileBiometricsAvailabilityResponse403
					| getPlayerProfileBiometricsAvailabilityResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { id: number },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<PlayerProfileBiometricsAvailabilityResponse>,
					GenericWretchError<
						| StatusMessageResponse
						| getPlayerProfileBiometricsAvailabilityResponse403
						| getPlayerProfileBiometricsAvailabilityResponse404
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<PlayerProfileBiometricsAvailabilityResponse>,
			GenericWretchError<
				| StatusMessageResponse
				| getPlayerProfileBiometricsAvailabilityResponse403
				| getPlayerProfileBiometricsAvailabilityResponse404
			>
		> => {
			const key = [
				"getPlayerProfileBiometricsAvailability",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<PlayerProfileBiometricsAvailabilityResponse>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<PlayerProfileBiometricsAvailabilityResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileBiometricsAvailabilityResponse403
					| getPlayerProfileBiometricsAvailabilityResponse404
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<PlayerProfileBiometricsAvailabilityResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileBiometricsAvailabilityResponse403
					| getPlayerProfileBiometricsAvailabilityResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getPlayerProfileBodyMeasurements = (
	wretch: Http,
	queryClient: QueryClient,
) => {
	const queryFn = async (
		params: { id: number },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<PlayerProfileBodyMeasurementsResponse>> => {
		const args = z.object({ id: z.number().int() }).parse(params);
		const { id } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/player-profile/${id}/body-measurements`)
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = PlayerProfileBodyMeasurementsResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 401: {
					err.json = StatusMessageResponse.parse(json);
					break;
				}
				case 403: {
					err.json = getPlayerProfileBodyMeasurementsResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = getPlayerProfileBodyMeasurementsResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { id: number },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<PlayerProfileBodyMeasurementsResponse>,
					GenericWretchError<
						| StatusMessageResponse
						| getPlayerProfileBodyMeasurementsResponse403
						| getPlayerProfileBodyMeasurementsResponse404
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<PlayerProfileBodyMeasurementsResponse>,
			GenericWretchError<
				| StatusMessageResponse
				| getPlayerProfileBodyMeasurementsResponse403
				| getPlayerProfileBodyMeasurementsResponse404
			>
		> => {
			const key = [
				"getPlayerProfileBodyMeasurements",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<PlayerProfileBodyMeasurementsResponse>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<PlayerProfileBodyMeasurementsResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileBodyMeasurementsResponse403
					| getPlayerProfileBodyMeasurementsResponse404
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<PlayerProfileBodyMeasurementsResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileBodyMeasurementsResponse403
					| getPlayerProfileBodyMeasurementsResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { id: number },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<PlayerProfileBodyMeasurementsResponse>,
					GenericWretchError<
						| StatusMessageResponse
						| getPlayerProfileBodyMeasurementsResponse403
						| getPlayerProfileBodyMeasurementsResponse404
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<PlayerProfileBodyMeasurementsResponse>,
			GenericWretchError<
				| StatusMessageResponse
				| getPlayerProfileBodyMeasurementsResponse403
				| getPlayerProfileBodyMeasurementsResponse404
			>
		> => {
			const key = [
				"getPlayerProfileBodyMeasurements",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<PlayerProfileBodyMeasurementsResponse>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<PlayerProfileBodyMeasurementsResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileBodyMeasurementsResponse403
					| getPlayerProfileBodyMeasurementsResponse404
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<PlayerProfileBodyMeasurementsResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileBodyMeasurementsResponse403
					| getPlayerProfileBodyMeasurementsResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getPlayerProfileBodyMetricTrend = (
	wretch: Http,
	queryClient: QueryClient,
) => {
	const queryFn = async (
		params: { id: number; metric?: string; page?: number; limit?: number },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<PlayerProfileBodyMetricTrendResponse>> => {
		const args = z
			.object({
				id: z.number().int(),
				metric: z
					.enum(["weight", "height", "wingspan", "bmi", "bri"])
					.nullable()
					.optional(),
				page: z
					.number()
					.int()
					.gte(1)
					.describe(
						"Page 1 holds the newest scans, the last page the oldest ones.",
					)
					.default(1),
				limit: z.number().int().gte(1).describe("Scans per page.").default(10),
			})
			.parse(params);
		const { id, metric, page, limit } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/player-profile/${id}/body-metric-trend`)
				.query(qs.stringify({ metric, page, limit }))
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = PlayerProfileBodyMetricTrendResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 401: {
					err.json = StatusMessageResponse.parse(json);
					break;
				}
				case 403: {
					err.json = getPlayerProfileBodyMetricTrendResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = getPlayerProfileBodyMetricTrendResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { id: number; metric?: string; page?: number; limit?: number },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<PlayerProfileBodyMetricTrendResponse>,
					GenericWretchError<
						| StatusMessageResponse
						| getPlayerProfileBodyMetricTrendResponse403
						| getPlayerProfileBodyMetricTrendResponse404
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<PlayerProfileBodyMetricTrendResponse>,
			GenericWretchError<
				| StatusMessageResponse
				| getPlayerProfileBodyMetricTrendResponse403
				| getPlayerProfileBodyMetricTrendResponse404
			>
		> => {
			const key = [
				"getPlayerProfileBodyMetricTrend",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<PlayerProfileBodyMetricTrendResponse>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<PlayerProfileBodyMetricTrendResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileBodyMetricTrendResponse403
					| getPlayerProfileBodyMetricTrendResponse404
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<PlayerProfileBodyMetricTrendResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileBodyMetricTrendResponse403
					| getPlayerProfileBodyMetricTrendResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { id: number; metric?: string; page?: number; limit?: number },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<PlayerProfileBodyMetricTrendResponse>,
					GenericWretchError<
						| StatusMessageResponse
						| getPlayerProfileBodyMetricTrendResponse403
						| getPlayerProfileBodyMetricTrendResponse404
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<PlayerProfileBodyMetricTrendResponse>,
			GenericWretchError<
				| StatusMessageResponse
				| getPlayerProfileBodyMetricTrendResponse403
				| getPlayerProfileBodyMetricTrendResponse404
			>
		> => {
			const key = [
				"getPlayerProfileBodyMetricTrend",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<PlayerProfileBodyMetricTrendResponse>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<PlayerProfileBodyMetricTrendResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileBodyMetricTrendResponse403
					| getPlayerProfileBodyMetricTrendResponse404
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<PlayerProfileBodyMetricTrendResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileBodyMetricTrendResponse403
					| getPlayerProfileBodyMetricTrendResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getPlayerProfileBodyCompositionChange = (
	wretch: Http,
	queryClient: QueryClient,
) => {
	const queryFn = async (
		params: { id: number; firstScanId?: string; secondScanId?: string },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<PlayerProfileBodyCompositionChangeResponse>> => {
		const args = z
			.object({
				id: z.number().int(),
				firstScanId: z.string().nullable().optional(),
				secondScanId: z.string().nullable().optional(),
			})
			.parse(params);
		const { id, firstScanId, secondScanId } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/player-profile/${id}/body-composition-change`)
				.query(qs.stringify({ firstScanId, secondScanId }))
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = PlayerProfileBodyCompositionChangeResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 401: {
					err.json = StatusMessageResponse.parse(json);
					break;
				}
				case 403: {
					err.json =
						getPlayerProfileBodyCompositionChangeResponse403.parse(json);
					break;
				}
				case 404: {
					err.json =
						getPlayerProfileBodyCompositionChangeResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { id: number; firstScanId?: string; secondScanId?: string },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<PlayerProfileBodyCompositionChangeResponse>,
					GenericWretchError<
						| StatusMessageResponse
						| getPlayerProfileBodyCompositionChangeResponse403
						| getPlayerProfileBodyCompositionChangeResponse404
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<PlayerProfileBodyCompositionChangeResponse>,
			GenericWretchError<
				| StatusMessageResponse
				| getPlayerProfileBodyCompositionChangeResponse403
				| getPlayerProfileBodyCompositionChangeResponse404
			>
		> => {
			const key = [
				"getPlayerProfileBodyCompositionChange",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<PlayerProfileBodyCompositionChangeResponse>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<PlayerProfileBodyCompositionChangeResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileBodyCompositionChangeResponse403
					| getPlayerProfileBodyCompositionChangeResponse404
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<PlayerProfileBodyCompositionChangeResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileBodyCompositionChangeResponse403
					| getPlayerProfileBodyCompositionChangeResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { id: number; firstScanId?: string; secondScanId?: string },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<PlayerProfileBodyCompositionChangeResponse>,
					GenericWretchError<
						| StatusMessageResponse
						| getPlayerProfileBodyCompositionChangeResponse403
						| getPlayerProfileBodyCompositionChangeResponse404
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<PlayerProfileBodyCompositionChangeResponse>,
			GenericWretchError<
				| StatusMessageResponse
				| getPlayerProfileBodyCompositionChangeResponse403
				| getPlayerProfileBodyCompositionChangeResponse404
			>
		> => {
			const key = [
				"getPlayerProfileBodyCompositionChange",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<PlayerProfileBodyCompositionChangeResponse>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<PlayerProfileBodyCompositionChangeResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileBodyCompositionChangeResponse403
					| getPlayerProfileBodyCompositionChangeResponse404
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<PlayerProfileBodyCompositionChangeResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileBodyCompositionChangeResponse403
					| getPlayerProfileBodyCompositionChangeResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getPlayerProfilePhysicalMetricsFilters = (
	wretch: Http,
	queryClient: QueryClient,
) => {
	const queryFn = async (
		params: { id: number },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<PlayerProfilePhysicalMetricsFiltersResponse>> => {
		const args = z.object({ id: z.number().int() }).parse(params);
		const { id } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/player-profile/${id}/physical-metrics-filters`)
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = PlayerProfilePhysicalMetricsFiltersResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 401: {
					err.json = StatusMessageResponse.parse(json);
					break;
				}
				case 403: {
					err.json =
						getPlayerProfilePhysicalMetricsFiltersResponse403.parse(json);
					break;
				}
				case 404: {
					err.json =
						getPlayerProfilePhysicalMetricsFiltersResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { id: number },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<PlayerProfilePhysicalMetricsFiltersResponse>,
					GenericWretchError<
						| StatusMessageResponse
						| getPlayerProfilePhysicalMetricsFiltersResponse403
						| getPlayerProfilePhysicalMetricsFiltersResponse404
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<PlayerProfilePhysicalMetricsFiltersResponse>,
			GenericWretchError<
				| StatusMessageResponse
				| getPlayerProfilePhysicalMetricsFiltersResponse403
				| getPlayerProfilePhysicalMetricsFiltersResponse404
			>
		> => {
			const key = [
				"getPlayerProfilePhysicalMetricsFilters",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<PlayerProfilePhysicalMetricsFiltersResponse>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<PlayerProfilePhysicalMetricsFiltersResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfilePhysicalMetricsFiltersResponse403
					| getPlayerProfilePhysicalMetricsFiltersResponse404
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<PlayerProfilePhysicalMetricsFiltersResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfilePhysicalMetricsFiltersResponse403
					| getPlayerProfilePhysicalMetricsFiltersResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { id: number },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<PlayerProfilePhysicalMetricsFiltersResponse>,
					GenericWretchError<
						| StatusMessageResponse
						| getPlayerProfilePhysicalMetricsFiltersResponse403
						| getPlayerProfilePhysicalMetricsFiltersResponse404
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<PlayerProfilePhysicalMetricsFiltersResponse>,
			GenericWretchError<
				| StatusMessageResponse
				| getPlayerProfilePhysicalMetricsFiltersResponse403
				| getPlayerProfilePhysicalMetricsFiltersResponse404
			>
		> => {
			const key = [
				"getPlayerProfilePhysicalMetricsFilters",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<PlayerProfilePhysicalMetricsFiltersResponse>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<PlayerProfilePhysicalMetricsFiltersResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfilePhysicalMetricsFiltersResponse403
					| getPlayerProfilePhysicalMetricsFiltersResponse404
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<PlayerProfilePhysicalMetricsFiltersResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfilePhysicalMetricsFiltersResponse403
					| getPlayerProfilePhysicalMetricsFiltersResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getPlayerProfilePhysicalMetrics = (
	wretch: Http,
	queryClient: QueryClient,
) => {
	const queryFn = async (
		params: { id: number; season?: number },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<PlayerProfilePhysicalMetricsResponse>> => {
		const args = z
			.object({
				id: z.number().int(),
				season: z
					.number()
					.int()
					.nullable()
					.describe(
						"Season to aggregate. Omit to aggregate across all seasons.",
					)
					.optional(),
			})
			.parse(params);
		const { id, season } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/player-profile/${id}/physical-metrics`)
				.query(qs.stringify({ season }))
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = PlayerProfilePhysicalMetricsResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 401: {
					err.json = StatusMessageResponse.parse(json);
					break;
				}
				case 403: {
					err.json = getPlayerProfilePhysicalMetricsResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = getPlayerProfilePhysicalMetricsResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { id: number; season?: number },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<PlayerProfilePhysicalMetricsResponse>,
					GenericWretchError<
						| StatusMessageResponse
						| getPlayerProfilePhysicalMetricsResponse403
						| getPlayerProfilePhysicalMetricsResponse404
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<PlayerProfilePhysicalMetricsResponse>,
			GenericWretchError<
				| StatusMessageResponse
				| getPlayerProfilePhysicalMetricsResponse403
				| getPlayerProfilePhysicalMetricsResponse404
			>
		> => {
			const key = [
				"getPlayerProfilePhysicalMetrics",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<PlayerProfilePhysicalMetricsResponse>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<PlayerProfilePhysicalMetricsResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfilePhysicalMetricsResponse403
					| getPlayerProfilePhysicalMetricsResponse404
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<PlayerProfilePhysicalMetricsResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfilePhysicalMetricsResponse403
					| getPlayerProfilePhysicalMetricsResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { id: number; season?: number },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<PlayerProfilePhysicalMetricsResponse>,
					GenericWretchError<
						| StatusMessageResponse
						| getPlayerProfilePhysicalMetricsResponse403
						| getPlayerProfilePhysicalMetricsResponse404
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<PlayerProfilePhysicalMetricsResponse>,
			GenericWretchError<
				| StatusMessageResponse
				| getPlayerProfilePhysicalMetricsResponse403
				| getPlayerProfilePhysicalMetricsResponse404
			>
		> => {
			const key = [
				"getPlayerProfilePhysicalMetrics",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<PlayerProfilePhysicalMetricsResponse>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<PlayerProfilePhysicalMetricsResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfilePhysicalMetricsResponse403
					| getPlayerProfilePhysicalMetricsResponse404
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<PlayerProfilePhysicalMetricsResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfilePhysicalMetricsResponse403
					| getPlayerProfilePhysicalMetricsResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getPlayerProfileBehavioralSignalsFilters = (
	wretch: Http,
	queryClient: QueryClient,
) => {
	const queryFn = async (
		params: { id: number },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<PlayerProfileBehavioralSignalsFiltersResponse>> => {
		const args = z.object({ id: z.number().int() }).parse(params);
		const { id } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/player-profile/${id}/behavioral-signals-filters`)
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data =
						PlayerProfileBehavioralSignalsFiltersResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 401: {
					err.json = StatusMessageResponse.parse(json);
					break;
				}
				case 403: {
					err.json =
						getPlayerProfileBehavioralSignalsFiltersResponse403.parse(json);
					break;
				}
				case 404: {
					err.json =
						getPlayerProfileBehavioralSignalsFiltersResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { id: number },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<PlayerProfileBehavioralSignalsFiltersResponse>,
					GenericWretchError<
						| StatusMessageResponse
						| getPlayerProfileBehavioralSignalsFiltersResponse403
						| getPlayerProfileBehavioralSignalsFiltersResponse404
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<PlayerProfileBehavioralSignalsFiltersResponse>,
			GenericWretchError<
				| StatusMessageResponse
				| getPlayerProfileBehavioralSignalsFiltersResponse403
				| getPlayerProfileBehavioralSignalsFiltersResponse404
			>
		> => {
			const key = [
				"getPlayerProfileBehavioralSignalsFilters",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<PlayerProfileBehavioralSignalsFiltersResponse>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<PlayerProfileBehavioralSignalsFiltersResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileBehavioralSignalsFiltersResponse403
					| getPlayerProfileBehavioralSignalsFiltersResponse404
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<PlayerProfileBehavioralSignalsFiltersResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileBehavioralSignalsFiltersResponse403
					| getPlayerProfileBehavioralSignalsFiltersResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { id: number },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<PlayerProfileBehavioralSignalsFiltersResponse>,
					GenericWretchError<
						| StatusMessageResponse
						| getPlayerProfileBehavioralSignalsFiltersResponse403
						| getPlayerProfileBehavioralSignalsFiltersResponse404
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<PlayerProfileBehavioralSignalsFiltersResponse>,
			GenericWretchError<
				| StatusMessageResponse
				| getPlayerProfileBehavioralSignalsFiltersResponse403
				| getPlayerProfileBehavioralSignalsFiltersResponse404
			>
		> => {
			const key = [
				"getPlayerProfileBehavioralSignalsFilters",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<PlayerProfileBehavioralSignalsFiltersResponse>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<PlayerProfileBehavioralSignalsFiltersResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileBehavioralSignalsFiltersResponse403
					| getPlayerProfileBehavioralSignalsFiltersResponse404
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<PlayerProfileBehavioralSignalsFiltersResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileBehavioralSignalsFiltersResponse403
					| getPlayerProfileBehavioralSignalsFiltersResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getPlayerProfileBehavioralSignalsTrend = (
	wretch: Http,
	queryClient: QueryClient,
) => {
	const queryFn = async (
		params: {
			id: number;
			period?: string;
			season?: number;
			signalType?: string;
			cohort?: string;
			position?: string;
			ageCategory?: string;
			page?: number;
		},
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<PlayerProfileBehavioralSignalsTrendResponse>> => {
		const args = z
			.object({
				id: z.number().int(),
				period: z
					.enum(["last10Games", "season"])
					.describe("Selected period. Defaults to the last games period.")
					.optional(),
				season: z
					.number()
					.int()
					.nullable()
					.describe(
						"Season to display. Only used with the season period; omit to aggregate all seasons.",
					)
					.optional(),
				signalType: z
					.enum(["sensitivity", "tolerance", "recovery"])
					.nullable()
					.describe(
						"Single signal tab. Omit for the overview tab showing all three signals.",
					)
					.optional(),
				cohort: z
					.enum(["ageGroup", "ageGroupPosition", "position"])
					.nullable()
					.describe("Cohort")
					.optional(),
				position: z
					.string()
					.nullable()
					.describe("Restricts the trend to the games played in this position.")
					.optional(),
				ageCategory: z
					.string()
					.nullable()
					.describe(
						"Restricts the trend to the games played in this age category.",
					)
					.optional(),
				page: z.number().int().describe("page").default(1),
			})
			.parse(params);
		const {
			id,
			period,
			season,
			signalType,
			cohort,
			position,
			ageCategory,
			page,
		} = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/player-profile/${id}/behavioral-signals-trend`)
				.query(
					qs.stringify({
						period,
						season,
						signalType,
						cohort,
						position,
						ageCategory,
						page,
					}),
				)
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = PlayerProfileBehavioralSignalsTrendResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 401: {
					err.json = StatusMessageResponse.parse(json);
					break;
				}
				case 403: {
					err.json =
						getPlayerProfileBehavioralSignalsTrendResponse403.parse(json);
					break;
				}
				case 404: {
					err.json =
						getPlayerProfileBehavioralSignalsTrendResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: {
				id: number;
				period?: string;
				season?: number;
				signalType?: string;
				cohort?: string;
				position?: string;
				ageCategory?: string;
				page?: number;
			},
			options?: Omit<
				UseQueryOptions<
					ApiResponse<PlayerProfileBehavioralSignalsTrendResponse>,
					GenericWretchError<
						| StatusMessageResponse
						| getPlayerProfileBehavioralSignalsTrendResponse403
						| getPlayerProfileBehavioralSignalsTrendResponse404
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<PlayerProfileBehavioralSignalsTrendResponse>,
			GenericWretchError<
				| StatusMessageResponse
				| getPlayerProfileBehavioralSignalsTrendResponse403
				| getPlayerProfileBehavioralSignalsTrendResponse404
			>
		> => {
			const key = [
				"getPlayerProfileBehavioralSignalsTrend",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<PlayerProfileBehavioralSignalsTrendResponse>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<PlayerProfileBehavioralSignalsTrendResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileBehavioralSignalsTrendResponse403
					| getPlayerProfileBehavioralSignalsTrendResponse404
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<PlayerProfileBehavioralSignalsTrendResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileBehavioralSignalsTrendResponse403
					| getPlayerProfileBehavioralSignalsTrendResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: {
				id: number;
				period?: string;
				season?: number;
				signalType?: string;
				cohort?: string;
				position?: string;
				ageCategory?: string;
				page?: number;
			},
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<PlayerProfileBehavioralSignalsTrendResponse>,
					GenericWretchError<
						| StatusMessageResponse
						| getPlayerProfileBehavioralSignalsTrendResponse403
						| getPlayerProfileBehavioralSignalsTrendResponse404
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<PlayerProfileBehavioralSignalsTrendResponse>,
			GenericWretchError<
				| StatusMessageResponse
				| getPlayerProfileBehavioralSignalsTrendResponse403
				| getPlayerProfileBehavioralSignalsTrendResponse404
			>
		> => {
			const key = [
				"getPlayerProfileBehavioralSignalsTrend",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<PlayerProfileBehavioralSignalsTrendResponse>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<PlayerProfileBehavioralSignalsTrendResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileBehavioralSignalsTrendResponse403
					| getPlayerProfileBehavioralSignalsTrendResponse404
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<PlayerProfileBehavioralSignalsTrendResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileBehavioralSignalsTrendResponse403
					| getPlayerProfileBehavioralSignalsTrendResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getPlayerProfileBehavioralSignalsSnapshot = (
	wretch: Http,
	queryClient: QueryClient,
) => {
	const queryFn = async (
		params: { id: number; period?: string; seasons?: number[] },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<PlayerProfileBehavioralSignalsSnapshotResponse>> => {
		const args = z
			.object({
				id: z.number().int(),
				period: z
					.enum(["last10Games", "season"])
					.describe("Selected period. Defaults to the season period.")
					.optional(),
				seasons: z.array(z.number()).max(3).describe("Seasons").optional(),
			})
			.parse(params);
		const { id, period, seasons } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/player-profile/${id}/behavioral-signals-snapshot`)
				.query(qs.stringify({ period, seasons }))
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data =
						PlayerProfileBehavioralSignalsSnapshotResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 401: {
					err.json = StatusMessageResponse.parse(json);
					break;
				}
				case 403: {
					err.json =
						getPlayerProfileBehavioralSignalsSnapshotResponse403.parse(json);
					break;
				}
				case 404: {
					err.json =
						getPlayerProfileBehavioralSignalsSnapshotResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { id: number; period?: string; seasons?: number[] },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<PlayerProfileBehavioralSignalsSnapshotResponse>,
					GenericWretchError<
						| StatusMessageResponse
						| getPlayerProfileBehavioralSignalsSnapshotResponse403
						| getPlayerProfileBehavioralSignalsSnapshotResponse404
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<PlayerProfileBehavioralSignalsSnapshotResponse>,
			GenericWretchError<
				| StatusMessageResponse
				| getPlayerProfileBehavioralSignalsSnapshotResponse403
				| getPlayerProfileBehavioralSignalsSnapshotResponse404
			>
		> => {
			const key = [
				"getPlayerProfileBehavioralSignalsSnapshot",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<PlayerProfileBehavioralSignalsSnapshotResponse>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<PlayerProfileBehavioralSignalsSnapshotResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileBehavioralSignalsSnapshotResponse403
					| getPlayerProfileBehavioralSignalsSnapshotResponse404
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<PlayerProfileBehavioralSignalsSnapshotResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileBehavioralSignalsSnapshotResponse403
					| getPlayerProfileBehavioralSignalsSnapshotResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { id: number; period?: string; seasons?: number[] },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<PlayerProfileBehavioralSignalsSnapshotResponse>,
					GenericWretchError<
						| StatusMessageResponse
						| getPlayerProfileBehavioralSignalsSnapshotResponse403
						| getPlayerProfileBehavioralSignalsSnapshotResponse404
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<PlayerProfileBehavioralSignalsSnapshotResponse>,
			GenericWretchError<
				| StatusMessageResponse
				| getPlayerProfileBehavioralSignalsSnapshotResponse403
				| getPlayerProfileBehavioralSignalsSnapshotResponse404
			>
		> => {
			const key = [
				"getPlayerProfileBehavioralSignalsSnapshot",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<PlayerProfileBehavioralSignalsSnapshotResponse>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<PlayerProfileBehavioralSignalsSnapshotResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileBehavioralSignalsSnapshotResponse403
					| getPlayerProfileBehavioralSignalsSnapshotResponse404
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<PlayerProfileBehavioralSignalsSnapshotResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileBehavioralSignalsSnapshotResponse403
					| getPlayerProfileBehavioralSignalsSnapshotResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getPlayerProfileBehavioralArchetypes = (
	wretch: Http,
	queryClient: QueryClient,
) => {
	const queryFn = async (
		params: { id: number },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<PlayerProfileArchetypesResponse>> => {
		const args = z.object({ id: z.number().int() }).parse(params);
		const { id } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/player-profile/${id}/behavioral-archetypes`)
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = PlayerProfileArchetypesResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 401: {
					err.json = StatusMessageResponse.parse(json);
					break;
				}
				case 403: {
					err.json =
						getPlayerProfileBehavioralArchetypesResponse403.parse(json);
					break;
				}
				case 404: {
					err.json =
						getPlayerProfileBehavioralArchetypesResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { id: number },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<PlayerProfileArchetypesResponse>,
					GenericWretchError<
						| StatusMessageResponse
						| getPlayerProfileBehavioralArchetypesResponse403
						| getPlayerProfileBehavioralArchetypesResponse404
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<PlayerProfileArchetypesResponse>,
			GenericWretchError<
				| StatusMessageResponse
				| getPlayerProfileBehavioralArchetypesResponse403
				| getPlayerProfileBehavioralArchetypesResponse404
			>
		> => {
			const key = [
				"getPlayerProfileBehavioralArchetypes",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<ApiResponse<PlayerProfileArchetypesResponse>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<PlayerProfileArchetypesResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileBehavioralArchetypesResponse403
					| getPlayerProfileBehavioralArchetypesResponse404
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<PlayerProfileArchetypesResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileBehavioralArchetypesResponse403
					| getPlayerProfileBehavioralArchetypesResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { id: number },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<PlayerProfileArchetypesResponse>,
					GenericWretchError<
						| StatusMessageResponse
						| getPlayerProfileBehavioralArchetypesResponse403
						| getPlayerProfileBehavioralArchetypesResponse404
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<PlayerProfileArchetypesResponse>,
			GenericWretchError<
				| StatusMessageResponse
				| getPlayerProfileBehavioralArchetypesResponse403
				| getPlayerProfileBehavioralArchetypesResponse404
			>
		> => {
			const key = [
				"getPlayerProfileBehavioralArchetypes",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<ApiResponse<PlayerProfileArchetypesResponse>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<PlayerProfileArchetypesResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileBehavioralArchetypesResponse403
					| getPlayerProfileBehavioralArchetypesResponse404
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<PlayerProfileArchetypesResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileBehavioralArchetypesResponse403
					| getPlayerProfileBehavioralArchetypesResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

type Http = ReturnType<AdminApiClient["createHttp"]>;

export class AdminApiClient {
	// biome-ignore lint/suspicious/noExplicitAny: <explanation>
	private operations: Map<string, any>;

	public http: Http;
	public queryClient: QueryClient;
	private onHttpConfigure: (wretch: Http) => Http;

	/**
				get discover clubhouses
			*/
	declare getDiscoverClubHouses: ReturnType<typeof getDiscoverClubHouses>;

	declare getDiscoverClubHouse: ReturnType<typeof getDiscoverClubHouse>;

	/**
				patch discover club house
			*/
	declare patchDiscoverClubHouse: ReturnType<typeof patchDiscoverClubHouse>;

	/**
				Get list of discover countries as choices
			*/
	declare getDiscoverCountries: ReturnType<typeof getDiscoverCountries>;

	/**
				get discover games
			*/
	declare getDiscoverGames: ReturnType<typeof getDiscoverGames>;

	/**
				Get discover games using Dynamic DataGrid
			*/
	declare getDiscoverGamesDynamic: ReturnType<typeof getDiscoverGamesDynamic>;

	/**
				Discover Games manager/content-manager filter options
			*/
	declare getDiscoverGamesV2FilterOptions: ReturnType<
		typeof getDiscoverGamesV2FilterOptions
	>;

	/**
				Discover Games assignee dropdown options
			*/
	declare getDiscoverGamesV2AssigneeOptions: ReturnType<
		typeof getDiscoverGamesV2AssigneeOptions
	>;

	/**
				Discover Games club/content manager row dropdown options
			*/
	declare getDiscoverGamesV2ClubMetadataUserOptions: ReturnType<
		typeof getDiscoverGamesV2ClubMetadataUserOptions
	>;

	/**
				Discover Games game status filter options
			*/
	declare getDiscoverGamesV2GameStatusOptions: ReturnType<
		typeof getDiscoverGamesV2GameStatusOptions
	>;

	/**
				Discover Games game owner filter options (only owners linked to games)
			*/
	declare getDiscoverGamesV2OwnerFilterOptions: ReturnType<
		typeof getDiscoverGamesV2OwnerFilterOptions
	>;

	/**
				Program game modal fixtures (legacy match types + translated labels)
			*/
	declare getDiscoverGamesProgramGameFixtures: ReturnType<
		typeof getDiscoverGamesProgramGameFixtures
	>;

	/**
				Program game modal club select options (search, limit, enriched label)
			*/
	declare getDiscoverGamesProgramGameClubOptions: ReturnType<
		typeof getDiscoverGamesProgramGameClubOptions
	>;

	/**
				Program game modal national league options, scoped to the home club country
			*/
	declare getDiscoverGamesProgramGameNationalLeagues: ReturnType<
		typeof getDiscoverGamesProgramGameNationalLeagues
	>;

	/**
				Create a Discover program game (ClubAmateur home/away, legacy match type, optional links)
			*/
	declare postDiscoverProgramGame: ReturnType<typeof postDiscoverProgramGame>;

	/**
				Per-field eligibility for Discover Games bulk update.
			*/
	declare getDiscoverGamesBulkEligibility: ReturnType<
		typeof getDiscoverGamesBulkEligibility
	>;

	/**
				Bulk patch Discover Games metadata for up to 50 selected game ids in one request.
			*/
	declare postDiscoverGamesBulkPatch: ReturnType<
		typeof postDiscoverGamesBulkPatch
	>;

	declare getDiscoverGame: ReturnType<typeof getDiscoverGame>;

	/**
				patch discover game
			*/
	declare patchDiscoverGame: ReturnType<typeof patchDiscoverGame>;

	declare getDiscoverGameV2: ReturnType<typeof getDiscoverGameV2>;

	/**
				Editable basic game metadata options
			*/
	declare getDiscoverGameMetadataOptions: ReturnType<
		typeof getDiscoverGameMetadataOptions
	>;

	/**
				approve game analysis
			*/
	declare approveGameAnalysis: ReturnType<typeof approveGameAnalysis>;

	/**
				deny game analysis
			*/
	declare denyGameAnalysis: ReturnType<typeof denyGameAnalysis>;

	declare getDiscoverGameHistoryNotes: ReturnType<
		typeof getDiscoverGameHistoryNotes
	>;

	declare postDiscoverGameHistoryNote: ReturnType<
		typeof postDiscoverGameHistoryNote
	>;

	declare deleteDiscoverGameHistoryNote: ReturnType<
		typeof deleteDiscoverGameHistoryNote
	>;

	declare patchDiscoverGameHistoryNote: ReturnType<
		typeof patchDiscoverGameHistoryNote
	>;

	declare postDiscoverGameLink: ReturnType<typeof postDiscoverGameLink>;

	declare deleteDiscoverGameLink: ReturnType<typeof deleteDiscoverGameLink>;

	declare patchDiscoverGameLink: ReturnType<typeof patchDiscoverGameLink>;

	/**
				get discover players data
			*/
	declare getDiscoverPlayersData: ReturnType<typeof getDiscoverPlayersData>;

	declare getDiscoverPlayerData: ReturnType<typeof getDiscoverPlayerData>;

	/**
				patch discover player data
			*/
	declare patchDiscoverPlayerData: ReturnType<typeof patchDiscoverPlayerData>;

	/**
				Queues data search ETL batch jobs for all games linked to nested players of this player data record
			*/
	declare postDiscoverPlayerDataUpdateEtl: ReturnType<
		typeof postDiscoverPlayerDataUpdateEtl
	>;

	/**
				get discover players
			*/
	declare getDiscoverPlayers: ReturnType<typeof getDiscoverPlayers>;

	declare getDiscoverPlayer: ReturnType<typeof getDiscoverPlayer>;

	/**
				patch discover player
			*/
	declare patchDiscoverPlayer: ReturnType<typeof patchDiscoverPlayer>;

	/**
				get player users
			*/
	declare getDiscoverSignUps: ReturnType<typeof getDiscoverSignUps>;

	declare getDiscoverSignUp: ReturnType<typeof getDiscoverSignUp>;

	/**
				patch discover sign up
			*/
	declare patchDiscoverSignUp: ReturnType<typeof patchDiscoverSignUp>;

	/**
				Get all active discover subscription products
			*/
	declare adminDiscoverSubscriptionProductsGet: ReturnType<
		typeof adminDiscoverSubscriptionProductsGet
	>;

	/**
				Get discover subscriptions by team
			*/
	declare adminDiscoverSubscriptionsByTeamGet: ReturnType<
		typeof adminDiscoverSubscriptionsByTeamGet
	>;

	/**
				Get discover subscriptions by club
			*/
	declare adminDiscoverSubscriptionsByClubGet: ReturnType<
		typeof adminDiscoverSubscriptionsByClubGet
	>;

	/**
				Create a subscription for a team with the given product
			*/
	declare adminDiscoverSubscriptionByTeamPost: ReturnType<
		typeof adminDiscoverSubscriptionByTeamPost
	>;

	/**
				Set discover subscription to free
			*/
	declare adminDiscoverSubscriptionSetToFreePost: ReturnType<
		typeof adminDiscoverSubscriptionSetToFreePost
	>;

	/**
				Set discover subscription to paid
			*/
	declare adminDiscoverSubscriptionSetToPaidPost: ReturnType<
		typeof adminDiscoverSubscriptionSetToPaidPost
	>;

	/**
				Activate a stopped discover subscription
			*/
	declare adminDiscoverSubscriptionActivatePost: ReturnType<
		typeof adminDiscoverSubscriptionActivatePost
	>;

	/**
				Stop an active discover subscription
			*/
	declare adminDiscoverSubscriptionStopPost: ReturnType<
		typeof adminDiscoverSubscriptionStopPost
	>;

	/**
				Activate renewal for a subscription with paused renewal status
			*/
	declare adminDiscoverSubscriptionActivateRenewalPost: ReturnType<
		typeof adminDiscoverSubscriptionActivateRenewalPost
	>;

	/**
				Pause renewal for a subscription with active renewal status
			*/
	declare adminDiscoverSubscriptionPauseRenewalPost: ReturnType<
		typeof adminDiscoverSubscriptionPauseRenewalPost
	>;

	/**
				get discover teams
			*/
	declare getDiscoverTeams: ReturnType<typeof getDiscoverTeams>;

	declare getDiscoverTeam: ReturnType<typeof getDiscoverTeam>;

	/**
				patch discover team
			*/
	declare patchDiscoverTeam: ReturnType<typeof patchDiscoverTeam>;

	/**
				get discover user guardian approvals
			*/
	declare getDiscoverUserGuardianApprovals: ReturnType<
		typeof getDiscoverUserGuardianApprovals
	>;

	/**
				Post admin discover user notes
			*/
	declare postAdminDiscoverUserNotes: ReturnType<
		typeof postAdminDiscoverUserNotes
	>;

	/**
				delete user note
			*/
	declare deleteAdminDiscoverUserNote: ReturnType<
		typeof deleteAdminDiscoverUserNote
	>;

	/**
				patch user note
			*/
	declare patchAdminDiscoverUserNote: ReturnType<
		typeof patchAdminDiscoverUserNote
	>;

	/**
				post discover user permission
			*/
	declare postDiscoverUserPermission: ReturnType<
		typeof postDiscoverUserPermission
	>;

	/**
				delete discover user permission
			*/
	declare deleteDiscoverUserPermission: ReturnType<
		typeof deleteDiscoverUserPermission
	>;

	/**
				get player users
			*/
	declare getDiscoverUsers: ReturnType<typeof getDiscoverUsers>;

	declare getDiscoverUser: ReturnType<typeof getDiscoverUser>;

	/**
				patch discover user
			*/
	declare patchDiscoverUser: ReturnType<typeof patchDiscoverUser>;

	/**
				Generate a one-time sign-in token to impersonate a Discover user (super admin, helpdesk TL or helpdesk agent)
			*/
	declare postDiscoverUserImpersonate: ReturnType<
		typeof postDiscoverUserImpersonate
	>;

	/**
				reject game analysis
			*/
	declare rejectGameAnalysis: ReturnType<typeof rejectGameAnalysis>;

	/**
				create game analysis
			*/
	declare createGameAnalysis: ReturnType<typeof createGameAnalysis>;

	/**
				swap game home away mapping
			*/
	declare swapGameHomeAwayMapping: ReturnType<typeof swapGameHomeAwayMapping>;

	declare getGameHomeAwayMappingSwapEligibility: ReturnType<
		typeof getGameHomeAwayMappingSwapEligibility
	>;

	/**
				Get operations management summary by country using Dynamic DataGrid
			*/
	declare getOperationsManagementSummaryByCountry: ReturnType<
		typeof getOperationsManagementSummaryByCountry
	>;

	/**
				Get distinct values for a filter field in Operations Summary by Country
			*/
	declare getOperationsManagementSummaryByCountryFilterOptions: ReturnType<
		typeof getOperationsManagementSummaryByCountryFilterOptions
	>;

	declare get_admin_operations_management_summary_by_country_export: ReturnType<
		typeof get_admin_operations_management_summary_by_country_export
	>;

	/**
				Get operations management summary by club using Dynamic DataGrid
			*/
	declare getOperationsManagementSummaryByClub: ReturnType<
		typeof getOperationsManagementSummaryByClub
	>;

	/**
				Get distinct values for a filter field in Operations Summary by Club
			*/
	declare getOperationsManagementSummaryByClubFilterOptions: ReturnType<
		typeof getOperationsManagementSummaryByClubFilterOptions
	>;

	declare get_admin_operations_management_summary_by_club_export: ReturnType<
		typeof get_admin_operations_management_summary_by_club_export
	>;

	/**
				patch player membership
			*/
	declare patchPlayerMembership: ReturnType<typeof patchPlayerMembership>;

	/**
				Login user via json
			*/
	declare jsonLogin: ReturnType<typeof jsonLogin>;

	/**
				Get currently logged in user
			*/
	declare getCurrentUser: ReturnType<typeof getCurrentUser>;

	/**
				Logout user via json
			*/
	declare adminJsonLogout: ReturnType<typeof adminJsonLogout>;

	/**
				Update User Locale
			*/
	declare setUserLocale: ReturnType<typeof setUserLocale>;

	/**
				request for changing password
			*/
	declare forgotPassword: ReturnType<typeof forgotPassword>;

	/**
				reset a password
			*/
	declare resetPassword: ReturnType<typeof resetPassword>;

	/**
				check if the user token is valid or not
			*/
	declare validateUserPasswordReset: ReturnType<
		typeof validateUserPasswordReset
	>;

	/**
				Get the leagues based on category
			*/
	declare searchNationalLeagues: ReturnType<typeof searchNationalLeagues>;

	/**
				Get all permissions
			*/
	declare getPermissionTree: ReturnType<typeof getPermissionTree>;

	/**
				Get the permission by role
			*/
	declare getPermissionByRole: ReturnType<typeof getPermissionByRole>;

	/**
				Assign the permission to role
			*/
	declare assignPermissionToRole: ReturnType<typeof assignPermissionToRole>;

	/**
				Delete the role
			*/
	declare deleteRole: ReturnType<typeof deleteRole>;

	/**
				Get all roles
			*/
	declare getRoles: ReturnType<typeof getRoles>;

	/**
				Get user roles
			*/
	declare getUserRoles: ReturnType<typeof getUserRoles>;

	/**
				Add and update the role
			*/
	declare addUpdateRole: ReturnType<typeof addUpdateRole>;

	/**
				Get all admin users
			*/
	declare getAllAdminUsers: ReturnType<typeof getAllAdminUsers>;

	/**
				create and update admin users
			*/
	declare addUpdateAdminUsers: ReturnType<typeof addUpdateAdminUsers>;

	/**
				Delete admin
			*/
	declare deleteAdminUser: ReturnType<typeof deleteAdminUser>;

	/**
				Upload photo for Admin
			*/
	declare uploadProfilePhoto: ReturnType<typeof uploadProfilePhoto>;

	/**
				Get all poles
			*/
	declare getAllPoles: ReturnType<typeof getAllPoles>;

	/**
				create and update pole
			*/
	declare addUpdatePole: ReturnType<typeof addUpdatePole>;

	/**
				Get timezones
			*/
	declare getTimezones: ReturnType<typeof getTimezones>;

	/**
				delete a pole
			*/
	declare deletePole: ReturnType<typeof deletePole>;

	/**
				get selected poles by club
			*/
	declare getSelectedPoleByClub: ReturnType<typeof getSelectedPoleByClub>;

	/**
				assign pole to club
			*/
	declare assignPole: ReturnType<typeof assignPole>;

	/**
				unassign pole from club
			*/
	declare unAssignPole: ReturnType<typeof unAssignPole>;

	/**
				sync pole
			*/
	declare syncPole: ReturnType<typeof syncPole>;

	/**
				Get club list
			*/
	declare getClubList: ReturnType<typeof getClubList>;

	/**
				Get non partner countries
			*/
	declare getCountries: ReturnType<typeof getCountries>;

	/**
				Get continent country list
			*/
	declare getContinentCountry: ReturnType<typeof getContinentCountry>;

	/**
				Get non partner clubs
			*/
	declare getNPClubs: ReturnType<typeof getNPClubs>;

	/**
				create and update np club
			*/
	declare addUpdateNPClub: ReturnType<typeof addUpdateNPClub>;

	/**
				delete np club
			*/
	declare deleteNPClub: ReturnType<typeof deleteNPClub>;

	/**
				Upload photo for NP Club
			*/
	declare uploadNPClubPhoto: ReturnType<typeof uploadNPClubPhoto>;

	/**
				Upload photo for player
			*/
	declare uploadNPPlayerPicture: ReturnType<typeof uploadNPPlayerPicture>;

	/**
				Get non partner player
			*/
	declare getNPPlayers: ReturnType<typeof getNPPlayers>;

	/**
				Get nationality list
			*/
	declare getNationalityList: ReturnType<typeof getNationalityList>;

	/**
				Get position list
			*/
	declare getPositionList: ReturnType<typeof getPositionList>;

	/**
				Get geographical area list
			*/
	declare getGeographicalAreaList: ReturnType<typeof getGeographicalAreaList>;

	/**
				create and update np player
			*/
	declare addUpdateNPPlayer: ReturnType<typeof addUpdateNPPlayer>;

	/**
				delete np player
			*/
	declare deleteNPPlayer: ReturnType<typeof deleteNPPlayer>;

	/**
				Get non partner clubs
			*/
	declare getAllNPClubs: ReturnType<typeof getAllNPClubs>;

	/**
				Get players by non partner and partner club id
			*/
	declare getClubPlayers: ReturnType<typeof getClubPlayers>;

	/**
				Execute non partner club and player migration 
			*/
	declare executeNPClubMigration: ReturnType<typeof executeNPClubMigration>;

	/**
				get Teams by target club 
			*/
	declare getTeamsByTargetClub: ReturnType<typeof getTeamsByTargetClub>;

	/**
				Check Player Exists or not in Target club and perform migration
			*/
	declare playerExistsTargetClub: ReturnType<typeof playerExistsTargetClub>;

	/**
				get Teams by target club 
			*/
	declare getMigrationStatus: ReturnType<typeof getMigrationStatus>;

	/**
				Execute non partner club and player migration 
			*/
	declare executePartnerClubMigration: ReturnType<
		typeof executePartnerClubMigration
	>;

	/**
				Create Partner Player from Nonpartner player details when migrate
			*/
	declare createPartnerPlayerFromNonpartner: ReturnType<
		typeof createPartnerPlayerFromNonpartner
	>;

	/**
				Check NP Player Exists or not in Target club and perform migration
			*/
	declare partnerPlayerExistsTargetClub: ReturnType<
		typeof partnerPlayerExistsTargetClub
	>;

	/**
				get Teams by target club 
			*/
	declare getPartnerMigrationStatus: ReturnType<
		typeof getPartnerMigrationStatus
	>;

	/**
				get Teams by target club 
			*/
	declare updateGameTeam: ReturnType<typeof updateGameTeam>;

	/**
				Delete source club if no players are available when migrate
			*/
	declare migrateSourceClubNoPlayers: ReturnType<
		typeof migrateSourceClubNoPlayers
	>;

	/**
				Begin Migration
			*/
	declare beginMigration: ReturnType<typeof beginMigration>;

	/**
				Execute Club Migration With Zero Players
			*/
	declare clubMigrationWithZeroPlayers: ReturnType<
		typeof clubMigrationWithZeroPlayers
	>;

	/**
				Execute Complete Migration
			*/
	declare completeMigration: ReturnType<typeof completeMigration>;

	/**
				When Migrating to Partner Club a Method to Create / Update the Player
		is required. This method is called once user selects the Team for the source Player to complete
		the Migration
			*/
	declare playerMigrationToPartner: ReturnType<typeof playerMigrationToPartner>;

	declare playerMigrationConflict: ReturnType<typeof playerMigrationConflict>;

	/**
				Get all Countries Tooltip
			*/
	declare getAllCountriesTooltip: ReturnType<typeof getAllCountriesTooltip>;

	/**
				Add and Update country information
			*/
	declare addUpdateCountry: ReturnType<typeof addUpdateCountry>;

	/**
				Delete country information
			*/
	declare deleteCountry: ReturnType<typeof deleteCountry>;

	/**
				Get all countries
			*/
	declare allCountries: ReturnType<typeof allCountries>;

	/**
				Enabling or disabling the country
			*/
	declare enableDisableCountry: ReturnType<typeof enableDisableCountry>;

	/**
				Get all ownership
			*/
	declare loadOwnerships: ReturnType<typeof loadOwnerships>;

	/**
				Get all team groups
			*/
	declare loadTeamGroups: ReturnType<typeof loadTeamGroups>;

	/**
				Get subscription filter
			*/
	declare getSubscriptionFilterData: ReturnType<
		typeof getSubscriptionFilterData
	>;

	/**
				assign subscription to club
			*/
	declare assignSubscriptionToClub: ReturnType<typeof assignSubscriptionToClub>;

	/**
				Get All Languages
			*/
	declare getAllLanguages: ReturnType<typeof getAllLanguages>;

	/**
				search the user by name or email
			*/
	declare userSearch: ReturnType<typeof userSearch>;

	/**
				search the amateur club by name
			*/
	declare clubAmateurSearch: ReturnType<typeof clubAmateurSearch>;

	/**
				search the pro club by name
			*/
	declare clubProSearch: ReturnType<typeof clubProSearch>;

	/**
				Get All Eyeball Addons
			*/
	declare getAllEyeballAddons: ReturnType<typeof getAllEyeballAddons>;

	declare getUserDetail: ReturnType<typeof getUserDetail>;

	declare getUserSessions: ReturnType<typeof getUserSessions>;

	declare revokeSessions: ReturnType<typeof revokeSessions>;

	declare getUserSignIns: ReturnType<typeof getUserSignIns>;

	/**
				Log User Activity
			*/
	declare logUserActivity: ReturnType<typeof logUserActivity>;

	/**
				Get user widget settings
			*/
	declare getUserWidgetSettings: ReturnType<typeof getUserWidgetSettings>;

	/**
				Create or update user widget settings
			*/
	declare saveUserWidgetSettings: ReturnType<typeof saveUserWidgetSettings>;

	/**
				Get user club all teams
			*/
	declare getClubAllTeams: ReturnType<typeof getClubAllTeams>;

	/**
				Get team by id and coach id
			*/
	declare getTeamByIdAndCoachId: ReturnType<typeof getTeamByIdAndCoachId>;

	/**
				Add and Update the Team
			*/
	declare addUpdateTeam: ReturnType<typeof addUpdateTeam>;

	/**
				Delete the Team by id
			*/
	declare deleteTeam: ReturnType<typeof deleteTeam>;

	/**
				Get Coaches of the Club
			*/
	declare clubCoaches: ReturnType<typeof clubCoaches>;

	/**
				Get user club teams for dropdown
			*/
	declare getClubTeamsList: ReturnType<typeof getClubTeamsList>;

	/**
				Get Team Players
			*/
	declare teamPlayers: ReturnType<typeof teamPlayers>;

	/**
				Add and Update the Player
			*/
	declare addUpdatePlayer: ReturnType<typeof addUpdatePlayer>;

	/**
				update vip to the Player by id
			*/
	declare updateVip: ReturnType<typeof updateVip>;

	/**
				update level to the team by id
			*/
	declare updateLevel: ReturnType<typeof updateLevel>;

	/**
				Delete the Player by id
			*/
	declare deletePlayer: ReturnType<typeof deletePlayer>;

	/**
				Get old or non team players in the club
			*/
	declare formerPlayers: ReturnType<typeof formerPlayers>;

	/**
				Upload photo for Player
			*/
	declare uploadPlayerPhoto: ReturnType<typeof uploadPlayerPhoto>;

	/**
				Assign and unassign multiple coaches to the team
			*/
	declare assignCoachesToTeam: ReturnType<typeof assignCoachesToTeam>;

	/**
				Get nationality list
			*/
	declare getNationalities: ReturnType<typeof getNationalities>;

	/**
				Get category list
			*/
	declare getCategories: ReturnType<typeof getCategories>;

	/**
				Update Batch Players
			*/
	declare batchUpdate: ReturnType<typeof batchUpdate>;

	/**
				generate sign up link for specific player
			*/
	declare generateSignUpLink: ReturnType<typeof generateSignUpLink>;

	/**
				Retrieve the latest updated values of a player&#39;s height, weight and photo.
			*/
	declare getPlayerLatestLogEntries: ReturnType<
		typeof getPlayerLatestLogEntries
	>;

	/**
				Create a new player possible duplicate record
			*/
	declare adminCreatePlayerPossibleDuplicate: ReturnType<
		typeof adminCreatePlayerPossibleDuplicate
	>;

	/**
				Get all possible duplicates with pagination, filtering, and sorting
			*/
	declare getPlayerPossibleDuplicates: ReturnType<
		typeof getPlayerPossibleDuplicates
	>;

	/**
				Update player possible duplicate status and notes
			*/
	declare patchPlayerPossibleDuplicate: ReturnType<
		typeof patchPlayerPossibleDuplicate
	>;

	declare getVideoUploads: ReturnType<typeof getVideoUploads>;

	declare getVideoUploaders: ReturnType<typeof getVideoUploaders>;

	declare getVideo: ReturnType<typeof getVideo>;

	declare deleteVideoUpload: ReturnType<typeof deleteVideoUpload>;

	declare addVideoMetadata: ReturnType<typeof addVideoMetadata>;

	declare approveVideoMetadata: ReturnType<typeof approveVideoMetadata>;

	declare removeApproveVideoMetadata: ReturnType<
		typeof removeApproveVideoMetadata
	>;

	declare getGames: ReturnType<typeof getGames>;

	declare getClubs: ReturnType<typeof getClubs>;

	declare getVideoUploadsNotifications: ReturnType<
		typeof getVideoUploadsNotifications
	>;

	declare getTestVideos: ReturnType<typeof getTestVideos>;

	declare getVideoGameDetail: ReturnType<typeof getVideoGameDetail>;

	declare videoProcessingReset: ReturnType<typeof videoProcessingReset>;

	declare videoProcessingRetry: ReturnType<typeof videoProcessingRetry>;

	declare renameVideo: ReturnType<typeof renameVideo>;

	declare getVideoServerStatus: ReturnType<typeof getVideoServerStatus>;

	/**
				Migrate Players to the selected Team
			*/
	declare migratePlayers: ReturnType<typeof migratePlayers>;

	/**
				Get club detail
			*/
	declare getClubDetail: ReturnType<typeof getClubDetail>;

	/**
				post player mapping results
			*/
	declare postPlayerMappingResults: ReturnType<typeof postPlayerMappingResults>;

	/**
				patch player profile
			*/
	declare patchPlayerProfile: ReturnType<typeof patchPlayerProfile>;

	/**
				get player users
			*/
	declare getPlayerUsers: ReturnType<typeof getPlayerUsers>;

	/**
				patch player user
			*/
	declare patchPlayerUser: ReturnType<typeof patchPlayerUser>;

	/**
				Get subscriptions grid
			*/
	declare getSubscriptions: ReturnType<typeof getSubscriptions>;

	/**
				Create subscription
			*/
	declare postSubscription: ReturnType<typeof postSubscription>;

	/**
				Get subscription detail
			*/
	declare getSubscription: ReturnType<typeof getSubscription>;

	/**
				Update subscription
			*/
	declare putSubscription: ReturnType<typeof putSubscription>;

	/**
				Delete subscription
			*/
	declare deleteSubscriptionV2: ReturnType<typeof deleteSubscriptionV2>;

	/**
				Get subscription products and their form field configuration
			*/
	declare getSubscriptionProductsConfig: ReturnType<
		typeof getSubscriptionProductsConfig
	>;

	/**
				Get discover team countries available for subscription configuration
			*/
	declare getSubscriptionDiscoverCountries: ReturnType<
		typeof getSubscriptionDiscoverCountries
	>;

	/**
				Get team groups grid
			*/
	declare getTeamGroups: ReturnType<typeof getTeamGroups>;

	/**
				Create team group
			*/
	declare postTeamGroup: ReturnType<typeof postTeamGroup>;

	/**
				Get discover team and club options for team group form
			*/
	declare getTeamGroupDiscoverTeamOptions: ReturnType<
		typeof getTeamGroupDiscoverTeamOptions
	>;

	/**
				Get team group detail
			*/
	declare getTeamGroup: ReturnType<typeof getTeamGroup>;

	/**
				Update team group
			*/
	declare putTeamGroup: ReturnType<typeof putTeamGroup>;

	/**
				Delete team group
			*/
	declare deleteTeamGroup: ReturnType<typeof deleteTeamGroup>;

	/**
				get sign up requests
			*/
	declare getSignUpRequests: ReturnType<typeof getSignUpRequests>;

	/**
				patch sign up request
			*/
	declare patchSignUpRequest: ReturnType<typeof patchSignUpRequest>;

	/**
				endpoint to gain all signup request data by eb_sign_up_request.id (uuid) or ub_user.id (discriminated as a player)
			*/
	declare getSignUpRequestData: ReturnType<typeof getSignUpRequestData>;

	/**
				get token movements
			*/
	declare getTokenMovements: ReturnType<typeof getTokenMovements>;

	/**
				post token movement
			*/
	declare postTokenMovement: ReturnType<typeof postTokenMovement>;

	/**
				get options clubs of sign up requests
			*/
	declare getOptionsClubsOfSignUpRequests: ReturnType<
		typeof getOptionsClubsOfSignUpRequests
	>;

	/**
				get options clubs of player users
			*/
	declare getOptionsClubsOfPlayerUsers: ReturnType<
		typeof getOptionsClubsOfPlayerUsers
	>;

	/**
				endpoint to gain game team sheet export by type
			*/
	declare postGameTeamSheetExport: ReturnType<typeof postGameTeamSheetExport>;

	declare putUpdateGameLeftTeam: ReturnType<typeof putUpdateGameLeftTeam>;

	declare putGameAnalysisApproval: ReturnType<typeof putGameAnalysisApproval>;

	declare putGameResync: ReturnType<typeof putGameResync>;

	/**
				share the clip link
			*/
	declare shareClipLink: ReturnType<typeof shareClipLink>;

	/**
				Get team token balance
			*/
	declare getTeamTokenBalance: ReturnType<typeof getTeamTokenBalance>;

	/**
				Get club token balance
			*/
	declare getClubTokenBalance: ReturnType<typeof getClubTokenBalance>;

	/**
				Get all video tasks
			*/
	declare getTasks: ReturnType<typeof getTasks>;

	/**
				Get list of amateur clubs
			*/
	declare getAmatuerClubs: ReturnType<typeof getAmatuerClubs>;

	/**
				Get teamsheet for a task
			*/
	declare getTeamsheet: ReturnType<typeof getTeamsheet>;

	/**
				Update player status
			*/
	declare updatePlayerStatus: ReturnType<typeof updatePlayerStatus>;

	/**
				Get all teams for dropdown
			*/
	declare getTeamsForDropdown: ReturnType<typeof getTeamsForDropdown>;

	/**
				Get all leagues for dropdown
			*/
	declare getLeaguesForDropdown: ReturnType<typeof getLeaguesForDropdown>;

	/**
				Get all countries for dropdown
			*/
	declare getCountriesForDropdown: ReturnType<typeof getCountriesForDropdown>;

	/**
				Create a player
			*/
	declare createPlayer: ReturnType<typeof createPlayer>;

	/**
				Search for players with a limit of 5 results for dropdown
			*/
	declare playerSearchDropdown: ReturnType<typeof playerSearchDropdown>;

	/**
				Retrieves physical game analysis data, including report, player data, event, and video URLs, for a specific game identified by its ID.
			*/
	declare adminGetGameAnalysisPhysical: ReturnType<
		typeof adminGetGameAnalysisPhysical
	>;

	/**
				Updates the video URLs for a physical game analysis identified by the game ID.
			*/
	declare postGameAnalysisPhysicalVideoUrl: ReturnType<
		typeof postGameAnalysisPhysicalVideoUrl
	>;

	declare getGameEventsDebug: ReturnType<typeof getGameEventsDebug>;

	declare getGameStatsDebug: ReturnType<typeof getGameStatsDebug>;

	declare getGamePage: ReturnType<typeof getGamePage>;

	declare getGamePageLazy: ReturnType<typeof getGamePageLazy>;

	/**
				put game score
			*/
	declare putGameScore: ReturnType<typeof putGameScore>;

	/**
				endpoint to gain game teams stat data for game page
			*/
	declare getGameTeamStat: ReturnType<typeof getGameTeamStat>;

	/**
				endpoint to gain game players stat data for game page
			*/
	declare getGamePlayerStat: ReturnType<typeof getGamePlayerStat>;

	/**
				put game team colors
			*/
	declare putGameTeamColors: ReturnType<typeof putGameTeamColors>;

	/**
				sets rating to game player
			*/
	declare setRatingToGamePlayer: ReturnType<typeof setRatingToGamePlayer>;

	/**
				sets badges to game player
			*/
	declare setBadgesToGamePlayer: ReturnType<typeof setBadgesToGamePlayer>;

	/**
				post highlight
			*/
	declare postGameHighlight: ReturnType<typeof postGameHighlight>;

	/**
				delete highlight
			*/
	declare deleteGameHighlight: ReturnType<typeof deleteGameHighlight>;

	/**
				patch highlight
			*/
	declare patchGameHighlight: ReturnType<typeof patchGameHighlight>;

	/**
				get game clips, limited by current user ownership
			*/
	declare getGameClips: ReturnType<typeof getGameClips>;

	/**
				get game clip
			*/
	declare getGameClip: ReturnType<typeof getGameClip>;

	/**
				put game clip
			*/
	declare putGameClip: ReturnType<typeof putGameClip>;

	/**
				delete game clip
			*/
	declare deleteGameClip: ReturnType<typeof deleteGameClip>;

	/**
				post game clip
			*/
	declare postGameClip: ReturnType<typeof postGameClip>;

	/**
				Edit game team sheet
			*/
	declare putGameTeamSheet: ReturnType<typeof putGameTeamSheet>;

	/**
				endpoint to gain game team sheet load by composition
			*/
	declare getGameTeamSheetCompositionPlayers: ReturnType<
		typeof getGameTeamSheetCompositionPlayers
	>;

	/**
				endpoint to gain game team sheet players based on associated club
			*/
	declare getGameTeamPlayersSearch: ReturnType<typeof getGameTeamPlayersSearch>;

	/**
				endpoint to gain game team sheet club players
			*/
	declare getGameTeamSheetClubPlayers: ReturnType<
		typeof getGameTeamSheetClubPlayers
	>;

	/**
				put game description
			*/
	declare putGameDescription: ReturnType<typeof putGameDescription>;

	/**
				post game stats export
			*/
	declare postGameStatsExport: ReturnType<typeof postGameStatsExport>;

	/**
				post game event data export
			*/
	declare postGameEventDataExport: ReturnType<typeof postGameEventDataExport>;

	/**
				get game clip is protected
			*/
	declare getSharedGameClipIsProtected: ReturnType<
		typeof getSharedGameClipIsProtected
	>;

	/**
				get game clip
			*/
	declare getSharedGameClip: ReturnType<typeof getSharedGameClip>;

	/**
				post game clip shared password validation
			*/
	declare postGameClipSharedPasswordValidation: ReturnType<
		typeof postGameClipSharedPasswordValidation
	>;

	/**
				get game event filters, limited by current user ownership
			*/
	declare getGameEventFilters: ReturnType<typeof getGameEventFilters>;

	/**
				post game event filter
			*/
	declare postGameEventFilter: ReturnType<typeof postGameEventFilter>;

	/**
				delete game event filter
			*/
	declare deleteGameEventFilter: ReturnType<typeof deleteGameEventFilter>;

	declare addCommentToGameEvent: ReturnType<typeof addCommentToGameEvent>;

	declare deleteGameEventComment: ReturnType<typeof deleteGameEventComment>;

	/**
				Get all available view options including countries per data source and gender
			*/
	declare getUserView: ReturnType<typeof getUserView>;

	declare updateSelectedView: ReturnType<typeof updateSelectedView>;

	/**
				get mfa domain list
			*/
	declare getMfaDomainList: ReturnType<typeof getMfaDomainList>;

	/**
				Check whether a domain requires MFA.
			*/
	declare getMfaDomainRequiredCheck: ReturnType<
		typeof getMfaDomainRequiredCheck
	>;

	/**
				Report a tagging issue for a game event
			*/
	declare postReportedTaggingIssue: ReturnType<typeof postReportedTaggingIssue>;

	/**
				Get all user countries
			*/
	declare getUserAllCountries: ReturnType<typeof getUserAllCountries>;

	/**
				Get user&#39;s selected countries
			*/
	declare getUserSelectedCountries: ReturnType<typeof getUserSelectedCountries>;

	declare updateUserSelectedCountries: ReturnType<
		typeof updateUserSelectedCountries
	>;

	/**
				Get team-group teams and clubs visible in search for the country-selection modal preview
			*/
	declare getTeamGroupVisibleEntities: ReturnType<
		typeof getTeamGroupVisibleEntities
	>;

	/**
				Get player profile event types list
			*/
	declare getPlayerProfileEventTypes: ReturnType<
		typeof getPlayerProfileEventTypes
	>;

	/**
				Get player profile event filters list
			*/
	declare getPlayerProfileEventFilters: ReturnType<
		typeof getPlayerProfileEventFilters
	>;

	/**
				Get player profile events list
			*/
	declare getPlayerProfileEvents: ReturnType<typeof getPlayerProfileEvents>;

	/**
				Get player profile key events list
			*/
	declare getPlayerProfileKeyEvents: ReturnType<
		typeof getPlayerProfileKeyEvents
	>;

	/**
				Create a video clip composed of intervals from multiple games
			*/
	declare postPlayerProfileCrossGameClip: ReturnType<
		typeof postPlayerProfileCrossGameClip
	>;

	/**
				Get player profile shadow team list
			*/
	declare getPlayerProfileShadowTeamList: ReturnType<
		typeof getPlayerProfileShadowTeamList
	>;

	/**
				Add all players belonging to a player data into one or more shadow teams
			*/
	declare postPlayerProfileShadowTeamPlayer: ReturnType<
		typeof postPlayerProfileShadowTeamPlayer
	>;

	/**
				Create a new shadow team
			*/
	declare postPlayerProfileShadowTeam: ReturnType<
		typeof postPlayerProfileShadowTeam
	>;

	/**
				Get the Recruitment Information section of the player profile. Only populated answers are returned, and the academic ones only once the player has made them public.
			*/
	declare getPlayerProfileRecruitmentInformation: ReturnType<
		typeof getPlayerProfileRecruitmentInformation
	>;

	/**
				Tells the client whether the player profile biometrics sub-tab may be rendered
			*/
	declare getPlayerProfileBiometricsAvailability: ReturnType<
		typeof getPlayerProfileBiometricsAvailability
	>;

	/**
				Get player profile body measurements
			*/
	declare getPlayerProfileBodyMeasurements: ReturnType<
		typeof getPlayerProfileBodyMeasurements
	>;

	/**
				Get player profile body metric trend
			*/
	declare getPlayerProfileBodyMetricTrend: ReturnType<
		typeof getPlayerProfileBodyMetricTrend
	>;

	/**
				Get player profile body composition change
			*/
	declare getPlayerProfileBodyCompositionChange: ReturnType<
		typeof getPlayerProfileBodyCompositionChange
	>;

	/**
				Get player profile physical metrics filters list
			*/
	declare getPlayerProfilePhysicalMetricsFilters: ReturnType<
		typeof getPlayerProfilePhysicalMetricsFilters
	>;

	/**
				Get player profile physical metrics
			*/
	declare getPlayerProfilePhysicalMetrics: ReturnType<
		typeof getPlayerProfilePhysicalMetrics
	>;

	/**
				Get player profile behavioral signals filters list
			*/
	declare getPlayerProfileBehavioralSignalsFilters: ReturnType<
		typeof getPlayerProfileBehavioralSignalsFilters
	>;

	/**
				Get player profile behavioral signals trend chart
			*/
	declare getPlayerProfileBehavioralSignalsTrend: ReturnType<
		typeof getPlayerProfileBehavioralSignalsTrend
	>;

	/**
				Get player profile behavioral signals radar snapshot
			*/
	declare getPlayerProfileBehavioralSignalsSnapshot: ReturnType<
		typeof getPlayerProfileBehavioralSignalsSnapshot
	>;

	/**
				Get player profile behavioral archetype profile
			*/
	declare getPlayerProfileBehavioralArchetypes: ReturnType<
		typeof getPlayerProfileBehavioralArchetypes
	>;

	constructor(
		baseUrl: string,
		queryClient: QueryClient,
		options: Pick<RequestInit, "headers" | "credentials" | "mode"> = {},
		onHttpConfigure: AdminApiClient["onHttpConfigure"] = (wretch) => wretch,
	) {
		this.queryClient = queryClient;
		this.onHttpConfigure = onHttpConfigure;
		this.operations = new Map();

		this.operations.set("getDiscoverClubHouses", getDiscoverClubHouses);

		this.operations.set("getDiscoverClubHouse", getDiscoverClubHouse);

		this.operations.set("patchDiscoverClubHouse", patchDiscoverClubHouse);

		this.operations.set("getDiscoverCountries", getDiscoverCountries);

		this.operations.set("getDiscoverGames", getDiscoverGames);

		this.operations.set("getDiscoverGamesDynamic", getDiscoverGamesDynamic);

		this.operations.set(
			"getDiscoverGamesV2FilterOptions",
			getDiscoverGamesV2FilterOptions,
		);

		this.operations.set(
			"getDiscoverGamesV2AssigneeOptions",
			getDiscoverGamesV2AssigneeOptions,
		);

		this.operations.set(
			"getDiscoverGamesV2ClubMetadataUserOptions",
			getDiscoverGamesV2ClubMetadataUserOptions,
		);

		this.operations.set(
			"getDiscoverGamesV2GameStatusOptions",
			getDiscoverGamesV2GameStatusOptions,
		);

		this.operations.set(
			"getDiscoverGamesV2OwnerFilterOptions",
			getDiscoverGamesV2OwnerFilterOptions,
		);

		this.operations.set(
			"getDiscoverGamesProgramGameFixtures",
			getDiscoverGamesProgramGameFixtures,
		);

		this.operations.set(
			"getDiscoverGamesProgramGameClubOptions",
			getDiscoverGamesProgramGameClubOptions,
		);

		this.operations.set(
			"getDiscoverGamesProgramGameNationalLeagues",
			getDiscoverGamesProgramGameNationalLeagues,
		);

		this.operations.set("postDiscoverProgramGame", postDiscoverProgramGame);

		this.operations.set(
			"getDiscoverGamesBulkEligibility",
			getDiscoverGamesBulkEligibility,
		);

		this.operations.set(
			"postDiscoverGamesBulkPatch",
			postDiscoverGamesBulkPatch,
		);

		this.operations.set("getDiscoverGame", getDiscoverGame);

		this.operations.set("patchDiscoverGame", patchDiscoverGame);

		this.operations.set("getDiscoverGameV2", getDiscoverGameV2);

		this.operations.set(
			"getDiscoverGameMetadataOptions",
			getDiscoverGameMetadataOptions,
		);

		this.operations.set("approveGameAnalysis", approveGameAnalysis);

		this.operations.set("denyGameAnalysis", denyGameAnalysis);

		this.operations.set(
			"getDiscoverGameHistoryNotes",
			getDiscoverGameHistoryNotes,
		);

		this.operations.set(
			"postDiscoverGameHistoryNote",
			postDiscoverGameHistoryNote,
		);

		this.operations.set(
			"deleteDiscoverGameHistoryNote",
			deleteDiscoverGameHistoryNote,
		);

		this.operations.set(
			"patchDiscoverGameHistoryNote",
			patchDiscoverGameHistoryNote,
		);

		this.operations.set("postDiscoverGameLink", postDiscoverGameLink);

		this.operations.set("deleteDiscoverGameLink", deleteDiscoverGameLink);

		this.operations.set("patchDiscoverGameLink", patchDiscoverGameLink);

		this.operations.set("getDiscoverPlayersData", getDiscoverPlayersData);

		this.operations.set("getDiscoverPlayerData", getDiscoverPlayerData);

		this.operations.set("patchDiscoverPlayerData", patchDiscoverPlayerData);

		this.operations.set(
			"postDiscoverPlayerDataUpdateEtl",
			postDiscoverPlayerDataUpdateEtl,
		);

		this.operations.set("getDiscoverPlayers", getDiscoverPlayers);

		this.operations.set("getDiscoverPlayer", getDiscoverPlayer);

		this.operations.set("patchDiscoverPlayer", patchDiscoverPlayer);

		this.operations.set("getDiscoverSignUps", getDiscoverSignUps);

		this.operations.set("getDiscoverSignUp", getDiscoverSignUp);

		this.operations.set("patchDiscoverSignUp", patchDiscoverSignUp);

		this.operations.set(
			"adminDiscoverSubscriptionProductsGet",
			adminDiscoverSubscriptionProductsGet,
		);

		this.operations.set(
			"adminDiscoverSubscriptionsByTeamGet",
			adminDiscoverSubscriptionsByTeamGet,
		);

		this.operations.set(
			"adminDiscoverSubscriptionsByClubGet",
			adminDiscoverSubscriptionsByClubGet,
		);

		this.operations.set(
			"adminDiscoverSubscriptionByTeamPost",
			adminDiscoverSubscriptionByTeamPost,
		);

		this.operations.set(
			"adminDiscoverSubscriptionSetToFreePost",
			adminDiscoverSubscriptionSetToFreePost,
		);

		this.operations.set(
			"adminDiscoverSubscriptionSetToPaidPost",
			adminDiscoverSubscriptionSetToPaidPost,
		);

		this.operations.set(
			"adminDiscoverSubscriptionActivatePost",
			adminDiscoverSubscriptionActivatePost,
		);

		this.operations.set(
			"adminDiscoverSubscriptionStopPost",
			adminDiscoverSubscriptionStopPost,
		);

		this.operations.set(
			"adminDiscoverSubscriptionActivateRenewalPost",
			adminDiscoverSubscriptionActivateRenewalPost,
		);

		this.operations.set(
			"adminDiscoverSubscriptionPauseRenewalPost",
			adminDiscoverSubscriptionPauseRenewalPost,
		);

		this.operations.set("getDiscoverTeams", getDiscoverTeams);

		this.operations.set("getDiscoverTeam", getDiscoverTeam);

		this.operations.set("patchDiscoverTeam", patchDiscoverTeam);

		this.operations.set(
			"getDiscoverUserGuardianApprovals",
			getDiscoverUserGuardianApprovals,
		);

		this.operations.set(
			"postAdminDiscoverUserNotes",
			postAdminDiscoverUserNotes,
		);

		this.operations.set(
			"deleteAdminDiscoverUserNote",
			deleteAdminDiscoverUserNote,
		);

		this.operations.set(
			"patchAdminDiscoverUserNote",
			patchAdminDiscoverUserNote,
		);

		this.operations.set(
			"postDiscoverUserPermission",
			postDiscoverUserPermission,
		);

		this.operations.set(
			"deleteDiscoverUserPermission",
			deleteDiscoverUserPermission,
		);

		this.operations.set("getDiscoverUsers", getDiscoverUsers);

		this.operations.set("getDiscoverUser", getDiscoverUser);

		this.operations.set("patchDiscoverUser", patchDiscoverUser);

		this.operations.set(
			"postDiscoverUserImpersonate",
			postDiscoverUserImpersonate,
		);

		this.operations.set("rejectGameAnalysis", rejectGameAnalysis);

		this.operations.set("createGameAnalysis", createGameAnalysis);

		this.operations.set("swapGameHomeAwayMapping", swapGameHomeAwayMapping);

		this.operations.set(
			"getGameHomeAwayMappingSwapEligibility",
			getGameHomeAwayMappingSwapEligibility,
		);

		this.operations.set(
			"getOperationsManagementSummaryByCountry",
			getOperationsManagementSummaryByCountry,
		);

		this.operations.set(
			"getOperationsManagementSummaryByCountryFilterOptions",
			getOperationsManagementSummaryByCountryFilterOptions,
		);

		this.operations.set(
			"get_admin_operations_management_summary_by_country_export",
			get_admin_operations_management_summary_by_country_export,
		);

		this.operations.set(
			"getOperationsManagementSummaryByClub",
			getOperationsManagementSummaryByClub,
		);

		this.operations.set(
			"getOperationsManagementSummaryByClubFilterOptions",
			getOperationsManagementSummaryByClubFilterOptions,
		);

		this.operations.set(
			"get_admin_operations_management_summary_by_club_export",
			get_admin_operations_management_summary_by_club_export,
		);

		this.operations.set("patchPlayerMembership", patchPlayerMembership);

		this.operations.set("jsonLogin", jsonLogin);

		this.operations.set("getCurrentUser", getCurrentUser);

		this.operations.set("adminJsonLogout", adminJsonLogout);

		this.operations.set("setUserLocale", setUserLocale);

		this.operations.set("forgotPassword", forgotPassword);

		this.operations.set("resetPassword", resetPassword);

		this.operations.set("validateUserPasswordReset", validateUserPasswordReset);

		this.operations.set("searchNationalLeagues", searchNationalLeagues);

		this.operations.set("getPermissionTree", getPermissionTree);

		this.operations.set("getPermissionByRole", getPermissionByRole);

		this.operations.set("assignPermissionToRole", assignPermissionToRole);

		this.operations.set("deleteRole", deleteRole);

		this.operations.set("getRoles", getRoles);

		this.operations.set("getUserRoles", getUserRoles);

		this.operations.set("addUpdateRole", addUpdateRole);

		this.operations.set("getAllAdminUsers", getAllAdminUsers);

		this.operations.set("addUpdateAdminUsers", addUpdateAdminUsers);

		this.operations.set("deleteAdminUser", deleteAdminUser);

		this.operations.set("uploadProfilePhoto", uploadProfilePhoto);

		this.operations.set("getAllPoles", getAllPoles);

		this.operations.set("addUpdatePole", addUpdatePole);

		this.operations.set("getTimezones", getTimezones);

		this.operations.set("deletePole", deletePole);

		this.operations.set("getSelectedPoleByClub", getSelectedPoleByClub);

		this.operations.set("assignPole", assignPole);

		this.operations.set("unAssignPole", unAssignPole);

		this.operations.set("syncPole", syncPole);

		this.operations.set("getClubList", getClubList);

		this.operations.set("getCountries", getCountries);

		this.operations.set("getContinentCountry", getContinentCountry);

		this.operations.set("getNPClubs", getNPClubs);

		this.operations.set("addUpdateNPClub", addUpdateNPClub);

		this.operations.set("deleteNPClub", deleteNPClub);

		this.operations.set("uploadNPClubPhoto", uploadNPClubPhoto);

		this.operations.set("uploadNPPlayerPicture", uploadNPPlayerPicture);

		this.operations.set("getNPPlayers", getNPPlayers);

		this.operations.set("getNationalityList", getNationalityList);

		this.operations.set("getPositionList", getPositionList);

		this.operations.set("getGeographicalAreaList", getGeographicalAreaList);

		this.operations.set("addUpdateNPPlayer", addUpdateNPPlayer);

		this.operations.set("deleteNPPlayer", deleteNPPlayer);

		this.operations.set("getAllNPClubs", getAllNPClubs);

		this.operations.set("getClubPlayers", getClubPlayers);

		this.operations.set("executeNPClubMigration", executeNPClubMigration);

		this.operations.set("getTeamsByTargetClub", getTeamsByTargetClub);

		this.operations.set("playerExistsTargetClub", playerExistsTargetClub);

		this.operations.set("getMigrationStatus", getMigrationStatus);

		this.operations.set(
			"executePartnerClubMigration",
			executePartnerClubMigration,
		);

		this.operations.set(
			"createPartnerPlayerFromNonpartner",
			createPartnerPlayerFromNonpartner,
		);

		this.operations.set(
			"partnerPlayerExistsTargetClub",
			partnerPlayerExistsTargetClub,
		);

		this.operations.set("getPartnerMigrationStatus", getPartnerMigrationStatus);

		this.operations.set("updateGameTeam", updateGameTeam);

		this.operations.set(
			"migrateSourceClubNoPlayers",
			migrateSourceClubNoPlayers,
		);

		this.operations.set("beginMigration", beginMigration);

		this.operations.set(
			"clubMigrationWithZeroPlayers",
			clubMigrationWithZeroPlayers,
		);

		this.operations.set("completeMigration", completeMigration);

		this.operations.set("playerMigrationToPartner", playerMigrationToPartner);

		this.operations.set("playerMigrationConflict", playerMigrationConflict);

		this.operations.set("getAllCountriesTooltip", getAllCountriesTooltip);

		this.operations.set("addUpdateCountry", addUpdateCountry);

		this.operations.set("deleteCountry", deleteCountry);

		this.operations.set("allCountries", allCountries);

		this.operations.set("enableDisableCountry", enableDisableCountry);

		this.operations.set("loadOwnerships", loadOwnerships);

		this.operations.set("loadTeamGroups", loadTeamGroups);

		this.operations.set("getSubscriptionFilterData", getSubscriptionFilterData);

		this.operations.set("assignSubscriptionToClub", assignSubscriptionToClub);

		this.operations.set("getAllLanguages", getAllLanguages);

		this.operations.set("userSearch", userSearch);

		this.operations.set("clubAmateurSearch", clubAmateurSearch);

		this.operations.set("clubProSearch", clubProSearch);

		this.operations.set("getAllEyeballAddons", getAllEyeballAddons);

		this.operations.set("getUserDetail", getUserDetail);

		this.operations.set("getUserSessions", getUserSessions);

		this.operations.set("revokeSessions", revokeSessions);

		this.operations.set("getUserSignIns", getUserSignIns);

		this.operations.set("logUserActivity", logUserActivity);

		this.operations.set("getUserWidgetSettings", getUserWidgetSettings);

		this.operations.set("saveUserWidgetSettings", saveUserWidgetSettings);

		this.operations.set("getClubAllTeams", getClubAllTeams);

		this.operations.set("getTeamByIdAndCoachId", getTeamByIdAndCoachId);

		this.operations.set("addUpdateTeam", addUpdateTeam);

		this.operations.set("deleteTeam", deleteTeam);

		this.operations.set("clubCoaches", clubCoaches);

		this.operations.set("getClubTeamsList", getClubTeamsList);

		this.operations.set("teamPlayers", teamPlayers);

		this.operations.set("addUpdatePlayer", addUpdatePlayer);

		this.operations.set("updateVip", updateVip);

		this.operations.set("updateLevel", updateLevel);

		this.operations.set("deletePlayer", deletePlayer);

		this.operations.set("formerPlayers", formerPlayers);

		this.operations.set("uploadPlayerPhoto", uploadPlayerPhoto);

		this.operations.set("assignCoachesToTeam", assignCoachesToTeam);

		this.operations.set("getNationalities", getNationalities);

		this.operations.set("getCategories", getCategories);

		this.operations.set("batchUpdate", batchUpdate);

		this.operations.set("generateSignUpLink", generateSignUpLink);

		this.operations.set("getPlayerLatestLogEntries", getPlayerLatestLogEntries);

		this.operations.set(
			"adminCreatePlayerPossibleDuplicate",
			adminCreatePlayerPossibleDuplicate,
		);

		this.operations.set(
			"getPlayerPossibleDuplicates",
			getPlayerPossibleDuplicates,
		);

		this.operations.set(
			"patchPlayerPossibleDuplicate",
			patchPlayerPossibleDuplicate,
		);

		this.operations.set("getVideoUploads", getVideoUploads);

		this.operations.set("getVideoUploaders", getVideoUploaders);

		this.operations.set("getVideo", getVideo);

		this.operations.set("deleteVideoUpload", deleteVideoUpload);

		this.operations.set("addVideoMetadata", addVideoMetadata);

		this.operations.set("approveVideoMetadata", approveVideoMetadata);

		this.operations.set(
			"removeApproveVideoMetadata",
			removeApproveVideoMetadata,
		);

		this.operations.set("getGames", getGames);

		this.operations.set("getClubs", getClubs);

		this.operations.set(
			"getVideoUploadsNotifications",
			getVideoUploadsNotifications,
		);

		this.operations.set("getTestVideos", getTestVideos);

		this.operations.set("getVideoGameDetail", getVideoGameDetail);

		this.operations.set("videoProcessingReset", videoProcessingReset);

		this.operations.set("videoProcessingRetry", videoProcessingRetry);

		this.operations.set("renameVideo", renameVideo);

		this.operations.set("getVideoServerStatus", getVideoServerStatus);

		this.operations.set("migratePlayers", migratePlayers);

		this.operations.set("getClubDetail", getClubDetail);

		this.operations.set("postPlayerMappingResults", postPlayerMappingResults);

		this.operations.set("patchPlayerProfile", patchPlayerProfile);

		this.operations.set("getPlayerUsers", getPlayerUsers);

		this.operations.set("patchPlayerUser", patchPlayerUser);

		this.operations.set("getSubscriptions", getSubscriptions);

		this.operations.set("postSubscription", postSubscription);

		this.operations.set("getSubscription", getSubscription);

		this.operations.set("putSubscription", putSubscription);

		this.operations.set("deleteSubscriptionV2", deleteSubscriptionV2);

		this.operations.set(
			"getSubscriptionProductsConfig",
			getSubscriptionProductsConfig,
		);

		this.operations.set(
			"getSubscriptionDiscoverCountries",
			getSubscriptionDiscoverCountries,
		);

		this.operations.set("getTeamGroups", getTeamGroups);

		this.operations.set("postTeamGroup", postTeamGroup);

		this.operations.set(
			"getTeamGroupDiscoverTeamOptions",
			getTeamGroupDiscoverTeamOptions,
		);

		this.operations.set("getTeamGroup", getTeamGroup);

		this.operations.set("putTeamGroup", putTeamGroup);

		this.operations.set("deleteTeamGroup", deleteTeamGroup);

		this.operations.set("getSignUpRequests", getSignUpRequests);

		this.operations.set("patchSignUpRequest", patchSignUpRequest);

		this.operations.set("getSignUpRequestData", getSignUpRequestData);

		this.operations.set("getTokenMovements", getTokenMovements);

		this.operations.set("postTokenMovement", postTokenMovement);

		this.operations.set(
			"getOptionsClubsOfSignUpRequests",
			getOptionsClubsOfSignUpRequests,
		);

		this.operations.set(
			"getOptionsClubsOfPlayerUsers",
			getOptionsClubsOfPlayerUsers,
		);

		this.operations.set("postGameTeamSheetExport", postGameTeamSheetExport);

		this.operations.set("putUpdateGameLeftTeam", putUpdateGameLeftTeam);

		this.operations.set("putGameAnalysisApproval", putGameAnalysisApproval);

		this.operations.set("putGameResync", putGameResync);

		this.operations.set("shareClipLink", shareClipLink);

		this.operations.set("getTeamTokenBalance", getTeamTokenBalance);

		this.operations.set("getClubTokenBalance", getClubTokenBalance);

		this.operations.set("getTasks", getTasks);

		this.operations.set("getAmatuerClubs", getAmatuerClubs);

		this.operations.set("getTeamsheet", getTeamsheet);

		this.operations.set("updatePlayerStatus", updatePlayerStatus);

		this.operations.set("getTeamsForDropdown", getTeamsForDropdown);

		this.operations.set("getLeaguesForDropdown", getLeaguesForDropdown);

		this.operations.set("getCountriesForDropdown", getCountriesForDropdown);

		this.operations.set("createPlayer", createPlayer);

		this.operations.set("playerSearchDropdown", playerSearchDropdown);

		this.operations.set(
			"adminGetGameAnalysisPhysical",
			adminGetGameAnalysisPhysical,
		);

		this.operations.set(
			"postGameAnalysisPhysicalVideoUrl",
			postGameAnalysisPhysicalVideoUrl,
		);

		this.operations.set("getGameEventsDebug", getGameEventsDebug);

		this.operations.set("getGameStatsDebug", getGameStatsDebug);

		this.operations.set("getGamePage", getGamePage);

		this.operations.set("getGamePageLazy", getGamePageLazy);

		this.operations.set("putGameScore", putGameScore);

		this.operations.set("getGameTeamStat", getGameTeamStat);

		this.operations.set("getGamePlayerStat", getGamePlayerStat);

		this.operations.set("putGameTeamColors", putGameTeamColors);

		this.operations.set("setRatingToGamePlayer", setRatingToGamePlayer);

		this.operations.set("setBadgesToGamePlayer", setBadgesToGamePlayer);

		this.operations.set("postGameHighlight", postGameHighlight);

		this.operations.set("deleteGameHighlight", deleteGameHighlight);

		this.operations.set("patchGameHighlight", patchGameHighlight);

		this.operations.set("getGameClips", getGameClips);

		this.operations.set("getGameClip", getGameClip);

		this.operations.set("putGameClip", putGameClip);

		this.operations.set("deleteGameClip", deleteGameClip);

		this.operations.set("postGameClip", postGameClip);

		this.operations.set("putGameTeamSheet", putGameTeamSheet);

		this.operations.set(
			"getGameTeamSheetCompositionPlayers",
			getGameTeamSheetCompositionPlayers,
		);

		this.operations.set("getGameTeamPlayersSearch", getGameTeamPlayersSearch);

		this.operations.set(
			"getGameTeamSheetClubPlayers",
			getGameTeamSheetClubPlayers,
		);

		this.operations.set("putGameDescription", putGameDescription);

		this.operations.set("postGameStatsExport", postGameStatsExport);

		this.operations.set("postGameEventDataExport", postGameEventDataExport);

		this.operations.set(
			"getSharedGameClipIsProtected",
			getSharedGameClipIsProtected,
		);

		this.operations.set("getSharedGameClip", getSharedGameClip);

		this.operations.set(
			"postGameClipSharedPasswordValidation",
			postGameClipSharedPasswordValidation,
		);

		this.operations.set("getGameEventFilters", getGameEventFilters);

		this.operations.set("postGameEventFilter", postGameEventFilter);

		this.operations.set("deleteGameEventFilter", deleteGameEventFilter);

		this.operations.set("addCommentToGameEvent", addCommentToGameEvent);

		this.operations.set("deleteGameEventComment", deleteGameEventComment);

		this.operations.set("getUserView", getUserView);

		this.operations.set("updateSelectedView", updateSelectedView);

		this.operations.set("getMfaDomainList", getMfaDomainList);

		this.operations.set("getMfaDomainRequiredCheck", getMfaDomainRequiredCheck);

		this.operations.set("postReportedTaggingIssue", postReportedTaggingIssue);

		this.operations.set("getUserAllCountries", getUserAllCountries);

		this.operations.set("getUserSelectedCountries", getUserSelectedCountries);

		this.operations.set(
			"updateUserSelectedCountries",
			updateUserSelectedCountries,
		);

		this.operations.set(
			"getTeamGroupVisibleEntities",
			getTeamGroupVisibleEntities,
		);

		this.operations.set(
			"getPlayerProfileEventTypes",
			getPlayerProfileEventTypes,
		);

		this.operations.set(
			"getPlayerProfileEventFilters",
			getPlayerProfileEventFilters,
		);

		this.operations.set("getPlayerProfileEvents", getPlayerProfileEvents);

		this.operations.set("getPlayerProfileKeyEvents", getPlayerProfileKeyEvents);

		this.operations.set(
			"postPlayerProfileCrossGameClip",
			postPlayerProfileCrossGameClip,
		);

		this.operations.set(
			"getPlayerProfileShadowTeamList",
			getPlayerProfileShadowTeamList,
		);

		this.operations.set(
			"postPlayerProfileShadowTeamPlayer",
			postPlayerProfileShadowTeamPlayer,
		);

		this.operations.set(
			"postPlayerProfileShadowTeam",
			postPlayerProfileShadowTeam,
		);

		this.operations.set(
			"getPlayerProfileRecruitmentInformation",
			getPlayerProfileRecruitmentInformation,
		);

		this.operations.set(
			"getPlayerProfileBiometricsAvailability",
			getPlayerProfileBiometricsAvailability,
		);

		this.operations.set(
			"getPlayerProfileBodyMeasurements",
			getPlayerProfileBodyMeasurements,
		);

		this.operations.set(
			"getPlayerProfileBodyMetricTrend",
			getPlayerProfileBodyMetricTrend,
		);

		this.operations.set(
			"getPlayerProfileBodyCompositionChange",
			getPlayerProfileBodyCompositionChange,
		);

		this.operations.set(
			"getPlayerProfilePhysicalMetricsFilters",
			getPlayerProfilePhysicalMetricsFilters,
		);

		this.operations.set(
			"getPlayerProfilePhysicalMetrics",
			getPlayerProfilePhysicalMetrics,
		);

		this.operations.set(
			"getPlayerProfileBehavioralSignalsFilters",
			getPlayerProfileBehavioralSignalsFilters,
		);

		this.operations.set(
			"getPlayerProfileBehavioralSignalsTrend",
			getPlayerProfileBehavioralSignalsTrend,
		);

		this.operations.set(
			"getPlayerProfileBehavioralSignalsSnapshot",
			getPlayerProfileBehavioralSignalsSnapshot,
		);

		this.operations.set(
			"getPlayerProfileBehavioralArchetypes",
			getPlayerProfileBehavioralArchetypes,
		);

		const http = this.configureHttp(baseUrl, options);
		this.http = http;

		// eslint-disable-next-line
		// biome-ignore lint/correctness/noConstructorReturn: <explanation>
		return new Proxy(this, {
			get(target, name: string) {
				// eslint-disable-next-line
				// biome-ignore lint/suspicious/noPrototypeBuiltins: <explanation>
				if (target.hasOwnProperty(name)) {
					//@ts-ignore
					return target[name];
				}

				return target.operations.get(name)(http, queryClient);
			},
		});
	}

	private createHttp(
		baseUrl: string,
		options: Pick<RequestInit, "headers" | "credentials" | "mode"> = {},
	) {
		return wretch(baseUrl, options).addon(QueryStringAddon);
	}

	private configureHttp(
		baseUrl: string,
		options: Pick<RequestInit, "headers" | "credentials" | "mode"> = {},
	) {
		return this.onHttpConfigure(this.createHttp(baseUrl, options));
	}
}
